import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useContext,
  createContext,
  Fragment,
} from "react";
import { SkuDelProducto } from "../components/SkuDelProducto";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { rutaDeVueltaARedes } from "../lib/estudioDesdeRedes";
import { useSidebar } from "../context/SidebarContext";
// El editor en el teléfono: mismo componente, otro cascarón
// (ADR-2026-09-16-el-estudio-de-video-en-el-telefono).
import { useDebajoDeMd } from "../hooks/useEsMobile";
// Marco del shell nuevo, el mismo que usan "Editar imagen" y "Editar video con IA".
import { MARCO_PANTALLA, BORDE_PANTALLA } from "../components/PantallaChrome";
import { supabase } from "../lib/supabase";
import { withTimeout } from "../lib/withTimeout";
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { fixExtensionFromBlob } from "../utils/downloadFileName";
import { detectAutocutRanges } from "../lib/autocut";
import DialogueAudioInput from "../components/DialogueAudioInput";
import CachedImg from "../components/CachedImg";
import ProductGridSkeleton from "../components/ProductGridSkeleton";
import VoiceCloneCreator from "../components/VoiceCloneCreator";
import ClipVoicePicker from "../components/ClipVoicePicker";
import {
  ClonedVoice,
  fetchClonedVoices,
} from "../lib/clonedVoicesApi";
import { GENERATE_AUTH_TOKEN, SUPABASE_ANON_KEY, SUPABASE_URL } from "../config";
import { generateAudioCreator } from "../lib/audioGenerate";
import {
  clampDuracionDeEscena,
  faltaPromptDeEscena,
  motivoDeClipSinDatos,
} from "../lib/contratoCreator";
import {
  mensajeDeFalloDeSubtitulos,
  type PasoDeSubtitulos,
} from "../lib/diagnosticoSubtitulos";
import { captureException } from "../lib/posthog";
import { cancelarEscena, escribirPromptDeVideo, generateVideoCreator } from "../lib/videoCreator";
import { escribirPromptDeImagen } from "../lib/escritorDeImagen";
import WakeSlider from "../components/WakeSlider";
import {
  camposDelEscritorDeImagen,
  camposDelEscritorDeVideo,
  claveDeRefs,
  escenaYaEscrita,
  mejoraDeImagen,
  referenciasDeImagen,
  TIPO_DE_VIDEO_DEL_ESTUDIO,
  type MejoraDeEscena,
  type MejoraDeImagen,
} from "./estudio/metapromptingDelEstudio";
import {
  creatorAttemptRef,
  creatorParamsKey,
  isN8nOnlyCreatorRefMode,
  N8N_ONLY_REF_MODES,
} from "../lib/creatorRetry";
import { changeClipVoiceVideo } from "../lib/changeClipVoiceVideo";
import { GenerateAdError, postGenerateAdAtlas } from "../lib/generateAdRequest";
import { USE_ATLAS_BACKEND } from "../config";
import { atlasBackendFetch, pollJobUntilSettled } from "../lib/atlasBackend";
import { awaitImageGeneration } from "./v2/imageGenerationRecovery";
import EraseBrushPanel from "../components/EraseBrushPanel";
import { EditarVideoIA } from "../components/EditarVideoIA";
import {
  excedeElTopeDeOmni,
  segundosDelRecorteOmni,
} from "../lib/omniEdit";
import { UpscaleVideoPanel } from "../components/UpscaleVideoPanel";
import {
  creditsForBrushErase,
  marksToSamPoints,
  requestSam2Mask,
  type EraseMark,
} from "../lib/sam2Erase";
import {
  uploadProjectAsset,
  getAssetSignedUrl,
  firmarVariosAssets,
  firmarVersionesLivianas,
  fetchAssetsByIds,
  fetchProjectAssetsByType,
  getSignedUrlsForAssetIds,
  registerExistingAsset,
  describeUploadError,
  type ProjectAsset,
} from "../utils/projectAssets";
import {
  uploadBrandAsset,
  fetchBrandAssets,
  getBrandAssetSignedUrl,
  deleteBrandAsset,
  type BrandAsset,
  type BrandAssetTipo,
} from "../utils/brandAssets";
import {
  fetchImagenesDeMoodboards,
  type ImagenDeMoodboard,
} from "../utils/moodboards";
import { clasesDePastilla } from "../components/pastillaDeCarpeta";
import SelectorDeImagenModal, {
  type AccionDelSelector,
  type PestanaDelSelector,
} from "../components/SelectorDeImagenModal";
import {
  CLAVE_PANTALLA_ESTUDIO,
  HERRAMIENTAS_DEL_ESTUDIO,
  SKILL_DEL_ESTUDIO,
} from "../agent/tools";
import {
  useChatAtlasCascaron,
  useEmpujeChat,
  useRegistrarPantalla,
  type DescripcionDePantalla,
} from "../chat-atlas";
import { useIsDesktop } from "../components/onboarding/useIsDesktop";
import {
  TEXT_FONT_OPTIONS,
  TEXT_FONT_OPTIONS_ALFABETICAS,
  FUENTE_POR_DEFECTO,
  fontStackFor,
  familiaDelCatalogo,
  familiaQueElRenderTiene,
  pilaDeFuenteComoEnElRender,
} from "../lib/fontOptions";
import {
  fetchUserProductsByBrand,
  fetchHistorialImages,
  fetchHistorialVideos,
  type HistorialVideo,
} from "./v2/productsApi";
import type { MockProductV2 } from "./v2/mockData";
import {
  fetchUserModels,
  createModelFromPhoto,
  type ModelV2,
} from "./v2/modelsApi";
import { CreateView, UploadFlow } from "./v2/ModelPicker";
import { postTranslateStart, postTranslateStatus } from "../lib/videoTranslate";
import { postVideoEraseStart, postVideoEraseStatus } from "../lib/videoErase";
import { postCorregirVozClipStart, postCorregirVozClipStatus } from "../lib/corregirVozClip";
import { resolverCorrecciones } from "../components/corregirVoz/resolverCorrecciones";
import CorregirVozDelClip from "../components/corregirVoz/CorregirVozDelClip";
import { postImageEdit } from "../lib/imageEdit";
import { AddOptionCard } from "./v2/components/AddOptionCard";
import { startEditorTour, startContextualTour } from "../lib/editorTour";
import {
  loadOnboardingSeen,
  isSeen as isOnboardingSeen,
  markSeen as markOnboardingSeen,
} from "../lib/onboardingSeen";
import { generateVideoThumbnails } from "../utils/videoThumbnails";
import {
  findCoverClipAtZero,
  hashCoverClip,
  regenerateProjectThumbnail,
} from "../utils/projectThumbnail";
import { AnimationLayer } from "../animations/AnimationLayer";
import {
  copiarImagenAlPortapapeles,
  cuadroDelVideo,
  descargarImagen,
  nombreDelCuadro,
} from "../utils/copiarCuadro";
import { evaluarVideoDeReferencia, segundosDeEntradaACobrar } from "../utils/videoDeReferencia";
import { esArchivoDeVideo } from "../utils/posterDeVideo";
import { duracionDeVideoPorUrl } from "../utils/duracionDeVideoPorUrl";
// Animaciones por código (ADR-2026-09-24-animacion-por-codigo): el sandbox del
// preview, el panel del clip, el historial de versiones y el generador.
import {
  SandboxAnimacion,
  capturarSandbox,
  esperarSandbox,
  reiniciarSandbox,
} from "../animations/SandboxAnimacion";
import { PanelAnimacionPorCodigo } from "../animations/PanelAnimacionPorCodigo";
import {
  agregarVersion,
  crearAnimacionPorCodigo,
  describirVersiones,
  versionActual,
  volverAVersion,
  type AnimacionPorCodigo,
  type AssetDeAnimacion,
  type MarcaDeAnimacion,
  type TipoDeAnimacionPorCodigo as TipoDeAnimacionParaClip,
} from "../animations/codigo/versiones";
import {
  edicionParaGuardar,
  retomarEdicionDeClip,
  sesionDeEdicion,
  useSesionesDeEdicion,
  type EdicionDeClipGuardada,
} from "../lib/edicionDeClip";
import {
  conVentanaDeAnimacion,
  duracionNaturalDeAnimacion,
  ventanaDeVersion,
} from "../animations/codigo/ventana";
import { acotarDuracion, CREDITOS_ANIMACION, MAX_ASSETS } from "../animations/codigo/reglas";
import {
  actualizarAnimacion,
  ANCHO_MINIATURA,
  esListaDeAnimacionesGuardadas,
  guardarAnimacion,
  nombreDeAnimacion,
  quitarAnimacion,
  type AnimacionGraficaGuardada,
} from "../animations/codigo/biblioteca";
import type { PropsDeAnimacion } from "../animations/sandbox/protocolo";
import { pedirAnimacionPorCodigo } from "../lib/animacionPorCodigo";
import {
  computeClipMotion,
  getMotionPreset,
  splitTextForMotion,
} from "../animations/clipMotion";
import { MotionPicker } from "../components/MotionPicker";
import {
  SelectionFrame,
  SEPARACION_ROTACION,
  type SelectionHandle,
} from "../components/SelectionFrame";
import { SegmentedControl } from "../components/SegmentedControl";
import { insertarFila, moverFila, ordenarFilas } from "../lib/ordenDeFilas";
import { acomodarPistaAlrededorDe } from "../lib/acomodarPista";
// Soltar algo arrastrado sobre la timeline: cae donde se suelta
// (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta).
import {
  capaLibreEn,
  CENTRO_DESDE,
  CENTRO_HASTA,
  lugarEnLaPistaPrincipal,
  destinoDeSoltar as calcularDestinoDeSoltar,
  familiaDeArchivo,
  familiaDelTransfer,
  pegarAlFinal,
  TIPO_ANIMACION,
  TIPO_ASSET,
  TIPO_FAMILIA,
  TIPO_GENERADA,
  type DestinoDeSoltar,
  type FamiliaDePista,
  type FilaDeLaTimeline,
} from "../lib/soltarEnLaTimeline";
import { arrastraArchivos } from "./v2/modes/shared/imagenesDeTransfer";
// Quitar el fondo de una capa de imagen (BiRefNet v2 en el backend; gratis)
// (ADR-2026-09-27-quitar-fondo-en-el-estudio-birefnet).
import { postQuitarFondo } from "../lib/quitarFondoBackend";
import { leerSaldo } from '../lib/saldo';
// El transcript de un clip vale sólo para el archivo que se transcribió
// (ADR-2026-09-28-el-transcript-sabe-de-que-archivo-es).
import { marcarTranscript, transcriptVigente } from "../lib/transcriptDelClip";
import {
  type EncuadreDeClip,
  type RecorteDeClip,
  SIN_RECORTE,
  cajaDelEncuadre,
  esEncuadreNeutro,
  escalarDesdeEsquina,
  estiloDelEncuadre,
  imantarAlCuadro,
  normalizarEncuadre,
  recortarDesdeLado,
  rotarDesdeManija,
} from "../lib/encuadreDeClip";
import { AnimationPreviewTile } from "../animations/AnimationPreviewTile";
import { LottiePicker } from "../animations/LottiePicker";
import {
  ANIMATION_TEMPLATES,
  getAnimationTemplate,
} from "../animations/registry";
import type { AnimationPropSpec, TextStylingLinks } from "../animations/types";
import { ANIMATION_FPS } from "../animations/types";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import {
  Play,
  Pause,
  Upload,
  UserPlus,
  Sparkles,
  SlidersHorizontal,
  Music,
  Type,
  Plus,
  Trash2,
  Image as ImageIcon,
  Film,
  Loader2,
  ZoomIn,
  ZoomOut,
  Copy,
  Download,
  Wand2,
  Undo2,
  Redo2,
  Folder,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  GalleryHorizontalEnd,
  Clock,
  Mic,
  ArrowUpFromLine,
  AudioLines,
  Captions,
  Languages,
  Send,
  Pencil,
  AlertCircle,
  Gem,
  Heart,
  Video,
  BookOpen,
  Search,
  BarChart3,
  Brain,
  Compass,
  ArrowLeftRight,
  Ratio,
  Hand,
  X,
  Check,
  Scissors,
  ScissorsLineDashed,
  RotateCcw,
  Volume2,
  VolumeX,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Palette,
  Eraser,
  Package,
  Images,
  Users,
  MousePointerClick,
  Camera,
} from "lucide-react";
import {
  exportProjectAsPremiereXmlZip,
  downloadBlob,
  type PremiereXmlExportClip,
  type PremiereXmlAspectRatio,
} from "../utils/premiereXmlExport";
import {
  serializeTimeline,
  preflightExport,
  startRender,
  seguirRender,
  buscarExportEnCurso,
  renderMessageFor,
  mensajeDeErrorDeExport,
  RENDER_MESSAGE_ROTATE_MS,
  type RenderHandle,
  type SkippedClip,
} from "../lib/videoExport";
import {
  creditsForVideoGeneral,
  creditsForVideoDialog,
  creditsForVoice,
  creditsForSong,
  creditsForEfecto,
  creditsForImage,
  creditsForAiClip,
  creditsForVideoTranslate,
  type VideoModel,
  type VideoResolution,
} from "../utils/credits";
import { AYUDA_BORRADOR, creditosDePasarA1080p, esBorrador } from "../lib/resolucionVideo";
import { pasarBorradorA1080p, seguirPasoA1080p } from "../lib/videoBorrador";
import { seguirUpscale } from "../lib/videoUpscale";
import { PasarA1080pDialogo } from "../components/PasarA1080pDialogo";
import { clipEsBorradorVigente, type BorradorDeClip } from "./estudio/borradorDeClip";
import { useGuardadoDelProyecto } from "./estudio/useGuardadoDelProyecto";
import {
  esArrastre,
  iniciarAutoscroll,
  velocidadDeAutoscroll,
  scrollParaVerElCursor,
} from "../lib/autoscrollDeLaTimeline";
import { tiempoConLaTecla } from "../lib/tecladoDeLaTimeline";
import { partirClip, videoBajoElCursor } from "../lib/dividirClip";
import { AvisoDeGuardado } from "./estudio/AvisoDeGuardado";
import { AvisoDelClip } from "./estudio/AvisoDelClip";
import { AvisoDeConflicto } from "./estudio/AvisoDeConflicto";
import { CargandoElVideo } from "./estudio/CargandoElVideo";
import { BotonDeAtajos } from "./estudio/BotonDeAtajos";
import { useUserGroup } from "../hooks/useUserGroup";
import { useSaldo } from "../hooks/useSaldo";
import { precargaDelClip } from "./estudio/precargaDelClip";
import {
  pedirVersionesLivianas,
  rutaDeLaVersionLiviana,
  srcDelPreview,
  usaLaVersionLiviana,
  videoOriginalEn,
} from "./estudio/versionLiviana";
import { motivoDelClip } from "./estudio/motivoDelClip";
import {
  clipParaGuardar,
  conLosProcesosDeAhora,
  repararAlAbrir,
  type ProcesoARetomar,
} from "./estudio/procesosCortados";
import { guardarConKeepalive } from "./estudio/guardarAlCerrar";
import { rutaDeUrlFirmada, rutaFullDeOpt } from "./estudio/rutaDeUrlFirmada";
import {
  aDondePrepararlo,
  siguienteTiempo,
  type VideoQueManda,
} from "./estudio/relojDeReproduccion";
import {
  claveDeMiniaturas,
  claveDeOnda,
  crearLimitador,
  guardarEnLaCache,
  leerDeLaCache,
  unaVezPorClave,
} from "./estudio/cacheDeMedios";
import type { OpcionesDeGuardado } from "./estudio/useGuardadoDelProyecto";
import {
  acotarDuracionDeEfecto,
  EFECTO_DURACION_MAX_SEG,
  EFECTO_DURACION_MIN_SEG,
} from "../utils/duracionDeEfecto";
import {
  acotarDuracionDeMusica,
  formatearDuracionDeMusica,
  MUSICA_DURACION_MAX_SEG,
  MUSICA_DURACION_MIN_SEG,
} from "../utils/duracionDeMusica";
import CreditBadge from "../components/CreditBadge";
import SubscriptionLimitModal from "../components/SubscriptionLimitModal";
import TransitionPreviewTile from "../components/TransitionPreviewTile";
import { DURACION_INICIAL_SEGUNDOS } from "../lib/videoProjectDefaults";
import { palabraDeModeloActual } from "../lib/nombreDeModelo";
import { LogoDeMarca } from '../branding';

// ─── Types ──────────────────────────────────────────────────────────────────

type ClipType =
  | "video"
  | "image"
  | "audio"
  | "text"
  | "ai-generated"
  | "animation";

type AiClipStatus = "draft" | "generating" | "ready" | "error";

type AspectRatio = "16:9" | "9:16" | "4:3";

// Color del marco de encuadre y del cartelito de grados. Es el azul de marca
// (`--brand-primary`) en una constante porque `SelectionFrame` lo necesita como
// valor de color plano —lo usa en `outline`, en el `fill` de un SVG y en el
// borde del círculo—. Va como variable para que siga al branding del cliente:
// el SVG del marco la aplica por `style`, donde una var de CSS sí sirve.
const AZUL_DEL_ENCUADRE = "var(--brand-primary)";

interface TimelineClip {
  id: string;
  type: ClipType;
  name: string;
  startTime: number;
  duration: number;
  color: string;
  thumbnail?: string;
  volume?: number;
  src?: string;
  /** La versión liviana del archivo, SÓLO para mirar (preview, miniaturas, ondas). No
   *  se guarda; el export y todo lo que necesita calidad usan `src`
   *  (ADR-2026-11-03-el-preview-usa-la-version-liviana). */
  srcPreview?: string;
  /** El `storagePath` del que es `srcPreview`: si el clip cambia de archivo, la liviana
   *  deja de usarse (ver `srcDelPreview`). */
  srcPreviewDe?: string;
  // Para retomar al abrir lo que quedó en curso
  // (ADR-2026-11-03-los-procesos-se-retoman-al-volver).
  eraseSabor?: "bria" | "sam2";
  corregirVozDesde?: string;
  pasarA1080pExecId?: string;
  mejorarCalidadStatus?: "processing";
  mejorarCalidadJobId?: string;
  // For split clips: seconds into the source media where this clip starts playing (0 = from the beginning)
  mediaOffset?: number;
  // Total duration of the source media (for video/audio). Used to cap trim handles at original length.
  sourceDuration?: number;
  // Velocidad de reproducción del clip (0.5x a 2x, 1 = normal). Cambia la
  // DURACIÓN del clip en la timeline: el material es el mismo, tarda más o
  // menos en pasar. `duration` sigue siendo tiempo de TIMELINE; los segundos
  // de material que consume son `duration * speed`.
  // Ver ADR-2026-09-15-velocidad-por-clip.
  speed?: number;
  storagePath?: string;
  // asset_id pointing to video_project_assets for uploaded video/audio/image clips
  assetId?: string;
  aiPrompt?: string;
  // New: asset-based references (arrays of video_project_assets.id)
  aiRefAssetIds?: string[];
  aiFirstFrameAssetId?: string;
  aiLastFrameAssetId?: string;
  // Legacy: base64 data URLs (kept for backward compat with old projects)
  aiFirstFrame?: string;
  aiLastFrame?: string;
  aiRefImage?: string;
  aiRefMode?: "none" | "reference" | "frames";
  aiQuality?: "rapido" | "estandar" | "profesional";
  aiResolution?: "480p" | "720p" | "1080p";
  // Salió como BORRADOR (Profesional 480p): se puede pasar a 1080p mientras el archivo sea
  // el entregado y no venza (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p).
  aiBorrador?: BorradorDeClip;
  // "Pasar a 1080p" en curso sobre este clip (shimmer en la timeline, como Corregir voz);
  // se limpia al guardar si quedó a mitad.
  pasarA1080pStatus?: "processing" | "error";
  pasarA1080pError?: string;
  aiStatus?: AiClipStatus;
  // Mensaje del último fallo de generación. Se setea en el catch cuando
  // aiStatus pasa a "error" y se limpia al darle Reintentar (cuando vuelve
  // a "generating"). El botón Reintentar lo muestra como tooltip.
  aiError?: string;
  // ── Idempotencia de la generación IA (fix doble cobro por reintentos) ──
  // execution_id que viaja al backend. Mientras el último intento no haya
  // concluido (fallo TÉCNICO: timeout/red/202), se REUSA en el reintento con
  // los mismos parámetros: el backend dedupea por idempotency key y se
  // re-engancha al job en curso (o devuelve el video ya generado) en vez de
  // generar y cobrar otro. Se limpia en éxito y en fallo definitivo del
  // backend; si cambian los parámetros (paramsKey distinto) se descarta.
  // Ver ADR-2026-08-24-doble-cobro-video-creator-reintentos.
  aiExecutionId?: string;
  aiExecutionParamsKey?: string;
  // Warning para cuando el video/audio AI se generó correctamente y está
  // en Supabase Storage, pero el registro como asset del proyecto falló.
  // Caso problemático: el usuario ya pagó créditos, el archivo existe,
  // pero NO aparece en la biblioteca → si recarga la página lo pierde.
  // El badge en el clip de la timeline lo avisa.
  aiAssetWarning?: string;
  // Error al cargar la signed URL del archivo binario al abrir el proyecto.
  // Sin signed URL el clip no se puede reproducir — antes fallaba silencioso
  // y el clip aparecía "vacío" en la timeline sin explicación. Ahora ponemos
  // este flag para mostrar un badge con la opción de reintentar.
  loadError?: string;
  aiGenerationStartedAt?: number;
  // ── Traducción de video con lip-sync (HeyGen Video Translate v3) ───────
  // Flow async: el usuario clickea "Traducir al inglés" → frontend POST al
  // endpoint /start de n8n → n8n llama HeyGen y devuelve `jobId` → el
  // frontend polea /status cada 15s hasta que HeyGen termine. Cuando
  // termina, el clip se REEMPLAZA en la timeline con el video traducido
  // (mismo startTime + trackIndex, nuevo src/asset/duration).
  aiTranslating?: boolean;
  aiTranslateJobId?: string;
  aiTranslateError?: string;
  aiTranslateStartedAt?: number;
  // ── Borrado de objetos (Bria video-erase-object via n8n) ───────────────
  // Flow async idéntico al de traducción: el Director llama erase_object →
  // POST a bria_erase_start → jobId → poll a bria_erase_status → cuando
  // termina, el clip se REEMPLAZA in-place con el video borrado (mismo
  // startTime/trackIndex/mediaOffset, nuevo src/storagePath/assetId).
  // `eraseStatus` "processing" muestra un shimmer en la timeline.
  eraseStatus?: "processing" | "error";
  eraseJobId?: string;
  eraseError?: string;
  eraseStartedAt?: number;
  // ── Corregir voz (atlas: clon efímero + TTS por oración, empalme) ─────
  // Mismo flujo async que el borrado: el agente llama corregir_voz_en_clip →
  // /start → jobId → poll /status → el clip se REEMPLAZA in-place (mismo
  // startTime/trackIndex/mediaOffset, nuevo src/storagePath). "processing"
  // muestra un shimmer. Gratis. Sirve para clips de video Y de audio.
  corregirVozStatus?: "processing" | "error";
  corregirVozJobId?: string;
  corregirVozError?: string;
  // storagePath del clip ANTES de borrar — para revertir al original (el
  // resultado borrado ya quedó como asset en Archivos, así que revertir es
  // solo visual y no cuesta créditos de nuevo).
  originalStoragePathBeforeErase?: string;
  originalSrcBeforeErase?: string;
  // ── Cambiar voz (S2S ElevenLabs) — clip de audio (Fase 1) ──────────────
  // Flow async idéntico a traducción/borrado: el usuario elige una voz en el
  // ClipVoicePicker → S2S sobre el audio del clip (preserva ritmo/entonación)
  // → el clip se REEMPLAZA in-place con el audio de la voz nueva.
  // `voiceChangeStatus` "processing" muestra un shimmer en la timeline.
  voiceChangeStatus?: "processing" | "error";
  voiceChangeError?: string;
  voiceChangeStartedAt?: number;
  aiSceneMode?: "general" | "personas";
  aiPersonType?: "static" | "motion";
  // Dialog-mode only: the spoken dialogue (while aiPrompt is the visual scene prompt).
  aiDialogue?: string;
  // Dialog-mode only: speech rate multiplier (0.5 .. 1.5). Default 1.1.
  aiSpeechSpeed?: number;
  // Dialog-mode only: how the user provided the spoken content.
  // `text` (default) means the spoken text is in aiPrompt/aiDialogue and the
  // workflow synthesizes it via ElevenLabs TTS. `audio` means the user
  // uploaded/recorded their own audio, which lives in aiDialogueAudio* fields
  // and gets sent through ElevenLabs speech-to-speech instead of TTS.
  aiDialogueInputMode?: "text" | "audio";
  aiDialogueAudioUrl?: string;
  aiDialogueAudioStoragePath?: string;
  aiDialogueAudioDurationSec?: number;
  aiDialogueAudioAssetId?: string;
  // Multiescena (legacy): el panel ya no la arma
  // (ADR-2026-10-05-el-estudio-sin-multiescena-y-con-el-slider-de-redes), pero los
  // clips viejos la conservan para poder regenerarse igual que se pidieron.
  aiMultiEnabled?: boolean;
  aiMultiScenes?: Array<{ prompt: string; duration: number }>;
  // El prompt ya es del escritor del Creador ("Mejorar prompt", dejado igual o casi): el
  // backend no lo vuelve a escribir
  // (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
  aiPromptEscrito?: boolean;
  // AI audio clip fields
  aiAudioType?: "voice" | "music" | "sfx";
  aiVoiceId?: string;
  aiVoiceName?: string;
  aiInstrumentalOnly?: boolean;
  // Row index within the same clip category (currently only used for audio to support multiple audio tracks). Defaults to 0.
  trackIndex?: number;
  // ── Encuadre manual del clip dentro del cuadro (video / ai-generated) ────
  // Lo que el usuario arma desde el preview: escalar (manijas de esquina,
  // siempre proporcional), recortar (manijas de los lados), girar (manija de
  // rotación) y mover (arrastrando el clip). `undefined` = el clip llena el
  // cuadro como siempre, que es lo que tienen todos los proyectos viejos.
  // La matemática vive en `lib/encuadreDeClip.ts` (espejada en Remotion para
  // que el MP4 salga igual). Ver ADR-2026-09-15-el-encuadre-del-clip.
  encuadre?: EncuadreDeClip;
  // URL ESTABLE de una capa de imagen que no vive en los assets del proyecto
  // (hoy: el logo de la marca, que sale de `brand_profiles`). Hace falta un
  // campo propio porque `src` se descarta al guardar —son URLs firmadas que
  // vencen— y se reconstruye al abrir desde `storagePath`/`assetId`; un logo no
  // tiene ninguno de los dos, así que sin esto la capa desaparecía al recargar.
  imageUrl?: string;
  // Ancho/alto natural de una capa de imagen. El editor lo mide del archivo al
  // cargarlo, pero además se GUARDA porque el render del MP4 no tiene DOM del
  // que medirlo y lo necesita para recortar donde corresponde (el recorte se
  // expresa sobre la imagen, que con `contain` no llena su caja).
  imageAspect?: number;
  // Opacidad de la capa, 0..1 (el panel la muestra de 0 a 100). `undefined` =
  // opaca, que es como se vieron siempre los clips. Se multiplica con la
  // opacidad de las transiciones en vez de pisarla: son dos cosas distintas
  // —una la elige el usuario para toda la capa, la otra dura lo que dura el
  // fundido— y aplicarlas en wrappers anidados las compone sola.
  opacity?: number;
  // ── Text clip fields (only meaningful when type === 'text') ───────────────
  // The actual rendered string. Multi-line allowed via "\n".
  text?: string;
  // CSS font-family value (no quotes). Must match one of TEXT_FONT_OPTIONS or
  // any system font; rendering uses the value verbatim with a sensible
  // fallback stack appended in the renderer.
  fontFamily?: string;
  // Font size as a percentage of the canvas height (1..40). We store a
  // resolution-independent number so the same clip looks identical on 16:9,
  // 9:16, etc., regardless of preview pixel size.
  fontSizePct?: number;
  fontWeight?: "normal" | "bold";
  fontStyle?: "normal" | "italic";
  // Text fill color. Hex or any CSS color.
  textColor?: string;
  // Text alignment for multi-line strings.
  textAlign?: "left" | "center" | "right";
  // Position of the text's *center* as a percentage of canvas (0..100). The
  // renderer translates by -50%/-50% so the text is anchored from its center,
  // which makes drag math simple and lets us clamp 0..100 against the canvas.
  textXPct?: number;
  textYPct?: number;
  // Ancho de la caja del texto como porcentaje del ancho de la canvas
  // (0..100). Cuando está seteado, el wrapper del texto fija ese ancho
  // y el contenido hace word-wrap dentro — *sin* cambiar el font-size.
  // Esto desacopla "tamaño visual del texto" (font) de "ancho de la
  // caja" (resize manual desde el preview). Cuando es undefined, el
  // wrapper usa su ancho natural acotado por `maxWidth`.
  textWidthPct?: number;
  // Giro del elemento en GRADOS (-180..180), el mismo modelo que
  // `encuadre.rotation` de los clips de video. Vive suelto y no adentro de
  // `encuadre` porque un texto no tiene recorte ni escala de encuadre: lo
  // único que comparte con el video es el giro.
  // Ver ADR-2026-09-15-el-marco-de-los-elementos-es-el-mismo-que-el-del-video.
  elementRotation?: number;
  // ── Subtitle group fields (only meaningful when type === 'text') ─────────
  // When `isSubtitle` is true the clip behaves like a normal text clip but
  // is rendered on its own dedicated timeline row and shares its style
  // with every other subtitle clip in the same `subtitleGroupId`. Editing
  // one (font, size, color, alignment, position, animations) propagates
  // to siblings — except for clips with `subtitleStyleOverride: true`,
  // which opt out of the group sync (both as source and as target).
  isSubtitle?: boolean;
  subtitleGroupId?: string;
  subtitleStyleOverride?: boolean;
  // ── Linked-subtitle fields ───────────────────────────────────────────────
  // When a subtitle text clip is generated from a video/audio clip, we
  // remember the source and the word timings in *source-media* seconds
  // (i.e. absolute to the original file, not to the clip's trimmed view).
  // A useEffect below reconciles `startTime`/`duration` of every linked
  // subtitle whenever its source clip is moved, resized or trimmed, so the
  // captions follow the clip visually. Clips whose linked range falls
  // entirely outside the source's trimmed window are kept with a tiny
  // duration so the user can restore them by undoing the trim.
  linkedClipId?: string;
  linkedMediaStart?: number;
  linkedMediaEnd?: number;
  // ── Transcript (only meaningful for video/audio/ai-generated audio) ──────
  // Full word-level transcript of the *source media*, with timestamps in
  // source-media seconds (absolute to the original file, NOT to the trimmed
  // clip view). Stored once per source clip; trims/splits never mutate it —
  // consumers are expected to filter words by `[mediaOffset, mediaOffset +
  // duration]` to get the slice that's actually visible on the timeline.
  // Populated on demand by the `generate_transcript` agent tool, and as a
  // side-effect of running the "Generar subtítulos" flow on this clip.
  transcript?: TranscriptResult;
  // ── Animation clip fields (only meaningful when type === 'animation') ──
  // Id of the template in `ANIMATION_TEMPLATES`. The renderer resolves it
  // through `getAnimationTemplate()` so unknown templates render nothing
  // (forward-compat: opening a project saved with a newer template just
  // shows an empty clip instead of crashing).
  animationTemplate?: string;
  // Per-instance overrides on top of the template defaults. Shape depends
  // on the template; the agent will validate this against the template's
  // schema before writing it.
  animationProps?: Record<string, unknown>;
  // Position of the animation's *center* as percentage of canvas (0..100).
  // Mismo modelo que `textXPct/YPct`: anclado al centro, así el clamp es
  // simétrico y la math del drag es trivial. Si no está seteado, se asume
  // 50/50 (centro), que respeta el comportamiento previo.
  animXPct?: number;
  animYPct?: number;
  // Escala uniforme aplicada al wrapper de la animación (1 = ocupa toda
  // la canvas, igual que antes; 0.5 = la mitad). Sin esto no había forma
  // de mover una animación: a 100% no hay espacio para correrla.
  animScale?: number;
  // Animación escrita por el agente a pedido del usuario, en vez de plantilla:
  // el código (con su historial de versiones), el tipo, las fotos que recibe
  // por props y la marca congelada. Un clip `animation` tiene plantilla O
  // código. Ver ADR-2026-09-24-animacion-por-codigo.
  animationCodigo?: AnimacionPorCodigo;
  // ── Motion (entrada / salida) ─────────────────────────────────────
  // Aplica a clips de tipo 'text', 'animation' e 'image'
  // (ADR-2026-09-27-las-capas-de-imagen-entran-y-salen). El renderer compone
  // estos efectos sobre el transform base del wrapper, sin tocar el
  // contenido del template, el texto ni el encuadre de la imagen. Ver
  // `clipMotion.ts` para la lista de presets disponibles. Los presets de
  // texto-only (typewriter, char-cascade, word-cascade) se muestran
  // únicamente en los dropdowns de clips de texto.
  enterAnimation?: string;
  enterDurationSec?: number;
  exitAnimation?: string;
  exitDurationSec?: number;
  // ── Incoming transition (only meaningful for video / ai-generated clips) ─
  // The transition is owned by the SECOND clip in an adjacent pair. When
  // present, the renderer cross-fades from the previous clip on the same
  // track during the first `durationSec` seconds of this clip — the
  // overlap is created by ripple-shifting this clip back when the
  // transition is added (so visually the two clips occupy the same
  // window of timeline time during the fade).
  transitionIn?: {
    type: TransitionType;
    durationSec: number;
  };
}

type TransitionType =
  | "crossfade"
  | "fade-to-black"
  | "slide" // B enters from the right
  | "slide-left" // B enters from the left
  | "slide-up" // B enters from the bottom (moving up)
  | "slide-down" // B enters from the top (moving down)
  | "zoom" // B grows from a small scale at center, fading in
  | "reveal" // White swipe-line travels across the frame revealing B
  | "diapositivas" // Both clips morph into a rounded "slide" rectangle, then slide past each other
  | "stretch-horizontal" // A stretches wide, cut, B comes in stretched and settles
  | "stretch-vertical" // Same but on the vertical axis
  // Overlay-style transitions — all use the same A/B opacity cut logic
  // and only differ in the .mp4 they composite via screen-blend.
  | "light-leak"
  | "plastic"
  | "distorsion"
  | "vhs"
  | "vintage"
  | "glitch";

// Las sub-pantallas: paneles que se abren DESDE otro y tienen que poder
// volver a él. Sin esto, la única salida era la X, que cierra todo y deja al
// usuario de vuelta en el editor pelado en vez de en el panel del clip desde el
// que entró. Ver ADR-2026-09-16-las-sub-pantallas-vuelven-a-su-panel.
const PANEL_DEL_QUE_VIENE = {
  "upscale-video": "clip",
  "edit-video-ia": "clip",
  silencios: "clip",
  voz: "clip",
  "corregir-voz": "clip",
} as const;

type ToolPanel =
  | "none"
  | "root"
  | "ai-generate"
  | "ai-scene"
  | "ai-audio"
  | "ai-image-gen"
  | "upload"
  | "audio"
  | "text"
  // "Animación gráfica": un prompt y un toggle; el generador escribe el
  // componente (ADR-2026-09-24-animacion-grafica-desde-el-panel).
  | "animacion-grafica"
  | "effects"
  | "files"
  | "agent-upload"
  | "animation"
  | "brand"
  | "transitions"
  | "erase-brush"
  | "edit-video-ia"
  | "upscale-video"
  | "silencios"
  // "Cambiar voz": sub-pantalla del panel del clip, como silencios. Antes era
  // un segundo panel de 380px al costado.
  // Ver ADR-2026-09-16-cambiar-voz-es-una-sub-pantalla.
  | "voz"
  // "Corregir voz": la misma corrección que el agente hace con
  // corregir_voz_en_clip, a mano. Ver ADR-2026-09-24-corregir-voz-es-un-boton-del-estudio.
  | "corregir-voz"
  // Panel de edición del clip seleccionado: lo que antes era la barra
  // flotante sobre el preview. Ver ADR-2026-09-15-panel-del-clip-a-la-izquierda.
  | "clip"
  // El mismo panel, cuando se soltó la selección: en vez de cerrarse (y
  // hacer saltar todo el editor 320px) se queda abierto con un estado vacío
  // que dice qué hacer.
  | "sin-seleccion";
// Sub-tabs for the unified "Archivos" panel: each surface lists assets of
// one type. The 'image' tab merges uploaded image assets with AI-generated
// images so the user has a single place to grab any visual.
// Sin "escenas": el catálogo de fondos de ATLAS dejó de tener pestaña propia
// acá (ni en el panel ni en el modo selección); tampoco la tiene el selector
// centrado (`SelectorDeImagenModal`) que usan los slots y "Añadir" de imagen.
type FilesTab = "media" | "audio" | "animaciones" | "productos" | "historial";

// ── Ámbito del panel de Archivos ──────────────────────────────────────────
// De dónde sale el material que se está mirando:
//   · `proyecto` → lo que se subió o se generó DENTRO de este proyecto y vive
//     en sus assets; se va con el proyecto.
//   · `atlas` → lo que existe en la cuenta y sirve para cualquier proyecto:
//     las Creaciones de la plataforma y el catálogo de productos de la marca.
// Es la pregunta que el usuario se hace primero ("¿esto lo tengo acá o lo
// tengo en Atlas?"), así que manda por encima del filtro por tipo.
type AmbitoDeArchivos = "proyecto" | "atlas";

const PESTANAS_POR_AMBITO: Record<AmbitoDeArchivos, readonly FilesTab[]> = {
  proyecto: ["media", "audio", "animaciones"],
  // Sin "productos": el catálogo de la marca se mudó al panel **Marca**, que es
  // donde el usuario va a buscar lo suyo (logos, productos, personajes). Acá
  // quedó una sola carpeta, así que "Creaciones" deja de ser una pastilla y se
  // muestra como título, igual que "Videos" e "Imágenes" más abajo.
  // Ver ADR-2026-09-16-las-carpetas-de-la-marca.
  atlas: ["historial"],
};

// Cómo se llama y con qué icono se muestra cada carpeta del panel de Archivos.
// Estaban como ternarios repetidos en los tres lugares que dibujan pastillas.
const ETIQUETA_DE_PESTANA: Record<FilesTab, string> = {
  media: "Multimedia",
  audio: "Audio",
  animaciones: "Animaciones",
  productos: "Productos",
  historial: "Creaciones",
};


// ── Carpetas del panel de Marca ───────────────────────────────────────────
// Lo que la marca YA tiene cargado en Atlas, cada cosa en su carpeta: los
// logos (brand_assets 'logo' + el logo principal del perfil), el catálogo de
// productos, los personajes (los modelos de la cuenta) y el moodboard (los
// tableros de referencia que se arman en la plataforma).
type CarpetaDeMarca = "logos" | "productos" | "personajes" | "moodboard";
const CARPETAS_DE_MARCA: ReadonlyArray<{
  id: CarpetaDeMarca;
  label: string;
}> = [
  { id: "logos", label: "Logos" },
  { id: "productos", label: "Productos" },
  // El Estudio decía "Modelos" para todas las marcas, desde antes de que la palabra
  // siguiera al rubro. Ahora sale del mismo módulo que el resto de la app: "Modelos" en
  // Moda, "Avatares" en los demás rubros
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo). Es un `get` porque la tabla se
  // arma al cargar el módulo; el `id` es interno y no se toca.
  {
    id: "personajes",
    get label() {
      return palabraDeModeloActual().tituloPlural;
    },
  },
  { id: "moodboard", label: "Estilos" },
];

// Qué muestra Creaciones. Son dos PASTILLAS sobre la misma pestaña de datos
// (`historial`, que trae videos e imágenes juntos): separarlas de verdad, en dos
// `FilesTab`, obligaría a tocar los ~25 lugares que nombran "historial"
// (efectos de carga, estados de importación, condiciones de render) sin cambiar
// nada de lo que se ve.
// Ver ADR-2026-09-16-creaciones-se-parte-en-videos-e-imagenes.
// "Todas" (por defecto) muestra los dos: videos arriba, imágenes abajo. Lo pidió
// Nacho en la daily del 3/11 (ADR-2026-11-03-archivos-abre-en-todas).
type MuestraDeCreaciones = "todas" | "imagenes" | "videos";

const PASTILLAS_DE_CREACIONES: ReadonlyArray<{
  id: MuestraDeCreaciones;
  label: string;
}> = [
  { id: "todas", label: "Todas" },
  { id: "imagenes", label: "Imágenes" },
  { id: "videos", label: "Videos" },
];

const OPCIONES_DE_AMBITO = [
  { id: "proyecto" as const, label: "Este proyecto" },
  { id: "atlas" as const, label: "Atlas" },
];

const ambitoDeLaPestana = (t: FilesTab): AmbitoDeArchivos =>
  (PESTANAS_POR_AMBITO.proyecto as readonly string[]).includes(t)
    ? "proyecto"
    : "atlas";

// ── Text clip catalogue ────────────────────────────────────────────────────
// Curated list of fonts loaded via Google Fonts in index.html. The `family`
// is the exact CSS font-family token; `category` decides the fallback stack
// applied at render time so unloaded fonts still look reasonable.
// TEXT_FONT_OPTIONS + FontCategory moved to src/lib/fontOptions.ts (shared with
// the brand typography editor in Configuración). Imported at the top.
// Brand colors can arrive in two shapes: the editor's own `string[]` of hex
// codes, or Estudio Virtual's richer `{ hex, name, role }[]` objects. The whole
// Marca → Colores UI (and the agent color tool) assume plain hex strings, so we
// normalize on read. Anything without a usable hex is dropped. This is read-only
// coercion; unifying the stored format lives in the brand-identity work item.
const normalizeBrandColors = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c): string | null => {
      if (typeof c === "string") return c.trim() || null;
      if (c && typeof c === "object") {
        const hex = (c as { hex?: unknown }).hex;
        if (typeof hex === "string") return hex.trim() || null;
      }
      return null;
    })
    .filter((c): c is string => c !== null);
};
// Toolbar-friendly numeric input that keeps a local string draft so the user
// can clear the field and retype without each keystroke being clamped /
// re-validated. We only commit (clamp + onCommit) on blur or Enter; Escape
// reverts to the canonical value. Externally-driven changes to `value` (e.g.
// the +/- stepper buttons) re-sync the draft when the field is not focused.
type NumberFieldProps = {
  value: number;
  min: number;
  max: number;
  step?: number;
  decimals?: number;
  onCommit: (n: number) => void;
  className?: string;
  ariaLabel?: string;
};
const NumberField: React.FC<NumberFieldProps> = ({
  value,
  min,
  max,
  step,
  decimals = 1,
  onCommit,
  className,
  ariaLabel,
}) => {
  const format = (n: number) => {
    const fixed = n.toFixed(decimals);
    // Trim trailing ".0" so integers stay clean ("16" not "16.0").
    return fixed.replace(/\.0+$/, "");
  };
  const [draft, setDraft] = React.useState<string>(() => format(value));
  const focusedRef = React.useRef(false);
  // Re-sync from prop only while the input isn't focused (so external
  // updates like +/- buttons reflect, but we never clobber what the user
  // is typing).
  React.useEffect(() => {
    if (!focusedRef.current) setDraft(format(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = () => {
    const trimmed = draft.trim().replace(",", ".");
    const parsed = parseFloat(trimmed);
    if (!isFinite(parsed)) {
      setDraft(format(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, parsed));
    setDraft(format(clamped));
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        focusedRef.current = true;
        e.currentTarget.select();
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        } else if (e.key === "Escape") {
          setDraft(format(value));
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
      step={step}
      className={className}
      aria-label={ariaLabel}
    />
  );
};
// ─── Piezas del panel de edición del clip ───────────────────────────────────
// El panel de la izquierda muestra qué se le puede hacer al clip que está
// seleccionado en la timeline. Tres piezas le dan la forma: una sección con
// título, una fila "etiqueta → control", y una lista de acciones donde cada
// una entra con su icono, su nombre y una línea de qué hace. Son SOLO
// presentación: cada acción sigue llamando al mismo handler que tenía en la
// barra flotante.
const SeccionDelPanel: React.FC<{
  titulo: string;
  children: React.ReactNode;
}> = ({ titulo, children }) => (
  <section className="mb-5 last:mb-0">
    <h4 className="text-[14px] font-semibold text-brand-black/90 mb-2.5">
      {titulo}
    </h4>
    <div className="space-y-2">{children}</div>
  </section>
);

const FilaDelPanel: React.FC<{
  etiqueta: string;
  anchoEtiqueta?: number;
  children: React.ReactNode;
}> = ({ etiqueta, anchoEtiqueta = 72, children }) => (
  <div className="flex items-center gap-3 min-h-[36px]">
    {/* `anchoEtiqueta` es un MÍNIMO, no un ancho fijo: alinea las etiquetas
    cortas en una columna, y la que no entra (ej. "Tamaño y posición") se
    estira en vez de partirse en dos líneas. El control de al lado tiene
    `min-w-0`, así que cede el espacio. */}
    <span
      className="text-[12px] text-neutral-500 shrink-0 leading-tight whitespace-nowrap"
      style={{ minWidth: anchoEtiqueta }}
    >
      {etiqueta}
    </span>
    <div className="flex-1 min-w-0 flex items-center gap-2">{children}</div>
  </div>
);

type AccionDeClip = {
  label: string;
  /** Una sola palabra para la barra flotante del preview, donde el espacio es
   *  horizontal y compartido. El `label` completo queda para el tooltip. */
  corto?: string;
  desc?: string;
  icon: React.ReactNode;
  // Clases del cuadradito del icono. Sin tono, la acción es "neutra" (gris):
  // los colores quedan para las herramientas de IA, que es donde ayudan a
  // distinguir una de otra de un vistazo.
  tono?: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  note?: string;
  danger?: boolean;
};

const ListaDeAcciones: React.FC<{ items: AccionDeClip[] }> = ({ items }) => (
  <div className="rounded-lg border border-black/[0.08] overflow-hidden bg-surface-card">
    {items.map((it, i) => (
      <button
        key={`${it.label}-${i}`}
        type="button"
        onClick={it.onClick}
        disabled={it.disabled}
        title={it.title}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
          i > 0 ? "border-t border-black/[0.06]" : ""
        } ${it.danger ? "hover:bg-red-50" : "hover:bg-black/[0.03]"}`}
      >
        {/* El cuadrito mide 32px = 17 del título + 15 de la descripción, así
        que su alto es exactamente el del bloque de texto de al lado. Los dos
        `leading` van escritos: sin ellos el alto lo decide la fuente y el
        cuadrado deja de coincidir. El tamaño del ícono también se fija acá y
        no en cada item: los callers pasan el suyo con `w-4 h-4` y son
        decenas. */}
        <span
          className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5 ${
            it.tono ??
            (it.danger
              ? "bg-red-50 text-red-600"
              : "bg-black/[0.05] text-brand-black/70")
          }`}
        >
          {it.icon}
        </span>
        <span className="flex-1 min-w-0">
          <span
            className={`block text-[12.5px] leading-[17px] font-semibold truncate ${
              it.danger ? "text-red-600" : "text-brand-black/90"
            }`}
          >
            {it.label}
          </span>
          {it.desc && (
            <span className="block text-[11.5px] leading-[15px] text-neutral-500 truncate">
              {it.desc}
            </span>
          )}
        </span>
        {it.note && (
          <span className="text-[11px] text-neutral-400 shrink-0">
            {it.note}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ─── FontPicker ─────────────────────────────────────────────────────────────
// Custom dropdown for picking a text-clip font family. We don't use the
// native <select> here because each option needs to be rendered in its own
// font family so the user can preview the face before committing to it
// (browsers do not consistently honor `font-family` on <option> elements).
type FontPickerProps = {
  value: string;
  onChange: (family: string) => void;
  triggerClassName: string;
  panelAlign?: "left" | "right" | "center";
  panelMinWidth?: number;
  ariaLabel?: string;
};
const FontPicker: React.FC<FontPickerProps> = ({
  value,
  onChange,
  triggerClassName,
  panelAlign = "left",
  panelMinWidth = 220,
  ariaLabel,
}) => {
  const [open, setOpen] = React.useState(false);
  // Lo tipeado en el buscador del panel. Se limpia al abrir y al cerrar: con
  // 50 fuentes, encontrarse el filtro de la vez pasada puesto es peor que
  // volver a escribir tres letras.
  const [busqueda, setBusqueda] = React.useState("");
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const currentLabel =
    TEXT_FONT_OPTIONS.find((o) => o.family === value)?.label || value;
  // La lista alfabética viene del catálogo (`TEXT_FONT_OPTIONS_ALFABETICAS`):
  // el array crudo está agrupado por tanda y ese orden lo compara el espejo
  // del render, así que el abecedario vive aparte.
  const opcionesOrdenadas = TEXT_FONT_OPTIONS_ALFABETICAS;
  // "Fuentes de la marca" = las tipografías que el usuario SUBIÓ
  // (`brand_assets` con `tipo='font'`), y nada más. Antes la sección también
  // listaba las familias del perfil de marca —los roles (`titulo`, `cuerpo`) y
  // las `suggested` del onboarding—, así que aparecía siempre, incluso sin
  // haber subido nada, con nombres que muchas veces son del catálogo general
  // (y entonces salían dos veces) o texto libre del onboarding que ni siquiera
  // se puede renderizar. Sin fuentes subidas la sección directamente no se
  // dibuja. Ver ADR-2026-09-18-fuentes-de-la-marca-son-las-que-se-subieron.
  const customBrandFonts = useContext(BrandCustomFontsContext);
  const brandFamilies = React.useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const fam of customBrandFonts) {
      if (!fam || typeof fam !== "string") continue;
      const trimmed = fam.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(trimmed);
    }
    return out;
  }, [customBrandFonts]);
  React.useEffect(() => {
    setBusqueda("");
  }, [open]);
  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const panelPos =
    panelAlign === "right"
      ? "right-0"
      : panelAlign === "center"
        ? "left-1/2 -translate-x-1/2"
        : "left-0";
  // Filtro por nombre, sin distinguir mayúsculas ni espacios de más.
  const coincide = React.useCallback(
    (nombre: string) => {
      const q = busqueda.trim().toLowerCase();
      if (!q) return true;
      return nombre.toLowerCase().includes(q);
    },
    [busqueda],
  );
  const familiasDeMarcaVisibles = brandFamilies.filter(coincide);
  const opcionesVisibles = opcionesOrdenadas.filter((o) =>
    coincide(o.label || o.family),
  );
  // Enter elige la primera de la lista filtrada: escribir "oswa" + Enter tiene
  // que alcanzar, sin obligar a soltar el teclado para ir a buscar el click.
  const elegirLaPrimera = () => {
    const primera = familiasDeMarcaVisibles[0] ?? opcionesVisibles[0]?.family;
    if (!primera) return;
    onChange(primera);
    setOpen(false);
  };
  return (
    <div ref={wrapperRef} className="relative" data-clip-ui="true">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName}
        style={{ fontFamily: fontStackFor(value) }}
        title="Fuente"
        aria-label={ariaLabel || "Fuente"}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex-1 truncate text-left">{currentLabel}</span>
        <ChevronDown className="w-3 h-3 text-neutral-400 shrink-0" />
      </button>
      {open && (
        <div
          role="listbox"
          className={`absolute top-full mt-1.5 z-30 ${panelPos} bg-white border border-neutral-200 rounded-lg shadow-md overflow-hidden`}
          /* `width: 100%` = el ancho del trigger, que es lo que se espera de un
             dropdown. `minWidth` queda como piso para el único lugar donde el
             trigger mide 160px y los nombres no entrarían. */
          style={{ width: "100%", minWidth: panelMinWidth }}
        >
          {/* Buscador — con 50 familias, recorrer la lista a ojo no es un
          plan. Queda FIJO arriba (el scroll es del listado de abajo) y se
          enfoca solo al abrir. */}
          <div className="p-1.5 border-b border-neutral-200/70">
            <input
              type="text"
              value={busqueda}
              autoFocus
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  elegirLaPrimera();
                }
              }}
              placeholder="Buscar fuente…"
              aria-label="Buscar fuente por nombre"
              className="w-full h-8 px-2.5 rounded-md bg-black/[0.04] text-[13px] text-brand-black/85 placeholder:text-brand-black/35 focus:outline-none focus:bg-black/[0.06] transition-colors"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
          {familiasDeMarcaVisibles.length === 0 &&
            opcionesVisibles.length === 0 && (
              <p className="px-3 py-3 text-[13px] text-neutral-400">
                Ninguna fuente se llama así.
              </p>
            )}
          {familiasDeMarcaVisibles.length > 0 && (
            <>
              <div className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-wider font-medium text-neutral-400">
                Fuentes de la marca
              </div>
              {familiasDeMarcaVisibles.map((fam) => {
                const isActive =
                  fam.toLowerCase() === (value || "").toLowerCase();
                const label =
                  TEXT_FONT_OPTIONS.find(
                    (o) => o.family.toLowerCase() === fam.toLowerCase(),
                  )?.label || fam;
                return (
                  <button
                    key={`brand-${fam}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    onClick={() => {
                      onChange(fam);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[14px] transition-colors ${
                      isActive
                        ? "bg-neutral-100 text-neutral-900"
                        : "text-neutral-800 hover:bg-neutral-50"
                    }`}
                    style={{ fontFamily: fontStackFor(fam) }}
                  >
                    <span className="truncate text-left">{label}</span>
                    {isActive && (
                      <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                    )}
                  </button>
                );
              })}
              <div className="my-1 mx-2 h-px bg-neutral-200" />
              <div className="px-3 pt-0.5 pb-1 text-[10px] uppercase tracking-wider font-medium text-neutral-400">
                Generales
              </div>
            </>
          )}
          {opcionesVisibles.map((opt) => {
            const isActive = opt.family === value;
            return (
              <button
                key={opt.family}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  onChange(opt.family);
                  setOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[14px] transition-colors ${
                  isActive
                    ? "bg-neutral-100 text-neutral-900"
                    : "text-neutral-800 hover:bg-neutral-50"
                }`}
                style={{ fontFamily: fontStackFor(opt.family) }}
              >
                <span className="truncate text-left">
                  {opt.label || opt.family}
                </span>
                {isActive && (
                  <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                )}
              </button>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
};
// ─── SubirTipografia ────────────────────────────────────────────────────────
// Subir una fuente propia de la marca (.woff2/.woff/.ttf/.otf). Vive AFUERA del
// dropdown de fuentes, debajo del campo: adentro era un pie del panel flotante,
// o sea que para descubrirlo había que abrir la lista y llegar hasta abajo, y
// una vez subida la fuente el panel se cerraba solo — el usuario no veía dónde
// había quedado. Afuera, el botón está siempre a la vista y lo que sube aparece
// en la sección "Fuentes de la marca" del dropdown, que es donde va a buscarla
// la próxima vez. Ver ADR-2026-09-18-fuentes-de-la-marca-son-las-que-se-subieron.
const SubirTipografia: React.FC<{
  /** Sube el archivo y devuelve la familia registrada (o `null` si falló). */
  onSubir: (file: File) => Promise<string | null>;
  /** Qué hacer con la fuente recién subida: el panel la deja elegida. */
  onSubida: (family: string) => void;
}> = ({ onSubir, onSubida }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          // El value se limpia SIEMPRE: sin esto, subir el mismo archivo dos
          // veces seguidas no dispara el change.
          e.target.value = "";
          if (!file) return;
          setError(null);
          setSubiendo(true);
          try {
            const family = await onSubir(file);
            // Nadie sube una fuente para no usarla: queda elegida.
            if (family) onSubida(family);
            else setError("No se pudo subir la tipografía.");
          } catch (err) {
            setError(
              err instanceof Error
                ? err.message
                : "No se pudo subir la tipografía.",
            );
          } finally {
            setSubiendo(false);
          }
        }}
      />
      <button
        type="button"
        disabled={subiendo}
        onClick={() => inputRef.current?.click()}
        /* Azul y no gris: es la acción que trae algo NUEVO al proyecto, y
           arriba del dropdown tiene que leerse como un botón y no como el
           borde de otro campo. Los colores van con el hex `var(--brand-primary)` y no con
           el token `brand-primary`: los tokens `brand-*` no llevan
           `<alpha-value>`, así que con el modificador de opacidad Tailwind no
           emite ninguna regla y el borde y el fondo quedaban transparentes. */
        className="w-full flex items-center justify-center gap-1.5 h-11 rounded-md border border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.45)] bg-[rgb(var(--brand-primary-rgb)_/_0.06)] text-[13px] font-medium text-brand-primary hover:border-[rgb(var(--brand-primary-rgb)_/_0.7)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.1)] transition-colors disabled:opacity-60"
      >
        {subiendo ? (
          <>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Subiendo…
          </>
        ) : (
          <>
            <Upload className="w-3.5 h-3.5" />
            Subir tipografía
          </>
        )}
      </button>
      {error ? (
        <p className="mt-1 px-0.5 text-[11px] text-red-600 leading-tight">
          {error}
        </p>
      ) : (
        <p className="mt-1 px-0.5 text-[10.5px] text-neutral-400 leading-tight">
          .woff2, .woff, .ttf u .otf — queda guardada en la marca.
        </p>
      )}
    </div>
  );
};

// Active brand palette surfaced to every color picker in the editor.
// Provided once at the VideoEditor level (with `brandProfile?.colors`) so
// nested components — including the ones rendered by sub-modules like the
// animation editor — can pull the palette from context instead of every
// caller having to thread the prop manually. An explicit `brandColors`
// prop on a picker still overrides the context, useful in a future world
// where a panel might want to scope the palette differently.
const BrandColorsContext = createContext<string[]>([]);

// Same idea as `BrandColorsContext` but for the brand typography. Stores a
// partial map of role → font family (matching `brandProfile?.fonts`). The
// FontPicker pulls this so it can render a "Fuentes de la marca" section at
// the top of its dropdown without callers having to thread a prop. Empty
// object when no brand profile exists.
const BrandFontsContext = createContext<Record<string, string | undefined>>({});

// Custom font families uploaded by the user (brand_assets with tipo='font').
// The values are the CSS family identifiers — already registered as
// @font-face at the editor root, so any consumer can render text in them
// just by setting `font-family`. Surfaced in the same "Fuentes de la marca"
// section of the FontPicker as the role-assigned fonts.
const BrandCustomFontsContext = createContext<string[]>([]);

// Quick palette for the color swatch grid in the text panel. The user can
// always type a custom hex.
const TEXT_COLOR_PRESETS: string[] = [
  "#FFFFFF",
  "#000000",
  "#1644EB", // hex-de-marca-ok: paleta que elige el usuario
  "#F59E0B",
  "#EF4444",
  "#10B981",
  "#A855F7",
  "#EC4899",
  "#0EA5E9",
  "#FACC15",
];

/**
 * Tiny visual used to represent a transparent color in pickers — a light
 * checkerboard pattern with a red diagonal slash on top, matching the
 * universal "no fill" / "PNG transparency" convention.
 */
const TransparentSwatch: React.FC<{
  size: number;
  rounded?: string;
  bordered?: boolean;
}> = ({ size, rounded = "rounded-sm", bordered = false }) => (
  <span
    className={`relative inline-block ${rounded} overflow-hidden shrink-0 ${
      bordered ? "ring-1 ring-black/10" : ""
    }`}
    style={{
      width: size,
      height: size,
      // Checkerboard via two linear gradients tiled at 50% — small tiles
      // (1/3 of the swatch) so the pattern reads even at 24px.
      backgroundColor: "#fff",
      backgroundImage:
        "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)," +
        "linear-gradient(45deg, #d4d4d4 25%, transparent 25%, transparent 75%, #d4d4d4 75%, #d4d4d4)",
      backgroundSize: `${Math.max(6, Math.round(size / 2))}px ${Math.max(6, Math.round(size / 2))}px`,
      backgroundPosition: `0 0, ${Math.max(3, Math.round(size / 4))}px ${Math.max(3, Math.round(size / 4))}px`,
    }}
  >
    {/* Red diagonal slash. Anchored corner-to-corner so the visual is
        consistent at any swatch size. */}
    <span
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage:
          "linear-gradient(to top right, transparent calc(50% - 1px), #DC2626 calc(50% - 1px), #DC2626 calc(50% + 1px), transparent calc(50% + 1px))",
      }}
    />
  </span>
);

// Standard swatch picker used wherever the editor needs a color field. The
// trigger is a small chip showing the current color (with a downward chevron
// so it reads as a control). On click it pops a 6-column preset grid plus a
// dashed '+' tile that opens the OS color picker for arbitrary hex values.
// Visually identical to the text-toolbar color popover so the experience
// stays consistent across the app.
const ColorSwatchPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  /** Where to anchor the popover relative to the trigger. */
  align?: "left" | "right";
  /** Optional aria-label for the trigger button. */
  ariaLabel?: string;
  /** When true, the picker exposes a "transparent" swatch in the popover. */
  allowTransparent?: boolean;
  /**
   * Optional brand palette. If non-empty, rendered as a labeled row above the
   * generic presets so the user can pick a brand color in one click. Hex
   * strings; case-insensitive comparisons. Caller decides what counts as a
   * brand color (typically `brandProfile?.colors`).
   */
  brandColors?: string[];
  /**
   * When provided, replaces the hex code in the trigger with this static
   * label (e.g. "Color"). Useful when the picker sits inside a compact
   * toolbar and the hex would feel verbose. The popover content is
   * unchanged.
   */
  triggerLabel?: string;
}> = ({
  value,
  onChange,
  align = "right",
  ariaLabel,
  allowTransparent,
  brandColors,
  triggerLabel,
}) => {
  // Pull the brand palette from context when the caller doesn't pass an
  // explicit override. Empty array if the project has no brand profile.
  const ctxBrand = useContext(BrandColorsContext);
  const effectiveBrand = brandColors ?? ctxBrand;
  const isTransparent = value === "transparent";
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Close on outside click. Uses pointerdown so it fires before the global
  // 'click outside clip' listener and doesn't fight with native pickers.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        // `h-9` y no `h-8`: es la altura del resto de los controles del editor
        // (el stepper de tamaño, los campos numéricos, los botones de formato),
        // y el color se para al lado de ellos. Con 32 contra 36 la fila quedaba
        // despareja por 4px.
        className={`h-9 ${triggerLabel ? "pl-2 pr-1" : "pl-1 pr-2"} flex items-center gap-2 rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15"
            : // El MISMO gris que el resto de los controles del editor
              // (`border-neutral-200`, el del stepper de tamaño que va al
              // lado). El `black/[0.08]` de antes daba un gris más claro y en
              // la misma fila se veía que eran dos bordes distintos.
              "border-neutral-200 hover:border-neutral-300"
        }`}
        aria-label={ariaLabel || "Elegir color"}
        title={isTransparent ? "Transparente" : value}
      >
        {triggerLabel && (
          <span className="text-[12px] text-neutral-700">{triggerLabel}</span>
        )}
        {isTransparent ? (
          <TransparentSwatch size={24} bordered />
        ) : (
          <span
            className="w-6 h-6 rounded-sm shrink-0"
            style={{
              backgroundColor: value || "#000000",
              boxShadow:
                (value || "").toLowerCase() === "#ffffff"
                  ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                  : undefined,
            }}
          />
        )}
        {!triggerLabel && (
          <span className="text-[12px] text-neutral-700 font-mono tabular-nums">
            {isTransparent ? "Sin fondo" : value || "—"}
          </span>
        )}
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className={`absolute top-full mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-2 ${
            align === "right" ? "right-0" : "left-0"
          }`}
          style={{ width: 200 }}
        >
          {(() => {
            // Dedupe brand colors (case-insensitive) and filter out anything
            // that isn't a plausible hex so a malformed entry can't crash
            // the row. Only render the section when there's at least one.
            const seen = new Set<string>();
            const cleanBrand = (effectiveBrand || [])
              .map((c) => (typeof c === "string" ? c.trim() : ""))
              .filter((c) => /^#?[0-9a-fA-F]{3,8}$/.test(c))
              .map((c) => (c.startsWith("#") ? c : `#${c}`))
              .filter((c) => {
                const k = c.toLowerCase();
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              });
            if (cleanBrand.length === 0) return null;
            return (
              <>
                <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                  De tu marca
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {cleanBrand.map((color) => {
                    const isActive =
                      !isTransparent &&
                      color.toLowerCase() === (value || "").toLowerCase();
                    return (
                      <button
                        key={`brand-${color}`}
                        type="button"
                        onClick={() => {
                          onChange(color);
                          setOpen(false);
                        }}
                        className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                          isActive
                            ? "ring-2 ring-brand-primary ring-offset-1"
                            : ""
                        }`}
                        style={{
                          backgroundColor: color,
                          boxShadow:
                            color.toLowerCase() === "#ffffff"
                              ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                              : undefined,
                        }}
                        title={`${color} (marca)`}
                        aria-label={`Color de marca ${color}`}
                      />
                    );
                  })}
                </div>
                <div className="h-px bg-neutral-200 -mx-2 mb-2" />
                <div className="text-[10px] uppercase tracking-wider font-medium text-neutral-400 mb-1.5 px-0.5">
                  Generales
                </div>
              </>
            );
          })()}
          <div className="grid grid-cols-6 gap-1.5">
            {TEXT_COLOR_PRESETS.map((color) => {
              const isActive =
                !isTransparent &&
                color.toLowerCase() === (value || "").toLowerCase();
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    onChange(color);
                    setOpen(false);
                  }}
                  className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ${
                    isActive ? "ring-2 ring-brand-primary ring-offset-1" : ""
                  }`}
                  style={{
                    backgroundColor: color,
                    boxShadow:
                      color.toLowerCase() === "#ffffff"
                        ? "inset 0 0 0 1px rgba(0,0,0,0.1)"
                        : undefined,
                  }}
                  title={color}
                  aria-label={`Color ${color}`}
                />
              );
            })}
            {/* Transparent swatch — shown like any other preset so the
                affordance reads as a color choice, not a separate setting. */}
            {allowTransparent && (
              <button
                type="button"
                onClick={() => {
                  onChange("transparent");
                  setOpen(false);
                }}
                className={`w-7 h-7 rounded-md transition-transform hover:scale-110 ring-1 ring-black/10 ${
                  isTransparent ? "ring-2 ring-brand-primary ring-offset-1" : ""
                }`}
                title="Sin fondo (transparente)"
                aria-label="Sin fondo (transparente)"
              >
                <TransparentSwatch size={28} rounded="rounded-md" />
              </button>
            )}
            <label
              className="w-7 h-7 rounded-md flex items-center justify-center border border-dashed border-neutral-300 text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 cursor-pointer relative transition-colors"
              title="Color personalizado"
            >
              <Plus className="w-3.5 h-3.5" />
              <input
                type="color"
                value={isTransparent ? "#000000" : value || "#000000"}
                onChange={(e) => onChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
/**
 * Image picker for animation templates. Trigger shows a thumbnail of the
 * current image (or a placeholder), click opens a popover with:
 *   - The project's image assets (via fetchProjectAssetsByType).
 *   - An "upload" tile that calls uploadProjectAsset and inserts the new
 *     asset at the head of the list.
 * The stored value is a signed URL string. NOTE: signed URLs may expire on
 * reload — users will need to re-pick if that happens. A future iteration
 * could store assetId + resolve at render time.
 */
const ImagePicker: React.FC<{
  value: string;
  onChange: (url: string) => void;
  projectId: string | null | undefined;
  companyId: string | null;
}> = ({ value, onChange, projectId, companyId }) => {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState<
    Array<{ id: string; url: string; name: string }>
  >([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  // Load project images when the popover opens. We resolve signed URLs in
  // parallel so the grid renders thumbnails as soon as it's mounted.
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await fetchProjectAssetsByType(projectId, "image");
        if (cancelled) return;
        const withUrls = await Promise.all(
          list.map(async (a) => ({
            id: a.id,
            url: (await getAssetSignedUrl(a.storage_path)) || "",
            name: a.name || "Imagen",
          })),
        );
        if (!cancelled) setAssets(withUrls.filter((a) => a.url));
      } catch (e) {
        console.error("[ImagePicker] fetch assets failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  const handleUpload = async (file: File) => {
    if (!projectId || !companyId) return;
    setUploading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      const url = await getAssetSignedUrl(asset.storage_path);
      if (url) {
        onChange(url);
        setAssets((prev) => [
          { id: asset.id, url, name: asset.name || file.name },
          ...prev,
        ]);
      }
    } catch (e) {
      console.error("[ImagePicker] upload failed", e);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full h-12 pl-1.5 pr-2 flex items-center gap-2 rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15"
            : "border-black/[0.08] hover:border-black/[0.16]"
        }`}
        aria-label="Elegir imagen"
      >
        {value ? (
          <img
            src={value}
            alt=""
            className="w-9 h-9 rounded object-cover shrink-0 ring-1 ring-black/10"
          />
        ) : (
          <div className="w-9 h-9 rounded bg-neutral-100 flex items-center justify-center text-neutral-400 shrink-0">
            <ImageIcon className="w-4 h-4" />
          </div>
        )}
        <span className="text-[13px] text-neutral-700 truncate flex-1 text-left">
          {value ? "Imagen seleccionada" : "Elegir imagen…"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className="absolute top-full right-0 mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-3 w-[320px]"
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
              Imágenes del proyecto
            </p>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !projectId || !companyId}
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md bg-brand-primary text-white text-[12px] font-medium hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Upload className="w-3 h-3" />
              {uploading ? "Subiendo…" : "Subir"}
            </button>
          </div>
          {loading ? (
            <p className="text-[12px] text-neutral-500 py-6 text-center">
              Cargando…
            </p>
          ) : assets.length === 0 ? (
            <p className="text-[12px] text-neutral-500 py-6 text-center">
              No hay imágenes en el proyecto.
              <br />
              Subí una con el botón de arriba.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto">
              {assets.map((a) => {
                const isActive = a.url === value;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      onChange(a.url);
                      setOpen(false);
                    }}
                    className={`relative aspect-square rounded-md overflow-hidden ring-1 transition-all hover:ring-2 ${
                      isActive
                        ? "ring-2 ring-brand-primary"
                        : "ring-black/[0.06] hover:ring-brand-primary/40"
                    }`}
                    title={a.name}
                  >
                    <img
                      src={a.url}
                      alt={a.name}
                      className="w-full h-full object-cover"
                    />
                    {isActive && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-brand-primary text-white flex items-center justify-center">
                        <Check className="w-2.5 h-2.5" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="mt-3 w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md text-[12px] font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Quitar imagen
            </button>
          )}
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleUpload(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

/**
 * Per-text styling popover used inside the animation editor. The trigger is
 * the small "Aa" icon next to a text input; clicking it opens a card with
 * font / size / color / bold / italic / alignment controls scoped to that
 * specific piece of copy. The available aspects are declared by the
 * template via `TextStylingLinks`, which maps each aspect to a sibling prop
 * in the same schema. The component just reads/writes those props.
 */
const TextStyleButton: React.FC<{
  styling: TextStylingLinks;
  schema: Record<string, AnimationPropSpec | undefined>;
  values: Record<string, unknown>;
  onChange: (key: string, value: unknown) => void;
  /** Optional title shown at the top of the popover. */
  title?: string;
}> = ({ styling, schema, values, onChange, title }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const get = (key: string | undefined) => (key ? values[key] : undefined);
  const fontSpec = styling.font ? schema[styling.font] : undefined;
  const sizeSpec = styling.size ? schema[styling.size] : undefined;
  const alignSpec = styling.align ? schema[styling.align] : undefined;
  const boldVal = !!get(styling.bold);
  const italicVal = !!get(styling.italic);
  const colorVal = (get(styling.color) as string) || "#FFFFFF";
  const fontVal = (get(styling.font) as string) || "";
  const sizeVal = (get(styling.size) as string) || "";
  const alignVal = (get(styling.align) as string) || "center";

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={title || "Estilo del texto"}
        aria-label={title || "Estilo del texto"}
        className={`h-9 w-9 inline-flex items-center justify-center rounded-md border bg-white transition-colors ${
          open
            ? "border-brand-primary/40 ring-2 ring-brand-primary/15 text-brand-black"
            : "border-black/[0.08] text-neutral-500 hover:border-black/[0.16] hover:text-neutral-800"
        }`}
      >
        <Type className="w-4 h-4" />
      </button>
      {open && (
        <div
          data-clip-ui="true"
          className="absolute top-full right-0 mt-2 z-30 bg-white border border-neutral-200 rounded-lg shadow-md p-3 space-y-3"
          style={{ width: 280 }}
        >
          {title && (
            <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 -mb-1">
              {title}
            </p>
          )}

          {/* Tipografía — shared FontPicker so each option renders in its
              own typeface, identical to the text-clip toolbar. */}
          {fontSpec?.type === "font" && styling.font && (
            <div>
              <span className="block text-[12px] font-medium text-brand-black/70 mb-1">
                Tipografía
              </span>
              <FontPicker
                value={fontVal || "Inter"}
                onChange={(fam) => onChange(styling.font!, fam)}
                triggerClassName="h-8 w-full pl-2.5 pr-2 flex items-center justify-between gap-1 rounded-md border border-black/[0.08] bg-white text-[13px] text-brand-black/85 hover:border-black/[0.16] transition-colors"
                panelAlign="left"
                panelMinWidth={240}
              />
            </div>
          )}

          {/* Tamaño — −/+ pill with free numeric input. */}
          {sizeSpec?.type === "number" &&
            styling.size &&
            (() => {
              const cur = Number(get(styling.size) ?? 0);
              const step = sizeSpec.step ?? 1;
              const min = sizeSpec.min ?? 0;
              const max = sizeSpec.max ?? 9999;
              const dec = sizeSpec.decimals ?? 0;
              const clamp = (n: number) => Math.min(max, Math.max(min, n));
              return (
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] font-medium text-brand-black/70">
                    {sizeSpec.label}
                  </span>
                  <div className="h-8 flex items-center rounded-md border border-black/[0.08] bg-white overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onChange(styling.size!, clamp(cur - step))}
                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                      title="Reducir"
                    >
                      −
                    </button>
                    <NumberField
                      value={cur}
                      min={min}
                      max={max}
                      step={step}
                      decimals={dec}
                      onCommit={(n) => onChange(styling.size!, n)}
                      className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                      ariaLabel={sizeSpec.label}
                    />
                    <button
                      type="button"
                      onClick={() => onChange(styling.size!, clamp(cur + step))}
                      className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                      title="Aumentar"
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })()}

          {/* Color */}
          {styling.color && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] font-medium text-brand-black/70">
                Color
              </span>
              <ColorSwatchPicker
                value={colorVal}
                onChange={(c) => onChange(styling.color!, c)}
                ariaLabel="Color del texto"
              />
            </div>
          )}

          {/* Bold / Italic / Alignment — compact toolbar row */}
          {(styling.bold || styling.italic || styling.align) && (
            <div className="flex items-center gap-2 pt-1 border-t border-black/[0.06]">
              {styling.bold && (
                <button
                  type="button"
                  onClick={() => onChange(styling.bold!, !boldVal)}
                  title="Negrita"
                  aria-pressed={boldVal}
                  className={`h-8 w-8 flex items-center justify-center rounded-md text-[14px] font-bold transition-colors ${
                    boldVal
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  B
                </button>
              )}
              {styling.italic && (
                <button
                  type="button"
                  onClick={() => onChange(styling.italic!, !italicVal)}
                  title="Cursiva"
                  aria-pressed={italicVal}
                  className={`h-8 w-8 flex items-center justify-center rounded-md text-[14px] italic font-serif transition-colors ${
                    italicVal
                      ? "bg-neutral-900 text-white"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  I
                </button>
              )}
              {alignSpec?.type === "select" && styling.align && (
                <div className="ml-auto inline-flex items-center bg-neutral-100 rounded-md p-0.5">
                  {alignSpec.options.map((opt) => {
                    const active = opt.value === alignVal;
                    const Icon =
                      opt.value === "left"
                        ? AlignLeft
                        : opt.value === "right"
                          ? AlignRight
                          : AlignCenter;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => onChange(styling.align!, opt.value)}
                        title={opt.label}
                        aria-label={opt.label}
                        aria-pressed={active}
                        className={`h-7 w-7 inline-flex items-center justify-center rounded transition-colors ${
                          active
                            ? "bg-white text-brand-black shadow-sm"
                            : "text-neutral-500 hover:text-neutral-700"
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Subtitle generation ───────────────────────────────────────────────────
// Shape returned by the `/webhook/generar_transcript` endpoint. Each word
// comes with start/end timestamps (seconds, relative to the media start).
// `spacing` entries are whitespace runs and are ignored when chunking.
export interface TranscriptWord {
  text: string;
  start: number;
  end: number;
  type: "word" | "spacing" | string;
  logprob?: number;
}
export interface TranscriptResult {
  language_code?: string;
  language_probability?: number;
  text: string;
  words: TranscriptWord[];
  transcription_id?: string;
  audio_duration_secs?: number;
  /** El archivo (`clip.storagePath`) que se transcribió: los tiempos son de ESE
   *  archivo. Si el clip cambia de archivo, el transcript deja de valer
   *  (`transcriptVigente`, ADR-2026-09-28-el-transcript-sabe-de-que-archivo-es). */
  storagePath?: string;
}

// URL of the n8n webhook that performs the transcription. Kept at module
// scope so tests can stub it, and so the call site stays readable.
const SUBTITLES_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/generar_transcript";

// Transporte del transcript (ex generar_transcript) → /v1/media/transcript con JWT
// cuando USE_ATLAS_BACKEND; fallback n8n. Devuelve un resultado discriminado para
// que cada call site conserve su manejo de error (throw / is_error / notice). Audio
// largo puede venir 202 (scribe tardó) → poleamos el job.
type TranscriptRequestResult =
  | { ok: true; transcript: TranscriptResult }
  | { ok: false; message: string };
async function requestTranscript(
  url: string,
  userId: string,
): Promise<TranscriptRequestResult> {
  const normalize = (raw: unknown): TranscriptResult | null => {
    const t = (
      Array.isArray(raw)
        ? raw[0]
        : raw && typeof raw === "object" && "data" in raw
          ? (raw as { data: TranscriptResult }).data
          : raw
    ) as TranscriptResult;
    return t && Array.isArray(t.words) ? t : null;
  };
  const badFormat = "La respuesta del servicio no tiene el formato esperado.";
  if (USE_ATLAS_BACKEND) {
    const res = await atlasBackendFetch("/v1/media/transcript", {
      user_id: userId,
      url,
    });
    let raw: unknown;
    if (res.status === 202) {
      const pending = (await res.json().catch(() => ({}))) as { job_id?: string };
      if (!pending.job_id) return { ok: false, message: "Transcript 202 sin job_id" };
      const settled = await pollJobUntilSettled(pending.job_id, {
        timeoutMs: 8 * 60 * 1000,
      });
      if (!settled)
        return { ok: false, message: "La transcripción tardó demasiado (timeout)." };
      if (settled.status === "failed")
        return { ok: false, message: settled.userMessage || "La transcripción falló." };
      raw = settled.output;
    } else {
      if (!res.ok) {
        if (res.status === 402) {
          return {
            ok: false,
            message:
              "No te alcanzan los créditos para transcribir este clip. Sumá créditos o actualizá tu plan.",
          };
        }
        const txt = await res.text().catch(() => "");
        return {
          ok: false,
          message: `El servicio de transcripción respondió ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`,
        };
      }
      raw = await res.json();
    }
    const t = normalize(raw);
    return t ? { ok: true, transcript: t } : { ok: false, message: badFormat };
  }
  const res = await fetch(SUBTITLES_WEBHOOK_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify({ user_id: userId, url }),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    return {
      ok: false,
      message: `El servicio de transcripción respondió ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`,
    };
  }
  const t = normalize(await res.json());
  return t ? { ok: true, transcript: t } : { ok: false, message: badFormat };
}

// Maximum words per subtitle line and maximum duration (seconds) per
// subtitle clip. Tuned so chunks feel snappy on social-media vertical
// video — short enough to always fit on one line, long enough that the
// viewer can read them.
const SUBTITLE_MAX_WORDS = 6;
const SUBTITLE_MIN_WORDS = 2;
const SUBTITLE_MAX_DURATION_SEC = 3;
// Inter-word silence thresholds. A "large" pause (typical sentence beat)
// is treated as a strong break and splits as long as the current chunk
// has at least SUBTITLE_MIN_WORDS. A "medium" pause (short breath, hedge)
// only splits when the chunk is already substantial, so we don't slice
// natural phrases in half.
const SUBTITLE_LARGE_PAUSE_SEC = 0.45;
const SUBTITLE_MEDIUM_PAUSE_SEC = 0.25;

// ── Borrado de objetos: parámetros ─────────────────────────────────────────
// Bria procesa ~5s por llamada; n8n chunkea. Tope blando: 60s (si el usuario
// quiere más, el Director avisa que tarda y tiene más riesgo, y re-llama con
// confirmLongClip:true). El poll espera lo suyo porque chunkear + recoser tarda.
const ERASE_SOFT_CAP_SEC = 60;
const ERASE_POLL_MS = 8000; // cada 8s
const ERASE_MAX_POLLS = 225; // ~30 min de techo (clips largos chunkeados)
// Tarifa de créditos por segundo de video borrado. Base: Bria $0.14/s en
// Replicate. AJUSTAR con el jefe según el ratio crédito↔USD del producto.
const ERASE_CREDITS_PER_SEC = 1;

// Créditos que cuesta borrar un objeto en un clip de `durationSec` segundos.
function creditsForErase({ durationSec }: { durationSec: number }): number {
  return Math.ceil(Math.max(1, durationSec) * ERASE_CREDITS_PER_SEC);
}

// Break the ElevenLabs-style word stream into subtitle segments. We
// filter out `spacing` entries, then append real words into a running
// chunk until any of the following triggers a flush. The order matters:
// hard caps run before the word is pushed, soft breaks (punctuation /
// silence) run after.
//   Hard caps (pre-push):
//     1. Chunk already at SUBTITLE_MAX_WORDS
//     2. Adding this word would exceed SUBTITLE_MAX_DURATION_SEC
//   Soft breaks (post-push, in priority order):
//     3. Last word ended with sentence punctuation (`.`, `?`, `!`)
//     4. Last word ended with a comma AND chunk has ≥3 words
//     5. Silence to the next word ≥ SUBTITLE_LARGE_PAUSE_SEC AND chunk
//        has ≥ SUBTITLE_MIN_WORDS (natural beat between phrases)
//     6. Silence to the next word ≥ SUBTITLE_MEDIUM_PAUSE_SEC AND chunk
//        has ≥4 words (shorter beat, only used to avoid overstuffing)
// After the main loop, an orphan-tail pass merges a 1-word final chunk
// into the previous one whenever it fits — this stops the splitter from
// ending a video on a lonely "ya." or "sí." caption.
// Returns `[]` if no word-type entries exist.
export function chunkWordsIntoSubtitles(
  words: TranscriptWord[],
): Array<{ start: number; end: number; text: string }> {
  const out: Array<{ start: number; end: number; text: string }> = [];
  const real = (words || []).filter(
    (w) =>
      w &&
      w.type === "word" &&
      typeof w.start === "number" &&
      typeof w.end === "number",
  );
  if (real.length === 0) return out;
  let buf: TranscriptWord[] = [];
  const flush = () => {
    if (buf.length === 0) return;
    const start = buf[0].start;
    const end = buf[buf.length - 1].end;
    const text = buf
      .map((w) => w.text)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (text) out.push({ start, end, text });
    buf = [];
  };
  for (let i = 0; i < real.length; i++) {
    const w = real[i];
    const prospectiveDur = buf.length === 0 ? 0 : w.end - buf[0].start;
    if (
      buf.length >= SUBTITLE_MAX_WORDS ||
      prospectiveDur > SUBTITLE_MAX_DURATION_SEC
    ) {
      flush();
    }
    buf.push(w);
    const trimmed = w.text.replace(/\s+$/, "");
    const lastChar = trimmed.slice(-1);
    if (/[.!?]/.test(lastChar)) {
      flush();
      continue;
    }
    if (lastChar === "," && buf.length >= 3) {
      flush();
      continue;
    }
    // Use the silence between this word and the next as a soft break.
    // ElevenLabs reports word boundaries tightly enough that the gap is
    // a reliable signal of a natural phrase boundary.
    const next = real[i + 1];
    if (next) {
      const pause = next.start - w.end;
      if (
        pause >= SUBTITLE_LARGE_PAUSE_SEC &&
        buf.length >= SUBTITLE_MIN_WORDS
      ) {
        flush();
      } else if (pause >= SUBTITLE_MEDIUM_PAUSE_SEC && buf.length >= 4) {
        flush();
      }
    }
  }
  flush();
  // Orphan-tail merge. If the final segment is a single word and folding
  // it back into the previous segment keeps us under the word/duration
  // budgets (with a tiny duration fudge to absorb the just-closed gap),
  // do so. Pure UX polish: avoids closing a video on a lonely caption.
  if (out.length >= 2) {
    const last = out[out.length - 1];
    const prev = out[out.length - 2];
    const lastWordCount = last.text.split(/\s+/).filter(Boolean).length;
    const prevWordCount = prev.text.split(/\s+/).filter(Boolean).length;
    if (
      lastWordCount === 1 &&
      prevWordCount + 1 <= SUBTITLE_MAX_WORDS &&
      last.end - prev.start <= SUBTITLE_MAX_DURATION_SEC + 0.5
    ) {
      prev.text = `${prev.text} ${last.text}`.replace(/\s+/g, " ").trim();
      prev.end = last.end;
      out.pop();
    }
  }
  return out;
}

// Style fields that are kept in sync across every clip in the same
// subtitle group. We deliberately EXCLUDE per-clip data like `text`,
// `name`, `startTime`, `duration`, `id`, `isSubtitle`, `subtitleGroupId`
// and `subtitleStyleOverride` — those are always individual.
const SUBTITLE_STYLE_FIELDS = [
  "fontFamily",
  "fontSizePct",
  "fontWeight",
  "fontStyle",
  "textColor",
  "textAlign",
  "textXPct",
  "textYPct",
  "textWidthPct",
  "enterAnimation",
  "enterDurationSec",
  "exitAnimation",
  "exitDurationSec",
] as const;

// Apply a patch to a single clip and, if that clip is a non-overridden
// subtitle, also propagate the subset of style fields to every other
// non-overridden member of the same subtitle group. Pure / immutable —
// always returns a new array. Designed to drop in wherever we currently
// do `setClips(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))`
// for a text clip.
function patchClipsWithSubtitleSync(
  prev: TimelineClip[],
  clipId: string,
  patch: Partial<TimelineClip>,
): TimelineClip[] {
  const target = prev.find((c) => c.id === clipId);
  if (!target) return prev;
  // Build the style-only patch up-front so we don't recompute it per row.
  let stylePatch: Partial<TimelineClip> | null = null;
  const shouldSync = !!(
    target.isSubtitle &&
    target.subtitleGroupId &&
    !target.subtitleStyleOverride
  );
  if (shouldSync) {
    const sp: Record<string, unknown> = {};
    let hasAny = false;
    for (const k of SUBTITLE_STYLE_FIELDS) {
      if (k in patch) {
        sp[k] = (patch as Record<string, unknown>)[k];
        hasAny = true;
      }
    }
    stylePatch = hasAny ? (sp as Partial<TimelineClip>) : null;
  }
  return prev.map((c) => {
    if (c.id === clipId) return { ...c, ...patch };
    if (
      stylePatch &&
      c.isSubtitle &&
      c.subtitleGroupId === target.subtitleGroupId &&
      !c.subtitleStyleOverride
    ) {
      return { ...c, ...stylePatch };
    }
    return c;
  });
}

// Default values applied when a brand-new text clip is created without
// explicit params (UI button or agent tool with missing fields).
const DEFAULT_TEXT_CLIP = {
  text: "Texto nuevo",
  // La primera de la lista alfabética, no una fuente escrita a mano: el picker
  // abre en la misma con la que nació el clip.
  fontFamily: FUENTE_POR_DEFECTO,
  fontSizePct: 8,
  fontWeight: "normal" as const,
  fontStyle: "normal" as const,
  // Negro por defecto. El blanco venía de suponer que todo texto va sobre
  // video oscuro; en la práctica el usuario elige el color, y arrancar en
  // negro es lo que se espera de un editor.
  textColor: "#000000",
  textAlign: "center" as const,
  textXPct: 50,
  textYPct: 50,
  duration: 3,
};

// ¿El color del texto es CLARO? Se usa para elegir el fondo de la vista previa
// del panel de texto: con texto blanco sobre gris claro no se ve nada. Es la
// luminancia relativa de siempre (los coeficientes del ojo humano: el verde
// pesa más que el rojo y mucho más que el azul).
const esColorClaro = (hex?: string): boolean => {
  const h = (hex || "").replace("#", "");
  const largo = h.length === 3 ? 1 : 2;
  if (h.length !== 3 && h.length !== 6) return false;
  const leer = (i: number) => {
    const t = h.slice(i * largo, i * largo + largo);
    const v = parseInt(largo === 1 ? t + t : t, 16);
    return Number.isFinite(v) ? v / 255 : 0;
  };
  return 0.2126 * leer(0) + 0.7152 * leer(1) + 0.0722 * leer(2) > 0.6;
};

// Target slot for generated image to replace when user hits "Utilizar"
type ImageGenTarget = { kind: "first" } | { kind: "last" } | { kind: "ref" };
// Lo que puede abrir el selector de imagen: un slot de escena, o "Añadir" del panel de
// generar imagen, que adjunta la elegida como referencia
// (ADR-2026-10-05-anadir-en-imagen-abre-el-selector-del-video).
type SelectorTarget = ImageGenTarget | { kind: "attach" };

/** El botón "Mejorar prompt" de los paneles de IA: pastilla blanca, como en Redes. */
const CLASE_BOTON_MEJORAR =
  "inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white py-1.5 px-3 text-[12.5px] font-medium text-brand-black/70 transition-colors hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-50 disabled:hover:border-neutral-200 disabled:hover:text-brand-black/70";

// Generated image result (during or after generation)
interface GenImageResult {
  id: string; // imagenes_generadas.id
  fileName?: string;
  signedUrl?: string;
  loading: boolean;
  createdAt?: string; // imagenes_generadas.created_at — para ordenar en Multimedia
  session?: boolean; // true si se generó en ESTA sesión (transitorio, NO se persiste). El panel de generar solo muestra estas; el historial completo vive en Multimedia/Historial.
}

type TrackType = "video" | "audio" | "elements";

interface TrackConfig {
  type: TrackType;
  label: string;
  color: string;
  clipTypes: ClipType[];
}

// Velocidades de reproducción del preview. Son las del menú y también el
// contrato con el resto: el playhead avanza `delta * velocidad` y cada
// <video>/<audio> toma el mismo número en su `playbackRate`, así lo que se ve y
// lo que se escucha van al mismo ritmo que el cursor.
// El "+" de las pistas se dibuja en DOS lugares —adentro del carril en
// escritorio, flotando a la derecha en el teléfono— y tiene que verse igual en
// los dos. Se eligió el del teléfono: blanco con sombra y el más en azul
// pleno. El celeste plano con el más al 50% que tenía escritorio no sirve
// flotando (se pierde sobre el gris del carril y no se lee como que está por
// encima), y este funciona en los dos contextos.
// Ver ADR-2026-09-18-el-mas-se-ve-igual-en-los-dos-lados.
const CLASES_DEL_MAS_DE_PISTA =
  "flex h-[30px] w-[30px] items-center justify-center rounded-md bg-white/95 text-brand-primary shadow-[0_1px_6px_rgb(var(--brand-black-rgb)_/_0.20)] transition-shadow hover:shadow-[0_2px_10px_rgb(var(--brand-black-rgb)_/_0.28)] active:opacity-70";

// Respiro a la izquierda DENTRO de la timeline: el segundo 0 no arranca pegado
// al borde del área. Es padding del contenedor posicionado, así que corre a
// TODO lo que se dibuja con coordenadas absolutas (regla, carriles, clips,
// cabezal) de una sola vez; a cambio, cualquier conversión de píxeles de
// pantalla a segundos tiene que descontarlo (`clientXToSeconds`).
const PAD_TIMELINE = 14;

// Cuánto dura el colapso/expansión de la timeline. Va a módulo (y no adentro
// del componente) para poder usarse en las deps de los `useCallback` sin
// recrearse en cada render. Tiene que coincidir con las clases
// `duration-[260ms]` de la tarjeta y de la zona de pistas: si el temporizador
// termina antes, el alto vuelve a `auto` con la animación todavía corriendo.
const DURACION_DEL_COLAPSO = 260;

// ── Cómo se dibuja un diálogo de confirmación del editor ────────────────────
// En escritorio, tarjeta centrada. En el TELÉFONO, hoja que sube desde abajo,
// como el resto de las superficies del editor: un cuadrado flotando al medio
// era lo único que no se comportaba como el editor alrededor.
//
// El velo va con `bg-black/60` y NO con `bg-brand-black/60`: los tokens
// `brand-*` no llevan `<alpha-value>`, así que con el modificador de opacidad
// Tailwind no emite ninguna regla y el fondo quedaba SIN oscurecer — sólo con
// el `backdrop-blur`. Es el mismo bug que ya se había arreglado en el pincel
// de borrado. Ver ADR-2026-09-18-los-dialogos-del-editor-son-hojas-en-el-telefono.
const claseVeloDeDialogo = (enElTelefono: boolean) =>
  `fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex justify-center ${
    enElTelefono ? "items-end" : "items-center"
  }`;
const claseTarjetaDeDialogo = (enElTelefono: boolean) =>
  enElTelefono
    ? "w-full max-h-[92dvh] overflow-y-auto bg-surface-card rounded-t-2xl shadow-[0_-4px_20px_rgb(var(--brand-black-rgb)_/_0.18)] animate-in slide-in-from-bottom duration-300"
    : "bg-surface-card rounded-lg shadow-md w-full max-w-md mx-4";
// La manija gris de las hojas. Decorativa: dice "esto sube desde abajo".
const ManijaDeHoja: React.FC = () => (
  <div aria-hidden="true" className="flex justify-center pt-2.5 pb-0.5">
    <span className="h-1 w-9 rounded-full bg-black/[0.18]" />
  </div>
);

// Barra del cursor que queda cuando la timeline está minimizada. El alto es
// EXACTO porque el colapso se lo suma al alto del marco para calcular cuánto
// mide la tarjeta minimizada: si no coincide, la cinta se sale de la tarjeta.
const ALTO_DE_LA_BARRA_DEL_CURSOR = 18;
// Azul intermedio: más claro que el var(--brand-primary) de marca (que acá, sobre una cinta
// de 4px al lado del gris, se lee casi negro) y más sobrio que un celeste.
const AZUL_DE_LA_BARRA_DEL_CURSOR = "#3B82F6";

// La cabeza del cursor de tiempo: un pin BLANCO con contorno y punta abajo, no
// un triángulo sólido ni un círculo. Relleno claro para que se despegue de los
// clips oscuros por los que pasa, y la punta abajo para leer exactamente sobre
// qué frame está parado. Vive acá porque la dibujan TRES lugares —el cursor de
// la timeline en escritorio, el del teléfono (clavado al centro) y la cinta de
// la timeline minimizada— y tienen que ser el mismo símbolo: es el mismo
// "dónde estoy".
const CabezaDelCursor: React.FC<{
  className?: string;
  style?: React.CSSProperties;
}> = ({ className, style }) => (
  <svg
    className={className}
    style={style}
    width={14}
    height={18}
    viewBox="0 0 14 18"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 2h10v9l-5 5-5-5V2Z"
      fill="#FFFFFF"
      style={{ stroke: 'var(--brand-black)' }}
      strokeWidth={1.6}
      strokeLinejoin="round"
    />
  </svg>
);

// ── Velocidad por clip ─────────────────────────────────────────────────────
// Los topes del slider. Más lento que 0.5x el video se ve a tirones y más
// rápido que 2x el audio deja de entenderse.
const VELOCIDAD_MIN = 0.5;
const VELOCIDAD_MAX = 2;
// Las tiras de miniaturas se arman de a dos: cada una es un <video> oculto haciendo
// seeks sobre la red (ADR-2026-11-03-el-estudio-abre-rapido).
const limitarMiniaturas = crearLimitador(2);
// La tira rápida que se muestra antes de la completa
// (ADR-2026-11-03-bajar-solo-lo-que-se-ve).
const TIRA_RAPIDA = 12;
// Las ondas bajan y decodifican el audio entero: de a una, para no pelear con el
// video que se está mirando.
const limitarOndas = crearLimitador(1);

const velocidadDeClip = (c: { speed?: number }): number => {
  const v = c.speed ?? 1;
  if (!Number.isFinite(v) || v <= 0) return 1;
  return Math.min(VELOCIDAD_MAX, Math.max(VELOCIDAD_MIN, v));
};

// ── ¿Este media se puede amplificar sin quedar mudo? ───────────────────────
// Subir el volumen por encima de 100% obliga a rutear el elemento por la Web
// Audio API, porque `HTMLMediaElement.volume` llega hasta 1. El enganche
// (`createMediaElementSource`) tiene dos filos: si el recurso es de otro origen
// y no se pidió con CORS, el nodo emite SILENCIO —sin excepción ni aviso en la
// consola— y además el elemento queda desconectado para siempre de la salida
// nativa, así que bajar el volumen tampoco lo recupera.
//
// Por eso se pregunta ANTES de enganchar. Es seguro cuando el elemento se pidió
// con `crossOrigin` (si el servidor no lo permite, el navegador falla la carga
// en vez de ensuciar el recurso), y cuando la fuente es del mismo origen, que
// incluye los `blob:` de un archivo recién subido. Si no, se deja en el camino
// nativo: suena al 100%, menos de lo pedido, pero suena.
// Ver ADR-2026-09-22-subir-el-volumen-no-deja-mudo-el-clip.
const puedeRutearsePorWebAudio = (
  el: HTMLVideoElement | HTMLAudioElement,
): boolean => {
  if (el.crossOrigin) return true;
  const src = el.currentSrc || el.src;
  if (!src) return false;
  if (src.startsWith("blob:") || src.startsWith("data:")) return true;
  try {
    return new URL(src, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
};

// Comparación para el buscador de Archivos: sin mayúsculas y sin tildes, así
// "mascara" encuentra "Máscara". `NFD` separa la letra de su tilde y el
// replace se queda con la letra.
const sinTildes = (t: string) =>
  t
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
const coincideConLaBusqueda = (nombre: string | null | undefined, q: string) =>
  !q.trim() || sinTildes(nombre || "").includes(sinTildes(q.trim()));

// Color del clip seleccionado en la timeline (el azul de la marca,
// `--brand-primary`). Va acompañado SIEMPRE de un borde interno blanco: los
// clips de video son tiras de frames casi negras y los de texto/audio son
// pasteles claros, así que un solo anillo se pierde contra uno de los dos.
// Ver ADR-2026-09-15-el-clip-seleccionado-se-ve-en-azul.
const COLOR_SELECCION = "var(--brand-primary)";

// Cuánto se estira cada pista HACIA LA IZQUIERDA del 0s, sólo como caja que
// recorta. El anillo de selección (2px por fuera del clip) y las manijas de
// trim (8px por fuera) sobresalen del clip: en el primer clip de la timeline
// eso caía justo sobre el borde de la pista y quedaba cortado.
// Ver ADR-2026-09-15-el-primer-clip-no-se-corta-en-el-0s.
const SANGRIA_PISTA = 12;

const TRACK_CONFIG: TrackConfig[] = [
  {
    type: "elements",
    label: "Elementos",
    color: "#F59E0B",
    clipTypes: ["text", "image", "animation"],
  },
  {
    type: "video",
    label: "Video",
    color: "#1644EB", // hex-de-marca-ok: se copia al clip (queda guardado en el proyecto) y se le pegan sufijos de opacidad
    clipTypes: ["video", "ai-generated"],
  },
  { type: "audio", label: "Audio", color: "#10B981", clipTypes: ["audio"] },
];

// Build a transient DOM element styled like a timeline clip so the HTML5
// drag image matches the clip the user is about to create on drop, instead
// of a screenshot of the sidebar tile being dragged. The returned element
// is appended to <body> off-screen, used as the drag image (snapshot is
// taken synchronously by the browser), and then removed on the next tick.
function buildTimelineClipDragImage(
  type: "video" | "audio" | "image",
  name: string,
  thumbnailUrl?: string | null,
): HTMLElement {
  const COLORS = { video: "#1644EB", audio: "#10B981", image: "#F59E0B" }; // hex-de-marca-ok: los colores de pista de TRACK_CONFIG (se les pega "20")
  const color = COLORS[type];
  const root = document.createElement("div");
  root.style.position = "fixed";
  root.style.top = "-1000px";
  root.style.left = "-1000px";
  root.style.width = "200px";
  root.style.height = "40px";
  root.style.borderRadius = "6px";
  root.style.overflow = "hidden";
  root.style.boxShadow = "0 6px 16px rgba(0,0,0,0.18)";
  root.style.pointerEvents = "none";
  root.style.fontFamily = "inherit";
  root.style.fontSize = "11px";
  root.style.fontWeight = "500";
  root.style.display = "flex";
  root.style.alignItems = "center";
  if ((type === "video" || type === "image") && thumbnailUrl) {
    // Mimic the dark "frame strip" look used for video/image timeline clips.
    root.style.backgroundColor = "#0a0a0a";
    const img = document.createElement("div");
    img.style.flex = "1";
    img.style.height = "100%";
    img.style.backgroundImage = `url("${thumbnailUrl}")`;
    img.style.backgroundSize = "cover";
    img.style.backgroundPosition = "center";
    root.appendChild(img);
    const label = document.createElement("div");
    label.textContent = name;
    label.style.position = "absolute";
    label.style.left = "8px";
    label.style.right = "8px";
    label.style.bottom = "4px";
    label.style.color = "#fff";
    label.style.textShadow = "0 1px 2px rgba(0,0,0,0.6)";
    label.style.whiteSpace = "nowrap";
    label.style.overflow = "hidden";
    label.style.textOverflow = "ellipsis";
    root.style.position = "fixed"; // keep fixed for offscreen
    root.appendChild(label);
  } else {
    // Audio (and any thumbnail-less fallback): tinted block + colored
    // left border + colored truncated label, matching the clip layout.
    root.style.backgroundColor = color + "20";
    root.style.borderLeft = `3px solid ${color}`;
    root.style.padding = "0 10px";
    root.style.color = color;
    root.style.whiteSpace = "nowrap";
    const span = document.createElement("span");
    span.textContent = name;
    span.style.overflow = "hidden";
    span.style.textOverflow = "ellipsis";
    span.style.flex = "1";
    root.appendChild(span);
  }
  document.body.appendChild(root);
  setTimeout(() => {
    root.remove();
  }, 0);
  return root;
}

// Fetch an image URL and return a resized base64-encoded JPEG (no `data:`
// prefix). Used to embed generated images into Anthropic tool_result blocks
// so Claude can observe them directly.
async function urlToBase64Jpeg(
  url: string,
  maxWidth: number = 768,
): Promise<string> {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = document.createElement("img");
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("No se pudo decodificar la imagen"));
      el.src = objectUrl;
    });
    const ratio = img.naturalWidth > maxWidth ? maxWidth / img.naturalWidth : 1;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.naturalWidth * ratio));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * ratio));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No se pudo crear contexto canvas");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
    return dataUrl.split(",")[1] || "";
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Extract `count` frames evenly distributed along the video at `videoUrl`.
// Returns base64-encoded JPEGs (without the data: prefix) plus the timestamp
// in seconds where each frame was sampled. Used by the analyze_video_quick
// agent tool to surface visual context to Claude as image content blocks.
async function extractVideoFrames(
  videoUrl: string,
  count: number,
  maxWidth: number = 768,
): Promise<{ tSec: number; base64: string }[]> {
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.preload = "auto";
  video.muted = true;
  (video as any).playsInline = true;
  video.src = videoUrl;
  await new Promise<void>((resolve, reject) => {
    const onLoaded = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("No se pudo cargar el video"));
    };
    const cleanup = () => {
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
    };
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
  });
  const duration = video.duration || 0;
  if (!isFinite(duration) || duration <= 0)
    throw new Error("Duración de video inválida");
  const ratio = video.videoWidth > maxWidth ? maxWidth / video.videoWidth : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(video.videoWidth * ratio));
  canvas.height = Math.max(1, Math.round(video.videoHeight * ratio));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo crear contexto canvas");
  const frames: { tSec: number; base64: string }[] = [];
  for (let i = 0; i < count; i++) {
    // Sample at evenly spaced internal points (avoids t=0 / t=duration which often render blank).
    const t = ((i + 0.5) / count) * duration;
    await new Promise<void>((resolve, reject) => {
      const onSeeked = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("Error al buscar frame"));
      };
      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("error", onError);
      };
      video.addEventListener("seeked", onSeeked, { once: true });
      video.addEventListener("error", onError, { once: true });
      try {
        video.currentTime = t;
      } catch (err) {
        cleanup();
        reject(err as Error);
      }
    });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    const base64 = dataUrl.split(",")[1] || "";
    frames.push({ tSec: t, base64 });
  }
  return frames;
}

// Rough estimate of speech duration (seconds) based on natural Spanish reading
// pace. Used to size placeholder AI clips (voice-over audio, dialogue scenes)
// in the timeline BEFORE generation finishes. The real duration is probed
// afterwards and updates the clip.
//   ~15 chars/s at speed=1.0 (Spanish natural), scaled by the speed multiplier.
//   +0.5s tail for natural pauses/breaths. Returns 0 for empty text.
const estimateSpeechDuration = (text: string, speed: number = 1.0): number => {
  const t = (text || "").trim();
  if (!t) return 0;
  const charsPerSecondAtSpeed1 = 15;
  const s = Math.max(0.1, speed);
  return t.length / charsPerSecondAtSpeed1 / s + 0.5;
};

const getTrackForClipType = (clipType: ClipType): TrackConfig =>
  TRACK_CONFIG.find((t) => t.clipTypes.includes(clipType)) || TRACK_CONFIG[0];

const getClipTrackIndex = (c: TimelineClip): number => c.trackIndex ?? 0;

// Las props que recibe una animación por código: las MISMAS en el preview (el
// sandbox) y en el render (`remotion/src/clips/CodigoClip.tsx`). Las URLs son las
// firmadas que tiene el clip; el export las vuelve a firmar por su storagePath.
const formatoDeAnimacion = (aspectRatio: AspectRatio): "16:9" | "9:16" | "4:3" =>
  aspectRatio === "9:16" || aspectRatio === "4:3" ? aspectRatio : "16:9";

const propsDeAnimacionPorCodigo = (clip: TimelineClip): PropsDeAnimacion => {
  const a = clip.animationCodigo!;
  return {
    assets: a.assets.map(({ url, descripcion }) => ({ url, descripcion })),
    marca: a.marca,
    duracionEnFrames: Math.max(1, Math.round(clip.duration * ANIMATION_FPS)),
  };
};

// Two video clips count as "adjacent" (and therefore a transition target)
// when they're on the same row of the same track and either touching or
// already overlapping by at most ADJACENCY_OVERLAP_LIMIT seconds. A larger
// gap is treated as intentional silence/jump-cut.
const ADJACENCY_GAP_LIMIT = 0.15;
const ADJACENCY_OVERLAP_LIMIT = 5;
const isVideoClip = (c: TimelineClip): boolean =>
  c.type === "video" || c.type === "ai-generated";

// Una escena que el usuario canceló: su resultado (o su error) se ignora cuando llega
// (ADR-2026-11-03-cancelar-una-escena-del-estudio).
class EscenaCancelada extends Error {}

/** El `ref_mode` con que se pide una escena (la misma cuenta que `generateAiClip`). */
const refModeDeLaEscena = (clip: TimelineClip): string =>
  clip.aiSceneMode === "personas"
    ? clip.aiPersonType === "motion"
      ? "dialogo_movimiento"
      : "dialogo"
    : clip.aiRefMode || "none";

// Qué panel edita cada tipo de clip: los textos van al panel "Texto", los
// elementos animados a "Elementos" y el resto —video, audio, imagen— al panel
// "clip" (ADR-2026-09-15-panel-del-clip-a-la-izquierda).
const panelDelClip = (clip: TimelineClip): "text" | "animation" | "clip" =>
  clip.type === "text"
    ? "text"
    : clip.type === "animation"
      ? "animation"
      : "clip";

// La duración real del archivo, cuando llega su metadata. Devuelve el MISMO
// arreglo si ya la tenía (ADR-2026-11-03-abrir-sin-esperar).
const conDuracionDeOrigen = (
  clips: TimelineClip[],
  clipId: string,
  dur: number,
): TimelineClip[] =>
  clips.some((c) => c.id === clipId && c.sourceDuration !== dur)
    ? clips.map((c) =>
        c.id === clipId && c.sourceDuration !== dur
          ? { ...c, sourceDuration: dur }
          : c,
      )
    : clips;

// Returns ordered pairs (left, right) of adjacent video clips. Pairs are
// derived once per `clips` change so the timeline can render the `+`
// indicators without recomputing on every frame.
const findAdjacentVideoPairs = (
  clips: TimelineClip[],
): Array<{ left: TimelineClip; right: TimelineClip }> => {
  const videos = clips.filter(isVideoClip);
  // Group by track index — only same-row clips are candidates.
  const byTrack = new Map<number, TimelineClip[]>();
  for (const c of videos) {
    const idx = getClipTrackIndex(c);
    if (!byTrack.has(idx)) byTrack.set(idx, []);
    byTrack.get(idx)!.push(c);
  }
  const pairs: Array<{ left: TimelineClip; right: TimelineClip }> = [];
  for (const list of byTrack.values()) {
    list.sort((a, b) => a.startTime - b.startTime);
    for (let i = 0; i < list.length - 1; i++) {
      const left = list[i];
      const right = list[i + 1];
      const leftEnd = left.startTime + left.duration;
      const gap = right.startTime - leftEnd;
      if (gap <= ADJACENCY_GAP_LIMIT && gap >= -ADJACENCY_OVERLAP_LIMIT) {
        pairs.push({ left, right });
      }
    }
  }
  return pairs;
};

// Where the fade sits relative to the boundary between A and B.
// 0.5 = perfectly centered: half the fade happens BEFORE B.startTime
// (while A is still in normal playback) and half AFTER (while A is in
// extension). Smaller values bias the fade toward B's side; larger
// values bias toward A's side. Centered is the sweet spot — both clips
// contribute real motion to the transition instead of one being a still.
const TRANSITION_EARLY_FRACTION = 0.5;

// Returns the [start, end] timeline range over which clip B's transition
// plays out, or null if clip has no transitionIn. The window is centered
// over the boundary between A and B per `TRANSITION_EARLY_FRACTION`, so
// it actually starts a bit BEFORE clip.startTime.
const getTransitionWindow = (
  clip: TimelineClip,
): { start: number; end: number; duration: number } | null => {
  if (!clip.transitionIn || clip.transitionIn.durationSec <= 0) return null;
  const D = clip.transitionIn.durationSec;
  const start = clip.startTime - D * TRANSITION_EARLY_FRACTION;
  const end = clip.startTime + D * (1 - TRANSITION_EARLY_FRACTION);
  return { start: Math.max(0, start), end, duration: D };
};

// Look up the clip that immediately succeeds `clip` on the same video
// track and carries a `transitionIn`. "Immediately" = touching (gap ≈ 0)
// or with a tiny gap up to ADJACENCY_GAP_LIMIT. Also tolerates the
// legacy overlapping arrangement (when transitions used to ripple-shift)
// so existing projects keep working.
const findTransitionSuccessor = (
  clip: TimelineClip,
  clips: TimelineClip[],
): TimelineClip | null => {
  if (!isVideoClip(clip)) return null;
  const myTrack = getClipTrackIndex(clip);
  const myEnd = clip.startTime + clip.duration;
  const candidate = clips
    .filter(
      (c) =>
        c.id !== clip.id &&
        isVideoClip(c) &&
        getClipTrackIndex(c) === myTrack &&
        c.transitionIn &&
        c.transitionIn.durationSec > 0,
    )
    .filter((c) => {
      const gap = c.startTime - myEnd;
      return gap <= ADJACENCY_GAP_LIMIT && gap >= -ADJACENCY_OVERLAP_LIMIT;
    })
    .sort((a, b) => a.startTime - b.startTime)[0];
  return candidate || null;
};

// Whether a clip should be treated as "playing" at a given timeline time.
// Normally that's just `[startTime, startTime + duration)`, but the
// active window stretches in two situations to support transitions
// without moving any clip on the timeline:
//
//   1. The clip itself has `transitionIn` and the fade has started
//      EARLIER than `clip.startTime` (because the window is centered
//      over the boundary). The clip is "pre-active" for that lead-in
//      portion — its <video> stays mounted and we freeze on its first
//      frame so the opacity fade has something to draw.
//
//   2. The clip is the immediate adjacent predecessor of one with a
//      `transitionIn` and the fade is still in progress past this
//      clip's end. The clip is "extended" for the trailing portion —
//      its <video> freezes on the last frame for the same reason.
const isClipActiveAtTime = (
  clip: TimelineClip,
  time: number,
  clips: TimelineClip[],
): boolean => {
  // Normal range first — the common case on every frame.
  if (time >= clip.startTime && time < clip.startTime + clip.duration)
    return true;
  if (!isVideoClip(clip)) return false;
  // Pre-active: this clip's own transitionIn has a window that starts
  // before `clip.startTime`.
  if (clip.transitionIn) {
    const win = getTransitionWindow(clip);
    if (win && time >= win.start && time < clip.startTime) return true;
  }
  // Extended: a successor's fade is in progress past this clip's end.
  const successor = findTransitionSuccessor(clip, clips);
  if (successor) {
    const win = getTransitionWindow(successor);
    if (win && time >= clip.startTime && time < win.end) return true;
  }
  return false;
};

// Per-frame render state for a clip. Most transitions only need to
// modulate `opacity`; the slide family also drives `translateXPct` /
// `translateYPct` (percent of clip width / height), and the zoom family
// uses `scale`. Adding a new transition type usually means returning a
// new combination here without touching the timeline / playback code.
interface ClipRenderState {
  opacity: number;
  translateXPct?: number;
  translateYPct?: number;
  scale?: number;
  blurPx?: number;
  // Direction of motion blur. 'x' = horizontal streak (for horizontal
  // slides), 'y' = vertical streak. Implemented via an SVG <feGaussianBlur>
  // with one of the stdDeviation axes pinned to 0 — CSS `filter: blur()`
  // is Gaussian/omnidirectional and gives a "lens defocus" look instead.
  blurDirection?: "x" | "y";
  // Horizontal wipe — used by the `reveal` transition. The clip is
  // visible only in the [0, wipeShowLeftPct%] horizontal range. The
  // outgoing side uses `wipeShowRightPct` to mask the LEFT side
  // instead, so the two clips fit together exactly along the wipe edge.
  wipeShowLeftPct?: number;
  wipeShowRightPct?: number;
  // Inset clip mask used by the `diapositivas` transition. The clip is
  // shown only inside a centered rectangle whose edges are insetPct
  // away from the container, with rounded corners of `slideRoundPx`.
  // Combined with translateXPct, the rectangle can also be slid in/out.
  slideInsetPct?: number;
  slideRoundPx?: number;
  // Per-axis scale used by the stretch transitions. When set, they
  // override the uniform `scale` field for that axis. Setting only one
  // (e.g. just stretchX) deforms the clip along that axis while the
  // other dimension stays at 1.
  stretchX?: number;
  stretchY?: number;
}

// Builds the CSS `transform` string for a render state. Centralised so
// the playback render and the preview tile compose transforms the same
// way. Returns `undefined` when there's no transform (vast majority of
// frames) so React skips the style entirely.
//
// Scale handling: a transition can either set a uniform `scale` (zoom,
// zoom-shake) OR per-axis `stretchX`/`stretchY` (stretch transitions).
// Per-axis takes priority when present so we can stretch one axis
// without touching the other.
const buildClipTransform = (s: ClipRenderState): string | undefined => {
  const parts: string[] = [];
  if (s.translateXPct != null && s.translateXPct !== 0)
    parts.push(`translateX(${s.translateXPct}%)`);
  if (s.translateYPct != null && s.translateYPct !== 0)
    parts.push(`translateY(${s.translateYPct}%)`);
  const sx = s.stretchX ?? s.scale;
  const sy = s.stretchY ?? s.scale;
  if (sx != null || sy != null) {
    const x = sx ?? 1;
    const y = sy ?? 1;
    if (x === y) parts.push(`scale(${x})`);
    else parts.push(`scale(${x}, ${y})`);
  }
  return parts.length > 0 ? parts.join(" ") : undefined;
};

// Builds the CSS `clip-path` for any clip-shape effect. Returns
// undefined when no shape is set (vast majority of frames).
//
//   wipeShowLeftPct / wipeShowRightPct: horizontal wipe used by the
//     `reveal` transition.
//   slideInsetPct / slideRoundPx: centred inset rectangle with rounded
//     corners, used by `diapositivas` to mask each clip into a slide.
const buildClipClipPath = (s: ClipRenderState): string | undefined => {
  if (s.wipeShowLeftPct != null) {
    const visible = Math.max(0, Math.min(100, s.wipeShowLeftPct));
    return `inset(0 ${100 - visible}% 0 0)`;
  }
  if (s.wipeShowRightPct != null) {
    const visible = Math.max(0, Math.min(100, s.wipeShowRightPct));
    return `inset(0 0 0 ${100 - visible}%)`;
  }
  if (s.slideInsetPct != null && s.slideInsetPct > 0) {
    const inset = Math.max(0, Math.min(45, s.slideInsetPct));
    const round = Math.max(0, s.slideRoundPx ?? 0);
    return `inset(${inset}% round ${round}px)`;
  }
  return undefined;
};

// Two-stage piecewise ease used by the `reveal` swipe-line. The first
// third of the time advances LINEARLY but at a slow pace (covers only
// `SLOW_PHASE_PROGRESS` of the distance), then the remaining two-thirds
// snap LINEARLY at a much faster pace to finish the run. Two distinct
// constant speeds with a kink in the middle reads as "build then whip"
// far more clearly than a smooth cubic/exponential curve.
const REVEAL_SLOW_PHASE_T = 0.6;
const REVEAL_SLOW_PHASE_PROGRESS = 0.32;
const revealLineEase = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  if (c <= REVEAL_SLOW_PHASE_T) {
    return (c / REVEAL_SLOW_PHASE_T) * REVEAL_SLOW_PHASE_PROGRESS;
  }
  const t2 = (c - REVEAL_SLOW_PHASE_T) / (1 - REVEAL_SLOW_PHASE_T);
  return REVEAL_SLOW_PHASE_PROGRESS + t2 * (1 - REVEAL_SLOW_PHASE_PROGRESS);
};

// Same idea for `filter`. Returns a reference to the directional motion
// blur SVG filter (`#atlas-motion-blur`) when the clip is mid-slide.
// The SVG element itself is rendered inside the preview container and
// its `stdDeviation` is updated each frame (see `motionBlurState`).
// When there's no blur on this clip we return undefined so React drops
// the style entirely — no filter computation cost on the GPU.
const MOTION_BLUR_FILTER_ID = "atlas-motion-blur";
const buildClipFilter = (s: ClipRenderState): string | undefined => {
  if (s.blurPx == null || s.blurPx < 0.1) return undefined;
  if (s.blurDirection === "x" || s.blurDirection === "y") {
    return `url(#${MOTION_BLUR_FILTER_ID})`;
  }
  // Fallback: omnidirectional CSS blur (kept for future non-slide
  // transitions that might want a Gaussian defocus).
  return `blur(${s.blurPx}px)`;
};

// Blur curve used by transitions that "punctuate" their middle: peaks at
// t=0.5 and lands sharp at both ends. Sine instead of a triangle gives
// a softer, more organic ramp. Used by transitions where motion is
// roughly symmetric in time (zoom punch). Each transition picks its own
// peak magnitude so the blur intensity matches the visual strength.
const blurPeakAt = (t: number, peakPx: number): number =>
  peakPx * Math.sin(Math.PI * Math.max(0, Math.min(1, t)));

// Punch ease-out cubic: front-loads the motion so 70% of the distance is
// covered in the first 30% of the time. This is what gives slides their
// "whip" feel (the visual signature of viral edits) instead of the
// "smooth premium" feel of linear interpolation. Matches CSS's
// `cubic-bezier(0.2, 0.9, 0.3, 1)` ballpark.
const punchEaseOut = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - c, 3);
};

// Back ease-out — motion overshoots the target slightly then settles
// back to it. Used by the slide variants so the incoming clip "lands
// with a small bounce" instead of stopping flat. `SLIDE_BOUNCE_OVERSHOOT`
// is intentionally small (~1.0) for a minimum/subtle bounce — higher
// values (1.7+) read as a cartoon-y spring which doesn't fit the viral
// edit aesthetic.
const SLIDE_BOUNCE_OVERSHOOT = 1.0;
const easeOutBack = (t: number): number => {
  const c = Math.max(0, Math.min(1, t)) - 1;
  return (
    c * c * ((SLIDE_BOUNCE_OVERSHOOT + 1) * c + SLIDE_BOUNCE_OVERSHOOT) + 1
  );
};

// Normalised velocity of the punch ease-out at time `t`. Used to drive
// motion blur magnitude — when the position is moving fastest (start of
// the curve) the blur is heaviest, when motion has settled (end) blur
// goes back to 0. This matches how camera shutter integration works in
// real life and is what makes whip-style blur read as "fast" instead of
// "out of focus".
const punchEaseOutVelocity = (t: number): number => {
  const c = Math.max(0, Math.min(1, t));
  return Math.pow(1 - c, 2);
};

// Blur peak constants. Higher values give a more aggressive viral whip;
// lower values feel "premium / cinematic". Tuneable from one place.
const SLIDE_BLUR_PEAK_PX = 28;
const ZOOM_BLUR_PEAK_PX = 32;

// Per-frame shake offsets used by the `zoom-shake` transition. Tuning
// notes from earlier iterations:
//
//   * Amplitude has to be big enough to register visually — at 2% on a
//     1080-wide frame each "kick" is ~22px, which is the floor for the
//     eye to read it as a separate displacement instead of just blur.
//     Going to 6% (~65px) lands clearly in the "violent action camera"
//     territory.
//   * Frequencies must sit around 8–18 Hz. Anything higher than ~25 Hz
//     blurs into continuous vibration (you can't see individual jolts);
//     anything lower than ~5 Hz reads as a slow swing instead of a
//     shake. We layer one main low-freq oscillation with a smaller
//     high-freq twitch on top so it doesn't look like a single sine.
//   * Phase offsets keep X and Y from syncing into a diagonal line.
//   * Trapezoidal envelope: full amplitude across most of the
//     transition, ramping in/out only at the very edges so the shake
//     starts fast and stops fast (no slow buildup before the punch).

// Incoming-side animation. `t` ∈ [0..1] is the normalized progress through
// the transition window (0 = clip B just appearing, 1 = transition done).
const computeIncomingTransitionState = (
  type: TransitionType,
  t: number,
): ClipRenderState => {
  switch (type) {
    case "crossfade":
      return { opacity: t };
    case "fade-to-black":
      // B is invisible for the first half (while A fades to black), then
      // ramps in during the second half.
      return { opacity: Math.max(0, t * 2 - 1) };
    case "slide": {
      // B whip-pans in from the right with a small bounce on landing.
      // `easeOutBack` overshoots ~3% past centre then settles, while
      // the velocity-tracked directional blur fades to 0 right at the
      // overshoot peak so the bounce reads as a clean visible wobble
      // rather than a blur smear.
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateXPct: (1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-left": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateXPct: -(1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-up": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateYPct: (1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "slide-down": {
      const eased = easeOutBack(t);
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        translateYPct: -(1 - eased) * 100,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "reveal": {
      // White swipe-line travels left → right across the frame.
      // Uses an exponential ease-in (slow build → fast finish) so it
      // feels like the line gathers momentum, instead of the mechanical
      // pace a linear wipe would have. B is masked to the area behind
      // the line that has already passed.
      const eased = revealLineEase(t);
      return {
        opacity: 1,
        wipeShowLeftPct: eased * 100, // shows left N% — grows with the line
      };
    }
    case "zoom": {
      // "Zoom punch" — A keeps zooming in fast; at the cut moment
      // (t≈0.5) B reveals SHARPLY at ~3.5×, then de-zooms linearly to
      // 1× over the entire second half so the user can clearly see the
      // settle motion. Two key choices that make this read as "viral"
      // instead of "smooth crossfade":
      //
      //   1) Sharp opacity cut around t=0.5 (10% of duration window)
      //      → B reaches full opacity well before the de-zoom finishes,
      //        so the entire de-zoom motion plays with B fully visible.
      //   2) LINEAR settle for the scale (not eased) — the de-zoom
      //      takes the whole second half to play out, giving the eye
      //      time to register the motion. Easing it would compress
      //      the motion into the first 30% of the visible window
      //      and the user would barely notice it.
      const opacity = t < 0.45 ? 0 : Math.min(1, (t - 0.45) / 0.1);
      const visT = Math.max(0, t * 2 - 1); // 0 at t=0.5, 1 at t=1
      return {
        opacity,
        scale: 1 + (1 - visT) * 2.5, // 3.5× → 1× LINEAR over the full visible window
        blurPx: blurPeakAt(t, ZOOM_BLUR_PEAK_PX),
      };
    }
    case "light-leak":
    case "plastic":
    case "distorsion":
    case "vhs":
    case "vintage":
    case "glitch": {
      // Sharp cut at the midpoint — A and B don't animate themselves
      // (no scale/translate/blur), the overlay video composited on top
      // does the visual work. B becomes visible right at t=0.5 with a
      // tiny 5% ramp so the cut isn't perfectly binary.
      return {
        opacity: t < 0.5 ? 0 : Math.min(1, (t - 0.5) / 0.05),
      };
    }
    case "stretch-horizontal":
    case "stretch-vertical": {
      // B comes in HEAVILY stretched along the transition's axis and
      // de-stretches linearly back to 1× over the second half — same
      // shape as the `zoom` settle but per-axis. The other axis stays
      // at 1 throughout, so the clip looks elongated along the chosen
      // direction. Sharp opacity ramp around t=0.5 so the eye sees a
      // clear "snap" rather than a slow crossfade.
      const opacity = t < 0.45 ? 0 : Math.min(1, (t - 0.45) / 0.1);
      const visT = Math.max(0, t * 2 - 1);
      const stretch = 1 + (1 - visT) * 3; // 4× → 1× linear
      const isHoriz = type === "stretch-horizontal";
      return {
        opacity,
        stretchX: isHoriz ? stretch : 1,
        stretchY: isHoriz ? 1 : stretch,
        // Directional motion blur along the stretch axis amplifies the
        // "smear" feel — same SVG filter the slides use.
        blurPx: blurPeakAt(t, SLIDE_BLUR_PEAK_PX),
        blurDirection: isHoriz ? "x" : "y",
      };
    }
    case "diapositivas": {
      // Slideshow-style transition with three temporal phases:
      //
      //   t ∈ [0.00, 0.25]  CROP: A morphs into a centred rounded
      //                      "slide" (B isn't visible yet)
      //   t ∈ [0.25, 0.75]  SLIDE: A slides off-screen left, B slides
      //                      in from off-screen right — both stay
      //                      framed as the rounded slide
      //   t ∈ [0.75, 1.00]  UNCROP: B's slide expands back to fill
      //                      the whole canvas
      //
      // B's perspective:
      //   • Phase 1: invisible (off-screen + alpha 0).
      //   • Phase 2: enters from translateX +150% with the slide mask.
      //   • Phase 3: stays at translate 0, mask animates back to full.
      if (t < 0.25) return { opacity: 0 };
      if (t < 0.75) {
        // Front-loaded slide motion: B snaps in from the right and
        // settles smoothly. Linear interpolation reads as "mechanical"
        // here — the punch ease-out is what gives the viral whip feel.
        const slideT = punchEaseOut((t - 0.25) / 0.5);
        return {
          opacity: 1,
          translateXPct: (1 - slideT) * 150,
          slideInsetPct: DIAPOS_INSET_PCT,
          slideRoundPx: DIAPOS_ROUND_PX,
        };
      }
      // Uncrop also uses ease-out so the expansion feels snappy at first
      // then lands gently at full-frame.
      const uncropT = punchEaseOut((t - 0.75) / 0.25);
      return {
        opacity: 1,
        translateXPct: 0,
        slideInsetPct: DIAPOS_INSET_PCT * (1 - uncropT),
        slideRoundPx: DIAPOS_ROUND_PX * (1 - uncropT),
      };
    }
  }
};

// Catalogue of overlay-video transitions. Each entry maps a transition
// type to the URL of an MP4 with a black background — the video is
// composited on top of the A/B cut via `mix-blend-mode: screen`, which
// treats black as transparent so only the bright effect (light leak,
// glitch, flash, etc.) is visible. The actual A→B cut happens at the
// midpoint of the transition window where the overlay is at its peak
// brightness, so the cut is visually masked by the effect.
//
// To add more overlays: drop the .mp4 in `project/public/images/transitions/`
// and add a new entry here + a new TransitionType variant.
const OVERLAY_TRANSITIONS: Partial<Record<TransitionType, string>> = {
  "light-leak": "/images/transitions/Light Leaks 09.mp4",
  plastic: "/images/transitions/Plastic 01.mp4",
  distorsion: "/images/transitions/Scratches 05.mp4",
  vhs: "/images/transitions/VHS 07.mp4",
  vintage: "/images/transitions/Vintage Film 08.mp4",
  glitch: "/images/transitions/Glitch 04.mp4",
};

// Tuneables for the `diapositivas` transition. Higher inset = smaller
// "polaroid"-style slide with more black around it. Bigger round = more
// pronounced corner radius. Tuned so the slide reads as a distinct card
// inside the frame, not just a slightly-cropped video.
const DIAPOS_INSET_PCT = 18;
const DIAPOS_ROUND_PX = 30;

// Outgoing-side animation for the predecessor clip during the same window.
const computeOutgoingTransitionState = (
  type: TransitionType,
  t: number,
): ClipRenderState => {
  switch (type) {
    case "crossfade":
      return { opacity: 1 - t };
    case "fade-to-black":
      // A fades to black over the first half, then stays at 0.
      return { opacity: Math.max(0, 1 - t * 2) };
    case "slide":
    case "slide-left": {
      // A holds steady but inherits the same velocity-tracked horizontal
      // blur as B — sells the "whole frame is whipping" feel of viral
      // edits instead of looking like B is sliding through static A.
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "x",
      };
    }
    case "slide-up":
    case "slide-down": {
      const vel = punchEaseOutVelocity(t);
      return {
        opacity: 1,
        blurPx: SLIDE_BLUR_PEAK_PX * vel,
        blurDirection: "y",
      };
    }
    case "reveal": {
      // A is masked to the area the line HASN'T passed yet — i.e. the
      // right side. As the line moves left → right, A's visible region
      // shrinks from the right edge inward, perfectly meeting B at the
      // line's position.
      const eased = revealLineEase(t);
      return {
        opacity: 1,
        wipeShowRightPct: (1 - eased) * 100,
      };
    }
    case "zoom": {
      // A explodes outward fast via punch-ease-out so by t≈0.3 it's
      // already at ~3×, fully blurred, ready to hand off. Stays fully
      // visible during the zoom-in, then SHARPLY cuts out around the
      // same t=0.45–0.55 window where B reveals. Mirroring B's sharp
      // opacity ramp here is what makes the cut feel like a punch
      // instead of a slow crossfade — together with the eased scale
      // and peak blur, the eye reads it as a single explosive moment.
      const eased = punchEaseOut(t);
      const opacity = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.1);
      return {
        opacity,
        scale: 1 + eased * 3.0, // 1× → 4× front-loaded
        blurPx: blurPeakAt(t, ZOOM_BLUR_PEAK_PX),
      };
    }
    case "light-leak":
    case "plastic":
    case "distorsion":
    case "vhs":
    case "vintage":
    case "glitch": {
      // Mirror of the incoming side — A stays fully visible until t=0.5
      // then cuts out sharply.
      return {
        opacity: t < 0.5 ? 1 : Math.max(0, 1 - (t - 0.5) / 0.05),
      };
    }
    case "stretch-horizontal":
    case "stretch-vertical": {
      // A explodes outward along the stretch axis with punch ease-out
      // (front-loaded, fast snap) reaching ~4× by the cut. The OTHER
      // axis stays at 1 so the clip elongates rather than zooming.
      // Same opacity / blur logic as the zoom outgoing.
      const eased = punchEaseOut(t);
      const opacity = t < 0.45 ? 1 : Math.max(0, 1 - (t - 0.45) / 0.1);
      const stretch = 1 + eased * 3; // 1× → 4× front-loaded
      const isHoriz = type === "stretch-horizontal";
      return {
        opacity,
        stretchX: isHoriz ? stretch : 1,
        stretchY: isHoriz ? 1 : stretch,
        blurPx: blurPeakAt(t, SLIDE_BLUR_PEAK_PX),
        blurDirection: isHoriz ? "x" : "y",
      };
    }
    case "diapositivas": {
      // A's perspective in the slideshow transition:
      //   • Phase 1 (CROP): morph from full-frame into the rounded
      //     slide rectangle (mask animates in, no translate yet).
      //   • Phase 2 (SLIDE): slide off-screen to the LEFT while
      //     keeping the slide mask, finishing fully out of view.
      //   • Phase 3 (UNCROP): A is gone — opacity 0.
      if (t < 0.25) {
        // Crop morphs A from full-frame into the slide. Ease-out so
        // the inset/round shoot in fast and land smoothly — the
        // polaroid "appears" with a snap rather than a slow grow.
        const cropT = punchEaseOut(t / 0.25);
        return {
          opacity: 1,
          translateXPct: 0,
          slideInsetPct: DIAPOS_INSET_PCT * cropT,
          slideRoundPx: DIAPOS_ROUND_PX * cropT,
        };
      }
      if (t < 0.75) {
        const slideT = punchEaseOut((t - 0.25) / 0.5);
        return {
          opacity: 1,
          translateXPct: -slideT * 150,
          slideInsetPct: DIAPOS_INSET_PCT,
          slideRoundPx: DIAPOS_ROUND_PX,
        };
      }
      return { opacity: 0 };
    }
  }
};

// Returns the per-frame render state (opacity + optional transform) for
// `clip` at the given timeline `time`. Returns `{ opacity: 1 }` for clips
// that don't intersect any transition window — the common case — so the
// caller can short-circuit cheaply.
const computeClipRenderStateAtTime = (
  clip: TimelineClip,
  time: number,
  clips: TimelineClip[],
): ClipRenderState => {
  // Incoming side: clip has its own transitionIn. The fade window is
  // centered over the boundary so it actually starts a bit before
  // `clip.startTime` (see TRANSITION_EARLY_FRACTION).
  if (clip.transitionIn) {
    const win = getTransitionWindow(clip);
    if (win && time >= win.start && time <= win.end) {
      const t = Math.max(0, Math.min(1, (time - win.start) / win.duration));
      return computeIncomingTransitionState(clip.transitionIn.type, t);
    }
  }
  // Outgoing side: this clip is the adjacent predecessor of a clip that
  // has `transitionIn` and the (centered) fade is currently in progress.
  if (isVideoClip(clip)) {
    const successor = findTransitionSuccessor(clip, clips);
    if (successor && successor.transitionIn) {
      const win = getTransitionWindow(successor);
      if (win && time >= win.start && time <= win.end) {
        const t = Math.max(0, Math.min(1, (time - win.start) / win.duration));
        return computeOutgoingTransitionState(successor.transitionIn.type, t);
      }
    }
  }
  return { opacity: 1 };
};

// Distinct colors for each audio track. Kept in the same vivid-yet-soft family
// as the video (var(--brand-primary)) and elements (#F59E0B) tracks so everything feels
// visually consistent. Cycles for trackIndex beyond the list length.
const AUDIO_TRACK_COLORS = [
  "#10B981", // emerald (default, matches historic audio color)
  "#06B6D4", // sky / cyan
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#F97316", // orange
  "#EAB308", // yellow
  "#14B8A6", // teal
];
const getAudioTrackColor = (trackIndex: number): string =>
  AUDIO_TRACK_COLORS[
    ((trackIndex % AUDIO_TRACK_COLORS.length) + AUDIO_TRACK_COLORS.length) %
      AUDIO_TRACK_COLORS.length
  ];

// ── Lightweight markdown renderer (chat bubbles) ─────────────────────────────
// Supports: **bold**, *italic*, _italic_, `code`, [label](url), ordered/unordered lists,
// headings (#..######), and preserves line breaks. No external dependencies.
const renderInlineMarkdown = (s: string): (string | React.ReactElement)[] => {
  const out: (string | React.ReactElement)[] = [];
  const re =
    /(\*\*[^*\n]+\*\*)|(`[^`\n]+`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*[^*\n]+\*)|(_[^_\n]+_)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1]) out.push(<strong key={key++}>{m[1].slice(2, -2)}</strong>);
    else if (m[2])
      out.push(
        <code
          key={key++}
          className="px-1 py-0.5 rounded bg-black/10 text-[0.9em] font-mono"
        >
          {m[2].slice(1, -1)}
        </code>,
      );
    else if (m[3])
      out.push(
        <a
          key={key++}
          href={m[5]}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:opacity-80"
        >
          {m[4]}
        </a>,
      );
    else if (m[6]) out.push(<em key={key++}>{m[6].slice(1, -1)}</em>);
    else if (m[7]) out.push(<em key={key++}>{m[7].slice(1, -1)}</em>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
};

const renderMarkdown = (text: string): React.ReactNode => {
  const lines = text.split("\n");
  const blocks: React.ReactElement[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trimStart();
    // Unordered list
    if (/^(-|\*)\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^(-|\*)\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^(-|\*)\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={k++} className="list-disc pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trimStart())) {
        items.push(lines[i].trimStart().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={k++} className="list-decimal pl-5 space-y-0.5 my-1">
          {items.map((it, idx) => (
            <li key={idx}>{renderInlineMarkdown(it)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    // GFM table: a header row of pipes followed by a separator row like
    // | --- | :---: | ---: |. Body rows continue until a non-pipe line.
    const isPipeRow = (s: string) => /^\s*\|.*\|\s*$/.test(s);
    const isSeparator = (s: string) =>
      /^\s*\|?\s*:?-{3,}:?(\s*\|\s*:?-{3,}:?)+\s*\|?\s*$/.test(s);
    if (isPipeRow(line) && i + 1 < lines.length && isSeparator(lines[i + 1])) {
      const splitRow = (s: string) =>
        s
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => c.trim());
      const headers = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map((spec) => {
        const l = spec.startsWith(":");
        const r = spec.endsWith(":");
        if (l && r) return "center";
        if (r) return "right";
        return "left";
      });
      i += 2;
      const rows: string[][] = [];
      while (
        i < lines.length &&
        isPipeRow(lines[i]) &&
        !isSeparator(lines[i])
      ) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      blocks.push(
        <div
          key={k++}
          className="my-2 overflow-x-auto rounded-md border border-black/[0.08]"
        >
          <table className="w-full text-[13px] border-collapse">
            <thead className="bg-black/[0.03]">
              <tr>
                {headers.map((h, hi) => (
                  <th
                    key={hi}
                    className="px-3 py-2 font-semibold text-brand-black/80 border-b border-black/[0.08]"
                    style={{
                      textAlign: aligns[hi] as "left" | "center" | "right",
                    }}
                  >
                    {renderInlineMarkdown(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr
                  key={ri}
                  className={
                    ri < rows.length - 1 ? "border-b border-black/[0.06]" : ""
                  }
                >
                  {r.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-2 align-top"
                      style={{
                        textAlign: aligns[ci] as "left" | "center" | "right",
                      }}
                    >
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Heading
    const hMatch = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (hMatch) {
      const level = Math.min(hMatch[1].length + 2, 6);
      const Tag = `h${level}` as keyof React.JSX.IntrinsicElements;
      blocks.push(
        React.createElement(
          Tag,
          { key: k++, className: "font-semibold my-1" },
          renderInlineMarkdown(hMatch[2]),
        ),
      );
      i++;
      continue;
    }
    // Blank line → small spacer (acts as paragraph break)
    if (line.trim() === "") {
      blocks.push(<div key={k++} className="h-2" />);
      i++;
      continue;
    }
    // Paragraph
    blocks.push(
      <p key={k++} className="my-0 whitespace-pre-wrap break-words">
        {renderInlineMarkdown(line)}
      </p>,
    );
    i++;
  }
  return <>{blocks}</>;
};

// Sorted list of distinct audio track indices currently in use. Always contains at least [0] if there are any audio clips.
const getAudioTrackIndices = (clips: TimelineClip[]): number[] => {
  const set = new Set<number>();
  clips.forEach((c) => {
    if (c.type === "audio") set.add(getClipTrackIndex(c));
  });
  return [...set].sort((a, b) => a - b);
};

// ── Cada elemento nuevo estrena su propia capa ─────────────────────────────
// Antes, sumar un video a un proyecto que ya tenía uno lo pegaba DETRÁS del
// último, en la misma pista: el material entraba en fila india y para ponerlo
// en paralelo (o simplemente para moverlo sin empujar al de al lado) había que
// arrastrarlo a mano a otra capa. Ahora cada cosa que se agrega abre una capa
// nueva y arranca en el 0, como una pila de capas y no como una lista.
// Ver ADR-2026-09-15-cada-elemento-nuevo-estrena-capa.
//
// Los subtítulos NO cuentan: son una tanda que vive en su propia fila y con sus
// propios tiempos, así que no empujan el índice de las capas de elementos.
const esElementoDeCapa = (c: TimelineClip): boolean =>
  (c.type === "text" || c.type === "image" || c.type === "animation") &&
  !(c.type === "text" && c.isSubtitle);

// Las tres familias de pistas que se pueden reordenar arrastrando un clip
// hacia arriba o hacia abajo. Cada una tiene su propia numeración de
// `trackIndex`: mezclarlas revolvería las tres.
// Ver ADR-2026-09-15-los-elementos-tambien-se-reordenan.
type TipoDePistaReordenable = "audio" | "video" | "elements";

/** Dónde quiere caer un clip que se suma desde afuera de la timeline: el
 *  segundo y, si se soltó arrastrando, la capa a la que se apuntó
 *  (`ubicarClipNuevo`). */
type UbicacionDeClipNuevo = {
  startTime?: number;
  destino?: DestinoDeSoltar;
  /** Lo que dura el clip nuevo, si ya se sabe: la capa y el inicio se eligen
   *  para que entre sin encimar al siguiente. Un video o audio recién subido
   *  no la trae (se mide después). */
  duracion?: number;
  /** Una URL del MISMO contenido que el navegador ya tiene bajada (la del video de
   *  Atlas que se estaba viendo): el clip nace con ella y no se vuelve a bajar con
   *  la firma nueva. Si vence, el clip se refirma con su `storagePath`
   *  (ADR-2026-11-03-agregar-al-instante). */
  src?: string;
};

const familiaDePista = (c: TimelineClip): TipoDePistaReordenable | null => {
  if (c.type === "audio") return "audio";
  if (c.type === "video" || c.type === "ai-generated") return "video";
  // Los subtítulos no: son una tanda que vive en su propia fila, y sacarle
  // una línea a otra capa parte el grupo.
  if (esElementoDeCapa(c)) return "elements";
  return null;
};

const esDeLaFamilia = (
  c: TimelineClip,
  familia: TipoDePistaReordenable,
): boolean => familiaDePista(c) === familia;

// ── ¿El archivo trae pista de audio? ────────────────────────────────────────
// No hay una API directa: `audioTracks` no existe en Chrome y
// `webkitAudioDecodedByteCount` recién se despega de 0 cuando el video se
// REPRODUJO, así que al abrir el proyecto siempre diría "no tiene".
// `captureStream()` sí lo sabe apenas hay metadata — se verificó contra un
// video grabado sin audio (0 pistas) y contra los del proyecto (1) —, pero pide
// que el elemento se haya cargado con CORS, que es el caso del preview.
//
// Devuelve `null` cuando NO SE PUEDE SABER (navegador sin la API, metadata a
// medio cargar, error de seguridad). Ese caso se trata como "puede tener":
// esconder por las dudas un control que sí servía es peor que mostrar uno de
// más, porque el usuario no tiene forma de sospechar que existía.
const detectarPistaDeAudio = (el: HTMLVideoElement): boolean | null => {
  const conCaptura = el as HTMLVideoElement & {
    captureStream?: () => MediaStream;
    mozCaptureStream?: () => MediaStream;
  };
  const capturar = conCaptura.captureStream ?? conCaptura.mozCaptureStream;
  if (typeof capturar !== "function") return null;
  if (el.readyState < 1) return null;
  try {
    const stream = capturar.call(conCaptura);
    const tiene = stream.getAudioTracks().length > 0;
    // Soltar lo capturado: sin esto el elemento queda con una captura viva por
    // cada clip del proyecto.
    stream.getTracks().forEach((t) => t.stop());
    return tiene;
  } catch {
    return null;
  }
};

const capaNuevaPara = (clips: TimelineClip[], tipo: ClipType): number => {
  const deLaFamilia =
    tipo === "audio"
      ? (c: TimelineClip) => c.type === "audio"
      : tipo === "video" || tipo === "ai-generated"
        ? (c: TimelineClip) => c.type === "video" || c.type === "ai-generated"
        : esElementoDeCapa;
  const usados = clips.filter(deLaFamilia).map(getClipTrackIndex);
  return usados.length === 0 ? 0 : Math.max(...usados) + 1;
};

// Distinct video track indices (for video and ai-generated clips). Always includes 0 so there is a default video row.
const getVideoTrackIndices = (clips: TimelineClip[]): number[] => {
  const set = new Set<number>();
  clips.forEach((c) => {
    if (c.type === "video" || c.type === "ai-generated")
      set.add(getClipTrackIndex(c));
  });
  // La fila 0 se fuerza SÓLO en un proyecto sin ningún clip de video: es la
  // que muestra el "+" con el que se arranca de cero. Antes se forzaba
  // siempre, así que con material en otras capas quedaba una pista vacía
  // colgada abajo que no era de nadie — y el "+" vive ahora al final de cada
  // pista con contenido.
  // Ver ADR-2026-09-18-el-mas-vive-al-final-de-cada-pista.
  if (set.size === 0) set.add(0);
  return [...set].sort((a, b) => a - b);
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const generateId = () => Math.random().toString(36).slice(2, 10);

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Small badge that ticks every second, displays mm:ss elapsed since startedAt.
const ElapsedBadge: React.FC<{ startedAt: number; color: string }> = ({
  startedAt,
  color,
}) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  const label = mm > 0 ? `${mm}:${ss.toString().padStart(2, "0")}` : `${ss}s`;
  return (
    <span
      className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold tabular-nums"
      style={{ backgroundColor: color + "24", color }}
    >
      {label}
    </span>
  );
};

// Topes del zoom de la timeline. Los comparten los tres controles: el
// ctrl+rueda (que ya usaba 0.25–6), los botones de alejar/acercar y el slider.
// El slider los necesita iguales a los de la rueda: si la rueda pudiera pasarse
// de su máximo, el thumb quedaría clavado en la punta y al tocarlo saltaría
// hacia atrás.
const ZOOM_MIN = 0.25;
// 60 = 2400 px/s con `BASE_PPS` 40. Dos razones, las dos pedidas:
//   · **Un cuadro por tira.** Las miniaturas del clip se dibujan en tiras de
//     `FRAME_WIDTH` (56px): a 2400 px/s cada tira son 23ms, o sea un cuadro a
//     ~43fps. Con el tope viejo (20 = 800 px/s) cada tira eran 70ms —tres o
//     cuatro cuadros— y no había forma de apoyarse en uno.
//   · **Un clip de 1 segundo ocupa la pantalla.** "Ajustar timeline" calcula
//     `ancho / (BASE_PPS × segundos)`: para 1s en una pantalla de ~1800px eso
//     da ~45, que con el tope en 20 quedaba recortado a menos de la mitad del
//     ancho. Con 60 entra holgado.
// Al agrandar tanto el rango, el slider pasó a ser LOGARÍTMICO y los botones a
// multiplicar/dividir: con pasos lineales de 0.25, ir de 20 a 60 eran 160
// clicks y el 1x vivía en el 1% del riel.
const ZOOM_MAX = 60;
// Cuánto acerca/aleja un click en la lupa: multiplica en vez de sumar, para que
// el paso se sienta igual en 0.5x que en 40x.
const PASO_DE_ZOOM = 1.5;
// Riel logarítmico: posición 0..1 ↔ zoom. `ln` reparte el recorrido por
// duplicaciones, así que del 1x al 2x hay tanto riel como del 20x al 40x.
const posicionDelZoom = (z: number): number =>
  (Math.log(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z))) - Math.log(ZOOM_MIN)) /
  (Math.log(ZOOM_MAX) - Math.log(ZOOM_MIN));
const zoomDesdePosicion = (p: number): number =>
  Math.exp(
    Math.log(ZOOM_MIN) + p * (Math.log(ZOOM_MAX) - Math.log(ZOOM_MIN)),
  );

// Ancho de los paneles que abren las herramientas del rail (Generar con IA,
// Archivos, Texto, Marca). Eran el mismo ancho que el rail del Director cuando
// el Director era un drawer propio del editor; ese drawer se fue con
// ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente (hoy el chat es
// el de Atlas y lo dibuja su cascarón), así que el número quedó acá solo.
const ANCHO_PANEL_HERRAMIENTAS = 408;

// El botón que abre el chat de Atlas, en la barra del editor. Desde
// ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente abre el chat de Atlas
// (panel lateral) con el contexto del proyecto: el director ES el agente del chat, con
// las herramientas del editor sumadas mientras esta pantalla está montada.
//
// Se llama y se ve como el "Preguntále a Atlas" del resto de la app: mismo ícono
// (`Sparkles` en azul), fondo blanco, borde finito y hover al azul de marca. Antes decía
// "Director de estudio" y traía un videíto que giraba con el puntero; los dos venían de
// cuando el Director era un agente aparte, así que la misma puerta se llamaba y se veía
// de dos formas según la pantalla. El videíto sigue en uso en Flujos (`OrbeDirector`),
// acá no.
//
// Lo único que NO se copia del botón de la web es el tamaño y la sombra: aquél es un
// flotante de esquina (texto 14, sombra larga) y éste vive en la barra, al lado de
// "Exportar", así que toma su altura y su tipografía para no desalinear la fila.
// Ver ADR-2026-09-22-el-estudio-y-el-lienzo-llaman-al-agente-por-su-nombre.
const DirectorButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label="Preguntále a Atlas"
    className="py-1.5 px-3.5 text-[12.5px] rounded-full font-semibold inline-flex items-center justify-center gap-1.5 whitespace-nowrap bg-white border border-[rgb(var(--brand-black-rgb)_/_0.1)] text-brand-black transition-colors hover:text-brand-primary hover:border-[rgb(var(--brand-primary-rgb)_/_0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
  >
    <Sparkles className="w-3.5 h-3.5 shrink-0 text-brand-primary" aria-hidden="true" />
    Preguntále a Atlas
  </button>
);


// ─── AnimationCanvasItem ────────────────────────────────────────────────────
// Wrapper de un único clip de animación dentro del preview. Encapsula:
//   • el div arrastrable (posición + escala),
//   • la capa interna que renderiza el AnimationLayer de Remotion,
//   • un outline calculado en runtime midiendo el bounding box real del
//     contenido pintado, en lugar de envolver toda la canvas.
//
// El outline vive como hermano del wrapper (no dentro), posicionado en
// coordenadas de la canvas. Así el grosor de la línea es siempre 1px sin
// depender del scale, y la caja queda pegada al contenido aunque el
// template ocupe solo una franja del frame (ej. statsGrid).
type AnimationCanvasItemProps = {
  clip: TimelineClip;
  isSelected: boolean;
  xPct: number;
  yPct: number;
  scale: number;
  compW: number;
  compH: number;
  localTime: number;
  propsForTemplate: Record<string, unknown>;
  previewRef: React.RefObject<HTMLDivElement>;
  /** Qué tan adelante va en el preview: lo decide el orden de la timeline
   *  (ver `zDeLaCapa` en el editor), no el orden del DOM. */
  z: number;
  onPointerDown: (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => void;
  onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  // Callback de resize emitido por las manijas del SelectionFrame.
  // El padre traduce los deltas en pixels a un nuevo `animScale`.
  onResize?: (
    handle: SelectionHandle,
    dx: number,
    dy: number,
    phase: "start" | "move" | "end",
  ) => void;
};

const AnimationCanvasItem: React.FC<AnimationCanvasItemProps> = ({
  clip,
  isSelected,
  xPct,
  yPct,
  scale,
  compW,
  compH,
  localTime,
  propsForTemplate,
  previewRef,
  z,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onResize,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Bounding box del contenido pintado, en % de la canvas (preview).
  // null cuando no se pudo medir (ej. todavía no montó el Player).
  const [contentBox, setContentBox] = useState<{
    leftPct: number;
    topPct: number;
    widthPct: number;
    heightPct: number;
  } | null>(null);

  // Mientras el clip está seleccionado medimos el contenido en cada
  // frame (vía rAF). Hace falta porque muchos templates animan posición
  // y opacidad, y un único snapshot quedaría desfasado en cuanto el
  // contenido se mueve. El costo es despreciable: solo corre cuando hay
  // selección activa, y un único elemento a la vez.
  useEffect(() => {
    if (!isSelected) {
      setContentBox(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      const wrapper = wrapperRef.current;
      const canvas = previewRef.current;
      if (wrapper && canvas) {
        const canvasRect = canvas.getBoundingClientRect();
        // Recorremos solo "hojas" — elementos sin hijos — para evitar
        // contar contenedores grandes que envuelven contenido pequeño.
        // Filtramos los que tengan width/height 0 (typical en
        // <AbsoluteFill> vacíos durante la animación inicial).
        const all = wrapper.querySelectorAll<HTMLElement>("*");
        let minX = Infinity,
          minY = Infinity,
          maxX = -Infinity,
          maxY = -Infinity;
        all.forEach((el) => {
          if (el.children.length > 0) return;
          let r = el.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0) return;
          // Si es una <img> con object-fit: contain, el elemento es más
          // grande que la imagen visible (letterbox). Medimos el rect real
          // de la imagen (según su aspecto natural) para que el marco abrace
          // lo visible (ej. un logo cuadrado), no la caja del elemento.
          if (
            el.tagName === "IMG" &&
            getComputedStyle(el).objectFit === "contain"
          ) {
            const img = el as HTMLImageElement;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
              const elAR = r.width / r.height;
              const imgAR = img.naturalWidth / img.naturalHeight;
              let vw = r.width;
              let vh = r.height;
              if (imgAR > elAR) vh = r.width / imgAR;
              else vw = r.height * imgAR;
              r = new DOMRect(
                r.left + (r.width - vw) / 2,
                r.top + (r.height - vh) / 2,
                vw,
                vh,
              );
            }
          }
          // Algunos elementos son completamente transparentes durante
          // las primeras frames; los incluimos igual porque ocupan
          // espacio "lógico" (sino el outline saltaría al aparecer).
          if (r.left < minX) minX = r.left;
          if (r.top < minY) minY = r.top;
          if (r.right > maxX) maxX = r.right;
          if (r.bottom > maxY) maxY = r.bottom;
        });
        if (
          minX !== Infinity &&
          canvasRect.width > 0 &&
          canvasRect.height > 0
        ) {
          const next = {
            leftPct: ((minX - canvasRect.left) / canvasRect.width) * 100,
            topPct: ((minY - canvasRect.top) / canvasRect.height) * 100,
            widthPct: ((maxX - minX) / canvasRect.width) * 100,
            heightPct: ((maxY - minY) / canvasRect.height) * 100,
          };
          // Evitamos setState si el cambio es despreciable (<0.25 px
          // a typical canvas size) — corta re-renders durante easing
          // suaves donde el bbox apenas se mueve.
          setContentBox((prev) => {
            if (
              prev &&
              Math.abs(prev.leftPct - next.leftPct) < 0.05 &&
              Math.abs(prev.topPct - next.topPct) < 0.05 &&
              Math.abs(prev.widthPct - next.widthPct) < 0.05 &&
              Math.abs(prev.heightPct - next.heightPct) < 0.05
            )
              return prev;
            return next;
          });
        }
      }
      raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [isSelected, previewRef]);

  // Calculamos el motion (enter/exit) para este instante. Se aplica en
  // un div *interno* — separado del wrapper de drag — para no contaminar
  // el transform base de posicionamiento, que ya combina translate(-50%)
  // con scale(). Si los componés en un solo div el orden importa y los
  // efectos terminan multiplicándose con la escala manual.
  const motion = computeClipMotion({
    localTime,
    duration: clip.duration,
    enterId: clip.enterAnimation,
    exitId: clip.exitAnimation,
    enterDur: clip.enterDurationSec,
    exitDur: clip.exitDurationSec,
  });

  return (
    <Fragment>
      <div
        ref={wrapperRef}
        onPointerDown={(e) => onPointerDown(e, clip)}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className={`absolute touch-none select-none ${
          isSelected ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{
          left: `${xPct}%`,
          top: `${yPct}%`,
          width: "100%",
          height: "100%",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "center center",
          zIndex: z,
        }}
      >
        {/* Capa interna que aplica enter/exit. Sus transforms se aplican
            *dentro* del scaled wrapper, así un slide de 40px se ve como
            40px en pantalla independientemente del scale del clip. */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={motion.wrapperStyle}
        >
          {clip.animationCodigo ? (
            // El código del agente corre en su iframe sandbox, nunca acá.
            // El código se dibuja con su duración REAL y el clip muestra una
            // ventana: `mediaOffset` corre el comienzo, como en un video
            // (ADR-2026-09-26-las-animaciones-se-recortan-como-un-video).
            <SandboxAnimacion
              clipId={clip.id}
              codigo={versionActual(clip.animationCodigo).codigo}
              props={propsForTemplate as unknown as PropsDeAnimacion}
              localTime={localTime + (clip.mediaOffset ?? 0)}
              durationSec={duracionNaturalDeAnimacion(clip)}
              width={compW}
              height={compH}
            />
          ) : (
            <AnimationLayer
              templateId={clip.animationTemplate as string}
              props={propsForTemplate}
              localTime={localTime}
              durationSec={clip.duration}
              width={compW}
              height={compH}
            />
          )}
        </div>
      </div>

      {/* Marco de selección con manijas. Vive en coordenadas de la
          canvas (no dentro del wrapper escalado) para que el grosor de
          la línea sea 1.5px sin depender del scale, y para que las
          manijas tengan tamaño fijo en pantalla aunque el elemento
          esté muy reducido. */}
      {isSelected && contentBox && (
        <div
          className="absolute"
          style={{
            left: `${contentBox.leftPct}%`,
            top: `${contentBox.topPct}%`,
            width: `${contentBox.widthPct}%`,
            height: `${contentBox.heightPct}%`,
          }}
        >
          {/* Mismo azul que el encuadre de video y que los textos: el marco
          de selección se lee igual en todo el preview. (Girar todavía no:
          este marco se MIDE del DOM y con el elemento girado la medición
          devuelve la caja envolvente, no la caja real.) */}
          <SelectionFrame color={AZUL_DEL_ENCUADRE} onResize={onResize} />
        </div>
      )}
    </Fragment>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

export const VideoEditor: React.FC = () => {
  const { sidebarWidth, setHideSidebar } = useSidebar();
  /* ── El editor en el teléfono ──────────────────────────────────────────────
     Hasta hoy `/editor-video/:projectId` mostraba en mobile un cartel de "solo
     disponible en desktop". Ahora el MISMO componente —mismo estado, mismos
     handlers, mismos paneles— se reacomoda en el cascarón de un editor de
     teléfono: topbar, preview, timeline con el cursor clavado al centro, y una
     barra de herramientas abajo que cambia según haya o no un clip tocado.
     Los paneles de la izquierda pasan a ser hojas que suben desde abajo.
     Ver ADR-2026-09-16-el-estudio-de-video-en-el-telefono.

     El corte es `md` (768px) y no `sm` (640px): el layout de escritorio vive
     bajo `hidden md:flex`, así que entre 640 y 767 preguntando por `sm` se
     montaría el de escritorio… que ahí todavía está oculto por CSS. */
  const enElTelefono = useDebajoDeMd();
  /* ── El chat empuja al editor, no lo tapa ──────────────────────────────────
     El editor es `fixed` clavado al borde derecho de la ventana, así que el
     espaciador con el que el shell le hace lugar al chat (`ColumnaChatShell`)
     no le llega: el panel del agente (`z-30`) se dibujaba ENCIMA del editor
     (`z-20`) y le tapaba el inspector. Ahora el editor lee el MISMO número
     (`useEmpujeChat`) y corre su borde derecho, como el `<main>` del shell.
     `ancho` vale 0 con el chat cerrado y en el teléfono, donde el chat sigue a
     pantalla completa por encima. Desde `lg` el marco flotante deja 8px de aire
     a la derecha (`lg:right-2` de `MARCO_PANTALLA`); se conservan también
     contra el chat. */
  const { ancho: anchoChat } = useEmpujeChat();
  const conMarcoFlotante = useIsDesktop();
  const derechaDelEditor =
    anchoChat > 0 ? anchoChat + (conMarcoFlotante ? 8 : 0) : undefined;
  // El Estudio de Video se queda con la pantalla entera: la sidebar de la app
  // se esconde mientras el editor está montado y vuelve al salir. La salida no
  // se pierde — el topbar tiene el logo de Atlas (al inicio) y "Volver a
  // proyectos" (ADR-2026-09-14-el-editor-tiene-dos-salidas-a-la-izquierda).
  useEffect(() => {
    setHideSidebar(true);
    return () => setHideSidebar(false);
  }, [setHideSidebar]);
  // El editor colapsaba la sidebar al entrar y la restauraba al salir, para
  // ganar ancho. Se sacó con ADR-2026-08-05-el-estudio-de-video-entra-al-shell:
  // el shell nuevo colapsa con su PROPIO estado y sólo re-provee la geometría,
  // así que ese toggle ya no movía la sidebar que se ve — le daba vuelta la
  // preferencia guardada del chrome viejo, calladamente y desde otra pantalla.
  // La sidebar queda como el usuario la dejó, igual que en los otros editores.
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // Llegó desde una sesión de Redes: el topbar ofrece volver a sus creaciones
  // (ADR-2026-10-05-el-estudio-desde-redes-queda-a-mano). Viaja en el estado de la
  // navegación, así que abrir el proyecto desde la lista no lo muestra.
  const vueltaARedes = rutaDeVueltaARedes(useLocation().state);

  // ── Project persistence ────────────────────────────────────────────────────
  const [projectLoading, setProjectLoading] = useState(true);
  const [projectName, setProjectName] = useState("Proyecto sin título");
  const [companyId, setCompanyId] = useState<string | null>(null);
  // Clip que se está editando con IA (Gemini Omni). null = panel cerrado. Solo
  // clips de video ≤10s. Feature nueva, independiente del editor Sora. ADR-500.
  const [omniEditClipId, setOmniEditClipId] = useState<string | null>(null);
  // Clip que se está mejorando con upscale (Topaz). null = panel cerrado. ADR-596.
  const [upscaleClipId, setUpscaleClipId] = useState<string | null>(null);
  // Clip abierto en la sub-pantalla de Eliminar silencios. Mismo patrón que
  // `upscaleClipId`: la herramienta vive en la lista de IA y se abre aparte.
  const [silenceClipId, setSilenceClipId] = useState<string | null>(null);
  // Clip abierto en la sub-pantalla de Corregir voz. Mismo patrón.
  const [corregirVozClipId, setCorregirVozClipId] = useState<string | null>(null);
  // Brand profile attached to this project — drives the "Memoria" panel and
  // the update_brand_context agent tool.
  // Fonts are stored as role -> font family, so the agent and the UI can
  // talk about "fuente del título" / "fuente del cuerpo" instead of a flat
  // list. Each role is optional; missing means "no pref, usar fallback".
  type BrandFontRole =
    | "antetitulo"
    | "titulo"
    | "subtitulo"
    | "cuerpo"
    | "detalles";
  type BrandFontRoles = Partial<Record<BrandFontRole, string>>;
  type BrandProfile = {
    id: string;
    name: string;
    context: string;
    colors: string[];
    fonts: BrandFontRoles;
    logoUrl: string | null;
  };
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  // Whether the read-only "Memoria de la IA" card in the Marca panel is
  // expanded (mirrors the brand view in Configuración → Mis Marcas).
  const [brandMemoriaExpanded, setBrandMemoriaExpanded] = useState(false);
  // Load the brand's suggested fonts so the read-only typography preview in the
  // Marca panel renders in the real families (same as Config → Mis Marcas).
  useEffect(() => {
    const f = (brandProfile?.fonts || {}) as Record<string, unknown>;
    const fams = (
      Array.isArray((f as any).suggested)
        ? ((f as any).suggested as unknown[])
        : Object.values(f)
    ).filter((v): v is string => typeof v === "string");
    if (fams.length === 0) return;
    const id = `brand-marca-fonts-${brandProfile?.id ?? ""}`;
    if (document.getElementById(id)) return;
    const families = fams
      .map((fam) => `family=${encodeURIComponent(fam)}:wght@400;500;700`)
      .join("&");
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    document.head.appendChild(link);
  }, [brandProfile?.id, brandProfile?.fonts]);
  // Active tab inside the Marca panel. The panel used to stack Memoria,
  // Fuentes and Colores vertically; now they share space behind a horizontal
  // segmented selector so the panel stays compact regardless of how long
  // the memoria text is or how many colors / fonts the brand has.
  const [brandTab, setBrandTab] = useState<
    "memoria" | "fuentes" | "colores" | "imagenes"
  >("imagenes");
  // Qué carpeta de la marca se está mirando (Logos / Productos / Personajes /
  // Marcas). Abre en Logos, que es lo que se usa más seguido desde el editor.
  const [carpetaDeMarca, setCarpetaDeMarca] =
    useState<CarpetaDeMarca>("logos");
  // Qué imagen de la marca se está importando ahora mismo (key del tile), para
  // mostrarle el spinner encima y que no se la toque dos veces.
  const [agregandoDeLaMarca, setAgregandoDeLaMarca] = useState<string | null>(
    null,
  );
  // id de la imagen (de moodboard, o de donde venga) → asset del proyecto ya
  // importado. Igual que `productSceneAssetMap`: evita subir dos veces la misma.
  const [imagenesDeMarcaImportadas, setImagenesDeMarcaImportadas] = useState<
    Record<string, string>
  >({});
  // Moodboards de la marca: los arma la plataforma y el editor sólo los lee.
  const [imagenesDeMoodboard, setImagenesDeMoodboard] = useState<
    ImagenDeMoodboard[]
  >([]);
  const [moodboardCargando, setMoodboardCargando] = useState(false);
  // Brand image library: cached per brand profile. Two `tipo` buckets
  // ('logo' / 'element') rendered as two stacked grids inside the
  // Imágenes tab. URLs are resolved lazily and stored alongside the row.
  const [brandAssets, setBrandAssets] = useState<
    Array<BrandAsset & { url?: string }>
  >([]);
  const [brandAssetsLoading, setBrandAssetsLoading] = useState(false);
  const [brandAssetsUploading, setBrandAssetsUploading] =
    useState<BrandAssetTipo | null>(null);
  // Hidden file inputs that the "Subir" tile of each section triggers.
  // Kept as separate refs so each section opens its own picker.
  const brandLogoInputRef = useRef<HTMLInputElement>(null);

  // ── Custom brand fonts ────────────────────────────────────────────────
  // Strip the extension off a font asset's `name` so the family identifier
  // is friendlier (e.g. "Helvetica-Bold" instead of "Helvetica-Bold.woff2").
  // Falls back to the asset id if no name was stored.

  // Load the brand-asset library whenever the active brand changes.
  // We load eagerly (instead of waiting for the Imágenes tab) because
  // custom font assets need to be registered as @font-face rules so any
  // FontPicker in the editor can render them — not just the Marca panel.
  // Signed URLs are resolved in parallel and stored alongside each row.
  useEffect(() => {
    const brandId = brandProfile?.id;
    if (!brandId) {
      setBrandAssets([]);
      return;
    }
    let cancelled = false;
    setBrandAssetsLoading(true);
    (async () => {
      try {
        const list = await fetchBrandAssets(brandId);
        const withUrls = await Promise.all(
          list.map(async (a) => ({
            ...a,
            url: (await getBrandAssetSignedUrl(a.storage_path)) || undefined,
          })),
        );
        if (!cancelled) setBrandAssets(withUrls);
      } catch (e) {
        console.error("[VideoEditor] brand-assets fetch failed", e);
      } finally {
        if (!cancelled) setBrandAssetsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandProfile?.id]);
  // El `font-family` de una fuente subida sale del NOMBRE DEL ARCHIVO. Hay que
  // limpiarlo: `new FontFace(...)` rechaza un nombre con comas o comillas
  // (tira SyntaxError y la fuente no se registra nunca), y no es un caso
  // teórico — en la base hay una subida que se llama
  // "Honk-Regular-VariableFont_MORF,SHLN.ttf". Se van también `;` y las llaves,
  // que romperían la regla CSS aunque el FontFace pase.
  const fontFamilyFromAsset = useCallback((asset: BrandAsset): string => {
    const sinExtension = (asset.name || asset.id)
      .trim()
      .replace(/\.(woff2?|ttf|otf)$/i, "");
    const limpio = sinExtension
      .replace(/["'`,;{}]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return limpio || asset.id;
  }, []);

  // List of custom font family names — derived from the loaded brand
  // assets. Memoized so the @font-face effect below has a stable dep.
  const customFontEntries = useMemo(() => {
    return brandAssets
      .filter((a) => a.tipo === "font" && a.url)
      .map((a) => ({
        id: a.id,
        family: fontFamilyFromAsset(a),
        url: a.url as string,
        // La `url` de arriba es la firmada para el preview y vence; el export
        // vuelve a firmar desde la ruta, que no caduca.
        storagePath: a.storage_path,
      }));
  }, [brandAssets, fontFamilyFromAsset]);
  const customFontFamilies = useMemo(
    () => customFontEntries.map((e) => e.family),
    [customFontEntries],
  );

  // Register a `FontFace` for every uploaded brand font so the browser can
  // actually render text styled with `font-family: <family>`. Cleans up on
  // change so removing a font also removes its rule. The map keeps a
  // reference per asset id; we re-load whenever the URL flips (e.g. signed
  // URL refresh) but keep stable identity otherwise.
  const registeredFontFacesRef = useRef<
    Map<string, { family: string; url: string; face: FontFace }>
  >(new Map());
  useEffect(() => {
    if (typeof document === "undefined" || !(document as any).fonts) return;
    const reg = registeredFontFacesRef.current;
    const seenIds = new Set<string>();
    for (const entry of customFontEntries) {
      seenIds.add(entry.id);
      const existing = reg.get(entry.id);
      if (
        existing &&
        existing.family === entry.family &&
        existing.url === entry.url
      )
        continue;
      // Family or URL changed — drop the previous face before adding the new one.
      if (existing) {
        try {
          (document as any).fonts.delete(existing.face);
        } catch {
          /* noop */
        }
      }
      try {
        const face = new FontFace(entry.family, `url("${entry.url}")`);
        face
          .load()
          .then((loaded) => {
            (document as any).fonts.add(loaded);
          })
          .catch((err) => {
            console.warn(
              "[VideoEditor] custom font load failed",
              entry.family,
              err,
            );
          });
        reg.set(entry.id, { family: entry.family, url: entry.url, face });
      } catch (err) {
        console.warn(
          "[VideoEditor] FontFace construction failed",
          entry.family,
          err,
        );
      }
    }
    // Drop any registered face whose asset is no longer present.
    for (const [id, info] of reg) {
      if (seenIds.has(id)) continue;
      try {
        (document as any).fonts.delete(info.face);
      } catch {
        /* noop */
      }
      reg.delete(id);
    }
  }, [customFontEntries]);

  // Upload a brand image (logo or element). Optimistically prepends the
  // new row to the grid once the request resolves.
  const handleBrandAssetUpload = useCallback(
    async (file: File, tipo: BrandAssetTipo) => {
      const brandId = brandProfile?.id;
      if (!brandId || !companyId) return;
      setBrandAssetsUploading(tipo);
      try {
        const asset = await uploadBrandAsset({
          file,
          brandProfileId: brandId,
          companyId,
          tipo,
        });
        const url =
          (await getBrandAssetSignedUrl(asset.storage_path)) || undefined;
        setBrandAssets((prev) => [{ ...asset, url }, ...prev]);
      } catch (e) {
        console.error("[VideoEditor] brand-asset upload failed", e);
      } finally {
        setBrandAssetsUploading(null);
      }
    },
    [brandProfile?.id, companyId],
  );

  // Sube una tipografía propia desde el picker del panel de Texto y devuelve
  // la familia con la que quedó registrada, para dejarla elegida. Reusa el
  // mismo camino que los logos (`brand_assets`), así que la fuente queda en la
  // MARCA: sirve en todos los proyectos, no sólo en este.
  //
  // A diferencia de `handleBrandAssetUpload`, acá el error SE PROPAGA en vez de
  // ir sólo a la consola: `uploadBrandAsset` rechaza los archivos que no son
  // fuente con un mensaje en castellano, y ese mensaje es lo único que le
  // explica al usuario por qué su .zip no entró.
  // Ver ADR-2026-09-17-la-tipografia-propia-se-sube-desde-el-picker.
  const subirTipografiaPropia = useCallback(
    async (file: File): Promise<string | null> => {
      const brandId = brandProfile?.id;
      if (!brandId || !companyId) {
        throw new Error(
          "Este proyecto todavía no tiene una marca asociada, así que no hay dónde guardar la tipografía.",
        );
      }
      setBrandAssetsUploading("font");
      try {
        const asset = await uploadBrandAsset({
          file,
          brandProfileId: brandId,
          companyId,
          tipo: "font",
        });
        const url =
          (await getBrandAssetSignedUrl(asset.storage_path)) || undefined;
        if (!url) {
          throw new Error(
            "La tipografía se subió pero no se pudo leer de vuelta. Probá recargar el proyecto.",
          );
        }
        setBrandAssets((prev) => [{ ...asset, url }, ...prev]);
        // La familia la deriva `fontFamilyFromAsset` del nombre del archivo:
        // el mismo criterio con el que se arma el @font-face, así que lo que
        // devolvemos acá es exactamente lo que el navegador va a poder pintar.
        return fontFamilyFromAsset(asset);
      } finally {
        setBrandAssetsUploading(null);
      }
    },
    [brandProfile?.id, companyId, fontFamilyFromAsset],
  );

  // Remove a brand asset (storage + row). State is updated first so the
  // tile disappears instantly; the network call follows.
  const handleBrandAssetDelete = useCallback(async (asset: BrandAsset) => {
    setBrandAssets((prev) => prev.filter((a) => a.id !== asset.id));
    try {
      await deleteBrandAsset(asset);
    } catch (e) {
      console.error("[VideoEditor] brand-asset delete failed", e);
    }
  }, []);

  // Load every brand profile belonging to the current company so the
  // header dropdown can offer them. Re-fetches whenever the company
  // changes (e.g. after the project finishes loading).
  const isLoadedRef = useRef(false);

  // ── State ─────────────────────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(DURACION_INICIAL_SEGUNDOS);
  const [zoom, setZoom] = useState(1);
  // Los proyectos nuevos arrancan en vertical: es el formato que más se usa
  // (redes) y evita tener que cambiarlo a mano en casi todos los proyectos.
  // Un proyecto guardado pisa esto con su `aspectRatio` al abrirse.
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("9:16");
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<ToolPanel>("none");
  /** Reabre "Editar clip" sobre un clip, con su edición tal como quedó. */
  const abrirEdicionDeClip = (clipId: string) => {
    setSelectedClipId(clipId);
    setOmniEditClipId(clipId);
    setActivePanel("edit-video-ia");
  };
  // ── De dónde se entró a "Video con IA" ─────────────────────────────────
  // Normalmente se entra desde el rail (o desde "Agregar", en el teléfono) y no
  // hay "atrás": es una pantalla raíz. Pero "Extender video" lo abre desde el
  // panel del CLIP —con el último frame ya cargado como frame inicial—, y ahí
  // el panel es un paso de otro flujo: sin flecha, volver al clip era cerrar y
  // volver a tocarlo. Guarda el panel al que hay que volver, o `null` cuando se
  // entró por la puerta de siempre.
  // Ver ADR-2026-09-17-video-con-ia-vuelve-al-panel-del-clip.
  const [panelDeVueltaDeEscena, setPanelDeVueltaDeEscena] =
    useState<ToolPanel | null>(null);
  // ── Transitions: the id of the SECOND clip in the pair currently being
  // edited. The transitions panel reads this to know which clip's
  // `transitionIn` it's mutating. Cleared when the panel closes.
  const [transitionTargetClipId, setTransitionTargetClipId] = useState<
    string | null
  >(null);

  const [silenceThresholdSec, setSilenceThresholdSec] = useState(0.5);
  // Padding (head/tail) que se deja a cada lado de cada tramo de habla
  // para que los cortes no suenen bruscos — cortar exacto donde termina
  // una palabra suele comer la última consonante o el resonar de la voz.
  // Default 0.12s = sweet spot entre respirado y aire excesivo.
  const [silencePaddingSec, setSilencePaddingSec] = useState(0.12);
  const [silenceCutBusy, setSilenceCutBusy] = useState<string | null>(null);
  const [silenceCutNotice, setSilenceCutNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!silenceCutNotice) return;
    const t = window.setTimeout(() => setSilenceCutNotice(null), 4000);
    return () => window.clearTimeout(t);
  }, [silenceCutNotice]);

  // ── Subtítulos ─────────────────────────────────────────────────────────
  // State for the "Generar subtítulos" modal. `subtitlesLoading` covers
  // the webhook request, `subtitlesError` any failure (network / missing
  // signed URL / etc), and `subtitlesResult` holds both the raw
  // transcript (so the user can see the full text) and the computed
  // chunk list ready to be inserted as text clips. `subtitlesSourceClipId`
  // remembers which clip was the source so we know where to anchor the
  // subtitle clips on the timeline even if the selection changes while
  // the modal is open.
  const [subtitlesModalOpen, setSubtitlesModalOpen] = useState(false);
  const [subtitlesLoading, setSubtitlesLoading] = useState(false);
  const [subtitlesError, setSubtitlesError] = useState<string | null>(null);
  const [subtitlesResult, setSubtitlesResult] = useState<{
    transcript: TranscriptResult;
    segments: Array<{ start: number; end: number; text: string }>;
  } | null>(null);
  const [subtitlesSourceClipId, setSubtitlesSourceClipId] = useState<
    string | null
  >(null);
  // La tool `generar_subtitulos` del agente usa el MISMO flujo que el botón
  // (`openSubtitlesModalForClip` → auto-aplicar). Como es un flujo por estado,
  // el agente espera acá: el efecto que aplica los subtítulos resuelve con la
  // cantidad, y el de error, con el mensaje.
  const subtitulosDelAgenteRef = useRef<
    | ((r: { ok: true; lineas: number } | { ok: false; error: string }) => void)
    | null
  >(null);
  // Con `reemplazar=true`, qué subtítulos viejos saca la tanda nueva (y de qué
  // clip). Se consumen al aplicar la nueva, no antes: si la transcripción
  // falla, los que había siguen ahí.
  const subtitulosAReemplazarRef = useRef<{
    fuenteId: string;
    ids: Set<string>;
  } | null>(null);
  // La transcripción de verdad sigue corriendo aunque el agente haya dejado de
  // esperarla (timeout de dos minutos): mientras tanto no arranca otra, así la
  // que llega tarde no se cruza con una nueva ni consume un reemplazo ajeno.
  const subtitlesLoadingRef = useRef(false);
  subtitlesLoadingRef.current = subtitlesLoading;
  // Animaciones con un `modificar_animacion` en vuelo (por clipId).
  const animacionesEnCambioRef = useRef(new Set<string>());

  // ── Edición de animaciones ───────────────────────────────────────────────
  // Draft con los cambios pendientes. Mientras el usuario edita una
  // animación, los cambios viven acá hasta que apriete "Guardar cambios".
  // Cuando es null, no hay edición en curso. La previsualización del
  // player consulta este draft (si su clipId coincide con el clip que está
  // renderizando) para mostrar los cambios sin haberlos persistido.
  const [animationEditDraft, setAnimationEditDraft] = useState<{
    clipId: string;
    props: Record<string, unknown>;
  } | null>(null);
  // ── Animaciones gráficas ─────────────────────────────────────────────────
  // La biblioteca del proyecto (Archivos → Animaciones): todo lo generado desde
  // el panel o por el agente, con su miniatura. Se guarda con el proyecto.
  const [animacionesGraficas, setAnimacionesGraficas] = useState<
    AnimacionGraficaGuardada[]
  >([]);
  // El panel "Animación gráfica": el prompt, el toggle y el estado del pedido.
  const [animGrafPrompt, setAnimGrafPrompt] = useState("");
  const [animGrafTransparente, setAnimGrafTransparente] = useState(true);
  const [animGrafGenerando, setAnimGrafGenerando] = useState(false);
  const [animGrafError, setAnimGrafError] = useState<string | null>(null);
  // ID del clip en modo "reproducir en loop". Cuando está seteado, la línea
  // de tiempo no se detiene al final del clip: hace snap al inicio y sigue
  // reproduciendo. Se limpia al apretar pausa (en el panel o en cualquier
  // otro botón que pause el video global).
  const [animationPreviewLoopClipId, setAnimationPreviewLoopClipId] = useState<
    string | null
  >(null);
  // Sub-pantalla activa dentro del panel "Elementos". `null` muestra la
  // pantalla raíz con las cards de categoría; los demás valores muestran
  // el contenido de cada categoría. Se resetea automáticamente cuando se
  // cierra el panel.
  const [elementsCategory, setElementsCategory] = useState<
    "text" | "image" | "lottie" | null
  >(null);
  useEffect(() => {
    if (activePanel !== "animation") setElementsCategory(null);
  }, [activePanel]);
  // Inicializa / limpia el draft cuando cambia el clip seleccionado. Solo
  // dependemos de selectedClipId a propósito: cuando guardamos cambios y
  // el array `clips` se actualiza, no queremos que este efecto re-pise el
  // draft con una versión vieja.
  useEffect(() => {
    const sel = clips.find((c) => c.id === selectedClipId);
    if (!sel || sel.type !== "animation") {
      setAnimationEditDraft(null);
      setAnimationPreviewLoopClipId(null);
      return;
    }
    setAnimationEditDraft({
      clipId: sel.id,
      props: { ...(sel.animationProps || {}) },
    });
    setAnimationPreviewLoopClipId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId]);
  // ── Text composer state ─────────────────────────────────────────────────
  // Working draft for the "Añadir texto" panel. Used to build a NEW text clip
  // before it's pushed onto the timeline. When the user selects an existing
  // text clip in the preview, the panel switches to editing mode and writes
  // through to the clip directly via setClips (the textDraft state stays
  // around as the "next new clip" template).
  const [textDraft, setTextDraft] = useState({
    text: "",
    fontFamily: DEFAULT_TEXT_CLIP.fontFamily,
    fontSizePct: DEFAULT_TEXT_CLIP.fontSizePct,
    fontWeight: DEFAULT_TEXT_CLIP.fontWeight as "normal" | "bold",
    fontStyle: DEFAULT_TEXT_CLIP.fontStyle as "normal" | "italic",
    textColor: DEFAULT_TEXT_CLIP.textColor,
    textAlign: DEFAULT_TEXT_CLIP.textAlign as "left" | "center" | "right",
    enterAnimation: "none" as string,
    enterDurationSec: 0.5,
    exitAnimation: "none" as string,
    exitDurationSec: 0.5,
  });
  // Whether the tracks portion of the timeline is collapsed (ruler + transport remain visible).
  // Dropdown above the "Generar con IA" button (Video / Audio options).
  // Dropdown next to the left rail's "Generar con IA" tile (Video / Imagen).
  // ── "Archivos del proyecto" panel state ────────────────────────────────
  // The Files panel unifies what used to be three separate sub-panels
  // (video-library, audio-library, and the implicit image gallery inside
  // the AI image-gen panel). It exposes one place to:
  //   1) upload any media file (video/audio/image),
  //   2) trigger any IA generation (Video, Audio, Imagen),
  //   3) browse existing project assets filtered by media type, with the
  //      `image` tab merging uploaded images + AI-generated ones.
  // Arranca en "media" —Multimedia, del ámbito **Este proyecto**—: lo primero
  // que se busca al abrir Archivos es el material de este proyecto, y el de la
  // cuenta está a un toque. Después NO se vuelve a forzar: la pestaña es
  // estado del editor, así que cerrar y volver a abrir el panel deja lo último
  // que el usuario eligió, sea de un ámbito o del otro.
  // (El id interno sigue siendo `historial` para Creaciones a propósito —
  // renombrarlo tocaría ~25 lugares sin cambiar nada de lo que se ve.)
  const [filesTab, setFilesTab] = useState<FilesTab>("media");
  // El ámbito se DERIVA de la pestaña abierta en vez de ser otro estado: con
  // dos habría que mantenerlos de acuerdo, y cualquiera de los `setFilesTab`
  // sueltos repartidos por el archivo (abrir Archivos desde el Director,
  // importar un producto, elegir un frame) dejaría el segmento marcando una
  // cosa y la lista mostrando otra.
  const ambitoDeArchivos = ambitoDeLaPestana(filesTab);
  // Qué muestra Creaciones. Arranca en todas.
  const [muestraDeCreaciones, setMuestraDeCreaciones] =
    useState<MuestraDeCreaciones>("todas");
  const cambiarAmbitoDeArchivos = useCallback((a: AmbitoDeArchivos) => {
    // Cambiar de ámbito entra por su primera pestaña.
    setFilesTab(PESTANAS_POR_AMBITO[a][0]);
  }, []);
  // Texto del buscador del panel de Archivos. Filtra por nombre lo que se está
  // viendo en la pestaña abierta (no busca en las otras: el resultado
  // aparecería en una pestaña que el usuario no está mirando).
  // Ver ADR-2026-09-15-archivos-con-buscador-y-nombre-en-el-tile.
  const [filesBusqueda, setFilesBusqueda] = useState("");
  // Cambiar de pestaña limpia la búsqueda: el texto de una no significa nada en
  // la otra, y dejarlo puesto hace que la pestaña nueva parezca vacía.
  useEffect(() => {
    setFilesBusqueda("");
  }, [filesTab]);
  // Pill "Productos" del panel Archivos: catálogo de la marca del proyecto.
  const [filesProductItems, setFilesProductItems] = useState<MockProductV2[]>(
    [],
  );
  const [filesProductsLoading, setFilesProductsLoading] = useState(false);
  const [filesImportingProductIds, setFilesImportingProductIds] = useState<
    Set<string>
  >(new Set());
  // Evita el flash del spinner al cambiar de pill: una vez que el panel
  // Archivos cargó la primera vez, los cambios de tab no muestran spinner.
  const filesLoadedOnceRef = useRef(false);
  // Pill "Escenas" del panel Archivos: fondos guardados del cliente + defaults
  // de ATLAS (filtrados por rubro). Mismo shape que productos.
  // Pill "Historial" del panel Archivos: imágenes generadas del cliente
  // (imagenes_generadas), filtradas por la marca del proyecto.
  const [filesHistorialItems, setFilesHistorialItems] = useState<
    MockProductV2[]
  >([]);
  const [filesHistorialLoading, setFilesHistorialLoading] = useState(false);
  // Videos de Creaciones: los que el usuario generó EN LA PLATAFORMA
  // (`imagenes_generadas`, misma tabla que las imágenes), no los assets de los
  // proyectos del Estudio de Video. Estado propio y no `libraryAssets`, que es
  // el bucket que comparten Multimedia y Audio y se pisa al cambiar de pestaña.
  const [filesHistorialVideos, setFilesHistorialVideos] = useState<
    HistorialVideo[]
  >([]);
  // Ids con importación en curso (bajar el mp4 y subirlo como asset del
  // proyecto tarda; sin esto el usuario clickea tres veces).
  const [filesImportingVideoIds, setFilesImportingVideoIds] = useState<
    Set<string>
  >(new Set());
  const [filesImportingHistorialIds, setFilesImportingHistorialIds] = useState<
    Set<string>
  >(new Set());
  // Hidden file input used by the panel's "Subir archivo" CTA. Accepts
  // video + audio + image; the change handler sniffs the MIME and routes
  // to either the timeline-aware video/audio uploader (auto-adds a clip)
  // or a plain image upload (asset-only).
  const filesUploadInputRef = useRef<HTMLInputElement | null>(null);
  // Image assets shown in the `image` tab. Loaded lazily when the tab is
  // opened, refreshed after a manual upload. Kept separate from
  // `libraryAssets` (which holds video OR audio per active sub-panel) so
  // switching tabs doesn't lose the other type's data.
  const [imageLibraryAssets, setImageLibraryAssets] = useState<ProjectAsset[]>(
    [],
  );
  const [imageLibraryLoading, setImageLibraryLoading] = useState(false);
  // Anchored dropdown shown when the user clicks an image tile in the
  // Archivos→Imágenes tab. We track the asset id + the tile's bounding
  // rect so the floating menu can position itself relative to it (same
  // pattern). Restricted to
  // project-asset images; AI-generated thumbs keep their existing
  // single-click "open detail" behavior because turning them into a
  // ref/frame requires first importing as a project asset.
  // Mapa product/scene id → asset id de los importados desde Productos/Escenas.
  // Sirve para deduplicar (no re-importar el mismo) y para ocultarlos de la
  // grilla Imágenes (viven solo en su pill). Persiste en el estado del proyecto.
  const [productSceneAssetMap, setProductSceneAssetMap] = useState<
    Record<string, string>
  >({});

  // ── Modelos (Modo Diálogo) ──────────────────────────────────────────────
  // Picker de modelos para el "Frame inicial" de una escena de diálogo: el
  // modelo elegido (su foto de presentación) pasa a ser la persona que habla.
  // Globales de ATLAS + los de la empresa (sin filtro por marca, como la web).
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  // El modelo elegido va siempre al Frame inicial de Modo Diálogo (lleva a
  // Generar con IA apuntando a ese slot). Los destinos "referencia" y "adjuntar
  // a la imagen" se fueron: los modelos se eligen desde la pestaña del selector
  // (ADR-2026-10-06-la-caja-de-imagen-del-estudio-en-una-linea).
  const [modelItems, setModelItems] = useState<ModelV2[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsImportingIds, setModelsImportingIds] = useState<Set<number>>(
    new Set(),
  );
  // Dedup modelo id → asset id importado (no re-subir la misma foto).
  const [modelAssetMap, setModelAssetMap] = useState<Record<number, string>>(
    {},
  );
  // Alta de personaje desde el picker — misma UX que el catálogo de la V2:
  // card "Agregar" con opciones "Creá tu personaje" (características →
  // CreateView) o "Cargá tu propio personaje" (foto cruda → UploadFlow).
  const [personajeAddView, setPersonajeAddView] = useState<
    "create" | "upload" | null
  >(null);
  // Al cerrar el picker (Cancelar o personaje elegido) el alta vuelve a la
  // grilla — que la próxima apertura no arranque en un form a medio llenar.
  useEffect(() => {
    if (!modelPickerOpen) setPersonajeAddView(null);
  }, [modelPickerOpen]);

  // Clip bounce + click-sound when an AI generation finishes
  const [bounceClipIds, setBounceClipIds] = useState<Set<string>>(new Set());
  const prevAiStatusRef = useRef<Record<string, AiClipStatus | undefined>>({});

  // Panel-field bounce — fires when the agent prefills a sidebar field
  // (prompt textarea, image refs, frames, etc.) so the user sees a small
  // pop where the agent just wrote. Keyed by stable strings (see usages).
  const [bouncePanelKeys, setBouncePanelKeys] = useState<Set<string>>(
    new Set(),
  );
  const triggerPanelBounce = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setBouncePanelKeys((curr) => {
      const next = new Set(curr);
      keys.forEach((k) => next.add(k));
      return next;
    });
    window.setTimeout(() => {
      setBouncePanelKeys((curr) => {
        const next = new Set(curr);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }, 700);
  }, []);
  const panelBounceClass = useCallback(
    (key: string) => (bouncePanelKeys.has(key) ? " clip-bounce" : ""),
    [bouncePanelKeys],
  );

  // Stronger 3x bounce — used by the agent to nudge the user toward an
  // image upload slot (e.g. "necesito que subas el primer frame").
  const [nudgePanelKeys, setNudgePanelKeys] = useState<Set<string>>(new Set());
  const triggerPanelNudge = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setNudgePanelKeys((curr) => {
      const next = new Set(curr);
      keys.forEach((k) => next.add(k));
      return next;
    });
    // 0.65s × 3 iterations ≈ 1950ms; clear shortly after to allow re-trigger.
    window.setTimeout(() => {
      setNudgePanelKeys((curr) => {
        const next = new Set(curr);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }, 2100);
  }, []);
  const panelNudgeClass = useCallback(
    (key: string) => (nudgePanelKeys.has(key) ? " clip-bounce-thrice" : ""),
    [nudgePanelKeys],
  );

  const finishSoundRef = useRef<HTMLAudioElement | null>(null);
  if (typeof window !== "undefined" && !finishSoundRef.current) {
    try {
      finishSoundRef.current = new Audio("/images/clicksound.m4a");
      finishSoundRef.current.preload = "auto";
      finishSoundRef.current.volume = 0.7;
    } catch {}
  }

  // Pincel inteligente (SAM2): frame capturado + clip objetivo, modal de
  // confirmación (tiempo+créditos) y toast de éxito/error del job async.
  // Qué panel estaba abierto cuando se abrió el borrador de objetos, para
  // volver ahí al cerrarlo. El borrador es una capa a pantalla completa por
  // ENCIMA de todo: cerrarlo dejaba al usuario en la timeline pelada, sin la
  // pantalla desde la que había entrado.
  // Ver ADR-2026-09-17-cerrar-el-borrador-vuelve-de-donde-se-vino.
  const panelPrevioAlBorradorRef = useRef<ToolPanel>("none");
  const [eraseBrushClipId, setEraseBrushClipId] = useState<string | null>(null);
  const [eraseBrushFrame, setEraseBrushFrame] = useState<
    { url: string; w: number; h: number } | null
  >(null);
  const [eraseConfirm, setEraseConfirm] = useState<
    { clipId: string; marks: EraseMark[] } | null
  >(null);

  // AI generation (old panel)
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDuration, setAiDuration] = useState<"3" | "5" | "10">("5");

  // AI scene (new panel)
  const [editingClipId, setEditingClipId] = useState<string | null>(null);
  const [scenePrompt, setScenePrompt] = useState("");
  const [sceneDialogue, setSceneDialogue] = useState("");
  const [sceneDialogueInputMode, setSceneDialogueInputMode] = useState<
    "text" | "audio"
  >("text");
  const [sceneDialogueAudio, setSceneDialogueAudio] = useState<{
    url: string;
    storagePath: string;
    durationSec: number;
    assetId: string;
  } | null>(null);
  // "none" sigue EXISTIENDO en el tipo aunque el selector ya no lo ofrezca: es
  // un valor real del payload (`ref_mode: "none"`), lo eligen los prefill del
  // Director cuando el pedido no trae referencias, y viene en clips guardados.
  // Lo que se fue es la opción de la pantalla; el arranque es "reference".
  const [sceneRefMode, setSceneRefModeState] = useState<
    "none" | "reference" | "frames"
  >("reference");
  // Los `prefill_*` del Director abren el panel ANTES de terminar de resolver
  // las imágenes de referencia (ver
  // ADR-2026-08-11-el-panel-del-prefill-abre-antes-de-resolver-referencias).
  // Eso deja al usuario editando un panel mientras hay escrituras en vuelo, así
  // que las tardías tienen que poder detectar que ya no corresponden:
  //   · el token lo pisa un prefill más nuevo,
  //   · el espejo del modo detecta que el usuario lo cambió a mano.
  const scenePrefillTokenRef = useRef(0);
  const sceneRefModeRef = useRef(sceneRefMode);
  // El espejo se actualiza EN EL MISMO TICK que el estado, no en un efecto:
  // entre el click del usuario y el commit de React hay una ventana en la que
  // un efecto todavía no corrió, y si la resolución cae justo ahí el guard lee
  // el valor viejo, cree que nada cambió y pisa al usuario — justo lo que el
  // guard existe para evitar. Envolver el setter mantiene los call sites
  // intactos y saca la sincronización del terreno de "cuándo corre el efecto".
  //
  // El setter además NORMALIZA "none" a "reference". El selector dejó de
  // ofrecer "Ninguna", así que un "none" —de un clip guardado, de un prefill
  // del Director, del estado persistido del panel— dejaría el segmentado sin
  // ninguna pastilla marcada. Se normaliza acá y no en cada call site para que
  // el estado y su espejo (`sceneRefModeRef`) nunca discrepen: el guard del
  // prefill compara contra el ref y con una normalización parcial leería un
  // cambio que el usuario no hizo. Sin referencias cargadas el clip igual sale
  // con `ref_mode: "none"` — lo traduce `refModeEfectivo` al generar.
  const setSceneRefMode = useCallback(
    (mode: "none" | "reference" | "frames") => {
      const visible = mode === "none" ? "reference" : mode;
      sceneRefModeRef.current = visible;
      setSceneRefModeState(visible);
    },
    [],
  );
  const [sceneMode, setSceneMode] = useState<"general" | "personas">("general");
  // El panel "Video con IA" es UNA sola cara. Tuvo una segunda —las
  // configuraciones avanzadas de diálogo: guión, voz y velocidad de habla— con
  // su entrada debajo del prompt y la flecha de volver en la cabecera; se
  // sacaron a pedido del usuario
  // (ADR-2026-09-17-se-van-las-configuraciones-avanzadas-de-dialogo, supersede
  // ADR-2026-09-16-el-dialogo-es-una-sub-vista-del-panel-de-escena). `sceneMode`
  // y `sceneDialogue` siguen vivos: los escribe el agente con
  // `prefill_ai_scene` y el editor al abrir un clip de diálogo ya creado.
  const [scenePersonType, setScenePersonType] = useState<"static" | "motion">(
    "static",
  );

  // AI audio (new panel) — generate voice-over or music with AI
  // "sfx": efecto de sonido con ElevenLabs (ADR-2026-09-27-efectos-de-sonido-con-elevenlabs).
  const [audioAiType, setAudioAiType] = useState<"voice" | "music" | "sfx">(
    "voice",
  );
  const [audioAiPrompt, setAudioAiPrompt] = useState("");
  // When `voice` mode, the user can either type text (default) or record/upload
  // their own audio. Audio mode goes through ElevenLabs speech-to-speech in the
  // workflow instead of TTS.
  const [audioAiDialogueInputMode, setAudioAiDialogueInputMode] = useState<
    "text" | "audio"
  >("text");
  const [audioAiDialogueAudio, setAudioAiDialogueAudio] = useState<{
    url: string;
    storagePath: string;
    durationSec: number;
    assetId: string;
  } | null>(null);
  const [audioAiDuration, setAudioAiDuration] = useState<number>(8);
  const [audioAiVoiceId, setAudioAiVoiceId] = useState<string | null>(null);
  const [audioAiVoiceName, setAudioAiVoiceName] = useState<string>("");
  const [audioAiInstrumentalOnly, setAudioAiInstrumentalOnly] =
    useState<boolean>(false);
  const [audioAiSpeechSpeed, setAudioAiSpeechSpeed] = useState<number>(1.1);
  const [showAudioAiVoiceSelector, setShowAudioAiVoiceSelector] =
    useState(false);
  const [audioAiVoices, setAudioAiVoices] = useState<
    Array<{
      id: string;
      nombre: string;
      sexo: string;
      descripcion: string;
      preview_url: string;
      idioma: string;
      acento: string;
    }>
  >([]);
  const [audioAiVoicesLoading, setAudioAiVoicesLoading] = useState(false);
  const [audioAiVoicesFetched, setAudioAiVoicesFetched] = useState(false);
  const [audioAiVoicePlayingId, setAudioAiVoicePlayingId] = useState<
    string | null
  >(null);
  const [audioAiVoiceSexFilter, setAudioAiVoiceSexFilter] =
    useState<string>("Todas");
  const [audioAiVoiceLanguageFilter, setAudioAiVoiceLanguageFilter] =
    useState<string>("Todas");
  const [voiceSexMenuOpen, setVoiceSexMenuOpen] = useState(false);
  const [voiceLangMenuOpen, setVoiceLangMenuOpen] = useState(false);
  const audioAiVoicePreviewRef = useRef<HTMLAudioElement | null>(null);

  // ── Voice cloning state ──────────────────────────────────────────────
  // El voice picker tiene 2 tabs: 'mis-voces' (voces clonadas de la
  // empresa) y 'voces-atlas' (catálogo global preset). El 'view'
  // permite alternar entre la lista y la sub-vista de creación.
  const [audioAiVoiceView, setAudioAiVoiceView] = useState<
    "list" | "create"
  >("list");
  const [audioAiVoiceTab, setAudioAiVoiceTab] = useState<
    "mis-voces" | "voces-atlas"
  >("voces-atlas");
  const [clonedVoices, setClonedVoices] = useState<ClonedVoice[]>([]);
  const [clonedVoicesLoading, setClonedVoicesLoading] = useState(false);
  const [clonedVoicesFetched, setClonedVoicesFetched] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  // Ref espejo: los callbacks de los tours (onDone diferido) leen el userId
  // FRESCO al momento de marcar visto, no el capturado al crear el effect.
  const currentUserIdRef = useRef<string | null>(null);
  currentUserIdRef.current = currentUserId;

  // Fetch user id once on mount — needed para pasarle a VoiceCloneCreator.
  // De la sesión guardada, sin pedido de red (ADR-2026-11-03-abrir-sin-esperar).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user;
      if (cancelled) return;
      setCurrentUserId(user?.id ?? null);
      // Cargar el estado "ya vi este onboarding" desde la DB (cross-device),
      // así los gates de los tours leen la verdad antes de decidir si muestran.
      if (user?.id) void loadOnboardingSeen(user.id);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch cloned voices lazily la primera vez que abren el voice picker.
  useEffect(() => {
    if (!showAudioAiVoiceSelector || clonedVoicesFetched) return;
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setClonedVoicesLoading(true);
      const voices = await fetchClonedVoices(companyId);
      if (cancelled) return;
      setClonedVoices(voices);
      setClonedVoicesFetched(true);
      setClonedVoicesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showAudioAiVoiceSelector, clonedVoicesFetched, companyId]);

  // Fetch voices lazily the first time the voice selector opens
  useEffect(() => {
    if (!showAudioAiVoiceSelector || audioAiVoicesFetched) return;
    let cancelled = false;
    (async () => {
      setAudioAiVoicesLoading(true);
      const { data, error } = await supabase
        .from("voces")
        .select("id, nombre, sexo, descripcion, preview_url, idioma, acento")
        .order("nombre");
      if (cancelled) return;
      if (!error && data) setAudioAiVoices(data as any);
      setAudioAiVoicesFetched(true);
      setAudioAiVoicesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [showAudioAiVoiceSelector, audioAiVoicesFetched]);

  // Stop voice preview audio on unmount
  useEffect(
    () => () => {
      if (audioAiVoicePreviewRef.current) {
        audioAiVoicePreviewRef.current.pause();
        audioAiVoicePreviewRef.current = null;
      }
    },
    [],
  );

  // ── AI Image Generation panel state ──
  // Slot de escena (frame inicial / final / referencia) cuyo SELECTOR DE IMAGEN
  // está abierto. Antes esto era un dropdown anclado al slot que sólo listaba
  // las fuentes ("Subir imagen", "Usar desde biblioteca", …) y te mandaba a
  // buscarlas a otro panel; hoy abre un pop-up centrado con las miniaturas de
  // cada fuente adentro. Ver
  // ADR-2026-09-16-el-slot-de-imagen-abre-un-selector-centrado.
  const [selectorDeImagen, setSelectorDeImagen] =
    useState<SelectorTarget | null>(null);
  // Pestaña abierta del selector. Vive afuera del pop-up para que elegir una
  // imagen, volver y abrir otro slot te deje donde estabas.
  const [pestanaDelSelector, setPestanaDelSelector] = useState<
    "subidas" | "creaciones" | "productos" | "personajes"
  >("subidas");
  // When user selects "Generar con IA" from a slot, we remember where to write the result
  const [imageGenTarget, setImageGenTarget] = useState<ImageGenTarget | null>(
    null,
  );
  const [imageGenPrompt, setImageGenPrompt] = useState("");
  const [imageGenResults, setImageGenResults] = useState<GenImageResult[]>([]);
  const [imageGenBusy, setImageGenBusy] = useState(false);
  const [imageGenPendingCount, setImageGenPendingCount] = useState(0);
  const [imageGenError, setImageGenError] = useState<string | null>(null);
  const [imageGenDetailId, setImageGenDetailId] = useState<string | null>(null);
  // True cuando el detalle/edición de una imagen generada se abrió DESDE
  // Multimedia (menú contextual "Editar"). La flecha de atrás vuelve a
  // Archivos → Multimedia en vez de a la grilla de generar / la escena.
  const [imageGenDetailFromFiles, setImageGenDetailFromFiles] = useState(false);
  const [imageGenHistorial, setImageGenHistorial] = useState<string[]>([]);
  const [showImageGenHistorial, setShowImageGenHistorial] = useState(false);
  const [imageGenCompanyId, setImageGenCompanyId] = useState<string | null>(
    null,
  );
  const [imageGenEditing, setImageGenEditing] = useState(false);
  const [imageGenEditPrompt, setImageGenEditPrompt] = useState("");
  // When the agent calls prefill_edit_generated_image, we need to open the
  // detail view AND enter edit mode with a specific prompt. The reset effect
  // on imageGenDetailId would normally clobber that, so we stash the
  // intended prompt here and the reset effect applies it instead of clearing.
  const pendingGenEditPromptRef = useRef<string | null>(null);
  const [imageGenEditSubmitting, setImageGenEditSubmitting] = useState(false);
  const [imageGenEditError, setImageGenEditError] = useState<string | null>(
    null,
  );
  // "Añadir" flow: attach reference images from device or previously-uploaded project gallery
  const [imageGenGalleryAssetIds, setImageGenGalleryAssetIds] = useState<
    string[]
  >([]);
  // Metadata (nombre + tipo) de los assets de la galería del modal "Añadir".
  // Se usa para filtrar: NO mostrar videos/no-imágenes (tiles rotos) ni las
  // copias-asset de imágenes generadas (duplicados, matcheo por nombre).
  const [galleryAssetMeta, setGalleryAssetMeta] = useState<
    Record<string, { name: string | null; type: string }>
  >({});
  const [imageGenAttachedAssetIds, setImageGenAttachedAssetIdsState] = useState<
    string[]
  >([]);
  // Mismo problema que en la escena: `prefill_ai_image` abre el panel antes de
  // resolver los adjuntos, así que el usuario puede tocarlos mientras tanto.
  const imagePrefillTokenRef = useRef(0);
  const imageGenAttachedAssetIdsRef = useRef(imageGenAttachedAssetIds);
  // Mismo criterio que `setSceneRefMode`: el espejo se sincroniza en el tick
  // del cambio, no en un efecto. Acá el setter recibe tanto un valor como un
  // updater —los call sites usan las dos formas— así que el ref se escribe
  // adentro del updater, donde ya está resuelto el valor final. Es idempotente
  // (mismo `prev` ⇒ mismo resultado), así que el doble render de StrictMode no
  // molesta.
  const setImageGenAttachedAssetIds = useCallback(
    (next: string[] | ((prev: string[]) => string[])) => {
      setImageGenAttachedAssetIdsState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        imageGenAttachedAssetIdsRef.current = value;
        return value;
      });
    },
    [],
  );
  const [imageGenImportingGenIds, setImageGenImportingGenIds] = useState<
    Set<string>
  >(new Set());
  // Tracks generated-image ids that have already been imported in this
  // session, mapped to their resulting asset id, so re-clicking toggles
  // attachment instead of importing twice.
  const [imageGenImportedMap, setImageGenImportedMap] = useState<
    Record<string, string>
  >({});
  // "Mejorar prompt" de imagen: el escritor de imagen del backend, el de Redes. Se
  // guarda lo devuelto con su contexto; al generar sin cambios relevantes viaja lo
  // declarado en vez de volver a escribir
  // (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
  const [imageGenMejora, setImageGenMejora] = useState<MejoraDeImagen | null>(null);
  const [imageGenMejorando, setImageGenMejorando] = useState(false);
  const [imageGenAttachedPreviewId, setImageGenAttachedPreviewId] = useState<
    string | null
  >(null);
  const [imageGenUploading, setImageGenUploading] = useState(false);
  // "Seleccionar de video" flow: pick first/last frame from a timeline clip as the target slot's frame
  const [frameFromVideoTarget, setFrameFromVideoTarget] =
    useState<ImageGenTarget | null>(null);
  // Slot-preview flow: when the user picks an asset for a scene slot (e.g. a
  // frame extracted from a clip), instead of assigning it directly we route
  // them through a preview screen — same UX as the generated-image detail —
  // with two actions: Utilizar (commit to the slot) or Editar (open the
  // generation flow with this image attached as a reference + a prompt
  // describing the change).
  const [slotPreview, setSlotPreview] = useState<{
    assetId: string;
    target: ImageGenTarget;
  } | null>(null);
  const [slotPreviewEditing, setSlotPreviewEditing] = useState(false);
  const [slotPreviewEditPrompt, setSlotPreviewEditPrompt] = useState("");
  const [frameFromVideoClipMenu, setFrameFromVideoClipMenu] = useState<{
    clipId: string;
    top: number;
    left: number;
  } | null>(null);
  const [pickerClipRects, setPickerClipRects] = useState<
    Array<{
      clipId: string;
      top: number;
      left: number;
      width: number;
      height: number;
    }>
  >([]);
  const imageGenFileInputRef = useRef<HTMLInputElement>(null);
  const imageGenDetailScrollRef = useRef<HTMLDivElement>(null);
  // Reference images: array of asset IDs from video_project_assets
  const [sceneRefAssetIds, setSceneRefAssetIds] = useState<string[]>([]);
  // Local preview URLs by asset id (blob URL just uploaded, or signed URL after load)
  const [assetPreviewUrls, setAssetPreviewUrls] = useState<
    Record<string, string>
  >({});
  // Cached video thumbnail strips by clip id
  const [clipThumbnails, setClipThumbnails] = useState<
    Record<string, string[]>
  >({});
  const thumbGenRef = useRef<Set<string>>(new Set());
  // Cached waveform peaks by clip id (downsampled amplitude values 0..1)
  const [audioPeaks, setAudioPeaks] = useState<Record<string, number[]>>({});
  const peaksGenRef = useRef<Set<string>>(new Set());
  // Same shape but keyed by ASSET id (independent of timeline clips). Used
  // by the Archivos→Audio tab so each card can render its waveform inline
  // even before the asset has been added to the timeline.
  const [audioAssetPeaks, setAudioAssetPeaks] = useState<
    Record<string, number[]>
  >({});
  const audioAssetPeaksGenRef = useRef<Set<string>>(new Set());
  // Single-track audio preview for the Audio tab: which asset is playing,
  // its current playback progress (0..1) for the moving waveform overlay,
  // and the underlying <audio> element + RAF id so we can clean up cleanly.
  const [playingAudioAssetId, setPlayingAudioAssetId] = useState<string | null>(
    null,
  );
  const [audioPlaybackProgress, setAudioPlaybackProgress] = useState(0);
  const audioPreviewElRef = useRef<HTMLAudioElement | null>(null);
  const audioPreviewRafRef = useRef<number | null>(null);
  // Single-track video preview for the Videos tab. We keep a registry of
  // each tile's <video> element via a ref-callback Map so the toggle helper
  // can pause the previously-playing tile when starting a new one.
  const [playingVideoAssetId, setPlayingVideoAssetId] = useState<string | null>(
    null,
  );
  const videoPreviewElsRef = useRef<Map<string, HTMLVideoElement>>(new Map());
  const sceneRefImageRef = useRef<HTMLInputElement>(null);
  const [sceneFirstFrameAssetId, setSceneFirstFrameAssetId] = useState<
    string | null
  >(null);
  const [sceneLastFrameAssetId, setSceneLastFrameAssetId] = useState<
    string | null
  >(null);
  const [sceneDuration, setSceneDuration] = useState(5);
  const [sceneSpeechSpeed, setSceneSpeechSpeed] = useState(1.1);
  // La multiescena se quitó del panel
  // (ADR-2026-10-05-el-estudio-sin-multiescena-y-con-el-slider-de-redes): una escena
  // es un prompt y una duración. Los clips viejos conservan `aiMultiEnabled`.
  // "Mejorar prompt" de la escena: lo que devolvió el escritor del Creador, con el
  // contexto en que se escribió (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
  const [sceneMejora, setSceneMejora] = useState<MejoraDeEscena | null>(null);
  const [sceneMejorando, setSceneMejorando] = useState(false);
  // When entering dialog + motion, snap duration to the closest UGC-nuevo option.
  useEffect(() => {
    if (!(sceneMode === "personas" && scenePersonType === "motion")) return;
    const OPTS = [4, 6, 8, 12, 14, 16, 20, 22, 24, 28, 30, 32];
    if (OPTS.includes(sceneDuration)) return;
    const snapped = OPTS.reduce((p, c) =>
      Math.abs(c - sceneDuration) < Math.abs(p - sceneDuration) ? c : p,
    );
    setSceneDuration(snapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneMode, scenePersonType]);
  const sceneFirstFrameRef = useRef<HTMLInputElement>(null);
  const sceneLastFrameRef = useRef<HTMLInputElement>(null);
  // "rapido" se quitó del Estudio el 2026-08-30 (clips viejos pueden seguir
  // teniendo aiQuality "rapido" persistido — al editarlos caen a Estándar).
  const [sceneQuality, setSceneQuality] = useState<"estandar" | "profesional">(
    "estandar",
  );
  const [sceneResolution, setSceneResolution] = useState<
    "480p" | "720p" | "1080p"
  >("720p");

  // Dialog mode doesn't support 480p — bump to 720p automatically.
  useEffect(() => {
    if (sceneMode === "personas" && sceneResolution === "480p") {
      setSceneResolution("720p");
    }
  }, [sceneMode, sceneResolution]);

  // El modo personas genera con HeyGen (rama dialogo): Profesional (Seedance
  // 2.5) no aplica ahí — si quedó elegido de otro modo, baja a Estándar para
  // no cobrar la tarifa pro por un video que no la usa.
  useEffect(() => {
    if (sceneMode === "personas" && sceneQuality === "profesional") {
      setSceneQuality("estandar");
    }
  }, [sceneMode, sceneQuality]);

  // Profesional permite hasta 30s; al bajar a Estándar el tope vuelve a
  // 15s — clamp para que el payload nunca salga fuera de rango.
  useEffect(() => {
    if (sceneQuality !== "profesional" && sceneDuration > 15) {
      setSceneDuration(15);
    }
  }, [sceneQuality, sceneDuration]);

  // Modelo de tarifa (utils/credits) según la calidad elegida — mismo mapeo
  // que hace el backend con `quality` (estandar→standard · profesional→pro /
  // Seedance 2.5).
  const sceneCreditModel: VideoModel =
    sceneQuality === "profesional" ? "pro" : "standard";

  // Live credit-cost projection for the audio panel's "Generar" button.
  // Music = flat song price; voice = per-character TTS price unless the user
  // uploaded their own audio (s2s), in which case we charge per real-second.
  // Read by the badge AND by addAiAudioClip's pre-flight guard.
  const audioPanelCost: number = (() => {
    if (audioAiType === "music") return creditsForSong(audioAiDuration);
    if (audioAiType === "sfx") return creditsForEfecto();
    if (
      audioAiDialogueInputMode === "audio" &&
      audioAiDialogueAudio
    ) {
      // ElevenLabs s2s costs the same as TTS per output second.
      // Pricing reference (credits.ts): VOICE_CR_PER_CHAR × CHARS_PER_SEC_AT_1X
      // ≈ 0.047 cr/s. Round up, minimum 1.
      return creditsForVoice(
        Math.ceil(audioAiDialogueAudio.durationSec * 12.5),
      );
    }
    return creditsForVoice(audioAiPrompt.trim().length);
  })();

  // Live credit-cost projection for the scene panel's "Generar" button.
  // The badge reads this; the assertCredits() guard reads it too — so the
  // displayed cost and the enforcement match exactly. Recomputed every
  // render (cheap: one switch + a few arithmetic ops).
  const scenePanelCost: number = (() => {
    if (sceneMode === "personas") {
      // Audio input mode: the user provided their own audio, so we know its
      // exact duration. The workflow charges per-second using the same
      // resolution/quality table as general clips, so we mirror that to
      // keep the badge consistent with the actual deduction.
      if (
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
      ) {
        return creditsForVideoGeneral({
          durationSec: sceneDialogueAudio.durationSec,
          model: sceneCreditModel,
          resolution: sceneResolution as VideoResolution,
        });
      }
      // Static personas: the lone textarea labeled "Diálogo de la escena"
      // writes to `scenePrompt` (a quirk of the current panel JSX). Motion
      // mode is the only one with a dedicated `sceneDialogue` textarea
      // separate from the visual prompt. Picking the wrong source is what
      // makes the badge get stuck at 1 in static mode.
      const dialogText =
        scenePersonType === "motion" ? sceneDialogue : scenePrompt;
      return creditsForVideoDialog({
        chars: dialogText.length,
        speed: sceneSpeechSpeed,
      });
    }
    return creditsForVideoGeneral({
      durationSec: sceneDuration,
      model: sceneCreditModel,
      resolution: sceneResolution as VideoResolution,
    });
  })();

  // Si salís del panel Archivos, cerramos el modo "elegir modelo" para que no
  // quede pegado y reaparezca la próxima vez que abras Archivos.
  useEffect(() => {
    if (activePanel !== "files") setModelPickerOpen(false);
  }, [activePanel]);
  // Onboarding del editor: una vez que el proyecto cargó, si el usuario no lo
  // vio antes, lanzamos el TOUR guiado (spotlight). El "ya lo vi" se persiste en
  // la DB (profiles.onboarding_seen) vía el helper, con localStorage de fallback.
  useEffect(() => {
    if (projectLoading || !currentUserId) return;
    let cancelled = false;
    let t: ReturnType<typeof setTimeout> | null = null;
    // Esperamos el estado de la DB (cross-device) antes de decidir, así no se
    // re-muestra en otra PC si el usuario ya lo vio. loadOnboardingSeen está
    // cacheado, así que esto es instantáneo si ya cargó en el mount.
    (async () => {
      await loadOnboardingSeen(currentUserId);
      if (cancelled || isOnboardingSeen("tour")) return;
      // Esperamos un toque a que el layout del editor esté pintado: el tour mide
      // los elementos (rail, Director, timeline) para el spotlight.
      t = setTimeout(() => {
        if (cancelled) return;
        startEditorTour(() =>
          markOnboardingSeen("tour", currentUserIdRef.current),
        );
      }, 500);
    })();
    return () => {
      cancelled = true;
      if (t) clearTimeout(t);
    };
  }, [projectLoading, currentUserId]);
  // ── Hydrate the "Añadir" gallery with all project image assets ──────────
  // `imageGenGalleryAssetIds` only held hand-curated IDs from inline
  // uploads / agent attachments / persisted state, so images uploaded via
  // other flows (the Archivos panel, logos, reference pickers, …) were
  // invisible here. When the AI image-gen panel opens we fetch every image
  // asset in the project and merge them in, deduped and with existing
  // ordering preserved (existing IDs stay first so recent attachments
  // don't jump around when the DB list lands).
  useEffect(() => {
    if (activePanel !== "ai-image-gen") return;
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const assets = await fetchProjectAssetsByType(projectId, "image");
        if (cancelled) return;
        setGalleryAssetMeta((prev) => {
          const next = { ...prev };
          for (const a of assets)
            next[a.id] = { name: a.name, type: a.type };
          return next;
        });
        setImageGenGalleryAssetIds((prev) => {
          const seen = new Set(prev);
          const merged = [...prev];
          for (const a of assets) {
            if (!seen.has(a.id)) {
              merged.push(a.id);
              seen.add(a.id);
            }
          }
          return merged;
        });
        // Kick off signed-URL resolution for any asset we don't have a
        // preview for yet; the gallery's effect also does this but only
        // runs when the id-set changes, so we preempt it here to avoid a
        // visible empty-placeholder flash.
        const missing = assets.filter((a) => !assetPreviewUrls[a.id]);
        if (missing.length > 0) {
          const resolved = await Promise.all(
            missing.map(async (a) => {
              try {
                const url = await getAssetSignedUrl(a.storage_path);
                return url ? ([a.id, url] as const) : null;
              } catch {
                return null;
              }
            }),
          );
          if (cancelled) return;
          const updates: Record<string, string> = {};
          for (const r of resolved) if (r) updates[r[0]] = r[1];
          if (Object.keys(updates).length > 0) {
            setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
          }
        }
      } catch (err) {
        console.warn("[VideoEditor] failed to hydrate image-gen gallery", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePanel, projectId]);
  // ── Interactive request_user_upload state ──────────────────────────────
  // When the agent calls request_user_upload, it opens a dedicated panel
  // with a custom message + button label so the user can drop files and
  // confirm. The tool call resolves with the asset IDs the user uploaded.
  const uploadResolverRef = useRef<
    ((result: { content: string | any[] }) => void) | null
  >(null);
  const [agentUploadRequest, setAgentUploadRequest] = useState<{
    message: string;
    buttonLabel: string;
    accept: string; // e.g. 'image/*' or 'image/*,video/*'
    // Cuando true, las imágenes subidas se procesan a fondo blanco (personaje)
    // antes de devolverlas al agente (en vez de usarse crudas).
    procesarComoPersonaje?: boolean;
  } | null>(null);
  // Project assets the user has uploaded during the current request session.
  // Reset every time a new request_user_upload is opened.
  const [agentUploadedAssets, setAgentUploadedAssets] = useState<
    Array<{ id: string; name: string; url?: string }>
  >([]);
  const [agentUploadInProgress, setAgentUploadInProgress] = useState(false);
  // Upload a batch of files to the project, tracking them as session uploads
  // so the confirm button can pass their IDs back to the agent tool call.
  const handleAgentUploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files || []);
      if (list.length === 0) return;
      if (!projectId || !companyId) return;
      const procesarModelo = !!agentUploadRequest?.procesarComoPersonaje;
      const cid = companyId ?? imageGenCompanyId;
      const rechazados: string[] = [];
      setAgentUploadInProgress(true);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        for (const file of list) {
          try {
            if (
              procesarModelo &&
              cid &&
              user?.id &&
              file.type.startsWith("image/")
            ) {
              // Procesar la foto como PERSONAJE (fondo blanco) vía n8n
              // (createModelFromPhoto → procesar_modelo_creado), guardarlo como
              // modelo, e importar la imagen PROCESADA como asset del proyecto
              // para devolvérsela al agente (en vez de la foto cruda).
              const nuevo = await createModelFromPhoto({
                companyId: cid,
                userId: user.id,
                name: "",
                file,
              });
              const resp = await fetch(nuevo.thumbnailUrl);
              const blob = await resp.blob();
              const procFile = new File(
                [blob],
                `${(nuevo.nombre || "personaje").slice(0, 40)}.png`,
                { type: blob.type || "image/png" },
              );
              const asset = await uploadProjectAsset({
                file: procFile,
                projectId,
                companyId,
                userId: user.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              setAgentUploadedAssets((prev) => [
                ...prev,
                { id: asset.id, name: nuevo.nombre || file.name, url: blobUrl },
              ]);
            } else {
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(file);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              setAgentUploadedAssets((prev) => [
                ...prev,
                { id: asset.id, name: asset.name || file.name, url: blobUrl },
              ]);
            }
          } catch (err) {
            console.error(
              "[VideoEditor] agent upload file failed",
              file.name,
              err,
            );
            rechazados.push(describeUploadError(err, file.name));
          }
        }
      } finally {
        setAgentUploadInProgress(false);
        // Una sola modal para toda la tanda: con `multiple` el usuario puede
        // elegir 5 archivos y que fallen 3, y encadenar tres modales sería
        // peor que no avisar. Los que sí entraron ya están en la lista.
        if (rechazados.length > 0) setUploadErrorMsg(rechazados.join("\n\n"));
      }
    },
    [projectId, companyId, imageGenCompanyId, agentUploadRequest],
  );
  const removeAgentUploadedAsset = useCallback((assetId: string) => {
    setAgentUploadedAssets((prev) => prev.filter((a) => a.id !== assetId));
  }, []);
  const confirmAgentUpload = useCallback(async () => {
    const resolver = uploadResolverRef.current;
    uploadResolverRef.current = null;
    const sessionAssets = agentUploadedAssets.slice();
    setAgentUploadRequest(null);
    setAgentUploadedAssets([]);
    setActivePanel("none");
    if (!resolver) return;
    if (sessionAssets.length === 0) {
      resolver({ content: JSON.stringify({ cancelled: true }) });
      return;
    }
    // Build a content block array: a leading text summary with the assetIds
    // (so the agent can reference them in subsequent tools), then each
    // uploaded image inline as an image block so Claude can actually SEE it.
    // Videos are listed by name only (Claude can't view video frames here;
    // for that the agent should call analyze_video_quick with the assetId).
    const isVideo = (name: string) =>
      /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(name);
    const isImage = (name: string) =>
      /\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i.test(name);
    const summary = {
      assetIds: sessionAssets.map((a) => a.id),
      assets: sessionAssets.map((a) => ({
        id: a.id,
        name: a.name,
        kind: isVideo(a.name) ? "video" : isImage(a.name) ? "image" : "other",
      })),
    };
    const blocks: any[] = [
      {
        type: "text",
        text:
          `El usuario subió ${sessionAssets.length} archivo(s). ` +
          `Detalle (id | nombre | tipo): ` +
          summary.assets
            .map((a) => `${a.id} | ${a.name} | ${a.kind}`)
            .join("  ;  ") +
          `. A continuación las imágenes inline para que las observes directamente. ` +
          `Para los videos, usá analyze_video_quick con su assetId si necesitás verlos.`,
      },
    ];
    for (const a of sessionAssets) {
      if (!a.url) continue;
      if (isImage(a.name)) {
        try {
          const base64 = await urlToBase64Jpeg(a.url);
          blocks.push({ type: "text", text: `asset_id=${a.id} (${a.name})` });
          blocks.push({
            type: "image",
            source: { type: "base64", media_type: "image/jpeg", data: base64 },
          });
        } catch (err: any) {
          blocks.push({
            type: "text",
            text: `asset_id=${a.id} (${a.name}): no se pudo cargar como imagen (${err?.message || err})`,
          });
        }
      } else if (isVideo(a.name)) {
        blocks.push({
          type: "text",
          text: `asset_id=${a.id} (${a.name}): video — usá analyze_video_quick para ver frames.`,
        });
      } else {
        blocks.push({
          type: "text",
          text: `asset_id=${a.id} (${a.name}): tipo no visualizable directamente.`,
        });
      }
    }
    resolver({ content: blocks });
  }, [agentUploadedAssets]);
  // If the panel gets closed by any means other than confirm (e.g. the
  // user clicked the X in the header, or the agent triggered another
  // prefill_* that changed activePanel) while the tool call is still
  // awaiting, resolve it as cancelled so the agent doesn't hang forever.
  useEffect(() => {
    if (activePanel !== "agent-upload" && uploadResolverRef.current) {
      const resolver = uploadResolverRef.current;
      uploadResolverRef.current = null;
      setAgentUploadRequest(null);
      setAgentUploadedAssets([]);
      resolver({ content: JSON.stringify({ cancelled: true }) });
    }
  }, [activePanel]);
  // ── Interactive request_product_selection state ─────────────────────────
  // Cuando el Director llama request_product_selection, abrimos la pill
  // "Productos" del panel Archivos en modo "selección para el Director".
  // El resolver devuelve los productos elegidos (o null si canceló); el
  // handler de la tool se encarga de importarlos como assets + mandarlos
  // inline al agente. En modo simple, tocar un producto resuelve al toque;
  // en modo múltiple se marcan varios y se confirma con el botón.
  const productPickResolverRef = useRef<
    ((products: MockProductV2[] | null) => void) | null
  >(null);
  const [agentProductRequest, setAgentProductRequest] = useState<{
    message: string;
    buttonLabel: string;
    multiple: boolean;
  } | null>(null);
  const [agentProductSelection, setAgentProductSelection] = useState<
    MockProductV2[]
  >([]);
  const confirmAgentProductPick = useCallback((products: MockProductV2[]) => {
    const resolver = productPickResolverRef.current;
    productPickResolverRef.current = null;
    setAgentProductRequest(null);
    setAgentProductSelection([]);
    setActivePanel("none");
    if (resolver) resolver(products);
  }, []);
  const cancelAgentProductPick = useCallback(() => {
    const resolver = productPickResolverRef.current;
    productPickResolverRef.current = null;
    setAgentProductRequest(null);
    setAgentProductSelection([]);
    if (resolver) resolver(null);
  }, []);
  // Si el usuario sale de la pill Productos mientras hay una selección
  // pendiente, resolvemos como cancelado para que el agente no quede colgado.
  useEffect(() => {
    if (
      productPickResolverRef.current &&
      !(activePanel === "files" && filesTab === "productos")
    ) {
      const resolver = productPickResolverRef.current;
      productPickResolverRef.current = null;
      setAgentProductRequest(null);
      setAgentProductSelection([]);
      resolver(null);
    }
  }, [activePanel, filesTab]);
  // ── Per-message hover actions (rewind & copy) ──────────────────────────
  // Rewind: confirms with the user, then truncates the conversation back to
  // (but excluding) the picked user message and refills the input with its
  // text. Also deletes the matching server-side rows.
  // Modal that lets the user choose an export format (MP4 standard /
  // FCPXML pro). MP4 is grayed-out for now since the render pipeline isn't
  // wired up yet.
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Support modal — shows a QR for desktop users and a "Open WhatsApp Web"
  // shortcut. Same pattern used by Paywall + SubscriptionLimitModal so the
  // contact UX stays consistent across the app.
  // FCPXML export progress + error state. `exporting` is the (loaded, total)
  // pair of asset downloads so the modal can render a determinate bar; null
  // means we're not currently exporting.
  const [exporting, setExporting] = useState<{
    loaded: number;
    total: number;
  } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // ── MP4 (Remotion) render export state ───────────────────────────────────
  // Discriminated union covering the 4 phases of the MP4 export modal:
  // idle (pick a format), rendering (progress + cancel), done (download),
  // error (retry). The `handle` lives both in state (for rendering UI) and
  // a ref (so the modal-close cleanup effect can cancel without reading
  // a stale closure of `exportState`).
  type ExportState =
    | { phase: "idle" }
    | {
        phase: "rendering";
        progress: number;
        label: string;
        handle: RenderHandle;
      }
    | {
        phase: "done";
        outputUrl: string;
        /** Clips que NO entraron al MP4, en palabras del usuario. El
         *  serializer los devuelve en `skipped` y antes se descartaban sin
         *  leerlos: el video salía sin ese texto, ese elemento o ese clip de
         *  IA y no había ningún aviso. */
         avisos?: string[];
      }
    | { phase: "error"; message: string };
  const [exportState, setExportState] = useState<ExportState>({
    phase: "idle",
  });
  const exportHandleRef = useRef<RenderHandle | null>(null);
  // Contador que rota la frase mostrada durante el render. El backend se
  // consulta cada 2.5s y puede devolver el mismo progreso varias veces
  // seguidas; sin este tick el texto quedaría congelado y parecería colgado.
  const [renderMessageTick, setRenderMessageTick] = useState(0);
  useEffect(() => {
    if (exportState.phase !== "rendering") {
      setRenderMessageTick(0);
      return;
    }
    const id = setInterval(
      () => setRenderMessageTick((t) => t + 1),
      RENDER_MESSAGE_ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [exportState.phase]);
  // Cancel any in-flight MP4 render when the modal closes — avoids ghost
  // progress callbacks firing after the dialog is gone — and reset the
  // phase so reopening the modal starts on the format picker.
  useEffect(() => {
    if (exportMenuOpen) return;
    const h = exportHandleRef.current;
    if (h) {
      h.cancel();
      exportHandleRef.current = null;
    }
    if (exportState.phase !== "idle") setExportState({ phase: "idle" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportMenuOpen]);
  // ── Credit balance + insufficient-credits modal ──────────────────────────
  // `companyCredits` mirrors the balance from `lib/saldo` (1 credit
  // = 1 image) and is refetched on mount + on the global
  // `atlas:credits-updated` event, so the editor stays in sync with other
  // pages that decrement the balance.
  const [companyCredits, setCompanyCredits] = useState<number | null>(null);
  const [companyPlan, setCompanyPlan] = useState<string | null>(null);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] =
    useState(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<
    "free-trial-ended" | "plan-limit-reached"
  >("plan-limit-reached");
  // ID del clip pendiente de confirmar traducción. Cuando el usuario
  // clickea "Traducir EN" del toolbar, en vez de disparar el flow directo,
  // seteamos este estado y mostramos un modal con el costo. Si confirma,
  // arrancamos translateClipToEnglish; si cancela, cerramos el modal.
  // Pasar a 1080p un clip borrador (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p).
  const [pasarA1080pClipId, setPasarA1080pClipId] = useState<string | null>(null);
  const [translateConfirmClipId, setTranslateConfirmClipId] = useState<
    string | null
  >(null);
  // "Cambiar voz" (barra IA del clip): clip target + visibilidad del picker.
  const [changeVoiceClipId, setChangeVoiceClipId] = useState<string | null>(
    null,
  );
  // Modal para elegir una imagen ya generada / subida en este proyecto y
  // usarla en alguno de los slots del panel AI Scene (frame inicial, frame
  // final, o como imagen de referencia). El kind indica a qué slot
  // aplicar la imagen seleccionada. null = modal cerrado.
  // El panel Archivos entra en modo selección para un slot de escena IA
  // (frame inicial / final / referencia). null = entrada normal a Archivos.
  const [filesPickTarget, setFilesPickTarget] = useState<
    { kind: "first" | "last" | "ref" } | null
  >(null);
  // Token para descartar importaciones en vuelo que ya no corresponden.
  // Elegir un producto / escena / historial / generada en modo selección sube
  // la imagen antes de poder asignarla, y eso tarda: si mientras tanto el
  // usuario cancela o toca OTRA imagen, la subida vieja termina igual y
  // asignaba el slot sin preguntar (pisando la elección nueva, o llenando un
  // slot de una selección que ya se había cancelado).
  // Se incrementa en dos lados: en cada intento de elección (así la última
  // gana) y ante cualquier cambio de `filesPickTarget`, que cubre el cancelar
  // y el cambio de slot sin tener que tocar cada call site.
  const filesPickTokenRef = useRef(0);
  useEffect(() => {
    filesPickTokenRef.current += 1;
  }, [filesPickTarget]);
  // Dónde estaba el usuario antes de que el modo selección lo mandara a
  // Creaciones. Elegir la imagen de un frame no es "el usuario cambió de
  // carpeta": es un desvío de otro flujo, y al terminar tiene que devolverlo
  // a donde estaba en vez de dejarlo en una carpeta que él no eligió.
  const pestanaAntesDelPicker = useRef<FilesTab | null>(null);
  useEffect(() => {
    if (filesPickTarget) return;
    const previa = pestanaAntesDelPicker.current;
    if (previa === null) return;
    pestanaAntesDelPicker.current = null;
    setFilesTab(previa);
  }, [filesPickTarget]);
  // Abre Archivos en modo selección para un slot. Reemplaza la vieja grilla
  // inline: ahora se elige desde la vista completa de Archivos (solo imágenes,
  // sin "Subir archivo" ni "Generar con IA").
  const openLibraryPicker = useCallback(
    (kind: "first" | "last" | "ref") => {
      // Se recuerda dónde estaba para devolverlo al salir del modo selección.
      if (pestanaAntesDelPicker.current === null) {
        pestanaAntesDelPicker.current = filesTab;
      }
      setFilesPickTarget({ kind });
      setActivePanel("files");
      // Creaciones: lo que se busca para un frame es casi siempre una imagen
      // que ya se generó.
      setFilesTab("historial");
    },
    // `filesTab` va en las deps: sin él, el callback se queda con el del primer
    // render y devolvería al usuario a la carpeta de arranque en vez de a la
    // que tenía abierta. (`filesTab` se declara bastante más arriba, así que
    // acá no hay riesgo de leerlo en zona muerta.)
    [filesTab],
  );
  // ID del clip cuyo registro de asset se está reintentando ahora mismo.
  // Cuando el usuario clickea el badge ⚠ ámbar de "no se guardó en
  // biblioteca", llamamos `registerExistingAsset` de nuevo y mostramos un
  // spinner en el badge mientras corre. null = no hay reintento en curso.
  const [retryingAssetForClipId, setRetryingAssetForClipId] = useState<
    string | null
  >(null);
  // ID del clip cuya signed URL se está reintentando cargar. El badge
  // rojo de "loadError" en el clip dispara esto al hacer click.
  const [retryingLoadForClipId, setRetryingLoadForClipId] = useState<
    string | null
  >(null);

  // Fetch the current company's credit balance and plan once on mount, and
  // refetch whenever any other surface dispatches `atlas:credits-updated`
  // (image generation, AI video success, etc) so the badges and guards
  // reflect reality without a full reload.
  const porMarcaRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    const refetch = async () => {
      try {
        // De la sesión guardada, sin pedido de red (ADR-2026-11-03-abrir-sin-esperar).
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return;
        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        if (!profile?.id_company) return;
        const { data } = await supabase
          .from("company_profiles")
          .select("plan")
          .eq("id_company", profile.id_company)
          .single();
        if (cancelled || !data) return;
        const saldo = await leerSaldo(profile.id_company);
        if (cancelled) return;
        porMarcaRef.current = saldo.facturaPorMarca;
        setCompanyCredits(saldo.disponibles);
        setCompanyPlan((data as { plan?: string | null }).plan ?? null);
      } catch (err) {
        // Silent — without a balance we let actions through (server is the
        // ultimate authority on enforcement); we just can't pre-block.
        console.warn("[VideoEditor] credit balance fetch failed", err);
      }
    };
    void refetch();
    const handler = () => {
      void refetch();
    };
    window.addEventListener("atlas:credits-updated", handler);
    const alCambiarMarca = () => {
      if (porMarcaRef.current) handler();
    };
    window.addEventListener("atlas:brand-changed", alCambiarMarca);
    return () => {
      cancelled = true;
      window.removeEventListener("atlas:credits-updated", handler);
      window.removeEventListener("atlas:brand-changed", alCambiarMarca);
    };
  }, []);

  // Guard: returns true when the user can afford `cost`, otherwise pops the
  // shared SubscriptionLimitModal (same UX as the rest of the app) and
  // returns false. Caller is expected to early-return on `false`.
  // When the balance is unknown (fetch failed) we let the action through —
  // the server still enforces and the user just won't see the pre-check.
  const assertCredits = useCallback(
    (cost: number): boolean => {
      if (companyCredits == null) return true;
      if (companyCredits >= cost) return true;
      setSubscriptionLimitType(
        companyPlan === "gratis" ? "free-trial-ended" : "plan-limit-reached",
      );
      setShowSubscriptionLimitModal(true);
      return false;
    },
    [companyCredits, companyPlan],
  );

  // Refs for textareas that grow with their content up to a max height.
  const imageGenPromptRef = useRef<HTMLTextAreaElement>(null);
  // Auto-grow helper: resets to auto, then clamps to scrollHeight up to `max`.
  const autoGrowTextarea = (el: HTMLTextAreaElement | null, max = 200) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  };
  useEffect(() => {
    autoGrowTextarea(imageGenPromptRef.current);
  }, [imageGenPrompt]);
  // Clips
  const [clips, setClips] = useState<TimelineClip[]>([]);
  // Las ediciones de "Editar clip" en curso o sin guardar, por clip: viven fuera del
  // panel (`lib/edicionDeClip`) y se guardan con el proyecto
  // (ADR-2026-09-27-editar-clip-sigue-con-el-panel-cerrado).
  const sesionesDeEdicion = useSesionesDeEdicion();
  // Qué clips resultaron tener pista de audio. Es efímero a propósito: se
  // recalcula al abrir el proyecto en vez de guardarse en el clip, así una
  // detección que falló una vez no queda grabada para siempre. Un clip que no
  // está acá todavía no se sabe.
  const [tienenAudio, setTienenAudio] = useState<Record<string, boolean>>({});
  // Final real de todo el contenido (para exportar y para el transporte). Se
  // declara acá, PEGADO a `clips` —su única dependencia— y no allá abajo junto
  // al resto de las medidas del timeline: media pantalla lo lee en deps de
  // hooks (el ref del fin de reproducción, el ancho del carril, el clamp del
  // cursor), y las deps se evalúan durante el render, así que tenerlo abajo
  // hacía que cualquier hook nuevo de más arriba reventara con TDZ al montar.
  const contentDuration = useMemo(() => {
    if (clips.length === 0) return 0;
    return clips.reduce((acc, c) => Math.max(acc, c.startTime + c.duration), 0);
  }, [clips]);

  // Onboarding contextual — Transiciones: la 1ª vez que aparece el botón ＋
  // entre dos clips de video pegados, lo resaltamos (es chico y difícil de
  // descubrir). El existence-check dentro de startContextualTour evita que
  // arranque antes de que el botón esté en el DOM. (Va DESPUÉS de declarar
  // `clips` para no caer en TDZ al referenciarlo en las deps.)
  useEffect(() => {
    if (projectLoading) return;
    if (isOnboardingSeen("ctx_transitions")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () =>
          markOnboardingSeen("ctx_transitions", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="clip-transition"]',
            popover: {
              title: "Transiciones",
              description:
                "Cuando dos clips quedan pegados, aparece este botón entre ellos. Tocalo para elegir una transición (fundido, deslizar, glitch…).",
              side: "top",
              align: "center",
            },
          },
        ],
      });
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clips, projectLoading]);
  // Onboarding contextual — Modo Diálogo: la 1ª vez que el usuario abre el panel
  // "Nueva escena con IA" en Modo Diálogo (personas), explicamos sus 3 partes:
  // Frame inicial (la persona/modelo), Diálogo (lo que dice) y Voz (cómo suena).
  // Las anclas dialog-text/dialog-voice existen tanto en sub-modo static como
  // motion; solo una está montada, así que el selector encuentra la correcta.
  useEffect(() => {
    if (activePanel !== "ai-scene" || sceneMode !== "personas") return;
    if (isOnboardingSeen("ctx_dialog")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () =>
          markOnboardingSeen("ctx_dialog", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="dialog-frame"]',
            popover: {
              title: "Frame inicial",
              description:
                `Acá va el ${palabraDeModeloActual().singular} que va a hablar en la escena. Subí una imagen o elegí uno de tu biblioteca.`,
              side: "left",
              align: "start",
            },
          },
          {
            element: '[data-tour="dialog-text"]',
            popover: {
              title: "Diálogo",
              description:
                "Escribí lo que la persona va a decir. También podés grabar tu propia voz en lugar de escribir.",
              side: "left",
              align: "center",
            },
          },
          {
            element: '[data-tour="dialog-voice"]',
            popover: {
              title: "Voz",
              description:
                "Elegí la voz con la que se sincronizan los labios. Podés probar varias hasta encontrar la que mejor queda.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, sceneMode]);
  // Onboarding contextual — Generar video (Modo General): la 1ª vez que se abre
  // el panel "Nueva escena con IA" en Modo General, explicamos el selector de
  // modo (General vs Diálogo), las imágenes de referencia y el prompt.
  useEffect(() => {
    if (activePanel !== "ai-scene" || sceneMode !== "general") return;
    if (isOnboardingSeen("ctx_video")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_video", currentUserIdRef.current),
        steps: [
          // El paso del selector de modo se fue con el selector: el panel es
          // Modo General y no hay nada que elegir.
          {
            element: '[data-tour="scene-reference"]',
            popover: {
              title: "Imágenes de referencia",
              description:
                "Opcional: subí una imagen para guiar el estilo de la escena, o definí frame de inicio y fin. Si no, la IA la crea solo con tu texto.",
              side: "left",
              align: "center",
            },
          },
          {
            element: '[data-tour="scene-prompt"]',
            popover: {
              title: "Prompt de la escena",
              description:
                "Describí qué querés ver. Cuanto más claro seas (qué pasa, dónde, con qué estilo), mejor sale el video.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, sceneMode]);
  // Onboarding contextual — Generar audio: la 1ª vez que se abre el panel
  // "Nuevo audio con IA", explicamos el tipo (Voz en off vs Música) y el texto.
  useEffect(() => {
    if (activePanel !== "ai-audio") return;
    if (isOnboardingSeen("ctx_audio")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_audio", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="audio-type"]',
            popover: {
              title: "Tipo de audio",
              description:
                "Elegí “Voz en off” para una narración hablada, o “Música” para generar una pista de fondo.",
              side: "bottom",
              align: "start",
            },
          },
          {
            element: '[data-tour="audio-text"]',
            popover: {
              title: "¿Qué querés generar?",
              description:
                "En Voz en off, escribí (o grabá) lo que se va a decir. En Música, describí el estilo y el clima que buscás.",
              side: "left",
              align: "center",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel]);
  // Onboarding contextual — Archivos: la 1ª vez que se abre el panel Archivos
  // (no en modo selección de un frame), señalamos las pills menos obvias:
  // Productos (catálogo de la marca), Escenas (fondos) e Historial (lo generado).
  useEffect(() => {
    if (activePanel !== "files" || filesPickTarget) return;
    if (isOnboardingSeen("ctx_files")) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      startContextualTour({
        onDone: () => markOnboardingSeen("ctx_files", currentUserIdRef.current),
        steps: [
          {
            element: '[data-tour="files-pills"]',
            popover: {
              title: "Tus archivos",
              description:
                "Además de Multimedia y Audio (tus subidas), tenés Productos (el catálogo de tu marca), Escenas (fondos listos) e Historial (todo lo que generaste con IA). Usalos sin volver a subir nada.",
              side: "bottom",
              align: "start",
            },
          },
        ],
      });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [activePanel, filesPickTarget]);
  // Ref siempre sincronizado con el último `clips`. Los executors del agente
  // viven en un closure de larga vida (durante toda la corrida del agente) y
  // capturan un `clips` viejo; leer `clipsRef.current` da el estado FRESCO
  // (ej. autocut necesita ver el transcript que generate_transcript acaba de
  // agregar con setClips, aunque el closure todavía no se haya recreado).
  const clipsRef = useRef<TimelineClip[]>([]);
  clipsRef.current = clips;
  const [snapTarget, setSnapTarget] = useState<{
    clipId: string;
    startTime: number;
  } | null>(null);
  // Segundo exacto donde el imán pegó una orilla — al estirar un clip o al
  // moverlo. Sólo vive mientras dura el arrastre: dibuja la guía vertical que
  // cruza todas las pistas para que se vea CON QUÉ se alineó (el clip de
  // referencia puede estar varias filas más abajo).
  const [snapLine, setSnapLine] = useState<number | null>(null);
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState(false);

  // ── Undo / redo history of the `clips` array ─────────────────────────────
  // Implementation notes:
  //   * Snapshots are coalesced via a ~250ms debounce, so a continuous
  //     drag-to-resize ends up as ONE history entry instead of dozens.
  //   * `isApplyingHistoryRef` suppresses snapshotting while an undo or
  //     redo is in flight — without it, restoring an old state would
  //     immediately push the just-restored value back onto the undo
  //     stack, breaking redo.
  //   * `historyInitializedRef` skips the very first non-empty load
  //     (clips populated from Supabase on mount). That isn't a user
  //     action, so we don't want it on the undo stack.
  //   * Stacks are length-capped (`HISTORY_LIMIT`) so memory stays bounded
  //     across long sessions.
  const HISTORY_LIMIT = 50;
  const SNAPSHOT_DEBOUNCE_MS = 250;
  const [undoStack, setUndoStack] = useState<TimelineClip[][]>([]);
  const [redoStack, setRedoStack] = useState<TimelineClip[][]>([]);
  const isApplyingHistoryRef = useRef(false);
  // Blobs de clips borrados: se revocan al cerrar el editor, no al borrar (el
  // Deshacer los necesita). ADR-2026-11-03-deshacer-no-revive-procesos.
  const blobsParaRevocarRef = useRef<Set<string>>(new Set());
  // Escenas con IA que el usuario canceló, por execution_id
  // (ADR-2026-11-03-cancelar-una-escena-del-estudio).
  const escenasCanceladasRef = useRef<Set<string>>(new Set());
  useEffect(
    () => () => {
      for (const u of blobsParaRevocarRef.current) URL.revokeObjectURL(u);
      blobsParaRevocarRef.current.clear();
    },
    [],
  );
  const historyInitializedRef = useRef(false);
  const lastSnapshotRef = useRef<TimelineClip[]>([]);
  const snapshotTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Skip while replaying history.
    if (isApplyingHistoryRef.current) {
      isApplyingHistoryRef.current = false;
      lastSnapshotRef.current = clips;
      return;
    }
    // Treat the first non-empty load as the initial baseline, not as
    // a user-driven change worth pushing.
    if (!historyInitializedRef.current) {
      if (clips.length > 0) {
        historyInitializedRef.current = true;
        lastSnapshotRef.current = clips;
      }
      return;
    }
    // Coalesce rapid sequential changes into one history entry.
    if (snapshotTimerRef.current) window.clearTimeout(snapshotTimerRef.current);
    const previous = lastSnapshotRef.current;
    snapshotTimerRef.current = window.setTimeout(() => {
      // Only push if something actually changed at the snapshot level.
      if (previous === lastSnapshotRef.current) {
        setUndoStack((prev) => {
          const next = [...prev, previous];
          if (next.length > HISTORY_LIMIT) next.shift();
          return next;
        });
        setRedoStack([]);
        lastSnapshotRef.current = clips;
      }
    }, SNAPSHOT_DEBOUNCE_MS);
    return () => {
      if (snapshotTimerRef.current) {
        window.clearTimeout(snapshotTimerRef.current);
      }
    };
  }, [clips]);

  // Deshacer y Rehacer aplican el paso COMBINADO con cómo están ahora los procesos
  // (una escena que ya terminó no vuelve a "generando", una subida que terminó no
  // pierde su archivo) y saltean los pasos que, combinados, no cambian nada: el
  // "pasó a lista" de un proceso no es algo que el usuario hizo
  // (ADR-2026-11-03-deshacer-no-revive-procesos). Leen las pilas por ref y hacen
  // los sets afuera de los updaters (con StrictMode, un set adentro de otro updater
  // se duplicaba).
  const undoStackRef = useRef(undoStack);
  undoStackRef.current = undoStack;
  const redoStackRef = useRef(redoStack);
  redoStackRef.current = redoStack;
  const aplicarPasoDelHistorial = useCallback(
    (desde: "undo" | "redo") => {
      const pila = desde === "undo" ? undoStackRef.current : redoStackRef.current;
      if (pila.length === 0) return;
      const actuales = clipsRef.current;
      const igualAAhora = (c: TimelineClip[]) =>
        JSON.stringify(c) === JSON.stringify(actuales);
      let i = pila.length - 1;
      let combinado = conLosProcesosDeAhora(pila[i], actuales);
      while (i > 0 && igualAAhora(combinado)) {
        i -= 1;
        combinado = conLosProcesosDeAhora(pila[i], actuales);
      }
      const restante = pila.slice(0, i);
      const setPila = desde === "undo" ? setUndoStack : setRedoStack;
      const setOtra = desde === "undo" ? setRedoStack : setUndoStack;
      setPila(restante);
      if (igualAAhora(combinado)) return;
      setOtra((otra) => {
        const next = [...otra, lastSnapshotRef.current];
        if (next.length > HISTORY_LIMIT) next.shift();
        return next;
      });
      isApplyingHistoryRef.current = true;
      setClips(combinado);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const undo = useCallback(() => aplicarPasoDelHistorial("undo"), [aplicarPasoDelHistorial]);
  const redo = useCallback(() => aplicarPasoDelHistorial("redo"), [aplicarPasoDelHistorial]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  // Keyboard shortcuts. Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo
  // (also accept Cmd/Ctrl+Y on Windows for redo). Suppressed while the
  // user is typing in an input/textarea/contenteditable to avoid eating
  // their native browser undo for text fields.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || target?.isContentEditable)
        return;
      const key = e.key.toLowerCase();
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [undo, redo]);

  // Barra espaciadora = reproducir / pausar, como en cualquier editor de video.
  // Tres cuidados para que no moleste:
  //   · si el foco está escribiendo (prompt, nombre del proyecto, el chat del
  //     Director), el espacio es un espacio y nada más;
  //   · si el foco está en un botón o un control (el usuario acaba de tocar
  //     "Generar", o está moviendo un slider), gana el comportamiento nativo —
  //     interceptarlo rompería activar con teclado;
  //   · con Cmd/Ctrl/Alt/Shift no hace nada: esas combinaciones son de otros.
  // El `preventDefault` es lo que evita que la página scrollee al apretarla.
  // Play/pausa, con rebobinado automático: si el cursor quedó parado en el
  // final —que es donde lo deja el loop cuando termina—, volver a darle play
  // arranca desde 0 en vez de no hacer nada visible.
  //
  // Lee el tiempo y el final por REF y no por dependencia: `currentTime` cambia
  // en cada frame mientras se reproduce, así que un `useCallback` que dependa
  // de él haría que el efecto del espacio desenganche y reenganche el listener
  // sesenta veces por segundo.
  const currentTimeRef = useRef(currentTime);
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);
  const finDeReproduccionRef = useRef(0);
  useEffect(() => {
    finDeReproduccionRef.current =
      contentDuration > 0 ? contentDuration : duration;
  }, [contentDuration, duration]);
  const isPlayingRef = useRef(isPlaying);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  const alternarReproduccion = useCallback(() => {
    if (isPlayingRef.current) {
      setIsPlaying(false);
      return;
    }
    // Los setters van FUERA del updater de `setIsPlaying`: un updater tiene que
    // ser puro, y en StrictMode React lo llama dos veces.
    const fin = finDeReproduccionRef.current;
    if (fin > 0 && currentTimeRef.current >= fin - 0.01) setCurrentTime(0);
    setIsPlaying(true);
  }, []);

  useEffect(() => {
    const onSpace = (e: KeyboardEvent) => {
      if (e.code !== "Space" && e.key !== " ") return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        tag === "button" ||
        tag === "a" ||
        target?.isContentEditable ||
        target?.getAttribute("role") === "button"
      )
        return;
      e.preventDefault();
      alternarReproduccion();
    };
    window.addEventListener("keydown", onSpace);
    return () => window.removeEventListener("keydown", onSpace);
  }, [alternarReproduccion]);

  // ← / → mueven el cursor un cuadro (con Shift, un segundo); Inicio y Fin van a
  // las puntas. Antes no había forma de "retroceder" sin ir a la regla
  // (ADR-2026-11-02-la-timeline-sigue-al-cursor). Mismos cuidados que el espacio,
  // más los controles que ya usan las flechas (menús, listas, sliders, diálogos).
  useEffect(() => {
    const onTecla = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable ||
        target?.closest?.(
          "[role='dialog'], [role='menu'], [role='listbox'], [role='radiogroup'], [role='tablist'], [role='slider']",
        )
      )
        return;
      const t = tiempoConLaTecla(
        e.key,
        e.shiftKey,
        currentTimeRef.current,
        finDeReproduccionRef.current,
      );
      if (t === null) return;
      e.preventDefault();
      if (isPlayingRef.current) setIsPlaying(false);
      setCurrentTime(t);
    };
    window.addEventListener("keydown", onTecla);
    return () => window.removeEventListener("keydown", onTecla);
  }, []);

  // ── Project media library (videos + audios) — populated when the user opens the Video/Audio subpanels ──
  const [libraryAssets, setLibraryAssets] = useState<ProjectAsset[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  // Lo que dura el video que una escena lleva de referencia (si la biblioteca ya lo trajo):
  // Enhancor factura también esos segundos y se cobran a la misma tarifa
  // (ADR-2026-10-20-el-video-de-referencia-cobra-entrada-y-salida). Sin el dato queda el
  // cobro de siempre en el botón; el backend cobra igual con la entrada real.
  const segundosDeVideoDeReferencia = useCallback(
    (ids?: string[] | null): number | null => {
      if (!ids || ids.length === 0) return null;
      const v = libraryAssets.find((a) => a.type === "video" && ids.includes(a.id));
      return v?.duration_seconds && v.duration_seconds > 0 ? v.duration_seconds : null;
    },
    [libraryAssets],
  );
  // El costo del panel de la escena con la entrada sumada. `scenePanelCost` se calcula más
  // arriba, antes de que exista la biblioteca; éste es el que leen el botón y el guard.
  const costoDeLaEscena: number = (() => {
    if (sceneMode === "personas") return scenePanelCost;
    const ref = segundosDeVideoDeReferencia(sceneRefAssetIds);
    if (!ref) return scenePanelCost;
    return creditsForVideoGeneral({
      durationSec: sceneDuration,
      model: sceneCreditModel,
      resolution: sceneResolution as VideoResolution,
      segundosDeEntrada: segundosDeEntradaACobrar({
        duracionSalida: sceneDuration,
        calidad: sceneQuality,
        tramoDisponible: ref,
      }),
    });
  })();

  // Measure media clip rects while the frame-from-video picker is active so we can render click hitboxes above the blur overlay
  useEffect(() => {
    if (!frameFromVideoTarget) {
      setPickerClipRects([]);
      return;
    }
    const measure = () => {
      const nodes = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-clip-id][data-is-media="1"]',
        ),
      );
      const rects = nodes
        .map((el) => {
          const r = el.getBoundingClientRect();
          return {
            clipId: el.getAttribute("data-clip-id") || "",
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          };
        })
        .filter((x) => x.clipId && x.width > 0 && x.height > 0);
      setPickerClipRects(rects);
    };
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [frameFromVideoTarget, clips]);

  // La versión liviana para el preview, primero sólo para dev testers
  // (ADR-2026-11-03-el-preview-usa-la-version-liviana). `useUserGroup` trae el
  // valor de su caché al toque; en el primer login (todavía null) esta apertura
  // sigue con el original. Para abrirlo a todos: `true` acá.
  const { isDevTester } = useUserGroup();
  // El saldo, para la barra de arriba (ADR-2026-11-03-el-saldo-se-ve-en-el-estudio).
  const { saldo: saldoDelEstudio } = useSaldo(companyId ?? null);
  const usaVersionLivianaRef = useRef(false);
  usaVersionLivianaRef.current = isDevTester === true;

  // ── Load project from DB ───────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) {
      navigate("/editor-video", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // Fetch companyId for storage paths
        // El usuario sale de la sesión guardada, sin ir al servidor: `getUser()`
        // hace un pedido de red DENTRO del candado de supabase-js, y al recargar
        // la app llega a encolar decenas — el proyecto se pedía a los 7,6 s.
        // El proyecto no espera al perfil (lo filtra la RLS): al recargar, el perfil
        // queda en una fila de decenas de consultas iguales de toda la app y llegaba
        // último. La empresa se anota cuando llega. ADR-2026-11-03-abrir-sin-esperar.
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user || cancelled) return;
        const empresaP: Promise<string | undefined> = Promise.resolve(
          supabase.from("profiles").select("id_company").eq("id", user.id).single(),
        )
          .then(({ data: profile }) => {
            const cId = (profile as any)?.id_company as string | undefined;
            if (cId && !cancelled) setCompanyId(cId);
            return cId;
          })
          .catch(() => undefined);
        const { data, error } = await supabase
          .from("video_projects")
          .select(
            "name, state, duration, brand_profile_id, thumbnail_file_name, updated_at",
          )
          .eq("id", projectId)
          .single();

        if (error || !data) {
          if (!cancelled) navigate("/editor-video", { replace: true });
          return;
        }
        if (cancelled) return;
        setProjectName(data.name || "Proyecto sin título");
        setDuration(data.duration || DURACION_INICIAL_SEGUNDOS);
        // Load the brand profile linked to this project (used by the "Memoria"
        // panel and the update_brand_context agent tool).
        const brandId = (data as any).brand_profile_id as string | null;
        if (brandId) {
          supabase
            .from("brand_profiles")
            .select("id, name, context, colors, fonts, logo_url")
            .eq("id", brandId)
            .maybeSingle()
            .then(({ data: bp }) => {
              if (cancelled || !bp) return;
              setBrandProfile({
                id: bp.id,
                name: bp.name || "Marca",
                context: bp.context || "",
                colors: normalizeBrandColors(bp.colors),
                logoUrl: (bp as any).logo_url ?? null,
                // Coerce legacy shape (array of family names) to an empty
                // role map; new writes from the UI / agent always store an
                // object, so old array data simply gets superseded.
                fonts:
                  (bp as any).fonts &&
                  !Array.isArray((bp as any).fonts) &&
                  typeof (bp as any).fonts === "object"
                    ? ((bp as any).fonts as BrandFontRoles)
                    : {},
              });
            });
        }
        const s = data.state as any;
        if (s && typeof s === "object") {
          // Resolve storage paths to signed URLs for clips with media
          let loadedClips: TimelineClip[] = Array.isArray(s.clips)
            ? s.clips
            : [];
          if (loadedClips.length > 0) {
            // Todas las firmas en UN pedido (y las que siguen vigentes, sin pedido):
            // antes era una por clip y el editor esperaba a la última. Las mitades
            // de un clip dividido comparten archivo y firma
            // (ADR-2026-11-03-el-estudio-abre-rapido).
            let firmadas = new Map<string, string>();
            let falloAlFirmar: string | null = null;
            // Las versiones livianas, en paralelo y en su propio pedido: el preview
            // las usa en vez del original. Si no hay, el clip sigue con el original
            // (ADR-2026-11-03-el-preview-usa-la-version-liviana).
            const conArchivo = loadedClips
              .filter(
                (c) =>
                  c.storagePath &&
                  (c.type === "video" ||
                    c.type === "ai-generated" ||
                    c.type === "audio"),
              )
              .map((c) => c.storagePath!);
            const livianasP: Promise<Map<string, string>> = usaVersionLivianaRef.current
              ? firmarVersionesLivianas(conArchivo, rutaDeLaVersionLiviana).catch(
                  () => new Map<string, string>(),
                )
              : Promise.resolve(new Map<string, string>());
            try {
              firmadas = await firmarVariosAssets(
                loadedClips
                  .filter((c) => c.storagePath && !c.src)
                  .map((c) => c.storagePath!),
              );
            } catch (e) {
              falloAlFirmar = e instanceof Error ? e.message : "Error desconocido";
            }
            const livianas = await livianasP;
            if (usaVersionLivianaRef.current && projectId) {
              // Las que faltan se piden al backend (los proyectos viejos se ponen al
              // día solos, sólo los que se abren). Sin esperar: esta apertura sigue
              // con el original.
              const sinVersion = loadedClips
                .filter(
                  (c) =>
                    c.storagePath &&
                    (c.type === "video" || c.type === "ai-generated") &&
                    !livianas.has(c.storagePath),
                )
                .map((c) => c.storagePath!);
              if (sinVersion.length > 0)
                void pedirVersionesLivianas(projectId, sinVersion);
            }
            loadedClips = loadedClips.map((clip: TimelineClip) => {
              const liviana = clip.storagePath
                ? livianas.get(clip.storagePath)
                : undefined;
              if (!clip.storagePath || clip.src)
                return liviana
                  ? { ...clip, srcPreview: liviana, srcPreviewDe: clip.storagePath }
                  : clip;
              const url = firmadas.get(clip.storagePath);
              // Con la firma, el aviso de una apertura anterior (si quedó guardado) ya
              // no vale (ADR-2026-11-03-el-no-se-pudo-cargar-no-se-guarda).
              if (url)
                return {
                  ...clip,
                  src: url,
                  srcPreview: liviana,
                  srcPreviewDe: liviana ? clip.storagePath : undefined,
                  loadError: undefined,
                };
              // Sin URL: el archivo probablemente se borró del bucket (o falló la
              // red). Marcamos el clip; el badge permite reintentar.
              return {
                ...clip,
                loadError: falloAlFirmar
                  ? `No pudimos cargar el archivo: ${falloAlFirmar}`
                  : "El archivo de este clip no se encontró en el servidor.",
              };
            });
          }
          // Las animaciones por código guardadas antes de que se recortaran como un
          // video toman como duración real la que tenían
          // (ADR-2026-09-26-las-animaciones-se-recortan-como-un-video).
          loadedClips = loadedClips.map(conVentanaDeAnimacion);
          // Lo que se cortó al cerrar vuelve con su motivo (o se retoma, la
          // traducción) en vez de quedar trabado o negro sin aviso
          // (ADR-2026-11-03-nada-queda-trabado-al-recargar).
          const reparacion = repararAlAbrir(loadedClips);
          loadedClips = reparacion.clips;
          traduccionesARetomarRef.current = reparacion.traduccionesARetomar;
          procesosARetomarRef.current = reparacion.procesosARetomar;
          // Desde qué versión se editó: cada guardado sólo pisa si sigue siendo ésta
          // (ADR-2026-11-03-dos-pestanas-no-se-pisan).
          updatedAtConocidoRef.current =
            (data as { updated_at?: string | null }).updated_at ?? null;
          // Si el proyecto ya tiene portada, se parte del clip que la forma: el
          // primer guardado de cada apertura ya no la rehace (bajar el video, sacar
          // un cuadro y subirlo) si nada cambió (ADR-2026-11-03-el-estudio-abre-rapido).
          if ((data as { thumbnail_file_name?: string | null }).thumbnail_file_name)
            lastThumbnailHashRef.current = hashCoverClip(
              findCoverClipAtZero(loadedClips),
            );
          if (!cancelled) {
            setClips(loadedClips);
            // "Editar clip" que había quedado en curso o sin guardar: se rearma
            // y, si seguía editando, se vuelve a esperar el resultado.
            const edicionesGuardadas = (s.edicionesDeClips ?? {}) as Record<
              string,
              EdicionDeClipGuardada
            >;
            for (const c of loadedClips) {
              const g = edicionesGuardadas[c.id];
              if (g && c.src)
                void retomarEdicionDeClip(
                  c.id,
                  { url: c.src, path: c.storagePath },
                  g,
                );
            }
          }

          // Backfill: register AI-generated ready clips as project assets if missing.
          // This makes them appear in the Video library and persist across clip deletion.
          if (projectId) {
            const backfillTargets = loadedClips.filter(
              (c) =>
                c.type === "ai-generated" &&
                c.aiStatus === "ready" &&
                c.storagePath &&
                !c.assetId,
            );
            if (backfillTargets.length > 0) {
              (async () => {
                const cId = await empresaP;
                if (!cId || cancelled) return;
                const pairs = await Promise.all(
                  backfillTargets.map(async (c) => {
                    try {
                      const asset = await registerExistingAsset({
                        projectId: projectId!,
                        companyId: cId,
                        userId: user.id,
                        storagePath: c.storagePath!,
                        type: "video",
                        name: c.aiPrompt?.slice(0, 80) || c.name || "Video IA",
                        mimeType: "video/mp4",
                        durationSeconds: c.duration,
                      });
                      return asset ? ([c.id, asset.id] as const) : null;
                    } catch {
                      return null;
                    }
                  }),
                );
                if (cancelled) return;
                const byClipId = new Map(
                  pairs.filter((p): p is readonly [string, string] => !!p),
                );
                if (byClipId.size > 0) {
                  setClips((prev) =>
                    prev.map((c) =>
                      byClipId.has(c.id)
                        ? { ...c, assetId: byClipId.get(c.id) }
                        : c,
                    ),
                  );
                }
              })();
            }
          }

          if (typeof s.zoom === "number") setZoom(s.zoom);
          if (
            Array.isArray(s.ordenDeFilas) &&
            s.ordenDeFilas.every((k: unknown) => typeof k === "string")
          )
            setOrdenDeFilas(s.ordenDeFilas as string[]);
          if (esListaDeAnimacionesGuardadas(s.animacionesGraficas))
            setAnimacionesGraficas(s.animacionesGraficas);
          if (typeof s.scenePrompt === "string") setScenePrompt(s.scenePrompt);
          if (s.sceneRefMode) setSceneRefMode(s.sceneRefMode);
          // Redondeamos al restaurar: los proyectos guardados antes de este
          // fix tienen el decimal del clip persistido en `sceneDuration`.
          // Mismo piso que el proveedor: un proyecto guardado con 3s volvía a
          // abrir el panel en 3s y cada Generar moría en 400.
          if (s.sceneDuration) setSceneDuration(clampDuracionDeEscena(s.sceneDuration));
          if (s.aspectRatio) setAspectRatio(s.aspectRatio);
          if (Array.isArray(s.imageGenHistorial))
            setImageGenHistorial(s.imageGenHistorial);
          if (typeof s.imageGenCompanyId === "string")
            setImageGenCompanyId(s.imageGenCompanyId);
          if (Array.isArray(s.imageGenGalleryAssetIds))
            setImageGenGalleryAssetIds(s.imageGenGalleryAssetIds);
          if (
            s.productSceneAssetMap &&
            typeof s.productSceneAssetMap === "object"
          )
            setProductSceneAssetMap(s.productSceneAssetMap);
          if (s.modelAssetMap && typeof s.modelAssetMap === "object")
            setModelAssetMap(s.modelAssetMap);
          if (
            Array.isArray(s.imageGenResults) &&
            s.imageGenResults.length > 0 &&
            typeof s.imageGenCompanyId === "string"
          ) {
            const savedResults: GenImageResult[] = s.imageGenResults;
            const compId = s.imageGenCompanyId;
            // Set placeholders first (clear loading since we have fileName)
            setImageGenResults(
              savedResults.map((r) => ({
                id: r.id,
                fileName: r.fileName,
                loading: !r.fileName,
                createdAt: r.createdAt,
              })),
            );
            // Resolve signed URLs in background
            (async () => {
              // Proyectos viejos no guardaron created_at: lo traemos en un solo
              // batch para poder ordenar Multimedia por fecha real.
              const missingTs = savedResults
                .filter((r) => !r.createdAt)
                .map((r) => r.id);
              const tsById = new Map<string, string>();
              if (missingTs.length > 0) {
                try {
                  // Fetch en lotes para no exceder el largo de URL del gateway
                  // REST con muchos resultados (HTTP 400). Ver utils/supabaseChunkedIn.
                  const rows = await fetchInChunks<any>(missingTs, (chunk) =>
                    supabase
                      .from("imagenes_generadas")
                      .select("id, created_at")
                      .in("id", chunk),
                  );
                  for (const row of ((rows || []) as any[]))
                    if (row?.created_at)
                      tsById.set(String(row.id), row.created_at);
                } catch {}
              }
              const resolved = await Promise.all(
                savedResults.map(async (r) => {
                  const createdAt = r.createdAt || tsById.get(r.id);
                  if (!r.fileName) return { ...r, createdAt };
                  try {
                    const { data: signed } = await supabase.storage
                      .from("generadas")
                      .createSignedUrl(`${compId}/full/${r.fileName}`, 86400);
                    return {
                      ...r,
                      createdAt,
                      signedUrl: signed?.signedUrl,
                      loading: false,
                    };
                  } catch {
                    return { ...r, createdAt, loading: false };
                  }
                }),
              );
              if (!cancelled) setImageGenResults(resolved);
            })();
          }
        }
        // ⚠️ SOLO si esta carga sigue siendo la vigente. `isLoadedRef` es lo
        // que habilita el autosave, y una carga CANCELADA (el usuario recargó
        // o se fue del editor antes de que terminara) nunca llegó a hacer sus
        // `setClips`: el estado de React quedó vacío. Marcarlo igual dejaba el
        // autosave suelto sobre un proyecto vacío y le guardaba `clips: []`
        // encima al proyecto real. Ver ADR-2026-09-15-la-carga-cancelada-no-habilita-guardar.
        if (!cancelled) isLoadedRef.current = true;
      } catch {
        if (!cancelled) navigate("/editor-video", { replace: true });
      } finally {
        if (!cancelled) setProjectLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, navigate]);

  // Hash del clip "de portada" (top-most en t=0) en el último thumbnail subido.
  // Si cambia entre saves, regeneramos el thumbnail en background.
  const lastThumbnailHashRef = useRef<string>("");
  const thumbnailRegenInFlightRef = useRef<boolean>(false);

  // ── Orden de las filas de la timeline ────────────────────────────────────
  // Lista de claves de fila, de arriba hacia abajo. Cualquier capa puede ir a
  // cualquier altura: un texto ENTRE dos videos, el audio arriba de todo. Ese
  // orden es también el que manda en el preview (lo que está más abajo en la
  // timeline se dibuja más atrás), y se guarda con el proyecto.
  // Ver ADR-2026-09-15-el-orden-de-la-timeline-manda-en-el-preview.
  const [ordenDeFilas, setOrdenDeFilas] = useState<string[]>([]);
  // El export necesita saber en qué capa va cada clip, pero `zDelClip` se
  // arma junto con las filas, mucho más abajo que `handleExportMp4`: si
  // entrara en sus dependencias sería TDZ (y el editor no abre). Va por ref,
  // como el resto de los valores vivos que los handlers leen.
  const zDelClipRef = useRef<(c: TimelineClip) => number>(() => 1);
  // Dónde va a caer lo que se arrastra: `posicion` es un HUECO entre filas
  // (0 = arriba de todo) y `yPx` es dónde se dibuja la línea.
  const [insercionDeFila, setInsercionDeFila] = useState<{
    clave: string;
    posicion: number;
    yPx: number;
  } | null>(null);
  // Dónde va a caer lo que se ARRASTRA DESDE AFUERA de la timeline (una ficha
  // de Archivos, un archivo de la computadora) mientras pasa por encima: la
  // misma línea o el mismo recuadro que el reorden de clips, porque es el
  // mismo resultado (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta).
  const [destinoDeLoSoltado, setDestinoDeLoSoltado] =
    useState<DestinoDeSoltar | null>(null);
  // Y en qué SEGUNDO: una línea vertical en el punto donde arranca lo que se
  // suelta. Antes sólo se marcaba la fila (ADR-2026-11-02-lo-tocado-va-a-la-pista-principal).
  const [segundoDeLoSoltado, setSegundoDeLoSoltado] = useState<number | null>(
    null,
  );
  // Hay archivos de la computadora arrastrándose sobre el editor: se pinta el
  // marco punteado que dice que se pueden soltar.
  const [arrastrandoArchivos, setArrastrandoArchivos] = useState(false);
  // Y sobre el cuadro de vista previa en particular.
  const [arrastrandoSobreElPreview, setArrastrandoSobreElPreview] =
    useState(false);

  // ── Auto-save to DB (debounced 3s) ─────────────────────────────────────────
  // El token de la sesión, a mano para el guardado al cerrar la página: ahí no hay
  // tiempo de pedírselo a supabase-js (ADR-2026-11-03-el-ultimo-guardado-sobrevive-al-cierre).
  const tokenDeSesionRef = useRef<string | null>(null);
  // Dos pestañas con el mismo proyecto: antes ganaba la última que guardaba y pisaba
  // todo. Cada guardado va con "sólo si `updated_at` sigue siendo el que conozco"; si
  // otra pestaña guardó en el medio, no pisa y se avisa. `forzarGuardadoRef` es
  // "Quedarme con esto" (ADR-2026-11-03-dos-pestanas-no-se-pisan).
  const updatedAtConocidoRef = useRef<string | null>(null);
  // Las últimas versiones (`updated_at`) que mandó esta pestaña, por instante: así un
  // guardado propio que se cruzó con otro propio no se toma por otra pestaña.
  const versionesPropiasRef = useRef<number[]>([]);
  const recordarVersionPropia = (v: string) => {
    const t = Date.parse(v);
    if (!Number.isFinite(t)) return;
    versionesPropiasRef.current = [...versionesPropiasRef.current.slice(-9), t];
  };
  const esVersionPropia = (v: string) => versionesPropiasRef.current.includes(Date.parse(v));
  const forzarGuardadoRef = useRef(false);
  const [hayConflicto, setHayConflicto] = useState(false);
  const hayConflictoRef = useRef(false);
  useEffect(() => {
    let vivo = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (vivo) tokenDeSesionRef.current = data.session?.access_token ?? null;
    });
    const { data } = supabase.auth.onAuthStateChange((_evento, sesion) => {
      tokenDeSesionRef.current = sesion?.access_token ?? null;
    });
    return () => {
      vivo = false;
      data.subscription.unsubscribe();
    };
  }, []);

  const saveProject = useCallback(async (opciones?: OpcionesDeGuardado): Promise<boolean> => {
    // Nada que guardar todavía (o ya no): cuenta como guardado, no como fallo.
    if (!projectId || !isLoadedRef.current) return true;
    // Strip ephemeral blob URLs and reset 'generating' to 'draft' before saving
    const persistableClips = clips
      // El placeholder de una animación gráfica que se está generando no es un
      // clip todavía: sin código ni plantilla no hay nada que reabrir.
      .filter((c) => !(c.type === "animation" && c.aiStatus === "generating"))
      // Lo que está en proceso se guarda con su motivo ("se cortó al cerrar el
      // editor") en vez de pegado en "processing" o borrado en silencio; la
      // traducción con job queda en curso para retomarla
      // (ADR-2026-11-03-nada-queda-trabado-al-recargar).
      .map(({ src, srcPreview, srcPreviewDe, ...rest }) => clipParaGuardar(rest));
    const state = {
      clips: persistableClips,
      zoom,
      // Cómo dejó el usuario apiladas las capas: se guarda con el proyecto
      // porque es una decisión de armado, no una preferencia de la sesión (y
      // además decide el orden de dibujo en el preview).
      ordenDeFilas,
      // La biblioteca de animaciones gráficas del proyecto (Archivos →
      // Animaciones), miniaturas incluidas.
      animacionesGraficas,
      // "Editar clip" en curso o sin guardar, para retomarlo al volver a abrir
      // el proyecto. Fuera de `clips` a propósito: si viviera en el clip, cada
      // cambio de la edición sería un paso del Deshacer de la timeline.
      edicionesDeClips: Object.fromEntries(
        clips.flatMap((c) => {
          const g = edicionParaGuardar(sesionesDeEdicion.get(c.id));
          return g ? [[c.id, g] as const] : [];
        }),
      ),
      scenePrompt,
      sceneRefMode,
      sceneDuration,
      aspectRatio,
      imageGenResults: imageGenResults
        .filter((r) => r.fileName) // skip failed/still-pending-without-filename entries
        .map((r) => ({
          id: r.id,
          fileName: r.fileName,
          loading: false,
          createdAt: r.createdAt,
        })),
      imageGenHistorial,
      imageGenCompanyId,
      imageGenGalleryAssetIds,
      productSceneAssetMap,
      modelAssetMap,
    };
    // Duración REAL del proyecto = fin del último clip (no el placeholder 30
    // que se setea al crear). Sin esto, la galería de proyectos muestra 0:30
    // fijo para todos. Se computa inline y no con el memo `contentDuration`
    // para no sumarle una dependencia más a este callback de guardado.
    const contentDur = clips.reduce(
      (acc, c) => Math.max(acc, c.startTime + c.duration),
      0,
    );
    // supabase-js no tira: un guardado rechazado vuelve como `{ error }`. Antes no se
    // leía y el proyecto dejaba de guardarse en silencio; ahora el hook reintenta y
    // avisa (ADR-2026-11-02-el-estudio-guarda-solo-lo-ultimo-y-avisa-si-falla).
    const fila = {
      state,
      duration: contentDur > 0 ? contentDur : duration,
      name: projectName,
      updated_at: new Date().toISOString(),
    };
    // La página se está yendo (recargar / cerrar): un pedido común el navegador lo
    // cancela. Con `keepalive` sobrevive; si el proyecto no entra en su tope, queda
    // el camino de siempre.
    // Con otra pestaña adelante, esta no guarda hasta que la persona elija.
    if (hayConflictoRef.current && !forzarGuardadoRef.current) return true;
    const condicion = forzarGuardadoRef.current
      ? null
      : updatedAtConocidoRef.current;
    // Las versiones que escribió ESTA pestaña: con un guardado común en vuelo y uno de
    // salida a la vez (cambio de pestaña), el segundo en llegar no encuentra la fila
    // porque la movió el primero, que también es nuestro (review Greptile #242).
    recordarVersionPropia(fila.updated_at);
    const envio = opciones?.alCerrar
      ? guardarConKeepalive({
          supabaseUrl: SUPABASE_URL,
          anonKey: SUPABASE_ANON_KEY,
          token: tokenDeSesionRef.current,
          projectId,
          fila,
          updatedAtConocido: condicion,
        })
      : null;
    if (envio) {
      // Si la página se va, esto no sigue (y da igual). Si sólo se ocultó (otra
      // pestaña), se lee la respuesta: la versión conocida avanza recién con la base
      // confirmando. Si no se guardó (sin red, o un guardado común en vuelo ya movió la
      // versión) queda pendiente y lo reintenta el camino de siempre, que es el que
      // distingue un conflicto real (review Greptile #242).
      const r = await envio.catch(() => null);
      if (!r || !r.ok) return false;
      const filas = (await r.json().catch(() => null)) as Array<{ updated_at?: string }> | null;
      const guardada = filas?.[0]?.updated_at;
      if (!guardada) return false;
      updatedAtConocidoRef.current = guardada;
      if (forzarGuardadoRef.current) forzarGuardadoRef.current = false;
      return true;
    }
    try {
      let pedido = supabase
        .from("video_projects")
        .update(fila)
        .eq("id", projectId);
      if (condicion) pedido = pedido.eq("updated_at", condicion);
      const { data: guardadas, error } = await pedido.select("updated_at");
      if (error) {
        console.warn("[estudio] no se pudo guardar el proyecto", error);
        return false;
      }
      if (!guardadas || guardadas.length === 0) {
        // Ninguna fila con esa versión. Si la que hay la escribió esta misma pestaña
        // (un guardado de salida que llegó antes), no es un conflicto: se adopta y se
        // reintenta con ella.
        const { data: actual } = await supabase
          .from("video_projects")
          .select("updated_at")
          .eq("id", projectId)
          .maybeSingle();
        const enLaBase = (actual as { updated_at?: string | null } | null)?.updated_at;
        if (enLaBase && esVersionPropia(enLaBase)) {
          updatedAtConocidoRef.current = enLaBase;
          // La que está es nuestra y MÁS NUEVA que esta (el guardado de salida llevó
          // el estado más reciente): ya está guardado lo último; reescribir con esta
          // fila pisaría ediciones más nuevas (review Greptile #242, tercera vuelta).
          if (Date.parse(enLaBase) >= Date.parse(fila.updated_at)) return true;
          // La nuestra es la más nueva: se reintenta una vez con la versión adoptada.
          const otraVez = { ...fila, updated_at: new Date().toISOString() };
          recordarVersionPropia(otraVez.updated_at);
          const { data: deNuevo, error: errorDeNuevo } = await supabase
            .from("video_projects")
            .update(otraVez)
            .eq("id", projectId)
            .eq("updated_at", enLaBase)
            .select("updated_at");
          if (errorDeNuevo || !deNuevo || deNuevo.length === 0) return false;
          updatedAtConocidoRef.current =
            (deNuevo[0] as { updated_at?: string | null }).updated_at ?? otraVez.updated_at;
          forzarGuardadoRef.current = false;
          return true;
        }
        // Si no, otra pestaña (u otro dispositivo) guardó en el medio. No se pisa; se
        // avisa y se espera la decisión.
        hayConflictoRef.current = true;
        setHayConflicto(true);
        return true;
      }
      updatedAtConocidoRef.current =
        (guardadas[0] as { updated_at?: string | null }).updated_at ??
        fila.updated_at;
      forzarGuardadoRef.current = false;
    } catch (err) {
      console.warn("[estudio] no se pudo guardar el proyecto", err);
      return false;
    }

    // Fire-and-forget thumbnail regen if the cover clip at t=0 changed.
    if (companyId && !thumbnailRegenInFlightRef.current) {
      const cover = findCoverClipAtZero(clips);
      const nextHash = hashCoverClip(cover);
      if (nextHash !== lastThumbnailHashRef.current) {
        thumbnailRegenInFlightRef.current = true;
        regenerateProjectThumbnail({ projectId, companyId, clip: cover })
          .then(() => {
            lastThumbnailHashRef.current = nextHash;
          })
          .catch(() => {})
          .finally(() => {
            thumbnailRegenInFlightRef.current = false;
          });
      }
    }
    return true;
  }, [
    projectId,
    companyId,
    clips,
    zoom,
    scenePrompt,
    sceneRefMode,
    sceneDuration,
    aspectRatio,
    duration,
    projectName,
    imageGenResults,
    imageGenHistorial,
    imageGenCompanyId,
    imageGenGalleryAssetIds,
    productSceneAssetMap,
    modelAssetMap,
    ordenDeFilas,
    animacionesGraficas,
    sesionesDeEdicion,
  ]);

  // Guardado automático (3 s después del último cambio), al salir del editor y al
  // ocultarse la pestaña, con reintento y aviso si falla. Antes el "guardar al salir"
  // dependía de `saveProject` y corría con CADA cambio, con la closure del render
  // anterior —al abrir, la de `clips: []`—. Ver
  // ADR-2026-11-02-el-estudio-guarda-solo-lo-ultimo-y-avisa-si-falla.
  const { fallo: guardadoFallo, guardarAhora } = useGuardadoDelProyecto(
    saveProject,
    isLoadedRef,
  );

  // Si el editor sigue abierto. Los seguimientos de procesos (traducir, borrar,
  // corregir voz) son bucles `while (true)`: sin esto seguían consultando —y
  // escribiendo estado— después de salir del editor. La traducción se retoma al
  // volver a abrir el proyecto (ADR-2026-11-03-nada-queda-trabado-al-recargar).
  const estaMontadoRef = useRef(true);
  useEffect(() => {
    estaMontadoRef.current = true;
    return () => {
      estaMontadoRef.current = false;
    };
  }, []);
  // Las traducciones que estaban en curso cuando se cerró el editor: las anota
  // la carga y las retoma un efecto cuando el proyecto terminó de abrir.
  // Borrado de objeto, corregir voz, 1080p y mejorar calidad que quedaron en curso al
  // cerrar: se vuelven a seguir al abrir (ADR-2026-11-03-los-procesos-se-retoman-al-volver).
  const procesosARetomarRef = useRef<ProcesoARetomar[]>([]);
  const traduccionesARetomarRef = useRef<{ clipId: string; jobId: string }[]>(
    [],
  );

  // Refs
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineAreaRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  // ── La cinta corre sola cuando un arrastre llega al borde ───────────────
  // Cursor, clip o manija de recorte: con el puntero cerca del borde (o pasado),
  // la timeline se desplaza y el arrastre se recalcula con el scroll nuevo.
  // Antes el tiempo seguía al mouse fuera de la vista y "lo perdías".
  // Ver ADR-2026-11-02-la-timeline-sigue-al-cursor.
  const punteroDelArrastreRef = useRef<{ x: number; y: number } | null>(null);
  const inicioDelArrastreRef = useRef<{ x: number; y: number } | null>(null);
  // Recién cuando el puntero se movió unos píxeles cuenta como arrastre: un
  // click sobre un clip pegado al borde no tiene que correr la cinta (ni el clip).
  const arrastreMovidoRef = useRef(false);
  const detenerAutoscrollRef = useRef<(() => void) | null>(null);
  const detenerAutoscroll = useCallback(() => {
    detenerAutoscrollRef.current?.();
    detenerAutoscrollRef.current = null;
  }, []);
  const empezarAutoscroll = useCallback(
    (inicio: { x: number; y: number }, alDesplazar: () => void) => {
      detenerAutoscroll();
      inicioDelArrastreRef.current = inicio;
      punteroDelArrastreRef.current = inicio;
      arrastreMovidoRef.current = false;
      // En el teléfono la cinta ya es el tiempo (cursor fijo al centro).
      if (enElTelefonoRef.current) return;
      detenerAutoscrollRef.current = iniciarAutoscroll({
        contenedor: () => timelineAreaRef.current,
        punteroX: () =>
          arrastreMovidoRef.current
            ? (punteroDelArrastreRef.current?.x ?? null)
            : null,
        alDesplazar,
      });
    },
    [detenerAutoscroll],
  );
  const anotarPunteroDelArrastre = useCallback(
    (p: { x: number; y: number }) => {
      punteroDelArrastreRef.current = p;
      const inicio = inicioDelArrastreRef.current;
      if (!arrastreMovidoRef.current && inicio && esArrastre(inicio, p))
        arrastreMovidoRef.current = true;
    },
    [],
  );
  useEffect(() => () => detenerAutoscroll(), [detenerAutoscroll]);
  // Lo que hace el autoscroll cada vez que corre la cinta: volver a aplicar el
  // arrastre de la timeline (cursor o clip) con la última posición del puntero.
  const seguirElArrastre = useCallback(() => {
    const p = punteroDelArrastreRef.current;
    if (p) moverElArrastreRef.current({ clientX: p.x, clientY: p.y });
  }, []);
  const draggingClipRef = useRef<{
    id: string;
    offsetSec: number;
    // Dónde estaba el clip en el TIEMPO cuando arrancó el arrastre. Si el
    // gesto termina siendo un reorden de capas, el clip vuelve acá: cambiar
    // de capa no mueve nada en el tiempo.
    // Ver ADR-2026-09-15-reordenar-capas-no-mueve-el-clip-en-el-tiempo.
    startTimeInicial: number;
  } | null>(null);
  // Pista de origen "pinneada" durante un drag vertical. Cuando el usuario
  // arrastra un clip audio/video a otra pista, el clip cambia su `trackIndex`
  // en vivo — si la pista origen queda vacía, `displayRows` la destruiría
  // inmediatamente, haciendo saltar al resto de las pistas y rompiendo la
  // referencia visual del usuario. Mientras haya un pin activo, inyectamos
  // esa fila en `displayRows` aunque no tenga clips, y la limpiamos al
  // soltar el mouse (`handleTimelinePointerUp`).
  const [pinnedOriginRow, setPinnedOriginRow] = useState<{
    trackType: TipoDePistaReordenable;
    index: number;
  } | null>(null);

  // Hueco al que apunta un arrastre vertical en curso. Mientras existe se
  // dibuja la línea que marca dónde va a quedar la capa; el reorden se aplica
  // recién al soltar.
  const [insercionDePista, setInsercionDePista] = useState<
    | {
        clipId: string;
        tipo: TipoDePistaReordenable;
        /** `hueco` abre una capa nueva ahí; `dentro` mete el clip en esa fila. */
        clase: "hueco";
        posicion: number;
        yPx: number;
      }
    | {
        clipId: string;
        tipo: TipoDePistaReordenable;
        clase: "dentro";
        /** El `trackIndex` de la fila donde va a caer. */
        trackIndex: number;
        yPx: number;
      }
    | null
  >(null);
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  const mediaRefs = useRef<Map<string, HTMLVideoElement | HTMLAudioElement>>(
    new Map(),
  );
  // WebAudio graph for clip-level amplification > 100%
  const audioCtxRef = useRef<AudioContext | null>(null);
  const mediaAudioNodesRef = useRef<
    Map<string, { source: MediaElementAudioSourceNode; gain: GainNode }>
  >(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const agentUploadInputRef = useRef<HTMLInputElement>(null);

  const selectedClip = clips.find((c) => c.id === selectedClipId);
  // Qué le pasó al clip seleccionado, para el aviso de arriba
  // (ADR-2026-11-03-los-errores-del-estudio-se-ven). Cerrarlo vale para ESE motivo:
  // si el clip vuelve a fallar con otro, aparece de nuevo.
  const motivoDelSeleccionadoCrudo = selectedClip
    ? motivoDelClip(selectedClip)
    : null;
  const claveDelAvisoDelClip =
    selectedClip && motivoDelSeleccionadoCrudo
      ? `${selectedClip.id}:${motivoDelSeleccionadoCrudo.motivo}`
      : null;
  const [avisoDelClipCerrado, setAvisoDelClipCerrado] = useState<
    string | null
  >(null);
  const motivoDelSeleccionado =
    claveDelAvisoDelClip && avisoDelClipCerrado !== claveDelAvisoDelClip
      ? motivoDelSeleccionadoCrudo
      : null;

  // Las URLs firmadas del preview vencen a la hora y no se renovaban: en una sesión
  // larga, todo lo que se volvía a montar o a buscar quedaba en negro sin aviso. Si el
  // <video>/<audio> del preview da error y el clip tiene archivo, se vuelve a firmar
  // (como mucho una vez por minuto por clip, por si el error es otro). No entra al
  // Deshacer: no es un cambio de la persona. ADR-2026-11-03-los-errores-del-estudio-se-ven.
  const ultimoRefirmadoRef = useRef<Map<string, number>>(new Map());
  const refirmarSiVencio = useCallback(async (clipId: string) => {
    const clip = clipsRef.current.find((c) => c.id === clipId);
    if (!clip?.storagePath || !clip.src) return;
    // Un `blob:` que da error está muerto (se revocó o es de otra sesión): con
    // archivo en el servidor se firma de una, sin la espera de un minuto
    // (ADR-2026-11-03-deshacer-no-revive-procesos).
    const esBlob = clip.src.startsWith("blob:");
    const ahora = Date.now();
    if (!esBlob && ahora - (ultimoRefirmadoRef.current.get(clipId) ?? 0) < 60_000) return;
    ultimoRefirmadoRef.current.set(clipId, ahora);
    const { data } = await supabase.storage
      .from("video-project-assets")
      .createSignedUrl(clip.storagePath, 3600);
    const url = data?.signedUrl;
    if (!url) return;
    isApplyingHistoryRef.current = true;
    setClips((prev) =>
      prev.map((c) => (c.id === clipId ? { ...c, src: url } : c)),
    );
  }, []);

  // Cancelar una escena con IA que se está generando
  // (ADR-2026-11-03-cancelar-una-escena-del-estudio). Si el backend la canceló, el
  // clip queda en borrador sin su execution_id (el próximo "Generar" es un pedido
  // nuevo) y lo que llegue de ese pedido se ignora. Si el video ya se generó, no se
  // cancela: está por aparecer, y ya se cobró.
  const cancelarEscenaDelClip = useCallback(async (clipId: string) => {
    const clip = clipsRef.current.find((c) => c.id === clipId);
    const execId = clip?.aiExecutionId;
    if (!clip || clip.aiStatus !== "generating" || !execId) return;
    const r = await cancelarEscena(execId);
    if (r.cancelada) {
      escenasCanceladasRef.current.add(execId);
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId && c.aiExecutionId === execId
            ? {
                ...c,
                aiStatus: "draft" as AiClipStatus,
                aiExecutionId: undefined,
                aiExecutionParamsKey: undefined,
                aiGenerationStartedAt: undefined,
                aiError: undefined,
              }
            : c,
        ),
      );
      return;
    }
    if (r.estado === "terminando" || r.estado === "succeeded")
      mostrarAviso(
        "Ya casi está",
        "El video ya se generó: en unos segundos aparece en la timeline.",
      );
    else if (r.estado !== "failed")
      mostrarAviso(
        "No se pudo cancelar",
        "Probá de nuevo en unos segundos.",
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si la versión liviana no carga (venció la firma, se borró), el clip vuelve al
  // original y no se insiste (ADR-2026-11-03-el-preview-usa-la-version-liviana).
  // Fuera del Deshacer, como la refirma.
  const dejarLaVersionLiviana = useCallback((clipId: string) => {
    isApplyingHistoryRef.current = true;
    setClips((prev) =>
      prev.map((c) =>
        c.id === clipId && c.srcPreview
          ? { ...c, srcPreview: undefined, srcPreviewDe: undefined }
          : c,
      ),
    );
  }, []);

  // ── Panel del clip seleccionado ─────────────────────────────────────────
  // Tocar un clip en la timeline abre, a la IZQUIERDA, lo que se le puede
  // editar. Cada tipo abre el panel que ya sabe editarlo: los textos van al
  // panel "Texto" y los elementos animados al panel "Elementos" (los dos ya
  // editaban en vivo el clip seleccionado); el resto —video, audio, imagen—
  // al panel "clip". Ver ADR-2026-09-15-panel-del-clip-a-la-izquierda.
  const tituloDelPanelDeClip = !selectedClip
    ? "Editar clip"
    : selectedClip.type === "audio"
      ? "Editar audio"
      : selectedClip.type === "image"
        ? "Editar imagen"
        : "Editar video";
  // Guardamos el clip anterior para reaccionar SOLO cuando cambia la
  // selección. Si dependiéramos también de `activePanel`, el panel volvería
  // a "clip" cada vez que una acción lleva a otro panel (Editar clip,
  // Mejorar calidad, Elementos…) y el usuario no podría salir de acá.
  // El cuerpo del panel scrollea; al pasar de un clip a otro hay que volver
  // arriba. Sin esto el panel del clip nuevo aparece por donde había quedado
  // el anterior (ej: abajo de todo, en "Eliminar").
  const cuerpoDelPanelRef = useRef<HTMLDivElement>(null);
  // Ref viva del breakpoint: el efecto de abajo reacciona a que CAMBIÓ el clip
  // seleccionado, no a que se giró el teléfono. Leyendo el estado directo, girar
  // la pantalla con un clip tocado volvería a dispararlo y abriría el panel.
  // ── ¿La hoja del teléfono mide por su contenido? ─────────────────────────
  // Sí para casi todos los paneles: una hoja corta (Texto) no tiene por qué
  // dejar media pantalla en blanco, y una larga (Editar video, con sus seis
  // herramientas de IA) se muestra ENTERA si el teléfono da el alto, en vez de
  // cortarse a los 72vh de antes. El techo lo pone el `maxHeight`.
  //
  // La excepción son los dos formularios largos de IA (Video con IA e Imagen
  // con IA): se plantan en 92dvh a propósito, para no cambiar de alto mientras
  // el usuario navega entre sus caras.
  //
  // Esto lo leen DOS lugares —el `height` de la hoja y el `flex` de su cuerpo—
  // y tienen que decir lo mismo: con `height: auto`, un cuerpo con `flex-1`
  // (basis 0) y `overflow` aporta CERO y la hoja se colapsaría al alto de la
  // cabecera. Ver ADR-2026-09-17-la-hoja-mide-lo-que-mide-su-contenido.
  const hojaMidePorContenido =
    enElTelefono &&
    activePanel !== "ai-scene" &&
    activePanel !== "ai-image-gen";

  // El panel abierto, en un ref: lo leen callbacks que no pueden sumarlo a sus
  // deps sin recrearse en cada apertura de panel.
  const activePanelRef = useRef<ToolPanel>(activePanel);
  useEffect(() => {
    activePanelRef.current = activePanel;
  }, [activePanel]);

  const enElTelefonoRef = useRef(enElTelefono);
  useEffect(() => {
    enElTelefonoRef.current = enElTelefono;
  }, [enElTelefono]);

  // ── Teléfono: material nuevo cierra la hoja ──────────────────────────────
  // En el teléfono la hoja TAPA el preview. Traer un archivo desde Archivos
  // (o subirlo) dejaba la hoja abierta encima, así que el clip entraba a la
  // timeline y no se veía nada: había que cerrar a mano para descubrir que sí
  // había pasado algo. Al aparecer un clip nuevo, la hoja se va y queda el
  // video a la vista, que es lo que se fue a buscar.
  //
  // Sólo las hojas que traen material —Archivos, subir, los archivos que pide
  // el agente—. Las de edición (el clip, Texto, Elementos) no se cierran: ahí
  // el clip nuevo es el que se está editando y cerrar la hoja sería sacarle al
  // usuario los controles de la mano.
  // Ver ADR-2026-09-17-el-archivo-nuevo-cierra-la-hoja.
  const idsDeLosClipsRef = useRef<Set<string>>(
    new Set(clips.map((c) => c.id)),
  );
  useEffect(() => {
    const antes = idsDeLosClipsRef.current;
    const nuevos = clips.filter((c) => !antes.has(c.id));
    idsDeLosClipsRef.current = new Set(clips.map((c) => c.id));
    if (!enElTelefono || nuevos.length === 0) return;
    if (
      activePanel !== "files" &&
      activePanel !== "upload" &&
      activePanel !== "agent-upload"
    )
      return;
    setActivePanel("none");
    // Y el cursor va al arranque de lo que entró: cerrar la hoja con el cursor
    // parado en otro momento muestra el preview de ANTES, y el archivo que se
    // acaba de traer no aparece en ningún lado. Se mira dónde empieza el clip
    // nuevo (el más temprano, si entraron varios) en vez de asumir el 0: un
    // drop sobre la timeline lo deja donde lo soltaron.
    const inicio = Math.min(...nuevos.map((c) => c.startTime));
    if (Number.isFinite(inicio)) setCurrentTime(Math.max(0, inicio));
  }, [clips, enElTelefono, activePanel]);

  // ── Arrastrar la hoja de herramientas para cerrarla (sólo teléfono) ──────
  // El gesto que trae cualquier bottom sheet del sistema: se agarra de la
  // manija de arriba y se tira para abajo. Pasada la cuarta parte del alto
  // (con tope de 120px) se cierra; antes de eso vuelve sola a su lugar.
  //
  // Mientras dura, el transform se escribe DIRECTO sobre el nodo y no por
  // estado: un `setState` por cada `pointermove` haría re-renderizar un
  // componente de 30.000 líneas sesenta veces por segundo. Al soltar se
  // restituyen EXACTAMENTE los valores que pone el `style` del render (no se
  // limpian a `""`), así que React y el DOM quedan diciendo lo mismo.
  const hojaDeHerramientasRef = useRef<HTMLDivElement>(null);
  const TRANSICION_HOJA = "transform 280ms cubic-bezier(0.32,0.72,0,1)";
  const empezarAArrastrarLaHoja = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const hoja = hojaDeHerramientasRef.current;
      if (!hoja) return;
      const yInicial = e.clientY;
      const alto = hoja.offsetHeight;
      hoja.style.transition = "none";
      const mover = (ev: PointerEvent) => {
        if (!hojaDeHerramientasRef.current) return;
        const dy = Math.max(0, ev.clientY - yInicial);
        hojaDeHerramientasRef.current.style.transform = `translateY(${dy}px)`;
      };
      const soltar = (ev: PointerEvent) => {
        window.removeEventListener("pointermove", mover);
        window.removeEventListener("pointerup", soltar);
        window.removeEventListener("pointercancel", soltar);
        const cierra = ev.clientY - yInicial > Math.min(120, alto * 0.25);
        const nodo = hojaDeHerramientasRef.current;
        if (nodo) {
          nodo.style.transition = TRANSICION_HOJA;
          nodo.style.transform = cierra
            ? "translateY(101%)"
            : "translateY(0)";
        }
        if (cierra) setActivePanel("none");
      };
      window.addEventListener("pointermove", mover);
      window.addEventListener("pointerup", soltar);
      window.addEventListener("pointercancel", soltar);
    },
    [],
  );
  const clipSeleccionadoAnteriorRef = useRef<string | null>(null);
  useEffect(() => {
    const anterior = clipSeleccionadoAnteriorRef.current;
    clipSeleccionadoAnteriorRef.current = selectedClipId;
    if (selectedClipId === anterior) return;
    if (!selectedClipId) {
      // Al soltar la selección, el panel que estaba mostrando ESE clip se
      // cierra. Antes pasaba a un cartel vacío para que el layout no saltara
      // con cada click (porque tocar un clip lo abría); ahora el panel se abre
      // sólo cuando se pide, y quedaba ahí sin nada que mostrar
      // (ADR-2026-11-03-tocar-un-clip-solo-lo-selecciona). Si el usuario se
      // había ido a Archivos o a Marca, ese panel es suyo y no se lo tocamos.
      setActivePanel((prev) =>
        prev === "clip" ||
        prev === "text" ||
        prev === "animation" ||
        prev === "sin-seleccion"
          ? "none"
          : prev,
      );
      return;
    }
    const clip = clipsRef.current.find((c) => c.id === selectedClipId);
    if (!clip) return;
    if (cuerpoDelPanelRef.current) cuerpoDelPanelRef.current.scrollTop = 0;
    // En el TELÉFONO tocar un clip no abre ninguna hoja: lo que cambia es la
    // barra de abajo, que pasa a las acciones de ese clip (dividir, duplicar,
    // eliminar…). Abrir el panel taparía más de media pantalla —incluida la
    // timeline en la que el usuario acaba de tocar— para mostrar un formulario
    // que todavía no pidió. Los ajustes siguen a un toque: el botón "Ajustes"
    // de esa misma barra. Ver ADR-2026-09-16-el-estudio-de-video-en-el-telefono.
    if (enElTelefonoRef.current) return;
    // Un clip con una edición ESPERANDO al usuario (llegó con el panel cerrado,
    // o falló) abre "Editar clip" con el resultado, no las herramientas: hay
    // algo que mirar y decidir (Guardar o Deshacer), y la pastilla chica de la
    // timeline sola no alcanzaba —el usuario tocaba el clip y veía las
    // herramientas—. Mientras la edición corre, las herramientas de siempre:
    // el clip se puede seguir usando y la pastilla lleva al progreso
    // (ADR-2026-09-28-tocar-un-clip-con-edicion-lista-abre-la-edicion).
    const edicion = sesionDeEdicion(clip.id);
    if (edicion && !edicion.enCurso && (edicion.lista || edicion.error)) {
      setOmniEditClipId(clip.id);
      setActivePanel("edit-video-ia");
      return;
    }
    // Tocar un clip sólo lo selecciona: mover, recortar o dividir no necesitan
    // el panel, y abrirse en cada toque molestaba. El panel se abre con "Editar"
    // de la barra de acciones; si ya estaba abierto mostrando un clip, sigue al
    // nuevo (ADR-2026-11-03-tocar-un-clip-solo-lo-selecciona; sin doble clic:
    // ADR-2026-11-03-el-panel-se-abre-solo-con-editar).
    const abierto = activePanelRef.current;
    if (
      abierto === "clip" ||
      abierto === "text" ||
      abierto === "animation" ||
      abierto === "sin-seleccion"
    )
      setActivePanel(panelDelClip(clip));
  }, [selectedClipId]);

  // Abre el panel de edición del clip ("Editar").
  const abrirPanelDelClip = useCallback((clip: TimelineClip) => {
    setSelectedClipId(clip.id);
    const edicion = sesionDeEdicion(clip.id);
    if (edicion && !edicion.enCurso && (edicion.lista || edicion.error)) {
      setOmniEditClipId(clip.id);
      setActivePanel("edit-video-ia");
      return;
    }
    if (cuerpoDelPanelRef.current) cuerpoDelPanelRef.current.scrollTop = 0;
    setActivePanel(panelDelClip(clip));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timeline geometry (pixel-based) ──────────────────────────────────────
  // Base pixels-per-second at zoom=1. Zoom scales this linearly.
  const BASE_PPS = 40;
  const MIN_TIMELINE_SECONDS = DURACION_INICIAL_SEGUNDOS;
  // Espacio vacío después del último clip, para poder scrollear más allá del
  // contenido. Es un TOPE, no una medida fija: lo que hace falta es "una
  // pantalla de más", y eso en segundos depende del zoom. Con 120s fijos, a 800
  // px/s la regla medía 96.000px de nada y había que dibujarle miles de marcas.
  const TIMELINE_TAIL_MAX_SECONDS = 120;
  const pps = BASE_PPS * zoom;

  // ── Handler that fires the MP4 render. Lives here, lejos del estado de
  // ── export de más arriba, por historia: dependía de `contentDuration`, que
  // ── se declaraba más abajo y lo empujaba hasta acá. Hoy `contentDuration`
  // ── vive pegado a `clips`, así que esta posición ya es solo inercia.
  // Qué clips no entraron al MP4, dicho en el idioma del usuario. El
  // serializer ya los devuelve agrupados por motivo (`skipped`); lo que
  // faltaba era LEERLOS: se descartaban en la desestructuración y el usuario
  // se enteraba mirando el video.
  const avisosDeLoQueQuedoAfuera = (skipped: SkippedClip[]): string[] => {
    if (skipped.length === 0) return [];
    const porMotivo = new Map<SkippedClip["reason"], string[]>();
    for (const s of skipped) {
      const lista = porMotivo.get(s.reason) ?? [];
      lista.push(s.name || "sin nombre");
      porMotivo.set(s.reason, lista);
    }
    const comoSeDice: Record<SkippedClip["reason"], string> = {
      "text-empty": "textos vacíos",
      "ai-not-ready": "clips de IA que todavía no terminaron",
      "missing-source": "clips sin archivo disponible",
      "signed-url-failed": "clips cuyo archivo no se pudo abrir",
      "animation-sin-plantilla": "elementos animados sin plantilla",
    };
    return [...porMotivo.entries()].map(
      ([motivo, nombres]) =>
        `${nombres.length} ${comoSeDice[motivo]} no entraron al video (${nombres
          .slice(0, 3)
          .join(", ")}${nombres.length > 3 ? "…" : ""}).`,
    );
  };

  // Seguir un render (nuevo o retomado) hasta el final, en el modal de exportar.
  const seguirElExport = useCallback(
    async (handle: RenderHandle, avisos: string[], etiqueta = "Iniciando…") => {
      exportHandleRef.current = handle;
      setExportState({ phase: "rendering", progress: 0, label: etiqueta, handle });
      handle.onProgress((p) => {
        setExportState((prev) =>
          prev.phase === "rendering" && prev.handle === handle
            ? { ...prev, progress: p.progress, label: p.label }
            : prev,
        );
      });
      try {
        const result = await handle.result;
        if (exportHandleRef.current !== handle) return;
        exportHandleRef.current = null;
        setExportState({ phase: "done", outputUrl: result.outputUrl, avisos });
      } catch (err) {
        if (exportHandleRef.current !== handle) return;
        exportHandleRef.current = null;
        setExportState({ phase: "error", message: mensajeDeErrorDeExport(err) });
      }
    },
    [],
  );

  // Un export de este proyecto que ya está corriendo (se recargó la página, se cerró
  // el modal): se retoma su seguimiento en vez de perderlo, y Exportar se engancha a
  // ése en vez de lanzar otro encima — los "Rate Exceeded" de AWS venían de eso
  // (ADR-2026-11-03-el-export-se-retoma-y-no-se-pisa).
  const retomarExportEnCurso = useCallback(
    async (etiqueta: string): Promise<boolean> => {
      if (!projectId) return false;
      const enCurso = await buscarExportEnCurso(projectId);
      if (!enCurso || exportHandleRef.current) return false;
      setExportMenuOpen(true);
      void seguirElExport(seguirRender(enCurso.jobId, enCurso.arrancoEn), [], etiqueta);
      return true;
    },
    [projectId, seguirElExport],
  );
  useEffect(() => {
    if (projectLoading || !projectId) return;
    void retomarExportEnCurso("Seguimos con el export que estaba en curso…");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectLoading, projectId]);

  const handleExportMp4 = useCallback(async () => {
    if (await retomarExportEnCurso("Este proyecto ya se estaba exportando: seguimos con ese…"))
      return;
    // Cada clip viaja con su capa para que el MP4 apile igual que el preview.
    const timelineClips = clips.map((c) => ({
      ...c,
      z: zDelClipRef.current(c),
    }));
    const timelineDurationSecs =
      contentDuration > 0 ? contentDuration : duration;

    const pre = preflightExport({
      clips: timelineClips,
      durationInSeconds: timelineDurationSecs,
    });
    if (pre.blockers.length > 0) {
      setExportState({ phase: "error", message: pre.blockers.join(" ") });
      return;
    }

    // Hasta que arranca el render la fase sigue en "idle" (el selector de
    // formato abierto). El guard del `catch` ignora los errores en "idle" para
    // no pisar un export que el usuario cerró; un error de ANTES de arrancar
    // —p. ej. una tipografía de la marca que no se pudo preparar— sí tiene que
    // verse, o el botón parece no hacer nada.
    // Ver ADR-2026-10-30-revision-greptile-229-el-error-de-la-fuente-se-ve.
    let renderArranco = false;
    try {
      const { input, skipped } = await serializeTimeline({
        clips: timelineClips,
        aspectRatio,
        durationInSeconds: timelineDurationSecs,
        fps: 30,
        // Sin esto, un texto con una tipografía subida se ve bien en el preview
        // y sale con la de fallback en el MP4: el renderer sólo conoce el
        // catálogo de `remotion/src/fonts.ts`.
        // Ver ADR-2026-09-11-fuentes-que-el-render-no-tiene.
        fuentesPersonalizadas: customFontEntries.map((f) => ({
          family: f.family,
          storagePath: f.storagePath,
        })),
      });
      if (input.clips.length === 0) {
        setExportState({
          phase: "error",
          message:
            "Ningún clip pudo resolverse para el render. Revisá que los medios estén disponibles.",
        });
        return;
      }

      // `projectId` is only required for the real (non-mock) render path.
      const handle = startRender(input, { projectId: projectId || undefined });
      exportHandleRef.current = handle;
      renderArranco = true;
      setExportState({
        phase: "rendering",
        progress: 0,
        label: "Iniciando…",
        handle,
      });

      handle.onProgress((p) => {
        setExportState((prev) =>
          prev.phase === "rendering"
            ? { ...prev, progress: p.progress, label: p.label }
            : prev,
        );
      });

      const result = await handle.result;
      exportHandleRef.current = null;
      setExportState({
        phase: "done",
        outputUrl: result.outputUrl,
        avisos: avisosDeLoQueQuedoAfuera(skipped),
      });
    } catch (err: any) {
      exportHandleRef.current = null;
      // El error crudo (stacktrace de Remotion/AWS) NO se le muestra al
      // usuario: `mensajeDeErrorDeExport` lo traduce a algo accionable. El
      // detalle técnico no se pierde — queda en `render_jobs.error_message`,
      // que es donde el equipo lo mira. Ojo: esto es sólo para los errores
      // del RENDER; los avisos del preflight ya vienen escritos para el
      // usuario y se setean más arriba, sin pasar por acá.
      setExportState((prev) =>
        renderArranco && prev.phase === "idle"
          ? prev
          : {
              phase: "error",
              message: mensajeDeErrorDeExport(err),
            },
      );
    }
  // `customFontEntries` también: las fuentes subidas cargan después de los
  // clips, y sin él el export mandaba la lista vieja (vacía) y la tipografía
  // no llegaba al video.
  }, [clips, contentDuration, duration, aspectRatio, projectId, customFontEntries, retomarExportEnCurso]);

  // Ancho visible del carril: lo usa la regla para llenar siempre la pantalla
  // y el cálculo del zoom que hace entrar todo. El `scrollWidth` se medía
  // además para el thumb de una barra de scroll propia, que se sacó — ver
  // ADR-2026-09-17-la-timeline-no-lleva-barra-de-scroll-propia.
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(0);
  const [timelineScrollLeft, setTimelineScrollLeft] = useState(0);
  // ── El menú del "+" de las pistas ────────────────────────────────────────
  // El "+" no agrega directo: abre un menú flotante con las cuatro formas de
  // sumar material (Video con IA, Audio con IA, Texto, Archivo). Guarda las
  // coordenadas de PANTALLA del botón que lo abrió para pararse al lado: el
  // botón vive adentro de una pista con `overflow-hidden`, así que el menú se
  // dibuja aparte, al final del árbol.
  // Ver ADR-2026-09-18-el-mas-abre-un-menu-con-cuatro-formas-de-sumar.
  const [menuDelMas, setMenuDelMas] = useState<{
    /** Borde izquierdo del botón, para el fallback a la izquierda. */
    izq: number;
    /** Borde derecho: es donde se para el menú. */
    der: number;
    /** Borde de ABAJO del botón: el menú apoya ahí y crece hacia arriba. */
    abajo: number;
    /** De qué pista es el "+": lo que se sume desde su menú cae a
     *  continuación del último clip de ESA pista
     *  (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista). */
    pista: { familia: FamiliaDePista; trackIndex: number; fin: number };
  } | null>(null);
  // El "+" que abrió el panel actual: mientras ese panel siga abierto, el
  // primer clip que entre por él (`ubicarClipNuevo`) cae a continuación del
  // último clip de esa pista, en esa misma pista. Se consume con el primer
  // clip y se borra si el panel cambia. Es un ref porque lo leen callbacks que
  // no tienen por qué re-crearse (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista).
  const origenDelMasRef = useRef<{
    familia: FamiliaDePista;
    trackIndex: number;
    startTime: number;
    panel: ToolPanel;
  } | null>(null);
  // Cuánto scrolleó la timeline a lo ALTO. Lo lee la columna de "+" flotantes
  // del teléfono, que vive FUERA del carril que scrollea y tiene que seguir a
  // sus pistas. Ver ADR-2026-09-18-el-mas-del-telefono-flota-a-la-derecha.
  const [timelineScrollTop, setTimelineScrollTop] = useState(0);
  useEffect(() => {
    const el = timelineAreaRef.current;
    if (!el) return;
    const update = () => {
      setTimelineViewportWidth(el.clientWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also observe the inner content wrapper to catch width changes from zoom
    const inner = el.firstElementChild as HTMLElement | null;
    if (inner) ro.observe(inner);
    return () => ro.disconnect();
  }, []);
  /* ── Dónde cae el 0s dentro de la timeline ────────────────────────────────
     En escritorio, a 14px del borde izquierdo (`PAD_TIMELINE`): un respiro para
     que el primer clip no nazca pegado y le quede lugar a su anillo de
     selección.

     En el teléfono, a MEDIA PANTALLA. El cursor no se arrastra: está clavado en
     el centro y lo que se mueve es la cinta por debajo, que es como se busca un
     momento en CapCut, TikTok o Instagram Edits (y como se maneja un dial). Para
     que el segundo 0 pueda quedar bajo el cursor, la cinta tiene que poder
     scrollear media pantalla hacia la derecha; el mismo respiro va al final para
     llegar al último frame. `scrollLeft` pasa a ser, literalmente, el tiempo:
     `scrollLeft / pps`. Ver ADR-2026-09-16-el-cursor-fijo-al-centro-en-el-telefono.

     Todo lo que traduce píxeles a segundos —y al revés— lee esta variable y no
     la constante: `clientXToSeconds`, el ancho del lienzo, la línea del imán y
     el propio cursor. */
  const padIzqTimeline = useMemo(
    () =>
      enElTelefono && timelineViewportWidth > 0
        ? timelineViewportWidth / 2
        : PAD_TIMELINE,
    [enElTelefono, timelineViewportWidth],
  );

  // Keep live refs of zoom so the native wheel handler (registered once) can read the current value.
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  // Pending zoom pivot: after zoom state changes, useLayoutEffect applies the scrollLeft needed to keep
  // `timeUnderCursor` under `cursorX`, synchronously before paint so width + scroll update in one frame.
  const zoomPivotRef = useRef<{
    timeUnderCursor: number;
    cursorX: number;
  } | null>(null);
  useLayoutEffect(() => {
    const pivot = zoomPivotRef.current;
    if (!pivot) return;
    const el = timelineAreaRef.current;
    if (!el) return;
    const newPps = BASE_PPS * zoom;
    const target = Math.max(0, pivot.timeUnderCursor * newPps - pivot.cursorX);
    el.scrollLeft = target;
    zoomPivotRef.current = null;
  }, [zoom]);
  // Native wheel listener (non-passive):
  //  - ctrlKey (trackpad pinch on macOS) or metaKey/ctrlKey + wheel → zoom centered on cursor
  //  - otherwise → horizontal scroll (vertical wheel delta translated to horizontal)
  useEffect(() => {
    const el = timelineAreaRef.current;
    if (!el) return;
    // Batch rapid pinch events into a single update per animation frame to avoid jitter.
    let accumulatedDeltaY = 0;
    let pendingCursorX = 0;
    let rafId: number | null = null;
    const flushZoom = () => {
      rafId = null;
      const el2 = timelineAreaRef.current;
      if (!el2 || accumulatedDeltaY === 0) {
        accumulatedDeltaY = 0;
        return;
      }
      const currentZoom = zoomRef.current;
      const currentPps = BASE_PPS * currentZoom;
      const timeUnderCursor = (el2.scrollLeft + pendingCursorX) / currentPps;
      const factor = Math.exp(-accumulatedDeltaY * 0.01);
      const newZoom = Math.max(
        ZOOM_MIN,
        Math.min(ZOOM_MAX, currentZoom * factor),
      );
      accumulatedDeltaY = 0;
      if (newZoom === currentZoom) return;
      zoomPivotRef.current = { timeUnderCursor, cursorX: pendingCursorX };
      setZoom(newZoom);
    };
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        pendingCursorX = e.clientX - rect.left;
        accumulatedDeltaY += e.deltaY;
        if (rafId === null) rafId = requestAnimationFrame(flushZoom);
        return;
      }
      if (el.scrollWidth <= el.clientWidth) return;
      const primary =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (primary === 0) return;
      e.preventDefault();
      el.scrollLeft += primary;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);
  // Visible timeline span — ruler always extends to cover at least the visible viewport, with tail beyond the last clip
  const minSecondsForViewport =
    timelineViewportWidth > 0 ? timelineViewportWidth / pps : 0;
  // La cola de 120s existe para poder scrollear más allá del último clip. Con
  // la timeline VACÍA no hay último clip, así que sumarla sólo dibujaba 120s de
  // regla para un proyecto que arranca en 15: la regla nacía con scroll y los
  // 15 segundos del proyecto quedaban apretados en la punta izquierda.
  const tailSeconds = Math.max(
    5,
    Math.min(
      TIMELINE_TAIL_MAX_SECONDS,
      minSecondsForViewport > 0
        ? minSecondsForViewport
        : TIMELINE_TAIL_MAX_SECONDS,
    ),
  );
  const timelineDuration = Math.max(
    contentDuration > 0 ? contentDuration + tailSeconds : 0,
    minSecondsForViewport,
    MIN_TIMELINE_SECONDS,
  );
  const timelineWidth = timelineDuration * pps;

  // Zoom inicial "entra todo": UNA sola vez, cuando ya se midió el viewport y
  // el proyecto terminó de cargar, ajustamos el zoom para que la duración del
  // proyecto ocupe exactamente el ancho visible. Sin esto, 15 segundos a 40
  // px/s son 600px y la regla cortaba en el medio de la pantalla.
  // Es sólo el punto de partida: el ref hace que no vuelva a correr, así que el
  // slider y el ctrl+rueda del usuario nunca se pisan.
  const zoomInicialAplicadoRef = useRef(false);
  useEffect(() => {
    if (zoomInicialAplicadoRef.current) return;
    if (projectLoading || timelineViewportWidth <= 0) return;
    const segundos = contentDuration > 0 ? contentDuration : duration;
    if (segundos <= 0) return;
    zoomInicialAplicadoRef.current = true;
    const ideal = timelineViewportWidth / (BASE_PPS * segundos);
    setZoom(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, ideal)));
  }, [projectLoading, timelineViewportWidth, contentDuration, duration]);

  // "Ajustar": el mismo cálculo del zoom inicial, pero a pedido. Después de
  // acercar/alejar a mano no hay forma de volver a ver el proyecto entero sin
  // pelear con el slider, así que el botón al lado del zoom recalcula el zoom
  // que hace entrar la duración del contenido en el ancho visible y vuelve el
  // scroll al principio. El margen de 4px es para que el final del último clip
  // no quede pegado al borde (y para que el redondeo no dispare un scroll de
  // un pixel).
  const ajustarTimelineAPantalla = useCallback(() => {
    if (timelineViewportWidth <= 0) return;
    const segundos = contentDuration > 0 ? contentDuration : duration;
    if (segundos <= 0) return;
    const ideal = (timelineViewportWidth - 4) / (BASE_PPS * segundos);
    const nuevoZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, ideal));
    if (nuevoZoom === zoom) {
      // Ya estaba ajustada: `setZoom` no dispara el layout effect del pivot,
      // así que el scroll lo llevamos nosotros (si no, el pivot quedaría
      // pendiente y se aplicaría en el próximo zoom del usuario).
      if (timelineAreaRef.current) timelineAreaRef.current.scrollLeft = 0;
      return;
    }
    // El pivot deja el segundo 0 en el borde izquierdo: el layout effect pone
    // el scroll en 0 con el ancho nuevo, en el mismo frame.
    zoomPivotRef.current = { timeUnderCursor: 0, cursorX: 0 };
    setZoom(nuevoZoom);
  }, [timelineViewportWidth, contentDuration, duration, zoom]);

  // Zoom desde los botones y el slider: se acerca sobre el CURSOR si está a la
  // vista (y si no, sobre el centro). Antes cambiaba el zoom sin pivote y el
  // contenido "saltaba": el cursor podía quedar fuera de la vista.
  // ADR-2026-11-02-la-timeline-sigue-al-cursor.
  const zoomDesdeLosControles = useCallback((nuevo: number) => {
    const actual = zoomRef.current;
    const destino = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, nuevo));
    if (destino === actual) return;
    const el = timelineAreaRef.current;
    if (el) {
      const ppsActual = BASE_PPS * actual;
      const xDelCursor = currentTimeRef.current * ppsActual - el.scrollLeft;
      const cursorX =
        xDelCursor >= 0 && xDelCursor <= el.clientWidth
          ? xDelCursor
          : el.clientWidth / 2;
      zoomPivotRef.current = {
        timeUnderCursor: (el.scrollLeft + cursorX) / ppsActual,
        cursorX,
      };
    }
    setZoom(destino);
  }, []);

  // ── Deselect clip when clicking anywhere outside a clip ───────────────────
  useEffect(() => {
    if (!selectedClipId) return;
    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.closest("[data-clip-id]") ||
          target.closest("[data-clip-ui]") ||
          // La regla y el cursor mueven el tiempo, no cambian la selección.
          // Sin esta excepción este listener deshace lo que decide
          // `handleTimelinePointerDown` y el clip se pierde igual.
          target.closest("[data-timeline-scrub]") ||
          // En el teléfono, TODA la timeline queda exenta: apoyar el dedo ahí
          // es, casi siempre, el comienzo de un scroll, y soltar la selección
          // al apoyarlo dejaba sin clip a cualquiera que quisiera buscar otro
          // momento. Quién suelta la selección es el `onPointerUp` de la
          // timeline, y sólo si el dedo se levantó sin haberse movido.
          (enElTelefonoRef.current &&
            target.closest("[data-timeline-area]")))
      )
        return;
      setSelectedClipId(null);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [selectedClipId]);

  // ── Generate thumbnails for video/ready AI clips ──────────────────────────
  useEffect(() => {
    // Primero los clips cerca del cursor: son los que se están mirando
    // (ADR-2026-11-03-bajar-solo-lo-que-se-ve).
    const ahora = currentTimeRef.current;
    const distancia = (c: TimelineClip) =>
      ahora < c.startTime
        ? c.startTime - ahora
        : Math.max(0, ahora - (c.startTime + c.duration));
    [...clips].sort((a, b) => distancia(a) - distancia(b)).forEach((clip) => {
      const isMedia =
        clip.type === "video" ||
        (clip.type === "ai-generated" && clip.aiStatus === "ready");
      if (!isMedia || !clip.src) return;
      const key = `${clip.id}::${clip.src}`;
      if (thumbGenRef.current.has(key)) return;
      if (clipThumbnails[clip.id]) return;
      thumbGenRef.current.add(key);
      (async () => {
        try {
          // Cuántas miniaturas sacarle al video. Eran 12 fijas, y con el zoom
          // llegando a 2400 px/s eso significaba la MISMA imagen repetida
          // durante media pantalla: el clip parecía congelado justo cuando se
          // lo mira de cerca. Ahora la densidad sigue a la duración —doce por
          // segundo, ~un cuadro cada dos a 24fps— con dos topes: nunca menos
          // de 12 (un clip de medio segundo igual necesita una tira que se
          // lea) y nunca más de 96,
          // porque cada miniatura es un `seek` + un `drawImage` y son
          // secuenciales: pasado ese punto el clip tarda demasiado en
          // aparecer. Ver ADR-2026-09-15-zoom-hasta-el-cuadro.
          const segundosDelClip = clip.sourceDuration ?? clip.duration ?? 0;
          const cuantasMiniaturas = Math.max(
            TIRA_RAPIDA,
            Math.min(96, Math.ceil(segundosDelClip * 12)),
          );
          // La tira depende sólo del ARCHIVO: guardada por `storagePath`, al volver
          // a abrir el proyecto (o con las dos mitades de un clip dividido) no se
          // rehace (ADR-2026-11-03-el-estudio-abre-rapido).
          const tira = async (cuantas: number): Promise<string[] | null> => {
            const clave = clip.storagePath
              ? claveDeMiniaturas(clip.storagePath, cuantas)
              : null;
            const guardada = clave ? await leerDeLaCache<string[]>(clave) : null;
            if (guardada) return guardada;
            const generar = () =>
              limitarMiniaturas(() =>
                generateVideoThumbnails(srcDelPreview(clip)!, cuantas, 96),
              );
            const hecha = await (clave ? unaVezPorClave(clave, generar) : generar());
            // Una tira cortada (un seek que no llegó) pondría cuadros del principio a
            // lo largo de todo el clip: no se muestra ni se guarda.
            if (hecha.length < cuantas) return null;
            if (clave) guardarEnLaCache(clave, hecha);
            return hecha;
          };
          const mostrar = (thumbs: string[]) =>
            setClipThumbnails((prev) => ({ ...prev, [clip.id]: thumbs }));
          // Una tira rápida de 12 primero, así el clip deja de ser una barra vacía
          // enseguida; la completa la reemplaza cuando está.
          if (cuantasMiniaturas > TIRA_RAPIDA) {
            const completaGuardada = clip.storagePath
              ? await leerDeLaCache<string[]>(
                  claveDeMiniaturas(clip.storagePath, cuantasMiniaturas),
                )
              : null;
            if (completaGuardada) {
              mostrar(completaGuardada);
              return;
            }
            const rapida = await tira(TIRA_RAPIDA);
            if (rapida) mostrar(rapida);
          }
          const completa = await tira(cuantasMiniaturas);
          if (completa) mostrar(completa);
        } catch (err) {
          console.warn("[VideoEditor] Thumbnail generation failed:", err);
        }
      })();
    });
  }, [clips, clipThumbnails]);

  // ── Generate waveform peaks for audio clips ──────────────────────────────
  useEffect(() => {
    clips.forEach((clip) => {
      if (clip.type !== "audio" || !clip.src) return;
      const key = `${clip.id}::${clip.src}`;
      if (peaksGenRef.current.has(key)) return;
      if (audioPeaks[clip.id]) return;
      peaksGenRef.current.add(key);
      (async () => {
        // La onda depende sólo del ARCHIVO (se calcula sobre el audio entero): se
        // guarda por `storagePath` y dos clips del mismo archivo la calculan una vez
        // (ADR-2026-11-03-el-estudio-abre-rapido).
        const clave = clip.storagePath ? claveDeOnda(clip.storagePath) : null;
        try {
          const guardada = clave ? await leerDeLaCache<number[]>(clave) : null;
          if (guardada) {
            setAudioPeaks((prev) => ({ ...prev, [clip.id]: guardada }));
            return;
          }
          const calcular = () =>
            limitarOndas(() => calcularOnda(srcDelPreview(clip)!));
          const peaks = clave
            ? await unaVezPorClave(clave, calcular)
            : await calcular();
          if (clave) guardarEnLaCache(clave, peaks);
          setAudioPeaks((prev) => ({ ...prev, [clip.id]: peaks }));
        } catch (err) {
          console.warn("[VideoEditor] Waveform generation failed:", err);
        }
      })();
    });
  }, [clips, audioPeaks]);

  // La onda de un audio: bajarlo entero, decodificarlo y resumirlo en 200 barras.
  // El AudioContext se cierra también si falla (antes quedaba abierto en el catch).
  const calcularOnda = useCallback(async (src: string): Promise<number[]> => {
    const res = await fetch(src);
    const buf = await res.arrayBuffer();
    const Ctx = (window.AudioContext ||
      (window as any).webkitAudioContext) as typeof AudioContext;
    const ctx = new Ctx();
    try {
      const decoded = await ctx.decodeAudioData(buf);
      const channel = decoded.getChannelData(0);
      const BUCKETS = 200;
      const samplesPerBucket = Math.max(
        1,
        Math.floor(channel.length / BUCKETS),
      );
      const peaks: number[] = new Array(BUCKETS);
      let globalMax = 0;
      for (let i = 0; i < BUCKETS; i++) {
        let max = 0;
        const start = i * samplesPerBucket;
        const end = Math.min(start + samplesPerBucket, channel.length);
        for (let j = start; j < end; j++) {
          const v = Math.abs(channel[j]);
          if (v > max) max = v;
        }
        peaks[i] = max;
        if (max > globalMax) globalMax = max;
      }
      // Normalize to 0..1 so quiet audio still renders visibly
      const normFactor = globalMax > 0 ? 1 / globalMax : 1;
      for (let i = 0; i < peaks.length; i++)
        peaks[i] = peaks[i] * normFactor;
      return peaks;
    } finally {
      void ctx.close().catch(() => {});
    }
  }, []);

  // ── Generate per-ASSET waveform peaks for the Archivos→Audio tab ─────────
  // Mirrors the per-clip peak generation above but keyed by asset id so the
  // tile waveforms persist across the panel lifecycle (independent of any
  // timeline clip referencing the asset).
  useEffect(() => {
    if (filesTab !== "audio") return;
    libraryAssets.forEach((asset) => {
      if (asset.type !== "audio") return;
      const url = assetPreviewUrls[asset.id];
      if (!url) return;
      if (audioAssetPeaks[asset.id]) return;
      if (audioAssetPeaksGenRef.current.has(asset.id)) return;
      audioAssetPeaksGenRef.current.add(asset.id);
      (async () => {
        try {
          const res = await fetch(url);
          const buf = await res.arrayBuffer();
          const Ctx = (window.AudioContext ||
            (window as any).webkitAudioContext) as typeof AudioContext;
          const ctx = new Ctx();
          const decoded = await ctx.decodeAudioData(buf);
          const channel = decoded.getChannelData(0);
          const BUCKETS = 56;
          const samplesPerBucket = Math.max(
            1,
            Math.floor(channel.length / BUCKETS),
          );
          const peaks: number[] = new Array(BUCKETS);
          let globalMax = 0;
          for (let i = 0; i < BUCKETS; i++) {
            let max = 0;
            const start = i * samplesPerBucket;
            const end = Math.min(start + samplesPerBucket, channel.length);
            for (let j = start; j < end; j++) {
              const v = Math.abs(channel[j]);
              if (v > max) max = v;
            }
            peaks[i] = max;
            if (max > globalMax) globalMax = max;
          }
          const norm = globalMax > 0 ? 1 / globalMax : 1;
          for (let i = 0; i < peaks.length; i++) peaks[i] = peaks[i] * norm;
          ctx.close();
          setAudioAssetPeaks((prev) => ({ ...prev, [asset.id]: peaks }));
        } catch (err) {
          console.warn("[VideoEditor] Asset waveform generation failed:", err);
        }
      })();
    });
  }, [filesTab, libraryAssets, assetPreviewUrls, audioAssetPeaks]);

  // ── Single-track audio preview controls (Archivos→Audio tab) ─────────────
  const stopAudioPreview = useCallback(() => {
    if (audioPreviewRafRef.current)
      cancelAnimationFrame(audioPreviewRafRef.current);
    audioPreviewRafRef.current = null;
    const el = audioPreviewElRef.current;
    if (el) {
      try {
        el.pause();
      } catch {}
      el.src = "";
      audioPreviewElRef.current = null;
    }
    setPlayingAudioAssetId(null);
    setAudioPlaybackProgress(0);
  }, []);
  const playAudioPreview = useCallback(
    (assetId: string, url: string) => {
      if (playingAudioAssetId === assetId) {
        stopAudioPreview();
        return;
      }
      // Stop any previous track first.
      if (audioPreviewRafRef.current)
        cancelAnimationFrame(audioPreviewRafRef.current);
      audioPreviewRafRef.current = null;
      const prev = audioPreviewElRef.current;
      if (prev) {
        try {
          prev.pause();
        } catch {}
        prev.src = "";
      }
      const audio = new Audio(url);
      audioPreviewElRef.current = audio;
      setPlayingAudioAssetId(assetId);
      setAudioPlaybackProgress(0);
      const onEnd = () => stopAudioPreview();
      audio.addEventListener("ended", onEnd);
      audio.play().catch((err) => {
        console.warn("[VideoEditor] audio preview play failed:", err);
        stopAudioPreview();
      });
      const tick = () => {
        const el = audioPreviewElRef.current;
        if (!el) return;
        const d = el.duration || 0;
        setAudioPlaybackProgress(d > 0 ? Math.min(1, el.currentTime / d) : 0);
        audioPreviewRafRef.current = requestAnimationFrame(tick);
      };
      audioPreviewRafRef.current = requestAnimationFrame(tick);
    },
    [playingAudioAssetId, stopAudioPreview],
  );
  // Stop preview if user leaves the audio tab / files panel.
  useEffect(() => {
    if (activePanel !== "files" || filesTab !== "audio") stopAudioPreview();
  }, [activePanel, filesTab, stopAudioPreview]);
  // Cleanup on unmount.
  useEffect(() => () => stopAudioPreview(), [stopAudioPreview]);

  // ── Single-track video preview controls (Archivos→Videos tab) ────────────
  const stopVideoPreview = useCallback(() => {
    const id = playingVideoAssetId;
    if (id) {
      const el = videoPreviewElsRef.current.get(id);
      if (el) {
        try {
          el.pause();
        } catch {}
        try {
          el.currentTime = 0;
        } catch {}
      }
    }
    setPlayingVideoAssetId(null);
  }, [playingVideoAssetId]);
  const toggleVideoPreview = useCallback(
    (assetId: string) => {
      if (playingVideoAssetId === assetId) {
        const el = videoPreviewElsRef.current.get(assetId);
        if (el) {
          try {
            el.pause();
          } catch {}
          try {
            el.currentTime = 0;
          } catch {}
        }
        setPlayingVideoAssetId(null);
        return;
      }
      // Pause previously playing one (if any).
      if (playingVideoAssetId) {
        const prev = videoPreviewElsRef.current.get(playingVideoAssetId);
        if (prev) {
          try {
            prev.pause();
          } catch {}
          try {
            prev.currentTime = 0;
          } catch {}
        }
      }
      const el = videoPreviewElsRef.current.get(assetId);
      if (!el) return;
      el.muted = false;
      el.currentTime = 0;
      el.play()
        .then(() => setPlayingVideoAssetId(assetId))
        .catch((err) => {
          console.warn("[VideoEditor] video preview play failed:", err);
          setPlayingVideoAssetId(null);
        });
    },
    [playingVideoAssetId],
  );
  // Stop preview if user leaves the videos tab / files panel.
  useEffect(() => {
    if (activePanel !== "files" || filesTab !== "media") stopVideoPreview();
  }, [activePanel, filesTab, stopVideoPreview]);

  // ── Detect AI generation finished → play sound + bounce ──────────────────
  useEffect(() => {
    const prev = prevAiStatusRef.current;
    const nextMap: Record<string, AiClipStatus | undefined> = {};
    const justFinished: string[] = [];
    clips.forEach((c) => {
      nextMap[c.id] = c.aiStatus;
      if (prev[c.id] === "generating" && c.aiStatus === "ready") {
        justFinished.push(c.id);
      }
    });
    prevAiStatusRef.current = nextMap;
    if (justFinished.length > 0) {
      try {
        const el = finishSoundRef.current;
        if (el) {
          el.currentTime = 0;
          el.play().catch(() => {});
        }
      } catch {}
      setBounceClipIds((curr) => {
        const next = new Set(curr);
        justFinished.forEach((id) => next.add(id));
        return next;
      });
      // Clear bounce class after the animation completes (matches index.css duration)
      window.setTimeout(() => {
        setBounceClipIds((curr) => {
          const next = new Set(curr);
          justFinished.forEach((id) => next.delete(id));
          return next;
        });
      }, 700);
    }
  }, [clips]);

  // Active motion-blur state — derives the current blur magnitude and
  // direction from whichever clip is mid-slide-transition. All clips in
  // a slide transition share the same `blurPeakAt(t, SLIDE_BLUR_PEAK_PX)` value so a single
  // SVG <feGaussianBlur> instance can drive both A and B simultaneously.
  // Returns null when no slide is in progress so the SVG element drops
  // out of the DOM entirely (no GPU compositing cost on idle frames).
  // Position of the white reveal-wipe line, in % of preview width. null
  // when no `reveal` transition is active. The line is rendered as an
  // overlay div on top of the videos so it stays sharp and white
  // regardless of what's behind it.
  const revealLineState = useMemo<{ leftPct: number } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!c.transitionIn || c.transitionIn.type !== "reveal") continue;
      const win = getTransitionWindow(c);
      if (!win) continue;
      if (currentTime < win.start || currentTime > win.end) continue;
      const t = (currentTime - win.start) / win.duration;
      return { leftPct: revealLineEase(t) * 100 };
    }
    return null;
  }, [clips, currentTime]);

  const motionBlurState = useMemo<{
    px: number;
    direction: "x" | "y";
  } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!isClipActiveAtTime(c, currentTime, clips)) continue;
      const state = computeClipRenderStateAtTime(c, currentTime, clips);
      if (
        state.blurPx &&
        state.blurPx > 0.1 &&
        (state.blurDirection === "x" || state.blurDirection === "y")
      ) {
        return { px: state.blurPx, direction: state.blurDirection };
      }
    }
    return null;
  }, [clips, currentTime]);

  // When an overlay-style transition is in progress, returns the URL of
  // the overlay video to composite on top + the normalised progress
  // through the transition window (0..1). The progress is what the
  // overlay's currentTime gets synced to, so the overlay's own
  // animation lines up with the transition timing regardless of the
  // user's chosen duration.
  const overlayState = useMemo<{ url: string; progress: number } | null>(() => {
    for (const c of clips) {
      if (c.type !== "video" && c.type !== "ai-generated") continue;
      if (!c.transitionIn) continue;
      const url = OVERLAY_TRANSITIONS[c.transitionIn.type];
      if (!url) continue;
      const win = getTransitionWindow(c);
      if (!win) continue;
      if (currentTime < win.start || currentTime > win.end) continue;
      const progress = (currentTime - win.start) / win.duration;
      return { url, progress };
    }
    return null;
  }, [clips, currentTime]);

  const mediaClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.src &&
          (c.type === "video" ||
            c.type === "audio" ||
            c.type === "ai-generated"),
      ),
    [clips],
  );

  // Clips de video/IA del preview, EN ORDEN DE APILADO. El orden importa:
  // el que se dibuja último tapa a los de atrás. Se ordena por `trackIndex`
  // ascendente —pista más alta en la timeline, más adelante en el cuadro—
  // con el orden del array como desempate dentro de una misma pista.
  //
  // Antes se renderizaban en el orden del array, o sea por orden de creación:
  // un clip chico (encuadrado como PiP) agregado después tapaba al de pantalla
  // completa aunque estuviera en la pista de ABAJO, y no había forma de
  // arreglarlo desde la timeline. El export (Remotion, `TimelineComposition`)
  // ya ordenaba así, con lo cual el preview y el archivo exportado podían
  // mostrar cosas distintas.
  // Ver ADR-2026-09-15-el-preview-apila-los-videos-por-pista.
  // Las capas visuales del preview, apiladas por pista. Incluye IMÁGENES: son
  // una capa más del cuadro, con el mismo encuadre (escalar, recortar, girar,
  // mover) y la misma opacidad que un video. Antes quedaban fuera y un clip de
  // imagen en la timeline simplemente no se veía.
  const videoClipsApilados = useMemo(
    () =>
      clips
        .filter(
          (c) =>
            (c.type === "video" ||
              c.type === "ai-generated" ||
              c.type === "image") &&
            (c.src || c.imageUrl),
        )
        .map((c, i) => ({ c, i }))
        .sort((a, b) => {
          // Las imágenes van SIEMPRE sobre los videos: son una capa de encima
          // (un logo, un sello), igual que el texto. Sus `trackIndex` además
          // pertenecen a otra familia —la de elementos— así que compararlos
          // contra los de video mezclaría dos numeraciones distintas.
          const fa = a.c.type === "image" ? 1 : 0;
          const fb = b.c.type === "image" ? 1 : 0;
          if (fa !== fb) return fa - fb;
          const ta = getClipTrackIndex(a.c);
          const tb = getClipTrackIndex(b.c);
          if (ta !== tb) return ta - tb;
          return a.i - b.i;
        })
        .map((x) => x.c),
    [clips],
  );

  // Text clips that are visible at the current playhead. Render order = clip
  // array order, so newly-added clips draw on top. The renderer uses these
  // to overlay <div>s on the preview canvas.
  const activeTextClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.type === "text" &&
          currentTime >= c.startTime &&
          currentTime < c.startTime + c.duration,
      ),
    [clips, currentTime],
  );

  // Animation clips visible at the playhead. Rendered in array order so the
  // newest clip ends up on top, matching the text clip behaviour.
  const activeAnimationClips = useMemo(
    () =>
      clips.filter(
        (c) =>
          c.type === "animation" &&
          (!!c.animationTemplate || !!c.animationCodigo) &&
          currentTime >= c.startTime &&
          currentTime < c.startTime + c.duration,
      ),
    [clips, currentTime],
  );

  // ── Preview canvas size tracking ─────────────────────────────────────────
  // The preview container is responsive (its height depends on viewport +
  // aspect ratio). We need the live pixel height to compute text font sizes
  // from the resolution-independent `fontSizePct` we store per clip, and the
  // pixel rect to translate pointer events into 0..100 % coordinates while
  // dragging text.
  const previewRef = useRef<HTMLDivElement | null>(null);
  // Ref to the overlay-transition <video> element (only mounted while
  // an overlay-style transition is in progress). The effect below
  // seeks/plays it in lockstep with the timeline playhead so the
  // overlay's animation always matches the transition's progress
  // regardless of the user's chosen duration.
  const overlayVideoRef = useRef<HTMLVideoElement | null>(null);
  // Cache of per-overlay-URL durations so we can normalise the seek
  // (overlay video's currentTime = progress × duration). Loaded lazily
  // via a tiny probe video on first sight of each URL.
  const [overlayDurations, setOverlayDurations] = useState<
    Record<string, number>
  >({});
  useEffect(() => {
    const el = overlayVideoRef.current;
    if (!el || !overlayState) return;
    const dur = overlayDurations[overlayState.url];
    if (!dur) {
      const handler = () => {
        const d = el.duration;
        if (isFinite(d) && d > 0) {
          setOverlayDurations((prev) =>
            prev[overlayState.url] === d
              ? prev
              : { ...prev, [overlayState.url]: d },
          );
        }
      };
      el.addEventListener("loadedmetadata", handler);
      return () => el.removeEventListener("loadedmetadata", handler);
    }
    // We need the overlay to PLAY (not just seek) so the browser decodes
    // every frame — pure currentTime assignments only land on keyframes
    // for typical MP4s, which is why the user saw "only first/last frame"
    // before. The clip's transitionIn.durationSec is what we scale to.
    const targetClip = clips.find(
      (c) =>
        c.transitionIn &&
        OVERLAY_TRANSITIONS[c.transitionIn.type] === overlayState.url,
    );
    const transitionDur = targetClip?.transitionIn?.durationSec ?? dur;
    const desiredRate = dur / transitionDur;
    if (Math.abs(el.playbackRate - desiredRate) > 0.01)
      el.playbackRate = desiredRate;
    const expected = overlayState.progress * dur;
    if (Math.abs(el.currentTime - expected) > 0.3) {
      try {
        el.currentTime = expected;
      } catch {}
    }
    if (isPlaying) {
      if (el.paused) el.play().catch(() => {});
    } else if (!el.paused) {
      el.pause();
    }
  }, [overlayState, overlayDurations, isPlaying, clips]);
  const [previewSize, setPreviewSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPreviewSize((prev) =>
        prev.w === r.width && prev.h === r.height
          ? prev
          : { w: r.width, h: r.height },
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Drag state for moving a text clip across the preview. Stored in a ref so
  // the rAF playback loop doesn't re-render on every pointermove.
  const textDragRef = useRef<{
    id: string;
    pointerId: number;
    rect: DOMRect;
    startX: number;
    startY: number;
    baseXPct: number;
    baseYPct: number;
    moved: boolean;
  } | null>(null);

  // Which text clip (if any) is currently in inline-content-edit mode
  // (entered via double-click on the overlay). While set, the overlay is
  // contenteditable and dragging is disabled so the user can select text.
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  // Exit content-edit mode whenever the selection changes to something else.
  useEffect(() => {
    if (editingTextId && selectedClipId !== editingTextId)
      setEditingTextId(null);
  }, [selectedClipId, editingTextId]);
  // Auto-focus + select-all when entering content-edit mode so the user can
  // start typing or replacing immediately without an extra click.
  useEffect(() => {
    if (!editingTextId) return;
    // Defer one frame so the just-rendered span has contentEditable=true.
    const raf = requestAnimationFrame(() => {
      const span = previewRef.current?.querySelector<HTMLSpanElement>(
        `span[contenteditable="true"]`,
      );
      if (!span) return;
      span.focus();
      const range = document.createRange();
      range.selectNodeContents(span);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [editingTextId]);

  const handleTextPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => {
    if (!previewRef.current) return;
    // While the user is editing the content inline, let the browser handle
    // pointer events for caret placement / text selection.
    if (editingTextId === clip.id) return;
    e.stopPropagation();
    setSelectedClipId(clip.id);
    // Editing happens in the top toolbar now; don't hijack the side panel.
    const rect = previewRef.current.getBoundingClientRect();
    textDragRef.current = {
      id: clip.id,
      pointerId: e.pointerId,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      baseXPct: clip.textXPct ?? 50,
      baseYPct: clip.textYPct ?? 50,
      moved: false,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handleTextPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = textDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = ((e.clientX - drag.startX) / drag.rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / drag.rect.height) * 100;
    if (!drag.moved && Math.hypot(dx, dy) < 0.5) return;
    drag.moved = true;
    const nextX = Math.max(0, Math.min(100, drag.baseXPct + dx));
    const nextY = Math.max(0, Math.min(100, drag.baseYPct + dy));
    setClips((prev) =>
      patchClipsWithSubtitleSync(prev, drag.id, {
        textXPct: nextX,
        textYPct: nextY,
      }),
    );
  };

  const handleTextPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = textDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    textDragRef.current = null;
  };

  // ── Text resize via SelectionFrame ───────────────────────────────────────
  // Cambiar el ancho de la caja del texto reacomoda el wrap (más / menos
  // líneas) sin alterar el font-size: separamos "tamaño visual" (font)
  // de "área de layout" (caja). Solo handles horizontales y de esquina
  // aplican — N/S serían no-ops porque el alto del texto sigue al contenido.
  const textResizeRef = useRef<{ id: string; baseWidthPct: number } | null>(
    null,
  );
  const handleTextResize = useCallback(
    (clip: TimelineClip) =>
      (
        handle: SelectionHandle,
        dx: number,
        _dy: number,
        phase: "start" | "move" | "end",
      ) => {
        if (handle === "n" || handle === "s") return;
        const canvasW = previewSize.w;
        if (!canvasW) return;

        if (phase === "start") {
          // Medimos el ancho actual del wrapper en pixels y lo pasamos
          // a % de canvas. Si el clip nunca tuvo `textWidthPct` seteado,
          // este es el primer commit explícito desde su ancho natural.
          let baseW = clip.textWidthPct;
          if (typeof baseW !== "number") {
            const el = previewRef.current?.querySelector<HTMLElement>(
              `[data-text-clip-id="${clip.id}"]`,
            );
            if (el) {
              const r = el.getBoundingClientRect();
              baseW = (r.width / canvasW) * 100;
            } else {
              baseW = 50;
            }
          }
          textResizeRef.current = { id: clip.id, baseWidthPct: baseW };
          return;
        }

        const ref = textResizeRef.current;
        if (!ref || ref.id !== clip.id) return;

        const dxPct = (dx / canvasW) * 100;
        // Como el texto está anclado al centro (translate -50%), mover
        // *un* lado debe ensanchar la caja en 2*dx para que el centro
        // quede fijo. Signo según qué handle se está arrastrando.
        const horizSign =
          handle === "e" || handle === "ne" || handle === "se"
            ? 1
            : handle === "w" || handle === "nw" || handle === "sw"
              ? -1
              : 0;
        const next = Math.max(
          5,
          Math.min(300, ref.baseWidthPct + 2 * horizSign * dxPct),
        );

        setClips((prev) =>
          prev.map((c) =>
            c.id === clip.id ? { ...c, textWidthPct: next } : c,
          ),
        );

        if (phase === "end") {
          textResizeRef.current = null;
        }
      },
    [previewSize.w],
  );

  // ── Velocidad del clip ───────────────────────────────────────────────────
  // El material que el clip muestra no cambia: cambia cuánto TARDA en pasar.
  // Por eso al mover el slider se recalcula `duration` —los segundos de
  // material (`duration * velocidad`) se mantienen— y el clip se achica en la
  // timeline al acelerar y se agranda al frenar.
  const cambiarVelocidadDeClip = useCallback(
    (clipId: string, velocidad: number) => {
      const nueva = Math.min(
        VELOCIDAD_MAX,
        Math.max(VELOCIDAD_MIN, Math.round(velocidad * 100) / 100),
      );
      setClips((prev) =>
        prev.map((c) => {
          if (c.id !== clipId) return c;
          const anterior = velocidadDeClip(c);
          if (Math.abs(anterior - nueva) < 0.001) return c;
          const segundosDeMaterial = c.duration * anterior;
          return {
            ...c,
            speed: nueva,
            duration: Math.max(0.1, segundosDeMaterial / nueva),
          };
        }),
      );
    },
    [],
  );

  // ── Girar un texto ───────────────────────────────────────────────────────
  // Mismo gesto que el encuadre de video (`handleEncuadreRotate`): se guarda
  // el ángulo de arranque y el centro del elemento en pantalla, y
  // `rotarDesdeManija` devuelve el giro nuevo a partir de cuánto se movió el
  // puntero alrededor de ese centro (con imán a 0/90/180/270).
  //
  // El centro sale del DOM y no de una cuenta: el texto no tiene caja
  // declarada —su alto lo pone el contenido— así que la única fuente de
  // verdad es lo que mide el navegador. Sirve girado: rotar alrededor del
  // centro no mueve el centro del rectángulo que devuelve `getBoundingClientRect`.
  const textoRotandoRef = useRef<{
    id: string;
    rotacionBase: number;
    centro: { x: number; y: number };
    desde: { x: number; y: number };
  } | null>(null);
  const handleTextRotate = useCallback(
    (clip: TimelineClip) =>
      (clientX: number, clientY: number, phase: "start" | "move" | "end") => {
        if (phase === "start") {
          const el = previewRef.current?.querySelector<HTMLElement>(
            `[data-text-clip-id="${clip.id}"]`,
          );
          if (!el) return;
          const r = el.getBoundingClientRect();
          textoRotandoRef.current = {
            id: clip.id,
            rotacionBase: clip.elementRotation ?? 0,
            centro: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
            desde: { x: clientX, y: clientY },
          };
          return;
        }

        const g = textoRotandoRef.current;
        if (!g || g.id !== clip.id) return;

        const elementRotation = rotarDesdeManija({
          rotacionBase: g.rotacionBase,
          centro: g.centro,
          desde: g.desde,
          hasta: { x: clientX, y: clientY },
        });
        setClips((prev) =>
          prev.map((c) => (c.id === clip.id ? { ...c, elementRotation } : c)),
        );

        if (phase === "end") textoRotandoRef.current = null;
      },
    [],
  );

  // ── Animation drag (mismo patrón que el texto) ──────────────────────────
  // Conceptualmente idéntico al drag de texto, pero con un clamp distinto:
  // como el wrapper se escala con `animScale`, hay que mantener el contenido
  // dentro del canvas teniendo en cuenta cuánto ocupa después de escalar.
  const animDragRef = useRef<{
    id: string;
    pointerId: number;
    rect: DOMRect;
    startX: number;
    startY: number;
    baseXPct: number;
    baseYPct: number;
    scale: number;
    moved: boolean;
  } | null>(null);

  const handleAnimPointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    clip: TimelineClip,
  ) => {
    if (!previewRef.current) return;
    e.stopPropagation();
    setSelectedClipId(clip.id);
    const rect = previewRef.current.getBoundingClientRect();
    animDragRef.current = {
      id: clip.id,
      pointerId: e.pointerId,
      rect,
      startX: e.clientX,
      startY: e.clientY,
      baseXPct: clip.animXPct ?? 50,
      baseYPct: clip.animYPct ?? 50,
      scale: clip.animScale ?? 1,
      moved: false,
    };
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handleAnimPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = animDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = ((e.clientX - drag.startX) / drag.rect.width) * 100;
    const dy = ((e.clientY - drag.startY) / drag.rect.height) * 100;
    if (!drag.moved && Math.hypot(dx, dy) < 0.5) return;
    drag.moved = true;
    // Mismo clamp que el texto: solo el *centro* tiene que quedar dentro
    // del canvas. Esto permite arrastrar el elemento parcialmente afuera
    // (medio fuera de pantalla a propósito), que es algo que el usuario
    // pidió explícitamente — útil para entradas/salidas y composiciones
    // tipo "viene desde el costado".
    const nextX = Math.max(0, Math.min(100, drag.baseXPct + dx));
    const nextY = Math.max(0, Math.min(100, drag.baseYPct + dy));
    setClips((prev) =>
      prev.map((c) =>
        c.id === drag.id ? { ...c, animXPct: nextX, animYPct: nextY } : c,
      ),
    );
  };

  const handleAnimPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = animDragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {}
    animDragRef.current = null;
  };

  // ── Animation resize via SelectionFrame ──────────────────────────────────
  // Cualquier handle escala uniformemente con respecto al centro.
  // Convertimos el drag-outward (componente del delta en la dirección del
  // handle) a un factor multiplicativo sobre `animScale`. El denominador
  // (alto del canvas en px) hace que la sensibilidad sea proporcional al
  // tamaño visible — un drag del 50% del canvas duplica/halve la escala.
  const animResizeRef = useRef<{ id: string; baseScale: number } | null>(null);
  const handleAnimResize = useCallback(
    (clip: TimelineClip) =>
      (
        handle: SelectionHandle,
        dx: number,
        dy: number,
        phase: "start" | "move" | "end",
      ) => {
        const refH = previewSize.h;
        if (!refH) return;
        if (phase === "start") {
          animResizeRef.current = {
            id: clip.id,
            baseScale: clip.animScale ?? 1,
          };
          return;
        }
        const ref = animResizeRef.current;
        if (!ref || ref.id !== clip.id) return;

        // Vector "outward" según qué lado/esquina se arrastra. N/S no
        // aportan en X y E/W no aportan en Y; las esquinas suman ambas.
        const dirX =
          handle === "e" || handle === "ne" || handle === "se"
            ? 1
            : handle === "w" || handle === "nw" || handle === "sw"
              ? -1
              : 0;
        const dirY =
          handle === "s" || handle === "se" || handle === "sw"
            ? 1
            : handle === "n" || handle === "ne" || handle === "nw"
              ? -1
              : 0;
        const dragOut = dx * dirX + dy * dirY;
        // Factor 2 porque la manija está a la mitad del bbox: un drag
        // de "d" mueve 2d el ancho total (centro fijo).
        const factor = 1 + (2 * dragOut) / refH;
        const next = Math.max(0.1, Math.min(3, ref.baseScale * factor));
        setClips((prev) =>
          prev.map((c) => (c.id === clip.id ? { ...c, animScale: next } : c)),
        );
        if (phase === "end") animResizeRef.current = null;
      },
    [previewSize.h],
  );

  // ── Encuadre del clip de video (escalar · recortar · girar · mover) ───────
  // Las tres acciones entran por el MISMO marco de selección que el texto y las
  // animaciones, pero con un reparto de manijas propio:
  //   · esquinas → escalar, siempre proporcional (el video no se deforma);
  //   · lados    → recortar ese borde (el de enfrente queda quieto);
  //   · círculo  → girar;
  //   · arrastrar el clip → moverlo dentro del cuadro.
  // La matemática entera vive en `lib/encuadreDeClip.ts`, que está espejado en
  // Remotion: lo que se ve acá es lo que sale en el MP4.
  const actualizarEncuadre = useCallback(
    (clipId: string, parcial: Partial<EncuadreDeClip>) => {
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? { ...c, encuadre: { ...normalizarEncuadre(c.encuadre), ...parcial } }
            : c,
        ),
      );
    },
    [],
  );

  const restablecerEncuadre = useCallback((clipId: string) => {
    // Se borra el campo en vez de guardar el encuadre neutro: un clip sin
    // tocar no tiene por qué arrastrar datos, y así `esEncuadreNeutro` y el
    // serializer pueden saltearlo sin mirar adentro.
    setClips((prev) =>
      prev.map((c) => {
        if (c.id !== clipId) return c;
        const { encuadre: _descartado, ...resto } = c;
        return resto as TimelineClip;
      }),
    );
  }, []);

  // Valores al empezar el gesto. Van por ref porque los `move` llegan a 60fps
  // y el cálculo siempre parte del estado inicial, no del anterior (acumular
  // deltas arrastra error y hace que el clip "derive" respecto del puntero).
  const encuadreGestoRef = useRef<{
    id: string;
    escalaBase: number;
    recorteBase: RecorteDeClip;
    rotacionBase: number;
    /** El encuadre completo al empezar: el escalado anclado necesita también
     *  la posición y el recorte para saber dónde cae el vértice opuesto. */
    encuadreBase: EncuadreDeClip;
    /** Ancho/alto del contenido, medido del DOM al arrancar el gesto. Sólo lo
     *  tienen los que NO llenan su caja (imágenes con `object-contain`). Se
     *  mide acá y no se lee del estado `aspectoDeImagen` por dos razones: ese
     *  estado se declara MÁS ABAJO que estos handlers, y meterlo en las deps
     *  del callback lo dejaría en zona muerta durante el render. Leerlo sin
     *  ponerlo en las deps es peor todavía: el callback se queda con el mapa
     *  vacío del primer render y el ancla nunca se corrige. */
    aspectoDelContenido?: number;
  } | null>(null);

  const handleEncuadreResize = useCallback(
    (clip: TimelineClip) =>
      (
        handle: SelectionHandle,
        dx: number,
        dy: number,
        phase: "start" | "move" | "end",
      ) => {
        const cuadro = { w: previewSize.w, h: previewSize.h };
        if (!cuadro.w || !cuadro.h) return;

        if (phase === "start") {
          const base = normalizarEncuadre(clip.encuadre);
          // El aspecto sale del <img> ya pintado: es la única fuente que no
          // depende de que un estado haya llegado a tiempo.
          const imgDelClip =
            clip.type === "image"
              ? previewRef.current?.querySelector<HTMLImageElement>(
                  `[data-encuadre-clip-id="${clip.id}"] img`,
                )
              : null;
          encuadreGestoRef.current = {
            id: clip.id,
            escalaBase: base.scale,
            recorteBase: base.crop,
            rotacionBase: base.rotation,
            encuadreBase: base,
            aspectoDelContenido:
              imgDelClip && imgDelClip.naturalWidth && imgDelClip.naturalHeight
                ? imgDelClip.naturalWidth / imgDelClip.naturalHeight
                : undefined,
          };
          return;
        }

        const g = encuadreGestoRef.current;
        if (!g || g.id !== clip.id) return;

        if (handle.length === 2) {
          // Devuelve escala Y posición juntas: el ancla es el vértice opuesto,
          // y mantenerlo quieto mientras el clip se achica es justamente mover
          // el centro.
          const r = escalarDesdeEsquina({
            encuadreBase: g.encuadreBase,
            handle,
            dx,
            dy,
            cuadro,
            // El aspecto del contenido, para los que NO llenan su caja: una
            // imagen se dibuja con `object-contain` y lo que se ve es más chico
            // que la caja del encuadre. Sin esto, el ancla se calcula sobre la
            // caja y la esquina opuesta —la que el usuario ve clavada en el
            // marco— se corre en el eje que sobra.
            aspectoDelContenido: g.aspectoDelContenido,
          });
          actualizarEncuadre(clip.id, {
            scale: r.scale,
            xPct: r.xPct,
            yPct: r.yPct,
          });
        } else {
          actualizarEncuadre(clip.id, {
            crop: recortarDesdeLado({
              recorteBase: g.recorteBase,
              handle,
              dx,
              dy,
              cuadro,
              rotacionGrados: g.rotacionBase,
              scale: g.escalaBase,
            }),
          });
        }

        if (phase === "end") encuadreGestoRef.current = null;
      },
    [previewSize.w, previewSize.h, actualizarEncuadre],
  );

  // La rotación necesita el CENTRO del clip en coordenadas de pantalla (el
  // ángulo se mide contra él) y el punto donde arrancó el gesto, para girar por
  // diferencia y que el video no pegue un salto al agarrar la manija.
  const rotacionGestoRef = useRef<{
    id: string;
    rotacionBase: number;
    centro: { x: number; y: number };
    desde: { x: number; y: number };
  } | null>(null);

  // Qué clip se está girando AHORA. Es estado y no ref porque dispara el
  // cartelito de grados: mientras dura el gesto hay que re-renderizar para
  // que el número siga al puntero.
  const [girandoClipId, setGirandoClipId] = useState<string | null>(null);

  const handleEncuadreRotate = useCallback(
    (clip: TimelineClip) =>
      (clientX: number, clientY: number, phase: "start" | "move" | "end") => {
        if (phase === "start") {
          const marco = previewRef.current?.getBoundingClientRect();
          if (!marco) return;
          const caja = cajaDelEncuadre(clip.encuadre, {
            w: previewSize.w,
            h: previewSize.h,
          });
          rotacionGestoRef.current = {
            id: clip.id,
            rotacionBase: normalizarEncuadre(clip.encuadre).rotation,
            centro: {
              x: marco.left + caja.centroXPx,
              y: marco.top + caja.centroYPx,
            },
            desde: { x: clientX, y: clientY },
          };
          setGirandoClipId(clip.id);
          return;
        }

        const g = rotacionGestoRef.current;
        if (!g || g.id !== clip.id) return;

        actualizarEncuadre(clip.id, {
          rotation: rotarDesdeManija({
            rotacionBase: g.rotacionBase,
            centro: g.centro,
            desde: g.desde,
            hasta: { x: clientX, y: clientY },
          }),
        });

        if (phase === "end") {
          rotacionGestoRef.current = null;
          setGirandoClipId(null);
        }
      },
    [previewSize.w, previewSize.h, actualizarEncuadre],
  );

  // Arrastrar el clip para moverlo. Sin esto, agrandar no serviría de mucho:
  // el zoom quedaría siempre centrado y no habría forma de elegir qué parte
  // del video queda a la vista.
  const encuadreDragRef = useRef<{
    id: string;
    pointerId: number;
    startX: number;
    startY: number;
    baseXPct: number;
    baseYPct: number;
    /** El resto del encuadre (escala, recorte, giro) no cambia mientras se
     *  mueve, pero el imán lo necesita para saber dónde caen los bordes. */
    encuadreBase: EncuadreDeClip;
  } | null>(null);

  // Guías del imán: dónde dibujar la línea mientras un borde está pegado.
  // `null` en un eje = ese eje no está pegado a nada.
  const [guiasDelIman, setGuiasDelIman] = useState<{
    x: number | null;
    y: number | null;
  }>({ x: null, y: null });

  const handleEncuadrePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, clip: TimelineClip) => {
      // El click selecciona el clip aunque después no se arrastre.
      setSelectedClipId(clip.id);
      const base = normalizarEncuadre(clip.encuadre);
      encuadreDragRef.current = {
        id: clip.id,
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        baseXPct: base.xPct,
        baseYPct: base.yPct,
        encuadreBase: base,
      };
      try {
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      } catch {}
    },
    [],
  );

  const handleEncuadrePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = encuadreDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      if (!previewSize.w || !previewSize.h) return;
      const dxPct = ((e.clientX - d.startX) / previewSize.w) * 100;
      const dyPct = ((e.clientY - d.startY) / previewSize.h) * 100;
      // Mismo criterio que el texto y las animaciones: se clampea el CENTRO,
      // no el borde, así se puede dejar el clip entrando desde un costado.
      const xPct = Math.max(-100, Math.min(200, d.baseXPct + dxPct));
      const yPct = Math.max(-100, Math.min(200, d.baseYPct + dyPct));

      // Imán a los bordes del cuadro (y a sus ejes centrales). Se aplica sobre
      // la posición que pedía el puntero, no sobre la anterior: así el imán
      // corrige una sola vez y soltarse de un borde es seguir arrastrando, no
      // pelear contra un arrastre que se acumula.
      const iman = imantarAlCuadro({
        encuadre: { ...d.encuadreBase, xPct, yPct },
        cuadro: { w: previewSize.w, h: previewSize.h },
      });
      actualizarEncuadre(d.id, { xPct: iman.xPct, yPct: iman.yPct });
      setGuiasDelIman({ x: iman.guiaXPx, y: iman.guiaYPx });
    },
    [previewSize.w, previewSize.h, actualizarEncuadre],
  );

  const handleEncuadrePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = encuadreDragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      try {
        (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
      } catch {}
      encuadreDragRef.current = null;
      setGuiasDelIman({ x: null, y: null });
    },
    [],
  );

  // ── Display rows (one per visible track; audio expands into N rows) ──────
  type DisplayRow = {
    key: string;
    trackType: TrackType;
    label: string;
    color: string;
    matchClip: (c: TimelineClip) => boolean;
    audioIndex?: number;
    videoIndex?: number;
    /** Capa de elementos (texto · imagen · animación). La fila de subtítulos
     *  NO lo tiene: no se reordena. */
    elementIndex?: number;
  };
  const displayRows = useMemo<DisplayRow[]>(() => {
    const rows: DisplayRow[] = [];
    // Si hay un pin de pista origen activo (drag en curso), aseguramos que
    // esa fila siga existiendo aunque su clip único ya se haya movido a
    // otra pista durante el drag.
    const pinnedAudioIdx =
      pinnedOriginRow?.trackType === "audio" ? pinnedOriginRow.index : null;
    const pinnedVideoIdx =
      pinnedOriginRow?.trackType === "video" ? pinnedOriginRow.index : null;
    const pinnedElementIdx =
      pinnedOriginRow?.trackType === "elements" ? pinnedOriginRow.index : null;
    for (const tc of TRACK_CONFIG) {
      if (tc.type === "audio") {
        const baseIdxs = getAudioTrackIndices(clips);
        const idxs =
          pinnedAudioIdx !== null && !baseIdxs.includes(pinnedAudioIdx)
            ? [...baseIdxs, pinnedAudioIdx].sort((a, b) => a - b)
            : baseIdxs;
        idxs.forEach((idx, orderPos) => {
          rows.push({
            key: `audio-${idx}`,
            trackType: "audio",
            label: idxs.length > 1 ? `Audio ${orderPos + 1}` : "Audio",
            color: getAudioTrackColor(idx),
            matchClip: (c) =>
              c.type === "audio" && getClipTrackIndex(c) === idx,
            audioIndex: idx,
          });
        });
      } else if (tc.type === "video") {
        // Render video tracks in descending trackIndex so that higher tracks
        // appear higher in the timeline (and are drawn on top in the preview).
        const baseVideoIdxs = getVideoTrackIndices(clips);
        const mergedVideoIdxs =
          pinnedVideoIdx !== null && !baseVideoIdxs.includes(pinnedVideoIdx)
            ? [...baseVideoIdxs, pinnedVideoIdx]
            : baseVideoIdxs;
        const idxs = [...mergedVideoIdxs].sort((a, b) => b - a);
        idxs.forEach((idx) => {
          rows.push({
            key: `video-${idx}`,
            trackType: "video",
            label: idxs.length > 1 ? `Video ${idx + 1}` : "Video",
            color: tc.color,
            matchClip: (c) =>
              tc.clipTypes.includes(c.type) && getClipTrackIndex(c) === idx,
            videoIndex: idx,
          });
        });
      } else {
        // Elements track: optionally split into a dedicated "Subtítulos" row
        // (when at least one subtitle clip exists) plus the regular elements
        // row. Subtitles always render *above* other elements so the user can
        // visually parse which row hosts the auto-generated captions.
        const hasSubtitles = clips.some(
          (c) => c.type === "text" && c.isSubtitle,
        );
        const hasOtherElements = clips.some(
          (c) =>
            tc.clipTypes.includes(c.type) &&
            !(c.type === "text" && c.isSubtitle),
        );
        if (hasSubtitles) {
          rows.push({
            key: "elements-subtitles",
            trackType: tc.type,
            label: "Subtítulos",
            color: "#8B5CF6",
            matchClip: (c) => c.type === "text" && !!c.isSubtitle,
          });
        }
        // Los elementos también se reparten en capas: cada texto, imagen o
        // animación que se agrega estrena la suya (capaNuevaPara), así que la
        // fila única de antes pasa a ser una fila por índice en uso. Van de
        // mayor a menor como las de video, para que la última que se agregó
        // quede arriba. Ver ADR-2026-09-15-cada-elemento-nuevo-estrena-capa.
        if (hasOtherElements || pinnedElementIdx !== null) {
          const base = new Set(
            clips.filter(esElementoDeCapa).map(getClipTrackIndex),
          );
          if (pinnedElementIdx !== null) base.add(pinnedElementIdx);
          const idxs = [...base].sort((a, b) => b - a);
          idxs.forEach((idx) => {
            rows.push({
              key: `${tc.type}-${idx}`,
              trackType: tc.type,
              label: idxs.length > 1 ? `${tc.label} ${idx + 1}` : tc.label,
              color: tc.color,
              matchClip: (c) =>
                esElementoDeCapa(c) && getClipTrackIndex(c) === idx,
              elementIndex: idx,
            });
          });
        }
      }
    }
    // El orden natural (elementos · video · audio) es sólo el punto de
    // partida: manda la lista que armó el usuario arrastrando.
    return ordenarFilas(rows, ordenDeFilas);
  }, [clips, pinnedOriginRow, ordenDeFilas]);

  // ── Qué tapa a qué en el preview ────────────────────────────────────────
  // Lo que está más ARRIBA en la timeline se dibuja más ADELANTE. Antes el
  // preview tenía su propio criterio —los videos por `trackIndex`, y el texto
  // y las animaciones siempre por encima de todo, por el orden del JSX—, así
  // que poner un texto debajo de un video en la timeline no cambiaba nada: se
  // seguía viendo sobre el video. Ahora el orden de las filas es el único
  // criterio, y por eso vale la pena reubicar una capa.
  // Los números arrancan en 1 y llegan a la cantidad de filas, por debajo de los
  // overlays de transición (la línea del wipe y el light-leak), que van adelante
  // de todo el contenido a propósito.
  // Ver ADR-2026-09-15-el-orden-de-la-timeline-manda-en-el-preview.
  const zDeLaCapa = useMemo(() => {
    const mapa = new Map<string, number>();
    displayRows.forEach((row, i) => mapa.set(row.key, displayRows.length - i));
    return mapa;
  }, [displayRows]);
  // Los overlays de transición, por encima de la capa de contenido más alta.
  // Antes eran z-20 y z-30 fijos: un proyecto con 20 filas o más empataba con
  // ellos y una capa de contenido tapaba el efecto. Los 20/30 quedan de piso
  // para no mover nada en los proyectos normales.
  // Espejo de `zDeLosOverlays` en `remotion/src/TimelineComposition.tsx`.
  const zDeLaLineaDeRevelado = Math.max(20, displayRows.length + 1);
  const zDelOverlayDeTransicion = Math.max(30, displayRows.length + 2);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const zDelClip = useCallback(
    (c: TimelineClip): number => {
      const clave =
        c.type === "text" && c.isSubtitle
          ? "elements-subtitles"
          : `${familiaDePista(c) ?? "elements"}-${getClipTrackIndex(c)}`;
      return zDeLaCapa.get(clave) ?? 1;
    },
    [zDeLaCapa],
  );
  zDelClipRef.current = zDelClip;

  // Layout constants shared between render and pointer handlers
  const TIMELINE_RULER_H = 28;
  const TIMELINE_ROW_H = 48;
  const TIMELINE_ROW_GAP = 6;

  // ── Sync a single media element to the timeline ────────────────────────
  const syncMediaElement = useCallback(
    (
      clip: TimelineClip,
      el: HTMLVideoElement | HTMLAudioElement,
      time: number,
      playing: boolean,
      allClips: TimelineClip[],
    ) => {
      const velocidad = velocidadDeClip(clip);
      // Video clips can stay "active" past their visual end while a successor
      // clip is mid cross-fade — see `isClipActiveAtTime`. Audio clips never
      // extend (no transitions for audio yet) so their behaviour is unchanged.
      const isActive =
        clip.type === "audio"
          ? time >= clip.startTime && time < clip.startTime + clip.duration
          : isClipActiveAtTime(clip, time, allClips);

      // Always keep the element volume in sync with the clip's volume.
      // HTMLMediaElement.volume only supports 0..1, so for values above 100% we
      // route the element through a GainNode that can go up to 2 (200%).
      //
      // Importante: una vez que se llamó a createMediaElementSource(el) para
      // un elemento, ese elemento queda PERMANENTEMENTE desconectado del
      // output nativo del navegador — todo el audio pasa por el AudioContext.
      // Por eso, si alguna vez existió un gain node para este clip, el
      // control real del volumen está en el gain, NO en el.volume. Setear
      // el.volume cuando el clip ya tiene gain node es no-op.
      const rawVol = Math.max(0, Math.min(2, clip.volume ?? 1));
      if (rawVol > 1) {
        // Lazy-init shared AudioContext + per-clip source & gain
        if (!audioCtxRef.current) {
          try {
            const Ctx = (window.AudioContext ||
              (window as any).webkitAudioContext) as typeof AudioContext;
            audioCtxRef.current = new Ctx();
          } catch {}
        }
        const ctx = audioCtxRef.current;
        if (ctx) {
          if (ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }
          let node = mediaAudioNodesRef.current.get(clip.id);
          // Red de seguridad: sólo se engancha lo que el navegador va a poder
          // leer. Enganchar un media que no puede leer no falla — devuelve
          // silencio, y como el enganche es irreversible el clip queda mudo
          // para siempre. Mejor no amplificar y que se escuche al 100%.
          if (!node && puedeRutearsePorWebAudio(el)) {
            try {
              const source = ctx.createMediaElementSource(el);
              const gain = ctx.createGain();
              source.connect(gain);
              gain.connect(ctx.destination);
              node = { source, gain };
              mediaAudioNodesRef.current.set(clip.id, node);
            } catch {
              // If the element is already attached to a different context, fall back silently
            }
          }
          if (node) node.gain.gain.value = rawVol;
        }
        if (Math.abs(el.volume - 1) > 0.001) el.volume = 1;
      } else {
        // 0..100%: si el clip alguna vez pasó por > 100% ya existe un gain
        // node y el audio rutea por el AudioContext. En ese caso seguimos
        // controlando todo desde el gain, no desde el.volume.
        const node = mediaAudioNodesRef.current.get(clip.id);
        if (node) {
          // El ctx se suspende cuando la página pierde foco / hay
          // inactividad. Si está suspended, no se escucha NADA aunque el
          // gain esté en su valor correcto. Lo resumimos.
          const ctx = audioCtxRef.current;
          if (ctx && ctx.state === "suspended") {
            ctx.resume().catch(() => {});
          }
          node.gain.gain.value = rawVol;
          if (Math.abs(el.volume - 1) > 0.001) el.volume = 1;
        } else {
          if (Math.abs(el.volume - rawVol) > 0.001) el.volume = rawVol;
        }
      }
      // Red de seguridad: si algún path raro dejó el elemento con
      // muted=true (autoplay policies del browser, otros componentes, etc),
      // lo destrabamos. El control de "audible o no" lo hace el rawVol
      // arriba (via el.volume o gain).
      if (el.muted) el.muted = false;

      if (!isActive) {
        if (!el.paused) el.pause();
        // El que viene se deja parado en su primer cuadro un poco antes de su
        // turno: llega al corte sin buscar (ADR-2026-11-03-el-play-sigue-al-video).
        const destino = aDondePrepararlo({
          inicio: clip.startTime,
          offset: clip.mediaOffset || 0,
          tiempo: time,
          tiempoDelArchivo: el.currentTime,
          buscando: el.seeking,
        });
        if (destino !== null) {
          try {
            el.currentTime = destino;
          } catch {}
        }
        return;
      }

      // ── Pre-active freeze ────────────────────────────────────────────────
      // The clip's own transitionIn window starts BEFORE `clip.startTime`
      // (we center the fade over the boundary). During that lead-in the
      // <video> is mounted but we don't want to dip into pre-roll source
      // — hold on the first visible frame instead.
      const inPreActive =
        clip.type !== "audio" && time < clip.startTime && !!clip.transitionIn;
      if (inPreActive) {
        const firstFrame = clip.mediaOffset || 0;
        if (Math.abs(el.currentTime - firstFrame) > 0.05) {
          try {
            el.currentTime = firstFrame;
          } catch {}
        }
        if (!el.paused) el.pause();
        return;
      }

      // ── Transition extension freeze ──────────────────────────────────────
      // When this video clip is past its normal end but a successor's
      // transition keeps it "active", we DON'T let playback advance into
      // the source's trimmed-away tail (that would reveal content the user
      // already cut). Instead we hold on the clip's last visible frame
      // while the opacity fade does the visual work.
      const normalEnd = clip.startTime + clip.duration;
      const inExtensionFreeze = clip.type !== "audio" && time >= normalEnd;
      if (inExtensionFreeze) {
        const trimEnd = (clip.mediaOffset || 0) + clip.duration * velocidad;
        // Land on the frame just BEFORE the cut (~16ms back, ~1 frame at
        // 60fps) so we're guaranteed to be inside the user's intended range.
        const freezeFrame = Math.max(0, trimEnd - 1 / 60);
        if (Math.abs(el.currentTime - freezeFrame) > 0.05) {
          try {
            el.currentTime = freezeFrame;
          } catch {}
        }
        if (!el.paused) el.pause();
        return;
      }

      // Un segundo de timeline consume `velocidad` segundos de material, así
      // que el punto del archivo avanza multiplicado por ella. Y el elemento
      // se reproduce a esa misma velocidad para que lo que se ve y lo que se
      // escucha vayan al ritmo del cursor.
      const localTime =
        (time - clip.startTime) * velocidad + (clip.mediaOffset || 0);
      if (Math.abs(el.playbackRate - velocidad) > 0.01)
        el.playbackRate = velocidad;
      // Only seek if difference is significant (avoid constant micro-seeks during playback)
      // Reproduciendo, un seek encima de otro que no terminó sólo lo demora más:
      // el reloj ya lo está esperando.
      if (
        Math.abs(el.currentTime - localTime) > 0.15 &&
        !(playing && el.seeking)
      ) {
        el.currentTime = localTime;
      }

      if (playing && el.paused) {
        el.play().catch(() => {});
      } else if (!playing && !el.paused) {
        el.pause();
      }
    },
    [],
  );

  // ── Sync all media to a given time ─────────────────────────────────────
  const syncAllMedia = useCallback(
    (time: number, playing: boolean) => {
      mediaClips.forEach((clip) => {
        const el = mediaRefs.current.get(clip.id);
        if (el) syncMediaElement(clip, el, time, playing, clips);
      });
    },
    [mediaClips, syncMediaElement, clips],
  );

  // ── Playback loop (rAF-based) ──────────────────────────────────────────
  // El tiempo sale del video que se está viendo; si está buscando o sin datos,
  // el reloj lo espera (ADR-2026-11-03-el-play-sigue-al-video).
  const videoQueMandaEn = useCallback(
    (time: number): VideoQueManda | null => {
      let elegido: { clip: TimelineClip; el: HTMLVideoElement } | null = null;
      for (const c of mediaClips) {
        if (!isVideoClip(c)) continue;
        if (time < c.startTime || time >= c.startTime + c.duration) continue;
        const el = mediaRefs.current.get(c.id);
        if (!(el instanceof HTMLVideoElement) || !el.currentSrc) continue;
        if (
          !elegido ||
          getClipTrackIndex(c) < getClipTrackIndex(elegido.clip)
        )
          elegido = { clip: c, el };
      }
      if (!elegido) return null;
      const { clip, el } = elegido;
      return {
        inicio: clip.startTime,
        duracion: clip.duration,
        offset: clip.mediaOffset || 0,
        velocidad: velocidadDeClip(clip),
        tiempoDelArchivo: el.currentTime,
        listo:
          !el.seeking &&
          !el.paused &&
          el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA,
        descartado: el.ended || !!el.error,
        bajando: el.networkState === HTMLMediaElement.NETWORK_LOADING,
      };
    },
    [mediaClips],
  );
  const esperandoAlVideoDesdeRef = useRef<number | null>(null);
  // Se avisa recién pasados unos 400 ms: las esperas de un cuadro al arrancar un
  // clip no se muestran.
  const [esperandoAlVideo, setEsperandoAlVideo] = useState(false);
  const esperandoAlVideoRef = useRef(false);
  const avisarEspera = useCallback((esperando: boolean) => {
    if (esperandoAlVideoRef.current === esperando) return;
    esperandoAlVideoRef.current = esperando;
    setEsperandoAlVideo(esperando);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      lastFrameRef.current = performance.now();
      esperandoAlVideoDesdeRef.current = null;
      const tick = (now: number) => {
        const delta = (now - lastFrameRef.current) / 1000;
        lastFrameRef.current = now;
        const desde = esperandoAlVideoDesdeRef.current;
        avisarEspera(desde !== null && now - desde > 400);
        setCurrentTime((prev) => {
          const paso = siguienteTiempo({
            anterior: prev,
            delta,
            ahora: now,
            manda: videoQueMandaEn(prev),
            esperandoDesde: esperandoAlVideoDesdeRef.current,
          });
          esperandoAlVideoDesdeRef.current = paso.esperandoDesde;
          const next = paso.tiempo;
          // Modo loop de previsualización: si hay un clip seleccionado para
          // reproducir en loop y el playhead llega al final del mismo,
          // hacemos snap a su inicio en lugar de frenar.
          if (animationPreviewLoopClipId) {
            const loopClip = clips.find(
              (c) => c.id === animationPreviewLoopClipId,
            );
            if (loopClip) {
              const end = loopClip.startTime + loopClip.duration;
              if (next >= end) return loopClip.startTime;
            }
          }
          const stopAt = contentDuration > 0 ? contentDuration : duration;
          if (next >= stopAt) {
            setIsPlaying(false);
            return stopAt;
          }
          return next;
        });
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } else {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      avisarEspera(false);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    avisarEspera,
    isPlaying,
    duration,
    contentDuration,
    animationPreviewLoopClipId,
    clips,
    videoQueMandaEn,
  ]);

  // Cuando el playback se detiene por cualquier vía (botón global de
  // pausa, llegar al fin del proyecto sin loop, etc.), limpiamos el modo
  // loop. Así el botón del panel queda sincronizado con el estado real.
  useEffect(() => {
    if (!isPlaying && animationPreviewLoopClipId) {
      setAnimationPreviewLoopClipId(null);
    }
  }, [isPlaying, animationPreviewLoopClipId]);

  // ── Keep media in sync with currentTime ────────────────────────────────
  useEffect(() => {
    syncAllMedia(currentTime, isPlaying);
  }, [currentTime, isPlaying, syncAllMedia]);

  // ── Clean up stale media refs when clips change ────────────────────────
  useEffect(() => {
    const clipIds = new Set(clips.map((c) => c.id));
    mediaRefs.current.forEach((_, id) => {
      if (!clipIds.has(id)) mediaRefs.current.delete(id);
    });
    mediaAudioNodesRef.current.forEach((node, id) => {
      if (!clipIds.has(id)) {
        try {
          node.source.disconnect();
          node.gain.disconnect();
        } catch {}
        mediaAudioNodesRef.current.delete(id);
      }
    });
  }, [clips]);

  // ── Timeline helpers ─────────────────────────────────────────────────────────
  const clientXToSeconds = useCallback(
    (clientX: number): number => {
      if (!timelineAreaRef.current) return 0;
      const el = timelineAreaRef.current;
      const rect = el.getBoundingClientRect();
      const px = Math.max(
        0,
        clientX - rect.left - padIzqTimeline + el.scrollLeft,
      );
      return px / pps;
    },
    [pps, padIzqTimeline],
  );

  // Segundos para MOVER EL CURSOR. `clientXToSeconds` ya corta en 0 por
  // izquierda, pero no tenía tope por derecha: arrastrando sobre la cola vacía
  // de la regla (120s después del último clip) el cursor se iba más allá del
  // final del video. El tope es el mismo que frena la reproducción (`stopAt`
  // del loop de rAF): el fin del contenido, o la duración del proyecto si la
  // timeline está vacía.
  //
  // Va aparte y no adentro de `clientXToSeconds` porque esa conversión también
  // la usan el drop de assets y el arrastre de clips, que sí pueden apuntar más
  // allá del último clip — ahí es donde se agrega material nuevo.
  const clientXToSeek = useCallback(
    (clientX: number): number => {
      const fin = contentDuration > 0 ? contentDuration : duration;
      return Math.min(fin, clientXToSeconds(clientX));
    },
    [clientXToSeconds, contentDuration, duration],
  );

  /* ── El scroll de la timeline ES el tiempo (sólo teléfono) ────────────────
     Con el cursor clavado al centro, "dónde estoy" y "cuánto scrolleé" son el
     mismo número: el tiempo bajo el cursor es `scrollLeft / pps`. Los dos
     sentidos de esa igualdad viven acá.

     Del scroll AL tiempo: cada evento de scroll fija el tiempo. Se ignora
     mientras se reproduce, porque ahí el scroll lo estamos escribiendo nosotros
     (y tocar la timeline pausa primero — ver `handleTimelinePointerDown`).

     Del tiempo AL scroll: un `useLayoutEffect` que corre con cada cambio de
     `currentTime` —también los 60 por segundo de la reproducción, que es lo que
     hace que la cinta CORRA bajo el cursor— y antes de pintar, así el frame que
     se ve ya está en su lugar.

     No hace falta ninguna bandera de "este scroll lo escribí yo" para cortar el
     ida y vuelta: el umbral de 0.015s absorbe el redondeo a píxeles del
     navegador (medio píxel son 0.0125s al zoom más lejano, y menos a cualquier
     otro), así que el scroll que provoca el propio efecto vuelve con el MISMO
     tiempo y no dispara nada. */
  const tiempoDesdeElScroll = useCallback((scrollLeft: number) => {
    if (isPlayingRef.current) return;
    const ppsActual = BASE_PPS * zoomRef.current;
    if (ppsActual <= 0) return;
    const fin = finDeReproduccionRef.current;
    const crudo = scrollLeft / ppsActual;
    const t = Math.max(0, fin > 0 ? Math.min(fin, crudo) : crudo);
    if (Math.abs(t - currentTimeRef.current) < 0.015) return;
    setCurrentTime(t);
  }, []);

  useLayoutEffect(() => {
    if (!enElTelefono) return;
    const el = timelineAreaRef.current;
    if (!el) return;
    const objetivo = Math.max(0, currentTime * pps);
    if (Math.abs(el.scrollLeft - objetivo) < 0.5) return;
    el.scrollLeft = objetivo;
  }, [currentTime, pps, enElTelefono, padIzqTimeline]);

  // En escritorio, si el cursor se sale de la vista (reproduciendo, o con las
  // flechas) la cinta salta para mostrarlo, como Premiere. No mientras se
  // arrastra (de eso se encarga el autoscroll) ni al hacer zoom: el zoom no es un
  // cambio de tiempo y el pivote del usuario manda (por eso `pps` va por ref).
  // ADR-2026-11-02-la-timeline-sigue-al-cursor.
  useEffect(() => {
    if (enElTelefono) return;
    if (isDraggingRef.current || draggingClipRef.current) return;
    const el = timelineAreaRef.current;
    if (!el) return;
    const objetivo = scrollParaVerElCursor(
      padIzqTimeline + currentTime * BASE_PPS * zoomRef.current,
      el.scrollLeft,
      el.clientWidth,
    );
    if (objetivo !== null) el.scrollLeft = objetivo;
  }, [currentTime, enElTelefono, padIzqTimeline]);

  // ── Timeline seek (drag-aware) ─────────────────────────────────────────────
  const handleTimelinePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      draggingClipRef.current = null;
      /* En el teléfono el cursor no se agarra —está fijo— así que acá no hay
         nada que scrubear: el dedo sobre la timeline es un scroll, y el tiempo
         sale de él. Tocarla PAUSA primero (si no, la cinta seguiría corriendo
         debajo del dedo y pelearían los dos por el scroll).

         Tampoco se suelta el clip acá: soltar la selección al apoyar el dedo
         haría que cada scroll de la timeline te deje sin el clip que estabas
         editando. Eso se decide al LEVANTAR el dedo, y sólo si fue un toque
         quieto (ver el `onPointerUp` de la timeline). */
      if (enElTelefonoRef.current) {
        if (isPlayingRef.current) setIsPlaying(false);
        toqueEnLaTimelineRef.current = { x: e.clientX, y: e.clientY };
        return;
      }
      // Mover el cursor por la regla (o agarrándolo) NO deselecciona: el
      // usuario necesita tener el clip elegido MIENTRAS busca el punto de
      // corte, o el botón Dividir desaparece justo cuando lo va a usar.
      // Click en un espacio vacío de las pistas sí deselecciona, que es la
      // forma natural de soltar el clip.
      const scrubbing = !!(e.target as HTMLElement | null)?.closest?.(
        "[data-timeline-scrub]",
      );
      if (!scrubbing) {
        setSelectedClipId(null);
        // Un CLICK (sin arrastrar) en el espacio vacío de una pista también lleva
        // el cursor ahí: se decide al soltar, así arrastrar desde el vacío sigue
        // sin mover el tiempo (ADR-2026-09-15-el-cursor-solo-se-mueve-desde-la-regla,
        // reemplazada en esta parte por ADR-2026-11-02-la-timeline-sigue-al-cursor).
        const sobreUnControl = !!(e.target as HTMLElement | null)?.closest?.(
          "button, a, input, textarea, select, [role='button'], [data-clip-id], [data-clip-ui]",
        );
        clickEnPistaRef.current = sobreUnControl
          ? null
          : { x: e.clientX, y: e.clientY };
        return;
      }
      // El cursor se mueve SÓLO desde la regla o desde su propia zona de
      // agarre, que es lo que lleva `data-timeline-scrub`. Antes cualquier
      // pointerdown en el área de la timeline lo movía —incluido el que
      // empieza un arrastre de clip—, así que agarrar una capa para correrla
      // te cambiaba el tiempo de reproducción de yapa.
      isDraggingRef.current = true;
      setIsDraggingPlayhead(true);
      setCurrentTime(clientXToSeek(e.clientX));
      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      empezarAutoscroll({ x: e.clientX, y: e.clientY }, seguirElArrastre);
    },
    [clientXToSeek, empezarAutoscroll, seguirElArrastre],
  );

  // Dónde apretó un click en el espacio vacío de las pistas (null = no fue ahí).
  const clickEnPistaRef = useRef<{ x: number; y: number } | null>(null);

  // Dónde empezó el toque sobre la timeline, para distinguir un TOQUE (suelta la
  // selección) de un ARRASTRE (scrollea la cinta y no toca la selección).
  const toqueEnLaTimelineRef = useRef<{ x: number; y: number } | null>(null);

  /* ── Pellizcar para acercar la timeline (sólo teléfono) ───────────────────
     Reemplaza al slider de zoom, que en un teléfono no tiene ancho ni
     precisión. Es el mismo gesto con el que se acerca cualquier cosa en un
     teléfono, y el equivalente del pinch del trackpad que ya maneja el
     listener de `wheel` en escritorio.

     No hace falta pivote: con el cursor clavado al centro, el tiempo que se ve
     es `scrollLeft / pps`, y el efecto que sincroniza scroll y tiempo vuelve a
     poner `currentTime` en el medio con el zoom nuevo. O sea que el pellizco ya
     acerca "sobre el cursor", que es exactamente donde el usuario está mirando.

     Los handlers de React para `touchmove` son pasivos, así que no se puede
     `preventDefault` — lo que evita que el navegador se lleve el gesto para
     hacerle zoom a la PÁGINA es el `touchAction` del contenedor. */
  const pellizcoRef = useRef<{ distancia: number; zoom: number } | null>(null);
  const distanciaEntreDedos = (t: React.TouchList) =>
    Math.hypot(
      t[0].clientX - t[1].clientX,
      t[0].clientY - t[1].clientY,
    );
  const empezarElPellizco = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 2) {
      pellizcoRef.current = null;
      return;
    }
    pellizcoRef.current = {
      distancia: distanciaEntreDedos(e.touches),
      zoom: zoomRef.current,
    };
  }, []);
  const seguirElPellizco = useCallback((e: React.TouchEvent) => {
    const p = pellizcoRef.current;
    if (!p || p.distancia <= 0 || e.touches.length !== 2) return;
    const factor = distanciaEntreDedos(e.touches) / p.distancia;
    setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, p.zoom * factor)));
  }, []);

  // ── Alto de la zona de la timeline (se arrastra desde el borde de arriba) ─
  // `null` = automático: la timeline mide lo que miden sus pistas y el preview
  // se queda con el resto, que es como venía. Apenas el usuario arrastra la
  // manija pasa a un alto FIJO: la timeline scrollea por dentro y el preview
  // se reacomoda al espacio que queda. Ver
  // ADR-2026-09-15-la-timeline-se-estira-desde-el-borde.
  const [altoDeLaTimeline, setAltoDeLaTimeline] = useState<number | null>(null);
  // Mientras se arrastra, la línea de arriba queda azul aunque el mouse se
  // haya ido de la franja de agarre (con el pointer capturado se sigue
  // estirando igual, y la línea tiene que seguir diciendo que está activa).
  const [estirandoElAlto, setEstirandoElAlto] = useState(false);
  // ── Minimizar / expandir la timeline ────────────────────────────────────
  // Minimizada, el bloque deja a la vista SÓLO la fila del transporte (play,
  // tiempo, zoom, Ajustar): la regla y las pistas se esconden y el preview se
  // queda con todo el alto que sobra. Es para mirar el video grande sin perder
  // el play ni el tiempo. El toggle existía a la derecha de la fila del
  // transporte y se fue al rediseñarla; vuelve al lado de "Ajustar timeline"
  // (ADR-2026-09-17-vuelve-minimizar-la-timeline).
  const [timelineMinimizada, setTimelineMinimizada] = useState(false);
  // A qué alto volver al expandir. Guarda TAMBIÉN el "automático" (`null`):
  // minimizar y expandir tiene que dejar el bloque exactamente como estaba,
  // no mandarlo a un alto fijo que el usuario nunca eligió.
  const altoAntesDeMinimizarRef = useRef<number | null>(null);
  // Mientras dura el colapso, la tarjeta lleva `transition: height`. Sólo
  // mientras dura: con la transición puesta siempre, arrastrar la manija se
  // sentiría elástico (cada píxel del gesto animaría 260ms hacia el destino).
  const [animandoElAlto, setAnimandoElAlto] = useState(false);
  const temporizadorDelColapsoRef = useRef<number | null>(null);
  const bloqueTimelineRef = useRef<HTMLDivElement>(null);
  const arrastreDeAltoRef = useRef<{
    pointerId: number;
    yInicial: number;
    altoInicial: number;
  } | null>(null);
  // Topes. Arriba: hasta donde el usuario quiera, o sea todo lo que dé la
  // columna; sólo se le deja al preview una franja mínima para que no se
  // cierre del todo (con 0 el video desaparece y no hay cómo volver más que
  // con el doble click). Se calcula contra la columna que los contiene a los
  // dos, así que cambia con el tamaño de la ventana.
  //
  // Abajo: `ALTO_MIN_TIMELINE` es sólo el piso de ÚLTIMA INSTANCIA, para
  // cuando todavía no se puede medir el DOM. El piso de verdad lo da
  // `pisoDelAltoDeLaTimeline`.
  const ALTO_MIN_TIMELINE = 120;
  const ALTO_MIN_PREVIEW = 56;
  // El piso del arrastre no es un número fijo: es "que la primera fila se siga
  // viendo ENTERA". Se mide el marco —la manija, la fila del transporte y los
  // paddings: todo lo que en el bloque no es la zona de pistas— y se le suma la
  // regla más una fila con su separación. Con los 120 fijos de antes, achicando
  // a fondo la primera pista quedaba cortada al medio: el tope no sabía cuánto
  // ocupaba el marco, que además cambió varias veces (la fila del transporte,
  // el botón de minimizar, los paddings nuevos).
  const pisoDelAltoDeLaTimeline = useCallback(() => {
    const bloque = bloqueTimelineRef.current;
    const area = timelineAreaRef.current;
    if (!bloque || !area) return ALTO_MIN_TIMELINE;
    const marco = Math.max(0, bloque.offsetHeight - area.clientHeight);
    return marco + TIMELINE_RULER_H + TIMELINE_ROW_GAP + TIMELINE_ROW_H;
  }, [
    ALTO_MIN_TIMELINE,
    TIMELINE_RULER_H,
    TIMELINE_ROW_GAP,
    TIMELINE_ROW_H,
  ]);
  // El gesto se sigue en WINDOW y no con los handlers del div + pointer
  // capture. Con el capture, soltar afuera de la franja —que es lo normal:
  // mientras arrastrás, la franja se mueve con el borde y el mouse le queda
  // lejos— podía dejar el `pointerup` sin llegar, y la línea se quedaba
  // "pegada" al cursor, azul y estirando sin que nadie la agarrara. Con los
  // listeners globales el gesto termina siempre: soltar el botón en cualquier
  // lado cierra, y `pointercancel` también.
  // Ver ADR-2026-09-16-la-manija-del-alto-suelta-siempre.
  const empezarAEstirarLaTimeline = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const bloque = bloqueTimelineRef.current;
      if (!bloque) return;
      e.preventDefault();
      const inicio = {
        pointerId: e.pointerId,
        yInicial: e.clientY,
        altoInicial: bloque.offsetHeight,
      };
      arrastreDeAltoRef.current = inicio;
      setEstirandoElAlto(true);
      // Si el alto lo pone la mano, el chevron vuelve a significar "agrandar":
      // dejarlo en "volver" después de estirar la timeline a mano deja el
      // ícono diciendo lo contrario de lo que haría.
      setPreviewAmpliado(false);
      // El marco no cambia mientras se arrastra, así que el piso se mide una
      // sola vez —al agarrar la línea— y no en cada `pointermove`.
      const piso = pisoDelAltoDeLaTimeline();

      const mover = (ev: PointerEvent) => {
        if (ev.pointerId !== inicio.pointerId) return;
        const b = bloqueTimelineRef.current;
        const columna = b?.parentElement;
        if (!b || !columna) return;
        // Arrastrar HACIA ARRIBA agranda: el borde que se agarra es el de arriba.
        const pedido = inicio.altoInicial + (inicio.yInicial - ev.clientY);
        const maximo = Math.max(
          piso,
          columna.clientHeight - ALTO_MIN_PREVIEW,
        );
        setAltoDeLaTimeline(Math.max(piso, Math.min(maximo, pedido)));
      };
      const soltar = (ev: PointerEvent) => {
        if (ev.pointerId !== inicio.pointerId) return;
        window.removeEventListener("pointermove", mover);
        window.removeEventListener("pointerup", soltar);
        window.removeEventListener("pointercancel", soltar);
        arrastreDeAltoRef.current = null;
        setEstirandoElAlto(false);
      };
      window.addEventListener("pointermove", mover);
      window.addEventListener("pointerup", soltar);
      window.addEventListener("pointercancel", soltar);
    },
    [pisoDelAltoDeLaTimeline],
  );

  // ── Preview ampliado (teléfono) ───────────────────────────────────────────
  // El chevron de abajo del cuadro le da al video todo el alto que se pueda:
  // la timeline se va al PISO —regla más una sola fila, lo mínimo para no
  // perder de vista dónde está el cursor— y el preview se queda con el resto.
  // Volver a tocarlo devuelve el alto que había, sea un número o el automático.
  // Se anima con el mismo `animandoElAlto` que usa Minimizar, para que la
  // tarjeta baje en vez de saltar.
  // Ver ADR-2026-09-17-el-chevron-agranda-el-video-en-el-telefono.
  const [previewAmpliado, setPreviewAmpliado] = useState(false);
  const altoAntesDeAmpliarRef = useRef<number | null>(null);
  const alternarPreviewAmpliado = useCallback(() => {
    if (temporizadorDelColapsoRef.current !== null) {
      window.clearTimeout(temporizadorDelColapsoRef.current);
      temporizadorDelColapsoRef.current = null;
    }
    setAnimandoElAlto(true);
    if (previewAmpliado) {
      setAltoDeLaTimeline(altoAntesDeAmpliarRef.current);
      setPreviewAmpliado(false);
    } else {
      altoAntesDeAmpliarRef.current = altoDeLaTimeline;
      setAltoDeLaTimeline(pisoDelAltoDeLaTimeline());
      setPreviewAmpliado(true);
    }
    temporizadorDelColapsoRef.current = window.setTimeout(() => {
      temporizadorDelColapsoRef.current = null;
      setAnimandoElAlto(false);
    }, DURACION_DEL_COLAPSO);
  }, [previewAmpliado, altoDeLaTimeline, pisoDelAltoDeLaTimeline]);

  // "Ajustar timeline" también ajusta el ALTO: le da al bloque exactamente el
  // que necesitan la regla y las filas que hay, ni más ni menos. Sirve en los
  // dos sentidos —si quedó chica y las pistas de abajo no se ven, crece; si
  // quedó estirada con medio bloque en blanco, se achica— y es la vuelta
  // rápida después de haber arrastrado la manija a ojo.
  //
  // El alto de lo que hay adentro se calcula con las constantes de layout (no
  // midiendo el DOM) porque en modo automático la zona de pistas se recorta a
  // 800px con `overflow: hidden`: medirla ahí devolvería el alto RECORTADO y
  // "ajustar" nunca podría hacerla más alta que el recorte. Lo que sí se mide
  // es el marco (manija + transporte + paddings), que es `bloque - área`.
  const ajustarElAltoDeLaTimeline = useCallback(() => {
    const bloque = bloqueTimelineRef.current;
    const area = timelineAreaRef.current;
    const columna = bloque?.parentElement;
    if (!bloque || !area || !columna) return;
    const marco = bloque.offsetHeight - area.clientHeight;
    const contenido =
      TIMELINE_RULER_H +
      displayRows.length * (TIMELINE_ROW_H + TIMELINE_ROW_GAP);
    // El mismo piso que el arrastre: con una sola fila, "ajustar" y "achicar a
    // fondo" terminan en el mismo alto, que es lo correcto.
    const piso = pisoDelAltoDeLaTimeline();
    const maximo = Math.max(piso, columna.clientHeight - ALTO_MIN_PREVIEW);
    setAltoDeLaTimeline(
      Math.max(piso, Math.min(maximo, marco + contenido)),
    );
  }, [
    displayRows.length,
    pisoDelAltoDeLaTimeline,
    TIMELINE_RULER_H,
    TIMELINE_ROW_H,
    TIMELINE_ROW_GAP,
  ]);

  // El botón hace las dos cosas de una: el zoom que hace entrar todo a lo
  // ancho y el alto que hace entrar todas las capas. Si la timeline estaba
  // minimizada, "ajustar" la trae de vuelta: pedirle que entre todo y dejarla
  // escondida es contradictorio.
  const ajustarLaTimelineEntera = useCallback(() => {
    ajustarTimelineAPantalla();
    if (timelineMinimizada) {
      // Estaba minimizada: la zona de pistas mide 0, así que el alto se
      // calcula en el frame siguiente, ya expandida. Con la transición puesta,
      // para que la tarjeta suba en vez de saltar (mismo movimiento que el
      // botón de Expandir).
      if (temporizadorDelColapsoRef.current !== null) {
        window.clearTimeout(temporizadorDelColapsoRef.current);
        temporizadorDelColapsoRef.current = null;
      }
      setAnimandoElAlto(true);
      setTimelineMinimizada(false);
      requestAnimationFrame(() => {
        ajustarElAltoDeLaTimeline();
        temporizadorDelColapsoRef.current = window.setTimeout(() => {
          temporizadorDelColapsoRef.current = null;
          setAnimandoElAlto(false);
        }, DURACION_DEL_COLAPSO);
      });
      return;
    }
    ajustarElAltoDeLaTimeline();
  }, [
    ajustarTimelineAPantalla,
    ajustarElAltoDeLaTimeline,
    timelineMinimizada,
  ]);

  // Minimizar esconde la regla y las pistas; expandir devuelve el alto que
  // había antes (fijo o automático). Las dos cosas ANIMADAS: la tarjeta baja
  // (o sube) su `height` con transición y las pistas se desvanecen, así el
  // preview crece en el mismo movimiento en vez de saltar.
  //
  // Un alto `auto` no se puede animar, así que el gesto siempre trabaja con
  // números: al minimizar se clava primero el alto ACTUAL medido y recién en
  // el frame siguiente se baja al del marco; al expandir, si lo guardado era
  // "automático", se anima hasta el alto que tendría el contenido y al final
  // se vuelve a `null` (el salto es de un par de píxeles y no se ve).
  // Salir de "Video con IA" borra el "atrás": si el usuario se fue a Archivos y
  // más tarde vuelve a abrir el panel desde el rail, ya no viene de ningún
  // lado y la flecha no tiene a dónde llevarlo.
  useEffect(() => {
    if (activePanel !== "ai-scene" && panelDeVueltaDeEscena !== null)
      setPanelDeVueltaDeEscena(null);
  }, [activePanel, panelDeVueltaDeEscena]);

  const alternarMinimizarTimeline = useCallback(() => {
    const bloque = bloqueTimelineRef.current;
    const area = timelineAreaRef.current;
    if (temporizadorDelColapsoRef.current !== null) {
      window.clearTimeout(temporizadorDelColapsoRef.current);
      temporizadorDelColapsoRef.current = null;
    }

    // Sin DOM para medir (no debería pasar): el toggle seco, sin animación.
    if (!bloque || !area) {
      if (timelineMinimizada) {
        setAltoDeLaTimeline(altoAntesDeMinimizarRef.current);
        setTimelineMinimizada(false);
      } else {
        altoAntesDeMinimizarRef.current = altoDeLaTimeline;
        setAltoDeLaTimeline(null);
        setTimelineMinimizada(true);
      }
      return;
    }

    // El "marco": lo que mide la tarjeta sin la zona de pistas (manija + fila
    // del transporte + paddings). Es el alto al que queda minimizada.
    const marco = bloque.offsetHeight - area.clientHeight;
    const terminarLaAnimacion = (alFinal?: () => void) => {
      temporizadorDelColapsoRef.current = window.setTimeout(() => {
        temporizadorDelColapsoRef.current = null;
        setAnimandoElAlto(false);
        alFinal?.();
      }, DURACION_DEL_COLAPSO);
    };

    if (timelineMinimizada) {
      const guardado = altoAntesDeMinimizarRef.current;
      // Minimizada, la cinta del cursor ES parte de la tarjeta y no de la zona
      // de pistas, así que `bloque - área` la incluye: hay que descontarla o el
      // alto al que se expande se va 18px de más (y el salto se ve al final,
      // cuando el alto vuelve a "automático").
      const marcoSinLaCinta = marco - ALTO_DE_LA_BARRA_DEL_CURSOR;
      const contenido = Math.min(
        800,
        TIMELINE_RULER_H +
          displayRows.length * (TIMELINE_ROW_H + TIMELINE_ROW_GAP),
      );
      setAnimandoElAlto(true);
      setTimelineMinimizada(false);
      setAltoDeLaTimeline(guardado ?? marcoSinLaCinta + contenido);
      terminarLaAnimacion(() => {
        if (guardado === null) setAltoDeLaTimeline(null);
      });
      return;
    }

    altoAntesDeMinimizarRef.current = altoDeLaTimeline;
    setAltoDeLaTimeline(bloque.offsetHeight);
    requestAnimationFrame(() => {
      setAnimandoElAlto(true);
      setTimelineMinimizada(true);
      // `+ ALTO_DE_LA_BARRA_DEL_CURSOR`: minimizada aparece la cinta del
      // cursor, que no existía cuando se midió el marco. Sin reservarle el
      // alto, la cinta se sale por abajo de la tarjeta.
      setAltoDeLaTimeline(marco + ALTO_DE_LA_BARRA_DEL_CURSOR);
      terminarLaAnimacion();
    });
  }, [
    timelineMinimizada,
    altoDeLaTimeline,
    displayRows.length,
    TIMELINE_RULER_H,
    TIMELINE_ROW_H,
    TIMELINE_ROW_GAP,
  ]);

  // ── La cinta del cursor de la timeline minimizada ────────────────────────
  // Con las pistas escondidas no queda dónde agarrar el tiempo: ni regla, ni
  // cursor, ni clips. Esta cinta —azul hasta donde va el video, gris lo que
  // falta— es el único control de ir y venir mientras está minimizada, y
  // escribe el MISMO `currentTime` que el cursor de la timeline.
  const barraDelCursorRef = useRef<HTMLDivElement>(null);
  const tiempoDesdeLaBarra = useCallback((clientX: number) => {
    const el = barraDelCursorRef.current;
    const fin = finDeReproduccionRef.current;
    if (!el || fin <= 0) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return;
    const fraccion = Math.min(
      1,
      Math.max(0, (clientX - r.left) / r.width),
    );
    setCurrentTime(fraccion * fin);
  }, []);
  // El gesto se sigue en WINDOW, igual que la manija del alto
  // (ADR-2026-09-16-la-manija-del-alto-suelta-siempre): soltar afuera de una
  // cinta de 4px es lo normal, y con los handlers del div el `pointerup` se
  // perdía y la barra quedaba pegada al cursor.
  const empezarAArrastrarLaBarra = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // `preventDefault` + `stopPropagation`: la tarjeta de la timeline vive
      // dentro de una columna con `overflow-hidden`, que ES un contenedor de
      // scroll. Cualquier default del navegador sobre la cinta —enfocarla,
      // arrastrar una selección— le pide "traeme esto a la vista" y la columna
      // se corre unos píxeles: la timeline salta y el preview parece achicarse.
      // Por lo mismo la cinta no es focuseable (no lleva `tabIndex`).
      e.preventDefault();
      e.stopPropagation();
      tiempoDesdeLaBarra(e.clientX);
      const mover = (ev: PointerEvent) => tiempoDesdeLaBarra(ev.clientX);
      const soltar = () => {
        window.removeEventListener("pointermove", mover);
        window.removeEventListener("pointerup", soltar);
        window.removeEventListener("pointercancel", soltar);
      };
      window.addEventListener("pointermove", mover);
      window.addEventListener("pointerup", soltar);
      window.addEventListener("pointercancel", soltar);
    },
    [tiempoDesdeLaBarra],
  );

  // ── Reubicar una fila de la timeline ─────────────────────────────────────
  // Todas las filas juegan en la misma pila: un texto puede quedar entre dos
  // videos, el audio arriba de todo. El gesto es uno solo —arrastrar el propio
  // clip y apuntar a un HUECO entre filas—. Hubo además una columna de
  // tiradores a la izquierda, uno por fila; se sacó a pedido del usuario
  // (ADR-2026-09-16-se-van-los-tiradores-de-las-capas).
  const clavesDeLasFilas = useMemo(
    () => displayRows.map((r) => r.key),
    [displayRows],
  );
  const huecoDeFilaPara = useCallback(
    (y: number) => {
      const paso = TIMELINE_ROW_H + TIMELINE_ROW_GAP;
      const topPrimeraFila = TIMELINE_RULER_H + TIMELINE_ROW_GAP;
      const posicion = Math.max(
        0,
        Math.min(displayRows.length, Math.round((y - topPrimeraFila) / paso)),
      );
      return {
        posicion,
        yPx: topPrimeraFila + posicion * paso - TIMELINE_ROW_GAP / 2,
      };
    },
    [displayRows.length],
  );
  // De qué pista es el "+" de una fila y dónde termina (el final de su último
  // clip): es lo que el menú del "+" recuerda para que lo que entre por él
  // caiga a continuación (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista).
  const pistaDelMas = useCallback(
    (row: DisplayRow): { familia: FamiliaDePista; trackIndex: number; fin: number } => {
      const trackIndex =
        (row.trackType === "audio"
          ? row.audioIndex
          : row.trackType === "video"
            ? row.videoIndex
            : row.elementIndex) ?? 0;
      const fin = clipsRef.current
        .filter(row.matchClip)
        .reduce((acc, c) => Math.max(acc, c.startTime + c.duration), 0);
      return { familia: row.trackType, trackIndex, fin };
    },
    [],
  );
  // Las filas como las ve la regla de soltar (`destinoDeSoltar`): familia e
  // índice de capa. La de subtítulos queda sin índice: no recibe nada.
  const filasParaSoltar = useMemo<FilaDeLaTimeline[]>(
    () =>
      displayRows.map((r) => ({
        familia: r.trackType,
        indice:
          r.trackType === "audio"
            ? r.audioIndex
            : r.trackType === "video"
              ? r.videoIndex
              : r.elementIndex,
      })),
    [displayRows],
  );
  const GEOMETRIA_DE_FILAS = {
    rulerH: TIMELINE_RULER_H,
    rowH: TIMELINE_ROW_H,
    gap: TIMELINE_ROW_GAP,
  };
  // Qué familia trae un arrastre que pasa por encima: la marca de nuestras
  // fichas, o el MIME del primer archivo si viene de la computadora (en
  // `dragover` los `files` vienen vacíos, pero `items[i].type` sí se lee).
  const familiaDelArrastre = useCallback(
    (dt: DataTransfer): FamiliaDePista | null => {
      const marcada = familiaDelTransfer(dt.types);
      if (marcada) return marcada;
      if (!arrastraArchivos(dt)) return null;
      for (const it of Array.from(dt.items ?? [])) {
        if (it.kind !== "file") continue;
        const f = familiaDeArchivo({ type: it.type, name: "" });
        if (f) return f;
      }
      return null;
    },
    [],
  );
  const SNAP_THRESHOLD = 0.5; // seconds proximity to trigger snap
  // Radio del imán de las orillas —al estirar y al mover un clip— en píxeles
  // de pantalla (se traduce a segundos con el zoom vivo).
  const SNAP_PX = 12;

  const handleTimelinePointerMove = useCallback(
    // Recibe el evento o, desde el autoscroll, sólo la última posición del puntero.
    (e: { clientX: number; clientY: number }) => {
      if (draggingClipRef.current || isDraggingRef.current)
        anotarPunteroDelArrastre({ x: e.clientX, y: e.clientY });
      if (draggingClipRef.current) {
        const sec = clientXToSeconds(e.clientX);
        const { id, offsetSec, startTimeInicial } = draggingClipRef.current;
        const rawStart = Math.max(0, sec - offsetSec);
        const draggedClip = clips.find((c) => c.id === id);
        if (!draggedClip) return;

        const clampedStart = rawStart;
        const draggedEnd = clampedStart + draggedClip.duration;
        // El gesto apunta a OTRA capa (un hueco nuevo, o una fila distinta de
        // la suya). Cuando eso pasa, el arrastre es un reorden y el tiempo
        // queda clavado donde estaba: llevar un clip a otra capa no tiene por
        // qué correrlo un cuarto de segundo, y volver a alinearlo después era
        // un trabajo que nadie pidió.
        let reordenando = false;

        // Vertical drag: let audio and video clips move between tracks of their
        // own kind (and create a new one beyond the existing rows).
        //
        // Caso especial — clip "solo" en su pista: si el usuario jitterea el
        // mouse cerca del borde, podría oscilar entre la pista actual y una
        // pista *nueva* recién creada → la pista vieja queda vacía, se destruye,
        // `displayRows` se reconstruye y los índices se corren, produciendo un
        // flicker de colores y otra ronda de oscilación. Para evitarlo, cuando
        // `soloOnTrack` está activo, sólo permitimos mover a **pistas que ya
        // existen**; nunca generamos pistas nuevas. Mover a una pista existente
        // distinta es siempre válido — el destino ya está en `displayRows`, así
        // que no hay reshuffle: la pista origen desaparece una vez y listo.
        // ── Reordenar capas: a qué HUECO apunta el arrastre ──────────────
        // No se toca el `trackIndex` mientras se arrastra: se dibuja una línea
        // en el hueco al que apunta y el reorden se aplica al SOLTAR. Moverlo
        // en vivo hacía que las filas se reacomodaran abajo del cursor, que es
        // de donde salían el flicker y las oscilaciones que antes había que
        // tapar con guardas ("¿está solo en su pista?", "¿ya está arriba de
        // todo?"). Con el reorden diferido el problema no existe.
        // Texto, imagen y animación se reordenan igual que audio y video: cada
        // uno tiene su capa, así que también se pueden apilar en otro orden o
        // juntar dos en la misma fila. Los subtítulos quedan afuera
        // (`familiaDePista` devuelve null).
        const tipoDePista = familiaDePista(draggedClip);

        if (tipoDePista && timelineAreaRef.current) {
          const rect = timelineAreaRef.current.getBoundingClientRect();
          // `scrollTop`: con la timeline estirada el área scrollea por dentro,
          // así que la posición contra la pantalla no alcanza — la fila se
          // busca en coordenadas del CONTENIDO.
          const y =
            e.clientY - rect.top + timelineAreaRef.current.scrollTop;
          const paso = TIMELINE_ROW_H + TIMELINE_ROW_GAP;
          const topPrimeraFila = TIMELINE_RULER_H + TIMELINE_ROW_GAP;

          // Todas las filas juegan en la misma pila (un texto puede estar
          // entre dos videos), así que se mira la fila GLOBAL bajo el cursor y
          // no un bloque por tipo.
          const filaFlotante = (y - topPrimeraFila) / paso;
          const indiceDeFila = Math.floor(filaFlotante);
          const dentroDeLaFila = filaFlotante - indiceDeFila;
          const fila =
            indiceDeFila >= 0 && indiceDeFila < displayRows.length
              ? displayRows[indiceDeFila]
              : undefined;
          // Casi toda la fila cuenta como "entrar en esta capa": la misma
          // franja que al soltar desde Archivos (`CENTRO_DESDE`/`CENTRO_HASTA`,
          // ADR-2026-11-03-entrar-en-la-pista-es-casi-toda-la-fila).
          const enElCentro =
            dentroDeLaFila > CENTRO_DESDE && dentroDeLaFila < CENTRO_HASTA;
          const indiceDeLaFila = (r: DisplayRow): number | undefined =>
            tipoDePista === "audio"
              ? r.audioIndex
              : tipoDePista === "video"
                ? r.videoIndex
                : r.elementIndex;
          const filaDeMiTipo =
            fila && fila.trackType === tipoDePista
              ? indiceDeLaFila(fila)
              : undefined;

          if (fila && enElCentro && typeof filaDeMiTipo === "number") {
            // Sobre una fila donde este clip PUEDE vivir: se suma a esa capa
            // (dos clips compartiendo fila es lo que hace falta para
            // superponer material). Si es la suya, no hay reorden: el gesto
            // sigue siendo mover el clip en el tiempo.
            setInsercionDeFila(null);
            reordenando = filaDeMiTipo !== getClipTrackIndex(draggedClip);
            setInsercionDePista({
              clipId: id,
              tipo: tipoDePista,
              clase: "dentro",
              trackIndex: filaDeMiTipo,
              yPx: topPrimeraFila + indiceDeFila * paso,
            });
          } else {
            // Un hueco —o una fila donde este clip no puede vivir, como un
            // texto sobre una pista de audio— es "poné tu capa acá": si el
            // clip está solo en su fila se mueve la fila entera; si comparte,
            // estrena una capa propia en ese hueco. En los dos casos el tiempo
            // queda quieto. Ver ADR-2026-09-15-el-orden-de-la-timeline-manda-en-el-preview.
            reordenando = true;
            setInsercionDePista(null);
            setInsercionDeFila({
              clave: `${tipoDePista}-${getClipTrackIndex(draggedClip)}`,
              ...huecoDeFilaPara(y),
            });
          }
        } else {
          setInsercionDePista(null);
          setInsercionDeFila(null);
        }

        // Reordenando no hay nada que imantar: el tiempo no se mueve.
        if (reordenando) {
          setSnapTarget(null);
          setSnapLine(null);
          setClips((prev) =>
            prev.map((c) =>
              c.id === id && c.startTime !== startTimeInicial
                ? { ...c, startTime: startTimeInicial }
                : c,
            ),
          );
          return;
        }

        // Imán al MOVER — mismo criterio que al estirar
        // (ADR-2026-09-15-el-iman-de-las-orillas-mira-todas-las-capas): los
        // candidatos son las dos orillas de TODOS los otros clips, de
        // cualquier pista, más el 0. Antes sólo miraba a los hermanos (misma
        // pista) y sólo la orilla opuesta, así que arrastrar un texto para
        // que arrancara con la escena de abajo quedaba siempre a ojo.
        const snapPoints: number[] = [0];
        for (const c of clips) {
          if (c.id === id) continue;
          snapPoints.push(c.startTime, c.startTime + c.duration);
        }
        // La tolerancia va en píxeles de pantalla, no en segundos: con zoom
        // alto medio segundo son cientos de píxeles.
        const snapTol = Math.min(
          SNAP_THRESHOLD,
          SNAP_PX / (BASE_PPS * zoomRef.current),
        );

        let bestSnap: number | null = null;
        let bestDist = snapTol;
        // Dónde cae la guía vertical: el punto con el que se alineó, que no
        // es el `startTime` resultante cuando lo que pegó fue el FINAL.
        let guideAt: number | null = null;

        for (const p of snapPoints) {
          // El comienzo del que se arrastra contra la orilla del otro.
          const dStart = Math.abs(clampedStart - p);
          if (dStart < bestDist) {
            bestDist = dStart;
            bestSnap = p;
            guideAt = p;
          }
          // El final del que se arrastra contra la orilla del otro.
          const dEnd = Math.abs(draggedEnd - p);
          if (dEnd < bestDist) {
            bestDist = dEnd;
            bestSnap = p - draggedClip.duration;
            guideAt = p;
          }
        }

        if (bestSnap !== null && bestSnap >= 0) {
          setSnapTarget({ clipId: id, startTime: bestSnap });
          setSnapLine(guideAt);
        } else {
          setSnapTarget(null);
          setSnapLine(null);
        }

        setClips((prev) =>
          prev.map((c) => (c.id === id ? { ...c, startTime: clampedStart } : c)),
        );
        return;
      }
      if (!isDraggingRef.current) return;
      setCurrentTime(clientXToSeek(e.clientX));
    },
    [
      clientXToSeconds,
      clientXToSeek,
      duration,
      clips,
      displayRows,
      anotarPunteroDelArrastre,
    ],
  );
  // El autoscroll vuelve a aplicar el arrastre con la última posición del
  // puntero cada vez que corre la cinta; lee el handler por ref porque el bucle
  // arranca una vez y el handler cambia con cada render.
  const moverElArrastreRef = useRef(handleTimelinePointerMove);
  useEffect(() => {
    moverElArrastreRef.current = handleTimelinePointerMove;
  }, [handleTimelinePointerMove]);

  const handleTimelinePointerUp = useCallback((cancelado = false) => {
    detenerAutoscroll();
    // Tocar un clip SIN arrastrarlo lo selecciona y lleva el cursor al punto
    // tocado, así "Dividir" queda listo ahí mismo (Nacho, 2/11). Arrastrarlo
    // sigue sin mover el tiempo (ADR-2026-09-15-el-cursor-solo-se-mueve-desde-la-regla).
    // En el teléfono no: ahí el cursor está fijo y la cinta es el tiempo.
    const toque = inicioDelArrastreRef.current;
    const fueUnToqueEnUnClip =
      !cancelado &&
      !enElTelefonoRef.current &&
      !!draggingClipRef.current &&
      !arrastreMovidoRef.current &&
      toque !== null;
    // Apply snap if active
    if (draggingClipRef.current && snapTarget) {
      const { clipId, startTime } = snapTarget;
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, startTime } : c)),
      );
    }

    // Reordenar las capas, si el arrastre terminó apuntando a un hueco. Acá y
    // no en el move: recién al soltar se sabe dónde quiso dejarlo el usuario, y
    // así las filas no se reacomodan bajo el cursor mientras arrastra.
    if (insercionDePista && insercionDePista.clase === "dentro") {
      // Meterlo en una capa que ya existe: no hay reorden, sólo cambia de
      // pista. Los dos clips pasan a compartir fila.
      const { clipId, trackIndex, tipo } = insercionDePista;
      setClips((prev) =>
        prev.map((c) => {
          if (c.id !== clipId || getClipTrackIndex(c) === trackIndex) return c;
          const actualizado: TimelineClip = { ...c, trackIndex };
          if (tipo === "audio") actualizado.color = getAudioTrackColor(trackIndex);
          return actualizado;
        }),
      );
      setInsercionDePista(null);
    }

    // El arrastre terminó apuntando a un HUECO: "poné tu capa acá". Si el clip
    // está solo en su fila, se mueve esa fila; si la comparte, estrena una capa
    // propia en ese hueco. Los `trackIndex` no se renumeran: el orden visual lo
    // decide `ordenDeFilas`, así que el índice es sólo la identidad de la capa.
    if (insercionDeFila && draggingClipRef.current) {
      const { clave, posicion } = insercionDeFila;
      const clipArrastrado = clips.find(
        (c) => c.id === draggingClipRef.current?.id,
      );
      const familia = clipArrastrado ? familiaDePista(clipArrastrado) : null;
      if (clipArrastrado && familia) {
        const compañeros = clips.filter(
          (c) =>
            c.id !== clipArrastrado.id &&
            esDeLaFamilia(c, familia) &&
            getClipTrackIndex(c) === getClipTrackIndex(clipArrastrado),
        );
        if (compañeros.length === 0) {
          // Está solo: mover la fila alcanza y el clip no se toca.
          setOrdenDeFilas(moverFila(clavesDeLasFilas, clave, posicion));
        } else {
          const indiceNuevo = capaNuevaPara(clips, clipArrastrado.type);
          const claveNueva = `${familia}-${indiceNuevo}`;
          setClips((prev) =>
            prev.map((c) => {
              if (c.id !== clipArrastrado.id) return c;
              const actualizado: TimelineClip = {
                ...c,
                trackIndex: indiceNuevo,
              };
              if (c.type === "audio")
                actualizado.color = getAudioTrackColor(indiceNuevo);
              return actualizado;
            }),
          );
          setOrdenDeFilas(
            insertarFila(clavesDeLasFilas, claveNueva, posicion),
          );
        }
      }
      setInsercionDeFila(null);
    }

    setSnapTarget(null);
    setSnapLine(null);
    isDraggingRef.current = false;
    draggingClipRef.current = null;
    setIsDraggingPlayhead(false);
    if (fueUnToqueEnUnClip && toque) setCurrentTime(clientXToSeek(toque.x));
    // Liberamos el pin de la pista origen: si quedó vacía por el drag,
    // `displayRows` la colapsará ahora (ya sin flicker porque el drag
    // terminó).
    setPinnedOriginRow(null);
  }, [
    snapTarget,
    insercionDePista,
    insercionDeFila,
    clavesDeLasFilas,
    clips,
    detenerAutoscroll,
    clientXToSeek,
  ]);

  // ── Edit AI clip ──────────────────────────────────────────────────────────
  const editAiClip = useCallback(
    (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "ai-generated") return;
      setEditingClipId(clipId);
      setScenePrompt(clip.aiPrompt || "");
      setSceneMejora(null);
      setSceneRefMode(clip.aiRefMode || "none");
      setSceneMode(clip.aiSceneMode || "general");
      setScenePersonType("static");
      setSceneDialogue(clip.aiDialogue || "");
      setSceneDialogueInputMode(clip.aiDialogueInputMode || "text");
      setSceneDialogueAudio(
        clip.aiDialogueAudioUrl &&
          clip.aiDialogueAudioStoragePath &&
          clip.aiDialogueAudioAssetId
          ? {
              url: clip.aiDialogueAudioUrl,
              storagePath: clip.aiDialogueAudioStoragePath,
              durationSec: clip.aiDialogueAudioDurationSec ?? 0,
              assetId: clip.aiDialogueAudioAssetId,
            }
          : null,
      );
      setAudioAiVoiceId(clip.aiVoiceId || null);
      setAudioAiVoiceName(clip.aiVoiceName || "");
      setSceneRefAssetIds(clip.aiRefAssetIds || []);
      setSceneFirstFrameAssetId(clip.aiFirstFrameAssetId || null);
      setSceneLastFrameAssetId(clip.aiLastFrameAssetId || null);
      // `clip.duration` es la duración REAL medida al archivo generado
      // (`probe.duration`), así que trae decimales: 4.0000012. Eso está bien
      // para el timeline, pero el control de duración es de segundos enteros —
      // los sliders solo producen enteros y `DURATION_OPTIONS.indexOf()` no
      // matchea un float (devuelve -1 y la barra se dibuja en el extremo).
      // Redondeamos acá, en el único punto por donde entra un decimal.
      // El piso es el del proveedor (4s), no 1: el clip se pudo recortar a mano
      // en el timeline, y esa duración recortada volvía al panel y salía en el
      // request → 400 "Duration must be between 4 and 15 seconds" en cada
      // Reintentar (incidente 2026-09-10).
      setSceneDuration(clampDuracionDeEscena(clip.duration));
      setSceneSpeechSpeed(clip.aiSpeechSpeed ?? 1.1);
      // Legacy: clips generados cuando existía "rapido" caen a Estándar.
      setSceneQuality(
        clip.aiQuality === "profesional" ? "profesional" : "estandar",
      );
      setSceneResolution(clip.aiResolution || "720p");
      setSelectedClipId(clipId);
      setActivePanel("ai-scene");

      // Resolve signed URLs for preview in background
      const idsToResolve: string[] = [];
      if (clip.aiRefAssetIds) idsToResolve.push(...clip.aiRefAssetIds);
      if (clip.aiFirstFrameAssetId) idsToResolve.push(clip.aiFirstFrameAssetId);
      if (clip.aiLastFrameAssetId) idsToResolve.push(clip.aiLastFrameAssetId);
      if (idsToResolve.length > 0) {
        (async () => {
          const assets = await fetchAssetsByIds(idsToResolve);
          const urls: Record<string, string> = {};
          await Promise.all(
            assets.map(async (a) => {
              const u = await getAssetSignedUrl(a.storage_path);
              if (u) urls[a.id] = u;
            }),
          );
          setAssetPreviewUrls((prev) => ({ ...prev, ...urls }));
        })();
      }
    },
    [clips],
  );

  // ── Edit AI audio clip ────────────────────────────────────────────────────
  const editAiAudioClip = useCallback(
    (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "audio" || !clip.aiPrompt) return;
      setEditingClipId(clipId);
      setAudioAiType(clip.aiAudioType || "voice");
      setAudioAiPrompt(clip.aiPrompt || "");
      setAudioAiDuration(clip.duration);
      setAudioAiVoiceId(clip.aiVoiceId || null);
      setAudioAiVoiceName(clip.aiVoiceName || "");
      setAudioAiInstrumentalOnly(!!clip.aiInstrumentalOnly);
      setAudioAiSpeechSpeed(clip.aiSpeechSpeed ?? 1.1);
      setAudioAiDialogueInputMode(clip.aiDialogueInputMode || "text");
      setAudioAiDialogueAudio(
        clip.aiDialogueAudioUrl &&
          clip.aiDialogueAudioStoragePath &&
          clip.aiDialogueAudioAssetId
          ? {
              url: clip.aiDialogueAudioUrl,
              storagePath: clip.aiDialogueAudioStoragePath,
              durationSec: clip.aiDialogueAudioDurationSec ?? 0,
              assetId: clip.aiDialogueAudioAssetId,
            }
          : null,
      );
      setSelectedClipId(clipId);
      setActivePanel("ai-audio");
    },
    [clips],
  );

  // ── Generate an existing AI audio clip (draft or error) ───────────────────
  const generateAiAudioClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || clip.type !== "audio") return;
      // Guard explícita: si falta info crítica, marcamos el clip con un
      // error visible en vez de hacer return silencioso. Así el usuario ve
      // por qué su Reintentar "no hizo nada".
      if (!clip.aiPrompt || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError: !clip.aiPrompt
                    ? // Recargar no arregla esto: lo que falta es la descripción.
                      "Falta la descripción del audio. Abrilo (doble click), escribí qué querés que suene y generalo de nuevo."
                    : "No se pudo iniciar la generación: falta información del proyecto. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }
      if (!assertCredits(creditsForAiClip(clip))) return;
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiStatus: "generating" as AiClipStatus,
                aiError: undefined,
                aiGenerationStartedAt: Date.now(),
              }
            : c,
        ),
      );
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        const body: Record<string, any> = {
          user_id: session.user.id,
          ref_mode:
            clip.aiAudioType === "music"
              ? "musica"
              : clip.aiAudioType === "sfx"
                ? "efecto"
                : "voz",
          voz_id: clip.aiVoiceId || "",
          project_id: projectId,
          // Trackeo (ADR-585): audio generado desde el Estudio.
          modo_utilizado: "estudio_audio",
          text: clip.aiPrompt,
          instrumental_only:
            clip.aiAudioType === "music" ? !!clip.aiInstrumentalOnly : false,
          ...(clip.aiAudioType === "music"
            ? { duracion_seg: acotarDuracionDeMusica(clip.duration) }
            : {}),
          ...(clip.aiAudioType === "sfx"
            ? { duracion_seg: acotarDuracionDeEfecto(clip.duration) }
            : {}),
          // Lo de la voz sólo viaja con una voz (antes: "todo lo que no es música").
          ...(clip.aiAudioType !== "music" && clip.aiAudioType !== "sfx"
            ? { velocidad_voz: clip.aiSpeechSpeed ?? 1.1 }
            : {}),
          // Voice mode audio input: user uploaded/recorded their own audio
          // and the workflow runs ElevenLabs speech-to-speech instead of TTS.
          ...(clip.aiAudioType !== "music" &&
          clip.aiAudioType !== "sfx" &&
          clip.aiDialogueInputMode === "audio" &&
          clip.aiDialogueAudioUrl
            ? {
                dialogo_input_mode: "audio",
                audio_url: clip.aiDialogueAudioUrl,
                audio_duration_sec: clip.aiDialogueAudioDurationSec ?? null,
              }
            : clip.aiAudioType !== "music" && clip.aiAudioType !== "sfx"
              ? { dialogo_input_mode: "texto" }
              : {}),
        };
        // Helper con flag atlas/n8n (ADR-583) — timeout de 5 min adentro
        // para que el clip no se quede en "generating" eterno; sin eso el
        // botón Reintentar nunca aparece porque el catch nunca corre.
        const { path: audioPath } = await generateAudioCreator(body);

        // Credits were just deducted by the workflow — notify the sidebar.
        window.dispatchEvent(new Event("atlas:credits-updated"));

        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(audioPath, 3600);

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "ready" as AiClipStatus,
                  storagePath: audioPath,
                  src: urlData?.signedUrl || undefined,
                }
              : c,
          ),
        );

        if (urlData?.signedUrl) {
          const probe = new Audio();
          probe.preload = "metadata";
          probe.src = urlData.signedUrl;
          probe.addEventListener("loadedmetadata", () => {
            const dur = probe.duration;
            if (isFinite(dur) && dur > 0) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? { ...c, duration: dur, sourceDuration: dur }
                    : c,
                ),
              );
            }
          });
        }

        if (companyId) {
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: audioPath,
              type: "audio",
              name: (
                clip.aiPrompt ||
                (clip.aiAudioType === "music"
                  ? "Música IA"
                  : clip.aiAudioType === "sfx"
                    ? "Efecto IA"
                    : "Voz IA")
              ).slice(0, 80),
              mimeType: "audio/mpeg",
            });
            if (asset) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, assetId: asset.id } : c,
                ),
              );
            }
          } catch (e) {
            console.warn("[VideoEditor] Failed to register AI audio asset:", e);
            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId
                  ? {
                      ...c,
                      aiAssetWarning:
                        "El audio se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                    }
                  : c,
              ),
            );
          }
        }
      } catch (err) {
        console.error("[VideoEditor] AI audio regeneration failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La generación tardó demasiado (más de 5 min). Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al generar el audio.";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, aiStatus: "error" as AiClipStatus, aiError: msg }
              : c,
          ),
        );
      }
    },
    [clips, projectId, companyId, assertCredits],
  );

  // ── Clip drag start ────────────────────────────────────────────────────────
  const lastClickRef = useRef<{ clipId: string; time: number } | null>(null);

  const handleClipPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, clip: TimelineClip) => {
      e.stopPropagation();
      // Seleccionar un clip NO mueve el cursor: el click puede ser el primer
      // paso de un arrastre, y mover el tiempo de reproducción cada vez que
      // agarrás una capa es un efecto que nadie pidió.

      // Doble click / doble toque (con el puntero capturado no llega el
      // `dblclick` nativo). Devuelve `true` si ya abrió el editor del clip, o
      // sea que el gesto terminó acá. En escritorio se evalúa al apretar; en el
      // teléfono, recién cuando se confirma que el toque fue un TAP.
      const esDobleToque = (): boolean => {
        const now = Date.now();
        if (
          lastClickRef.current &&
          lastClickRef.current.clipId === clip.id &&
          now - lastClickRef.current.time < 400
        ) {
          lastClickRef.current = null;
          if (clip.type === "ai-generated") {
            editAiClip(clip.id);
            return true;
          }
          if (clip.type === "audio" && clip.aiPrompt) {
            editAiAudioClip(clip.id);
            return true;
          }
        }
        lastClickRef.current = { clipId: clip.id, time: now };
        return false;
      };

      const xInicial = e.clientX;
      const yInicial = e.clientY;
      const pointerId = e.pointerId;

      const armarElArrastre = () => {
        const sec = clientXToSeconds(xInicial);
        draggingClipRef.current = {
          id: clip.id,
          offsetSec: sec - clip.startTime,
          startTimeInicial: clip.startTime,
        };
        isDraggingRef.current = false; // not a playhead drag

        // Pin de la pista de origen: sólo aplica a clips verticalmente
        // draggables (audio / video / ai-generated). Si durante el drag la
        // pista origen queda vacía, este pin evita que `displayRows` la
        // colapse hasta que el usuario suelte el mouse.
        const familiaDelClip = familiaDePista(clip);
        if (familiaDelClip) {
          setPinnedOriginRow({
            trackType: familiaDelClip,
            index: getClipTrackIndex(clip),
          });
        }
        // Capture on the timeline area so we get move/up even outside the clip
        if (timelineAreaRef.current) {
          timelineAreaRef.current.setPointerCapture(pointerId);
        }
        // Llevar el clip hasta el borde corre la cinta
        // (ADR-2026-11-02-la-timeline-sigue-al-cursor).
        empezarAutoscroll({ x: xInicial, y: yInicial }, seguirElArrastre);
      };

      if (!enElTelefonoRef.current) {
        setSelectedClipId(clip.id);
        // Tocar un clip con una edición esperando abre "Editar clip". El efecto
        // de selección ya lo hace, pero sólo cuando la selección CAMBIA: si la
        // edición terminó con el clip ya seleccionado, volver a tocarlo no
        // hacía nada (ADR-2026-09-28-tocar-un-clip-con-edicion-lista-abre-la-edicion).
        const edicion = sesionDeEdicion(clip.id);
        if (edicion && !edicion.enCurso && (edicion.lista || edicion.error)) {
          setOmniEditClipId(clip.id);
          setActivePanel("edit-video-ia");
        }
        if (esDobleToque()) return;
        armarElArrastre();
        return;
      }

      /* ── En el teléfono, el dedo sobre la cinta es SIEMPRE la cinta ──────
         Dos gestos y nada más:

         · **arrastrar** (desde donde caiga el dedo, clips incluidos) → mueve la
           TIMELINE, o sea busca el momento;
         · **tocar** (apoyar y levantar sin moverse más de 8px) → selecciona el
           clip.

         Mantener apretado NO hace nada distinto: se sigue pudiendo arrastrar la
         cinta. Hubo una versión con long-press de 320ms que armaba el arrastre
         DEL CLIP (como CapCut) y se sacó a pedido del usuario: al mantener el
         dedo y arrastrar, la timeline quedaba clavada —el `setPointerCapture`
         del arrastre le saca el scroll al navegador— justo cuando lo que se
         quería era seguir buscando el momento.

         Por eso acá no se captura el puntero en ningún caso: el scroll nativo
         es el que manda. La selección se decide al LEVANTAR el dedo (si se
         decidiera al apoyarlo, cualquier arrastre elegiría la capa de abajo), y
         el doble toque se evalúa ahí mismo: en el `pointerdown` dos scrolls
         seguidos sobre el mismo clip abrían su generador.

         Mover un clip de lugar quedó sin gesto en el teléfono; ver el pendiente
         del ADR-2026-09-17-en-el-telefono-arrastrar-es-siempre-la-cinta. */
      // Apoyar el dedo PAUSA, igual que tocar la cinta en cualquier otro lado
      // (`handleTimelinePointerDown`). No es un lujo: mientras se reproduce, el
      // efecto que sincroniza el tiempo con el scroll le reescribe el
      // `scrollLeft` a la cinta en cada frame, así que arrastrar sin pausar no
      // movía nada — la cinta volvía sola. Y sobre un clip ese handler no
      // corre, porque el clip corta la propagación.
      if (isPlayingRef.current) setIsPlaying(false);

      let seMovio = false;
      let terminado = false;
      const soltarEscuchas = () => {
        window.removeEventListener("pointermove", alMover);
        window.removeEventListener("pointerup", alSoltar);
        window.removeEventListener("pointercancel", alCancelar);
      };
      const alMover = (ev: PointerEvent) => {
        if (Math.hypot(ev.clientX - xInicial, ev.clientY - yInicial) > 8)
          seMovio = true;
      };
      const alCancelar = () => {
        if (terminado) return;
        terminado = true;
        soltarEscuchas();
      };
      const alSoltar = () => {
        if (terminado) return;
        terminado = true;
        soltarEscuchas();
        // Quieto de punta a punta: fue un toque, no un arrastre de la cinta.
        if (!seMovio) {
          setSelectedClipId(clip.id);
          esDobleToque();
        }
      };
      window.addEventListener("pointermove", alMover, { passive: true });
      window.addEventListener("pointerup", alSoltar);
      window.addEventListener("pointercancel", alCancelar);
    },
    [
      clientXToSeconds,
      editAiClip,
      editAiAudioClip,
      empezarAutoscroll,
      seguirElArrastre,
    ],
  );

  // ── Load project library assets when Files panel opens / tab changes ───
  // The unified Files panel hosts three tabs (video, audio, image). For
  // video and audio we reuse `libraryAssets` (single bucket) and refetch
  // when the user switches; for image we keep a separate `imageLibraryAssets`
  // because the image tab also folds in AI-generated images on top.
  useEffect(() => {
    // El selector de imagen de una escena mira las MISMAS imágenes del proyecto
    // en su pestaña Subidas, y puede abrirse sin haber pasado nunca por
    // Archivos: sin esto la pestaña arranca vacía hasta que el usuario abre el
    // panel. Cuenta como "media" (un frame es una imagen, nunca un audio).
    const paraElSelector = !!selectorDeImagen;
    if (activePanel !== "files" && !paraElSelector) return;
    if (!projectId) return;
    const pestanaDeDatos = paraElSelector ? "media" : filesTab;
    if (pestanaDeDatos !== "media" && pestanaDeDatos !== "audio") return;
    let cancelled = false;

    // Resuelve signed URLs (en background) para los assets sin preview.
    const resolvePreviews = async (assets: ProjectAsset[]) => {
      const missing = assets.filter((a) => !assetPreviewUrls[a.id]);
      if (missing.length === 0) return;
      const resolved = await Promise.all(
        missing.map(async (a) => {
          try {
            const url = await getAssetSignedUrl(a.storage_path);
            return url ? ([a.id, url] as const) : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const updates: Record<string, string> = {};
      for (const r of resolved) if (r) updates[r[0]] = r[1];
      if (Object.keys(updates).length > 0)
        setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
    };

    if (pestanaDeDatos === "media") {
      // Multimedia: imágenes (bucket propio) + videos (libraryAssets), juntos.
      setImageLibraryLoading(true);
      setLibraryLoading(true);
      (async () => {
        try {
          const [imgs, vids] = await Promise.all([
            fetchProjectAssetsByType(projectId, "image"),
            fetchProjectAssetsByType(projectId, "video"),
          ]);
          if (cancelled) return;
          setImageLibraryAssets(imgs);
          setLibraryAssets(vids);
          await resolvePreviews([...imgs, ...vids]);
        } finally {
          if (!cancelled) {
            setImageLibraryLoading(false);
            setLibraryLoading(false);
          }
          filesLoadedOnceRef.current = true;
        }
      })();
    } else {
      // Audio (aparte).
      setLibraryLoading(true);
      (async () => {
        try {
          const assets = await fetchProjectAssetsByType(projectId, "audio");
          if (cancelled) return;
          setLibraryAssets(assets);
          await resolvePreviews(assets);
        } finally {
          if (!cancelled) setLibraryLoading(false);
          filesLoadedOnceRef.current = true;
        }
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [activePanel, projectId, filesTab, selectorDeImagen]);

  // ── Add an existing project asset (video or audio) to the timeline ──────
  // ── Dónde cae un clip nuevo ──────────────────────────────────────────────
  // Todo lo que se suma a la timeline desde afuera (una ficha de Archivos, un
  // archivo soltado, una animación guardada, una escena o un audio generados)
  // pasa por acá para decidir su capa y su inicio, en este orden:
  //   1. con `destino` —lo que se soltó arrastrando— cae DONDE se soltó:
  //      `dentro` de una capa que ya existe, pegado al final del clip que
  //      hubiera ahí, o en un `hueco` entre filas, que estrena una capa ahí
  //      (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta);
  //   2. si vino del "+" de una pista, a continuación de esa pista
  //      (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista);
  //   3. si no, en el cursor de la timeline (o el segundo que mande quien
  //      llama), en la capa de su familia que esté libre ahí; si ninguna, una
  //      nueva (ADR-2026-09-27-lo-que-entra-con-un-clic-sigue-al-cursor).
  const ubicarClipNuevo = useCallback(
    (
      tipo: ClipType,
      ubicacion?: UbicacionDeClipNuevo,
    ): { trackIndex: number; startTime: number } => {
      const vivos = clipsRef.current;
      const familia: FamiliaDePista =
        tipo === "audio"
          ? "audio"
          : tipo === "video" || tipo === "ai-generated"
            ? "video"
            : "elements";
      // Sin destino ni segundo explícitos (un clic, no un arrastre ni el
      // agente pidiendo un segundo) y con un "+" de pista abierto: a
      // continuación del último clip de esa pista. Misma familia ⇒ en esa
      // misma capa; otra familia (un audio desde el "+" de un video) ⇒ a
      // continuación en el tiempo, en una capa propia. Se consume acá: el
      // segundo clip que entre por el mismo panel ya no lo sigue
      // (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista).
      const origen = origenDelMasRef.current;
      if (!ubicacion?.destino && ubicacion?.startTime === undefined && origen) {
        origenDelMasRef.current = null;
        ubicacion = {
          ...ubicacion,
          startTime: origen.startTime,
          destino:
            origen.familia === familia
              ? { clase: "dentro", trackIndex: origen.trackIndex, yPx: 0 }
              : undefined,
        };
      }
      // Sin segundo explícito, el CURSOR de la timeline: es donde el usuario
      // está mirando (ADR-2026-09-27-lo-que-entra-con-un-clic-sigue-al-cursor).
      const startTime = Math.max(
        0,
        ubicacion?.startTime ?? currentTimeRef.current,
      );
      const destino = ubicacion?.destino;
      if (destino?.clase === "dentro") {
        const enLaCapa = vivos.filter(
          (c) =>
            esDeLaFamilia(c, familia) &&
            getClipTrackIndex(c) === destino.trackIndex,
        );
        return {
          trackIndex: destino.trackIndex,
          startTime: pegarAlFinal({
            clips: enLaCapa,
            inicio: startTime,
            duracion: ubicacion?.duracion,
          }),
        };
      }
      if (!destino) {
        // Un video o un audio TOCADO (sin segundo pedido): a su pista
        // principal, en el cursor si entra y si no en el primer hueco después.
        // Antes, con un clip debajo del cursor, abría una capa nueva ARRIBA que
        // tapaba al de abajo (ADR-2026-11-02-lo-tocado-va-a-la-pista-principal).
        // Con un segundo explícito (el agente, un archivo soltado) se respeta
        // ese segundo, como siempre.
        if (familia !== "elements" && ubicacion?.startTime === undefined) {
          const principal = lugarEnLaPistaPrincipal({
            clips: vivos
              .filter((c) => esDeLaFamilia(c, familia))
              .map((c) => ({
                id: c.id,
                trackIndex: getClipTrackIndex(c),
                startTime: c.startTime,
                duration: c.duration,
              })),
            t: startTime,
            duracion: ubicacion?.duracion,
          });
          if (principal) return principal;
        }
        // Un clic (o un archivo soltado sin saber de qué tipo era): la capa
        // de su familia que esté libre en ese segundo, con más lugar por
        // delante; si todas tienen un clip ahí, una nueva.
        const libre = capaLibreEn({
          clips: vivos
            .filter((c) => esDeLaFamilia(c, familia))
            .map((c) => ({
              id: c.id,
              trackIndex: getClipTrackIndex(c),
              startTime: c.startTime,
              duration: c.duration,
            })),
          t: startTime,
          duracion: ubicacion?.duracion,
        });
        if (libre !== null) return { trackIndex: libre, startTime };
      }
      const trackIndex = capaNuevaPara(vivos, tipo);
      if (destino?.clase === "hueco") {
        // Updater: varios archivos soltados de una pasan por acá antes del
        // render, y cada uno tiene que sumar su fila a la del anterior.
        setOrdenDeFilas((prev) =>
          insertarFila(
            clavesDeLasFilas.every((k) => prev.includes(k))
              ? prev
              : clavesDeLasFilas,
            `${familia}-${trackIndex}`,
            destino.posicion,
          ),
        );
      }
      return { trackIndex, startTime };
    },
    [clavesDeLasFilas],
  );
  // El "+" recordado vale sólo para el panel que abrió: si el usuario se va a
  // otro (o lo cierra) sin sumar nada, lo siguiente vuelve a lo de siempre.
  useEffect(() => {
    const origen = origenDelMasRef.current;
    if (origen && activePanel !== origen.panel) origenDelMasRef.current = null;
  }, [activePanel]);

  const addAssetToTimeline = useCallback(
    async (asset: ProjectAsset, opts?: UbicacionDeClipNuevo) => {
      if (asset.type !== "video" && asset.type !== "audio") return;
      const clipType: ClipType = asset.type;
      const track = getTrackForClipType(clipType);
      const clipId = generateId();
      // Resolve signed URL (use cached one if available)
      let src = opts?.src ?? assetPreviewUrls[asset.id];
      if (!src) {
        const url = await getAssetSignedUrl(asset.storage_path);
        if (url) {
          src = url;
          setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: url }));
        }
      }
      const baseName = asset.name || `Asset ${asset.id.slice(0, 6)}`;
      // La duración que ya se sabe del asset: con ella se ubica Y con ella nace
      // el clip, así el siguiente de un lote no se ubica contra 5 s de
      // placeholder. La medición de abajo la confirma igual.
      const duracionConocida =
        asset.duration_seconds && asset.duration_seconds > 0
          ? asset.duration_seconds
          : undefined;
      const { startTime, trackIndex } = ubicarClipNuevo(clipType, {
        ...opts,
        duracion: duracionConocida,
      });
      const newClip: TimelineClip = {
        id: clipId,
        type: clipType,
        name: baseName.length > 20 ? baseName.slice(0, 17) + "..." : baseName,
        startTime,
        trackIndex,
        duration: duracionConocida ?? 5,
        color: track.color,
        src,
        volume: 1,
        assetId: asset.id,
        storagePath: asset.storage_path,
      };
      setClips((prev) => [...prev, newClip]);
      // Al toque, no en el próximo render: el siguiente de un lote se ubica
      // mirando éste.
      clipsRef.current = [...clipsRef.current, newClip];
      setSelectedClipId(newClip.id);
      // Que se vea dónde cayó: puede haber ido al final de la pista, fuera de
      // la vista (ADR-2026-11-02-lo-tocado-va-a-la-pista-principal).
      clipAEnfocarRef.current = newClip.id;
      // Probe real duration so the clip length matches the source
      if (src) {
        const el = document.createElement(
          clipType === "video" ? "video" : "audio",
        );
        el.preload = "metadata";
        el.onloadedmetadata = () => {
          const d = el.duration;
          if (isFinite(d) && d > 0) {
            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId ? { ...c, duration: d, sourceDuration: d } : c,
              ),
            );
          }
        };
        (el as HTMLMediaElement).src = src;
      }
    },
    [assetPreviewUrls, ubicarClipNuevo],
  );

  // Insert a logo (or any image URL) as a centered, movable overlay on the
  // Elements track — same mechanism the Elements panel uses for templates,
  // via the `image-reveal-v1` template but with text/background stripped so
  // only the logo shows. Safe only for permanent URLs (e.g. the brand's public
  // logo_url); private signed URLs would expire and aren't re-signed on reload.
  // El logo entra como CAPA DE IMAGEN, no como plantilla animada. Antes se
  // insertaba con el template `image-reveal-v1` pero apagado (`reveal: "none"`,
  // `kenBurns: false`): era una imagen quieta adentro de un motor de animación,
  // y por eso heredaba el marco de las animaciones —que se mide del DOM— en vez
  // del encuadre de los videos. Como capa de imagen tiene lo mismo que un clip
  // de video: escalar anclado al vértice opuesto, recortar de los lados, girar,
  // mover con imán y opacidad, y sale igual en el MP4.
  // Ver ADR-2026-09-15-el-logo-es-una-capa-de-imagen.
  const insertLogoOverlay = useCallback(
    (imageUrl: string, name: string) => {
      const elementsTrack = TRACK_CONFIG.find((t) => t.type === "elements")!;
      // La duración por defecto sale del template que se usaba antes, para que
      // un logo dure lo mismo que venía durando.
      const tpl = Object.values(ANIMATION_TEMPLATES).find(
        (t) => t.id === "image-reveal-v1",
      );
      const clipId = generateId();
      const { startTime, trackIndex } = ubicarClipNuevo("image", {
        duracion: tpl?.defaultDurationSec ?? 5,
      });
      const newClip: TimelineClip = {
        id: clipId,
        type: "image",
        name,
        startTime,
        trackIndex,
        duration: tpl?.defaultDurationSec ?? 5,
        color: elementsTrack.color,
        src: imageUrl,
        // La misma URL, en el campo que SÍ sobrevive al guardado.
        imageUrl,
        // Mismo tamaño de entrada que tenía como plantilla: un logo a pantalla
        // completa nunca es lo que se busca.
        encuadre: { scale: 0.6 },
      };
      setClips((prev) => [...prev, newClip]);
      setSelectedClipId(clipId);
    },
    [ubicarClipNuevo],
  );

  // ── De una imagen del proyecto a la timeline ─────────────────────────────
  // La usan las carpetas de Marca y los tiles de Archivos: meten la imagen
  // como CAPA DE IMAGEN, igual que el logo, pero atada al asset del proyecto: el clip guarda `assetId` y
  // `storagePath`, así al reabrir el proyecto la URL se vuelve a firmar. Con
  // la URL firmada sola (lo que hace `insertLogoOverlay`) el clip se rompe a la
  // hora, que es lo que dura la firma — el logo principal se salva porque su
  // URL es pública.
  const agregarImagenAlVideo = useCallback(
    async (assetId: string, nombre: string, ubicacion?: UbicacionDeClipNuevo) => {
      const [asset] = await fetchAssetsByIds([assetId]);
      let src = assetPreviewUrls[assetId];
      if (!src && asset) {
        const url = await getAssetSignedUrl(asset.storage_path);
        if (url) {
          src = url;
          setAssetPreviewUrls((prev) => ({ ...prev, [assetId]: url }));
        }
      }
      const elementsTrack = TRACK_CONFIG.find((t) => t.type === "elements")!;
      const clipId = generateId();
      const { startTime, trackIndex } = ubicarClipNuevo("image", {
        ...ubicacion,
        duracion: 5,
      });
      const newClip: TimelineClip = {
        id: clipId,
        type: "image",
        name: nombre,
        startTime,
        trackIndex,
        duration: 5,
        color: elementsTrack.color,
        src,
        imageUrl: src,
        assetId,
        storagePath: asset?.storage_path,
        // Igual que el logo: a pantalla completa no es lo que se busca.
        encuadre: { scale: 0.6 },
      };
      setClips((prev) => [...prev, newClip]);
      // Al toque: la próxima imagen del mismo lote se ubica mirando ésta.
      clipsRef.current = [...clipsRef.current, newClip];
      setSelectedClipId(clipId);
    },
    [assetPreviewUrls, ubicarClipNuevo],
  );

  // Importa una imagen de la marca (hoy: la del moodboard) como asset del
  // proyecto. Mismo patrón que `importProductToImageAsset`: dedup por id para
  // no subir dos veces la misma.
  const importarImagenDeMarca = useCallback(
    async (item: {
      id: string;
      url?: string;
      nombre?: string | null;
    }): Promise<string | null> => {
      const cacheado = imagenesDeMarcaImportadas[item.id];
      if (cacheado) {
        if (item.url)
          setAssetPreviewUrls((prev) =>
            prev[cacheado] ? prev : { ...prev, [cacheado]: item.url! },
          );
        return cacheado;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!item.url || !projectId || !cid) return null;
      try {
        const resp = await fetch(item.url);
        const blob = await resp.blob();
        const ext = (
          item.nombre?.split(".").pop() ||
          blob.type.split("/")[1] ||
          "jpg"
        ).toLowerCase();
        const file = new File(
          [blob],
          item.nombre || `marca-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setImagenesDeMarcaImportadas((prev) => ({
          ...prev,
          [item.id]: asset.id,
        }));
        return asset.id;
      } catch (err) {
        console.error("[VideoEditor] importar imagen de marca falló", err);
        return null;
      }
    },
    [imagenesDeMarcaImportadas, companyId, imageGenCompanyId, projectId],
  );

  // ── File upload ───────────────────────────────────────────────────────────
  // Modal de error de subida (ej. archivo demasiado grande). Mensaje o null.
  const [uploadErrorMsg, setUploadErrorMsg] = useState<string | null>(null);
  // El título del mismo aviso: lo usan también "Quitar fondo" y la descarga, que no
  // son subidas (ADR-2026-11-03-los-errores-del-estudio-se-ven). null = el de siempre.
  const [tituloDelAviso, setTituloDelAviso] = useState<string | null>(null);
  const mostrarAviso = (titulo: string, mensaje: string) => {
    setTituloDelAviso(titulo);
    setUploadErrorMsg(mensaje);
  };

  // Un video o un audio de la computadora: el clip entra YA (con un blob URL)
  // y el archivo sube en segundo plano. Es lo que hace el botón "Subir" y, desde
  // que se puede soltar un archivo sobre el editor, también el arrastre: la
  // `ubicacion` es donde se soltó (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta).
  const subirMediaALaTimeline = useCallback(
    (file: File, type: "video" | "audio", ubicacion?: UbicacionDeClipNuevo) => {
      if (!companyId || !projectId) return;

      // Tope de 100MB: hoy Supabase Storage tiene el Global file size limit en
      // 100MB; un archivo más grande falla en el gateway (502) sin feedback al
      // usuario. Hasta subir ese límite, lo bloqueamos acá y avisamos.
      if (file.size > 100 * 1024 * 1024) {
        setUploadErrorMsg(
          `El archivo "${file.name}" pesa ${(file.size / (1024 * 1024)).toFixed(0)} MB. ` +
            `Por ahora no se aceptan videos de más de 100 MB. Probá con uno más liviano.`,
        );
        return;
      }

      const blobUrl = URL.createObjectURL(file);
      const isVideo = type === "video";
      const clipType: ClipType = isVideo ? "video" : "audio";
      const track = getTrackForClipType(clipType);
      const clipId = generateId();
      const { startTime, trackIndex } = ubicarClipNuevo(clipType, ubicacion);

      const newClip: TimelineClip = {
        id: clipId,
        type: clipType,
        name:
          file.name.length > 20 ? file.name.slice(0, 17) + "..." : file.name,
        startTime,
        trackIndex,
        duration: 5,
        color: track.color,
        src: blobUrl,
        volume: 1,
      };

      if (isVideo) {
        const vid = document.createElement("video");
        vid.preload = "metadata";
        vid.onloadedmetadata = () => {
          setClips((prev) =>
            prev.map((c) =>
              c.id === newClip.id ? { ...c, duration: vid.duration } : c,
            ),
          );
        };
        vid.src = blobUrl;
      } else {
        const aud = document.createElement("audio");
        aud.preload = "metadata";
        aud.onloadedmetadata = () => {
          setClips((prev) =>
            prev.map((c) =>
              c.id === newClip.id ? { ...c, duration: aud.duration } : c,
            ),
          );
        };
        aud.src = blobUrl;
      }

      setClips((prev) => [...prev, newClip]);
      // Al toque: el próximo archivo del mismo lote se ubica mirando éste.
      clipsRef.current = [...clipsRef.current, newClip];
      setSelectedClipId(newClip.id);
      // A la vista, como lo que se toca en Archivos: si caía fuera de la parte
      // visible de la timeline parecía que no había aparecido
      // (ADR-2026-11-03-agregar-al-instante).
      clipAEnfocarRef.current = newClip.id;

      // Upload to Storage + insert into video_project_assets in background
      (async () => {
        const _t0 = performance.now();
        console.log(
          `[Atlas][upload] START "${file.name}" ${(file.size / 1e6).toFixed(1)}MB (${file.type || "?"})`,
        );
        try {
          // De la sesión guardada, sin pedido de red (ADR-2026-11-03-abrir-sin-esperar).
          const {
            data: { session },
          } = await supabase.auth.getSession();
          const user = session?.user;
          const asset = await uploadProjectAsset({
            file,
            projectId,
            companyId,
            userId: user?.id,
          });
          console.log(
            `[Atlas][upload] DONE en ${((performance.now() - _t0) / 1000).toFixed(1)}s → storagePath=${asset.storage_path}`,
          );
          // Attach the asset id and storage path back to the clip
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? { ...c, assetId: asset.id, storagePath: asset.storage_path }
                : c,
            ),
          );
          // Mostrarlo al toque en el panel Archivos (Multimedia/Audio) sin
          // tener que salir y volver a entrar a la pill. OJO: usamos un blob URL
          // PROPIO (no el del clip), porque al borrar el clip de la timeline se
          // revoca su `src` — si lo compartiéramos, la miniatura de Multimedia
          // quedaría rota (ERR_FILE_NOT_FOUND).
          setAssetPreviewUrls((prev) => ({
            ...prev,
            [asset.id]: URL.createObjectURL(file),
          }));
          setLibraryAssets((prev) =>
            prev.some((a) => a.id === asset.id) ? prev : [asset, ...prev],
          );
        } catch (err) {
          console.error(
            `[Atlas][upload] FALLÓ tras ${((performance.now() - _t0) / 1000).toFixed(1)}s:`,
            err,
          );
          // OJO: el clip ya está en la línea de tiempo (se agrega antes de
          // subir, para que se vea al toque). Si la subida falla, queda ahí
          // apuntando a un blob que NO sobrevive un reload. Avisamos, pero no
          // lo sacamos solo: el usuario puede haberlo movido o recortado ya.
          setUploadErrorMsg(describeUploadError(err, file.name));
        }
      })();
    },
    [projectId, companyId, ubicarClipNuevo],
  );

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, type: "video" | "audio") => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      subirMediaALaTimeline(file, type);
    },
    [subirMediaALaTimeline],
  );

  // Una imagen de la computadora: sube al proyecto y queda en Archivos →
  // Multimedia (arriba de todo). Devuelve el asset, o `null` si falló (ya
  // avisó). Es lo que hacía el input de "Subir" de Archivos, sacado acá para
  // que el arrastre suba por el mismo camino.
  const subirImagenAArchivos = useCallback(
    async (file: File): Promise<ProjectAsset | null> => {
      if (!companyId || !projectId) return null;
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        const url = await getAssetSignedUrl(asset.storage_path);
        if (url)
          setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: url }));
        setImageLibraryAssets((prev) => [asset, ...prev]);
        return asset;
      } catch (err) {
        console.error("[VideoEditor] image upload failed:", err);
        setUploadErrorMsg(describeUploadError(err, file.name));
        return null;
      }
    },
    [companyId, projectId],
  );

  // Archivos soltados sobre el editor: cada uno va por el camino de su tipo.
  // Un video o un audio entran a la timeline al toque; una imagen sube primero
  // y después entra como capa de imagen (dura poco: es una foto). Lo que no es
  // ninguna de las tres cosas avisa, como el input de "Subir".
  const soltarArchivosEnElEstudio = useCallback(
    (archivos: File[], ubicacion?: UbicacionDeClipNuevo) => {
      for (const file of archivos) {
        const familia = familiaDeArchivo(file);
        if (familia === "video" || familia === "audio") {
          subirMediaALaTimeline(file, familia, ubicacion);
        } else if (familia === "elements") {
          void (async () => {
            const asset = await subirImagenAArchivos(file);
            if (asset)
              await agregarImagenAlVideo(asset.id, file.name, ubicacion);
          })();
        } else {
          setUploadErrorMsg(
            describeUploadError(new Error("Unsupported file type"), file.name),
          );
        }
      }
    },
    [subirMediaALaTimeline, subirImagenAArchivos, agregarImagenAlVideo],
  );

  // ── Quitar fondo de una capa de imagen ──────────────────────────────────
  // El backend recorta el producto (BiRefNet v2, con Bria de respaldo) y devuelve
  // el PNG con transparencia firmado; acá se baja y se da de alta como asset del
  // proyecto por `uploadProjectAsset` —el único camino de escritura de Archivos—,
  // y el clip pasa a mostrar el nuevo. El original NO se toca: queda en Archivos
  // por si el recorte no gustó. Gratis: no pasa por créditos
  // (ADR-2026-09-27-quitar-fondo-en-el-estudio-birefnet). Lo usan el botón del
  // panel del clip y la herramienta `quitar_fondo` del agente.
  const [quitandoFondoDe, setQuitandoFondoDe] = useState<string | null>(null);
  const quitarFondoDelClip = useCallback(
    async (
      clipId: string,
    ): Promise<{ assetId: string; aplicado: boolean } | null> => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip || clip.type !== "image") return null;
      // Si mientras se recorta el clip pasa a mostrar otra imagen (la cambió o
      // deshizo), el recorte de la anterior no la pisa: queda en Archivos. Como
      // "Pasar a 1080p" y "Corregir voz".
      const pathAlEmpezar = clip.storagePath;
      if (!clip.storagePath || !companyId || !projectId) {
        mostrarAviso(
          "No se pudo quitar el fondo",
          "Esta imagen todavía no terminó de subir. Esperá un momento y probá de nuevo.",
        );
        return null;
      }
      setQuitandoFondoDe(clipId);
      try {
        const { url } = await postQuitarFondo({
          bucket: "video-project-assets",
          path: clip.storagePath,
        });
        const resp = await fetch(url);
        if (!resp.ok) throw new Error("No se pudo bajar el recorte.");
        const blob = await resp.blob();
        const base = (clip.name || "imagen").replace(/\.[^.]+$/, "");
        const file = new File([blob], `${base}-sin-fondo.png`, { type: "image/png" });
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        const src = (await getAssetSignedUrl(asset.storage_path)) ?? URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: src }));
        setImageLibraryAssets((prev) => [asset, ...prev]);
        const ahora = clipsRef.current.find((c) => c.id === clipId);
        if (!ahora || ahora.storagePath !== pathAlEmpezar) {
          mostrarAviso(
          "No se pudo quitar el fondo",
            "La imagen cambió mientras se quitaba el fondo: el recorte quedó en Archivos del proyecto.",
          );
          return { assetId: asset.id, aplicado: false };
        }
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId && c.storagePath === pathAlEmpezar
              ? {
                  ...c,
                  src,
                  imageUrl: src,
                  assetId: asset.id,
                  storagePath: asset.storage_path,
                  // Se vuelve a medir del <img>: el recorte no cambia el
                  // tamaño, pero no hay por qué confiar en eso.
                  imageAspect: undefined,
                }
              : c,
          ),
        );
        return { assetId: asset.id, aplicado: true };
      } catch (err) {
        console.error("[VideoEditor] quitar fondo falló", err);
        mostrarAviso(
          "No se pudo quitar el fondo",
          err instanceof Error && err.message
            ? err.message
            : "No se pudo quitar el fondo. Probá de nuevo en un momento.",
        );
        return null;
      } finally {
        setQuitandoFondoDe(null);
      }
    },
    [companyId, projectId],
  );

  // ── AI generate (placeholder) ─────────────────────────────────────────────
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim() || aiGenerating) return;
    setAiGenerating(true);
    await new Promise((res) => setTimeout(res, 3000));

    const aiTrack = getTrackForClipType("ai-generated");
    const newClip: TimelineClip = {
      id: generateId(),
      type: "ai-generated",
      name: `IA: ${aiPrompt.slice(0, 15)}...`,
      ...ubicarClipNuevo("ai-generated", { duracion: parseInt(aiDuration) }),
      duration: parseInt(aiDuration),
      color: aiTrack.color,
      volume: 1,
    };

    setClips((prev) => [...prev, newClip]);
    setSelectedClipId(newClip.id);
    setAiGenerating(false);
    setAiPrompt("");
  };

  // ── Add text clip ─────────────────────────────────────────────────────────
  // Creates a new text clip on the elements track at the current playhead
  // (or end-of-track if the playhead overlaps existing clips). Returns the
  // new clip id so callers (agent tool) can reference it.
  const addTextClip = useCallback(
    (
      opts?: Partial<{
        text: string;
        fontFamily: string;
        fontSizePct: number;
        fontWeight: "normal" | "bold";
        fontStyle: "normal" | "italic";
        textColor: string;
        textAlign: "left" | "center" | "right";
        textXPct: number;
        textYPct: number;
        // Optional % of canvas width the text box should occupy. When set, the
        // text wraps inside a fixed-width box instead of auto-sizing to the
        // content. Used by the subtitle generator to make captions span most
        // of the canvas.
        textWidthPct: number;
        startTime: number;
        duration: number;
        enterAnimation: string;
        enterDurationSec: number;
        exitAnimation: string;
        exitDurationSec: number;
        // Subtitle group fields. When `isSubtitle` is true the caller should also
        // pass a stable `subtitleGroupId` so that all clips created in the same
        // batch can be style-synced together by the toolbar later on.
        isSubtitle: boolean;
        subtitleGroupId: string;
        // Linked-subtitle authoring: when set, the created text clip will track
        // its source media/video clip on every move/resize/trim (see the
        // reconciler useEffect). `linkedMediaStart/End` are in *source-media*
        // seconds (original file timeline), NOT timeline seconds.
        linkedClipId: string;
        linkedMediaStart: number;
        linkedMediaEnd: number;
        // When true, the newly created clip will not be auto-selected. Useful
        // when adding many clips in a loop (subtitle batch) to avoid hijacking
        // the user's current selection.
        skipSelect: boolean;
      }>,
    ): string => {
      const textTrack = getTrackForClipType("text");
      const text = (opts?.text ?? DEFAULT_TEXT_CLIP.text).slice(0, 500);
      // Un texto suelto pasa por `ubicarClipNuevo` (el "+" de una pista lo
      // manda a continuación de esa pista; sin segundo, al cursor); los
      // subtítulos no: van todos a su fila, en el segundo que les toca.
      const duration =
        typeof opts?.duration === "number"
          ? Math.max(0.5, Math.min(60, opts.duration))
          : DEFAULT_TEXT_CLIP.duration;
      const ubicado = opts?.isSubtitle
        ? null
        : ubicarClipNuevo("text", {
            startTime:
              typeof opts?.startTime === "number" ? opts.startTime : undefined,
            duracion: duration,
          });
      const startTime = ubicado
        ? ubicado.startTime
        : typeof opts?.startTime === "number"
          ? Math.max(0, opts.startTime)
          : 0;
      const id = generateId();
      const newClip: TimelineClip = {
        id,
        type: "text",
        name: text.length > 20 ? text.slice(0, 17) + "…" : text || "Texto",
        startTime,
        duration,
        color: textTrack.color,
        // Los subtítulos van todos a la fila de subtítulos (capa 0); un texto
        // suelto estrena capa propia, como el resto de los elementos.
        ...(ubicado ? { trackIndex: ubicado.trackIndex } : {}),
        text,
        fontFamily: opts?.fontFamily ?? DEFAULT_TEXT_CLIP.fontFamily,
        fontSizePct: Math.max(
          2,
          Math.min(40, opts?.fontSizePct ?? DEFAULT_TEXT_CLIP.fontSizePct),
        ),
        fontWeight: opts?.fontWeight ?? DEFAULT_TEXT_CLIP.fontWeight,
        fontStyle: opts?.fontStyle ?? DEFAULT_TEXT_CLIP.fontStyle,
        textColor: opts?.textColor ?? DEFAULT_TEXT_CLIP.textColor,
        textAlign: opts?.textAlign ?? DEFAULT_TEXT_CLIP.textAlign,
        textXPct: Math.max(
          0,
          Math.min(100, opts?.textXPct ?? DEFAULT_TEXT_CLIP.textXPct),
        ),
        textYPct: Math.max(
          0,
          Math.min(100, opts?.textYPct ?? DEFAULT_TEXT_CLIP.textYPct),
        ),
        enterAnimation: opts?.enterAnimation ?? "none",
        enterDurationSec:
          typeof opts?.enterDurationSec === "number"
            ? Math.max(0, Math.min(10, opts.enterDurationSec))
            : 0.5,
        exitAnimation: opts?.exitAnimation ?? "none",
        exitDurationSec:
          typeof opts?.exitDurationSec === "number"
            ? Math.max(0, Math.min(10, opts.exitDurationSec))
            : 0.5,
        ...(typeof opts?.textWidthPct === "number"
          ? { textWidthPct: Math.max(5, Math.min(100, opts.textWidthPct)) }
          : {}),
        ...(opts?.isSubtitle ? { isSubtitle: true } : {}),
        ...(opts?.subtitleGroupId
          ? { subtitleGroupId: opts.subtitleGroupId }
          : {}),
        ...(opts?.linkedClipId &&
        typeof opts.linkedMediaStart === "number" &&
        typeof opts.linkedMediaEnd === "number"
          ? {
              linkedClipId: opts.linkedClipId,
              linkedMediaStart: Math.max(0, opts.linkedMediaStart),
              linkedMediaEnd: Math.max(
                opts.linkedMediaStart,
                opts.linkedMediaEnd,
              ),
            }
          : {}),
      };
      setClips((prev) => [...prev, newClip]);
      if (!opts?.skipSelect) setSelectedClipId(id);
      return id;
    },
    [clips, ubicarClipNuevo],
  );

  // ── Subtítulos ───────────────────────────────────────────────────────────
  // Kick off the transcription flow for a given clip: resolve the clip's
  // signed URL, POST it (plus the current user id) to the n8n webhook,
  // chunk the returned word stream and open the modal so the user can
  // review the full transcript and confirm adding subtitle clips.
  //
  // The caller is expected to have already verified that the clip is
  // transcribable (video / audio / ai-generated with a reachable source).
  // We still double-check here so the UI can surface a helpful error
  // instead of silently calling the webhook with an empty URL.
  const openSubtitlesModalForClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      // We intentionally do NOT open the modal here anymore. The generation
      // UI lives directly on top of the source clip in the timeline (shimmer
      // + "Generando subtítulos" overlay). The modal is only surfaced below
      // in the error branch, so the user can still see details + retry.
      setSubtitlesSourceClipId(clipId);
      // En el teléfono la hoja TAPA la timeline, que es justo donde se muestra
      // que se están generando (el shimmer y el cartel "Generando
      // subtítulos…" van encima del clip). Con la hoja abierta el usuario
      // toca y no ve nada pasar. En escritorio el panel convive con la
      // timeline, así que se queda donde está.
      // Ver ADR-2026-09-17-subtitulos-en-el-telefono-cierra-la-hoja.
      if (enElTelefonoRef.current) setActivePanel("none");
      setSubtitlesLoading(true);
      setSubtitlesError(null);
      setSubtitlesResult(null);
      // En qué paso vamos. Antes los cuatro motivos de fallo terminaban en el
      // MISMO texto ("probá volver a subir el archivo") y no se registraba
      // nada: cuando una usuaria reportó que no le andaban los subtítulos no
      // hubo dónde mirar — el pedido nunca llega a `executions` si se rompe
      // acá (ADR-2026-09-11-los-subtitulos-fallan-sin-dejar-rastro).
      let paso: PasoDeSubtitulos = "sin_archivo";
      let detalle: string | undefined;
      try {
        // 1) Resolve a fresh signed URL. Prefer the asset's storage path
        //    (long-lived row in the DB → always re-signable) and fall back
        //    to the in-memory `src` only if the clip was never persisted.
        let url: string | null = null;
        if (clip.storagePath) {
          // Firmar puede devolver null por un hipo de Storage con el archivo
          // intacto: es un paso distinto de "el clip no tiene archivo".
          paso = "firmar_url";
          url = await getAssetSignedUrl(clip.storagePath);
        }
        if (
          !url &&
          typeof clip.src === "string" &&
          /^https?:\/\//.test(clip.src)
        ) {
          url = clip.src;
        }
        if (!url) {
          if (!clip.storagePath) paso = "sin_archivo";
          throw new Error(mensajeDeFalloDeSubtitulos({ paso }));
        }

        // 2) Resolve the current user id so the webhook can attribute the
        //    transcription. Fail loudly if there's no session — the user
        //    should always be authenticated inside the editor.
        paso = "sin_sesion";
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user?.id) throw new Error(mensajeDeFalloDeSubtitulos({ paso }));

        // 3) Fire the POST. We don't retry automatically because the
        //    webhook is slow (ElevenLabs transcription) and users benefit
        //    from seeing the error immediately and deciding what to do.
        paso = "transcribir";
        const tr = await requestTranscript(url, user.id);
        if (!tr.ok) {
          detalle = tr.message;
          throw new Error(mensajeDeFalloDeSubtitulos({ paso, detalle }));
        }
        const transcript = tr.transcript;
        const segments = chunkWordsIntoSubtitles(transcript.words);
        // Transcribió bien pero no hay una sola palabra: el clip no tiene voz
        // (los videos de IA suelen ser mudos). Antes esto terminaba en un
        // resultado vacío y el usuario veía que "no pasó nada".
        if (segments.length === 0) {
          paso = "sin_habla";
          throw new Error(mensajeDeFalloDeSubtitulos({ paso }));
        }
        setSubtitlesResult({ transcript, segments });
        // La marca es el archivo que SE TRANSCRIBIÓ (`clip`, capturado antes del
        // pedido), no el que tiene el clip al volver: si cambió mientras tanto,
        // `transcriptVigente` descarta estas palabras.
        // Cache the full transcript on the source clip itself. This makes it
        // immediately available to the agent (`get_clip_transcript`) without a
        // second webhook call, and survives splits/trims because consumers
        // filter words by the clip's visible window at read-time.
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, transcript: marcarTranscript(transcript, clip.storagePath) }
              : c,
          ),
        );
      } catch (err) {
        console.error("[VideoEditor] subtitle generation failed", err);
        // Registro con el contexto mínimo para diagnosticar sin el usuario
        // delante: en qué paso se rompió, qué clip era y si tenía archivo.
        // Nada de esto llega a `executions` cuando el fallo es del navegador.
        captureException(err, {
          flujo: "subtitulos",
          paso,
          detalle,
          clip_tipo: clip.type,
          clip_ai_status: clip.aiStatus ?? null,
          tenia_storage_path: !!clip.storagePath,
          tenia_src: typeof clip.src === "string" && clip.src.length > 0,
          project_id: projectId ?? null,
        });
        setSubtitlesError(
          err instanceof Error
            ? err.message
            : mensajeDeFalloDeSubtitulos({ paso }),
        );
        // Pop the modal only on failure so the user can read the message
        // and trigger the retry flow.
        setSubtitlesModalOpen(true);
      } finally {
        setSubtitlesLoading(false);
      }
    },
    [clips, projectId],
  );

  // Apply the currently computed subtitle segments to the timeline as
  // individual text clips anchored to `subtitlesSourceClipId`'s
  // startTime. We default them to a centered, bold, white style placed
  // near the bottom of the frame — the user can restyle afterwards
  // using the regular text-clip toolbar.
  const applySubtitlesToTimeline = useCallback(() => {
    if (!subtitlesResult || subtitlesResult.segments.length === 0) return;
    const baseClip = subtitlesSourceClipId
      ? clips.find((c) => c.id === subtitlesSourceClipId)
      : null;
    const base = baseClip?.startTime ?? 0;
    // Segment timestamps come from the transcription service in
    // *source-media* seconds (absolute to the original file). To map them
    // onto the timeline we subtract the source clip's mediaOffset (0 for
    // non-split clips). This also lets us later re-derive the timeline
    // position whenever the source clip is trimmed/moved, using the stored
    // linkedMediaStart/End values.
    const srcMediaOffset = baseClip?.mediaOffset ?? 0;
    const srcDuration = baseClip?.duration ?? Infinity;
    // One groupId per "generate subtitles" run so style edits to this batch
    // never leak into a previous batch the user may have already styled.
    const groupId = generateId();
    // Segments come from the transcription service anchored to the spoken
    // word boundaries, so two consecutive captions leave a silence gap
    // between them (breaths, commas, word-final consonants, hedges). We
    // close that gap by extending each clip up to the next segment's
    // start, capped so actual long silences between sentences/paragraphs
    // don't get absorbed into the previous caption. The cap is generous
    // on purpose — short pauses should feel seamless; only genuinely
    // long pauses (>1.2s) remain as visible silence.
    const MAX_HOLD_EXTEND_SEC = 1.2;
    const segs = subtitlesResult.segments;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i];
      const nextStart = i + 1 < segs.length ? segs[i + 1].start : seg.end;
      const heldEnd = Math.min(nextStart, seg.end + MAX_HOLD_EXTEND_SEC);
      // Convert source-media seconds to the source clip's *local* seconds
      // (i.e. seconds from the source clip's visible start) by subtracting
      // mediaOffset, then clamp to the visible window. Captions whose
      // entire range falls outside the trimmed window are skipped.
      const localStart = Math.max(
        0,
        Math.min(srcDuration, seg.start - srcMediaOffset),
      );
      const localEnd = Math.max(
        0,
        Math.min(srcDuration, heldEnd - srcMediaOffset),
      );
      const segDur = Math.max(0.3, localEnd - localStart);
      if (localEnd <= 0 || localStart >= srcDuration) continue;
      addTextClip({
        text: seg.text,
        startTime: base + localStart,
        duration: segDur,
        linkedClipId: subtitlesSourceClipId ?? undefined,
        linkedMediaStart: seg.start,
        linkedMediaEnd: heldEnd,
        textAlign: "center",
        textXPct: 50,
        textYPct: 85,
        fontWeight: "bold",
        textColor: "#FFFFFF",
        fontSizePct: 4,
        // Take up almost the full canvas width so long lines wrap nicely
        // instead of overflowing or getting auto-shrunk. Leaves a small
        // safe-area margin on each side.
        textWidthPct: 90,
        isSubtitle: true,
        subtitleGroupId: groupId,
        skipSelect: true,
      });
    }
    setSubtitlesModalOpen(false);
    setSubtitlesResult(null);
    setSubtitlesSourceClipId(null);
  }, [subtitlesResult, subtitlesSourceClipId, clips, addTextClip]);

  // Delta-based linked-subtitle reconciler. Each render we snapshot every
  // source clip's "anchor" (startTime + mediaOffset). On the *next* render,
  // if a source clip's anchor has shifted we translate every caption
  // linked to it by the same timeline delta — which keeps subtitles glued
  // to the source when the source is moved or left-trimmed, *without*
  // overwriting the user's own edits to the subtitle (move/trim/resize on
  // its own track). Left-trims propagate naturally because trimming from
  // the left increments both startTime and mediaOffset by the same amount;
  // the delta we apply is `Δ startTime`, so subtitles follow. Right-trims
  // only change `duration`, not the anchor, so subtitles stay put — the
  // user can manually re-time them if desired.
  // IMPORTANT: we deliberately compute the anchor diff *outside* of the
  // `setClips` updater and only invoke `setClips` when we actually need to
  // shift subtitles. Mutating `sourceAnchorsRef.current` inside the updater
  // is unsafe under React 18 StrictMode, which invokes state updaters twice
  // to surface side effects — on the second pass the ref would already be
  // updated, `delta` would collapse to 0, and the shifted-subtitles result
  // would be thrown away (the regression the user hit: captions refusing to
  // follow their source clip on drag).
  const sourceAnchorsRef = useRef<
    Map<string, { startTime: number; mediaOffset: number }>
  >(new Map());
  useEffect(() => {
    const byId = new Map(clips.map((c) => [c.id, c] as const));
    const prevAnchors = sourceAnchorsRef.current;
    const nextAnchors = new Map<
      string,
      { startTime: number; mediaOffset: number }
    >();
    // Only track anchors for source clips that actually have at least one
    // linked caption; avoids growing the map for every clip on the board.
    const linkedSourceIds = new Set<string>();
    for (const c of clips)
      if (c.linkedClipId) linkedSourceIds.add(c.linkedClipId);
    for (const id of linkedSourceIds) {
      const src = byId.get(id);
      if (src)
        nextAnchors.set(id, {
          startTime: src.startTime,
          mediaOffset: src.mediaOffset ?? 0,
        });
    }
    // Collect per-source deltas ahead of time so the updater below stays
    // pure (reads only from `prev` + the captured `deltas` map).
    const deltas = new Map<string, number>();
    for (const [id, nextA] of nextAnchors) {
      const prevA = prevAnchors.get(id);
      if (!prevA) continue; // first time we see this source — nothing to sync.
      const d = nextA.startTime - prevA.startTime;
      if (Math.abs(d) >= 1e-6) deltas.set(id, d);
    }
    // Update the ref unconditionally so the next render's diff is correct
    // — whether or not we actually schedule a subtitle shift this tick.
    sourceAnchorsRef.current = nextAnchors;
    if (deltas.size === 0) return;
    setClips((prev) =>
      prev.map((c) => {
        if (!c.linkedClipId) return c;
        const d = deltas.get(c.linkedClipId);
        if (d === undefined) return c;
        return { ...c, startTime: Math.max(0, c.startTime + d) };
      }),
    );
  }, [clips]);

  // Drop linked subtitles when their source clip is removed so we don't
  // leave orphan captions floating on the timeline.
  useEffect(() => {
    setClips((prev) => {
      const ids = new Set(prev.map((c) => c.id));
      const filtered = prev.filter(
        (c) => !c.linkedClipId || ids.has(c.linkedClipId),
      );
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [clips]);

  // Auto-apply transcription as soon as the webhook returns a usable result
  // so the user skips the intermediate "review the analysis" screen. Errors
  // and the loading spinner still render inside the modal because they're
  // the only states that actually need user attention.
  useEffect(() => {
    if (!subtitlesResult) return;
    if (subtitlesLoading) return;
    if (subtitlesError) return;
    if (subtitlesResult.segments.length === 0) return;
    applySubtitlesToTimeline();
    // `generar_subtitulos` con reemplazar=true: la tanda nueva ya está, recién
    // ahora salen las viejas de ese clip.
    const reemplazar = subtitulosAReemplazarRef.current;
    if (reemplazar && reemplazar.fuenteId === subtitlesSourceClipId) {
      subtitulosAReemplazarRef.current = null;
      setClips((prev) => prev.filter((c) => !reemplazar.ids.has(c.id)));
    }
    const avisar = subtitulosDelAgenteRef.current;
    if (avisar) {
      subtitulosDelAgenteRef.current = null;
      avisar({ ok: true, lineas: subtitlesResult.segments.length });
    }
  }, [
    subtitlesResult,
    subtitlesLoading,
    subtitlesError,
    subtitlesSourceClipId,
    applySubtitlesToTimeline,
  ]);
  useEffect(() => {
    if (!subtitlesError) return;
    // Falló: los subtítulos que había se quedan.
    subtitulosAReemplazarRef.current = null;
    const avisar = subtitulosDelAgenteRef.current;
    if (!avisar) return;
    subtitulosDelAgenteRef.current = null;
    avisar({ ok: false, error: subtitlesError });
  }, [subtitlesError]);

  // ── Animaciones gráficas: crear el clip (panel y agente, el MISMO camino) ──
  // La marca como la recibe el código: colores y las fuentes por rol (sin
  // repetir), congeladas en el clip al crearlo.
  const marcaDelProyectoParaAnimacion = useCallback((): MarcaDeAnimacion => {
    // Sólo familias del CATÁLOGO: es lo único que cargan el sandbox del preview
    // y el clip host de Lambda (las subidas no llegan a la animación). Antes
    // pasaba cualquier string de `fonts` —incluida la descripción libre del
    // onboarding (`style`)— y el prompt le pide al generador que las prefiera.
    // `suggested` es un array y antes se perdía. Ver
    // ADR-2026-10-30-fuentes-fuera-del-catalogo.
    const fuentes = Array.from(
      new Set(
        Object.values(brandProfile?.fonts ?? {})
          .flatMap((f: unknown) => (Array.isArray(f) ? f : [f]))
          .map((f) =>
            typeof f === "string" ? familiaDelCatalogo(f) : null,
          )
          .filter((f): f is string => f !== null),
      ),
    );
    return {
      colores: brandProfile?.colors ?? [],
      fuentes,
      nombre: brandProfile?.name || projectName || "",
    };
  }, [brandProfile, projectName]);

  // La miniatura de la biblioteca: una captura JPEG chica del medio de la
  // animación, tomada del sandbox del preview cuando compila.
  const capturarMiniaturaDeAnimacion = useCallback(
    async (clipId: string, bibliotecaId: string, duracionSeg: number) => {
      const estado = await esperarSandbox(clipId);
      if (estado.tipo !== "listo") return;
      const total = Math.max(1, Math.round(duracionSeg * ANIMATION_FPS));
      const [img] = await capturarSandbox(
        clipId,
        [Math.floor(total / 2)],
        ANCHO_MINIATURA,
        undefined,
        true,
      );
      if (!img) return;
      setAnimacionesGraficas((prev) =>
        actualizarAnimacion(prev, bibliotecaId, { miniatura: img }),
      );
    },
    [],
  );

  /**
   * Mientras el generador escribe (15 a 40 s), el clip ya está en la timeline
   * como "Generando…", igual que un video con IA: el usuario ve dónde va a caer
   * y que algo pasa. Se completa con `completarAnimacionGrafica` o se descarta
   * con `descartarPlaceholderDeAnimacion` si el pedido falla. No se guarda con
   * el proyecto (ver `saveProject`) ni se dibuja en el preview.
   */
  const crearPlaceholderDeAnimacion = useCallback(
    (p: { nombre: string; tipo: TipoDeAnimacionParaClip; startTime?: number }) => {
      const id = generateId();
      const startTime =
        typeof p.startTime === "number" && p.startTime >= 0
          ? p.startTime
          : Math.max(0, currentTimeRef.current);
      const elementsTrack = TRACK_CONFIG.find((t) => t.type === "elements")!;
      const nuevo: TimelineClip = {
        id,
        type: "animation",
        name: p.nombre,
        startTime,
        trackIndex: capaNuevaPara(clips, "animation"),
        duration: 4,
        color: elementsTrack.color,
        animXPct: 50,
        animYPct: 50,
        animScale: p.tipo === "escena" ? 1 : 0.6,
        aiStatus: "generating",
        aiGenerationStartedAt: Date.now(),
      };
      setClips((prev) => [...prev, nuevo]);
      return { id, startTime };
    },
    [clips],
  );

  const descartarPlaceholderDeAnimacion = useCallback((id: string) => {
    setClips((prev) => prev.filter((c) => c.id !== id));
  }, []);

  /**
   * El código llegó: el placeholder pasa a ser el clip de verdad (nombre,
   * duración, código), se selecciona, el playhead va encima (para que el
   * sandbox lo monte) y la animación queda en la biblioteca del proyecto.
   */
  const completarAnimacionGrafica = useCallback(
    (
      id: string,
      p: {
        codigo: string;
        resumen: string;
        tipo: TipoDeAnimacionParaClip;
        assets?: AssetDeAnimacion[];
        marca?: MarcaDeAnimacion;
        duracionSeg: number;
        nombre: string;
      },
    ) => {
      const duracion = acotarDuracion(p.duracionSeg, 4);
      const bibliotecaId = generateId();
      const animacion: AnimacionPorCodigo = {
        ...crearAnimacionPorCodigo({
          version: {
            codigo: p.codigo,
            resumen: p.resumen,
            fecha: new Date().toISOString(),
            duracionSeg: duracion,
          },
          tipo: p.tipo,
          assets: p.assets ?? [],
          marca: p.marca ?? marcaDelProyectoParaAnimacion(),
        }),
        bibliotecaId,
      };
      // El placeholder pudo desaparecer durante los 15 a 40 s del pedido
      // (el usuario lo borró o deshizo). La animación ya se cobró: se guarda
      // igual en la biblioteca (Archivos → Animaciones) para que no se
      // pierda, pero sin seleccionar ni capturar un clip que no existe, y el
      // que llamó se entera por `enTimeline` para no anunciarla como creada
      // en la timeline.
      const placeholder = clipsRef.current.find((c) => c.id === id);
      if (!placeholder) {
        setAnimacionesGraficas((prev) =>
          guardarAnimacion(prev, {
            id: bibliotecaId,
            nombre: p.nombre,
            animacion,
            creadaEn: new Date().toISOString(),
          }),
        );
        return { id, bibliotecaId, duracion, enTimeline: false };
      }
      const startTime = placeholder.startTime;
      setClips((prev) =>
        prev.map((c) => {
          if (c.id !== id) return c;
          const { aiStatus: _a, aiGenerationStartedAt: _b, ...resto } = c;
          return {
            ...resto,
            name: p.nombre,
            ...ventanaDeVersion(duracion),
            animScale: p.tipo === "escena" ? 1 : 0.6,
            animationCodigo: animacion,
          };
        }),
      );
      setSelectedClipId(id);
      setIsPlaying(false);
      setCurrentTime(startTime + Math.min(0.1, duracion / 2));
      setAnimacionesGraficas((prev) =>
        guardarAnimacion(prev, {
          id: bibliotecaId,
          nombre: p.nombre,
          animacion,
          creadaEn: new Date().toISOString(),
        }),
      );
      void capturarMiniaturaDeAnimacion(id, bibliotecaId, duracion);
      return { id, bibliotecaId, duracion, enTimeline: true };
    },
    [marcaDelProyectoParaAnimacion, capturarMiniaturaDeAnimacion],
  );

  /**
   * Crea el clip de una animación gráfica en la timeline, lo selecciona, deja
   * el playhead encima (para que el sandbox lo monte) y, si es nueva, la guarda
   * en la biblioteca del proyecto. Lo usan el panel "Animación gráfica", la
   * tool `crear_animacion` del agente y "agregar" desde Archivos → Animaciones.
   */
  const crearClipDeAnimacionGrafica = useCallback(
    (p: {
      /** Una ya guardada (Archivos → Animaciones), o los datos de una nueva. */
      animacion?: AnimacionPorCodigo;
      codigo?: string;
      resumen?: string;
      tipo: TipoDeAnimacionParaClip;
      assets?: AssetDeAnimacion[];
      marca?: MarcaDeAnimacion;
      duracionSeg: number;
      nombre: string;
      startTime?: number;
      /** Si se soltó arrastrando: la capa a la que se apuntó. */
      destino?: DestinoDeSoltar;
    }) => {
      const id = generateId();
      const duracion = acotarDuracion(p.duracionSeg, 4);
      // Sin segundo, `ubicarClipNuevo` cae al cursor (o sigue al "+" de una
      // pista): no hay que resolverlo acá.
      const { startTime, trackIndex } = ubicarClipNuevo("animation", {
        startTime:
          typeof p.startTime === "number" && p.startTime >= 0
            ? p.startTime
            : undefined,
        destino: p.destino,
        duracion,
      });
      const bibliotecaId = p.animacion?.bibliotecaId ?? generateId();
      const animacion: AnimacionPorCodigo = p.animacion
        ? { ...p.animacion, bibliotecaId }
        : {
            ...crearAnimacionPorCodigo({
              version: {
                codigo: p.codigo ?? "",
                resumen: p.resumen ?? "",
                fecha: new Date().toISOString(),
                duracionSeg: duracion,
              },
              tipo: p.tipo,
              assets: p.assets ?? [],
              marca: p.marca ?? marcaDelProyectoParaAnimacion(),
            }),
            bibliotecaId,
          };
      const elementsTrack = TRACK_CONFIG.find((t) => t.type === "elements")!;
      const nuevo: TimelineClip = {
        id,
        type: "animation",
        name: p.nombre,
        startTime,
        trackIndex,
        ...ventanaDeVersion(duracion),
        color: elementsTrack.color,
        animXPct: 50,
        animYPct: 50,
        // Una escena ocupa toda la pantalla; una superpuesta entra como los
        // demás Elementos, al 60 %, para que se vea como algo movible.
        animScale: animacion.tipo === "escena" ? 1 : 0.6,
        animationCodigo: animacion,
      };
      setClips((prev) => [...prev, nuevo]);
      setSelectedClipId(id);
      setIsPlaying(false);
      setCurrentTime(startTime + Math.min(0.1, duracion / 2));
      if (!p.animacion) {
        setAnimacionesGraficas((prev) =>
          guardarAnimacion(prev, {
            id: bibliotecaId,
            nombre: p.nombre,
            animacion,
            creadaEn: new Date().toISOString(),
          }),
        );
      }
      void capturarMiniaturaDeAnimacion(id, bibliotecaId, duracion);
      return { id, bibliotecaId, duracion, startTime };
    },
    [ubicarClipNuevo, marcaDelProyectoParaAnimacion, capturarMiniaturaDeAnimacion],
  );

  // ── Agent tool executor ────────────────────────────────────────────────
  // Receives a tool name + parsed input, returns a {content, is_error?} shape.
  // Content is normally a string (JSON or plain text). For tools that need to
  // surface images to Claude (e.g. analyze_video_quick) it can also be an
  // array of content blocks (text + image) compatible with Anthropic's
  // tool_result.content format.
  const executeAgentTool = useCallback(
    async (
      name: string,
      input: any,
    ): Promise<{ content: string | any[]; is_error?: boolean }> => {
      // Las etiquetas de progreso que el Director mostraba en su drawer. Hoy el chat
      // muestra la `etiqueta` de cada herramienta mientras corre y no hay un historial
      // propio que persistir: queda como no-op para no tocar los cuarenta cases
      // (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente).
      const toolMessage = (
        _label: string,
        _Icon?: React.ComponentType<{ className?: string }>,
        _hint?: string,
      ) => {};
      // If the agent passes a generated-image id where an assetId is expected
      // (ref / firstFrame / lastFrame), import it as a project asset on the fly
      // so the panel preview works and the generation webhook receives a valid
      // assetId. Returns null if the id can't be resolved (e.g. the generated
      // image is still loading). A normal assetId is returned unchanged.
      const resolveRefIdToAssetId = async (
        rawId: string,
      ): Promise<string | null> => {
        const id = String(rawId || "").trim();
        if (!id) return null;
        const gen = imageGenResults.find((r) => r.id === id);
        if (!gen) return id; // not a generated image → assume it's already an assetId
        if (!gen.signedUrl) {
          console.warn(
            "[agent] skipping generated image ref — not ready yet:",
            id,
          );
          return null;
        }
        if (!projectId || !companyId) return null;
        try {
          const resp = await fetch(gen.signedUrl);
          const blob = await resp.blob();
          const ext = (gen.fileName?.split(".").pop() || "jpg").toLowerCase();
          const file = new File(
            [blob],
            gen.fileName || `ai-${Date.now()}.${ext}`,
            { type: blob.type || "image/jpeg" },
          );
          const {
            data: { user },
          } = await supabase.auth.getUser();
          const asset = await uploadProjectAsset({
            file,
            projectId,
            companyId,
            userId: user?.id,
          });
          const blobUrl = URL.createObjectURL(blob);
          setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
          return asset.id;
        } catch (err) {
          console.error(
            "[agent] failed to import generated image as asset",
            id,
            err,
          );
          return null;
        }
      };
      // En paralelo, no en fila: cada resolución puede descargar y volver a
      // subir una imagen entera, así que con 3 referencias la versión
      // secuencial sumaba las tres esperas. `Promise.all` preserva el orden
      // del input, igual que el `for` que reemplaza.
      const resolveRefIdList = async (raw: unknown): Promise<string[]> => {
        if (!Array.isArray(raw)) return [];
        const resolved = await Promise.all(
          raw.map((v) => resolveRefIdToAssetId(String(v))),
        );
        return resolved.filter((id): id is string => !!id);
      };
      // ── Animaciones por código: helpers de las tres tools ──────────────
      // Los assetIds del agente → {url firmada, descripción, storagePath}, en el
      // orden pedido y sólo imágenes (el código recibe fotos y logos).
      const resolverAssetsDeAnimacion = async (
        ids: string[],
      ): Promise<AssetDeAnimacion[]> => {
        if (ids.length === 0) return [];
        const filas = await fetchAssetsByIds(ids);
        const salida: AssetDeAnimacion[] = [];
        for (const id of ids) {
          const a = filas.find((f) => f.id === id);
          if (!a || a.type !== "image") continue;
          const url = await getAssetSignedUrl(a.storage_path);
          if (!url) continue;
          salida.push({
            url,
            descripcion: a.name || "imagen",
            storagePath: a.storage_path,
          });
        }
        return salida;
      };
      // Espera a que el sandbox del clip compile y devuelve al agente los datos
      // más 3 capturas (inicio, medio, fin) para que se mire antes de avisar.
      const resultadoConCapturas = async (
        clipId: string,
        datos: Record<string, unknown> & { duracionSeg: number },
      ): Promise<{ content: string | any[]; is_error?: boolean }> => {
        const estado = await esperarSandbox(clipId);
        if (estado.tipo === "error") {
          return {
            content: JSON.stringify({
              ...datos,
              error: estado.mensaje,
              etapa: estado.etapa,
              sugerencia:
                "El clip quedó en la timeline pero no se dibuja. Corregilo con modificar_animacion describiendo el error, o simplificá el pedido.",
            }),
            is_error: true,
          };
        }
        const total = Math.max(1, Math.round(datos.duracionSeg * ANIMATION_FPS));
        const frames = Array.from(
          new Set([0, Math.floor(total / 2), Math.max(0, total - 2)]),
        );
        const imagenes = await capturarSandbox(clipId, frames);
        const bloques: any[] = [
          {
            type: "text",
            text:
              JSON.stringify(datos) +
              (imagenes.length
                ? " Capturas del preview (inicio, medio, fin): mirá que nada quede cortado ni ilegible."
                : " No se pudieron tomar capturas del preview; pedile al usuario que lo mire."),
          },
        ];
        const etiquetas = ["inicio", "medio", "fin"];
        imagenes.forEach((dataUrl, i) => {
          const m = /^data:(image\/[a-z+]+);base64,(.+)$/i.exec(dataUrl);
          if (!m) return;
          bloques.push({ type: "text", text: etiquetas[i] ?? `frame ${i}` });
          bloques.push({
            type: "image",
            source: { type: "base64", media_type: m[1], data: m[2] },
          });
        });
        return { content: bloques };
      };
      try {
        switch (name) {
          case "request_user_upload": {
            const message =
              typeof input?.message === "string" && input.message.trim()
                ? input.message.trim()
                : "Subí uno o más archivos para continuar.";
            const buttonLabel =
              typeof input?.buttonLabel === "string" && input.buttonLabel.trim()
                ? input.buttonLabel.trim()
                : "Listo";
            const acceptKey: string =
              typeof input?.accept === "string" ? input.accept : "image";
            const accept =
              acceptKey === "video"
                ? "video/*"
                : acceptKey === "image+video"
                  ? "image/*,video/*"
                  : "image/*";
            const procesarComoPersonaje = input?.procesar_como_personaje === true;
            toolMessage("Pidiendo archivos al usuario", Upload);
            setAgentUploadedAssets([]);
            setAgentUploadInProgress(false);
            setAgentUploadRequest({
              message,
              buttonLabel,
              accept,
              procesarComoPersonaje,
            });
            setActivePanel("agent-upload");
            // Block until the user confirms or cancels. The panel UI calls
            // uploadResolverRef.current(json) to resolve.
            return await new Promise<{ content: string | any[] }>((resolve) => {
              uploadResolverRef.current = (result) => resolve(result);
            });
          }
          case "request_product_selection": {
            const message =
              typeof input?.message === "string" && input.message.trim()
                ? input.message.trim()
                : "Elegí un producto de tu catálogo.";
            const buttonLabel =
              typeof input?.buttonLabel === "string" && input.buttonLabel.trim()
                ? input.buttonLabel.trim()
                : "Usar selección";
            const multiple = input?.multiple === true;
            if (!companyId && !imageGenCompanyId)
              return { content: "No hay empresa cargada.", is_error: true };
            if (!brandProfile?.id)
              return {
                content: "No hay marca cargada en el proyecto.",
                is_error: true,
              };
            toolMessage(
              "Pidiéndole al usuario que elija un producto",
              Package,
            );
            setAgentProductSelection([]);
            setAgentProductRequest({ message, buttonLabel, multiple });
            setActivePanel("files");
            setFilesTab("productos");
            // Bloquea hasta que el usuario elige (resolver recibe los
            // productos) o cancela (null). El resolver lo dispara la grilla.
            const chosen = await new Promise<MockProductV2[] | null>(
              (resolve) => {
                productPickResolverRef.current = (products) =>
                  resolve(products);
              },
            );
            if (!chosen || chosen.length === 0)
              return { content: JSON.stringify({ cancelled: true }) };
            // Importar cada producto elegido como asset + mostrarlo inline.
            const imported: Array<{
              assetId: string;
              name: string;
              url: string;
            }> = [];
            for (const p of chosen) {
              const assetId = await importProductToImageAsset(p);
              if (assetId)
                imported.push({
                  assetId,
                  name: p.name,
                  url: p.imageUrl,
                });
            }
            if (imported.length === 0)
              return {
                content: "No se pudo importar ningún producto elegido.",
                is_error: true,
              };
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `El usuario eligió ${imported.length} producto(s) de su catálogo (ya importados como assets del proyecto). ` +
                  `Detalle (assetId | nombre): ` +
                  imported
                    .map((i) => `${i.assetId} | ${i.name}`)
                    .join("  ;  ") +
                  `. Usá estos assetIds como firstFrameAssetId / lastFrameAssetId / refAssetIds en prefill_ai_scene o prefill_ai_image. ` +
                  `A continuación las imágenes inline para que las observes.`,
              },
            ];
            for (const i of imported) {
              try {
                const base64 = await urlToBase64Jpeg(i.url);
                blocks.push({
                  type: "text",
                  text: `asset_id=${i.assetId} (${i.name})`,
                });
                blocks.push({
                  type: "image",
                  source: {
                    type: "base64",
                    media_type: "image/jpeg",
                    data: base64,
                  },
                });
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `asset_id=${i.assetId} (${i.name}): no se pudo cargar imagen inline (${err?.message || err}).`,
                });
              }
            }
            return { content: blocks };
          }
          case "get_project_context": {
            const counts: Record<string, number> = {};
            for (const c of clips) counts[c.type] = (counts[c.type] || 0) + 1;
            toolMessage("Leyendo contexto del proyecto", BarChart3);
            return {
              content: JSON.stringify({
                name: projectName,
                duration,
                aspectRatio,
                totalClips: clips.length,
                clipCounts: counts,
              }),
            };
          }
          case "list_timeline_clips": {
            toolMessage("Leyendo clips de la timeline", Video);
            return {
              content: JSON.stringify(
                clips.map((c) => ({
                  id: c.id,
                  type: c.type,
                  name: c.name,
                  startTime: c.startTime,
                  duration: c.duration,
                  trackIndex: c.trackIndex ?? 0,
                  aiPrompt: c.aiPrompt,
                  aiStatus: c.aiStatus,
                  ...(c.animationCodigo
                    ? {
                        animacionPorCodigo: {
                          tipo: c.animationCodigo.tipo,
                          versiones: describirVersiones(c.animationCodigo),
                        },
                      }
                    : c.animationTemplate
                      ? { plantilla: c.animationTemplate }
                      : {}),
                })),
              ),
            };
          }
          case "list_project_assets": {
            if (!projectId)
              return { content: "Project not loaded", is_error: true };
            toolMessage("Buscando assets del proyecto", FolderOpen);
            const typeIn = input?.type as
              | "video"
              | "audio"
              | "image"
              | undefined;
            const list = await fetchProjectAssetsByType(projectId, typeIn);
            return {
              content: JSON.stringify(
                list.map((a) => ({
                  id: a.id,
                  name: a.name,
                  type: a.type,
                  duration: (a as any).duration,
                })),
              ),
            };
          }
          case "analyze_video_quick": {
            const assetId: string | undefined =
              typeof input?.assetId === "string" ? input.assetId : undefined;
            const frameCount = Math.max(
              3,
              Math.min(12, Number(input?.frameCount) || 6),
            );
            if (!assetId) return { content: "Falta assetId", is_error: true };
            toolMessage("Análisis rápido del video", Film);
            const assets = await fetchAssetsByIds([assetId]);
            const asset = assets[0];
            if (!asset)
              return { content: "Asset no encontrado", is_error: true };
            if (asset.type !== "video")
              return { content: "El asset no es un video", is_error: true };
            const url = await getAssetSignedUrl(asset.storage_path);
            if (!url)
              return {
                content: "No se pudo firmar URL del video",
                is_error: true,
              };
            let frames: { tSec: number; base64: string }[] = [];
            try {
              // Reloj: la extracción usa un <video> oculto + seek; si un
              // evento (loadedmetadata/seeked) nunca dispara (video no
              // seekable, CORS, compu dormida), colgaría el turno para siempre.
              frames = await withTimeout(
                extractVideoFrames(url, frameCount),
                60_000,
                "extracción de frames del video",
              );
            } catch (err: any) {
              return {
                content: `No se pudieron extraer frames: ${err?.message || err}`,
                is_error: true,
              };
            }
            if (frames.length === 0)
              return { content: "No se obtuvieron frames", is_error: true };
            // Build a tool_result content array with a header text + image blocks
            // (one per frame, with its timestamp inlined as a label).
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Frames extraídos del video "${asset.name || assetId}" (duración aprox. ` +
                  `${asset.duration_seconds ? asset.duration_seconds.toFixed(1) : "?"}s). ` +
                  `Se muestran ${frames.length} frames distribuidos uniformemente. ` +
                  `Timestamps (s): ${frames.map((f) => f.tSec.toFixed(2)).join(", ")}.`,
              },
            ];
            for (const f of frames) {
              blocks.push({ type: "text", text: `t=${f.tSec.toFixed(2)}s` });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: f.base64,
                },
              });
            }
            return { content: blocks };
          }
          case "analyze_media_deep": {
            const ids: string[] = Array.isArray(input?.assetIds)
              ? input.assetIds.filter((x: any) => typeof x === "string")
              : [];
            const prompt: string =
              typeof input?.prompt === "string" ? input.prompt.trim() : "";
            if (ids.length === 0)
              return { content: "Falta assetIds", is_error: true };
            if (!prompt) return { content: "Falta prompt", is_error: true };
            toolMessage(
              "Analizando en profundidad",
              Search,
              "análisis con IA — puede tardar",
            );
            const urls = await getSignedUrlsForAssetIds(ids);
            if (urls.length === 0)
              return {
                content: "No se pudieron resolver URLs firmadas.",
                is_error: true,
              };
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (!user) return { content: "Sin sesión activa", is_error: true };
            try {
              // Atlas backend (ex analizar_video_o_audio) → /v1/media/analyze con
              // JWT; fallback n8n. Video largo puede venir 202 → poleamos el job.
              if (USE_ATLAS_BACKEND) {
                const bkRes = await atlasBackendFetch("/v1/media/analyze", {
                  user_id: user.id,
                  asset_ids: ids,
                  urls,
                  prompt,
                });
                const pick = (out: unknown): string =>
                  out &&
                  typeof out === "object" &&
                  typeof (out as { response?: unknown }).response === "string"
                    ? (out as { response: string }).response
                    : out
                      ? JSON.stringify(out)
                      : "";
                if (bkRes.status === 202) {
                  const pending = (await bkRes.json().catch(() => ({}))) as {
                    job_id?: string;
                  };
                  if (!pending.job_id)
                    return { content: "Analyze devolvió 202 sin job_id", is_error: true };
                  const settled = await pollJobUntilSettled(pending.job_id, {
                    timeoutMs: 5 * 60 * 1000,
                  });
                  if (!settled)
                    return { content: "El análisis tardó demasiado (timeout).", is_error: true };
                  if (settled.status === "failed")
                    return {
                      content: settled.userMessage || "El análisis falló.",
                      is_error: true,
                    };
                  return { content: pick(settled.output) };
                }
                if (!bkRes.ok) {
                  if (bkRes.status === 402) {
                    return {
                      content:
                        "No te alcanzan los créditos para analizar este medio. Sumá créditos o actualizá tu plan.",
                      is_error: true,
                    };
                  }
                  const txt = await bkRes.text().catch(() => "");
                  return {
                    content: `Analyze HTTP ${bkRes.status}: ${txt.slice(0, 500)}`,
                    is_error: true,
                  };
                }
                return { content: pick(await bkRes.json().catch(() => null)) };
              }
              const res = await fetch(
                "https://ai-agency-dt7d.onrender.com/webhook/analizar_video_o_audio",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: GENERATE_AUTH_TOKEN,
                  },
                  body: JSON.stringify({
                    user_id: user.id,
                    asset_ids: ids,
                    urls,
                    prompt,
                  }),
                },
              );
              if (!res.ok) {
                const txt = await res.text().catch(() => "");
                return {
                  content: `Webhook HTTP ${res.status}: ${txt.slice(0, 500)}`,
                  is_error: true,
                };
              }
              const txt = await res.text();
              // The webhook returns { response: "<gemini answer>" }. Fall back to
              // the raw payload if the shape is different so we never lose info.
              try {
                const json = JSON.parse(txt);
                if (
                  json &&
                  typeof json === "object" &&
                  typeof (json as any).response === "string"
                ) {
                  return { content: (json as any).response };
                }
                return {
                  content:
                    typeof json === "string" ? json : JSON.stringify(json),
                };
              } catch {
                return { content: txt };
              }
            } catch (err: any) {
              return {
                content: `Error contactando webhook: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "generate_transcript": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const force = input?.force === true;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `Clip ${clipId} no encontrado en la timeline`,
                is_error: true,
              };
            const isTranscribable =
              clip.type === "video" ||
              clip.type === "audio" ||
              (clip.type === "ai-generated" && clip.aiStatus === "ready");
            if (!isTranscribable) {
              return {
                content: `El clip "${clip.name}" (tipo=${clip.type}, status=${clip.aiStatus ?? "n/a"}) no se puede transcribir. Soporta video, audio, o ai-generated con audio listo.`,
                is_error: true,
              };
            }
            // Cache hit: skip the network round-trip entirely. Sólo si el
            // transcript es del archivo ACTUAL del clip.
            const cacheado = transcriptVigente(clip);
            if (
              !force &&
              cacheado &&
              Array.isArray(cacheado.words) &&
              cacheado.words.length > 0
            ) {
              toolMessage("Transcript ya cacheado", Captions);
              const wordCount = cacheado.words.filter(
                (w) => w.type === "word",
              ).length;
              return {
                content: JSON.stringify({
                  clipId,
                  cached: true,
                  language: cacheado.language_code,
                  wordCount,
                  durationSecs:
                    cacheado.audio_duration_secs ??
                    clip.sourceDuration ??
                    clip.duration,
                  preview: cacheado.text?.slice(0, 240) ?? "",
                  hint: "Usá get_clip_transcript para leer SÓLO la ventana visible (post-trim) o get_timeline_transcript para todo el proyecto.",
                }),
              };
            }
            toolMessage(
              "Transcribiendo clip",
              Captions,
              "transcribiendo — puede tardar ~1 min",
            );
            // Resolve a fresh signed URL the same way the subtitle generator
            // does — prefer the persisted storagePath, fall back to in-memory src.
            let url: string | null = null;
            if (clip.storagePath) {
              try {
                url = await getAssetSignedUrl(clip.storagePath);
              } catch {}
            }
            if (
              !url &&
              typeof clip.src === "string" &&
              /^https?:\/\//.test(clip.src)
            ) {
              url = clip.src;
            }
            if (!url)
              return {
                content: "No pudimos obtener la URL del clip para transcribir.",
                is_error: true,
              };
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (!user?.id)
              return { content: "No hay una sesión activa.", is_error: true };
            try {
              const tr = await requestTranscript(url, user.id);
              if (!tr.ok) return { content: tr.message, is_error: true };
              const transcript = tr.transcript;
              // Persist on the source clip so future calls (and split halves)
              // can read it without re-transcribing.
              setClips((prev) =>
                prev.map((c) =>
            c.id === clipId
              ? { ...c, transcript: marcarTranscript(transcript, clip.storagePath) }
              : c,
          ),
              );
              // Sync el ref AHORA para que un autocut inmediato (mismo turno del
              // agente) vea el transcript sin esperar el re-render de React.
              clipsRef.current = clipsRef.current.map((c) =>
                c.id === clipId
                  ? { ...c, transcript: marcarTranscript(transcript, clip.storagePath) }
                  : c,
              );
              const wordCount = transcript.words.filter(
                (w) => w.type === "word",
              ).length;
              return {
                content: JSON.stringify({
                  clipId,
                  cached: false,
                  language: transcript.language_code,
                  wordCount,
                  durationSecs:
                    transcript.audio_duration_secs ??
                    clip.sourceDuration ??
                    clip.duration,
                  preview: transcript.text?.slice(0, 240) ?? "",
                  hint: "Para leer la ventana visible llamá get_clip_transcript; para todo el proyecto, get_timeline_transcript.",
                }),
              };
            } catch (err: any) {
              return {
                content: `Error contactando webhook de transcripción: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "get_clip_transcript": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `Clip ${clipId} no encontrado en la timeline`,
                is_error: true,
              };
            const t = transcriptVigente(clip);
            if (!t || !Array.isArray(t.words)) {
              return {
                content: `El clip "${clip.name}" todavía no tiene transcript guardado. Llamá primero a generate_transcript con clipId="${clipId}".`,
                is_error: true,
              };
            }
            toolMessage("Leyendo transcript del clip", Captions);
            const offset = clip.mediaOffset ?? 0;
            const winStart = offset;
            const winEnd = offset + clip.duration;
            // Pure word entries inside the visible window. We use overlap
            // (`end > winStart && start < winEnd`) so a word straddling the
            // edge still appears — the agent generally cares "did the user
            // say X within this trim", not strict containment.
            const visible = t.words.filter(
              (w) =>
                w &&
                w.type === "word" &&
                typeof w.start === "number" &&
                typeof w.end === "number" &&
                w.end > winStart &&
                w.start < winEnd,
            );
            // Remap to timeline-relative seconds so the agent can quote
            // timestamps that match what list_timeline_clips returns.
            const timelineWords = visible.map((w) => ({
              word: w.text,
              start: Math.max(
                clip.startTime,
                clip.startTime + (w.start - offset),
              ),
              end: Math.min(
                clip.startTime + clip.duration,
                clip.startTime + (w.end - offset),
              ),
            }));
            const text = timelineWords
              .map((w) => w.word)
              .join(" ")
              .replace(/\s+/g, " ")
              .trim();
            return {
              content: JSON.stringify({
                clipId,
                name: clip.name,
                startTime: clip.startTime,
                endTime: clip.startTime + clip.duration,
                durationVisible: clip.duration,
                language: t.language_code,
                text,
                words: timelineWords,
              }),
            };
          }
          case "get_timeline_transcript": {
            const includeMuted = input?.includeMuted === true;
            toolMessage("Leyendo transcript de la timeline", Captions);
            // Only audible-source clips contribute. Subtitles are derivative
            // (text clips) and would double-count, so we skip them.
            const candidates = clips
              .filter(
                (c) =>
                  c.type === "video" ||
                  c.type === "audio" ||
                  (c.type === "ai-generated" && c.aiStatus === "ready"),
              )
              .filter((c) => includeMuted || (c.volume ?? 1) > 0)
              .sort((a, b) => a.startTime - b.startTime);
            const segments: any[] = [];
            const missing: string[] = [];
            for (const clip of candidates) {
              const t = transcriptVigente(clip);
              if (!t || !Array.isArray(t.words)) {
                missing.push(clip.id);
                continue;
              }
              const offset = clip.mediaOffset ?? 0;
              const winStart = offset;
              const winEnd = offset + clip.duration;
              const visible = t.words.filter(
                (w) =>
                  w &&
                  w.type === "word" &&
                  typeof w.start === "number" &&
                  typeof w.end === "number" &&
                  w.end > winStart &&
                  w.start < winEnd,
              );
              if (visible.length === 0) continue;
              const timelineWords = visible.map((w) => ({
                word: w.text,
                start: Math.max(
                  clip.startTime,
                  clip.startTime + (w.start - offset),
                ),
                end: Math.min(
                  clip.startTime + clip.duration,
                  clip.startTime + (w.end - offset),
                ),
              }));
              const text = timelineWords
                .map((w) => w.word)
                .join(" ")
                .replace(/\s+/g, " ")
                .trim();
              segments.push({
                clipId: clip.id,
                clipName: clip.name,
                startTime: clip.startTime,
                endTime: clip.startTime + clip.duration,
                text,
                words: timelineWords,
              });
            }
            const fullText = segments
              .map((s) => s.text)
              .join(" ")
              .replace(/\s+/g, " ")
              .trim();
            return {
              content: JSON.stringify({
                segments,
                fullText,
                missingTranscriptClipIds: missing,
                hint:
                  missing.length > 0
                    ? `Hay ${missing.length} clip(s) audibles sin transcript todavía. Llamá generate_transcript en cada uno si querés cobertura completa.`
                    : undefined,
              }),
            };
          }
          case "list_generated_images": {
            const limit = Math.max(1, Math.min(20, Number(input?.limit) || 12));
            // Take newest first; skip still-loading entries without a URL.
            const ready = imageGenResults
              .filter((r) => !!r.signedUrl)
              .slice(0, limit);
            if (ready.length === 0) {
              return {
                content:
                  "El proyecto todavía no tiene imágenes generadas (o ninguna terminó de cargar).",
              };
            }
            toolMessage(
              `Leyendo ${ready.length} imagen(es) generada(s)`,
              ImageIcon,
            );
            // Fetch prompt + created_at for these IDs so the agent can pick by
            // semantic meaning / recency without having to analyze every image
            // visually. One query, tolerant to missing rows.
            const metaById = new Map<
              string,
              { prompt?: string; createdAt?: string }
            >();
            try {
              // Fetch en lotes para no exceder el largo de URL del gateway REST
              // con muchos resultados (HTTP 400). Ver utils/supabaseChunkedIn.
              const metaRows = await fetchInChunks<any>(
                ready.map((r) => r.id),
                (chunk) =>
                  supabase
                    .from("imagenes_generadas")
                    .select("id, prompt, created_at")
                    .in("id", chunk),
              );
              if (Array.isArray(metaRows)) {
                for (const row of metaRows as Array<{
                  id: string;
                  prompt: string | null;
                  created_at: string | null;
                }>) {
                  metaById.set(row.id, {
                    prompt: row.prompt || undefined,
                    createdAt: row.created_at || undefined,
                  });
                }
              }
            } catch (err) {
              console.warn(
                "[list_generated_images] failed to fetch metadata",
                err,
              );
            }
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Imágenes generadas con IA guardadas en el proyecto (${ready.length} de ${imageGenResults.length}). ` +
                  `IDs en orden (más nuevas primero): ${ready.map((r) => r.id).join(", ")}.`,
              },
            ];
            for (const r of ready) {
              let base64 = "";
              try {
                base64 = await urlToBase64Jpeg(r.signedUrl!);
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `imagen ${r.id}: no se pudo cargar (${err?.message || err})`,
                });
                continue;
              }
              const meta = metaById.get(r.id);
              const header = [`imagen_id=${r.id}`];
              if (meta?.createdAt) header.push(`generada=${meta.createdAt}`);
              if (meta?.prompt)
                header.push(`prompt=${JSON.stringify(meta.prompt)}`);
              blocks.push({ type: "text", text: header.join(" | ") });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: base64,
                },
              });
            }
            return { content: blocks };
          }
          case "import_generated_image_as_asset": {
            const genId: string | undefined =
              typeof input?.generatedImageId === "string"
                ? input.generatedImageId
                : undefined;
            if (!genId)
              return { content: "Falta generatedImageId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            const result = imageGenResults.find((r) => r.id === genId);
            if (result && !result.signedUrl)
              return {
                content: "La imagen todavía no terminó de generarse.",
                is_error: true,
              };
            let signedUrl = result?.signedUrl;
            let fileName = result?.fileName;
            if (!result) {
              // No es de este proyecto: puede ser una creación del CHAT (generate_image,
              // extraer_frames, generate_video), que vive en `imagenes_generadas` de la
              // empresa. Es el único camino para meter en el proyecto lo que el agente
              // generó desde el chat
              // (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente).
              const { preloadGeneratedImages } = await import(
                "../utils/preloadGeneratedImages"
              );
              const [delChat] = await preloadGeneratedImages([genId], companyId);
              if (!delChat?.signedUrl)
                return {
                  content: `No hay una imagen generada con id ${genId}: ni en este proyecto ni en las creaciones del chat.`,
                  is_error: true,
                };
              signedUrl = delChat.signedUrl;
              fileName = delChat.fileName;
            }
            if (!signedUrl)
              return {
                content: "La imagen todavía no terminó de generarse.",
                is_error: true,
              };
            toolMessage("Importando imagen generada como asset", ImageIcon);
            try {
              const resp = await fetch(signedUrl);
              const blob = await resp.blob();
              const ext = (fileName?.split(".").pop() || "jpg").toLowerCase();
              const file = new File([blob], fileName || `ai-${Date.now()}.${ext}`, {
                type: blob.type || "image/jpeg",
              });
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: asset.type,
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo importar la imagen: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "use_product": {
            const productId: string | undefined =
              typeof input?.productId === "string"
                ? input.productId
                : undefined;
            if (!productId)
              return { content: "Falta productId", is_error: true };
            const cid = companyId ?? imageGenCompanyId;
            if (!cid || !brandProfile?.id)
              return {
                content: "No hay marca/empresa cargada en el proyecto.",
                is_error: true,
              };
            toolMessage("Importando producto del catálogo", Package);
            try {
              const catalog = await fetchUserProductsByBrand(
                cid,
                brandProfile.id,
              );
              const product = catalog.find((p) => p.id === productId);
              if (!product)
                return {
                  content: `No encontré un producto con id ${productId} en el catálogo de la marca. Usá list_products para ver los ids válidos.`,
                  is_error: true,
                };
              const assetId = await importProductToImageAsset(product);
              if (!assetId)
                return {
                  content: "No se pudo importar el producto como asset.",
                  is_error: true,
                };
              const blocks: any[] = [
                {
                  type: "text",
                  text:
                    `Producto importado como asset del proyecto. ` +
                    `assetId=${assetId} | nombre=${product.name}. ` +
                    `Pasá este assetId como firstFrameAssetId / lastFrameAssetId / refAssetIds en prefill_ai_scene o prefill_ai_image. ` +
                    `A continuación la imagen inline para que confirmes que es el producto correcto.`,
                },
              ];
              try {
                const base64 = await urlToBase64Jpeg(product.imageUrl);
                blocks.push({
                  type: "image",
                  source: {
                    type: "base64",
                    media_type: "image/jpeg",
                    data: base64,
                  },
                });
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `(no se pudo cargar la imagen inline: ${err?.message || err})`,
                });
              }
              return { content: blocks };
            } catch (err: any) {
              return {
                content: `No se pudo usar el producto: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "extract_clip_frame_as_asset": {
            const clipId: string | undefined =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const which: "first" | "last" =
              input?.which === "last" ? "last" : "first";
            if (!clipId) return { content: "Falta clipId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            const clip = clips.find((c) => c.id === clipId);
            if (!clip)
              return {
                content: `No hay clip con id ${clipId}`,
                is_error: true,
              };
            if (clip.type !== "video" && clip.type !== "ai-generated") {
              return { content: "El clip no es de video", is_error: true };
            }
            toolMessage(
              `Extrayendo ${which === "first" ? "primer" : "último"} frame del clip`,
              Film,
            );
            try {
              // Resolve at native resolution. For AI-generated clips we already
              // have full-res first/last frames cached. For regular video clips
              // we deliberately SKIP the clipThumbnails strip (low-res, meant
              // for the timeline) and always capture from the video source so
              // the extracted frame is sharp enough to reuse as an asset.
              let frameDataUrl: string | null = null;
              if (clip.type === "ai-generated") {
                frameDataUrl =
                  which === "first"
                    ? clip.aiFirstFrame || null
                    : clip.aiLastFrame || null;
              }
              if (!frameDataUrl && clip.src) {
                frameDataUrl = await new Promise<string | null>((resolve) => {
                  const video = document.createElement("video");
                  video.crossOrigin = "anonymous";
                  video.src = clip.src!;
                  video.muted = true;
                  video.preload = "auto";
                  const onLoaded = () => {
                    const seekTo =
                      which === "first"
                        ? 0.05
                        : Math.max(0, (video.duration || 0) - 0.05);
                    video.currentTime = seekTo;
                  };
                  const onSeeked = () => {
                    try {
                      const canvas = document.createElement("canvas");
                      canvas.width = video.videoWidth || 720;
                      canvas.height = video.videoHeight || 1280;
                      const ctx = canvas.getContext("2d");
                      if (!ctx) return resolve(null);
                      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                      resolve(canvas.toDataURL("image/jpeg", 0.92));
                    } catch {
                      resolve(null);
                    }
                  };
                  video.addEventListener("loadedmetadata", onLoaded, {
                    once: true,
                  });
                  video.addEventListener("seeked", onSeeked, { once: true });
                  video.addEventListener("error", () => resolve(null), {
                    once: true,
                  });
                  // Reloj: si loadedmetadata/seeked nunca disparan (video no
                  // seekable, CORS, compu dormida) degradamos a null en vez de
                  // colgar el turno del agente para siempre. resolve() es
                  // idempotente, así que un evento tardío no rompe nada.
                  setTimeout(() => {
                    console.warn(
                      "[video-agent][timeout] ⏱ extracción de frame local venció (20s) → null",
                    );
                    resolve(null);
                  }, 20_000);
                });
              }
              if (!frameDataUrl)
                return {
                  content: "No se pudo extraer el frame",
                  is_error: true,
                };
              const res = await fetch(frameDataUrl);
              const blob = await res.blob();
              const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
              const file = new File(
                [blob],
                `frame-${clip.id}-${which}-${Date.now()}.${ext}`,
                { type: blob.type || "image/jpeg" },
              );
              const blobUrl = URL.createObjectURL(blob);
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: "image",
                  clipId,
                  which,
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo extraer el frame: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "list_available_voices": {
            toolMessage("Consultando voces IA disponibles", Mic);
            const { data, error } = await supabase
              .from("voces")
              .select("id, nombre, sexo, descripcion, idioma, acento")
              .order("nombre");
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            let list = (data || []) as any[];
            // Filtros TOLERANTES a propósito: el agente manda 'es'/'español' o
            // 'femenino'/'femenina' según cómo lo razonó, pero la tabla `voces`
            // guarda 'Español'/'Inglés' y 'Femenina'/'Masculina'. Con igualdad
            // estricta el filtro devolvía [] y el agente concluía "no hay voces
            // en español". Normalizamos acento+caso y mapeamos sinónimos.
            const normText = (s: unknown) =>
              String(s ?? "")
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "")
                .trim();
            if (input?.language) {
              const LANG_ALIASES: Record<string, string> = {
                es: "espanol",
                esp: "espanol",
                "es-ar": "espanol",
                spanish: "espanol",
                castellano: "espanol",
                en: "ingles",
                eng: "ingles",
                english: "ingles",
              };
              const q = normText(input.language);
              const target = LANG_ALIASES[q] ?? q;
              list = list.filter((v) => {
                const lang = normText(v.idioma);
                return (
                  lang !== "" &&
                  (lang === target ||
                    lang.startsWith(target) ||
                    target.startsWith(lang))
                );
              });
            }
            if (input?.sex) {
              // 'femenino'/'femenina' → 'fem'; 'masculino'/'masculina' → 'mas'.
              const sexRoot = (s: unknown) => {
                const n = normText(s);
                if (n.startsWith("fem") || n === "f" || n === "mujer")
                  return "fem";
                if (
                  n.startsWith("mas") ||
                  n === "m" ||
                  n === "hombre" ||
                  n === "varon"
                )
                  return "mas";
                return n;
              };
              const target = sexRoot(input.sex);
              list = list.filter((v) => sexRoot(v.sexo) === target);
            }
            return { content: JSON.stringify(list) };
          }
          case "get_brand_context": {
            toolMessage("Leyendo contexto de marca", BookOpen);
            // Only return the brand_profile attached to this project — the
            // free-form memoria the agent edits via update_brand_context. We do
            // NOT include the company-level config (media_agent_*) because that
            // serves a different purpose.
            if (!brandProfile) {
              return { content: JSON.stringify({ brand_profile: null }) };
            }
            return {
              content: JSON.stringify({
                brand_profile: {
                  id: brandProfile.id,
                  name: brandProfile.name,
                  context: brandProfile.context,
                  colors: brandProfile.colors,
                  fonts: brandProfile.fonts,
                },
              }),
            };
          }
          case "update_brand_context": {
            const text =
              typeof input?.context === "string" ? input.context.trim() : "";
            const mode: "append" | "replace" =
              input?.mode === "replace" ? "replace" : "append";
            if (!text)
              return { content: "Falta el texto del contexto", is_error: true };
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const existing = brandProfile.context || "";
            const nextContext =
              mode === "replace"
                ? text
                : existing
                  ? `${existing.trimEnd()}\n\n${text}`
                  : text;
            toolMessage(
              `${mode === "replace" ? "Reemplazando" : "Guardando en"} memoria de ${brandProfile.name}`,
              Brain,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                context: nextContext,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, context: nextContext });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                context: nextContext,
              }),
            };
          }
          case "update_brand_colors": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const incoming = Array.isArray(input?.colors)
              ? (input.colors as unknown[])
                  .filter(
                    (c): c is string =>
                      typeof c === "string" && c.trim().length > 0,
                  )
                  .map((c) => c.trim().toLowerCase())
              : [];
            if (incoming.length === 0)
              return { content: "Falta el array `colors`.", is_error: true };
            const mode: "add" | "remove" | "replace" =
              input?.mode === "replace"
                ? "replace"
                : input?.mode === "remove"
                  ? "remove"
                  : "add";
            const current = brandProfile.colors.map((c) => c.toLowerCase());
            let nextColors: string[];
            if (mode === "replace") {
              // Dedupe while preserving order.
              const seen = new Set<string>();
              nextColors = incoming.filter((c) =>
                seen.has(c) ? false : (seen.add(c), true),
              );
            } else if (mode === "remove") {
              const drop = new Set(incoming);
              nextColors = current.filter((c) => !drop.has(c));
            } else {
              // add: merge, dedupe, keep existing first.
              const seen = new Set(current);
              nextColors = [...current];
              for (const c of incoming)
                if (!seen.has(c)) {
                  seen.add(c);
                  nextColors.push(c);
                }
            }
            toolMessage(
              `${mode === "replace" ? "Reemplazando" : mode === "remove" ? "Quitando" : "Agregando"} colores en ${brandProfile.name}`,
              Palette,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                colors: nextColors,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, colors: nextColors });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                colors: nextColors,
              }),
            };
          }
          case "update_brand_fonts": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const VALID_ROLES: BrandFontRole[] = [
              "antetitulo",
              "titulo",
              "subtitulo",
              "cuerpo",
              "detalles",
            ];
            const assignments = (
              input && typeof input === "object" ? input.assignments : null
            ) as Record<string, unknown> | null;
            if (!assignments || typeof assignments !== "object") {
              return {
                content:
                  "Falta el objeto `assignments` con la asignación rol → familia.",
                is_error: true,
              };
            }
            // Map every input role; null/empty clears the role, a string sets
            // it to the canonical family name. Unknown roles or unknown
            // families are reported back without halting the rest.
            const known = new Map(
              TEXT_FONT_OPTIONS.map((o) => [o.family.toLowerCase(), o.family]),
            );
            const sets: Partial<Record<BrandFontRole, string>> = {};
            const clears: BrandFontRole[] = [];
            const ignoredFonts: Array<{ role: string; value: string }> = [];
            const ignoredRoles: string[] = [];
            for (const [rawRole, rawValue] of Object.entries(assignments)) {
              if (!VALID_ROLES.includes(rawRole as BrandFontRole)) {
                ignoredRoles.push(rawRole);
                continue;
              }
              const role = rawRole as BrandFontRole;
              if (
                rawValue === null ||
                rawValue === undefined ||
                rawValue === ""
              ) {
                clears.push(role);
                continue;
              }
              if (typeof rawValue !== "string") {
                ignoredFonts.push({ role, value: String(rawValue) });
                continue;
              }
              const canonical = known.get(rawValue.trim().toLowerCase());
              if (!canonical) {
                ignoredFonts.push({ role, value: rawValue });
                continue;
              }
              sets[role] = canonical;
            }
            if (Object.keys(sets).length === 0 && clears.length === 0) {
              return {
                content: `No se aplicó ninguna asignación válida. Roles válidos: ${VALID_ROLES.join(", ")}. Familias disponibles: ${TEXT_FONT_OPTIONS.map((o) => o.family).join(", ")}`,
                is_error: true,
              };
            }
            const mode: "merge" | "replace" =
              input?.mode === "replace" ? "replace" : "merge";
            let nextFonts: BrandFontRoles;
            if (mode === "replace") {
              // Replace wipes everything not present in `sets`.
              nextFonts = { ...sets };
            } else {
              nextFonts = { ...brandProfile.fonts, ...sets };
              for (const role of clears) delete nextFonts[role];
            }
            toolMessage(
              `${mode === "replace" ? "Redefiniendo" : "Actualizando"} fuentes de ${brandProfile.name}`,
              Type,
            );
            const { error } = await supabase
              .from("brand_profiles")
              .update({
                fonts: nextFonts,
                updated_at: new Date().toISOString(),
              })
              .eq("id", brandProfile.id);
            if (error)
              return { content: `Error: ${error.message}`, is_error: true };
            setBrandProfile({ ...brandProfile, fonts: nextFonts });
            return {
              content: JSON.stringify({
                ok: true,
                mode,
                brand_profile_id: brandProfile.id,
                fonts: nextFonts,
                ignored_fonts: ignoredFonts,
                ignored_roles: ignoredRoles,
              }),
            };
          }
          case "get_brand_assets": {
            if (!brandProfile)
              return {
                content: "El proyecto no tiene brand_profile asociado.",
                is_error: true,
              };
            const tipoFilter: BrandAssetTipo | undefined =
              input?.tipo === "logo" || input?.tipo === "element"
                ? input.tipo
                : undefined;
            toolMessage(
              tipoFilter
                ? `Leyendo ${tipoFilter === "logo" ? "logos" : "elementos"} de la marca`
                : "Leyendo imágenes de la marca",
              ImageIcon,
            );
            const list = await fetchBrandAssets(brandProfile.id, tipoFilter);
            if (list.length === 0) {
              return {
                content:
                  tipoFilter === "logo"
                    ? "La marca todavía no tiene logos cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba."
                    : tipoFilter === "element"
                      ? "La marca todavía no tiene elementos visuales cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba."
                      : "La marca todavía no tiene logos ni elementos cargados. Si los necesitás, llamá a open_brand_panel(tab='imagenes') para que el usuario los suba.",
              };
            }
            // Inline each image as base64 so Claude sees them visually,
            // mirroring list_generated_images. Headers carry the metadata
            // the agent needs to pick the right one (id + tipo + name + dims).
            const blocks: any[] = [
              {
                type: "text",
                text:
                  `Imágenes guardadas en la marca "${brandProfile.name}" (${list.length} item${list.length === 1 ? "" : "s"}). ` +
                  `IDs en orden: ${list.map((a) => a.id).join(", ")}. ` +
                  "Para usar una de estas imágenes como referencia / primer / último frame de una escena IA o como adjunto de imagen IA, primero llamá a import_brand_asset_to_project con su id para obtener un assetId del proyecto.",
              },
            ];
            for (const a of list) {
              const url = await getBrandAssetSignedUrl(a.storage_path);
              if (!url) {
                blocks.push({
                  type: "text",
                  text: `brand_asset id=${a.id} tipo=${a.tipo} name=${JSON.stringify(a.name)}: no se pudo firmar URL`,
                });
                continue;
              }
              let base64 = "";
              try {
                base64 = await urlToBase64Jpeg(url);
              } catch (err: any) {
                blocks.push({
                  type: "text",
                  text: `brand_asset id=${a.id}: no se pudo cargar (${err?.message || err})`,
                });
                continue;
              }
              const header = [
                `brand_asset_id=${a.id}`,
                `tipo=${a.tipo}`,
                `name=${JSON.stringify(a.name || "")}`,
              ];
              if (a.width && a.height)
                header.push(`dim=${a.width}x${a.height}`);
              blocks.push({ type: "text", text: header.join(" | ") });
              blocks.push({
                type: "image",
                source: {
                  type: "base64",
                  media_type: "image/jpeg",
                  data: base64,
                },
              });
            }
            return { content: blocks };
          }
          case "import_brand_asset_to_project": {
            const brandAssetId: string | undefined =
              typeof input?.brandAssetId === "string"
                ? input.brandAssetId.trim()
                : undefined;
            if (!brandAssetId)
              return { content: "Falta brandAssetId", is_error: true };
            if (!projectId || !companyId)
              return { content: "Project not loaded", is_error: true };
            // Fetch the brand-assets row directly (avoids depending on the
            // local brandAssets cache, which only loads when the Imágenes
            // tab is opened).
            const { data: row, error: fetchErr } = await supabase
              .from("brand_assets")
              .select("*")
              .eq("id", brandAssetId)
              .maybeSingle();
            if (fetchErr || !row) {
              return {
                content: `No hay brand_asset con id ${brandAssetId}.`,
                is_error: true,
              };
            }
            const ba = row as BrandAsset;
            const url = await getBrandAssetSignedUrl(ba.storage_path);
            if (!url)
              return {
                content: "No se pudo firmar URL del brand asset.",
                is_error: true,
              };
            toolMessage("Importando imagen de marca como asset", ImageIcon);
            try {
              const resp = await fetch(url);
              const blob = await resp.blob();
              const ext = (
                ba.name?.split(".").pop() ||
                blob.type.split("/")[1] ||
                "jpg"
              ).toLowerCase();
              const file = new File(
                [blob],
                ba.name || `brand-${ba.tipo}-${Date.now()}.${ext}`,
                { type: blob.type || ba.mime_type || "image/jpeg" },
              );
              const {
                data: { user },
              } = await supabase.auth.getUser();
              const asset = await uploadProjectAsset({
                file,
                projectId,
                companyId,
                userId: user?.id,
              });
              const blobUrl = URL.createObjectURL(blob);
              setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
              return {
                content: JSON.stringify({
                  assetId: asset.id,
                  name: asset.name,
                  type: "image",
                  from_brand_asset_id: ba.id,
                  tipo: ba.tipo,
                }),
              };
            } catch (err: any) {
              return {
                content: `No se pudo importar el brand asset: ${err?.message || err}`,
                is_error: true,
              };
            }
          }
          case "open_brand_panel": {
            const tab = input?.tab;
            if (
              tab !== "memoria" &&
              tab !== "fuentes" &&
              tab !== "colores" &&
              tab !== "imagenes"
            ) {
              return {
                content:
                  "tab debe ser 'memoria' | 'fuentes' | 'colores' | 'imagenes'.",
                is_error: true,
              };
            }
            setActivePanel("brand");
            setBrandTab(tab);
            const labels: Record<typeof tab, string> = {
              memoria: "Memoria",
              fuentes: "Fuentes",
              colores: "Colores",
              imagenes: "Imágenes",
            };
            toolMessage(`Abriendo panel de Marca · ${labels[tab]}`, Palette);
            return {
              content: `Panel de Marca abierto en la pestaña "${labels[tab]}". Avisale al usuario qué tiene que hacer ahí.`,
            };
          }
          case "get_ui_context": {
            toolMessage("Leyendo estado de la UI", Compass);
            const selectedClip = selectedClipId
              ? clips.find((c) => c.id === selectedClipId)
              : null;
            let panelFields: Record<string, any> | null = null;
            if (activePanel === "ai-scene") {
              panelFields = {
                mode: sceneMode,
                prompt: scenePrompt,
                duration: sceneDuration,
                quality: sceneQuality,
                resolution: sceneResolution,
                dialogue: sceneMode === "personas" ? sceneDialogue : undefined,
                speechSpeed:
                  sceneMode === "personas" ? sceneSpeechSpeed : undefined,
                voiceId: sceneMode === "personas" ? audioAiVoiceId : undefined,
                voiceName:
                  sceneMode === "personas" ? audioAiVoiceName : undefined,
                refMode: sceneRefMode,
                firstFrameAssetId: sceneFirstFrameAssetId || null,
                lastFrameAssetId: sceneLastFrameAssetId || null,
                refAssetIds: sceneRefAssetIds,
                editingClipId: editingClipId || null,
              };
            } else if (activePanel === "ai-audio") {
              panelFields = {
                type: audioAiType,
                prompt: audioAiPrompt,
                duration: audioAiDuration,
                voiceId: audioAiType === "voice" ? audioAiVoiceId : undefined,
                voiceName:
                  audioAiType === "voice" ? audioAiVoiceName : undefined,
                speechSpeed:
                  audioAiType === "voice" ? audioAiSpeechSpeed : undefined,
                instrumentalOnly:
                  audioAiType === "music" ? audioAiInstrumentalOnly : undefined,
                editingClipId: editingClipId || null,
              };
            } else if (activePanel === "ai-image-gen") {
              panelFields = {
                prompt: imageGenPrompt,
                attachedAssetIds: imageGenAttachedAssetIds,
                aspectRatio,
              };
            }
            return {
              content: JSON.stringify({
                activePanel,
                aspectRatio,
                selectedClipId: selectedClipId || null,
                selectedClip: selectedClip
                  ? {
                      id: selectedClip.id,
                      type: selectedClip.type,
                      name: selectedClip.name,
                      startTime: selectedClip.startTime,
                      duration: selectedClip.duration,
                      trackIndex: selectedClip.trackIndex ?? 0,
                      aiPrompt: selectedClip.aiPrompt,
                      aiStatus: selectedClip.aiStatus,
                    }
                  : null,
                panelFields,
              }),
            };
          }
          case "finalizar_borrador": {
            toolMessage("Pasando el clip a 1080p", Sparkles);
            const r = await pasarClipA1080p(String(input?.clipId ?? ""));
            // Con `ok` y `mensaje`, el clip cambió mientras se generaba y el
            // 1080p quedó en Archivos: no decir que se reemplazó.
            return r.ok
              ? {
                  content:
                    r.mensaje ??
                    "Listo: el clip ahora es el 1080p de la misma toma; el borrador se reemplazó en la timeline.",
                }
              : { content: r.mensaje ?? "No se pudo pasar a 1080p.", is_error: true };
          }
          case "prefill_ai_scene": {
            const sm: "general" | "personas" =
              input?.sceneMode === "personas" ? "personas" : "general";
            // En modo personas la UI tiene dos sub-modos con bindings
            // distintos:
            //   · static (default, UGC testimonial): UN textarea labeled
            //     "Diálogo de la escena" que escribe sobre `scenePrompt`.
            //     `sceneDialogue` queda oculto y NO se envía como `dialogo`
            //     a la API — el texto spoken viaja en `prompt`.
            //   · motion (más cinematográfico): DOS textareas — visual
            //     `scenePrompt` + spoken `sceneDialogue`.
            // El agente puede pasar `personType` explícito; si no, lo
            // inferimos: si vino sólo `dialogue` (típico de static) o
            // sólo `prompt` lo tratamos como static. Si vinieron AMBOS,
            // motion. Esto evita el bug clásico donde el agente llena los
            // dos pensando en motion pero la UI estaba en static y mostraba
            // la descripción visual donde el usuario espera el diálogo.
            let pt: "static" | "motion" = "static";
            if (sm === "personas") {
              if (
                input?.personType === "motion" ||
                input?.personType === "static"
              ) {
                pt = input.personType;
              } else {
                const hasPrompt =
                  typeof input?.prompt === "string" &&
                  input.prompt.trim().length > 0;
                const hasDialogue =
                  typeof input?.dialogue === "string" &&
                  input.dialogue.trim().length > 0;
                pt = hasPrompt && hasDialogue ? "motion" : "static";
              }
            }
            // Si hay un ai-generated clip seleccionado y todavía no se
            // disparó la generación (draft o error), tratamos esta llamada
            // como edición — así el agente puede "fijar" un prefill mal
            // armado sin crear un clip nuevo paralelo. Si está generando o
            // ya está ready, no editamos en sitio (sería destructivo).
            const selectedAi = selectedClipId
              ? clips.find(
                  (c) => c.id === selectedClipId && c.type === "ai-generated",
                )
              : null;
            const editable =
              selectedAi &&
              (selectedAi.aiStatus === "draft" ||
                selectedAi.aiStatus === "error" ||
                !selectedAi.aiStatus);
            setEditingClipId(editable ? selectedAi!.id : null);
            setSceneMode(sm);
            if (sm === "personas") setScenePersonType(pt);
            setSceneMejora(null);
            if (sm === "personas" && pt === "static") {
              // Static dialog: el textarea único está atado a `scenePrompt`
              // y la UI lo etiqueta como "Diálogo". Routeamos lo que el
              // agente haya mandado (prefiriendo `dialogue`) hacia ese
              // campo, y limpiamos `sceneDialogue` para que no quede
              // residuo de un personType anterior.
              const spoken =
                typeof input?.dialogue === "string" && input.dialogue
                  ? input.dialogue
                  : input?.prompt || "";
              setScenePrompt(String(spoken));
              setSceneDialogue("");
              if (typeof input?.duration === "number")
                setSceneDuration(input.duration);
            } else {
              setScenePrompt(String(input?.prompt || ""));
              if (typeof input?.duration === "number")
                setSceneDuration(input.duration);
            }
            if (input?.aspectRatio) setAspectRatio(input.aspectRatio);
            // "rapido" ya no existe en el Estudio — si el agente lo manda
            // (prompt viejo), cae a Estándar.
            if (input?.quality === "estandar" || input?.quality === "profesional")
              setSceneQuality(input.quality);
            else if (input?.quality === "rapido") setSceneQuality("estandar");
            if (
              input?.resolution === "480p" ||
              input?.resolution === "720p" ||
              input?.resolution === "1080p"
            )
              setSceneResolution(input.resolution);
            if (sm === "personas") {
              // Sólo motion usa el campo dedicado de diálogo. En static ya
              // routeamos el spoken text a `scenePrompt` arriba.
              if (pt === "motion" && input?.dialogue)
                setSceneDialogue(String(input.dialogue));
              if (typeof input?.speechSpeed === "number")
                setSceneSpeechSpeed(input.speechSpeed);
              if (input?.voiceId) {
                const vid = String(input.voiceId);
                setAudioAiVoiceId(vid);
                const cached = audioAiVoices.find((x) => x.id === vid);
                if (cached) {
                  setAudioAiVoiceName(cached.nombre);
                } else {
                  supabase
                    .from("voces")
                    .select("nombre")
                    .eq("id", vid)
                    .maybeSingle()
                    .then(({ data }) => {
                      if (data?.nombre)
                        setAudioAiVoiceName(String(data.nombre));
                    });
                }
              }
            }
            // El modo de referencia se decide con el input CRUDO, no con los
            // ids ya resueltos: se sabe al instante y deja que el panel abra
            // con los slots correctos a la vista, en vez de arrancar en "sin
            // referencia" y saltar cuando las imágenes terminan de resolverse.
            const wantsFrames = !!(
              input?.firstFrameAssetId || input?.lastFrameAssetId
            );
            const wantsRefs = !!(
              Array.isArray(input?.refAssetIds) && input.refAssetIds.length > 0
            );
            let optimisticRefMode: "none" | "reference" | "frames" =
              wantsFrames
                ? "frames"
                : wantsRefs
                  ? "reference"
                  : "none";
            // `setSceneRefMode` sincroniza `sceneRefModeRef` en el mismo tick,
            // así que las escrituras tardías de más abajo pueden compararlo
            // sin depender de que haya corrido ningún efecto.
            // Mismo mapeo que hace el setter: el guard de más abajo compara
            // `sceneRefModeRef.current` contra esta variable.
            if (optimisticRefMode === "none") optimisticRefMode = "reference";
            setSceneRefMode(optimisticRefMode);
            // Token de ESTE prefill: si el Director dispara otro mientras
            // resolvemos, el viejo no debe escribir nada.
            const prefillToken = ++scenePrefillTokenRef.current;
            // Abrimos el panel ACÁ, antes de resolver las referencias.
            // Resolver una referencia no es una consulta: descarga la imagen
            // generada y la vuelve a subir como asset del proyecto, y pueden
            // ser varias. Con el `setActivePanel` al final, el prompt quedaba
            // escrito en el estado desde el primer instante pero el panel no
            // aparecía hasta que terminaban todas las subidas — el usuario
            // leía "te cargué el panel" y no veía nada durante segundos.
            // Los slots se llenan solos cuando cada referencia aterriza.
            setActivePanel("ai-scene");
            {
              const keys: string[] = [];
              if (input?.prompt) keys.push("scene-prompt");
              if (sm === "personas" && input?.dialogue)
                keys.push("scene-dialogue");
              if (input?.firstFrameAssetId) keys.push("scene-firstframe");
              if (input?.lastFrameAssetId) keys.push("scene-lastframe");
              if (wantsRefs) keys.push("scene-refs");
              triggerPanelBounce(keys);
            }
            // Las tres resoluciones son independientes entre sí: en paralelo.
            const [resolvedFirstFrame, resolvedLastFrame, resolvedSceneRefs] =
              await Promise.all([
                input?.firstFrameAssetId
                  ? resolveRefIdToAssetId(String(input.firstFrameAssetId))
                  : Promise.resolve(null),
                input?.lastFrameAssetId
                  ? resolveRefIdToAssetId(String(input.lastFrameAssetId))
                  : Promise.resolve(null),
                resolveRefIdList(input?.refAssetIds),
              ]);
            // El panel estuvo abierto y editable durante toda la resolución,
            // así que estas escrituras pueden llegar tarde a una escena que ya
            // no es la que estaban armando. Dos formas de quedar obsoleto:
            //   · otro prefill del Director pisó a éste,
            //   · el usuario cambió el modo de referencia a mano.
            // En cualquiera de los dos casos mandan ellos: descartamos y se lo
            // decimos al Director, que si no seguiría el plan creyendo que las
            // referencias quedaron cargadas.
            const supersededByPrefill =
              scenePrefillTokenRef.current !== prefillToken;
            const userChangedRefMode =
              sceneRefModeRef.current !== optimisticRefMode;
            const stale = supersededByPrefill || userChangedRefMode;
            if (!stale) {
              if (resolvedFirstFrame)
                setSceneFirstFrameAssetId(resolvedFirstFrame);
              if (resolvedLastFrame)
                setSceneLastFrameAssetId(resolvedLastFrame);
              if (Array.isArray(input?.refAssetIds))
                setSceneRefAssetIds(resolvedSceneRefs);
              // Si NINGUNA referencia pudo resolverse, el modo que fijamos
              // arriba quedó prometiendo slots que nunca se van a llenar.
              // Corregimos al valor real.
              {
                let corrected: "none" | "reference" | "frames" | null = null;
                if (wantsFrames && !resolvedFirstFrame && !resolvedLastFrame) {
                  corrected =
                    resolvedSceneRefs.length > 0 ? "reference" : "none";
                } else if (
                  !wantsFrames &&
                  wantsRefs &&
                  resolvedSceneRefs.length === 0
                ) {
                  corrected = "none";
                }
                if (corrected) setSceneRefMode(corrected);
              }
            }
            if (stale) {
              const motivo = supersededByPrefill
                ? "se disparó otro prefill mientras se cargaban"
                : "el usuario cambió el modo de referencia a mano mientras se cargaban";
              toolMessage(
                "Escena pre-llenada (referencias descartadas)",
                Sparkles,
              );
              return {
                content: `Panel de escena IA abierto y pre-llenado, PERO las imágenes de referencia no se aplicaron: ${motivo}. No asumas que están cargadas — verificá con get_ui_context antes de seguir.`,
              };
            }
            toolMessage("Panel de escena IA pre-llenado", Sparkles);
            return {
              content:
                "Panel de escena IA abierto y pre-llenado. El usuario debe revisar y apretar Generar.",
            };
          }
          case "prefill_ai_audio": {
            const t: "voice" | "music" | "sfx" =
              input?.type === "music"
                ? "music"
                : input?.type === "sfx"
                  ? "sfx"
                  : "voice";
            setEditingClipId(null);
            setAudioAiType(t);
            setAudioAiPrompt(String(input?.prompt || ""));
            if (typeof input?.duration === "number")
              setAudioAiDuration(
                t === "music"
                  ? acotarDuracionDeMusica(input.duration)
                  : t === "sfx"
                    ? acotarDuracionDeEfecto(input.duration)
                    : // La voz conserva el piso de 2 s que antes ponía el schema.
                      Math.max(2, input.duration),
              );
            if (t === "voice") {
              if (input?.voiceId) {
                const vid = String(input.voiceId);
                setAudioAiVoiceId(vid);
                const cached = audioAiVoices.find((x) => x.id === vid);
                if (cached) {
                  setAudioAiVoiceName(cached.nombre);
                } else {
                  // Voice cache may not be loaded yet (it's lazy). Fetch the
                  // single voice name so the user sees the label immediately.
                  supabase
                    .from("voces")
                    .select("nombre")
                    .eq("id", vid)
                    .maybeSingle()
                    .then(({ data }) => {
                      if (data?.nombre)
                        setAudioAiVoiceName(String(data.nombre));
                    });
                }
              }
              if (typeof input?.speechSpeed === "number")
                setAudioAiSpeechSpeed(input.speechSpeed);
            } else {
              setAudioAiInstrumentalOnly(!!input?.instrumentalOnly);
            }
            setActivePanel("ai-audio");
            if (input?.prompt) triggerPanelBounce(["audio-prompt"]);
            toolMessage("Panel de audio IA pre-llenado", Music);
            return {
              content:
                "Panel de audio IA abierto y pre-llenado. El usuario debe revisar y apretar Generar.",
            };
          }
          case "prefill_ai_image": {
            setImageGenPrompt(String(input?.prompt || ""));
            if (input?.aspectRatio) setAspectRatio(input.aspectRatio);
            // Mismo criterio que `prefill_ai_scene`: el panel se abre ANTES de
            // resolver las referencias (que descargan y vuelven a subir cada
            // imagen). Los adjuntos se agregan cuando terminan.
            setActivePanel("ai-image-gen");
            {
              const keys: string[] = [];
              if (input?.prompt) keys.push("image-prompt");
              if (
                Array.isArray(input?.refAssetIds) &&
                input.refAssetIds.length > 0
              )
                keys.push("image-attach");
              triggerPanelBounce(keys);
            }
            const imgToken = ++imagePrefillTokenRef.current;
            // Foto de los adjuntos al arrancar: si al terminar difieren, el
            // usuario los tocó con el panel ya abierto y manda él.
            const attachedBefore = imageGenAttachedAssetIdsRef.current;
            const resolvedRefs = await resolveRefIdList(input?.refAssetIds);
            const imgSuperseded = imagePrefillTokenRef.current !== imgToken;
            const current = imageGenAttachedAssetIdsRef.current;
            const userChangedAttachments =
              current.length !== attachedBefore.length ||
              current.some((id, i) => id !== attachedBefore[i]);
            const imgStale = imgSuperseded || userChangedAttachments;
            if (!imgStale && Array.isArray(input?.refAssetIds))
              setImageGenAttachedAssetIds(resolvedRefs);
            toolMessage("Panel de imagen IA pre-llenado", ImageIcon);
            if (imgStale) {
              const motivo = imgSuperseded
                ? "se disparó otro prefill mientras se cargaban"
                : "el usuario cambió los adjuntos a mano mientras se cargaban";
              return {
                content: `Panel de imagen IA abierto y pre-llenado, PERO las referencias no se adjuntaron: ${motivo}. No asumas que están cargadas — verificá con get_ui_context antes de seguir.`,
              };
            }
            return { content: "Panel de imagen IA abierto y pre-llenado." };
          }
          case "prefill_edit_generated_image": {
            const id =
              typeof input?.generatedImageId === "string"
                ? input.generatedImageId.trim()
                : "";
            const editPrompt =
              typeof input?.editPrompt === "string"
                ? input.editPrompt.trim()
                : "";
            if (!id)
              return { content: "Falta generatedImageId", is_error: true };
            if (!editPrompt)
              return { content: "Falta editPrompt", is_error: true };
            const exists = imageGenResults.some((r) => r.id === id);
            if (!exists)
              return {
                content: `Imagen generada ${id} no encontrada en este proyecto.`,
                is_error: true,
              };
            setActivePanel("ai-image-gen");
            setSlotPreview(null);
            setSlotPreviewEditing(false);
            // Stash the prompt so the reset effect on imageGenDetailId applies
            // it instead of clearing edit mode.
            pendingGenEditPromptRef.current = editPrompt;
            if (imageGenDetailId === id) {
              // The id didn't change, so the reset effect won't fire — apply
              // the edit state directly here.
              pendingGenEditPromptRef.current = null;
              setImageGenEditing(true);
              setImageGenEditPrompt(editPrompt);
            } else {
              setImageGenDetailId(id);
            }
            triggerPanelBounce(["gen-edit-prompt"]);
            toolMessage("Imagen generada abierta para editar", Pencil);
            return {
              content:
                "Panel de edición de imagen generada abierto y pre-llenado.",
            };
          }
          case "prefill_edit_scene_frame": {
            const which: "first" | "last" =
              input?.which === "last" ? "last" : "first";
            const editPrompt =
              typeof input?.editPrompt === "string"
                ? input.editPrompt.trim()
                : "";
            if (!editPrompt)
              return { content: "Falta editPrompt", is_error: true };
            const assetId =
              which === "first"
                ? sceneFirstFrameAssetId
                : sceneLastFrameAssetId;
            if (!assetId) {
              return {
                content: `No hay ${which === "first" ? "primer" : "último"} frame cargado. Cargá uno primero (prefill_ai_scene con firstFrameAssetId/lastFrameAssetId).`,
                is_error: true,
              };
            }
            setSlotPreview({ assetId, target: { kind: which } });
            setSlotPreviewEditing(true);
            setSlotPreviewEditPrompt(editPrompt);
            setActivePanel("ai-image-gen");
            triggerPanelBounce(["slot-edit-prompt"]);
            toolMessage(
              `${which === "first" ? "Primer" : "Último"} frame abierto para editar`,
              Pencil,
            );
            return {
              content:
                "Frame abierto en modo preview con prompt de edición pre-llenado.",
            };
          }
          case "nudge_image_slot": {
            const allowed = new Set([
              "scene-firstframe",
              "scene-lastframe",
              "scene-ref",
              "image-attach",
            ]);
            const slots = Array.isArray(input?.slots)
              ? input.slots.map(String).filter((s: string) => allowed.has(s))
              : [];
            if (slots.length === 0)
              return { content: "No valid slots provided.", is_error: true };
            triggerPanelNudge(slots);
            toolMessage("Resaltando slot de imagen", Hand);
            return { content: `Saltito disparado en: ${slots.join(", ")}.` };
          }
          case "add_text_clip": {
            const textVal = String(input?.text || DEFAULT_TEXT_CLIP.text);
            // La familia tiene que ser una que el RENDER tenga (catálogo o
            // subida por la marca). Antes pasaba cualquier string: el agente le
            // puso "Barlow Condensed" a Kiero, el preview la tomó de la copia
            // instalada en su compu y el MP4 salió con el fallback. Se rechaza
            // con la lista para que el agente elija otra en el mismo turno.
            // Ver ADR-2026-10-30-fuentes-fuera-del-catalogo.
            let familiaPedida: string | undefined;
            if (
              typeof input?.fontFamily === "string" &&
              input.fontFamily.trim()
            ) {
              const conocida = familiaQueElRenderTiene(
                input.fontFamily,
                customFontFamilies,
              );
              if (!conocida) {
                const subidas =
                  customFontFamilies.length > 0
                    ? ` Las que subió la marca: ${customFontFamilies.join(", ")}.`
                    : "";
                return {
                  content:
                    `La fuente "${input.fontFamily}" no está disponible: el video exportado ` +
                    `saldría con otra tipografía. No se agregó el texto. Usá una del catálogo: ` +
                    `${TEXT_FONT_OPTIONS.map((o) => o.family).join(", ")}.${subidas}`,
                  is_error: true,
                };
              }
              familiaPedida = conocida;
            }
            // Map the legacy `style` enum to concrete typography defaults so
            // older agent calls keep working. New calls can also pass any of
            // the explicit fields (fontFamily, fontSizePct, fontWeight,
            // fontStyle, textColor, textAlign, textXPct, textYPct).
            const styleDefaults: Partial<{
              fontFamily: string;
              fontSizePct: number;
              fontWeight: "normal" | "bold";
              fontStyle: "normal" | "italic";
              textAlign: "left" | "center" | "right";
            }> = (() => {
              switch (input?.style) {
                case "titulo":
                  return {
                    fontSizePct: 12,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                case "subtitulo":
                  return {
                    fontSizePct: 8,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                case "cuerpo":
                  return {
                    fontSizePct: 5,
                    fontWeight: "normal",
                    textAlign: "center",
                  };
                case "animado":
                  return {
                    fontSizePct: 10,
                    fontWeight: "bold",
                    textAlign: "center",
                  };
                default:
                  return {};
              }
            })();
            const id = addTextClip({
              text: textVal,
              startTime:
                typeof input?.startTime === "number"
                  ? input.startTime
                  : undefined,
              duration:
                typeof input?.duration === "number"
                  ? input.duration
                  : undefined,
              fontFamily: familiaPedida ?? styleDefaults.fontFamily,
              fontSizePct:
                typeof input?.fontSizePct === "number"
                  ? input.fontSizePct
                  : styleDefaults.fontSizePct,
              fontWeight:
                input?.fontWeight === "bold"
                  ? "bold"
                  : (styleDefaults.fontWeight ?? undefined),
              fontStyle: input?.fontStyle === "italic" ? "italic" : undefined,
              textColor:
                typeof input?.textColor === "string"
                  ? input.textColor
                  : undefined,
              textAlign: ["left", "center", "right"].includes(input?.textAlign)
                ? input.textAlign
                : styleDefaults.textAlign,
              textXPct:
                typeof input?.textXPct === "number"
                  ? input.textXPct
                  : undefined,
              textYPct:
                typeof input?.textYPct === "number"
                  ? input.textYPct
                  : undefined,
            });
            toolMessage(`Texto agregado: "${textVal.slice(0, 40)}"`, Type);
            return { content: JSON.stringify({ clipId: id }) };
          }
          case "crear_animacion": {
            const brief =
              typeof input?.brief === "string" ? input.brief.trim() : "";
            if (brief.length < 3)
              return { content: "brief required", is_error: true };
            const tipo: "superpuesta" | "escena" =
              input?.tipo === "escena" ? "escena" : "superpuesta";
            const assetIds: string[] = Array.isArray(input?.assetIds)
              ? input.assetIds.map(String).slice(0, MAX_ASSETS)
              : [];
            const assets = await resolverAssetsDeAnimacion(assetIds);
            const marca = marcaDelProyectoParaAnimacion();
            toolMessage("Creando la animación", Sparkles);
            const nombrePedido =
              typeof input?.nombre === "string" && input.nombre.trim()
                ? input.nombre.trim().slice(0, 60)
                : "";
            const { id, startTime } = crearPlaceholderDeAnimacion({
              nombre: nombrePedido || "Animación gráfica",
              tipo,
              startTime:
                typeof input?.startTime === "number" ? input.startTime : undefined,
            });
            const r = await pedirAnimacionPorCodigo({
              brief,
              tipo,
              formato: formatoDeAnimacion(aspectRatio),
              duracionSeg:
                typeof input?.duracionSeg === "number"
                  ? acotarDuracion(input.duracionSeg, 4)
                  : undefined,
              marca,
              assets,
            });
            if (!r.ok) {
              descartarPlaceholderDeAnimacion(id);
              return {
                content: JSON.stringify({ error: r.codigo, detalle: r.mensaje }),
                is_error: true,
              };
            }
            const nombre = nombrePedido || nombreDeAnimacion(r.resumen, brief);
            const { duracion, enTimeline } = completarAnimacionGrafica(id, {
              codigo: r.codigo,
              resumen: r.resumen,
              tipo,
              assets,
              marca,
              duracionSeg: r.duracionSeg,
              nombre,
            });
            if (!enTimeline) {
              // Sin clip no hay nada que capturar ni un clipId que ofrecer.
              return {
                content: JSON.stringify({
                  nombre,
                  resumen: r.resumen,
                  duracionSeg: duracion,
                  enTimeline: false,
                  mensaje:
                    "El clip «Generando…» se borró o se deshizo mientras se escribía la animación, así que no quedó en la timeline. Se guardó en Archivos → Animaciones: decile al usuario que la agregue desde ahí si la quiere.",
                }),
              };
            }
            return await resultadoConCapturas(id, {
              clipId: id,
              nombre,
              resumen: r.resumen,
              duracionSeg: duracion,
              startTime,
              version: 1,
            });
          }
          case "modificar_animacion": {
            const clip = clips.find(
              (c) =>
                c.id === input?.clipId &&
                c.type === "animation" &&
                !!c.animationCodigo,
            );
            if (!clip || !clip.animationCodigo)
              return {
                content:
                  "No hay una animación por código con ese clipId (list_timeline_clips las muestra con sus versiones).",
                is_error: true,
              };
            const cambios =
              typeof input?.cambios === "string" ? input.cambios.trim() : "";
            if (!cambios) return { content: "cambios required", is_error: true };
            const anim = clip.animationCodigo;
            const actual = versionActual(anim);
            // Un cambio por vez en la misma animación: dos que terminan juntos leen
            // el mismo `clipsRef` (todavía sin el render del primero) y el segundo
            // pisaba la versión del primero en la timeline y en la biblioteca.
            if (animacionesEnCambioRef.current.has(clip.id)) {
              return {
                content:
                  "Ya hay un cambio en curso sobre esta animación. Esperá a que termine y pedí el siguiente sobre esa versión.",
                is_error: true,
              };
            }
            animacionesEnCambioRef.current.add(clip.id);
            try {
              toolMessage("Cambiando la animación", Sparkles);
              const r = await pedirAnimacionPorCodigo({
                brief: cambios,
                tipo: anim.tipo,
                formato: formatoDeAnimacion(aspectRatio),
                duracionSeg:
                  typeof input?.duracionSeg === "number"
                    ? acotarDuracion(input.duracionSeg, clip.duration)
                    : acotarDuracion(clip.duration, actual.duracionSeg),
                marca: anim.marca,
                assets: anim.assets,
                codigoActual: actual.codigo,
                cambios,
              });
              if (!r.ok) {
                return {
                  content: JSON.stringify({ error: r.codigo, detalle: r.mensaje }),
                  is_error: true,
                };
              }
              // El pedido tarda 15 a 40 s: mientras tanto el usuario pudo volver
              // a otra versión desde el panel, recortar el clip o borrarlo. Se
              // relee el clip VIGENTE y la versión nueva se suma a SU historial
              // (nada de lo que pasó en el medio se pisa); `clip`/`anim` son la
              // foto de antes del pedido.
              const vigente = clipsRef.current.find(
                (c) => c.id === clip.id && !!c.animationCodigo,
              );
              if (!vigente?.animationCodigo) {
                return {
                  content:
                    "El clip de la animación ya no está en la timeline (se borró o se deshizo mientras se generaba el cambio). No se aplicó nada.",
                  is_error: true,
                };
              }
              const animVigente = vigente.animationCodigo;
              // La duración del clip sólo cambia si el agente la pidió: el
              // usuario puede haberlo recortado a mano.
              const duracion =
                typeof input?.duracionSeg === "number"
                  ? acotarDuracion(input.duracionSeg, vigente.duration)
                  : vigente.duration;
              const version = {
                codigo: r.codigo,
                resumen: r.resumen || cambios,
                fecha: new Date().toISOString(),
                duracionSeg: duracion,
              };
              // UN solo valor para la timeline y la biblioteca: si cada una agregaba
              // la versión sobre su propia base (la del updater y la del ref),
              // con otro cambio en cola podían quedar con historiales distintos.
              // Entre leer el ref y el set no hay await, así que la base es la
              // vigente.
              const nuevaAnim = agregarVersion(animVigente, version);
              reiniciarSandbox(clip.id);
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clip.id && c.animationCodigo
                    ? { ...c, ...ventanaDeVersion(duracion), animationCodigo: nuevaAnim }
                    : c,
                ),
              );
              // La guardada en Archivos → Animaciones sigue a la última versión.
              if (animVigente.bibliotecaId) {
                setAnimacionesGraficas((prev) =>
                  actualizarAnimacion(prev, animVigente.bibliotecaId!, {
                    animacion: nuevaAnim,
                  }),
                );
                void capturarMiniaturaDeAnimacion(
                  clip.id,
                  animVigente.bibliotecaId,
                  duracion,
                );
              }
              setSelectedClipId(clip.id);
              setIsPlaying(false);
              setCurrentTime(vigente.startTime + Math.min(0.1, duracion / 2));
              return await resultadoConCapturas(clip.id, {
                clipId: clip.id,
                resumen: r.resumen,
                duracionSeg: duracion,
                version: nuevaAnim.versiones.length,
              });
            } finally {
              animacionesEnCambioRef.current.delete(clip.id);
            }
          }
          case "volver_a_version_animacion": {
            const clip = clips.find(
              (c) =>
                c.id === input?.clipId &&
                c.type === "animation" &&
                !!c.animationCodigo,
            );
            if (!clip || !clip.animationCodigo)
              return {
                content: "No hay una animación por código con ese clipId.",
                is_error: true,
              };
            const anim = clip.animationCodigo;
            const indice = Number(input?.version) - 1;
            if (
              !Number.isInteger(indice) ||
              indice < 0 ||
              indice >= anim.versiones.length
            ) {
              return {
                content: JSON.stringify({
                  error: `version tiene que estar entre 1 y ${anim.versiones.length}`,
                  versiones: describirVersiones(anim),
                }),
                is_error: true,
              };
            }
            toolMessage("Volviendo a una versión", RotateCcw);
            const nuevaAnim = volverAVersion(anim, indice);
            reiniciarSandbox(clip.id);
            setClips((prev) =>
              prev.map((c) =>
                c.id === clip.id
                  ? {
                      ...c,
                      ...ventanaDeVersion(versionActual(nuevaAnim).duracionSeg),
                      animationCodigo: nuevaAnim,
                    }
                  : c,
              ),
            );
            if (anim.bibliotecaId) {
              setAnimacionesGraficas((prev) =>
                actualizarAnimacion(prev, anim.bibliotecaId!, { animacion: nuevaAnim }),
              );
              void capturarMiniaturaDeAnimacion(clip.id, anim.bibliotecaId, clip.duration);
            }
            setSelectedClipId(clip.id);
            setIsPlaying(false);
            setCurrentTime(clip.startTime + Math.min(0.1, clip.duration / 2));
            return await resultadoConCapturas(clip.id, {
              clipId: clip.id,
              version: indice + 1,
              resumen: anim.versiones[indice]!.resumen,
              duracionSeg: clip.duration,
            });
          }
          case "poner_transicion": {
            const derecho = clips.find((c) => c.id === input?.clipId);
            if (
              !derecho ||
              (derecho.type !== "video" && derecho.type !== "ai-generated")
            ) {
              return {
                content: "clipId tiene que ser un clip de video de la timeline.",
                is_error: true,
              };
            }
            // El mismo criterio que dibuja el botón de transición entre dos
            // clips de la pista: el anterior INMEDIATO en orden de inicio
            // (mismo trackIndex), y sólo si está pegado (hasta 0,15 s de
            // hueco) o solapado. Buscar "el de menor hueco" entre todos los
            // anteriores elegía, con solapes, uno más viejo que no es con el
            // que el botón ni el render arman la transición.
            const izquierdo = findAdjacentVideoPairs(clips).find(
              (p) => p.right.id === derecho.id,
            )?.left;
            if (!izquierdo) {
              return {
                content:
                  "Ese clip no tiene otro clip de video pegado antes en la misma pista, así que no hay dónde poner la transición. Acomodalos con move_clip para que el anterior termine donde éste empieza.",
                is_error: true,
              };
            }
            const tipo = String(input?.tipo ?? "");
            if (tipo === "ninguna") {
              setClips((prev) =>
                prev.map((c) => {
                  if (c.id !== derecho.id || !c.transitionIn) return c;
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { transitionIn, ...rest } = c;
                  return rest as TimelineClip;
                }),
              );
              toolMessage("Transición quitada", Scissors);
              return { content: JSON.stringify({ clipId: derecho.id, transicion: null }) };
            }
            const TIPOS: TransitionType[] = [
              "crossfade", "fade-to-black", "slide", "slide-left", "slide-up", "slide-down",
              "zoom", "reveal", "diapositivas", "stretch-horizontal", "stretch-vertical",
              "light-leak", "plastic", "distorsion", "vhs", "vintage", "glitch",
            ];
            if (!TIPOS.includes(tipo as TransitionType)) {
              return { content: `tipo desconocido: ${tipo}`, is_error: true };
            }
            // Las duraciones típicas por tipo, las mismas del panel.
            const porDefecto =
              tipo === "diapositivas" ? 0.8
              : tipo === "light-leak" || tipo === "distorsion" ? 0.5
              : tipo === "plastic" ? 0.6
              : tipo === "vhs" || tipo === "vintage" ? 1
              : 0.3;
            const durTr =
              typeof input?.duracionSeg === "number"
                ? Math.max(0.1, Math.min(3, input.duracionSeg))
                : porDefecto;
            setClips((prev) =>
              prev.map((c) =>
                c.id === derecho.id
                  ? { ...c, transitionIn: { type: tipo as TransitionType, durationSec: durTr } }
                  : c,
              ),
            );
            toolMessage(`Transición ${tipo}`, ArrowLeftRight);
            return {
              content: JSON.stringify({
                clipId: derecho.id,
                entre: [izquierdo.name, derecho.name],
                transicion: { tipo, duracionSeg: durTr },
              }),
            };
          }
          case "animar_entrada_salida": {
            const clip = clips.find((c) => c.id === input?.clipId);
            if (
              !clip ||
              (clip.type !== "text" && clip.type !== "animation" && clip.type !== "image")
            ) {
              return {
                content:
                  "clipId tiene que ser un clip de texto, de animación o de imagen (para video, poner_transicion).",
                is_error: true,
              };
            }
            const validar = (id: unknown, campo: string): string | undefined => {
              if (id === undefined) return undefined;
              if (id === "none") return "none";
              const p = getMotionPreset(String(id));
              if (!p) throw new Error(`${campo}: preset desconocido «${String(id)}»`);
              if (p.scope === "text" && clip.type !== "text") {
                throw new Error(
                  `${campo}: «${p.id}» es sólo para clips de texto; para una animación o una imagen usá fade, slide-*, scale, pop o blur.`,
                );
              }
              return p.id;
            };
            const entrada = validar(input?.entrada, "entrada");
            const salida = validar(input?.salida, "salida");
            const dur = (v: unknown) =>
              typeof v === "number" ? Math.max(0.1, Math.min(3, v)) : undefined;
            const patch: Partial<TimelineClip> = {
              ...(entrada !== undefined ? { enterAnimation: entrada } : {}),
              ...(salida !== undefined ? { exitAnimation: salida } : {}),
              ...(dur(input?.duracionEntradaSeg) !== undefined
                ? { enterDurationSec: dur(input?.duracionEntradaSeg) }
                : {}),
              ...(dur(input?.duracionSalidaSeg) !== undefined
                ? { exitDurationSec: dur(input?.duracionSalidaSeg) }
                : {}),
            };
            if (Object.keys(patch).length === 0) {
              return { content: "Mandá entrada, salida o alguna duración.", is_error: true };
            }
            // Un subtítulo comparte estilo con su tanda: el mismo helper que
            // usa el panel de Texto propaga el cambio al grupo.
            setClips((prev) =>
              clip.type === "text"
                ? patchClipsWithSubtitleSync(prev, clip.id, patch)
                : prev.map((c) => (c.id === clip.id ? { ...c, ...patch } : c)),
            );
            toolMessage("Entrada y salida ajustadas", Sparkles);
            return {
              content: JSON.stringify({
                clipId: clip.id,
                entrada: patch.enterAnimation ?? clip.enterAnimation ?? "none",
                salida: patch.exitAnimation ?? clip.exitAnimation ?? "none",
                duracionEntradaSeg: patch.enterDurationSec ?? clip.enterDurationSec ?? 0.5,
                duracionSalidaSeg: patch.exitDurationSec ?? clip.exitDurationSec ?? 0.5,
              }),
            };
          }
          case "quitar_fondo": {
            const clip = clips.find((c) => c.id === input?.clipId);
            if (!clip || clip.type !== "image") {
              return {
                content: "clipId tiene que ser una capa de imagen de la timeline.",
                is_error: true,
              };
            }
            if (!clip.storagePath) {
              return {
                content: "Esa imagen todavía no terminó de subir: probá en unos segundos.",
                is_error: true,
              };
            }
            toolMessage("Quitando el fondo…", Eraser);
            const resultado = await quitarFondoDelClip(clip.id);
            if (!resultado) {
              return { content: "No se pudo quitar el fondo de esa imagen.", is_error: true };
            }
            return {
              content: JSON.stringify({
                clipId: clip.id,
                assetId: resultado.assetId,
                nota: resultado.aplicado
                  ? "El clip ya muestra la imagen sin fondo; el original sigue en Archivos."
                  : "La imagen del clip cambió mientras se recortaba: el recorte quedó en Archivos y el clip no se tocó.",
              }),
            };
          }
          case "generar_subtitulos": {
            const clip = clips.find((c) => c.id === input?.clipId);
            if (
              !clip ||
              (clip.type !== "video" && clip.type !== "audio" && clip.type !== "ai-generated")
            ) {
              return {
                content: "clipId tiene que ser un clip de video o de audio de la timeline.",
                is_error: true,
              };
            }
            if (clip.type === "ai-generated" && clip.aiStatus && clip.aiStatus !== "ready") {
              return { content: "Ese clip todavía se está generando.", is_error: true };
            }
            const existentes = clips.filter(
              (c) => c.type === "text" && c.isSubtitle && c.linkedClipId === clip.id,
            );
            if (existentes.length > 0 && input?.reemplazar !== true) {
              return {
                content: JSON.stringify({
                  yaTenia: existentes.length,
                  mensaje:
                    "Ese clip ya tiene subtítulos. Si querés regenerarlos, llamá de nuevo con reemplazar=true; si querés cambiarles el estilo, es desde el panel de Texto.",
                }),
              };
            }
            if (subtitulosDelAgenteRef.current || subtitlesLoadingRef.current) {
              return { content: "Ya hay una generación de subtítulos en curso.", is_error: true };
            }
            // Los subtítulos viejos NO se borran acá: si firmar, transcribir o
            // esperar falla, el usuario se quedaría sin los que ya tenía. Se
            // sacan recién cuando la tanda nueva se aplica (el efecto de
            // auto-aplicar lee este ref); un error lo limpia y quedan intactos.
            subtitulosAReemplazarRef.current =
              existentes.length > 0
                ? { fuenteId: clip.id, ids: new Set(existentes.map((c) => c.id)) }
                : null;
            toolMessage("Generando subtítulos", Captions);
            const resultado = await new Promise<
              { ok: true; lineas: number } | { ok: false; error: string }
            >((resolve) => {
              const t = setTimeout(() => {
                if (subtitulosDelAgenteRef.current === resolver) {
                  subtitulosDelAgenteRef.current = null;
                  // El reemplazo pendiente NO se limpia: si la transcripción
                  // termina tarde, se aplica y saca las viejas (limpiarlo dejaría
                  // dos tandas). Otra no puede arrancar hasta que ésta termine
                  // (`subtitlesLoadingRef`).
                  resolve({
                    ok: false,
                    error:
                      "La transcripción tarda más de dos minutos. Si termina, los subtítulos aparecen solos en el clip; no la pidas de nuevo.",
                  });
                }
              }, 120_000);
              const resolver = (r: { ok: true; lineas: number } | { ok: false; error: string }) => {
                clearTimeout(t);
                resolve(r);
              };
              subtitulosDelAgenteRef.current = resolver;
              void openSubtitlesModalForClip(clip.id);
            });
            if (!resultado.ok) {
              return { content: JSON.stringify({ error: resultado.error }), is_error: true };
            }
            return {
              content: JSON.stringify({
                clipId: clip.id,
                lineas: resultado.lineas,
                mensaje:
                  "Subtítulos agregados como una tanda enganchada al clip. Se editan desde el panel de Texto.",
              }),
            };
          }
          case "add_asset_to_timeline": {
            if (!input?.assetId)
              return { content: "assetId required", is_error: true };
            const [asset] = await fetchAssetsByIds([String(input.assetId)]);
            if (!asset || (asset.type !== "video" && asset.type !== "audio")) {
              return {
                content: "Asset no encontrado o no soportado",
                is_error: true,
              };
            }
            await addAssetToTimeline(asset, {
              startTime:
                typeof input?.startTime === "number"
                  ? input.startTime
                  : undefined,
            });
            toolMessage(`Asset agregado: ${asset.name}`, Plus);
            return { content: `Asset "${asset.name}" agregado.` };
          }
          case "move_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) =>
              prev.map((c) =>
                c.id === input.clipId
                  ? {
                      ...c,
                      startTime:
                        typeof input.startTime === "number"
                          ? Math.max(0, input.startTime)
                          : c.startTime,
                      trackIndex:
                        typeof input.trackIndex === "number"
                          ? Math.max(0, Math.floor(input.trackIndex))
                          : c.trackIndex,
                    }
                  : c,
              ),
            );
            toolMessage("Clip movido", ArrowLeftRight);
            return { content: "Clip movido." };
          }
          case "trim_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) =>
              prev.map((c) => {
                if (c.id !== input.clipId) return c;
                const mediaOffset =
                  typeof input.mediaOffset === "number"
                    ? Math.max(0, input.mediaOffset)
                    : c.mediaOffset;
                let duration = Math.max(0.1, Number(input.duration) || c.duration);
                // Con material (video, audio, animación por código) no se puede
                // mostrar más de lo que queda después del recorte: igual que la
                // manija derecha. Una animación no se estira: se alarga con
                // modificar_animacion
                // (ADR-2026-09-26-las-animaciones-se-recortan-como-un-video).
                if (c.sourceDuration !== undefined) {
                  const queda =
                    (c.sourceDuration - (mediaOffset ?? 0)) / velocidadDeClip(c);
                  duration = Math.max(0.1, Math.min(duration, queda));
                }
                return { ...c, duration, mediaOffset };
              }),
            );
            toolMessage("Clip recortado", Scissors);
            return { content: "Clip ajustado." };
          }
          case "split_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const splitAt = Number(input.splitAt);
            const MIN_GAP = 0.1;
            const newId = generateId();
            let ok = false;
            setClips((prev) => {
              const clip = prev.find((c) => c.id === input.clipId);
              if (
                !clip ||
                splitAt < MIN_GAP ||
                splitAt > clip.duration - MIN_GAP
              )
                return prev;
              ok = true;
              const next: TimelineClip[] = [];
              for (const c of prev) {
                if (c.id !== clip.id) {
                  next.push(c);
                  continue;
                }
                next.push({ ...c, duration: splitAt });
                next.push({
                  ...c,
                  id: newId,
                  startTime: c.startTime + splitAt,
                  duration: c.duration - splitAt,
                  mediaOffset: (c.mediaOffset || 0) + splitAt,
                });
              }
              return next;
            });
            if (ok) {
              toolMessage("Clip dividido", Scissors);
              return { content: JSON.stringify({ newClipId: newId }) };
            }
            return { content: "splitAt inválido", is_error: true };
          }
          case "delete_clip": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            setClips((prev) => prev.filter((c) => c.id !== input.clipId));
            toolMessage("Clip eliminado", Trash2);
            return { content: "Clip eliminado." };
          }
          case "autocut_interview": {
            const clipId =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            try {
              const liveClips = clipsRef.current;
              const clip = liveClips.find((c) => c.id === clipId);
              console.log("[Atlas][autocut][DIAG] entrando", {
                clipId,
                totalClips: liveClips.length,
                clipFound: !!clip,
                clipType: clip?.type,
                hasTranscript: !!clip?.transcript,
                wordCount: clip?.transcript?.words?.length ?? 0,
                input,
              });
              if (!clip) {
                console.warn("[Atlas][autocut] clip NO encontrado:", clipId);
                return {
                  content: `Clip ${clipId} no encontrado en la timeline`,
                  is_error: true,
                };
              }
              const t = transcriptVigente(clip);
              if (!t || !Array.isArray(t.words)) {
                console.warn(
                  "[Atlas][autocut] el clip NO tiene transcript (¿estado viejo de React?):",
                  { clipId, hasTranscript: !!t, isArray: Array.isArray(t?.words) },
                );
                return {
                  content: `El clip "${clip.name}" no tiene transcript. Llamá primero a generate_transcript con clipId="${clipId}".`,
                  is_error: true,
                };
              }
              const removeFillers = input?.remove_filler_words !== false;
              const removeSilences = input?.remove_silences !== false;
              if (!removeFillers && !removeSilences) {
                console.warn("[Atlas][autocut] ambos removes en false");
                return {
                  content:
                    "Dejá al menos uno de remove_filler_words o remove_silences en true.",
                  is_error: true,
                };
              }

              const offset = clip.mediaOffset ?? 0;
              const winStart = offset;
              const winEnd = offset + clip.duration;
              const words = t.words
                .filter(
                  (w) =>
                    w &&
                    w.type === "word" &&
                    typeof w.start === "number" &&
                    typeof w.end === "number",
                )
                .map((w) => ({ text: w.text, start: w.start, end: w.end }));
              console.log("[Atlas][autocut][DIAG] palabras", {
                totalWords: t.words.length,
                wordsInClip: words.length,
                winStart,
                winEnd,
                removeFillers,
                removeSilences,
              });

              const result = detectAutocutRanges(words, {
                fillerWords: Array.isArray(input?.filler_words)
                  ? input.filler_words
                  : undefined,
                maxSilenceSeconds:
                  typeof input?.max_silence_seconds === "number"
                    ? input.max_silence_seconds
                    : undefined,
                paddingSeconds:
                  typeof input?.padding_seconds === "number"
                    ? input.padding_seconds
                    : undefined,
                removeFillerWords: removeFillers,
                removeSilences,
                windowStart: winStart,
                windowEnd: winEnd,
              });
              console.log("[Atlas][autocut][DIAG] detección", {
                cuts: result.cuts.length,
                keepSegments: result.keepSegments.length,
                totalRemoved: Number(result.totalRemoved.toFixed(2)),
              });

              if (result.cuts.length === 0) {
                return {
                  content: JSON.stringify({
                    cuts: 0,
                    totalSecondsRemoved: 0,
                    message:
                      "No encontré muletillas ni silencios para cortar en este clip.",
                  }),
                };
              }

              const fillerCuts = result.cuts.filter(
                (c) => c.reason === "filler",
              ).length;
              const silenceCuts = result.cuts.filter(
                (c) => c.reason === "silence",
              ).length;
              const dryRun = !!input?.dry_run;

              if (dryRun || result.keepSegments.length === 0) {
                console.log("[Atlas][autocut][DIAG] preview/dry_run", {
                  dryRun,
                  keepSegments: result.keepSegments.length,
                });
                return {
                  content: JSON.stringify({
                    dryRun: true,
                    cuts: result.cuts.length,
                    fillerCuts,
                    silenceCuts,
                    totalSecondsRemoved: Number(
                      result.totalRemoved.toFixed(2),
                    ),
                    keepSegments: result.keepSegments.length,
                    warning:
                      result.keepSegments.length === 0
                        ? "Todo el clip quedaría cortado — NO aplico sin confirmación explícita del usuario."
                        : "Mostrale esto al usuario y pedí confirmación antes de aplicar (dry_run=false).",
                    ranges: result.cuts.map((c) => ({
                      start: Number(c.start.toFixed(2)),
                      end: Number(c.end.toFixed(2)),
                      reason: c.reason,
                      detail: c.detail,
                    })),
                  }),
                };
              }

              // Aplicar: reconstruir el clip como N sub-clips contiguos + ripple
              // de los clips siguientes en la MISMA pista.
              const subClips: TimelineClip[] = [];
              let acc = 0;
              for (const k of result.keepSegments) {
                subClips.push({
                  ...clip,
                  id: generateId(),
                  mediaOffset: k.start,
                  duration: k.length,
                  startTime: clip.startTime + acc,
                });
                acc += k.length;
              }
              const removed = result.totalRemoved;
              const clipTrack = clip.trackIndex ?? 0;
              const clipEnd = clip.startTime + clip.duration;
              setClips((prev) => {
                const out: TimelineClip[] = [];
                for (const c of prev) {
                  if (c.id === clip.id) {
                    out.push(...subClips);
                  } else if (
                    (c.trackIndex ?? 0) === clipTrack &&
                    c.startTime >= clipEnd
                  ) {
                    out.push({ ...c, startTime: c.startTime - removed });
                  } else {
                    out.push(c);
                  }
                }
                return out;
              });
              console.log("[Atlas][autocut][DIAG] APLICADO", {
                subClips: subClips.length,
                removed: Number(removed.toFixed(2)),
              });
              toolMessage(
                `Autocut: ${result.cuts.length} cortes (−${removed.toFixed(1)}s)`,
                Scissors,
              );
              return {
                content: JSON.stringify({
                  applied: true,
                  cuts: result.cuts.length,
                  fillerCuts,
                  silenceCuts,
                  totalSecondsRemoved: Number(removed.toFixed(2)),
                  resultingSubclips: subClips.length,
                }),
              };
            } catch (err: any) {
              console.error("[Atlas][autocut] ERROR inesperado:", err);
              return {
                content: `Error inesperado en autocut: ${err?.message || String(err)}`,
                is_error: true,
              };
            }
          }
          case "erase_object": {
            const clipId =
              typeof input?.clipId === "string" ? input.clipId : undefined;
            const prompt =
              typeof input?.prompt === "string" ? input.prompt.trim() : "";
            if (!clipId) return { content: "Falta clipId", is_error: true };
            if (!prompt)
              return { content: "Falta describir qué objeto borrar (prompt).", is_error: true };
            const clip = clipsRef.current.find((c) => c.id === clipId);
            if (!clip)
              return { content: `Clip ${clipId} no encontrado.`, is_error: true };
            if (clip.type !== "video" && clip.type !== "ai-generated")
              return { content: "Solo se puede borrar objetos en clips de video.", is_error: true };
            if (!clip.storagePath)
              return {
                content: `El clip "${clip.name}" todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.`,
                is_error: true,
              };
            const confirmLong = input?.confirm_long_clip === true;
            if (clip.duration > ERASE_SOFT_CAP_SEC && !confirmLong) {
              return {
                content: JSON.stringify({
                  needsConfirmation: true,
                  reason: "long_clip",
                  durationSec: Math.round(clip.duration),
                  message: `El clip dura ${Math.round(clip.duration)}s (más de ${ERASE_SOFT_CAP_SEC}s). Va a tardar más y tiene más riesgo de costuras visibles. Confirmale al usuario y, si acepta, volvé a llamar con confirm_long_clip:true.`,
                }),
              };
            }
            const cost = creditsForErase({ durationSec: clip.duration });
            // Fire-and-forget: la función maneja su propio polling y estado.
            void eraseObjectInClip(clipId, prompt, confirmLong);
            return {
              content: JSON.stringify({
                started: true,
                estimatedCredits: cost,
                message: `Borrando "${prompt}" del clip. Tarda unos minutos; el clip muestra un shimmer y al terminar se reemplaza solo (y queda en Archivos).`,
              }),
            };
          }
          case "set_clip_volume": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const v = Math.max(0, Math.min(2, Number(input.volume)));
            setClips((prev) =>
              prev.map((c) =>
                c.id === input.clipId ? { ...c, volume: v } : c,
              ),
            );
            toolMessage(
              v === 0 ? "Clip silenciado" : `Volumen = ${v}`,
              v === 0 ? VolumeX : Volume2,
            );
            return { content: `Volumen = ${v}` };
          }
          case "translate_clip_to_english": {
            if (!input?.clipId)
              return { content: "clipId required", is_error: true };
            const targetClip = clips.find((c) => c.id === input.clipId);
            if (!targetClip) {
              return {
                content: `No existe ningún clip con id "${input.clipId}".`,
                is_error: true,
              };
            }
            if (
              targetClip.type !== "video" &&
              targetClip.type !== "ai-generated"
            ) {
              return {
                content: `El clip "${input.clipId}" es de tipo "${targetClip.type}", no se puede traducir. Solo aplica a video y ai-generated.`,
                is_error: true,
              };
            }
            if (
              targetClip.type === "ai-generated" &&
              targetClip.aiStatus !== "ready"
            ) {
              return {
                content: `El clip AI "${input.clipId}" todavía no está listo (aiStatus="${targetClip.aiStatus}"). Esperá a que termine la generación antes de traducir.`,
                is_error: true,
              };
            }
            if (targetClip.aiTranslating) {
              return {
                content: `El clip "${input.clipId}" ya tiene una traducción en curso.`,
                is_error: true,
              };
            }
            if (!targetClip.src) {
              return {
                content: `El clip "${input.clipId}" no tiene un archivo de video cargado.`,
                is_error: true,
              };
            }
            const cost = creditsForVideoTranslate({
              durationSec: targetClip.duration,
            });
            toolMessage(
              `Traduciendo "${targetClip.name}" al inglés (${cost} cr)`,
              Languages,
            );
            // Fire-and-forget: la función maneja su propio polling y
            // actualiza el clip cuando termina (5-15 min). Devolvemos
            // inmediato al agente para que pueda seguir conversando.
            void translateClipToEnglish(targetClip.id);
            return {
              content: `Traducción iniciada para el clip "${targetClip.name}" (id ${targetClip.id}). Costo: ${cost} créditos. Tarda entre 5 y 15 minutos. El clip se va a reemplazar in-place cuando termine.`,
            };
          }
          case "corregir_voz_en_clip": {
            const clipId = typeof input?.clipId === "string" ? input.clipId : undefined;
            if (!clipId) return { content: "Falta clipId", is_error: true };
            const correcciones = Array.isArray(input?.correcciones)
              ? (input.correcciones as unknown[]).filter(
                  (c): c is { dice: string; deberia_decir: string } =>
                    !!c && typeof c === "object" &&
                    typeof (c as Record<string, unknown>).dice === "string" &&
                    typeof (c as Record<string, unknown>).deberia_decir === "string",
                )
              : [];
            if (correcciones.length === 0)
              return { content: "Falta al menos una corrección {dice, deberia_decir}.", is_error: true };
            const clip = clipsRef.current.find((c) => c.id === clipId);
            if (!clip) return { content: `Clip ${clipId} no encontrado.`, is_error: true };
            if (clip.type === "image" || clip.type === "text")
              return { content: "Sólo se puede corregir la voz de clips de video o de audio.", is_error: true };
            if (clip.type === "ai-generated" && clip.aiStatus !== "ready")
              return { content: `El clip AI "${clipId}" todavía no está listo.`, is_error: true };
            if (!clip.storagePath)
              return {
                content: `El clip "${clip.name}" todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.`,
                is_error: true,
              };
            if (clip.corregirVozStatus === "processing")
              return { content: `El clip "${clip.name}" ya tiene una corrección de voz en curso.`, is_error: true };
            if (clip.eraseStatus === "processing" || clip.aiTranslating || clip.pasarA1080pStatus === "processing")
              return { content: `El clip "${clip.name}" tiene otra operación en curso (borrado, traducción o 1080p). Esperá a que termine.`, is_error: true };
            const palabras = transcriptVigente(clip)?.words;
            if (!palabras || palabras.length === 0)
              return {
                content: `El clip "${clip.name}" no tiene transcript. Llamá primero a generate_transcript con clipId="${clipId}" y volvé a intentar.`,
                is_error: true,
              };
            // Contra el transcript COMPLETO del asset, en tiempos del archivo: el de
            // get_clip_transcript viene recortado a la ventana visible y en tiempos de
            // timeline, y usarlo correría el empalme en cualquier clip recortado.
            const r = resolverCorrecciones(palabras, correcciones);
            if (r.ediciones.length === 0) {
              return {
                content: JSON.stringify({
                  started: false,
                  noResueltas: r.noResueltas,
                  message: "No pude aplicar ninguna corrección. Contale al usuario los motivos y pedile cómo seguir.",
                }),
                is_error: true,
              };
            }
            toolMessage(`Corrigiendo la voz de "${clip.name}"`, Mic);
            // Fire-and-forget: la función maneja su propio polling y estado.
            void corregirVozEnClip(clipId, r.ediciones);
            return {
              content: JSON.stringify({
                started: true,
                aplicadas: r.aplicadas,
                noResueltas: r.noResueltas,
                message: `Corrigiendo ${r.ediciones.length} palabra(s) en "${clip.name}". Es gratis, tarda entre 15 y 60 segundos; el clip muestra un shimmer y al terminar se reemplaza solo (el original queda en Archivos).`,
              }),
            };
          }
          case "set_project_name": {
            if (!input?.name)
              return { content: "name required", is_error: true };
            const n = String(input.name).slice(0, 120);
            setProjectName(n);
            toolMessage(`Proyecto renombrado a "${n}"`, Pencil);
            return { content: "Nombre actualizado." };
          }
          case "set_project_aspect_ratio": {
            if (!input?.aspectRatio)
              return { content: "aspectRatio required", is_error: true };
            setAspectRatio(input.aspectRatio);
            toolMessage(`Aspect ratio → ${input.aspectRatio}`, Ratio);
            return { content: "Aspect ratio actualizado." };
          }
          default:
            return { content: `Unknown tool: ${name}`, is_error: true };
        }
      } catch (e: any) {
        console.error(`executeAgentTool(${name}) failed:`, e);
        return { content: `Error: ${e?.message || String(e)}`, is_error: true };
      }
    },
    [
      clips,
      projectId,
      companyId,
      projectName,
      duration,
      aspectRatio,
      audioAiVoices,
      addAssetToTimeline,
      addTextClip,
      customFontFamilies,
      openSubtitlesModalForClip,
      quitarFondoDelClip,
      crearClipDeAnimacionGrafica,
      crearPlaceholderDeAnimacion,
      completarAnimacionGrafica,
      descartarPlaceholderDeAnimacion,
      marcaDelProyectoParaAnimacion,
      capturarMiniaturaDeAnimacion,
      activePanel,
      selectedClipId,
      editingClipId,
      brandProfile,
      sceneMode,
      scenePrompt,
      sceneDuration,
      sceneQuality,
      sceneResolution,
      sceneDialogue,
      sceneSpeechSpeed,
      sceneRefMode,
      sceneFirstFrameAssetId,
      sceneLastFrameAssetId,
      sceneRefAssetIds,
      audioAiType,
      audioAiPrompt,
      audioAiDuration,
      audioAiVoiceId,
      audioAiVoiceName,
      audioAiSpeechSpeed,
      audioAiInstrumentalOnly,
      imageGenPrompt,
      imageGenAttachedAssetIds,
      imageGenResults,
      clipThumbnails,
    ],
  );

  // ── El agente del chat es el director ──────────────────────────────────
  // ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente: el editor se
  // registra en el puente de pantalla mientras está montado. El chat le suma al
  // modelo las herramientas del Estudio SÓLO acá y las resuelve con
  // `executeAgentTool` (el mismo executor que usaba el Director). `describir` es
  // lo que el prompt cuenta de "dónde está el usuario" en cada turno; se lee por
  // ref en el momento del envío, así que siempre es el estado vivo.
  const { abrirChat } = useChatAtlasCascaron();
  const abrirDirector = useCallback(() => {
    abrirChat({
      contexto: {
        pantalla: "el Estudio de Video",
        ruta: `/editor-video/${projectId ?? ""}`,
        detalle: projectName ? `el proyecto «${projectName}»` : undefined,
      },
      vista: "panel",
    });
  }, [abrirChat, projectId, projectName]);

  // ── Sheet "Agregar" (sólo teléfono) ──────────────────────────────────────
  // Las tres herramientas que CREAN algo nuevo —Video con IA, Audio con IA y
  // Texto— salieron de la barra de abajo y viven acá adentro. La barra quedó
  // como una tab bar de verdad: cuatro lugares fijos (Agregar, Director,
  // Archivos, Marca) en vez de seis baldosas que había que scrollear en
  // horizontal para llegar a la última. Es el mismo gesto que el botón
  // "Generar" de la home (`SheetGenerar`): el "+" de la barra abre un sheet con
  // lo que se puede crear. Ver
  // ADR-2026-09-17-la-barra-del-estudio-en-el-telefono-es-una-tab-bar.
  // El título del topbar del teléfono está en modo edición. Ver el comentario
  // del topbar: como texto respeta los 13px del diseño; como input, la regla
  // global anti-zoom de iOS lo fuerza a 16px.
  const [editandoNombreEnElTelefono, setEditandoNombreEnElTelefono] =
    useState(false);
  const [sheetAgregar, setSheetAgregar] = useState(false);
  const [sheetAgregarAnimado, setSheetAgregarAnimado] = useState(false);
  const abrirSheetAgregar = useCallback(() => {
    setSheetAgregar(true);
    // Dos frames: el primero monta el sheet abajo de todo (`translate-y-full`),
    // el segundo dispara la transición. Con uno solo el navegador puede pintar
    // los dos estados juntos y el sheet aparece de golpe. Mismo patrón que
    // `SheetGenerar` y que los sheets de opciones de la app.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setSheetAgregarAnimado(true)),
    );
  }, []);
  const cerrarSheetAgregar = useCallback(() => {
    setSheetAgregarAnimado(false);
    window.setTimeout(() => setSheetAgregar(false), 300);
  }, []);

  // ── En el teléfono, sumar algo a la timeline cierra el panel ─────────────
  // El panel es una hoja que tapa dos tercios de la pantalla: elegir un
  // producto, un logo, un personaje, una imagen del moodboard, un archivo o una
  // plantilla metía el clip DETRÁS de la hoja, y había que cerrarla a mano para
  // ver qué pasó. Ahora se cierra sola.
  //
  // Va como efecto sobre la CANTIDAD de clips y no llamando a un `cerrar()` en
  // cada lugar que inserta: hay más de diez caminos que agregan un clip
  // (Archivos, las cuatro carpetas de Marca, plantillas, texto, los dos
  // generadores de IA, separar audio, cortar silencios…) y cualquiera nuevo
  // nacería sin el cierre. Acá el criterio es uno solo: si la timeline tiene
  // más clips que hace un momento, algo entró.
  const cantidadDeClipsRef = useRef(clips.length);
  useEffect(() => {
    const antes = cantidadDeClipsRef.current;
    cantidadDeClipsRef.current = clips.length;
    // La hidratación del proyecto (0 → N al abrirlo) no es "algo que entró".
    if (projectLoading) return;
    if (!enElTelefono) return;
    if (clips.length <= antes) return;
    setActivePanel((p) => (p === "none" ? p : "none"));
  }, [clips.length, enElTelefono, projectLoading]);
  const ejecutarHerramientaDelEstudio = useCallback(
    async (nombre: string, input: unknown) => {
      const r = await executeAgentTool(nombre, input);
      return { content: r.content as string | unknown[], is_error: r.is_error };
    },
    [executeAgentTool],
  );
  useRegistrarPantalla({
    clave: CLAVE_PANTALLA_ESTUDIO,
    describir: (): DescripcionDePantalla => {
      const seleccionado = selectedClipId
        ? clips.find((c) => c.id === selectedClipId)
        : null;
      const porTipo: Record<string, number> = {};
      for (const c of clips) porTipo[c.type] = (porTipo[c.type] || 0) + 1;
      const tipos = Object.entries(porTipo)
        .map(([t, n]) => `${n} ${t}`)
        .join(", ");
      return {
        pantalla: `el Estudio de Video, proyecto «${projectName || "sin nombre"}»`,
        ruta: `/editor-video/${projectId ?? ""}`,
        queHace:
          "El editor de video: una timeline con clips, escenas/audios/imágenes generadas con IA, texto, la marca del proyecto y la exportación.",
        campos: [],
        faltaParaGenerar: [],
        estado: `Formato ${aspectRatio}, ${Math.round(duration)} s, ${clips.length} clip(s)${
          tipos ? ` (${tipos})` : ""
        }. ${
          seleccionado
            ? `Seleccionado: «${seleccionado.name}» (${seleccionado.type}, ${seleccionado.duration.toFixed(1)} s desde ${seleccionado.startTime.toFixed(1)} s).`
            : "Sin clip seleccionado."
        } Panel abierto: ${activePanel === "none" ? "ninguno" : activePanel}.${
          clips.some((c) => c.corregirVozStatus === "processing")
            ? ` Corrigiendo la voz de ${clips.filter((c) => c.corregirVozStatus === "processing").length} clip(s).`
            : ""
        }${
          clips.some((c) => c.animationCodigo)
            ? ` ${clips.filter((c) => c.animationCodigo).length} animación(es) por código (crear_animacion / modificar_animacion).`
            : ""
        }${
          clips.some((c) => clipEsBorradorVigente(c))
            ? ` Borradores que se pueden pasar a 1080p (finalizar_borrador): ${clips
                .filter((c) => clipEsBorradorVigente(c))
                .map((c) => `«${c.name}» (${c.id}, ${c.aiBorrador!.segundos} s, ${creditosDePasarA1080p(c.aiBorrador!.segundos)} cr)`)
                .join(", ")}.`
            : ""
        }`,
      };
    },
    // Sin campos: acá se trabaja con las herramientas, no con configurar_pantalla.
    aplicar: () => ({ aplicados: [], fallidos: [] }),
    herramientas: HERRAMIENTAS_DEL_ESTUDIO,
    ejecutar: ejecutarHerramientaDelEstudio,
    skill: SKILL_DEL_ESTUDIO,
  });

  // ── AI scene creation ───────────────────────────────────────────────────
  const handleFrameUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    target: "first" | "last",
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    // Show blob URL preview immediately
    const blobUrl = URL.createObjectURL(file);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      if (target === "first") setSceneFirstFrameAssetId(asset.id);
      else setSceneLastFrameAssetId(asset.id);
    } catch (err) {
      console.error("[VideoEditor] Frame upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    }
  };

  // Import a generated image into the project as a regular image asset.
  // Used by the "Imágenes del proyecto" overlay so the user can attach a
  // previously-generated image as a reference for a new generation. Returns
  // the resulting asset id, or null if it can't be imported (e.g. signed
  // URL still pending). Idempotent within a session: the result is cached
  // in `imageGenImportedMap` so re-clicking the same generation reuses the
  // existing asset id instead of uploading a new copy.
  const importGeneratedImageAsAsset = useCallback(
    async (genId: string): Promise<string | null> => {
      const cached = imageGenImportedMap[genId];
      if (cached) return cached;
      const gen = imageGenResults.find((r) => r.id === genId);
      if (!gen?.signedUrl || !projectId || !imageGenCompanyId) return null;
      try {
        const resp = await fetch(gen.signedUrl);
        const blob = await resp.blob();
        const ext = (gen.fileName?.split(".").pop() || "jpg").toLowerCase();
        const file = new File(
          [blob],
          gen.fileName || `ai-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: imageGenCompanyId,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setImageGenImportedMap((prev) => ({ ...prev, [genId]: asset.id }));
        return asset.id;
      } catch (err) {
        console.error(
          "[VideoEditor] failed to import generated image as asset",
          genId,
          err,
        );
        return null;
      }
    },
    [imageGenImportedMap, imageGenResults, projectId, imageGenCompanyId],
  );

  // ── Soltar en el Estudio ─────────────────────────────────────────────────
  // Lo que llega en un `drop`, venga de donde venga: una ficha de Archivos
  // (asset, imagen generada o animación) o archivos de la computadora. La
  // `ubicacion` la pone quien recibe el soltado: la timeline manda el segundo
  // y la capa donde se soltó; el cuadro de vista previa y el resto del editor,
  // el playhead (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta).
  const soltarEnElEstudio = useCallback(
    async (dt: DataTransfer, ubicacion: UbicacionDeClipNuevo) => {
      const assetId = dt.getData(TIPO_ASSET);
      if (assetId) {
        const asset =
          libraryAssets.find((a) => a.id === assetId) ??
          imageLibraryAssets.find((a) => a.id === assetId) ??
          (await fetchAssetsByIds([assetId]))[0];
        if (!asset) return;
        if (asset.type === "image")
          await agregarImagenAlVideo(asset.id, asset.name || "Imagen", ubicacion);
        else await addAssetToTimeline(asset, ubicacion);
        return;
      }
      const genId = dt.getData(TIPO_GENERADA);
      if (genId) {
        // Lo mismo que tocar la ficha: primero se vuelve asset del proyecto.
        setImageGenImportingGenIds((prev) => new Set(prev).add(genId));
        try {
          const importado = await importGeneratedImageAsAsset(genId);
          if (importado) {
            const gen = imageGenResults.find((r) => r.id === genId);
            await agregarImagenAlVideo(
              importado,
              gen?.fileName || "Imagen",
              ubicacion,
            );
          }
        } finally {
          setImageGenImportingGenIds((prev) => {
            const next = new Set(prev);
            next.delete(genId);
            return next;
          });
        }
        return;
      }
      const animId = dt.getData(TIPO_ANIMACION);
      if (animId) {
        const a = animacionesGraficas.find((x) => x.id === animId);
        if (!a) return;
        crearClipDeAnimacionGrafica({
          animacion: a.animacion,
          tipo: a.animacion.tipo,
          duracionSeg: versionActual(a.animacion).duracionSeg,
          nombre: a.nombre,
          startTime: ubicacion.startTime,
          destino: ubicacion.destino,
        });
        return;
      }
      if (dt.files.length > 0)
        soltarArchivosEnElEstudio(Array.from(dt.files), ubicacion);
    },
    [
      libraryAssets,
      imageLibraryAssets,
      agregarImagenAlVideo,
      addAssetToTimeline,
      importGeneratedImageAsAsset,
      imageGenResults,
      animacionesGraficas,
      crearClipDeAnimacionGrafica,
      soltarArchivosEnElEstudio,
    ],
  );

  // Archivos de la computadora en CUALQUIER parte del editor: se aceptan (sin
  // el `preventDefault` del `dragover`, el navegador abre el archivo y saca al
  // usuario del editor) y entran en el playhead. Un soltado que ya atendió la
  // timeline o el cuadro llega con `defaultPrevented` y no se repite. Sólo
  // archivos: una ficha de Archivos soltada en el vacío no hace nada, como
  // antes.
  const soltarEnElEstudioRef = useRef(soltarEnElEstudio);
  soltarEnElEstudioRef.current = soltarEnElEstudio;
  useEffect(() => {
    let profundidad = 0;
    const apagar = () => {
      profundidad = 0;
      setArrastrandoArchivos(false);
    };
    const onEnter = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer)) return;
      profundidad += 1;
      setArrastrandoArchivos(true);
    };
    const onOver = (e: DragEvent) => {
      if (arrastraArchivos(e.dataTransfer)) e.preventDefault();
    };
    const onLeave = (e: DragEvent) => {
      if (!arrastraArchivos(e.dataTransfer)) return;
      profundidad = Math.max(0, profundidad - 1);
      if (profundidad === 0) setArrastrandoArchivos(false);
    };
    const onDrop = (e: DragEvent) => {
      const dt = e.dataTransfer;
      apagar();
      if (!dt || !arrastraArchivos(dt)) return;
      if (e.defaultPrevented) return;
      e.preventDefault();
      void soltarEnElEstudioRef.current(dt, {
        startTime: Math.max(0, currentTimeRef.current),
      });
    };
    document.addEventListener("dragenter", onEnter);
    document.addEventListener("dragover", onOver);
    document.addEventListener("dragleave", onLeave);
    document.addEventListener("drop", onDrop);
    document.addEventListener("dragend", apagar);
    return () => {
      document.removeEventListener("dragenter", onEnter);
      document.removeEventListener("dragover", onOver);
      document.removeEventListener("dragleave", onLeave);
      document.removeEventListener("drop", onDrop);
      document.removeEventListener("dragend", apagar);
    };
  }, []);

  // [Archivos] Importa un producto del catálogo como imagen del proyecto, para
  // que se comporte igual que cualquier imagen (aparece en Imágenes, mismo menú).
  /**
   * Traer un video del historial de la plataforma al proyecto: baja el mp4
   * firmado, lo sube como asset del proyecto y lo suma a la timeline.
   *
   * Hay que copiarlo: el video vive en el bucket `generadas` (historial de la
   * cuenta) y la timeline trabaja con `video_project_assets`. Es el mismo
   * camino que `importProductToImageAsset` hace para las imágenes.
   */
  const importarVideoDelHistorial = useCallback(
    async (v: HistorialVideo) => {
      const cid = companyId ?? imageGenCompanyId;
      if (!v.videoUrl || !projectId || !cid) return;
      if (filesImportingVideoIds.has(v.id)) return;
      setFilesImportingVideoIds((prev) => new Set(prev).add(v.id));
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        // Primero se COPIA dentro de Storage (generadas → video-project-assets):
        // antes se bajaba el mp4 entero al navegador y se volvía a subir (6,5 s
        // medidos con un video corto) y el clip recién aparecía al final. Si la copia
        // no se puede (permisos, ruta desconocida), queda el camino de siempre
        // (ADR-2026-11-03-traer-de-atlas-sin-bajar-y-subir).
        let asset: ProjectAsset | null = null;
        const origen = rutaDeUrlFirmada(v.videoUrl, "generadas");
        if (origen) {
          const destino = `${cid}/${projectId}/${crypto.randomUUID()}.mp4`;
          const { error: errorDeCopia } = await supabase.storage
            .from("generadas")
            .copy(origen, destino, { destinationBucket: "video-project-assets" });
          if (!errorDeCopia) {
            asset = await registerExistingAsset({
              projectId,
              companyId: cid,
              userId: user?.id,
              storagePath: destino,
              type: "video",
              name: v.name.slice(0, 80),
              mimeType: "video/mp4",
            }).catch(() => null);
          } else {
            console.warn("[VideoEditor] no se pudo copiar el video; se sube", errorDeCopia);
          }
        }
        let yaBajado: string | undefined = asset ? v.videoUrl : undefined;
        if (!asset) {
          const resp = await fetch(v.videoUrl);
          const blob = await resp.blob();
          const file = new File([blob], `${v.name.slice(0, 40)}.mp4`, {
            type: blob.type || "video/mp4",
          });
          asset = await uploadProjectAsset({
            file,
            projectId,
            companyId: cid,
            userId: user?.id,
          });
          const subido = asset;
          setAssetPreviewUrls((prev) => ({
            ...prev,
            [subido.id]: URL.createObjectURL(blob),
          }));
          yaBajado = undefined;
        }
        // Copiado: el clip usa la URL del video que ya se estaba viendo, que el
        // navegador tiene bajada (ADR-2026-11-03-agregar-al-instante).
        await addAssetToTimeline(asset, yaBajado ? { src: yaBajado } : undefined);
      } catch (err) {
        console.error("[VideoEditor] importar video del historial falló", err);
      } finally {
        setFilesImportingVideoIds((prev) => {
          const n = new Set(prev);
          n.delete(v.id);
          return n;
        });
      }
    },
    [
      companyId,
      imageGenCompanyId,
      projectId,
      filesImportingVideoIds,
      addAssetToTimeline,
    ],
  );

  const importProductToImageAsset = useCallback(
    async (product: MockProductV2): Promise<string | null> => {
      // Dedup: si ya lo importamos, reusar el mismo asset (no subir otra copia).
      const cachedId = productSceneAssetMap[product.id];
      if (cachedId) {
        // El mapping producto→asset se PERSISTE en el proyecto, pero la preview
        // (blob URL) NO sobrevive a una recarga. Si volvemos de otra sesión, el
        // asset existe pero no tiene preview → el slot de referencia/frame sale
        // vacío ("..."). Aseguramos la preview con la URL firmada del producto.
        if (product.imageUrl) {
          setAssetPreviewUrls((prev) =>
            prev[cachedId] ? prev : { ...prev, [cachedId]: product.imageUrl },
          );
        }
        return cachedId;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!product.imageUrl || !projectId || !cid) return null;
      try {
        // Las creaciones de Atlas llegan con la miniatura de la grilla (`opt/`,
        // ~300 px): importada así, la foto salía de 300 px en la timeline y en el
        // video exportado. Se pide la ORIGINAL (`full/`) y, si no está, queda la de
        // la grilla (ADR-2026-11-03-traer-de-atlas-sin-bajar-y-subir). Las fotos de
        // productos vienen de otro bucket y no cambian.
        let urlParaImportar = product.imageUrl;
        const rutaOpt = rutaDeUrlFirmada(product.imageUrl, "generadas");
        const rutaFull = rutaOpt ? rutaFullDeOpt(rutaOpt) : null;
        if (rutaFull) {
          const { data: firmada } = await supabase.storage
            .from("generadas")
            .createSignedUrl(rutaFull, 600);
          if (firmada?.signedUrl) urlParaImportar = firmada.signedUrl;
        }
        let resp = await fetch(urlParaImportar);
        if (!resp.ok && urlParaImportar !== product.imageUrl)
          resp = await fetch(product.imageUrl);
        const blob = await resp.blob();
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const file = new File(
          [blob],
          `${(product.name || "producto").slice(0, 40)}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        // NO se agrega a imageLibraryAssets: el producto/escena vive en su pill,
        // no en Imágenes. Se registra para dedup + filtrado de Imágenes.
        setProductSceneAssetMap((prev) => ({
          ...prev,
          [product.id]: asset.id,
        }));
        return asset.id;
      } catch (err) {
        console.error(
          "[VideoEditor] import product to image asset failed",
          err,
        );
        return null;
      }
    },
    [productSceneAssetMap, companyId, imageGenCompanyId, projectId],
  );

  // Importa la foto de un modelo como asset del proyecto (para usarla como la
  // persona del "Frame inicial" en Modo Diálogo). Dedup vía `modelAssetMap`.
  const importModelToImageAsset = useCallback(
    async (model: ModelV2): Promise<string | null> => {
      const cachedId = modelAssetMap[model.id];
      if (cachedId) {
        if (model.thumbnailUrl)
          setAssetPreviewUrls((prev) =>
            prev[cachedId]
              ? prev
              : { ...prev, [cachedId]: model.thumbnailUrl },
          );
        return cachedId;
      }
      const cid = companyId ?? imageGenCompanyId;
      if (!model.thumbnailUrl || !projectId || !cid) return null;
      try {
        const resp = await fetch(model.thumbnailUrl);
        const blob = await resp.blob();
        const ext = (blob.type.split("/")[1] || "jpg").toLowerCase();
        const file = new File(
          [blob],
          `${(model.nombre || "modelo").slice(0, 40)}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId: cid,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        setModelAssetMap((prev) => ({ ...prev, [model.id]: asset.id }));
        return asset.id;
      } catch (err) {
        console.error("[VideoEditor] import model to image asset failed", err);
        return null;
      }
    },
    [modelAssetMap, companyId, imageGenCompanyId, projectId],
  );

  // Moodboards de la marca: se traen al abrir esa carpeta del panel de Marca.
  // Se cachea en el estado (`length > 0` corta), como el resto de las carpetas.
  useEffect(() => {
    if (activePanel !== "brand") return;
    if (carpetaDeMarca !== "moodboard") return;
    if (imagenesDeMoodboard.length > 0) return;
    const brandId = brandProfile?.id;
    if (!brandId) return;
    let cancelado = false;
    (async () => {
      setMoodboardCargando(true);
      try {
        const items = await fetchImagenesDeMoodboards(brandId);
        if (!cancelado) setImagenesDeMoodboard(items.filter((i) => i.url));
      } catch (e) {
        console.error("[VideoEditor] fetch moodboards falló", e);
      } finally {
        if (!cancelado) setMoodboardCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [
    activePanel,
    carpetaDeMarca,
    brandProfile?.id,
    imagenesDeMoodboard.length,
  ]);

  // Carga los modelos (globales + de la empresa) cuando se abre el picker O
  // cuando se abre el panel Archivos (los necesitamos para ocultar de
  // Multimedia, por nombre, los assets que son fotos de modelos).
  useEffect(() => {
    // El selector de imagen tiene su propia pestaña Personajes.
    if (
      !modelPickerOpen &&
      activePanel !== "files" &&
      activePanel !== "brand" &&
      !selectorDeImagen
    )
      return;
    if (modelItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid) return;
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const items = await fetchUserModels(cid);
        if (!cancelled) setModelItems(items.filter((m) => m.thumbnailUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch models failed", e);
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    modelPickerOpen,
    activePanel,
    modelItems.length,
    companyId,
    imageGenCompanyId,
    selectorDeImagen,
  ]);

  // [Archivos] Cargar los productos de la marca cuando se activa el pill
  // "Productos" del panel Archivos.
  useEffect(() => {
    // Dos entradas al mismo catálogo: el modo selección de Archivos (elegir la
    // foto de un frame) y la carpeta "Productos" del panel de Marca.
    const enArchivos = activePanel === "files" && filesTab === "productos";
    const enMarca = activePanel === "brand" && carpetaDeMarca === "productos";
    // Tercera entrada: la pestaña Productos del selector de imagen.
    const enSelector =
      !!selectorDeImagen && pestanaDelSelector === "productos";
    if (!enArchivos && !enMarca && !enSelector) return;
    if (filesProductItems.length > 0) return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) return;
    let cancelled = false;
    (async () => {
      setFilesProductsLoading(true);
      try {
        const items = await fetchUserProductsByBrand(cid, brandProfile.id);
        if (!cancelled) setFilesProductItems(items.filter((p) => p.imageUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch products for files failed", e);
      } finally {
        setFilesProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    filesTab,
    carpetaDeMarca,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    filesProductItems.length,
    selectorDeImagen,
    pestanaDelSelector,
  ]);

  // [Archivos] Cargar el historial (imágenes generadas del cliente) filtrado
  // por la marca del proyecto, cuando se activa el pill "Historial".
  useEffect(() => {
    // Dos entradas: el pill Creaciones del panel y la pestaña Creaciones del
    // selector de imagen de una escena.
    const enArchivos = activePanel === "files" && filesTab === "historial";
    const enSelector =
      !!selectorDeImagen && pestanaDelSelector === "creaciones";
    if (!enArchivos && !enSelector) return;
    if (filesHistorialItems.length > 0 || filesHistorialVideos.length > 0)
      return;
    const cid = companyId ?? imageGenCompanyId;
    if (!cid || !brandProfile?.id) return;
    let cancelled = false;
    (async () => {
      setFilesHistorialLoading(true);
      try {
        // Imágenes y videos de la marca, en paralelo: los dos alimentan la
        // misma pestaña.
        const [items, vids] = await Promise.all([
          fetchHistorialImages(cid, brandProfile.id),
          fetchHistorialVideos(cid, brandProfile.id),
        ]);
        if (cancelled) return;
        setFilesHistorialItems(items.filter((h) => h.imageUrl));
        setFilesHistorialVideos(vids.filter((v) => v.videoUrl));
      } catch (e) {
        console.error("[VideoEditor] fetch historial failed", e);
      } finally {
        if (!cancelled) setFilesHistorialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePanel,
    filesTab,
    companyId,
    imageGenCompanyId,
    brandProfile?.id,
    filesHistorialItems.length,
    filesHistorialVideos.length,
    selectorDeImagen,
    pestanaDelSelector,
  ]);

  // Re-sign an expired signed URL for a generated image (uses project companyId)
  const refreshGenImageSignedUrl = useCallback(
    async (id: string) => {
      const target = imageGenResults.find((r) => r.id === id);
      if (!target?.fileName || !imageGenCompanyId) return;
      try {
        const { data: signed } = await supabase.storage
          .from("generadas")
          .createSignedUrl(
            `${imageGenCompanyId}/full/${target.fileName}`,
            86400,
          );
        if (signed?.signedUrl) {
          setImageGenResults((prev) =>
            prev.map((r) =>
              r.id === id ? { ...r, signedUrl: signed.signedUrl } : r,
            ),
          );
        }
      } catch {
        // silent
      }
    },
    [imageGenResults, imageGenCompanyId],
  );

  // Reset edit mode whenever the selected detail image changes or closes.
  // Exception: if a pending edit prompt was queued (e.g. by the agent
  // calling prefill_edit_generated_image), enter edit mode with it.
  useEffect(() => {
    setImageGenEditError(null);
    const pending = pendingGenEditPromptRef.current;
    if (pending && imageGenDetailId) {
      pendingGenEditPromptRef.current = null;
      setImageGenEditing(true);
      setImageGenEditPrompt(pending);
    } else {
      setImageGenEditing(false);
      setImageGenEditPrompt("");
    }
  }, [imageGenDetailId]);

  // Scroll detail carousel to the clicked image's index when detail opens
  useEffect(() => {
    if (!imageGenDetailId) return;
    const idx = imageGenResults.findIndex((r) => r.id === imageGenDetailId);
    if (idx < 0) return;
    // Wait for DOM to render the detail view, then scroll
    const t = setTimeout(() => {
      const el = imageGenDetailScrollRef.current;
      if (!el) return;
      el.scrollTo({ left: idx * el.clientWidth, behavior: "auto" });
    }, 0);
    return () => clearTimeout(t);
  }, [imageGenDetailId, imageGenResults]);

  // ── AI Image Generation helpers ───────────────────────────────────────────
  const openImageGenPanel = useCallback((target: ImageGenTarget) => {
    setImageGenTarget(target);
    setImageGenPrompt("");
    setImageGenMejora(null);
    // Do NOT clear imageGenResults — keep existing project-generated images visible
    setImageGenError(null);
    setImageGenDetailId(null);
    setImageGenDetailFromFiles(false); // venimos de una escena, no de Multimedia
    setShowImageGenHistorial(false);
    setActivePanel("ai-image-gen");
    setSelectorDeImagen(null);
  }, []);

  const backToSceneFromImageGen = useCallback(() => {
    // Slot-preview takes precedence: cancel the preview (and any inline edit
    // mode) without committing.
    if (slotPreview) {
      setSlotPreview(null);
      setSlotPreviewEditing(false);
      setSlotPreviewEditPrompt("");
      setActivePanel("ai-scene");
      return;
    }
    if (imageGenDetailId) {
      // Si el detalle/edición se abrió desde Multimedia, volvemos ahí (no a la
      // grilla de generar ni a la escena).
      if (imageGenDetailFromFiles) {
        setImageGenDetailId(null);
        setImageGenEditing(false);
        setImageGenEditPrompt("");
        setImageGenDetailFromFiles(false);
        setActivePanel("files");
        setFilesTab("media");
        return;
      }
      setImageGenDetailId(null);
      return;
    }
    setActivePanel("ai-scene");
  }, [imageGenDetailId, imageGenDetailFromFiles, slotPreview]);

  // Poll imagenes_generadas rows until file_name is available, then resolve signed URL
  const pollGeneratedImage = useCallback(
    async (imageId: number | string, compId: string) => {
      const maxAttempts = 20;
      const delayMs = 2000;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const { data, error } = await supabase
            .from("imagenes_generadas")
            .select("file_name, created_at")
            .eq("id", imageId)
            .eq("id_company", compId)
            .single();
          if (!error && data?.file_name) {
            const filePath = `${compId}/full/${data.file_name}`;
            const { data: signed } = await supabase.storage
              .from("generadas")
              .createSignedUrl(filePath, 86400);
            if (signed?.signedUrl) {
              setImageGenResults((prev) =>
                prev.map((r) =>
                  r.id === String(imageId)
                    ? {
                        ...r,
                        fileName: data.file_name,
                        signedUrl: signed.signedUrl,
                        loading: false,
                        createdAt:
                          (data as any).created_at || r.createdAt,
                      }
                    : r,
                ),
              );
              return;
            }
          }
        } catch {}
        await new Promise((res) => setTimeout(res, delayMs));
      }
      // Mark failed after timeout
      setImageGenResults((prev) =>
        prev.map((r) =>
          r.id === String(imageId) ? { ...r, loading: false } : r,
        ),
      );
    },
    [],
  );

  // Las URLs firmadas de unos assets del proyecto, EN EL ORDEN pedido: el escritor y
  // la generación los mencionan por posición (`Imagen 1`, `Imagen 2`), así que el orden
  // tiene que ser el mismo en los dos pedidos.
  const firmarAssets = useCallback(async (ids: string[]): Promise<string[]> => {
    if (ids.length === 0) return [];
    const assets = await fetchAssetsByIds(ids);
    const porId = new Map(assets.map((a) => [a.id, a] as const));
    const urls: string[] = [];
    for (const id of ids) {
      const a = porId.get(id);
      if (!a) continue;
      const url = await getAssetSignedUrl(a.storage_path).catch(() => null);
      if (url) urls.push(url);
    }
    return urls;
  }, []);

  // "Mejorar prompt" de una ESCENA: el escritor del Creador, suelto y sin cobrar, con lo
  // mismo que después viaja al generar (texto, duración, formato, referencias firmadas y
  // el frame inicial). Reemplaza el prompt de la caja y recuerda el contexto: si se
  // genera así, viaja `prompt_escrito` (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
  const mejorarPromptDeEscena = useCallback(async () => {
    if (sceneMejorando || sceneMode !== "general") return;
    const prompt = scenePrompt.trim();
    if (!prompt && sceneRefAssetIds.length === 0 && !sceneFirstFrameAssetId) return;
    setSceneMejorando(true);
    try {
      // Las imágenes de referencia (un video de referencia no es foto para el escritor).
      const videos = new Set(
        libraryAssets.filter((a) => a.type === "video").map((a) => a.id),
      );
      const imagenes = sceneRefAssetIds.filter((id) => !videos.has(id));
      const [urls, [firstFrame]] = await Promise.all([
        firmarAssets(imagenes),
        firmarAssets(sceneFirstFrameAssetId ? [sceneFirstFrameAssetId] : []),
      ]);
      const r = await escribirPromptDeVideo({
        prompt,
        tipo_video: TIPO_DE_VIDEO_DEL_ESTUDIO,
        duration: clampDuracionDeEscena(sceneDuration),
        aspect_ratio: aspectRatio,
        ...(urls.length > 0
          ? { reference_images: urls.map((url) => ({ url, tipo: "adjunto" })) }
          : {}),
        ...(firstFrame ? { first_frame: firstFrame } : {}),
      });
      setScenePrompt(r.prompt);
      setSceneMejora({
        prompt: r.prompt,
        duracion: sceneDuration,
        refs: claveDeRefs([
          ...sceneRefAssetIds,
          sceneFirstFrameAssetId,
          sceneLastFrameAssetId,
        ]),
        aspecto: aspectRatio,
      });
    } catch (e) {
      setUploadErrorMsg(
        e instanceof Error ? e.message : "No pudimos mejorar el prompt. Probá de nuevo.",
      );
    } finally {
      setSceneMejorando(false);
    }
  }, [
    sceneMejorando,
    sceneMode,
    scenePrompt,
    sceneRefAssetIds,
    sceneFirstFrameAssetId,
    sceneLastFrameAssetId,
    sceneDuration,
    aspectRatio,
    libraryAssets,
    firmarAssets,
  ]);

  // "Mejorar prompt" de imagen: el escritor de imagen del backend (el de Redes), suelto
  // y sin cobrar. Reemplaza el prompt de la caja y guarda lo que declaró
  // (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
  const mejorarPromptDeImagen = useCallback(async () => {
    const prompt = imageGenPrompt.trim();
    if (imageGenMejorando) return;
    if (!prompt && imageGenAttachedAssetIds.length === 0) return;
    setImageGenMejorando(true);
    setImageGenError(null);
    try {
      const urls = await firmarAssets(imageGenAttachedAssetIds);
      const r = await escribirPromptDeImagen({
        prompt,
        Format: aspectRatio,
        ...(urls.length > 0 ? { reference_images: referenciasDeImagen(urls) } : {}),
      });
      setImageGenPrompt(r.prompt);
      setImageGenMejora(
        mejoraDeImagen(r, claveDeRefs(imageGenAttachedAssetIds), aspectRatio),
      );
    } catch (e) {
      setImageGenError(
        e instanceof Error ? e.message : "No pudimos mejorar el prompt. Probá de nuevo.",
      );
    } finally {
      setImageGenMejorando(false);
    }
  }, [
    imageGenPrompt,
    imageGenMejorando,
    imageGenAttachedAssetIds,
    aspectRatio,
    firmarAssets,
  ]);

  const startImageGeneration = useCallback(
    async (override?: { prompt?: string; attachedAssetIds?: string[] }) => {
      const prompt = (override?.prompt ?? imageGenPrompt).trim();
      if (!prompt) return;
      if (!assertCredits(creditsForImage())) return;
      const attachedIds =
        override?.attachedAssetIds ?? imageGenAttachedAssetIds;
      setImageGenBusy(true);
      setImageGenError(null);
      setImageGenPendingCount((c) => c + 1);
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) throw new Error("No user session");

        const body: Record<string, any> = {
          user_id: user.id,
          variante_id: [],
          modo: "creativo_plus",
          // Trackeo (ADR-585): imagen generada desde el Estudio.
          modo_utilizado: "estudio_imagen",
          cantidad_imagenes: 1,
          Format: aspectRatio,
          new_ui: true,
          prompt,
          // El mismo metaprompting que el modo Redes: el backend redacta el prompt y
          // elige el motor, salvo que "Mejorar prompt" ya lo haya escrito y el usuario
          // lo haya dejado igual o casi: entonces viaja lo que declaró
          // (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
          ...camposDelEscritorDeImagen(
            imageGenMejora,
            prompt,
            claveDeRefs(attachedIds),
            aspectRatio,
          ),
        };

        // Las referencias adjuntas, firmadas y con su mención (`Imagen N`): el
        // escritor las nombra así y el backend reemplaza cada mención por el rótulo
        // del motor. Si la mejora ya dijo qué es cada una, va su etiqueta.
        if (attachedIds.length > 0) {
          const urls = await firmarAssets(attachedIds);
          if (urls.length > 0)
            body.reference_images = referenciasDeImagen(
              urls,
              imageGenMejora?.etiquetas,
            );
        }

        // Ruteo atlas/n8n (ADR-587): este flujo es freeform PURO
        // (variante_id: [] siempre) — mismo contrato que el freeform del
        // asistente (ADR-583). El backend exige execution_id; con atlas
        // corremos la carrera respuesta-vs-poll para que un 202 (>150s) no
        // pierda la imagen. El fallback n8n es el webhook V2 byte-idéntico.
        const executionId = crypto.randomUUID();
        body.execution_id = executionId;
        let ids: (number | string)[];
        let compId: string;
        if (USE_ATLAS_BACKEND) {
          if (companyId) {
            const result = await awaitImageGeneration({
              fetchPromise: postGenerateAdAtlas(body),
              executionId,
              companyId,
              expectedCount: 1,
            });
            ids = result.imageIds;
            compId = result.companyId || companyId;
          } else {
            // Sin companyId no hay poll de respaldo — camino directo.
            const data = await postGenerateAdAtlas(body);
            ids = Array.isArray(data?.imagenes_generadas_ids)
              ? data.imagenes_generadas_ids
              : [];
            compId = (data?.id_company as string) || "";
          }
        } else {
          const res = await fetch(
            "https://ai-agency-dt7d.onrender.com/webhook/generar_anuncio_v2",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: GENERATE_AUTH_TOKEN,
              },
              body: JSON.stringify(body),
            },
          );
          if (!res.ok) throw new Error(`API ${res.status}`);
          const data = await res.json();
          if (
            data?.response !== "success" ||
            !Array.isArray(data?.imagenes_generadas_ids)
          ) {
            throw new Error("No se recibieron imágenes generadas.");
          }
          ids = data.imagenes_generadas_ids;
          compId = data.id_company || companyId || "";
        }
        if (ids.length === 0) {
          throw new Error("No se recibieron imágenes generadas.");
        }
        setImageGenCompanyId(compId);
        // Prepend new placeholders so newest appear first in grid
        setImageGenResults((prev) => [
          ...ids.map((id) => ({
            id: String(id),
            loading: true,
            session: true,
          })),
          ...prev,
        ]);
        setImageGenHistorial((prev) =>
          [prompt, ...prev.filter((p) => p !== prompt)].slice(0, 30),
        );
        setImageGenPrompt("");
        setImageGenAttachedAssetIds([]);
        // Start polling in parallel
        ids.forEach((id) => {
          pollGeneratedImage(id, compId);
        });
      } catch (err: any) {
        console.error("[VideoEditor] image generation failed:", err);
        setImageGenError(err?.message || "Error al generar imágenes");
      } finally {
        setImageGenBusy(false);
        setImageGenPendingCount((c) => Math.max(0, c - 1));
      }
    },
    [
      imageGenPrompt,
      companyId,
      pollGeneratedImage,
      aspectRatio,
      imageGenAttachedAssetIds,
      assertCredits,
      imageGenMejora,
      firmarAssets,
    ],
  );

  // Remove a generated image from the project's gallery (local state only — persisted on next save)
  const deleteGeneratedImage = useCallback(
    (id: string) => {
      setImageGenResults((prev) => {
        const next = prev.filter((r) => r.id !== id);
        // If the detail view was showing the removed image, navigate to a sibling or close
        if (imageGenDetailId === id) {
          if (next.length > 0) {
            setImageGenDetailId(next[0].id);
          } else {
            setImageGenDetailId(null);
          }
        }
        return next;
      });
    },
    [imageGenDetailId],
  );

  // Edit a generated image via editor_ia webhook; replaces the current detail image with the edited result
  const handleEditImage = useCallback(async () => {
    const detailId = imageGenDetailId;
    const instruction = imageGenEditPrompt.trim();
    if (!detailId || !instruction || !imageGenCompanyId) return;
    if (!assertCredits(creditsForImage())) return;
    setImageGenEditSubmitting(true);
    setImageGenEditError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No user session");

      // Atlas o n8n según flag (ADR-601).
      const data = await postImageEdit({
        user_id: user.id,
        imagen_id: detailId,
        instruction,
        mode: "edit_general",
        modo_utilizado: "estudio_edicion",
      });

      if (data?.response === "error_moderation") {
        throw new Error(
          data.error || "La instrucción fue rechazada por moderación. Probá con otra.",
        );
      }

      const newImageIdRaw: string | number | undefined =
        data?.edited_image_id ?? data?.imagen_id ?? data?.id;
      if (!newImageIdRaw)
        throw new Error("No se recibió el ID de la imagen editada");

      const newIdStr = String(newImageIdRaw);
      // Resolve DB row (id UUID or file_name)
      let newRow: {
        id: string;
        file_name: string;
        created_at?: string;
      } | null = null;
      const { data: idMatch } = await supabase
        .from("imagenes_generadas")
        .select("id, file_name, created_at")
        .eq("id", newIdStr)
        .eq("id_company", imageGenCompanyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      if (!newRow) {
        const { data: exact } = await supabase
          .from("imagenes_generadas")
          .select("id, file_name, created_at")
          .eq("file_name", newIdStr)
          .eq("id_company", imageGenCompanyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      if (!newRow) {
        const { data: likeData } = await supabase
          .from("imagenes_generadas")
          .select("id, file_name, created_at")
          .like("file_name", `${newIdStr}.%`)
          .eq("id_company", imageGenCompanyId)
          .maybeSingle();
        if (likeData) newRow = likeData as any;
      }
      if (!newRow?.file_name)
        throw new Error("No se pudo cargar la imagen editada");

      const { data: signed } = await supabase.storage
        .from("generadas")
        .createSignedUrl(
          `${imageGenCompanyId}/full/${newRow.file_name}`,
          86400,
        );
      if (!signed?.signedUrl)
        throw new Error("No se pudo obtener la URL firmada");

      const newResult: GenImageResult = {
        id: newRow.id,
        fileName: newRow.file_name,
        signedUrl: signed.signedUrl,
        loading: false,
        createdAt: newRow.created_at || undefined,
        // La grilla del panel de generar muestra SOLO las de esta sesión
        // (`sessionGenResults`). Sin esta marca la imagen editada se
        // generaba bien pero quedaba invisible junto a las otras.
        session: true,
      };

      // Prepend the edited image so it appears at the top of the grid; switch detail to it
      setImageGenResults((prev) => {
        // Avoid duplicates if already present
        const filtered = prev.filter((r) => r.id !== newResult.id);
        return [newResult, ...filtered];
      });
      setImageGenDetailId(newResult.id);
      setImageGenEditPrompt("");
      setImageGenEditing(false);
    } catch (err: any) {
      console.error("[VideoEditor] edit image failed:", err);
      setImageGenEditError(err?.message || "No se pudo editar la imagen");
    } finally {
      setImageGenEditSubmitting(false);
    }
  }, [imageGenDetailId, imageGenEditPrompt, imageGenCompanyId, assertCredits]);

  // "Utilizar" — upload the selected generated image as a project asset and,
  // if there's an explicit slot target (set when the panel was opened from the
  // AI scene flow), also assign it to that slot. When the panel was opened
  // without a target (e.g. from the agent or the root tools menu), we still
  // upload the image so it appears in the media library / list_project_assets.
  const useGeneratedImage = useCallback(
    async (result: GenImageResult) => {
      if (!result.signedUrl || !projectId || !companyId) return;
      try {
        const resp = await fetch(result.signedUrl);
        const blob = await resp.blob();
        const ext = (result.fileName?.split(".").pop() || "jpg").toLowerCase();
        const file = new File(
          [blob],
          result.fileName || `ai-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        const blobUrl = URL.createObjectURL(blob);
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
        if (imageGenTarget) {
          if (imageGenTarget.kind === "first")
            setSceneFirstFrameAssetId(asset.id);
          else if (imageGenTarget.kind === "last")
            setSceneLastFrameAssetId(asset.id);
          else setSceneRefAssetIds((prev) => [...prev, asset.id]);
          // Return to scene panel only when we came from there.
          setActivePanel("ai-scene");
        } else {
          // No slot context: just close the detail view and leave the panel
          // (the user explicitly opened image gen without a scene target).
          setActivePanel("none");
        }
        setImageGenDetailId(null);
      } catch (err) {
        console.error("[VideoEditor] useGeneratedImage failed:", err);
      }
    },
    [imageGenTarget, projectId, companyId],
  );

  // Asigna una imagen de la biblioteca al slot de la escena IA. first/last
  // reemplazan el valor; ref agrega si no estaba (mismo comportamiento que la
  // modal vieja). El toggle de ref (sacar una ya elegida) lo maneja el grid.
  const assignSceneImage = useCallback(
    (kind: "first" | "last" | "ref", assetId: string) => {
      if (kind === "first") setSceneFirstFrameAssetId(assetId);
      else if (kind === "last") setSceneLastFrameAssetId(assetId);
      else {
        // Un VIDEO como referencia: entra uno solo (el proveedor toma uno), de
        // hasta 29 s; de más de 14 s pasa la calidad a Profesional, que es la
        // que acepta esa entrada
        // (ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente).
        const video = libraryAssets.find(
          (a) => a.id === assetId && a.type === "video",
        );
        if (video) {
          const veredicto = evaluarVideoDeReferencia(video.duration_seconds);
          if (!veredicto.ok) {
            setUploadErrorMsg(veredicto.motivo);
            return;
          }
          if (veredicto.calidad === "profesional")
            setSceneQuality("profesional");
          const otrosVideos = new Set(
            libraryAssets.filter((a) => a.type === "video").map((a) => a.id),
          );
          setSceneRefAssetIds((prev) => [
            ...prev.filter((id) => !otrosVideos.has(id)),
            assetId,
          ]);
          return;
        }
        setSceneRefAssetIds((prev) =>
          prev.includes(assetId) ? prev : [...prev, assetId],
        );
      }
    },
    [libraryAssets],
  );

  // ── Slot-preview helpers ──────────────────────────────────────────────────
  // Commit the previewed asset to its target scene slot and return to the
  // scene panel.
  const useSlotPreviewImage = useCallback(() => {
    if (!slotPreview) return;
    const { assetId, target } = slotPreview;
    if (target.kind === "first") setSceneFirstFrameAssetId(assetId);
    else if (target.kind === "last") setSceneLastFrameAssetId(assetId);
    else
      setSceneRefAssetIds((prev) =>
        prev.includes(assetId) ? prev : [...prev, assetId],
      );
    setSlotPreview(null);
    setSlotPreviewEditing(false);
    setSlotPreviewEditPrompt("");
    setActivePanel("ai-scene");
  }, [slotPreview]);


  // Submit the edit prompt: pivot into the image-gen flow with the previewed
  // asset attached as a reference, the prompt pre-filled, and the slot's
  // target preserved (so the next "Utilizar" sends the new image back).
  const submitSlotPreviewEdit = useCallback(() => {
    if (!slotPreview) return;
    const prompt = slotPreviewEditPrompt.trim();
    if (!prompt) return;
    const { assetId, target } = slotPreview;
    setImageGenTarget(target);
    setImageGenAttachedAssetIds([assetId]);
    setImageGenPrompt(prompt);
    setSlotPreview(null);
    setSlotPreviewEditing(false);
    setSlotPreviewEditPrompt("");
    setImageGenError(null);
    setImageGenDetailId(null);
    // Fire-and-forget: kick off the generation immediately so the user lands
    // in the grid view watching the result come in. We pass the prompt and
    // attached asset ids explicitly to avoid racing with React state.
    void startImageGeneration({ prompt, attachedAssetIds: [assetId] });
  }, [slotPreview, slotPreviewEditPrompt, startImageGeneration]);

  // Start "Seleccionar de video" flow — open the frame picker overlay to choose a frame from a timeline clip
  const startFrameFromVideoPicker = useCallback((target: ImageGenTarget) => {
    setSelectorDeImagen(null);
    setFrameFromVideoTarget(target);
  }, []);

  // ── Selector de imagen de un slot de escena ───────────────────────────────
  // Abre el pop-up centrado para el slot que se tocó. Arranca siempre en
  // "Subidas" la primera vez, pero después respeta la pestaña que el usuario
  // dejó abierta: quien viene de elegir una creación para el frame inicial casi
  // siempre quiere otra creación para el final.
  const abrirSelectorDeImagen = useCallback((target: SelectorTarget) => {
    setSelectorDeImagen(target);
  }, []);

  // Dónde aterriza lo elegido: en un slot de la escena, o como referencia adjunta del
  // panel de generar imagen ("Añadir" abre el MISMO selector que "Añadir imagen o
  // video" del video — ADR-2026-10-05-anadir-en-imagen-abre-el-selector-del-video).
  const elegirEnDestino = useCallback(
    (target: SelectorTarget, assetId: string) => {
      if (target.kind === "attach") {
        setImageGenAttachedAssetIds((prev) =>
          prev.includes(assetId) ? prev : [...prev, assetId],
        );
        return;
      }
      assignSceneImage(target.kind, assetId);
    },
    [assignSceneImage],
  );

  // Token de la apertura actual. Las importaciones (bajar la imagen de la
  // plataforma y subirla como asset del proyecto) tardan, y en el medio el
  // usuario puede cerrar el pop-up o abrirlo para OTRO slot: sin esto la imagen
  // aterriza en el frame equivocado. Mismo patrón que `filesPickTokenRef`.
  const selectorTokenRef = useRef(0);
  useEffect(() => {
    selectorTokenRef.current += 1;
  }, [selectorDeImagen]);

  // Asigna un asset ya existente al slot que abrió el pop-up y lo cierra.
  const elegirAssetEnSelector = useCallback(
    (assetId: string) => {
      const target = selectorDeImagen;
      if (!target) return;
      elegirEnDestino(target, assetId);
      setSelectorDeImagen(null);
    },
    [selectorDeImagen, elegirEnDestino],
  );

  // Lo mismo para lo que todavía NO es un asset del proyecto (una generada de
  // la plataforma, un producto, un personaje): se materializa al vuelo y recién
  // ahí se asigna. El pop-up queda abierto con el tile en spinner mientras sube.
  const importarYElegirEnSelector = useCallback(
    async (
      id: string,
      enCurso: Set<string>,
      marcar: (fn: (prev: Set<string>) => Set<string>) => void,
      importar: () => Promise<string | null>,
    ) => {
      const target = selectorDeImagen;
      if (!target || enCurso.has(id)) return;
      const token = selectorTokenRef.current;
      marcar((prev) => new Set(prev).add(id));
      try {
        const assetId = await importar();
        // Se cerró el pop-up o se abrió para otro slot mientras subía.
        if (!assetId || selectorTokenRef.current !== token) return;
        elegirEnDestino(target, assetId);
        setSelectorDeImagen(null);
      } finally {
        marcar((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
      }
    },
    [selectorDeImagen, elegirEnDestino],
  );

  // Las imágenes "subidas" del proyecto: los assets de imagen + las generadas
  // con IA en esta sesión, ya deduplicadas. Lo miran DOS pantallas —la grilla
  // Multimedia del panel Archivos y la pestaña Subidas del selector de imagen—
  // y el filtrado no es obvio (esconde productos, personajes y la copia-asset
  // de una generada), así que vive una sola vez acá.
  const imagenesSubidas = useMemo(() => {
    // Generadas: sólo las que ya tienen signed URL resuelta, para no dibujar
    // tiles vacíos. El prefijo en la key evita chocar ids entre las dos fuentes.
    const generadas = imageGenResults
      .filter((r) => !!r.signedUrl)
      .map((r) => ({
        key: `gen:${r.id}`,
        kind: "generated" as const,
        id: r.id,
        url: r.signedUrl as string | undefined,
        name: r.fileName || "Imagen IA",
        asset: undefined as ProjectAsset | undefined,
        ts: r.createdAt || "",
        esVideo: false,
      }));
    // Esconder los assets que vienen de Productos/Escenas/Historial y de
    // Modelos: viven en su carpeta, no son media del proyecto.
    const ocultos = new Set<string>([
      ...Object.values(productSceneAssetMap),
      ...Object.values(modelAssetMap),
    ]);
    // Esconder también la COPIA-asset de una generada: al "materializarla"
    // (Utilizar / Usar como referencia / Animar) se sube con el MISMO nombre de
    // archivo, y sin esto la imagen aparece dos veces. Por nombre, así cubre
    // también las copias viejas hechas antes de este fix.
    const nombresGenerados = new Set(
      imageGenResults.map((r) => r.fileName).filter((n): n is string => !!n),
    );
    // Y las fotos de PERSONAJES, también por nombre (el asset se sube como
    // `${nombre}.ext`): cubre los huérfanos importados antes de persistir el
    // map y los duplicados.
    const nombresDeModelos = new Set(
      modelItems.map((m) => (m.nombre || "modelo").slice(0, 40)),
    );
    const sinExtension = (n: string | null) => (n || "").replace(/\.[^.]+$/, "");
    const subidas = imageLibraryAssets
      .filter(
        (a) =>
          !ocultos.has(a.id) &&
          !(a.name && nombresGenerados.has(a.name)) &&
          !nombresDeModelos.has(sinExtension(a.name)),
      )
      .map((a) => ({
        key: `asset:${a.id}`,
        kind: "asset" as const,
        id: a.id,
        url: assetPreviewUrls[a.id] as string | undefined,
        name: a.name || "Imagen",
        asset: a as ProjectAsset | undefined,
        ts: a.created_at || "",
        esVideo: false,
      }));
    // Los VIDEOS del proyecto entran sólo para el slot de referencia: un video
    // de referencia guía estilo, ritmo y cámara del video nuevo. Un frame es
    // una imagen, así que ahí no se ofrecen
    // (ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente).
    const videos =
      selectorDeImagen?.kind === "ref"
        ? libraryAssets
            .filter((a) => a.type === "video")
            .map((a) => ({
              key: `asset:${a.id}`,
              kind: "asset" as const,
              id: a.id,
              url: assetPreviewUrls[a.id] as string | undefined,
              name: a.name || "Video",
              asset: a as ProjectAsset | undefined,
              ts: a.created_at || "",
              esVideo: true,
            }))
        : [];
    // Más nuevo arriba; las que no tienen fecha conocida caen al final.
    return [...subidas, ...videos, ...generadas].sort((a, b) =>
      (b.ts || "").localeCompare(a.ts || ""),
    );
  }, [
    imageGenResults,
    imageLibraryAssets,
    libraryAssets,
    selectorDeImagen,
    assetPreviewUrls,
    productSceneAssetMap,
    modelAssetMap,
    modelItems,
  ]);

  // Un personaje se importa y va DIRECTO al slot de referencia, que es el único
  // que lo ofrece (ver el filtro de pestañas más abajo): ese slot acepta varias
  // imágenes y un modelo es exactamente eso, una.
  const elegirPersonajeEnSelector = useCallback(
    async (model: ModelV2) => {
      const target = selectorDeImagen;
      if (
        !target ||
        (target.kind !== "ref" && target.kind !== "attach") ||
        modelsImportingIds.has(model.id)
      )
        return;
      const token = selectorTokenRef.current;
      setModelsImportingIds((prev) => new Set(prev).add(model.id));
      try {
        const assetId = await importModelToImageAsset(model);
        if (!assetId || selectorTokenRef.current !== token) return;
        elegirEnDestino(target, assetId);
        setSelectorDeImagen(null);
      } finally {
        setModelsImportingIds((prev) => {
          const n = new Set(prev);
          n.delete(model.id);
          return n;
        });
      }
    },
    [
      selectorDeImagen,
      modelsImportingIds,
      importModelToImageAsset,
      elegirEnDestino,
    ],
  );

  // Las pestañas del selector: una por fuente de imágenes de la cuenta, con sus
  // miniaturas adentro. Reemplazan al dropdown que sólo NOMBRABA las fuentes
  // ("Usar desde biblioteca") y te mandaba a otro panel a buscarlas.
  const pestanasDelSelector = useMemo<PestanaDelSelector[]>(() => {
    // Un frame es una imagen TERMINADA de la escena; un personaje es materia
    // prima (hay que vestirlo y ubicarlo antes, que es lo que hace Generar con
    // IA). Como referencia sí sirve tal cual: ese slot es justamente "mirá
    // esto".
    const conPersonajes =
      selectorDeImagen?.kind === "ref" || selectorDeImagen?.kind === "attach";
    const todas: PestanaDelSelector[] = [
      {
        id: "subidas",
        label: "Multimedia",
        Icon: Images,
        cargando: imageLibraryLoading,
        vacio:
          selectorDeImagen?.kind === "ref"
            ? "Todavía no subiste ni generaste imágenes ni videos en este proyecto."
            : "Todavía no subiste ni generaste imágenes en este proyecto.",
        conAcciones: true,
        items: imagenesSubidas.map((i) => ({
          id: i.key,
          url: i.url,
          nombre: i.name,
          esVideo: i.esVideo,
          importando:
            i.kind === "generated" && imageGenImportingGenIds.has(i.id),
        })),
        onElegir: (item) => {
          const original = imagenesSubidas.find((i) => i.key === item.id);
          if (!original) return;
          // Un asset del proyecto ya es lo que el slot necesita. Una generada
          // todavía no: se materializa al vuelo.
          if (original.kind === "asset") {
            elegirAssetEnSelector(original.id);
            return;
          }
          void importarYElegirEnSelector(
            original.id,
            imageGenImportingGenIds,
            setImageGenImportingGenIds,
            () => importGeneratedImageAsAsset(original.id),
          );
        },
      },
      {
        id: "creaciones",
        label: "Creaciones",
        Icon: Clock,
        cargando: filesHistorialLoading,
        vacio: "Todavía no generaste nada con esta marca.",
        items: filesHistorialItems.map((h) => ({
          id: h.id,
          url: h.imageUrl,
          nombre: h.name || "Creación",
          importando: filesImportingHistorialIds.has(h.id),
        })),
        onElegir: (item) => {
          const original = filesHistorialItems.find((h) => h.id === item.id);
          if (!original) return;
          void importarYElegirEnSelector(
            original.id,
            filesImportingHistorialIds,
            setFilesImportingHistorialIds,
            () => importProductToImageAsset(original),
          );
        },
      },
      {
        id: "productos",
        label: "Productos",
        Icon: Package,
        cargando: filesProductsLoading,
        vacio: "No hay productos en esta marca.",
        items: filesProductItems.map((pr) => ({
          id: pr.id,
          url: pr.imageUrl,
          nombre: pr.name || "Producto",
          sku: pr.sku ?? null,
          importando: filesImportingProductIds.has(pr.id),
        })),
        onElegir: (item) => {
          const original = filesProductItems.find((pr) => pr.id === item.id);
          if (!original) return;
          void importarYElegirEnSelector(
            original.id,
            filesImportingProductIds,
            setFilesImportingProductIds,
            () => importProductToImageAsset(original),
          );
        },
      },
      {
        id: "personajes",
        label: palabraDeModeloActual().tituloPlural,
        Icon: Users,
        cargando: modelsLoading,
        vacio: `Esta cuenta todavía no tiene ${palabraDeModeloActual().plural}.`,
        // Fotos de cuerpo entero: a cuadrado se les corta la cabeza o los pies.
        aspecto: "retrato" as const,
        items: modelItems.map((m) => ({
          id: String(m.id),
          url: m.thumbnailUrl,
          nombre: m.nombre || palabraDeModeloActual().titulo,
          importando: modelsImportingIds.has(m.id),
        })),
        onElegir: (item) => {
          const original = modelItems.find((m) => String(m.id) === item.id);
          if (original) void elegirPersonajeEnSelector(original);
        },
      },
    ];
    return todas.filter((p) => p.id !== "personajes" || conPersonajes);
  }, [
    selectorDeImagen,
    imagenesSubidas,
    imageLibraryLoading,
    imageGenImportingGenIds,
    importGeneratedImageAsAsset,
    filesHistorialItems,
    filesHistorialLoading,
    filesImportingHistorialIds,
    filesProductItems,
    filesProductsLoading,
    filesImportingProductIds,
    importProductToImageAsset,
    modelItems,
    modelsLoading,
    modelsImportingIds,
    elegirPersonajeEnSelector,
    elegirAssetEnSelector,
    importarYElegirEnSelector,
  ]);

  // Lo que no es una grilla: traer un archivo de la compu o generar una imagen
  // nueva. Van como baldosas al frente de la primera pestaña, que es donde el
  // usuario aterriza.
  const accionesDelSelector = useMemo<AccionDelSelector[]>(() => {
    const target = selectorDeImagen;
    if (!target) return [];
    const acciones: AccionDelSelector[] = [
      {
        id: "subir",
        label: target.kind === "ref" ? "Subir imagen o video" : "Subir imagen",
        Icon: Upload,
        onClick: () => {
          setSelectorDeImagen(null);
          const input =
            target.kind === "first"
              ? sceneFirstFrameRef.current
              : target.kind === "last"
                ? sceneLastFrameRef.current
                : target.kind === "attach"
                  ? imageGenFileInputRef.current
                  : sceneRefImageRef.current;
          input?.click();
        },
      },
      {
        id: "generar",
        label: "Generar con IA",
        Icon: Sparkles,
        tono: "ia" as const,
        onClick: () => {
          if (target.kind !== "attach") openImageGenPanel(target);
        },
      },
    ];
    // Desde el panel de generar imagen no tiene sentido "Generar con IA": ya está ahí.
    return target.kind === "attach"
      ? acciones.filter((a) => a.id !== "generar")
      : acciones;
  }, [selectorDeImagen, openImageGenPanel]);

  // Extract a frame (first or last) from a clip and assign it to the target slot
  const pickFrameFromClip = useCallback(
    async (clipId: string, which: "first" | "last") => {
      const target = frameFromVideoTarget;
      if (!target || !projectId || !companyId) return;
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      setFrameFromVideoClipMenu(null);

      try {
        // Resolve a frame data/URL from available sources. For AI-generated
        // clips we have a full-resolution first/last frame already cached. For
        // regular video clips we deliberately SKIP the clipThumbnails strip
        // (which is intentionally low-res for the timeline) and capture from
        // the original video source so the picked frame stays sharp.
        let frameDataUrl: string | null = null;
        if (clip.type === "ai-generated") {
          frameDataUrl =
            which === "first"
              ? clip.aiFirstFrame || null
              : clip.aiLastFrame || null;
        }
        // Capture from the video source using a canvas at native resolution.
        if (!frameDataUrl && clip.src) {
          frameDataUrl = await new Promise<string | null>((resolve) => {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.src = clip.src!;
            video.muted = true;
            video.preload = "auto";
            const onLoaded = () => {
              const seekTo =
                which === "first"
                  ? 0.05
                  : Math.max(0, (video.duration || 0) - 0.05);
              video.currentTime = seekTo;
            };
            const onSeeked = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 720;
                canvas.height = video.videoHeight || 1280;
                const ctx = canvas.getContext("2d");
                if (!ctx) return resolve(null);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.92));
              } catch {
                resolve(null);
              }
            };
            video.addEventListener("loadedmetadata", onLoaded, { once: true });
            video.addEventListener("seeked", onSeeked, { once: true });
            video.addEventListener("error", () => resolve(null), {
              once: true,
            });
          });
        }
        if (!frameDataUrl) {
          console.warn(
            "[VideoEditor] Could not extract frame for clip",
            clipId,
          );
          return;
        }

        // Convert to File and upload as project asset
        const res = await fetch(frameDataUrl);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
        const file = new File(
          [blob],
          `frame-${clip.id}-${which}-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const blobUrl = URL.createObjectURL(blob);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));

        // Route through the slot-preview screen instead of assigning directly,
        // so the user can confirm (Utilizar) or branch into editing the image
        // (Editar → image-gen flow with this frame attached as reference).
        setSlotPreview({ assetId: asset.id, target });
        setSlotPreviewEditing(false);
        setSlotPreviewEditPrompt("");
        setActivePanel("ai-image-gen");
      } catch (err) {
        console.error("[VideoEditor] pickFrameFromClip failed:", err);
      } finally {
        setFrameFromVideoTarget(null);
      }
    },
    [frameFromVideoTarget, projectId, companyId, clips],
  );

  // Extend an existing video: open AI scene panel (General + frames) with the clip's last frame
  // pre-loaded as the new scene's first frame.
  const extendVideoClip = useCallback(
    async (clipId: string) => {
      if (!projectId || !companyId) return;
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;

      try {
        // Resolve the last frame at native resolution. AI-generated clips
        // already have full-res aiLastFrame cached; for regular video clips we
        // skip the low-res timeline thumbnails and capture directly from the
        // video source.
        let frameDataUrl: string | null = null;
        if (clip.type === "ai-generated") {
          frameDataUrl = clip.aiLastFrame || null;
        }
        if (!frameDataUrl && clip.src) {
          frameDataUrl = await new Promise<string | null>((resolve) => {
            const video = document.createElement("video");
            video.crossOrigin = "anonymous";
            video.src = clip.src!;
            video.muted = true;
            video.preload = "auto";
            const onLoaded = () => {
              video.currentTime = Math.max(0, (video.duration || 0) - 0.05);
            };
            const onSeeked = () => {
              try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth || 720;
                canvas.height = video.videoHeight || 1280;
                const ctx = canvas.getContext("2d");
                if (!ctx) return resolve(null);
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.92));
              } catch {
                resolve(null);
              }
            };
            video.addEventListener("loadedmetadata", onLoaded, { once: true });
            video.addEventListener("seeked", onSeeked, { once: true });
            video.addEventListener("error", () => resolve(null), {
              once: true,
            });
          });
        }
        if (!frameDataUrl) {
          console.warn(
            "[VideoEditor] extendVideoClip: could not extract last frame for",
            clipId,
          );
          return;
        }

        const res = await fetch(frameDataUrl);
        const blob = await res.blob();
        const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
        const file = new File(
          [blob],
          `extend-${clip.id}-${Date.now()}.${ext}`,
          { type: blob.type || "image/jpeg" },
        );
        const blobUrl = URL.createObjectURL(blob);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await uploadProjectAsset({
          file,
          projectId,
          companyId,
          userId: user?.id,
        });
        setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));

        // Configure the ai-scene panel: General + frames, with this frame as first frame.
        setEditingClipId(null);
        setSceneMode("general");
        setSceneRefMode("frames");
        setScenePrompt("");
        setSceneDialogue("");
        setSceneRefAssetIds([]);
        setSceneFirstFrameAssetId(asset.id);
        setSceneLastFrameAssetId(null);
        // Se entró desde el panel del clip: la cabecera dibuja la flecha de
        // volver a él.
        setPanelDeVueltaDeEscena("clip");
        setActivePanel("ai-scene");
      } catch (err) {
        console.error("[VideoEditor] extendVideoClip failed:", err);
      }
    },
    [projectId, companyId, clips],
  );

  // Upload a file and attach it to the next image generation (and add to persistent gallery)
  const handleImageGenAttachUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    const blobUrl = URL.createObjectURL(file);
    setImageGenUploading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      setImageGenGalleryAssetIds((prev) =>
        prev.includes(asset.id) ? prev : [...prev, asset.id],
      );
      setImageGenAttachedAssetIds((prev) =>
        prev.includes(asset.id) ? prev : [...prev, asset.id],
      );
    } catch (err) {
      console.error("[VideoEditor] image-gen attach upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    } finally {
      setImageGenUploading(false);
    }
  };

  // Resolve signed URLs + metadata (nombre/tipo) para los assets de la galería.
  // Fetcheamos los ids que no tengan META todavía (aunque tengan preview), así
  // un video persistido —que tiene blob pero no es imagen— queda con type
  // "video" y se filtra del modal.
  useEffect(() => {
    const needFetch = imageGenGalleryAssetIds.filter(
      (id) => !galleryAssetMeta[id] || !assetPreviewUrls[id],
    );
    if (needFetch.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const assets = await fetchAssetsByIds(needFetch);
        if (cancelled) return;
        setGalleryAssetMeta((prev) => {
          const next = { ...prev };
          for (const a of assets)
            next[a.id] = { name: a.name, type: a.type };
          return next;
        });
        const results = await Promise.all(
          assets
            .filter((a) => !assetPreviewUrls[a.id])
            .map(async (a) => {
              try {
                const url = await getAssetSignedUrl(a.storage_path);
                return url ? ([a.id, url] as const) : null;
              } catch {
                return null;
              }
            }),
        );
        if (cancelled) return;
        const updates: Record<string, string> = {};
        for (const r of results) if (r) updates[r[0]] = r[1];
        if (Object.keys(updates).length > 0) {
          setAssetPreviewUrls((prev) => ({ ...prev, ...updates }));
        }
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
  }, [imageGenGalleryAssetIds, assetPreviewUrls, galleryAssetMeta]);

  const handleRefImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId || !companyId) return;
    const blobUrl = URL.createObjectURL(file);
    try {
      // Un video se mide ANTES de subirlo: uno de más de 29 s no sirve como
      // referencia y no tiene por qué ocupar Storage (revisión de Greptile al
      // PR #149). Si no se pudo medir, se sube igual y decide la duración que
      // sondea la subida.
      let duracionMedida: number | null = null;
      if (esArchivoDeVideo(file)) {
        duracionMedida = await duracionDeVideoPorUrl(blobUrl);
        const veredicto = evaluarVideoDeReferencia(duracionMedida);
        if (!veredicto.ok) {
          URL.revokeObjectURL(blobUrl);
          setUploadErrorMsg(veredicto.motivo);
          return;
        }
      }
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const asset = await uploadProjectAsset({
        file,
        projectId,
        companyId,
        userId: user?.id,
      });
      setAssetPreviewUrls((prev) => ({ ...prev, [asset.id]: blobUrl }));
      if (asset.type === "video") {
        // Queda en la lista de videos del proyecto (es lo que mira el panel
        // para dibujarlo y la regla de calidad) y entra por la misma puerta que
        // uno elegido del selector.
        // Con la duración MEDIDA si la subida no la sondeó: la guarda al
        // generar mira este asset, y sin ella dejaría pasar un video largo en
        // Básica (revisión de Greptile al PR #149).
        const assetConDuracion: ProjectAsset = {
          ...asset,
          duration_seconds: asset.duration_seconds ?? duracionMedida,
        };
        setLibraryAssets((prev) =>
          prev.some((a) => a.id === asset.id) ? prev : [assetConDuracion, ...prev],
        );
        const veredicto = evaluarVideoDeReferencia(assetConDuracion.duration_seconds);
        if (!veredicto.ok) {
          setUploadErrorMsg(veredicto.motivo);
          return;
        }
        if (veredicto.calidad === "profesional") setSceneQuality("profesional");
        const otrosVideos = new Set(
          libraryAssets.filter((a) => a.type === "video").map((a) => a.id),
        );
        setSceneRefAssetIds((prev) => [
          ...prev.filter((id) => !otrosVideos.has(id)),
          asset.id,
        ]);
        return;
      }
      setSceneRefAssetIds((prev) => [...prev, asset.id]);
    } catch (err) {
      console.error("[VideoEditor] Reference image upload failed:", err);
      URL.revokeObjectURL(blobUrl);
      setUploadErrorMsg(describeUploadError(err, file.name));
    }
  };

  // Guard síncrona anti doble-click: clips con una generación de video IA en
  // vuelo. El estado de React (aiExecutionId) se publica async, así que dos
  // clicks en el mismo tick leerían ambos el clip "sin id" y generarían dos
  // jobs con UUIDs distintos → dos videos cobrados. El ref se toca sincrónico,
  // sin ventana de carrera (JS single-thread, no hay await entre check y add).
  const aiVideoInFlightRef = useRef<Set<string>>(new Set());

  const generateAiClip = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      // Ya hay una generación en vuelo para este clip (doble click / carrera
      // con el panel): el primer click ya la está haciendo — no-op.
      if (aiVideoInFlightRef.current.has(clipId)) return;
      // Guard explícita: si falta info crítica marcamos el clip con error
      // visible en vez de hacer return silencioso (sino el usuario clickea
      // Reintentar y no pasa nada).
      // El motivo distingue los dos casos: un clip guardado sin prompt (los que
      // dejó el incidente 2026-09-07/08) mandaba a recargar la página, y
      // recargar no lo arregla — lo que falta es escribir el prompt.
      const motivoIncompleto = motivoDeClipSinDatos({
        sceneMode: clip.aiSceneMode ?? "general",
        prompt: clip.aiPrompt,
        multiescena: !!clip.aiMultiEnabled,
        tieneProyecto: !!companyId && !!projectId,
      });
      if (motivoIncompleto) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError: motivoIncompleto,
                }
              : c,
          ),
        );
        return;
      }

      // Pre-flight credit check. Centralized here so retry/draft/agent paths
      // all hit the same guard — server still enforces the spend.
      // Con execution_id es RETOMAR un pedido que ya se hizo (se cortó al cerrar
      // el editor, o un fallo técnico): si ya terminó, el backend lo devuelve sin
      // cobrar de nuevo, así que el saldo de ahora no tiene por qué frenarlo. El
      // backend sigue controlando el gasto
      // (ADR-2026-11-03-nada-queda-trabado-al-recargar).
      const retomando = !!clip.aiExecutionId;
      if (
        !retomando &&
        !assertCredits(
          creditsForAiClip(clip, segundosDeVideoDeReferencia(clip.aiRefAssetIds)),
        )
      )
        return;

      aiVideoInFlightRef.current.add(clipId);

      // Mark as generating
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiStatus: "generating" as AiClipStatus,
                aiError: undefined,
                aiGenerationStartedAt: Date.now(),
                // Mismo fallback que el alta de escena: en `personas` el prompt
                // visual es opcional (el texto vive en `aiDialogue`), así que
                // acá ya no se puede asumir que existe.
                name: clip.aiPrompt?.trim().slice(0, 30) || "Escena IA",
              }
            : c,
        ),
      );

      // Dialog mode overrides ref_mode with `dialogo` / `dialogo_movimiento`.
      // Fuera del try: el catch lo necesita para decidir si el execution_id
      // se conserva (solo tiene sentido si la request ruteó a atlas).
      const refModeForApi =
        clip.aiSceneMode === "personas"
          ? clip.aiPersonType === "motion"
            ? "dialogo_movimiento"
            : "dialogo"
          : clip.aiRefMode || "none";

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        // Reintento del MISMO pedido (parámetros iguales, intento previo sin
        // concluir) → mismo execution_id: el backend dedupea y no cobra un
        // segundo video. Pedido nuevo o parámetros cambiados → id nuevo.
        const attemptRef = creatorAttemptRef(
          clip.aiExecutionId && clip.aiExecutionParamsKey
            ? {
                executionId: clip.aiExecutionId,
                paramsKey: clip.aiExecutionParamsKey,
              }
            : null,
          creatorParamsKey({
            prompt: clip.aiPrompt,
            duration: clampDuracionDeEscena(clip.duration),
            refMode: refModeForApi,
            resolution: clip.aiResolution || "720p",
            quality: clip.aiQuality || "estandar",
            aspectRatio,
            speechSpeed:
              clip.aiSceneMode === "personas"
                ? (clip.aiSpeechSpeed ?? 1.1)
                : null,
            multiScenes:
              clip.aiMultiEnabled && clip.aiMultiScenes
                ? clip.aiMultiScenes.map((s) => ({
                    prompt: s.prompt,
                    duration: s.duration,
                  }))
                : null,
            refAssetIds: clip.aiRefAssetIds ?? null,
            firstFrameAssetId: clip.aiFirstFrameAssetId ?? null,
            lastFrameAssetId: clip.aiLastFrameAssetId ?? null,
            voiceId: clip.aiVoiceId ?? null,
            dialogue: clip.aiDialogue ?? null,
            dialogueInputMode: clip.aiDialogueInputMode ?? null,
            audioUrl: clip.aiDialogueAudioUrl ?? null,
          }),
        );
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiExecutionId: attemptRef.executionId,
                  aiExecutionParamsKey: attemptRef.paramsKey,
                }
              : c,
          ),
        );
        const body: Record<string, any> = {
          user_id: session.user.id,
          project_id: projectId,
          company_id: companyId,
          execution_id: attemptRef.executionId,
          prompt: clip.aiPrompt,
          // Redondeado como en el alta de escena ("ningún float sale por el
          // request") y para que coincida con la clave de idempotencia.
          duration: clampDuracionDeEscena(clip.duration),
          ref_mode: refModeForApi,
          // Trackeo (ADR-585): video generado desde el Estudio.
          modo_utilizado: "estudio_video",
          aspect_ratio: aspectRatio,
          quality: clip.aiQuality || "estandar",
          resolution: clip.aiResolution || "720p",
          // Mismo metaprompting que al crear la escena (ver `addAiSceneClip`).
          ...camposDelEscritorDeVideo(clip.aiSceneMode, clip.aiPromptEscrito),
          ...(clip.aiSceneMode === "personas" && clip.aiVoiceId
            ? { voz_id: clip.aiVoiceId }
            : {}),
          ...(clip.aiSceneMode === "personas" && clip.aiDialogue
            ? { dialogo: clip.aiDialogue }
            : {}),
          ...(clip.aiSceneMode === "personas"
            ? { velocidad_voz: clip.aiSpeechSpeed ?? 1.1 }
            : {}),
          // Audio input for static-personas dialog (ElevenLabs speech-to-speech
          // instead of TTS). Workflow branches on `dialogo_input_mode`.
          ...(clip.aiSceneMode === "personas" &&
          clip.aiPersonType !== "motion" &&
          clip.aiDialogueInputMode === "audio" &&
          clip.aiDialogueAudioUrl
            ? {
                dialogo_input_mode: "audio",
                audio_url: clip.aiDialogueAudioUrl,
                audio_duration_sec: clip.aiDialogueAudioDurationSec ?? null,
              }
            : clip.aiSceneMode === "personas"
              ? { dialogo_input_mode: "texto" }
              : {}),
          ...(clip.aiMultiEnabled && clip.aiMultiScenes
            ? {
                multiescena: true,
                escenas: clip.aiMultiScenes.map((s) => ({
                  prompt: s.prompt,
                  duration: s.duration,
                })),
              }
            : {}),
        };

        // Resolve asset IDs to signed URLs
        const assetIdsToResolve: string[] = [];
        if (clip.aiRefAssetIds) assetIdsToResolve.push(...clip.aiRefAssetIds);
        if (clip.aiFirstFrameAssetId)
          assetIdsToResolve.push(clip.aiFirstFrameAssetId);
        if (clip.aiLastFrameAssetId)
          assetIdsToResolve.push(clip.aiLastFrameAssetId);
        const assetsById = new Map<string, ProjectAsset>();
        if (assetIdsToResolve.length > 0) {
          const assets = await fetchAssetsByIds(assetIdsToResolve);
          for (const a of assets) assetsById.set(a.id, a);
        }
        const resolveUrl = async (assetId?: string) => {
          if (!assetId) return null;
          const a = assetsById.get(assetId);
          if (!a) return null;
          return await getAssetSignedUrl(a.storage_path);
        };

        if (clip.aiRefAssetIds && clip.aiRefAssetIds.length > 0) {
          // Las imágenes van en `reference_images`; un video, en
          // `reference_videos` con modo "referencia" (el proveedor toma uno):
          // guía estilo, ritmo y cámara del video nuevo
          // (ADR-2026-09-26-video-de-referencia-en-el-estudio-y-el-agente).
          const imagenes: string[] = [];
          let videoDeReferencia: string | null = null;
          for (const id of clip.aiRefAssetIds) {
            const url = await resolveUrl(id);
            if (!url) continue;
            if (assetsById.get(id)?.type === "video") {
              if (!videoDeReferencia) videoDeReferencia = url;
            } else imagenes.push(url);
          }
          if (imagenes.length > 0) body.reference_images = imagenes;
          if (videoDeReferencia)
            body.reference_videos = [
              { url: videoDeReferencia, modo: "referencia" },
            ];
        }
        const firstUrl = await resolveUrl(clip.aiFirstFrameAssetId);
        if (firstUrl) body.first_frame = firstUrl;
        const lastUrl = await resolveUrl(clip.aiLastFrameAssetId);
        if (lastUrl) body.last_frame = lastUrl;

        // Legacy base64 fallback for old projects
        if (!body.first_frame && clip.aiFirstFrame)
          body.first_frame = clip.aiFirstFrame;
        if (!body.last_frame && clip.aiLastFrame)
          body.last_frame = clip.aiLastFrame;
        if (!body.reference_images && clip.aiRefImage)
          body.reference_images = [clip.aiRefImage];

        // Helper con flag atlas/n8n y ruteo parcial por ref_mode
        // ('dialogo_movimiento' sigue en n8n) — timeout de 5 min adentro
        // para que el clip no quede en "generating" eterno. Ver ADR-584.
        const creator = await generateVideoCreator(body).catch((e) => {
          if (escenasCanceladasRef.current.has(attemptRef.executionId))
            throw new EscenaCancelada();
          throw e;
        });
        if (escenasCanceladasRef.current.has(attemptRef.executionId))
          throw new EscenaCancelada();
        if (creator.pending) {
          // 202: el wait del backend venció pero el video sigue generándose.
          throw new Error(
            "La generación está tardando más de lo normal. Tocá Reintentar en un rato: si ya terminó, la traemos sin volver a cobrar.",
          );
        }
        const videoPath = creator.path;

        if (videoPath) {
          // The workflow's `reduce images` node ran by now — tell the
          // sidebar (and any other listener) to refetch the credit balance.
          window.dispatchEvent(new Event("atlas:credits-updated"));

          // Get signed URL from the returned storage path
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(videoPath, 3600);

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "ready" as AiClipStatus,
                    storagePath: videoPath,
                    src: urlData?.signedUrl || undefined,
                    // Borrador: con el archivo entregado; si otra edición lo reemplaza,
                    // deja de ser finalizable.
                    aiBorrador: creator.borrador
                      ? { ...creator.borrador, storagePath: videoPath }
                      : undefined,
                    // Intento concluido: un próximo "generar" es un pedido
                    // nuevo y debe llevar execution_id nuevo.
                    aiExecutionId: undefined,
                    aiExecutionParamsKey: undefined,
                  }
                : c,
            ),
          );

          // Probe real video duration and update the clip. Important when the
          // placeholder duration was estimated (e.g. dialogue mode).
          if (urlData?.signedUrl) {
            const probe = document.createElement("video");
            probe.preload = "metadata";
            probe.src = urlData.signedUrl;
            probe.addEventListener("loadedmetadata", () => {
              const dur = probe.duration;
              if (isFinite(dur) && dur > 0) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId
                      ? { ...c, duration: dur, sourceDuration: dur }
                      : c,
                  ),
                );
              }
            });
          }

          // Register the AI video as a project asset so it appears in the Video library
          // and persists across clip deletions.
          (async () => {
            try {
              const asset = await registerExistingAsset({
                projectId: projectId!,
                companyId: companyId!,
                userId: session.user.id,
                storagePath: videoPath,
                type: "video",
                name: clip.aiPrompt?.slice(0, 80) || "Video IA",
                mimeType: "video/mp4",
                durationSeconds: clip.duration,
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn("[VideoEditor] Could not register AI asset:", e);
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El video se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          })();
        } else {
          throw new Error("Generation failed");
        }
      } catch (err) {
        // Cancelada por el usuario: el clip ya quedó en borrador.
        if (err instanceof EscenaCancelada) return;
        console.error("[VideoEditor] AI generation failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La generación tardó más de 5 minutos y se dejó de esperar. Probá de nuevo en un rato."
            : err instanceof Error
              ? err.message
              : "Error desconocido al generar el video.";
        // El execution_id se conserva SOLO si el reintento puede re-engancharse:
        // fallo TÉCNICO (Error plano: timeout/red/202 — el job puede seguir
        // corriendo) Y la request ruteó a atlas (que dedupea por idempotency
        // key). GenerateAdError = el backend CONCLUYÓ → id inútil. Modos
        // n8n-only (dialogo_movimiento) o flag de atlas apagado = n8n, que NO
        // dedupea → conservar el id sería falsa protección.
        const definitivo =
          err instanceof GenerateAdError ||
          !USE_ATLAS_BACKEND ||
          isN8nOnlyCreatorRefMode(refModeForApi);
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiStatus: "error" as AiClipStatus,
                  aiError: msg,
                  ...(definitivo
                    ? {
                        aiExecutionId: undefined,
                        aiExecutionParamsKey: undefined,
                      }
                    : {}),
                }
              : c,
          ),
        );
      } finally {
        aiVideoInFlightRef.current.delete(clipId);
      }
    },
    [clips, companyId, projectId, aspectRatio, assertCredits, segundosDeVideoDeReferencia],
  );

  // ── Traducción de video con lip-sync (HeyGen Video Translate v3) ────────
  // Flow async en 2 fases:
  //   1) Frontend POST a /traducir_video_creator_start → n8n llama a HeyGen,
  //      recibe `video_translation_id`, devuelve `{ jobId }` rápido.
  //   2) Frontend polea /traducir_video_creator_status cada 15s con el jobId
  //      hasta que HeyGen reporta `completed`. Cuando termina, n8n descarga
  //      el video traducido + lo sube a Supabase + responde con el path.
  //   3) El frontend REEMPLAZA el clip en la timeline con el video nuevo
  //      (mismo startTime + trackIndex, nuevo src/asset/duration).
  //
  // El polling sobrevive a re-renders porque el state vive en el clip
  // (`aiTranslating`, `aiTranslateJobId`), no en estado local del callback.
  // Si el usuario refresca la página mientras está procesando, el flag
  // queda guardado y se podría retomar el polling — eso lo dejamos para
  // después si hace falta. Por ahora si refresca, pierde el polling pero
  // el video traducido igual queda en la biblioteca cuando HeyGen termine
  // (n8n lo sube a Supabase sin importar si el frontend está escuchando).
  const TRANSLATE_POLL_MS = 15_000;
  const TRANSLATE_MAX_POLLS = 80; // ~20 min máximo (15s × 80)

  // Reintenta SOLO el INSERT en `video_project_assets` para un clip cuyo
  // archivo binario YA está en Supabase Storage pero cuyo registro falló
  // (estado: `aiAssetWarning` seteado en el clip). El badge ámbar del clip
  // dispara esto al hacer click. No re-genera el video/audio ni descuenta
  // créditos — solo intenta hacer el INSERT con los datos del clip.
  const retryAssetRegistration = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !clip.aiAssetWarning) return;
      if (!companyId || !projectId) return;

      const assetType: "video" | "audio" =
        clip.type === "audio" ? "audio" : "video";
      const mimeType = assetType === "audio" ? "audio/mpeg" : "video/mp4";
      const fallbackName =
        assetType === "audio"
          ? clip.aiAudioType === "music"
            ? "Música IA"
            : "Voz IA"
          : "Video IA";
      const name = (clip.aiPrompt || clip.name || fallbackName).slice(0, 80);

      setRetryingAssetForClipId(clipId);
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        const asset = await registerExistingAsset({
          projectId,
          companyId,
          userId: session.user.id,
          storagePath: clip.storagePath,
          type: assetType,
          name,
          mimeType,
          ...(assetType === "video" && clip.duration
            ? { durationSeconds: clip.duration }
            : {}),
        });

        if (!asset) throw new Error("El registro devolvió null");

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  assetId: asset.id,
                  aiAssetWarning: undefined,
                }
              : c,
          ),
        );
      } catch (e) {
        console.warn("[VideoEditor] Retry asset registration failed:", e);
        const msg = e instanceof Error ? e.message : "Error desconocido";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiAssetWarning: `Reintento fallido: ${msg}. Probá de nuevo en unos segundos.`,
                }
              : c,
          ),
        );
      } finally {
        setRetryingAssetForClipId(null);
      }
    },
    [clips, companyId, projectId],
  );

  // Reintenta generar la signed URL de un clip cuyo `loadError` está seteado.
  // El badge rojo en el clip dispara esto al hacer click. Si la signed URL
  // se genera bien, limpia `loadError` y setea `src` para que el clip
  // vuelva a ser reproducible.
  const retryLoadSignedUrl = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !clip.loadError) return;

      setRetryingLoadForClipId(clipId);
      try {
        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(clip.storagePath, 3600);
        if (!urlData?.signedUrl) {
          throw new Error(
            "El archivo no se encontró en el servidor. Probá refrescar la página o regenerá el clip.",
          );
        }
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, src: urlData.signedUrl, loadError: undefined }
              : c,
          ),
        );
      } catch (e) {
        console.warn("[VideoEditor] Retry load signed URL failed:", e);
        const msg = e instanceof Error ? e.message : "Error desconocido";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, loadError: `Reintento fallido: ${msg}` }
              : c,
          ),
        );
      } finally {
        setRetryingLoadForClipId(null);
      }
    },
    [clips],
  );

  // "Cambiar voz" de un clip de AUDIO (Fase 1): pasa el audio del clip por
  // speech-to-speech (ElevenLabs) con la voz elegida y reemplaza el clip
  // in-place. El S2S preserva ritmo/entonación, así que suena natural. Mismo
  // patrón async que traducción/borrado (shimmer + replace in-place).
  const changeClipVoice = useCallback(
    async (clipId: string, voiceId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.voiceChangeStatus === "processing") return;
      const isAudioClip = clip.type === "audio";
      const isVideoClip =
        clip.type === "video" || clip.type === "ai-generated";
      if (!isAudioClip && !isVideoClip) return;

      // El backend exige que la URL de entrada sea firmada de Supabase.
      // Firmamos fresca desde storagePath; si no hay, usamos src sólo si ya
      // es una URL de Supabase.
      let sourceUrl: string | null = null;
      if (clip.storagePath)
        sourceUrl = await getAssetSignedUrl(clip.storagePath);
      if (!sourceUrl && clip.src && clip.src.includes(".supabase.co"))
        sourceUrl = clip.src;
      if (!sourceUrl) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: "error" as const,
                  voiceChangeError:
                    "No se pudo leer el clip. Recargá la página e intentá de nuevo.",
                }
              : c,
          ),
        );
        return;
      }
      const durationSec = clip.sourceDuration || clip.duration;

      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                voiceChangeStatus: "processing" as const,
                voiceChangeError: undefined,
                voiceChangeStartedAt: Date.now(),
              }
            : c,
        ),
      );

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Clip de audio → S2S directo (mp3). Clip de video → endpoint que
        // extrae el audio, S2S y re-muxea el audio nuevo al video (mp4).
        let newPath: string;
        if (isAudioClip) {
          const body: Record<string, any> = {
            user_id: session.user.id,
            ref_mode: "voz",
            voz_id: voiceId,
            project_id: projectId,
            // Trackeo (ADR-585): audio generado desde el Estudio.
            modo_utilizado: "estudio_audio",
            velocidad_voz: 1.0,
            dialogo_input_mode: "audio",
            audio_url: sourceUrl,
            audio_duration_sec: durationSec,
          };
          newPath = (await generateAudioCreator(body)).path;
        } else {
          newPath = (
            await changeClipVoiceVideo({
              clipUrl: sourceUrl,
              voiceId,
              durationSec,
              projectId: projectId ?? null,
            })
          ).path;
        }
        window.dispatchEvent(new Event("atlas:credits-updated"));

        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(newPath, 3600);

        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: undefined,
                  voiceChangeError: undefined,
                  voiceChangeStartedAt: undefined,
                  storagePath: newPath,
                  // Si la firma falló NO dejamos el src viejo (reproduciría la
                  // voz ANTERIOR, y se rompe al expirar esa URL): apuntamos al
                  // media nuevo con src vacío + loadError → el badge de reintento
                  // lo re-firma desde el storagePath nuevo (retryLoadForClip).
                  src: urlData?.signedUrl || undefined,
                  loadError: urlData?.signedUrl
                    ? undefined
                    : "No pudimos cargar el video con la voz nueva. Tocá para reintentar.",
                }
              : c,
          ),
        );

        // Probe de la duración real del source (sólo audio; el video conserva
        // su duración por el -c:v copy). NO tocamos `duration` para no
        // redimensionar el clip ya colocado en la timeline.
        if (isAudioClip && urlData?.signedUrl) {
          const probe = new Audio();
          probe.preload = "metadata";
          probe.src = urlData.signedUrl;
          probe.addEventListener("loadedmetadata", () => {
            const dur = probe.duration;
            if (isFinite(dur) && dur > 0) {
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, sourceDuration: dur } : c,
                ),
              );
            }
          });
        }

        // Registrar el asset nuevo en la biblioteca del proyecto.
        if (companyId && projectId) {
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: newPath,
              type: isAudioClip ? "audio" : "video",
              name: isAudioClip
                ? "Voz IA (cambiada)"
                : "Video (voz cambiada)",
              mimeType: isAudioClip ? "audio/mpeg" : "video/mp4",
            });
            if (asset)
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId ? { ...c, assetId: asset.id } : c,
                ),
              );
          } catch (e) {
            console.warn(
              "[VideoEditor] change-voice asset register failed:",
              e,
            );
          }
        }
      } catch (err) {
        console.error("[VideoEditor] change clip voice failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "El cambio de voz tardó demasiado (más de 5 min). Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al cambiar la voz.";
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  voiceChangeStatus: "error" as const,
                  voiceChangeError: msg,
                }
              : c,
          ),
        );
      }
    },
    [clips, projectId, companyId],
  );

  // Pasar a 1080p un clip borrador: el MISMO src se reemplaza, como Mejorar calidad
  // (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). La usan el botón y la tool.
  const pasarClipA1080p = useCallback(
    async (clipId: string): Promise<{ ok: boolean; mensaje?: string }> => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip || !clipEsBorradorVigente(clip) || !companyId || !projectId) {
        return { ok: false, mensaje: "Ese clip no es un borrador vigente." };
      }
      if (clip.pasarA1080pStatus === "processing") {
        return { ok: false, mensaje: "Ese clip ya se está pasando a 1080p." };
      }
      // El estado vive en el clip: shimmer + pastilla en la timeline mientras corre, y el
      // error como badge sobre el clip (mismo patrón que Corregir voz).
      const marcar = (patch: Partial<TimelineClip>) =>
        setClips((prev) => prev.map((c) => (c.id === clipId ? { ...c, ...patch } : c)));
      const fallar = (mensaje: string) => {
        marcar({ pasarA1080pStatus: "error", pasarA1080pError: mensaje, pasarA1080pExecId: undefined });
        return { ok: false, mensaje };
      };
      // El execution_id del pedido queda en el clip: si se cierra el editor a mitad, se
      // retoma por él (ADR-2026-11-03-los-procesos-se-retoman-al-volver).
      const execId = crypto.randomUUID();
      marcar({ pasarA1080pStatus: "processing", pasarA1080pError: undefined, pasarA1080pExecId: execId });
      try {
        const r = await pasarBorradorA1080p({ jobId: clip.aiBorrador!.jobId, executionId: execId });
        if (!r.ok) return fallar(r.mensaje);
        if (!r.path) return fallar("El video salió sin archivo.");
        const rutaFinal = r.path;
        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(rutaFinal, 3600);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const asset = await registerExistingAsset({
          projectId,
          companyId,
          userId: user?.id ?? "",
          storagePath: rutaFinal,
          type: "video",
          name: `${clip.name || "Video"} (1080p)`,
          mimeType: "video/mp4",
          durationSeconds: clip.duration,
        });
        // Si mientras se esperaba el usuario le aplicó otra edición al clip (Mejorar
        // calidad, Editar clip…), no se la pisa: el 1080p queda en Archivos del proyecto.
        const ahora = clipsRef.current.find((c) => c.id === clipId);
        const sigueSiendoElBorrador = !!ahora && ahora.storagePath === clip.aiBorrador!.storagePath;
        if (sigueSiendoElBorrador) {
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    src: urlData?.signedUrl || c.src,
                    storagePath: rutaFinal,
                    // Misma toma y mismo audio: el transcript sigue valiendo.
                    transcript: c.transcript ? marcarTranscript(c.transcript, rutaFinal) : undefined,
                    assetId: asset?.id ?? c.assetId,
                    aiResolution: "1080p",
                    aiBorrador: undefined,
                  }
                : c,
            ),
          );
        }
        marcar({ pasarA1080pStatus: undefined, pasarA1080pError: undefined, pasarA1080pExecId: undefined });
        window.dispatchEvent(new Event("atlas:credits-updated"));
        return sigueSiendoElBorrador
          ? { ok: true }
          : { ok: true, mensaje: "El clip cambió mientras se generaba: el 1080p quedó en Archivos del proyecto." };
      } catch (e) {
        return fallar(e instanceof Error ? e.message : "No se pudo pasar a 1080p.");
      }
    },
    [companyId, projectId],
  );

  // Sigue un job de traducción hasta que termina y reemplaza el clip. Aparte del
  // botón para poder RETOMARLO: el job queda guardado en el clip
  // (`aiTranslateJobId`) y, si se cerró el editor a mitad, se vuelve a seguir al
  // abrir el proyecto en vez de perder lo que ya se pagó
  // (ADR-2026-11-03-nada-queda-trabado-al-recargar).
  const seguirTraduccion = useCallback(
    async (clipId: string, jobId: string) => {
      const failWith = (msg: string) => {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslating: false,
                  aiTranslateError: msg,
                  aiTranslateJobId: undefined,
                }
              : c,
          ),
        );
      };
      if (!companyId || !projectId) return;
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        // 2) Polling
        let polls = 0;
        // Loop manual con sleep — más simple que recursión + setTimeout.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > TRANSLATE_MAX_POLLS) {
            throw new Error(
              "La traducción tardó demasiado (más de 20 min). Probá de nuevo en un rato.",
            );
          }
          await new Promise((r) => setTimeout(r, TRANSLATE_POLL_MS));
          // Con el editor cerrado se deja de mirar: el job queda guardado en el
          // clip y se retoma la próxima vez que se abra el proyecto.
          if (!estaMontadoRef.current) return;

          const statusJson = await postTranslateStatus({
            job_id: jobId,
            project_id: projectId,
            company_id: companyId,
          });
          if (!statusJson) continue; // error transient, reintentamos en el próximo poll

          if (statusJson.status === "failed") {
            throw new Error(
              statusJson.error || "HeyGen reportó que la traducción falló.",
            );
          }
          if (statusJson.status !== "completed") {
            continue; // running / pending → seguir polleando
          }
          if (!statusJson.path) {
            throw new Error(
              "Traducción completada pero el backend no devolvió path del video.",
            );
          }

          // 3) Completado: registrar el asset y reemplazar el clip. El clip se
          // lee recién acá: si se retomó al abrir, el de antes no existe.
          const clip = clipsRef.current.find((c) => c.id === clipId);
          if (!clip) return;
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(statusJson.path, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el video traducido.");

          let newAssetId: string | undefined;
          try {
            const asset = await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: statusJson.path,
              type: "video",
              name: `${clip.name || "Video"} (EN)`.slice(0, 80),
              mimeType: "video/mp4",
            });
            if (asset) newAssetId = asset.id;
          } catch (e) {
            console.warn(
              "[VideoEditor] Translated video registered but asset record failed:",
              e,
            );
          }

          // Probar la duración real del video traducido (HeyGen puede
          // ajustar la duración levemente para que el lip-sync calce).
          const probeDuration = async (): Promise<number | null> =>
            new Promise((resolve) => {
              const probe = document.createElement("video");
              probe.preload = "metadata";
              probe.muted = true;
              probe.src = newSrc;
              const cleanup = () => {
                probe.removeAttribute("src");
                probe.load?.();
              };
              probe.onloadedmetadata = () => {
                const d = probe.duration;
                cleanup();
                resolve(isFinite(d) && d > 0 ? d : null);
              };
              probe.onerror = () => {
                cleanup();
                resolve(null);
              };
            });
          const newDuration = (await probeDuration()) ?? clip.duration;

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    src: newSrc,
                    storagePath: statusJson.path,
                    assetId: newAssetId ?? c.assetId,
                    duration: newDuration,
                    sourceDuration: newDuration,
                    mediaOffset: 0,
                    // Otra voz en otro idioma: el transcript viejo no sirve.
                    transcript: undefined,
                    aiTranslating: false,
                    aiTranslateError: undefined,
                    aiTranslateJobId: undefined,
                  }
                : c,
            ),
          );
          // Toca el sonido de "listo" si está disponible.
          try {
            if (finishSoundRef.current) {
              finishSoundRef.current.currentTime = 0;
              finishSoundRef.current.play().catch(() => {});
            }
          } catch {}
          return;
        }
      } catch (err) {
        console.error("[VideoEditor] seguirTraduccion failed:", err);
        failWith(
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al traducir el video.",
        );
      }
    },
    [companyId, projectId],
  );

  // ── Retomar procesos que quedaron en curso al cerrar ───────────────────────
  // (ADR-2026-11-03-los-procesos-se-retoman-al-volver). Cada uno hace lo mismo que su
  // botón al terminar; si al volver el clip ya es otro archivo, el resultado queda en
  // Archivos y no lo pisa.
  const firmarDelEstudio = async (path: string) =>
    (await supabase.storage.from("video-project-assets").createSignedUrl(path, 3600)).data
      ?.signedUrl;

  const aplicarMejoraDeCalidad = useCallback(
    async (clipId: string, path: string, url?: string) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip || !projectId || !companyId) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const asset = await registerExistingAsset({
        projectId,
        companyId,
        userId: session?.user?.id ?? "",
        storagePath: path,
        type: "video",
        name: `${clip.name || "Video"} (mejorado)`,
        mimeType: "video/mp4",
        durationSeconds: clip.duration,
      }).catch(() => null);
      const src = url ?? (await firmarDelEstudio(path));
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                src: src || c.src,
                storagePath: path,
                // Mismo audio: el transcript sigue valiendo.
                transcript: c.transcript ? marcarTranscript(c.transcript, path) : undefined,
                assetId: asset?.id ?? c.assetId,
                mejorarCalidadStatus: undefined,
                mejorarCalidadJobId: undefined,
              }
            : c,
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, companyId],
  );

  const retomarProceso = useCallback(
    async (p: ProcesoARetomar) => {
      if (!projectId || !companyId) return;
      const limpiar = (patch: Partial<TimelineClip>) =>
        setClips((prev) => prev.map((c) => (c.id === p.clipId ? { ...c, ...patch } : c)));
      const clipDe = () => clipsRef.current.find((c) => c.id === p.clipId);
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? "";
      try {
        if (p.tipo === "calidad") {
          const r = await seguirUpscale(p.jobId);
          if (!estaMontadoRef.current) return;
          await aplicarMejoraDeCalidad(p.clipId, r.path, r.signedUrl);
          return;
        }
        if (p.tipo === "1080p") {
          const r = await seguirPasoA1080p(p.jobId);
          if (!estaMontadoRef.current) return;
          if (!r.ok || !r.path) {
            limpiar({
              pasarA1080pStatus: "error",
              pasarA1080pError: r.ok ? "El video salió sin archivo." : r.mensaje,
              pasarA1080pExecId: undefined,
            });
            return;
          }
          const clip = clipDe();
          const asset = await registerExistingAsset({
            projectId,
            companyId,
            userId,
            storagePath: r.path,
            type: "video",
            name: `${clip?.name || "Video"} (1080p)`,
            mimeType: "video/mp4",
            durationSeconds: clip?.duration,
          }).catch(() => null);
          const sigue = !!clip?.aiBorrador && clip.storagePath === clip.aiBorrador.storagePath;
          const src = sigue ? await firmarDelEstudio(r.path) : undefined;
          limpiar({
            ...(sigue
              ? {
                  src: src || clip!.src,
                  storagePath: r.path,
                  transcript: clip!.transcript ? marcarTranscript(clip!.transcript, r.path) : undefined,
                  assetId: asset?.id ?? clip!.assetId,
                  aiResolution: "1080p",
                  aiBorrador: undefined,
                }
              : {}),
            pasarA1080pStatus: undefined,
            pasarA1080pError: undefined,
            pasarA1080pExecId: undefined,
          });
          window.dispatchEvent(new Event("atlas:credits-updated"));
          return;
        }
        // Borrado de objeto y corregir voz: polling por su job, igual que el botón.
        const esBorrado = p.tipo === "borrado";
        for (let intento = 0; intento < ERASE_MAX_POLLS; intento++) {
          await new Promise((r) => setTimeout(r, ERASE_POLL_MS));
          if (!estaMontadoRef.current) return;
          const res = esBorrado
            ? await postVideoEraseStatus(p.sabor === "sam2" ? "sam2" : "bria", {
                job_id: p.jobId,
                project_id: projectId,
                company_id: companyId,
              })
            : await postCorregirVozClipStatus({ job_id: p.jobId });
          if (!res.ok) continue;
          const st = (await res.json().catch(() => ({}))) as {
            status?: string;
            storagePath?: string;
            error?: string;
          };
          if (st.status === "error" || st.status === "failed") throw new Error(st.error || "El proceso falló en el servidor.");
          if (st.status !== "done") continue;
          if (!st.storagePath) throw new Error("El proceso terminó sin archivo.");
          const clip = clipDe();
          const ext = st.storagePath.split(".").pop()?.toLowerCase();
          const esAudio = !esBorrado && ext === "m4a";
          await registerExistingAsset({
            projectId,
            companyId,
            userId,
            storagePath: st.storagePath,
            type: esAudio ? "audio" : "video",
            name: `${clip?.name || "Video"} (${esBorrado ? "sin objeto" : "voz corregida"})`.slice(0, 80),
            mimeType: esAudio ? "audio/mp4" : "video/mp4",
          }).catch(() => null);
          // Si el clip ya es otro archivo, no se pisa: queda en Archivos.
          const desde = esBorrado ? undefined : clip?.corregirVozDesde;
          const sigue = !!clip && (!desde || clip.storagePath === desde);
          const src = sigue ? await firmarDelEstudio(st.storagePath) : undefined;
          limpiar({
            ...(sigue
              ? {
                  ...(esBorrado
                    ? {
                        originalStoragePathBeforeErase: clip!.storagePath,
                        originalSrcBeforeErase: clip!.src,
                        transcript: clip!.transcript
                          ? marcarTranscript(clip!.transcript, st.storagePath)
                          : undefined,
                      }
                    : // La voz cambió: el transcript ya no vale (se rehace al pedirlo).
                      { transcript: undefined }),
                  src: src || clip!.src,
                  storagePath: st.storagePath,
                }
              : {}),
            ...(esBorrado
              ? { eraseStatus: undefined, eraseError: undefined, eraseJobId: undefined, eraseSabor: undefined }
              : {
                  corregirVozStatus: undefined,
                  corregirVozError: undefined,
                  corregirVozJobId: undefined,
                  corregirVozDesde: undefined,
                }),
          });
          return;
        }
        throw new Error("Tardó demasiado y se dejó de esperar. Probá de nuevo.");
      } catch (e) {
        if (!estaMontadoRef.current) return;
        const msg = e instanceof Error ? e.message : "El proceso falló.";
        if (p.tipo === "borrado")
          limpiar({ eraseStatus: "error", eraseError: msg, eraseJobId: undefined });
        else if (p.tipo === "voz")
          limpiar({ corregirVozStatus: "error", corregirVozError: msg, corregirVozJobId: undefined });
        else if (p.tipo === "1080p")
          limpiar({ pasarA1080pStatus: "error", pasarA1080pError: msg, pasarA1080pExecId: undefined });
        else {
          limpiar({ mejorarCalidadStatus: undefined, mejorarCalidadJobId: undefined });
          mostrarAviso("No se pudo mejorar la calidad", msg);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, companyId, aplicarMejoraDeCalidad],
  );
  useEffect(() => {
    if (projectLoading || !companyId || !projectId) return;
    const pendientes = procesosARetomarRef.current;
    if (pendientes.length === 0) return;
    procesosARetomarRef.current = [];
    for (const p of pendientes) void retomarProceso(p);
  }, [projectLoading, companyId, projectId, retomarProceso]);

  // Retomar las traducciones que quedaron en curso, una vez que el proyecto
  // abrió y se sabe la empresa.
  useEffect(() => {
    if (projectLoading || !companyId || !projectId) return;
    const pendientes = traduccionesARetomarRef.current;
    if (pendientes.length === 0) return;
    traduccionesARetomarRef.current = [];
    for (const p of pendientes) void seguirTraduccion(p.clipId, p.jobId);
  }, [projectLoading, companyId, projectId, seguirTraduccion]);

  const translateClipToEnglish = useCallback(
    async (clipId: string) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.type !== "video" && clip.type !== "ai-generated") return;
      if (clip.aiTranslating) return; // ya está en proceso
      if (!clip.src) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslateError:
                    "El clip no tiene un video cargado. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }
      if (!companyId || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslateError:
                    "Falta información del proyecto. Probá recargar la página.",
                }
              : c,
          ),
        );
        return;
      }

      const cost = creditsForVideoTranslate({ durationSec: clip.duration });
      if (!assertCredits(cost)) return;

      // Marcar el clip como en proceso (limpia errores previos).
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                aiTranslating: true,
                aiTranslateError: undefined,
                aiTranslateJobId: undefined,
                aiTranslateStartedAt: Date.now(),
              }
            : c,
        ),
      );

      const failWith = (msg: string) => {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  aiTranslating: false,
                  aiTranslateError: msg,
                  aiTranslateJobId: undefined,
                }
              : c,
          ),
        );
      };

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Refrescar signed URL antes de mandarla a HeyGen. clip.src puede
        // ser una URL que ya tenga 1 hora (las signed URLs de Supabase
        // expiran a la hora por default), y HeyGen tarda varios minutos en
        // bajar el video — si llega a tocarlo cuando ya expiró, falla con
        // "Failed to download video from url". Generamos una nueva con 3h
        // de validez, suficiente para que HeyGen termine sin riesgo.
        let videoUrlForHeygen = clip.src;
        if (clip.storagePath) {
          try {
            const { data: urlData } = await supabase.storage
              .from("video-project-assets")
              .createSignedUrl(clip.storagePath, 60 * 60 * 3);
            if (urlData?.signedUrl) videoUrlForHeygen = urlData.signedUrl;
          } catch (e) {
            console.warn(
              "[VideoEditor] No pude refrescar signed URL, uso la del clip:",
              e,
            );
          }
        }

        // 1) Kickoff (atlas o n8n según flag — ADR-600; sin créditos el atlas
        // responde 402 y el helper tira el copy de saldo).
        const jobId = await postTranslateStart({
          user_id: session.user.id,
          company_id: companyId,
          project_id: projectId,
          clip_id: clipId,
          video_url: videoUrlForHeygen,
          duration_sec: clip.duration,
          target_language: "English",
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });

        // Guardar el jobId en el clip por si el usuario refresca o queremos
        // mostrar el id en debug.
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId ? { ...c, aiTranslateJobId: jobId } : c,
          ),
        );

        // Créditos los descontó n8n en el kickoff — notificamos a la sidebar.
        window.dispatchEvent(new Event("atlas:credits-updated"));

        // 2) Seguir el job hasta que termine. Va aparte para poder RETOMARLO al
        // volver a abrir el proyecto (ADR-2026-11-03-nada-queda-trabado-al-recargar).
        await seguirTraduccion(clipId, jobId);
      } catch (err) {
        console.error("[VideoEditor] translateClipToEnglish failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al traducir el video.";
        failWith(msg);
      }
    },
    [clips, companyId, projectId, assertCredits, seguirTraduccion],
  );

  // Borra un objeto de un clip de video por descripción de texto (Bria
  // video-erase-object via n8n). Mismo patrón async que translateClipToEnglish:
  // valida → assertCredits → POST start → poll status → registra asset →
  // swapea el clip. Guarda el original para revertir. Fire-and-forget.
  const eraseObjectInClip = useCallback(
    async (clipId: string, prompt: string, confirmLongClip: boolean) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip) return;
      if (clip.type !== "video" && clip.type !== "ai-generated") {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseError: "Solo se puede borrar objetos en clips de video." }
              : c,
          ),
        );
        return;
      }
      if (clip.eraseStatus === "processing") return; // ya en proceso
      if (!clip.storagePath || !clip.src) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  eraseError:
                    "El clip todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.",
                }
              : c,
          ),
        );
        return;
      }
      if (!companyId || !projectId) {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseError: "Falta info del proyecto. Recargá la página." }
              : c,
          ),
        );
        return;
      }
      if (clip.duration > ERASE_SOFT_CAP_SEC && !confirmLongClip) {
        // backstop: el Director debería haber confirmado antes; si no, frenamos.
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? {
                  ...c,
                  eraseError: `El clip dura ${Math.round(clip.duration)}s (> ${ERASE_SOFT_CAP_SEC}s). Confirmá que querés procesarlo igual.`,
                }
              : c,
          ),
        );
        return;
      }

      const cost = creditsForErase({ durationSec: clip.duration });
      if (!assertCredits(cost)) return;

      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? {
                ...c,
                eraseStatus: "processing",
                eraseError: undefined,
                eraseJobId: undefined,
                eraseStartedAt: Date.now(),
              }
            : c,
        ),
      );

      const failWith = (msg: string) => {
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, eraseStatus: "error", eraseError: msg, eraseJobId: undefined }
              : c,
          ),
        );
      };

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");

        // Signed URL fresca de 3h (n8n + Bria tardan; evitamos que expire).
        let videoUrl = clip.src;
        try {
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(clip.storagePath, 60 * 60 * 3);
          if (urlData?.signedUrl) videoUrl = urlData.signedUrl;
        } catch (e) {
          console.warn("[VideoEditor][erase] no pude refrescar signed URL:", e);
        }

        // 1) Kickoff
        // Atlas unifica bria+sam2 en /v1/videos/erase (sin points = marca de
        // agua); flag OFF vuelve al par bria_erase_* de n8n. ADR-601.
        const startRes = await postVideoEraseStart("bria", {
          user_id: session.user.id,
          company_id: companyId,
          project_id: projectId,
          clip_id: clipId,
          video_url: videoUrl,
          duration_sec: clip.duration,
          prompt,
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });
        if (!startRes.ok) throw new Error(`Start failed: ${startRes.status}`);
        const startJson = await startRes.json();
        if (startJson.response !== "success" || !startJson.jobId) {
          throw new Error(startJson.error || "El backend no devolvió un jobId válido.");
        }
        const jobId: string = startJson.jobId;

        setClips((prev) =>
          prev.map((c) => (c.id === clipId ? { ...c, eraseJobId: jobId, eraseSabor: "bria" } : c)),
        );
        window.dispatchEvent(new Event("atlas:credits-updated"));

        // 2) Polling
        let polls = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > ERASE_MAX_POLLS) {
            throw new Error(
              "El borrado tardó demasiado y se dejó de esperar. Probá de nuevo.",
            );
          }
          await new Promise((r) => setTimeout(r, ERASE_POLL_MS));
          // Con el editor cerrado se deja de mirar (ADR-2026-11-03-nada-queda-trabado-al-recargar).
          if (!estaMontadoRef.current) return;

          const statusRes = await postVideoEraseStatus("bria", {
            job_id: jobId,
            project_id: projectId,
            company_id: companyId,
          });
          if (!statusRes.ok) continue; // transient → reintentar
          const statusJson = await statusRes.json();

          if (statusJson.status === "error" || statusJson.status === "failed") {
            throw new Error(statusJson.error || "El borrado falló en el servidor.");
          }
          if (statusJson.status !== "done") continue; // processing → seguir
          if (!statusJson.storagePath) {
            throw new Error("Borrado completado pero el backend no devolvió storagePath.");
          }

          // 3) Completado: firmar, registrar asset en Archivos, swapear.
          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(statusJson.storagePath, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el video borrado.");

          // El resultado SIEMPRE queda en Archivos (ya costó plata).
          try {
            await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: statusJson.storagePath,
              type: "video",
              name: `${clip.name || "Video"} (sin objeto)`.slice(0, 80),
              mimeType: "video/mp4",
            });
          } catch (e) {
            console.warn("[VideoEditor][erase] asset record failed:", e);
          }

          const current = clipsRef.current.find((c) => c.id === clipId);
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    // guardar original para revertir
                    originalStoragePathBeforeErase: current?.storagePath,
                    originalSrcBeforeErase: current?.src,
                    src: newSrc,
                    storagePath: statusJson.storagePath,
                    // Mismo audio, otro archivo: el transcript sigue valiendo.
                    transcript: c.transcript
                      ? marcarTranscript(c.transcript, statusJson.storagePath)
                      : undefined,
                    eraseStatus: undefined,
                    eraseError: undefined,
                    eraseJobId: undefined,
                  }
                : c,
            ),
          );
          try {
            if (finishSoundRef.current) {
              finishSoundRef.current.currentTime = 0;
              finishSoundRef.current.play().catch(() => {});
            }
          } catch {}
          return;
        }
      } catch (err) {
        console.error("[VideoEditor][erase] eraseObjectInClip failed:", err);
        const msg =
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al borrar el objeto.";
        failWith(msg);
      }
    },
    [companyId, projectId, assertCredits],
  );

  // ── Corregir voz de un clip (atlas) ─────────────────────────────────────
  // Espejo de eraseObjectInClip: fire-and-forget con polling propio; shimmer
  // mientras procesa; al terminar firma, registra en Archivos y swapea in-place.
  // Gratis, así que no pasa por assertCredits. Sirve para video y audio: el
  // backend devuelve `.mp4` si el clip tenía video y `.m4a` si era audio solo.
  const CORREGIR_VOZ_POLL_MS = 3_000;
  const CORREGIR_VOZ_MAX_POLLS = 200; // 10 min
  const corregirVozEnClip = useCallback(
    async (clipId: string, ediciones: Array<{ indice: number; texto: string }>) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip) return;
      const fallar = (msg: string) =>
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, corregirVozStatus: "error", corregirVozError: msg, corregirVozJobId: undefined }
              : c,
          ),
        );
      if (clip.corregirVozStatus === "processing") return;
      if (!clip.storagePath || !clip.src) {
        fallar("El clip todavía no terminó de subirse a la nube. Esperá unos segundos y reintentá.");
        return;
      }
      const palabras = transcriptVigente(clip)?.words;
      if (!palabras || palabras.length === 0) {
        fallar("El clip no tiene transcript. Generalo primero.");
        return;
      }
      if (!companyId || !projectId) {
        fallar("Falta info del proyecto. Recargá la página.");
        return;
      }
      // Lo que había al arrancar: si otra operación (borrado, traducción, re-transcripción)
      // termina antes y cambia el asset o el transcript, este resultado es de un clip que
      // ya no existe y NO se aplica — quedaría un medio viejo o un parche en índices ajenos.
      const storagePathAlEmpezar = clip.storagePath;
      const transcriptAlEmpezar = clip.transcript;
      setClips((prev) =>
        prev.map((c) =>
          c.id === clipId
            ? { ...c, corregirVozStatus: "processing", corregirVozError: undefined }
            : c,
        ),
      );
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        // URL firmada fresca del asset COMPLETO (el backend opera sobre el archivo entero).
        const { data: urlData } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(clip.storagePath, 60 * 60 * 3);
        if (!urlData?.signedUrl) throw new Error("No pude firmar el clip para corregirlo.");

        const startRes = await postCorregirVozClipStart({
          video_url: urlData.signedUrl,
          project_id: projectId,
          palabras,
          ediciones,
          modo_utilizado: "corregir_voz_clip",
        });
        const startJson = await startRes.json().catch(() => ({}));
        if (!startRes.ok || startJson.response !== "success" || !startJson.jobId) {
          throw new Error(startJson.error || `El servidor respondió ${startRes.status}.`);
        }
        const jobId: string = startJson.jobId;
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId
              ? { ...c, corregirVozJobId: jobId, corregirVozDesde: storagePathAlEmpezar }
              : c,
          ),
        );

        let polls = 0;
        while (true) {
          await new Promise((r) => setTimeout(r, CORREGIR_VOZ_POLL_MS));
          // Con el editor cerrado se deja de mirar (ADR-2026-11-03-nada-queda-trabado-al-recargar).
          if (!estaMontadoRef.current) return;
          polls++;
          if (polls > CORREGIR_VOZ_MAX_POLLS) {
            throw new Error("La corrección tardó demasiado. Probá de nuevo en un rato.");
          }
          const statusRes = await postCorregirVozClipStatus({ job_id: jobId });
          if (!statusRes.ok) continue; // transitorio → reintentar
          const statusJson = await statusRes.json();
          if (statusJson.status === "error" || statusJson.status === "failed") {
            throw new Error(statusJson.error || "La corrección falló en el servidor.");
          }
          if (statusJson.status !== "done") continue;
          if (!statusJson.storagePath) {
            throw new Error("Corrección completada pero el backend no devolvió storagePath.");
          }

          const esAudio = /\.m4a$/i.test(statusJson.storagePath);
          const { data: nuevaUrl } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(statusJson.storagePath, 3600);
          const newSrc = nuevaUrl?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el clip corregido.");

          // El resultado queda en Archivos, como con el borrado: así el original
          // sigue ahí y el corregido se puede volver a usar.
          try {
            await registerExistingAsset({
              projectId,
              companyId,
              userId: session.user.id,
              storagePath: statusJson.storagePath,
              type: esAudio ? "audio" : "video",
              name: `${clip.name || (esAudio ? "Audio" : "Video")} (voz corregida)`.slice(0, 80),
              mimeType: esAudio ? "audio/mp4" : "video/mp4",
            });
          } catch (e) {
            console.warn("[VideoEditor][corregirVoz] asset record failed:", e);
          }

          const actual = clipsRef.current.find((c) => c.id === clipId);
          if (!actual) return; // el clip ya no está; el resultado quedó en Archivos
          if (actual.storagePath !== storagePathAlEmpezar) {
            fallar(
              "El clip cambió mientras se corregía la voz (otra edición terminó antes). El resultado quedó en Archivos como «voz corregida».",
            );
            return;
          }
          // El transcript se parchea en vez de borrarse: cada tramo regenerado se
          // ajustó a la duración exacta de su hueco, así que los tiempos siguen
          // valiendo y el agente puede corregir otra palabra sin re-transcribir.
          // Si mientras tanto se re-transcribió, los índices son de otro transcript: se
          // limpia y el agente lo vuelve a generar.
          const transcriptIntacto = actual.transcript === transcriptAlEmpezar;
          const textoPorIndice = new Map(ediciones.map((e) => [e.indice, e.texto] as const));
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    src: newSrc,
                    storagePath: statusJson.storagePath,
                    // Y pasa a ser del archivo NUEVO: mismos tiempos, otro path.
                    transcript:
                      transcriptIntacto && c.transcript
                        ? marcarTranscript(
                            {
                              ...c.transcript,
                              words: c.transcript.words.map((w, i) =>
                                textoPorIndice.has(i) ? { ...w, text: textoPorIndice.get(i)! } : w,
                              ),
                            },
                            statusJson.storagePath,
                          )
                        : undefined,
                    corregirVozStatus: undefined,
                    corregirVozError: undefined,
                    corregirVozJobId: undefined,
                  }
                : c,
            ),
          );
          try {
            if (finishSoundRef.current) {
              finishSoundRef.current.currentTime = 0;
              finishSoundRef.current.play().catch(() => {});
            }
          } catch {}
          return;
        }
      } catch (err) {
        console.error("[VideoEditor][corregirVoz] corregirVozEnClip failed:", err);
        fallar(
          err instanceof DOMException && err.name === "TimeoutError"
            ? "La conexión con el servidor tardó demasiado. Probá de nuevo."
            : err instanceof Error
              ? err.message
              : "Error desconocido al corregir la voz.",
        );
      }
    },
    [companyId, projectId],
  );

  // Transcribe un clip y guarda el transcript en él, para la sub-pantalla de
  // Corregir voz. Mismo camino que generate_transcript del agente. Devuelve el
  // mensaje de error, o null.
  const transcribirClipParaCorregirVoz = useCallback(
    async (clipId: string): Promise<string | null> => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip) return "El clip ya no está en la timeline.";
      let url: string | null = null;
      if (clip.storagePath) {
        try {
          url = await getAssetSignedUrl(clip.storagePath);
        } catch {}
      }
      if (!url && typeof clip.src === "string" && /^https?:\/\//.test(clip.src)) {
        url = clip.src;
      }
      if (!url) return "No pudimos acceder al archivo del clip.";
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) return "Sesión expirada — recargá la página.";
      try {
        const tr = await requestTranscript(url, user.id);
        if (!tr.ok) return tr.message;
        const transcript = tr.transcript;
        setClips((prev) =>
          prev.map((c) =>
            c.id === clipId ? { ...c, transcript: marcarTranscript(transcript, clip.storagePath) } : c,
          ),
        );
        clipsRef.current = clipsRef.current.map((c) =>
          c.id === clipId ? { ...c, transcript: marcarTranscript(transcript, clip.storagePath) } : c,
        );
        return null;
      } catch (err) {
        return err instanceof Error ? err.message : "No pudimos transcribir el clip.";
      }
    },
    [],
  );

  // ── Pincel inteligente (SAM2) ─────────────────────────────────────────────
  // Captura el frame actual del clip a un dataURL y abre el panel del pincel.
  const openEraseBrush = useCallback(async (clipId: string) => {
    const clip = clipsRef.current.find((c) => c.id === clipId);
    if (!clip || (clip.type !== "video" && clip.type !== "ai-generated")) return;
    if (!clip.storagePath) {
      setUploadErrorMsg("El clip todavía no terminó de subirse. Esperá unos segundos.");
      return;
    }
    const delPreview = mediaRefs.current.get(clipId) as HTMLVideoElement | undefined;
    if (!delPreview || !delPreview.videoWidth) {
      setUploadErrorMsg("El frame del clip no está cargado. Movete sobre el clip y reintentá.");
      return;
    }
    // Con la versión liviana en el preview, el cuadro para pintar sale del ORIGINAL:
    // la máscara se dibuja sobre la calidad completa
    // (ADR-2026-11-03-el-preview-usa-la-version-liviana).
    let vid: HTMLVideoElement = delPreview;
    if (usaLaVersionLiviana(clip) && clip.src) {
      try {
        vid = await videoOriginalEn(clip.src, delPreview.currentTime);
      } catch {
        setUploadErrorMsg("No se pudo cargar el video original. Reintentá en unos segundos.");
        return;
      }
    }
    const cv = document.createElement("canvas");
    cv.width = vid.videoWidth; cv.height = vid.videoHeight;
    cv.getContext("2d")!.drawImage(vid, 0, 0);
    // toDataURL falla con SecurityError si el canvas quedó "tainted" (video
    // cross-origin cargado sin CORS). El <video> ya monta con crossOrigin, pero
    // si el navegador sirvió una respuesta cacheada sin CORS, el canvas puede
    // seguir sucio → mensaje claro y pedirle recargar, en vez de un crash.
    let frameUrl: string;
    try {
      frameUrl = cv.toDataURL("image/jpeg", 0.9);
    } catch (e) {
      console.error("[erase-brush] canvas tainted en toDataURL:", e);
      setUploadErrorMsg(
        "No pude capturar el frame del clip (problema de seguridad del navegador). Recargá la página (F5) y reintentá.",
      );
      return;
    }
    setEraseBrushFrame({ url: frameUrl, w: vid.videoWidth, h: vid.videoHeight });
    setEraseBrushClipId(clipId);
    panelPrevioAlBorradorRef.current = activePanelRef.current;
    // El pincel ahora es una MODAL grande (no la sidebar). Se renderiza
    // top-level cuando hay eraseBrushFrame + eraseBrushClipId + companyId.
  }, []);

  // Salir del pincel —por la flecha de volver o por la X—: las dos hacen lo
  // mismo, devolver la pantalla de la que se vino.
  //
  // Y devuelven también el CLIP a la selección. El editor suelta el clip
  // elegido ante cualquier `pointerdown` que no caiga en UI marcada como del
  // clip, y aunque el pincel ya lleva esa marca, cualquier otro camino que
  // pase por el medio (un modal nuevo, un toque en el velo) dejaría el panel
  // del clip abierto y VACÍO: la cabecera decía "Editar clip" y abajo no había
  // nada. Reelegirlo al volver es barato y cierra el agujero de raíz.
  // Ver ADR-2026-09-18-el-pincel-no-suelta-el-clip.
  const volverDelPincel = useCallback(() => {
    const clipId = eraseBrushClipId;
    setEraseBrushClipId(null);
    setEraseBrushFrame(null);
    if (clipId) setSelectedClipId(clipId);
    // La pantalla de la que se vino. Si no había ninguna abierta —en el
    // teléfono se entra desde la barra de acciones del clip, sin hoja— se abre
    // la del clip, que es "Editar video": es de donde salió la acción.
    const previo = panelPrevioAlBorradorRef.current;
    setActivePanel(previo !== "none" ? previo : "clip");
  }, [eraseBrushClipId]);

  // Sube el frame (dataURL→blob) a Supabase, lo firma y llama a SAM2 para el
  // preview de la máscara. El panel queda puro de UI.
  const requestBrushMask = useCallback(
    async (marks: EraseMark[]): Promise<string> => {
      if (!eraseBrushFrame || !companyId || !projectId) throw new Error("Falta info del frame.");
      // dataURL → blob
      const blob = await (await fetch(eraseBrushFrame.url)).blob();
      const path = `${companyId}/${projectId}/_erase_frame.jpg`;
      const { error: upErr } = await supabase.storage
        .from("video-project-assets")
        .upload(path, blob, { upsert: true, contentType: "image/jpeg" });
      if (upErr) throw new Error("No se pudo subir el frame.");
      const { data } = await supabase.storage.from("video-project-assets").createSignedUrl(path, 3600);
      if (!data?.signedUrl) throw new Error("No se pudo firmar el frame.");
      return requestSam2Mask({ frameUrl: data.signedUrl, marks, companyId });
    },
    [eraseBrushFrame, companyId, projectId],
  );

  // Job async del pincel (espeja eraseObjectInClip): start→poll→swap apuntando
  // a los endpoints SAM2 y mandando las marcas + el keyframe.
  const runBrushErase = useCallback(
    async (clipId: string, marks: EraseMark[], frame: { w: number; h: number }) => {
      const clip = clipsRef.current.find((c) => c.id === clipId);
      if (!clip || !clip.storagePath || !companyId || !projectId) return;
      setClips((prev) => prev.map((c) => c.id === clipId
        ? { ...c, eraseStatus: "processing", eraseError: undefined, eraseStartedAt: Date.now() } : c));
      // Mismo patrón que la traducción: el error queda guardado en el clip y se
      // muestra como badge clickeable en la timeline (no un toast ad-hoc).
      const fail = (msg: string) => {
        setClips((prev) => prev.map((c) => c.id === clipId ? { ...c, eraseStatus: "error", eraseError: msg } : c));
      };
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) throw new Error("No session");
        let videoUrl = clip.src!;
        try {
          const { data } = await supabase.storage.from("video-project-assets")
            .createSignedUrl(clip.storagePath, 60 * 60 * 3);
          if (data?.signedUrl) videoUrl = data.signedUrl;
        } catch {}
        // Atlas unifica bria+sam2 en /v1/videos/erase; flag OFF vuelve al par
        // sam2_erase_* de n8n. ADR-601.
        const startRes = await postVideoEraseStart("sam2", {
          user_id: session.user.id, company_id: companyId, project_id: projectId, clip_id: clipId,
          video_url: videoUrl, duration_sec: clip.duration,
          points: marksToSamPoints(marks), frame_width: frame.w, frame_height: frame.h,
          // Trackeo (ADR-585): edición desde el Estudio.
          modo_utilizado: "estudio_edicion",
        });
        const startRaw = await startRes.text();
        if (!startRes.ok) {
          console.error("[erase-brush][start] HTTP", startRes.status, "→", startRaw.slice(0, 600));
          throw new Error(`Start falló (HTTP ${startRes.status}). ${startRaw.slice(0, 200)}`);
        }
        let sj: any;
        try { sj = JSON.parse(startRaw); }
        catch {
          console.error("[erase-brush][start] respuesta NO-JSON:", JSON.stringify(startRaw.slice(0, 600)));
          throw new Error("No se pudo empezar el borrado. Probá de nuevo en un momento.");
        }
        if (sj.response !== "success" || !sj.jobId) throw new Error(sj.error || "Sin jobId.");
        const jobId: string = sj.jobId;
        setClips((prev) => prev.map((c) => c.id === clipId ? { ...c, eraseJobId: jobId, eraseSabor: "sam2" } : c));
        window.dispatchEvent(new Event("atlas:credits-updated"));
        let polls = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          polls++;
          if (polls > 300) throw new Error("El borrado tardó demasiado y se dejó de esperar. Probá de nuevo.");
          await new Promise((r) => setTimeout(r, 8000));
          // Con el editor cerrado se deja de mirar (ADR-2026-11-03-nada-queda-trabado-al-recargar).
          if (!estaMontadoRef.current) return;
          const stRes = await postVideoEraseStatus("sam2", {
            job_id: jobId, project_id: projectId, company_id: companyId,
          });
          if (!stRes.ok) continue;
          const stRaw = await stRes.text();
          let st: any;
          try { st = JSON.parse(stRaw); }
          catch {
            console.warn("[erase-brush][status] respuesta NO-JSON (reintento):", JSON.stringify(stRaw.slice(0, 300)));
            continue;
          }
          if (st.status === "error" || st.status === "failed") throw new Error(st.error || "Falló en el servidor.");
          if (st.status !== "done") continue;
          if (!st.storagePath) throw new Error("Sin storagePath.");
          const { data: urlData } = await supabase.storage.from("video-project-assets").createSignedUrl(st.storagePath, 3600);
          const newSrc = urlData?.signedUrl;
          if (!newSrc) throw new Error("No se pudo firmar el resultado.");
          try {
            await registerExistingAsset({ projectId, companyId, userId: session.user.id,
              storagePath: st.storagePath, type: "video", name: `${clip.name || "Video"} (sin objeto)`.slice(0, 80), mimeType: "video/mp4" });
          } catch {}
          const cur = clipsRef.current.find((c) => c.id === clipId);
          setClips((prev) => prev.map((c) => c.id === clipId
            ? { ...c, originalStoragePathBeforeErase: cur?.storagePath, originalSrcBeforeErase: cur?.src,
                src: newSrc, storagePath: st.storagePath,
                // Mismo audio, otro archivo: el transcript sigue valiendo.
                transcript: c.transcript ? marcarTranscript(c.transcript, st.storagePath) : undefined,
                eraseStatus: undefined, eraseError: undefined, eraseJobId: undefined }
            : c));
          // Éxito: el clip se reemplaza in-place y suena el "listo" (mismo
          // patrón que la traducción). El shimmer desaparece solo.
          try { if (finishSoundRef.current) { finishSoundRef.current.currentTime = 0; finishSoundRef.current.play().catch(() => {}); } } catch {}
          return;
        }
      } catch (err: any) {
        fail(err?.message || "Error desconocido.");
      }
    },
    [companyId, projectId],
  );

  const addAiSceneClip = (mode: "generate" | "draft") => {
    if (mode === "generate" && !assertCredits(costoDeLaEscena)) return;
    const aiTrack = getTrackForClipType("ai-generated");
    const promptPreview = scenePrompt.trim()
      ? scenePrompt.trim().slice(0, 30)
      : "Escena IA";

    // If editing an existing clip, update it in place
    const isEditing = !!editingClipId;
    const clipId = isEditing ? editingClipId! : generateId();

    // For dialogue scenes, estimate duration from the spoken text so the
    // placeholder clip length approximates reality before generation finishes.
    // The real duration is probed once the video is ready.
    // When the user provided audio instead of text, the audio's measured
    // duration is the real value (no estimation needed).
    const isStaticDialogAudioMode =
      sceneMode === "personas" &&
      scenePersonType === "static" &&
      sceneDialogueInputMode === "audio" &&
      sceneDialogueAudio;
    const estimatedFromDialogue = isStaticDialogAudioMode
      ? sceneDialogueAudio!.durationSec
      : sceneMode === "personas" && sceneDialogue.trim()
        ? estimateSpeechDuration(sceneDialogue, sceneSpeechSpeed)
        : 0;

    // Un video de referencia de más de 14 s sólo entra entero en Profesional
    // (la regla ya cambió la calidad al elegirlo; acá se vuelve a mirar por si
    // el usuario la bajó a mano). Más de 29 s no entra en ninguna.
    if (sceneRefMode === "reference") {
      const videoDeReferencia = libraryAssets.find(
        (a) => a.type === "video" && sceneRefAssetIds.includes(a.id),
      );
      if (videoDeReferencia) {
        const veredicto = evaluarVideoDeReferencia(
          videoDeReferencia.duration_seconds,
        );
        if (!veredicto.ok) {
          setUploadErrorMsg(veredicto.motivo);
          return;
        }
        if (
          veredicto.calidad === "profesional" &&
          sceneQuality !== "profesional"
        ) {
          setUploadErrorMsg(
            "Ese video de referencia dura más de 14 s: sólo entra entero en calidad Profesional. Cambiá la calidad o usá uno más corto.",
          );
          return;
        }
      }
    }

    const effectiveDuration =
      estimatedFromDialogue > 0
        ? Math.max(2, Math.min(60, estimatedFromDialogue))
        : sceneDuration;

    const effectivePrompt = scenePrompt.trim() || undefined;
    // ¿El prompt ya es del escritor ("Mejorar prompt", dejado igual o casi, con la
    // misma duración, referencias y formato)? Entonces viaja tal cual.
    const promptEscrito =
      sceneMode === "general" &&
      escenaYaEscrita(
        sceneMejora,
        scenePrompt,
        sceneDuration,
        claveDeRefs([
          ...sceneRefAssetIds,
          sceneFirstFrameAssetId,
          sceneLastFrameAssetId,
        ]),
        aspectRatio,
      );

    // El selector ya no ofrece "Ninguna", así que "sin referencias" es quedarse
    // en Referencia (o Frames) y no cargar ninguna imagen. El payload SÍ sigue
    // distinguiendo los tres casos (`ref_mode`), así que lo traducimos acá: sin
    // imágenes cargadas el clip sale como "none", igual que antes cuando el
    // usuario elegía esa pastilla a mano.
    const refModeEfectivo: "none" | "reference" | "frames" =
      sceneMode !== "general"
        ? sceneRefMode
        : sceneRefMode === "reference" && sceneRefAssetIds.length === 0
          ? "none"
          : sceneRefMode === "frames" &&
              !sceneFirstFrameAssetId &&
              !sceneLastFrameAssetId
            ? "none"
            : sceneRefMode;

    const updatedFields: Partial<TimelineClip> = {
      name: mode === "draft" ? `Borrador: ${promptPreview}` : promptPreview,
      duration: effectiveDuration,
      aiPrompt: effectivePrompt,
      aiFirstFrameAssetId: sceneFirstFrameAssetId || undefined,
      aiLastFrameAssetId: sceneLastFrameAssetId || undefined,
      aiRefAssetIds:
        sceneRefAssetIds.length > 0 ? [...sceneRefAssetIds] : undefined,
      aiRefMode: refModeEfectivo,
      aiQuality: sceneQuality,
      aiResolution: sceneResolution,
      aiSceneMode: sceneMode,
      aiPersonType: sceneMode === "personas" ? scenePersonType : undefined,
      aiDialogue:
        sceneMode === "personas"
          ? sceneDialogue.trim() || undefined
          : undefined,
      aiSpeechSpeed: sceneMode === "personas" ? sceneSpeechSpeed : undefined,
      aiVoiceId:
        sceneMode === "personas" && audioAiVoiceId ? audioAiVoiceId : undefined,
      aiVoiceName:
        sceneMode === "personas" && audioAiVoiceName
          ? audioAiVoiceName
          : undefined,
      // El panel ya no arma multiescena: un clip viejo que se reedita vuelve a ser
      // una escena sola con el prompt que se ve.
      aiMultiEnabled: undefined,
      aiMultiScenes: undefined,
      aiPromptEscrito: promptEscrito || undefined,
      // Dialog input mode (text vs audio) — only meaningful for static personas
      aiDialogueInputMode:
        sceneMode === "personas" && scenePersonType === "static"
          ? sceneDialogueInputMode
          : undefined,
      aiDialogueAudioUrl:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.url
          : undefined,
      aiDialogueAudioStoragePath:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.storagePath
          : undefined,
      aiDialogueAudioDurationSec:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.durationSec
          : undefined,
      aiDialogueAudioAssetId:
        sceneMode === "personas" &&
        scenePersonType === "static" &&
        sceneDialogueInputMode === "audio" &&
        sceneDialogueAudio
          ? sceneDialogueAudio.assetId
          : undefined,
      aiStatus: mode === "draft" ? "draft" : "generating",
      aiGenerationStartedAt: mode === "draft" ? undefined : Date.now(),
    };

    if (isEditing) {
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, ...updatedFields } : c)),
      );
    } else {
      // Sin "+" abierto arranca en 0 y en capa nueva, como siempre; desde el
      // "+" de una pista, a continuación de su último clip.
      const newClip: TimelineClip = {
        id: clipId,
        type: "ai-generated",
        ...ubicarClipNuevo("ai-generated", { duracion: updatedFields.duration }),
        color: aiTrack.color,
        ...updatedFields,
      } as TimelineClip;
      setClips((prev) => [...prev, newClip]);
    }
    setSelectedClipId(clipId);
    setScenePrompt("");
    setSceneDialogue("");
    setSceneFirstFrameAssetId(null);
    setSceneLastFrameAssetId(null);
    setSceneRefAssetIds([]);
    setSceneDuration(5);
    setSceneSpeechSpeed(1.1);
    setSceneQuality("estandar");
    setSceneResolution("720p");
    setSceneMejora(null);
    setEditingClipId(null);
    setActivePanel("none");

    // If generating, call API after adding the clip
    if (mode === "generate") {
      // Use setTimeout to let state update first, then call with the clip data directly
      setTimeout(async () => {
        // Doble submit del panel (o carrera con Reintentar) para el mismo
        // clip: el primer disparo ya está generando — no-op (ver
        // aiVideoInFlightRef).
        if (aiVideoInFlightRef.current.has(clipId)) return;
        aiVideoInFlightRef.current.add(clipId);
        // Fuera del try: el catch lo necesita para decidir si el execution_id
        // se conserva (solo tiene sentido si la request ruteó a atlas).
        const refModeForApi =
          updatedFields.aiSceneMode === "personas"
            ? updatedFields.aiPersonType === "motion"
              ? "dialogo_movimiento"
              : "dialogo"
            : updatedFields.aiRefMode || "none";
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.user) throw new Error("No session");
          // Mismo pedido que un intento previo sin concluir (p. ej. reintento
          // re-abriendo el panel sin cambiar nada) → mismo execution_id: el
          // backend dedupea y no cobra un segundo video. Si cambió algo, o el
          // intento previo concluyó, es un pedido nuevo con id nuevo.
          const prevClip = isEditing
            ? clips.find((c) => c.id === clipId)
            : undefined;
          const attemptRef = creatorAttemptRef(
            prevClip?.aiExecutionId && prevClip.aiExecutionParamsKey
              ? {
                  executionId: prevClip.aiExecutionId,
                  paramsKey: prevClip.aiExecutionParamsKey,
                }
              : null,
            creatorParamsKey({
              prompt: updatedFields.aiPrompt,
              duration: clampDuracionDeEscena(updatedFields.duration),
              refMode: refModeForApi,
              resolution: updatedFields.aiResolution || "720p",
              quality: updatedFields.aiQuality || "estandar",
              aspectRatio,
              speechSpeed:
                updatedFields.aiSceneMode === "personas"
                  ? (updatedFields.aiSpeechSpeed ?? 1.1)
                  : null,
              multiScenes:
                updatedFields.aiMultiEnabled && updatedFields.aiMultiScenes
                  ? updatedFields.aiMultiScenes.map((s) => ({
                      prompt: s.prompt,
                      duration: s.duration,
                    }))
                  : null,
              refAssetIds: updatedFields.aiRefAssetIds ?? null,
              firstFrameAssetId: updatedFields.aiFirstFrameAssetId ?? null,
              lastFrameAssetId: updatedFields.aiLastFrameAssetId ?? null,
              voiceId: updatedFields.aiVoiceId ?? null,
              dialogue: updatedFields.aiDialogue ?? null,
              dialogueInputMode: updatedFields.aiDialogueInputMode ?? null,
              audioUrl: updatedFields.aiDialogueAudioUrl ?? null,
            }),
          );
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiExecutionId: attemptRef.executionId,
                    aiExecutionParamsKey: attemptRef.paramsKey,
                  }
                : c,
            ),
          );
          const body: Record<string, any> = {
            user_id: session.user.id,
            project_id: projectId,
            company_id: companyId,
            execution_id: attemptRef.executionId,
            prompt: updatedFields.aiPrompt,
            // La API espera segundos enteros. `updatedFields.duration` puede
            // traer decimales por otros caminos además del editar: la
            // estimación de habla (`estimateSpeechDuration`) y la duración
            // medida del audio de diálogo. Redondeamos acá para que ningún
            // float salga por el request, venga de donde venga.
            duration: clampDuracionDeEscena(updatedFields.duration),
            ref_mode: refModeForApi,
            // Trackeo (ADR-585): video generado desde el Estudio.
            modo_utilizado: "estudio_video",
            aspect_ratio: aspectRatio,
            quality: updatedFields.aiQuality || "estandar",
            resolution: updatedFields.aiResolution || "720p",
            // El escritor del Creador —el mismo metaprompting de Redes— redacta el
            // prompt de una escena general; si ya lo escribió "Mejorar prompt" y el
            // usuario lo dejó igual o casi, viaja tal cual
            // (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes).
            ...camposDelEscritorDeVideo(
              updatedFields.aiSceneMode,
              updatedFields.aiPromptEscrito,
            ),
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiVoiceId
              ? { voz_id: updatedFields.aiVoiceId }
              : {}),
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiDialogue
              ? { dialogo: updatedFields.aiDialogue }
              : {}),
            ...(updatedFields.aiSceneMode === "personas"
              ? { velocidad_voz: updatedFields.aiSpeechSpeed ?? 1.1 }
              : {}),
            ...(updatedFields.aiSceneMode === "personas" &&
            updatedFields.aiPersonType !== "motion" &&
            updatedFields.aiDialogueInputMode === "audio" &&
            updatedFields.aiDialogueAudioUrl
              ? {
                  dialogo_input_mode: "audio",
                  audio_url: updatedFields.aiDialogueAudioUrl,
                  audio_duration_sec:
                    updatedFields.aiDialogueAudioDurationSec ?? null,
                }
              : updatedFields.aiSceneMode === "personas"
                ? { dialogo_input_mode: "texto" }
                : {}),
            ...(updatedFields.aiMultiEnabled && updatedFields.aiMultiScenes
              ? {
                  multiescena: true,
                  escenas: updatedFields.aiMultiScenes.map((s) => ({
                    prompt: s.prompt,
                    duration: s.duration,
                  })),
                }
              : {}),
          };

          // Resolve asset IDs to signed URLs
          const idsToResolve: string[] = [];
          if (updatedFields.aiRefAssetIds)
            idsToResolve.push(...updatedFields.aiRefAssetIds);
          if (updatedFields.aiFirstFrameAssetId)
            idsToResolve.push(updatedFields.aiFirstFrameAssetId);
          if (updatedFields.aiLastFrameAssetId)
            idsToResolve.push(updatedFields.aiLastFrameAssetId);
          const assetsById = new Map<string, ProjectAsset>();
          if (idsToResolve.length > 0) {
            const fetched = await fetchAssetsByIds(idsToResolve);
            for (const a of fetched) assetsById.set(a.id, a);
          }
          const resolveUrl = async (assetId?: string) => {
            if (!assetId) return null;
            const a = assetsById.get(assetId);
            if (!a) return null;
            return await getAssetSignedUrl(a.storage_path);
          };

          if (
            updatedFields.aiRefAssetIds &&
            updatedFields.aiRefAssetIds.length > 0
          ) {
            // Mismo reparto que el reintento (`generateAiClip`): las imágenes
            // en `reference_images`, un video en `reference_videos` con modo
            // "referencia" (revisión de Greptile al PR #149: este camino lo
            // mandaba como imagen).
            const imagenes: string[] = [];
            let videoDeReferencia: string | null = null;
            for (const id of updatedFields.aiRefAssetIds) {
              const url = await resolveUrl(id);
              if (!url) continue;
              if (assetsById.get(id)?.type === "video") {
                if (!videoDeReferencia) videoDeReferencia = url;
              } else imagenes.push(url);
            }
            if (imagenes.length > 0) body.reference_images = imagenes;
            if (videoDeReferencia)
              body.reference_videos = [
                { url: videoDeReferencia, modo: "referencia" },
              ];
          }
          const firstUrl = await resolveUrl(updatedFields.aiFirstFrameAssetId);
          if (firstUrl) body.first_frame = firstUrl;
          const lastUrl = await resolveUrl(updatedFields.aiLastFrameAssetId);
          if (lastUrl) body.last_frame = lastUrl;

          // Helper con flag atlas/n8n y ruteo parcial por ref_mode
          // ('dialogo_movimiento' sigue en n8n) — timeout de 5 min y manejo
          // de los 200-vacío/no-JSON de n8n adentro. Ver ADR-584.
          const creator = await generateVideoCreator(body).catch((e) => {
            if (escenasCanceladasRef.current.has(attemptRef.executionId))
              throw new EscenaCancelada();
            throw e;
          });
          if (escenasCanceladasRef.current.has(attemptRef.executionId))
            throw new EscenaCancelada();
          if (creator.pending) {
            // 202: el wait del backend venció, el video sigue generándose.
            throw new Error(
              "La generación está tardando más de lo normal. Tocá Reintentar en un rato: si ya terminó, la traemos sin volver a cobrar.",
            );
          }
          const videoPath = creator.path;

          if (videoPath) {
            // Credits were just deducted by the workflow — notify the sidebar.
            window.dispatchEvent(new Event("atlas:credits-updated"));

            const { data: urlData } = await supabase.storage
              .from("video-project-assets")
              .createSignedUrl(videoPath, 3600);

            setClips((prev) =>
              prev.map((c) =>
                c.id === clipId
                  ? {
                      ...c,
                      aiStatus: "ready" as AiClipStatus,
                      storagePath: videoPath,
                      src: urlData?.signedUrl || undefined,
                      // Borrador: con el archivo entregado; si otra edición lo
                      // reemplaza, deja de ser finalizable.
                      aiBorrador: creator.borrador
                        ? { ...creator.borrador, storagePath: videoPath }
                        : undefined,
                      // Intento concluido: un próximo "generar" es un pedido
                      // nuevo y debe llevar execution_id nuevo.
                      aiExecutionId: undefined,
                      aiExecutionParamsKey: undefined,
                    }
                  : c,
              ),
            );

            // Probe real video duration; important because dialogue placeholders
            // use an estimated duration until the video is ready.
            if (urlData?.signedUrl) {
              const probe = document.createElement("video");
              probe.preload = "metadata";
              probe.src = urlData.signedUrl;
              probe.addEventListener("loadedmetadata", () => {
                const dur = probe.duration;
                if (isFinite(dur) && dur > 0) {
                  setClips((prev) =>
                    prev.map((c) =>
                      c.id === clipId
                        ? { ...c, duration: dur, sourceDuration: dur }
                        : c,
                    ),
                  );
                }
              });
            }

            // Register the AI video as a project asset (appears in Video library, persists after clip deletion)
            try {
              const asset = await registerExistingAsset({
                projectId: projectId!,
                companyId: companyId!,
                userId: session.user.id,
                storagePath: videoPath,
                type: "video",
                name: updatedFields.aiPrompt?.slice(0, 80) || "Video IA",
                mimeType: "video/mp4",
                durationSeconds: updatedFields.duration,
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn("[VideoEditor] Could not register AI asset:", e);
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El video se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          } else {
            throw new Error("Generation failed");
          }
        } catch (err) {
          // Cancelada por el usuario: el clip ya quedó en borrador.
          if (err instanceof EscenaCancelada) return;
          console.error("[VideoEditor] AI generation failed:", err);
          const msg =
            err instanceof DOMException && err.name === "TimeoutError"
              ? "La generación tardó más de 5 minutos y se dejó de esperar. Probá de nuevo en un rato."
              : err instanceof Error
                ? err.message
                : "Error desconocido al generar el video.";
          // El execution_id se conserva SOLO si el reintento puede
          // re-engancharse: fallo TÉCNICO (Error plano: timeout/red/202) Y
          // request ruteada a atlas (que dedupea). GenerateAdError = concluyó;
          // modos n8n-only o flag apagado = n8n, sin dedupe → id inútil.
          const definitivo =
            err instanceof GenerateAdError ||
            !USE_ATLAS_BACKEND ||
            isN8nOnlyCreatorRefMode(refModeForApi);
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "error" as AiClipStatus,
                    aiError: msg,
                    ...(definitivo
                      ? {
                          aiExecutionId: undefined,
                          aiExecutionParamsKey: undefined,
                        }
                      : {}),
                  }
                : c,
            ),
          );
        } finally {
          aiVideoInFlightRef.current.delete(clipId);
        }
      }, 0);
    }
  };

  // ── Add AI audio clip (voice-over or music) ──────────────────────────────
  // When mode === 'generate' we POST to the generar_audio_creator webhook.
  // When mode === 'draft' we just place a draft clip on the audio track.
  const addAiAudioClip = (mode: "generate" | "draft") => {
    if (mode === "generate" && !assertCredits(audioPanelCost)) return;
    const audioTrack = getTrackForClipType("audio");
    const rawPrompt = audioAiPrompt.trim();
    const typeLabel =
      audioAiType === "voice"
        ? "Voz IA"
        : audioAiType === "sfx"
          ? "Efecto IA"
          : "Música IA";
    const preview = rawPrompt ? rawPrompt.slice(0, 30) : typeLabel;
    const name =
      mode === "draft" ? `Borrador: ${preview}` : `${typeLabel}: ${preview}`;

    const isEditing = !!editingClipId;
    const clipId = isEditing ? editingClipId! : generateId();

    // For voice audios, estimate duration from the narration text so the
    // placeholder clip length approximates reality before generation finishes.
    // The real duration is probed once the audio is ready (see below).
    // When the user provided their own audio, that duration is the truth.
    const isVoiceAudioMode =
      audioAiType === "voice" &&
      audioAiDialogueInputMode === "audio" &&
      audioAiDialogueAudio;
    const estimatedFromVoice = isVoiceAudioMode
      ? audioAiDialogueAudio!.durationSec
      : audioAiType === "voice" && rawPrompt
        ? estimateSpeechDuration(rawPrompt, audioAiSpeechSpeed)
        : 0;
    const effectiveAudioDuration =
      estimatedFromVoice > 0
        ? Math.max(1, Math.min(120, estimatedFromVoice))
        : audioAiType === "sfx"
          ? acotarDuracionDeEfecto(audioAiDuration)
          : audioAiDuration;

    const updatedFields: Partial<TimelineClip> = {
      name: name.length > 40 ? name.slice(0, 37) + "..." : name,
      duration: effectiveAudioDuration,
      aiPrompt: rawPrompt || undefined,
      aiAudioType: audioAiType,
      aiVoiceId: audioAiVoiceId || undefined,
      aiVoiceName: audioAiVoiceName || undefined,
      aiInstrumentalOnly:
        audioAiType === "music" ? audioAiInstrumentalOnly : undefined,
      aiSpeechSpeed: audioAiType === "voice" ? audioAiSpeechSpeed : undefined,
      // Voice mode: text vs audio input
      aiDialogueInputMode:
        audioAiType === "voice" ? audioAiDialogueInputMode : undefined,
      aiDialogueAudioUrl:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.url
          : undefined,
      aiDialogueAudioStoragePath:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.storagePath
          : undefined,
      aiDialogueAudioDurationSec:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.durationSec
          : undefined,
      aiDialogueAudioAssetId:
        audioAiType === "voice" &&
        audioAiDialogueInputMode === "audio" &&
        audioAiDialogueAudio
          ? audioAiDialogueAudio.assetId
          : undefined,
      aiStatus: mode === "draft" ? "draft" : "generating",
      aiGenerationStartedAt: mode === "draft" ? undefined : Date.now(),
    };

    if (isEditing) {
      setClips((prev) =>
        prev.map((c) => (c.id === clipId ? { ...c, ...updatedFields } : c)),
      );
    } else {
      const newClip: TimelineClip = {
        id: clipId,
        type: "audio",
        ...ubicarClipNuevo("audio", { duracion: updatedFields.duration }),
        color: audioTrack.color,
        volume: 1,
        ...updatedFields,
      } as TimelineClip;
      setClips((prev) => [...prev, newClip]);
    }
    setSelectedClipId(clipId);

    // Snapshot before we reset panel state
    const refMode: "voz" | "musica" | "efecto" =
      audioAiType === "voice"
        ? "voz"
        : audioAiType === "sfx"
          ? "efecto"
          : "musica";
    const voiceId = audioAiVoiceId;
    const promptText = rawPrompt;
    const instrumentalOnly =
      audioAiType === "music" ? audioAiInstrumentalOnly : false;
    // ElevenLabs Music la respeta (ADR-2026-09-26-musica-con-elevenlabs-y-duracion).
    const musicDuration = acotarDuracionDeMusica(audioAiDuration);
    const sfxDuration = acotarDuracionDeEfecto(audioAiDuration);
    const speechSpeed = audioAiSpeechSpeed;
    const dialogueInputMode =
      audioAiType === "voice" ? audioAiDialogueInputMode : "text";
    const dialogueAudio =
      audioAiType === "voice" && audioAiDialogueInputMode === "audio"
        ? audioAiDialogueAudio
        : null;

    // Reset panel state and close
    setAudioAiPrompt("");
    setAudioAiDuration(8);
    setAudioAiType("voice");
    setAudioAiVoiceId(null);
    setAudioAiVoiceName("");
    setAudioAiInstrumentalOnly(false);
    setAudioAiSpeechSpeed(1.1);
    setAudioAiDialogueInputMode("text");
    setAudioAiDialogueAudio(null);
    setEditingClipId(null);
    setActivePanel("none");

    // Fire webhook on generate
    if (mode === "generate") {
      setTimeout(async () => {
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.user) throw new Error("No session");

          const body: Record<string, any> = {
            user_id: session.user.id,
            ref_mode: refMode,
            voz_id: voiceId || "",
            project_id: projectId,
            // Trackeo (ADR-585): audio generado desde el Estudio.
            modo_utilizado: "estudio_audio",
            text: promptText,
            instrumental_only: instrumentalOnly,
            ...(refMode === "musica" ? { duracion_seg: musicDuration } : {}),
            ...(refMode === "efecto"
              ? { duracion_seg: acotarDuracionDeEfecto(sfxDuration) }
              : {}),
            ...(refMode === "voz" ? { velocidad_voz: speechSpeed } : {}),
            // Audio input branch (voice-only): the workflow runs speech-to-speech
            // using the user's audio instead of TTS over `text`.
            ...(refMode === "voz" && dialogueInputMode === "audio" && dialogueAudio
              ? {
                  dialogo_input_mode: "audio",
                  audio_url: dialogueAudio.url,
                  audio_duration_sec: dialogueAudio.durationSec,
                }
              : refMode === "voz"
                ? { dialogo_input_mode: "texto" }
                : {}),
          };

          // Helper con flag atlas/n8n (ADR-583) — timeout de 5 min adentro.
          const { path: audioPath } = await generateAudioCreator(body);

          // Credits were just deducted by the workflow — notify the sidebar.
          window.dispatchEvent(new Event("atlas:credits-updated"));

          const { data: urlData } = await supabase.storage
            .from("video-project-assets")
            .createSignedUrl(audioPath, 3600);

          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? {
                    ...c,
                    aiStatus: "ready" as AiClipStatus,
                    storagePath: audioPath,
                    src: urlData?.signedUrl || undefined,
                  }
                : c,
            ),
          );

          // Probe the actual audio duration and update the clip so the timeline reflects reality
          if (urlData?.signedUrl) {
            const probe = new Audio();
            probe.preload = "metadata";
            probe.src = urlData.signedUrl;
            probe.addEventListener("loadedmetadata", () => {
              const dur = probe.duration;
              if (isFinite(dur) && dur > 0) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId
                      ? { ...c, duration: dur, sourceDuration: dur }
                      : c,
                  ),
                );
              }
            });
          }

          // Register the AI audio as a project asset so it appears in the Audio library
          if (companyId && projectId) {
            try {
              const asset = await registerExistingAsset({
                projectId,
                companyId,
                userId: session.user.id,
                storagePath: audioPath,
                type: "audio",
                name: (
                  promptText ||
                  (refMode === "musica"
                    ? "Música IA"
                    : refMode === "efecto"
                      ? "Efecto IA"
                      : "Voz IA")
                ).slice(0, 80),
                mimeType: "audio/mpeg",
              });
              if (asset) {
                setClips((prev) =>
                  prev.map((c) =>
                    c.id === clipId ? { ...c, assetId: asset.id } : c,
                  ),
                );
              }
            } catch (e) {
              console.warn(
                "[VideoEditor] Failed to register AI audio asset:",
                e,
              );
              setClips((prev) =>
                prev.map((c) =>
                  c.id === clipId
                    ? {
                        ...c,
                        aiAssetWarning:
                          "El audio se generó pero no pudimos guardarlo en tu biblioteca. Recargá la página para reintentar.",
                      }
                    : c,
                ),
              );
            }
          }
        } catch (err) {
          console.error("[VideoEditor] AI audio generation failed:", err);
          const msg =
            err instanceof DOMException && err.name === "TimeoutError"
              ? "La generación tardó demasiado (más de 5 min). Probá de nuevo."
              : err instanceof Error
                ? err.message
                : "Error desconocido al generar el audio.";
          setClips((prev) =>
            prev.map((c) =>
              c.id === clipId
                ? { ...c, aiStatus: "error" as AiClipStatus, aiError: msg }
                : c,
            ),
          );
        }
      }, 0);
    }
  };

  // ── Download selected clip ────────────────────────────────────────────────
  // Fetches the clip's source (blob or signed URL) and triggers a browser
  // download with a reasonable filename/extension based on the clip type.
  const downloadSelectedClip = async () => {
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip?.src) return;
    try {
      let res = await fetch(clip.src);
      // Con la URL vencida, Storage contesta un error en JSON y se bajaba ESO con
      // nombre de .mp4. Se firma de nuevo y se reintenta una vez; si igual falla, se
      // avisa (ADR-2026-11-03-los-errores-del-estudio-se-ven).
      if (!res.ok && clip.storagePath) {
        const { data } = await supabase.storage
          .from("video-project-assets")
          .createSignedUrl(clip.storagePath, 600);
        if (data?.signedUrl) res = await fetch(data.signedUrl);
      }
      if (!res.ok) {
        mostrarAviso(
          "No se pudo descargar el clip",
          "El archivo no respondió. Probá de nuevo en un momento.",
        );
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const defaultExt =
        clip.type === "audio" ? "mp3" : clip.type === "image" ? "png" : "mp4";
      const safeName = (clip.name || "clip").trim().replace(/[^\w\-. ]+/g, "_");
      const nombreBase = /\.[a-z0-9]{2,5}$/i.test(safeName)
        ? safeName
        : `${safeName}.${defaultExt}`;
      // Para las imágenes, la extensión sale de los BYTES: el nombre del clip suele
      // venir del archivo en Storage, que llama `.png` a cosas que son JPEG y deja
      // el archivo sin miniatura en Finder
      // (ADR-2026-09-08-descargas-con-extension-que-miente). Audio y video quedan
      // intactos: `fixExtensionFromBlob` sólo reconoce imágenes.
      const finalName =
        clip.type === "image" ? await fixExtensionFromBlob(nombreBase, blob) : nombreBase;
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      console.error("[VideoEditor] downloadSelectedClip failed:", err);
    }
  };

  // ── Transitions ───────────────────────────────────────────────────────────
  // The transition is owned by the SECOND clip in the pair as `transitionIn`.
  // We DO NOT move clips when a transition is added or its duration
  // changes — the renderer extends the outgoing clip's playback past its
  // visual end during the fade window, so visually nothing on the timeline
  // shifts. See `findTransitionSuccessor` + `isClipActiveAtTime` for the
  // playback-side machinery.
  const setClipTransition = useCallback(
    (rightClipId: string, type: TransitionType, durationSec: number) => {
      setClips((prev) =>
        prev.map((c) =>
          c.id === rightClipId
            ? { ...c, transitionIn: { type, durationSec } }
            : c,
        ),
      );
    },
    [],
  );

  const removeClipTransition = useCallback((rightClipId: string) => {
    setClips((prev) =>
      prev.map((c) => {
        if (c.id !== rightClipId || !c.transitionIn) return c;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { transitionIn, ...rest } = c;
        return rest as TimelineClip;
      }),
    );
  }, []);

  // ── Delete / Duplicate ────────────────────────────────────────────────────
  const deleteSelectedClip = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    setClips((prev) => {
      const next = prev.filter((c) => c.id !== selectedClipId);
      // El blob NO se revoca al borrar: el Deshacer lo trae de vuelta y lo
      // necesita vivo (volvía en negro). Se revocan todos al cerrar el editor
      // (ADR-2026-11-03-deshacer-no-revive-procesos). Antes se cuidaba de no
      // revocar uno compartido por split/duplicar; ahora no se revoca ninguno acá.
      if (clip?.src && clip.src.startsWith("blob:"))
        blobsParaRevocarRef.current.add(clip.src);
      return next;
    });
    setSelectedClipId(null);
  };

  // `S` divide en el cursor, como en la mayoría de los editores: el clip
  // seleccionado o, si no hay ninguno, el video principal que está bajo el
  // cursor (ADR-2026-11-02-tocar-un-clip-lleva-el-cursor). Antes no había
  // atajo: había que seleccionar el clip y buscar el botón.
  useEffect(() => {
    const onS = (e: KeyboardEvent) => {
      if (e.key !== "s" && e.key !== "S") return;
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t?.isContentEditable)
        return;
      if (t?.closest?.("[role='dialog']")) return;
      const seleccionado = selectedClipId
        ? clips.find((c) => c.id === selectedClipId)
        : undefined;
      const objetivo =
        seleccionado && seleccionado.type !== "text"
          ? seleccionado
          : !seleccionado
            ? videoBajoElCursor(clips, currentTimeRef.current)
            : null;
      if (!objetivo) return;
      e.preventDefault();
      if (isPlayingRef.current) setIsPlaying(false);
      dividirClip(objetivo.id);
    };
    window.addEventListener("keydown", onS);
    return () => window.removeEventListener("keydown", onS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId, clips, currentTime]);

  // Delete the selected clip with the Delete/Backspace key (unless typing in an input).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (!selectedClipId) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable)
          return;
      }
      e.preventDefault();
      deleteSelectedClip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClipId, clips]);

  // Escape suelta el campo de texto que tenga el foco. Sin esto, con un
  // subtítulo (o cualquier texto) seleccionado y el cursor metido en su campo
  // del panel, Delete borra una letra y no el clip — el atajo de borrar se
  // apaga a propósito mientras se escribe, y no había forma de salir del campo
  // sin ir al mouse. El clip sigue seleccionado: se suelta el campo, no la
  // selección. Ver ADR-2026-09-16-escape-suelta-el-campo-de-texto.
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== "INPUT" && tag !== "TEXTAREA" && !t.isContentEditable) return;
      t.blur();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  // Relación de aspecto NATURAL de cada capa de imagen (ancho/alto), tomada
  // del archivo cuando carga. El marco de selección la necesita: la imagen se
  // dibuja con `object-contain`, así que adentro de la caja del encuadre deja
  // barras a los costados (o arriba y abajo) y el marco tiene que abrazar la
  // imagen, no la caja.
  // Ver ADR-2026-09-16-el-marco-de-una-imagen-abraza-la-imagen.
  const [aspectoDeImagen, setAspectoDeImagen] = useState<
    Record<string, number>
  >({});

  // La caja que el MARCO tiene que abrazar. Para un video es la del encuadre
  // (el video llena su caja, `object-cover`); para una imagen, que se dibuja
  // con `object-contain`, es el rectángulo que la imagen ocupa de verdad
  // adentro de esa caja — centrado, así que sólo hay que achicar el lado que
  // sobra. Sin esto, el marco de una foto vertical en un cuadro 16:9 abrazaba
  // todo el ancho del cuadro, con las manijas en el medio de la nada.
  const cajaVisibleDelClip = useCallback(
    (clip: TimelineClip) => {
      // El ajuste de `contain` lo hace `cajaDelEncuadre` con el aspecto: así
      // el marco, el escalado y el recorte miden todos la misma caja. Antes
      // esto recortaba la caja a mano DESPUÉS del recorte del usuario, y con
      // un recorte asimétrico daba una caja corrida.
      return cajaDelEncuadre(
        clip.encuadre,
        { w: previewSize.w, h: previewSize.h },
        clip.type === "image" ? aspectoDeImagen[clip.id] : undefined,
      );
    },
    [previewSize.w, previewSize.h, aspectoDeImagen],
  );

  // Clip que hay que traer a la vista en la timeline en cuanto exista. Es un
  // ref y no estado: lo escribe quien crea el clip y lo consume el efecto de
  // abajo, sin que nadie más re-renderice por esto.
  const clipAEnfocarRef = useRef<string | null>(null);

  const duplicateSelectedClip = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip) return;
    const newClip = {
      ...clip,
      id: generateId(),
      // Pegada al original, sin hueco: la copia casi siempre es para
      // continuar la toma, y medio segundo de negro en el medio es algo que
      // después hay que sacar a mano.
      startTime: clip.startTime + clip.duration,
    };
    setClips((prev) => [...prev, newClip]);
    setSelectedClipId(newClip.id);
    // La copia nace DESPUÉS del original: con la timeline con zoom, o con el
    // original terminando cerca del borde derecho, la copia aparece fuera de
    // la vista y el click parece no haber hecho nada.
    clipAEnfocarRef.current = newClip.id;
  };

  // Trae a la vista el clip anotado en `clipAEnfocarRef`. Corre cuando cambia
  // `clips` —o sea, en el render en el que el clip nuevo ya existe y el ancho
  // del contenido ya creció—, así el scroll que calcula es el definitivo.
  useEffect(() => {
    const id = clipAEnfocarRef.current;
    if (!id) return;
    const clip = clips.find((c) => c.id === id);
    if (!clip) return;
    clipAEnfocarRef.current = null;
    const el = timelineAreaRef.current;
    if (!el) return;
    // En el teléfono el cursor está CLAVADO al centro y el scroll de la cinta
    // ES el tiempo, así que "traer a la vista" no se hace scrolleando —eso
    // pelearía con la sincronía— sino moviendo el cursor al arranque del clip:
    // la cinta se acomoda sola (el layout effect que escribe `scrollLeft` a
    // partir de `currentTime`) y el clip queda justo bajo el cursor, en el
    // centro de la pantalla.
    //
    // Antes acá se cortaba con un `return` porque el caso pensado era el clip
    // que nace DONDE está el cursor. Duplicar es el otro caso: la copia nace
    // pegada al final del original, muchas veces fuera de la vista, y el toque
    // parecía no haber hecho nada.
    // Ver ADR-2026-09-17-duplicar-en-el-telefono-lleva-el-cursor-a-la-copia.
    if (enElTelefonoRef.current) {
      setCurrentTime(Math.max(0, clip.startTime));
      return;
    }

    const ppsActual = BASE_PPS * zoomRef.current;
    const izquierda = PAD_TIMELINE + clip.startTime * ppsActual;
    const derecha = izquierda + clip.duration * ppsActual;
    const MARGEN = 24;
    const visibleIzq = el.scrollLeft;
    const visibleDer = el.scrollLeft + el.clientWidth;

    let destino: number | null = null;
    if (izquierda < visibleIzq + MARGEN) {
      destino = Math.max(0, izquierda - MARGEN);
    } else if (derecha > visibleDer - MARGEN) {
      // Si entra entero, lo pegamos al borde derecho; si no (clip más ancho
      // que la ventana), mostramos su comienzo, que es lo que importa.
      const cabe = derecha - izquierda <= el.clientWidth - MARGEN * 2;
      destino = cabe
        ? derecha + MARGEN - el.clientWidth
        : Math.max(0, izquierda - MARGEN);
    }
    if (destino === null) return;
    el.scrollTo({ left: Math.max(0, destino), behavior: "smooth" });
  }, [clips]);

  // ── Separate audio from a video clip ─────────────────────────────────────
  // Mutes the original video and creates a new audio clip referencing the
  // same media file (browsers play the audio track of an MP4 via <audio>).
  // The new audio clip is placed on the first audio track that has no overlap
  // at its timeline position; a new track is created if none is free.
  const separateAudioFromVideoClip = (clipId: string) => {
    const clip = clips.find((c) => c.id === clipId);
    if (!clip) return;
    const isVideoLike =
      clip.type === "video" ||
      (clip.type === "ai-generated" && clip.aiStatus === "ready");
    if (!isVideoLike || !clip.src) return;

    const clipStart = clip.startTime;
    const clipEnd = clip.startTime + clip.duration;
    const overlaps = (a0: number, a1: number, b0: number, b1: number) =>
      a0 < b1 && b0 < a1;

    const audioClips = clips.filter((c) => c.type === "audio");
    const existingIndices = getAudioTrackIndices(clips);

    let targetTrack = 0;
    if (existingIndices.length === 0) {
      targetTrack = 0;
    } else {
      let found = false;
      for (const idx of existingIndices) {
        const trackClips = audioClips.filter(
          (c) => getClipTrackIndex(c) === idx,
        );
        const hasOverlap = trackClips.some((c) =>
          overlaps(clipStart, clipEnd, c.startTime, c.startTime + c.duration),
        );
        if (!hasOverlap) {
          targetTrack = idx;
          found = true;
          break;
        }
      }
      if (!found) targetTrack = existingIndices[existingIndices.length - 1] + 1;
    }

    const newAudioClip: TimelineClip = {
      id: generateId(),
      type: "audio",
      name: `${clip.name} (audio)`,
      startTime: clip.startTime,
      duration: clip.duration,
      color: getAudioTrackColor(targetTrack),
      volume: clip.volume ?? 1,
      src: clip.src,
      storagePath: clip.storagePath,
      mediaOffset: clip.mediaOffset,
      sourceDuration: clip.sourceDuration,
      assetId: clip.assetId,
      trackIndex: targetTrack,
    };

    setClips((prev) => [
      ...prev.map((c) => (c.id === clipId ? { ...c, volume: 0 } : c)),
      newAudioClip,
    ]);
    setSelectedClipId(newAudioClip.id);
  };

  // ── Trim clip via yellow edge handles ──────────────────────────────────
  const handleTrimPointerDown = useCallback(
    (
      e: React.PointerEvent<HTMLDivElement>,
      clip: TimelineClip,
      edge: "left" | "right",
    ) => {
      e.stopPropagation();
      e.preventDefault();
      const startX = e.clientX;
      const origStart = clip.startTime;
      const origDuration = clip.duration;
      const origOffset = clip.mediaOffset || 0;
      const sourceDur = clip.sourceDuration;
      // Con el clip acelerado, un segundo de timeline se come más material:
      // los topes de "hasta dónde puedo estirar" van divididos por la
      // velocidad, y lo que se corre el comienzo dentro del archivo va
      // multiplicado por ella.
      const velocidadDelClip = velocidadDeClip(clip);
      const MIN_DURATION = 0.1;
      const ppsAtStart = BASE_PPS * zoomRef.current;

      // Imán de las orillas. Antes sólo miraba a los "hermanos" (los clips de
      // la MISMA pista), que es lo que sirve para pegar dos clips en fila;
      // pero lo que uno hace todo el tiempo es estirar un clip para que
      // termine (o empiece) exactamente donde termina otro de OTRA capa — el
      // video con su música, el texto con la escena — y a ojo nunca queda
      // parejo. Así que los candidatos son las dos orillas de TODOS los otros
      // clips de la línea de tiempo, sin importar la pista, más el 0.
      const snapPoints: number[] = [0];
      for (const c of clips) {
        if (c.id === clip.id) continue;
        snapPoints.push(c.startTime, c.startTime + c.duration);
      }
      // La tolerancia va en PÍXELES, no en segundos: con zoom 20x, medio
      // segundo son 400px de pantalla y el imán se comía todo el gesto.
      const snapTol = Math.min(SNAP_THRESHOLD, SNAP_PX / ppsAtStart);
      const closestSnap = (value: number): number | null => {
        let best: number | null = null;
        let bestDist = snapTol;
        for (const p of snapPoints) {
          const d = Math.abs(value - p);
          if (d < bestDist) {
            bestDist = d;
            best = p;
          }
        }
        return best;
      };

      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      // El corrimiento cuenta también lo que se movió la cinta: con el
      // autoscroll (ADR-2026-11-02-la-timeline-sigue-al-cursor) el puntero puede
      // quedarse quieto en el borde mientras la manija sigue avanzando.
      const scrollInicial = timelineAreaRef.current?.scrollLeft ?? 0;
      const onMove = (ev: { clientX: number; clientY: number }) => {
        anotarPunteroDelArrastre({ x: ev.clientX, y: ev.clientY });
        const scrollAhora = timelineAreaRef.current?.scrollLeft ?? scrollInicial;
        const deltaSec =
          (ev.clientX - startX + (scrollAhora - scrollInicial)) / ppsAtStart;
        // Guía vertical a mostrar mientras dure el imán (null = sin imán).
        let guide: number | null = null;
        let next: Partial<TimelineClip>;

        if (edge === "right") {
          let newDuration = origDuration + deltaSec;
          if (newDuration < MIN_DURATION) newDuration = MIN_DURATION;
          if (sourceDur !== undefined) {
            const maxDur = Math.max(
              MIN_DURATION,
              (sourceDur - origOffset) / velocidadDelClip,
            );
            if (newDuration > maxDur) newDuration = maxDur;
          }
          const snapTo = closestSnap(origStart + newDuration);
          if (snapTo !== null) {
            let snapped = snapTo - origStart;
            if (snapped < MIN_DURATION) snapped = MIN_DURATION;
            if (sourceDur !== undefined) {
              const maxDur = Math.max(
              MIN_DURATION,
              (sourceDur - origOffset) / velocidadDelClip,
            );
              if (snapped > maxDur) snapped = maxDur;
            }
            // Si el clamp (mínimo o fin del material) movió la orilla, el
            // imán no se cumplió: no pegamos ni dibujamos la guía.
            if (Math.abs(origStart + snapped - snapTo) < 1e-6) {
              newDuration = snapped;
              guide = snapTo;
            }
          }
          next = { duration: newDuration };
        } else {
          // Left edge. El fin queda clavado; lo que se mueve es el comienzo.
          const maxStart = origStart + origDuration - MIN_DURATION;
          if (sourceDur === undefined) {
            // Clip SIN material de origen (texto, imagen): su duración no sale
            // de ningún archivo, así que el comienzo se puede estirar hacia
            // ATRÁS, no sólo recortar. Antes no se podía: la cuenta iba por
            // `mediaOffset`, que no puede bajar de 0, y con un clip de texto
            // (offset 0) tirar de la manija izquierda no hacía nada.
            let newStart = origStart + deltaSec;
            if (newStart < 0) newStart = 0;
            if (newStart > maxStart) newStart = maxStart;
            const snapTo = closestSnap(newStart);
            if (snapTo !== null && snapTo >= 0 && snapTo <= maxStart) {
              newStart = snapTo;
              guide = snapTo;
            }
            next = {
              startTime: newStart,
              duration: origStart + origDuration - newStart,
            };
          } else {
            // Clip CON material de origen (video, audio): hacia atrás sólo
            // hay tanto como se haya recortado antes — el comienzo se corre
            // dentro del archivo vía `mediaOffset`, que no puede ser negativo
            // porque antes del primer frame no hay nada.
            let newOffset = origOffset + deltaSec * velocidadDelClip;
            if (newOffset < 0) newOffset = 0;
            const maxOffset =
              origOffset + (origDuration - MIN_DURATION) * velocidadDelClip;
            if (newOffset > maxOffset) newOffset = maxOffset;
            let offsetDelta = newOffset - origOffset;
            let newStart = origStart + offsetDelta / velocidadDelClip;
            const snapTo = closestSnap(newStart);
            if (snapTo !== null) {
              let snappedOffset =
                origOffset + (snapTo - origStart) * velocidadDelClip;
              if (snappedOffset < 0) snappedOffset = 0;
              if (snappedOffset > maxOffset) snappedOffset = maxOffset;
              const snappedStart =
                origStart + (snappedOffset - origOffset) / velocidadDelClip;
              if (Math.abs(snappedStart - snapTo) < 1e-6) {
                newOffset = snappedOffset;
                offsetDelta = snappedOffset - origOffset;
                newStart = snappedStart;
                guide = snapTo;
              }
            }
            next = {
              startTime: newStart,
              duration: origDuration - offsetDelta / velocidadDelClip,
              mediaOffset: newOffset,
            };
          }
        }

        setSnapLine(guide);
        setClips((prev) =>
          prev.map((c) => (c.id === clip.id ? { ...c, ...next } : c)),
        );
      };
      const onUp = () => {
        detenerAutoscroll();
        setSnapLine(null);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      // Si el navegador cancela el gesto, la manija se suelta igual.
      window.addEventListener("pointercancel", onUp);
      empezarAutoscroll({ x: startX, y: e.clientY }, () => {
        const p = punteroDelArrastreRef.current;
        if (p) onMove({ clientX: p.x, clientY: p.y });
      });
    },
    [clips, anotarPunteroDelArrastre, detenerAutoscroll, empezarAutoscroll],
  );

  const restoreSelectedClipToOriginal = () => {
    if (!selectedClipId) return;
    const clip = clips.find((c) => c.id === selectedClipId);
    if (!clip || clip.sourceDuration === undefined) return;
    const offset = clip.mediaOffset || 0;
    const duracionCompleta = clip.sourceDuration;
    // Al recuperar el material recortado, el clip crece por los dos lados y le
    // queda encima a sus vecinos. Se los corre para que queden pegados, sin
    // encimarse y sin cambiar el orden.
    // Ver ADR-2026-09-16-restaurar-corre-a-los-vecinos.
    const trackCfg = getTrackForClipType(clip.type);
    const idx = getClipTrackIndex(clip);
    const enLaPista = clips.filter(
      (c) => trackCfg.clipTypes.includes(c.type) && getClipTrackIndex(c) === idx,
    );
    const cambios = acomodarPistaAlrededorDe({
      clips: enLaPista.map((c) => ({
        id: c.id,
        startTime: c.startTime,
        duration: c.duration,
      })),
      id: clip.id,
      nuevoInicio: clip.startTime - offset,
      nuevaDuracion: duracionCompleta,
    });
    setClips((prev) =>
      prev.map((c) => {
        const inicio = cambios.get(c.id);
        if (c.id === clip.id) {
          return {
            ...c,
            startTime: inicio ?? clip.startTime - offset,
            duration: duracionCompleta,
            mediaOffset: 0,
          };
        }
        return inicio === undefined ? c : { ...c, startTime: inicio };
      }),
    );
  };

  // Parte un clip en el cursor. Las dos mitades salen de `partirClip`: la de la
  // derecha ya no hereda la transición de entrada (quedaba un fundido en el
  // corte) y la entrada/salida de las capas se reparte entre las dos
  // (ADR-2026-11-02-tocar-un-clip-lleva-el-cursor).
  const dividirClip = (clipId: string) => {
    const clip = clips.find((c) => c.id === clipId);
    if (!clip) return;
    const rightId = generateId();
    const mitades = partirClip(clip, currentTime, rightId, velocidadDeClip(clip));
    if (!mitades) return;
    setClips((prev) =>
      prev.flatMap((c) => (c.id === clip.id ? mitades : [c])),
    );
    setSelectedClipId(rightId);
  };
  const splitSelectedClip = () => {
    if (selectedClipId) dividirClip(selectedClipId);
  };

  // ── Copiar el cuadro actual al portapapeles ──────────────────────────────
  // El `<video>` del preview ya está en el cuadro del cursor y monta con CORS
  // (lo necesita el pincel), así que se exporta tal cual: canvas → PNG →
  // portapapeles, sin red ni créditos. Si el navegador no deja escribir
  // imágenes (Firefox, permiso denegado), el PNG se descarga
  // (ADR-2026-09-26-copiar-cuadro-del-estudio-al-portapapeles).
  // El aviso dice QUÉ pasó y de QUÉ clip ("Copiado" / "Descargado"), y un
  // pedido nuevo pisa el reloj del anterior (revisión de Greptile al PR #149).
  const [cuadroCopiado, setCuadroCopiado] = useState<
    { clipId: string; como: "copiado" | "descargado" } | null
  >(null);
  const relojDelCuadroRef = useRef<number | null>(null);
  const avisarCuadro = (clipId: string, como: "copiado" | "descargado") => {
    if (relojDelCuadroRef.current) window.clearTimeout(relojDelCuadroRef.current);
    setCuadroCopiado({ clipId, como });
    relojDelCuadroRef.current = window.setTimeout(() => {
      setCuadroCopiado(null);
      relojDelCuadroRef.current = null;
    }, 2000);
  };
  const copiarCuadroDelClip = async (clip: TimelineClip) => {
    const vid = mediaRefs.current.get(clip.id) as HTMLVideoElement | undefined;
    if (!vid || !vid.videoWidth) {
      setUploadErrorMsg(
        "El video todavía no está cargado. Movete sobre el clip y reintentá.",
      );
      return;
    }
    const tLocal = currentTime - clip.startTime;
    const nombre = nombreDelCuadro(clip.name, tLocal);
    // El segundo del ARCHIVO que corresponde al cursor (misma cuenta que
    // `syncMediaElement`): el preview tolera 0,15 s de diferencia para no
    // buscar a cada rato, y si el cursor se acaba de mover el video puede estar
    // buscando todavía; el PNG tiene que ser ESE cuadro, así que se lleva
    // exacto y se espera el seeked antes de dibujar.
    const tMaterial = tLocal * velocidadDeClip(clip) + (clip.mediaOffset || 0);
    // La PROMESA del PNG entra al portapapeles sin esperar: Safari sólo escribe
    // mientras dura el click.
    // Con la versión liviana en el preview, la captura sale del ORIGINAL
    // (ADR-2026-11-03-el-preview-usa-la-version-liviana).
    const imagen =
      usaLaVersionLiviana(clip) && clip.src
        ? videoOriginalEn(clip.src, tMaterial).then((el) => cuadroDelVideo(el, tMaterial))
        : cuadroDelVideo(vid, tMaterial);
    // Un fallo del canvas (video servido sin CORS) se lee acá y no como
    // "unhandled": el portapapeles devuelve false y el respaldo lo vuelve a esperar.
    imagen.catch(() => {});
    try {
      if (await copiarImagenAlPortapapeles(imagen)) {
        avisarCuadro(clip.id, "copiado");
        return;
      }
      descargarImagen(await imagen, nombre);
      avisarCuadro(clip.id, "descargado");
    } catch (err) {
      console.error("[VideoEditor] copiar cuadro falló:", err);
      setUploadErrorMsg(
        "No se pudo sacar la captura del video. Recargá la página y volvé a intentar.",
      );
    }
  };

  // ── Acciones del clip seleccionado (barra flotante del preview) ──────────
  // Las mismas que vivían en la sección "Clip" del panel de la izquierda.
  // Ahora van al costado del cuadro, que es donde el usuario está mirando
  // mientras edita: dividir, duplicar o borrar son gestos sobre el clip, no
  // ajustes de un formulario.
  // Va DESPUÉS de todos los handlers que usa: las deps de un hook se arman
  // durante el render y un `const` declarado más abajo todavía está en su
  // zona muerta (ADR-2026-09-15-content-duration-vive-pegado-a-clips).
  // Ver ADR-2026-09-16-las-acciones-del-clip-flotan-al-lado-del-cuadro.
  const accionesDelClipSeleccionado = useMemo<AccionDeClip[]>(() => {
    const clip = selectedClip;
    if (!clip) return [];

    const esElemento =
      clip.type === "text" || clip.type === "animation" || clip.type === "image";

    const iaLista = clip.type === "ai-generated" && clip.aiStatus === "ready";
    const videoOrReady = clip.type === "video" || iaLista;
    const localSplit = currentTime - clip.startTime;
    const puedeDividir =
      localSplit > 0.1 && localSplit < clip.duration - 0.1;
    const fueRecortado =
      clip.sourceDuration !== undefined &&
      (Math.abs(clip.duration - clip.sourceDuration) > 0.05 ||
        (clip.mediaOffset || 0) > 0.05);

    const items: AccionDeClip[] = [];
    // En escritorio, la puerta al panel de edición (tocar el clip ya no lo
    // abre). En el teléfono ya está "Avanzado" adelante de la barra
    // (ADR-2026-11-03-tocar-un-clip-solo-lo-selecciona).
    if (!enElTelefono)
      items.push({
        label: "Editar",
        icon: <SlidersHorizontal className="w-4 h-4" />,
        title: "Abrir la edición de este clip",
        onClick: () => abrirPanelDelClip(clip),
      });
    // Todos menos el texto: partir una frase al medio deja dos clips con el
    // mismo texto completo, que no es lo que nadie espera.
    if (clip.type !== "text")
      items.push({
        label: "Dividir",
        icon: <Scissors className="w-4 h-4" />,
        disabled: !puedeDividir,
        title: puedeDividir
          ? "Dividir clip en el punto actual"
          : "Ubicá el selector de tiempo dentro del clip para dividirlo",
        onClick: splitSelectedClip,
      });
    if (videoOrReady) {
      // "Extender video" ya no está acá: no es un gesto de edición como
      // dividir o duplicar, GENERA material nuevo con IA a partir del último
      // frame. Vive en la lista de Herramientas IA del panel del clip
      // (ADR-2026-09-17-extender-video-es-una-herramienta-de-ia).
      items.push({
        label: "Separar audio",
        corto: "Separar",
        icon: <Music className="w-4 h-4" />,
        onClick: () => separateAudioFromVideoClip(clip.id),
      });
      // El cuadro que se está viendo, al portapapeles (o descargado si el
      // navegador no deja). Sólo con el cursor sobre el clip: es ESE cuadro.
      const cursorEnElClip =
        localSplit >= 0 && localSplit <= clip.duration;
      const avisoDeEsteClip =
        cuadroCopiado?.clipId === clip.id ? cuadroCopiado.como : null;
      items.push({
        label:
          avisoDeEsteClip === "copiado"
            ? "Copiado"
            : avisoDeEsteClip === "descargado"
              ? "Descargado"
              : "Captura",
        corto:
          avisoDeEsteClip === "copiado"
            ? "Copiado"
            : avisoDeEsteClip === "descargado"
              ? "Descargado"
              : "Captura",
        icon: <Camera className="w-4 h-4" />,
        disabled: !cursorEnElClip,
        title: cursorEnElClip
          ? "Copiar una captura del video (lo que se ve ahora) al portapapeles"
          : "Ubicá el selector de tiempo sobre el clip para sacar una captura",
        onClick: () => void copiarCuadroDelClip(clip),
      });
    }
    if (fueRecortado)
      items.push({
        label: "Restaurar",
        icon: <RotateCcw className="w-4 h-4" />,
        onClick: restoreSelectedClipToOriginal,
      });
    items.push({
      label: "Duplicar",
      icon: <Copy className="w-4 h-4" />,
      onClick: duplicateSelectedClip,
    });
    if (clip.src && !esElemento)
      items.push({
        label: "Descargar",
        icon: <Download className="w-4 h-4" />,
        onClick: downloadSelectedClip,
      });
    items.push({
      label: "Eliminar",
      icon: <Trash2 className="w-4 h-4" />,
      danger: true,
      title: clip.isSubtitle ? "Eliminar este subtítulo" : "Eliminar clip",
      onClick: deleteSelectedClip,
    });
    // Un solo botón de borrar, también en los subtítulos: el de la tanda
    // entera convivía con el del clip, las dos papeleras rojas una al lado de
    // la otra, y era muy fácil llevarse puestos los cincuenta subtítulos
    // queriendo sacar uno.
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClip, currentTime, cuadroCopiado, enElTelefono]);

  // ── Cortar silencios automáticamente ─────────────────────────────────
  // Lee (y si hace falta genera) el transcript del clip, detecta los huecos
  // entre palabras consecutivas que pasan del umbral, y reemplaza el clip
  // por N piezas de "habla" pegadas — cerrando los huecos con ripple-shift
  // de todo lo que viene después en la timeline. Si el clip no tiene
  // ninguna palabra detectada (silencio total / sin habla), no toca nada
  // y muestra un aviso. Soporta clips de video, audio, y ai-generated con
  // audio listo. Best-effort sobre subtítulos linkeados: descarta los que
  // caen completamente dentro de un silencio y remapea el resto a su
  // nueva posición de timeline.
  const cutSilencesFromClip = useCallback(
    async (clipId: string, thresholdSec: number, paddingSec: number) => {
      const clip = clips.find((c) => c.id === clipId);
      if (!clip) return;
      const isCuttable =
        clip.type === "video" ||
        clip.type === "audio" ||
        (clip.type === "ai-generated" && clip.aiStatus === "ready");
      if (!isCuttable) {
        setSilenceCutNotice("Este tipo de clip no se puede analizar.");
        return;
      }
      if (!Number.isFinite(thresholdSec) || thresholdSec <= 0) {
        setSilenceCutNotice("Umbral inválido.");
        return;
      }
      try {
        setSilenceCutBusy(clipId);
        // 1. Asegurar transcript: si no está cacheado, llamar al webhook y
        //    persistirlo en el clip (mismo flujo que "Generar subtítulos").
        let transcript = transcriptVigente(clip);
        if (
          !transcript ||
          !Array.isArray(transcript.words) ||
          transcript.words.length === 0
        ) {
          let url: string | null = null;
          if (clip.storagePath) {
            try {
              url = await getAssetSignedUrl(clip.storagePath);
            } catch {}
          }
          if (
            !url &&
            typeof clip.src === "string" &&
            /^https?:\/\//.test(clip.src)
          ) {
            url = clip.src;
          }
          if (!url) {
            setSilenceCutNotice("No pudimos acceder al archivo del clip.");
            return;
          }
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (!user?.id) {
            setSilenceCutNotice("Sesión expirada — recargá la página.");
            return;
          }
          const tr = await requestTranscript(url, user.id);
          if (!tr.ok) {
            setSilenceCutNotice(tr.message);
            return;
          }
          transcript = tr.transcript;
          // Cachear para próximas llamadas; igual seguimos abajo con el valor
          // local — el setClips de abajo va a pisar `transcript` con el mismo
          // valor cuando reconstruya el clip.
          setClips((prev) =>
            prev.map((c) =>
            c.id === clipId
              ? { ...c, transcript: marcarTranscript(tr.transcript, clip.storagePath) }
              : c,
          ),
          );
        }

        // 2. Detectar palabras dentro de la ventana visible del clip.
        const offset = clip.mediaOffset ?? 0;
        const winStart = offset;
        const winEnd = offset + clip.duration;
        const visibleWords = transcript.words
          .filter(
            (w) =>
              w &&
              w.type === "word" &&
              typeof w.start === "number" &&
              typeof w.end === "number" &&
              w.end > winStart &&
              w.start < winEnd,
          )
          .map((w) => ({
            // Clampar al window — si una palabra cruza el borde, recortarla.
            start: Math.max(winStart, w.start),
            end: Math.min(winEnd, w.end),
          }))
          .filter((w) => w.end > w.start);
        if (visibleWords.length === 0) {
          setSilenceCutNotice("No se detectó habla en este clip.");
          return;
        }
        visibleWords.sort((a, b) => a.start - b.start);

        // 3. Construir las regiones de silencio en source-media seconds.
        //    Incluye head (antes de la primera palabra) y tail (después de la
        //    última). Usamos un padding chico para no cortar tan a filo que
        //    se pierdan respiraciones / arranques de consonante.
        // Clampar el padding — si es más grande que la mitad del umbral,
        // los cortes quedarían invertidos (end < start). Garantizamos que
        // siempre haya al menos un mínimo de silencio real removido.
        const PADDING = Math.max(0, Math.min(paddingSec, thresholdSec * 0.45));
        const silences: Array<{ start: number; end: number }> = [];
        // Head silence
        const headSilence = visibleWords[0].start - winStart;
        if (headSilence > thresholdSec) {
          silences.push({
            start: winStart,
            end: visibleWords[0].start - PADDING,
          });
        }
        for (let i = 0; i < visibleWords.length - 1; i++) {
          const gap = visibleWords[i + 1].start - visibleWords[i].end;
          if (gap > thresholdSec) {
            silences.push({
              start: visibleWords[i].end + PADDING,
              end: visibleWords[i + 1].start - PADDING,
            });
          }
        }
        // Tail silence
        const tailSilence = winEnd - visibleWords[visibleWords.length - 1].end;
        if (tailSilence > thresholdSec) {
          silences.push({
            start: visibleWords[visibleWords.length - 1].end + PADDING,
            end: winEnd,
          });
        }
        if (silences.length === 0) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }
        // Sanity: clampar y filtrar silencios degenerados.
        const cleanSilences = silences
          .map((s) => ({
            start: Math.max(winStart, s.start),
            end: Math.min(winEnd, s.end),
          }))
          .filter((s) => s.end - s.start > 0.05)
          .sort((a, b) => a.start - b.start);
        if (cleanSilences.length === 0) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }

        // 4. Convertir silencios en "speech segments" complementarios sobre
        //    [winStart, winEnd]. Cada speech segment va a ser una pieza nueva
        //    en la timeline (la primera reusa el id original).
        const speechSegments: Array<{ start: number; end: number }> = [];
        let cursor = winStart;
        for (const sil of cleanSilences) {
          if (sil.start > cursor + 1e-3)
            speechSegments.push({ start: cursor, end: sil.start });
          cursor = Math.max(cursor, sil.end);
        }
        if (cursor < winEnd - 1e-3)
          speechSegments.push({ start: cursor, end: winEnd });
        if (speechSegments.length === 0) {
          setSilenceCutNotice(
            "No quedó habla después de cortar los silencios.",
          );
          return;
        }
        const totalRemoved = cleanSilences.reduce(
          (acc, s) => acc + (s.end - s.start),
          0,
        );
        if (totalRemoved < 0.05) {
          setSilenceCutNotice("No se encontraron silencios mayores al umbral.");
          return;
        }

        // 5. Aplicar el cambio en un único setClips (atómico, una entrada de
        //    Undo). Reemplazamos C por N piezas pegadas, ripple-shift de los
        //    clips que arrancan a partir del final de C, y compactamos los
        //    subtítulos linkeados.
        const clipEndTL = clip.startTime + clip.duration;
        setClips((prev) => {
          const next: TimelineClip[] = [];
          for (const c of prev) {
            // (a) El propio clip → expandir en N piezas contiguas.
            if (c.id === clipId) {
              let tlCursor = c.startTime;
              speechSegments.forEach((seg, i) => {
                const pieceDuration = seg.end - seg.start;
                const pieceMediaOffset = seg.start;
                const piece: TimelineClip = {
                  ...c,
                  id: i === 0 ? c.id : generateId(),
                  startTime: tlCursor,
                  duration: pieceDuration,
                  mediaOffset: pieceMediaOffset,
                };
                next.push(piece);
                tlCursor += pieceDuration;
              });
              continue;
            }
            // (b) Subtítulos linkeados al clip que estamos cortando → mapear
            //     según en qué speech segment cae su `linkedMediaStart`. Si
            //     cae en silencio, el caption desaparece. Si sobrevive, su
            //     startTime se calcula contra el offset acumulado del segmento
            //     correspondiente.
            if (
              c.linkedClipId === clipId &&
              typeof c.linkedMediaStart === "number" &&
              typeof c.linkedMediaEnd === "number"
            ) {
              const lms = c.linkedMediaStart;
              // ¿En qué speech segment cae el inicio de la palabra?
              let cumulative = 0;
              let matched: { newStart: number; remap: number } | null = null;
              for (const seg of speechSegments) {
                if (lms >= seg.start && lms < seg.end) {
                  // Posición relativa dentro del segmento + offset acumulado +
                  // startTime original del clip fuente = nuevo startTime de la
                  // caption en timeline.
                  const intoSeg = lms - seg.start;
                  matched = {
                    newStart: clip.startTime + cumulative + intoSeg,
                    remap: 0,
                  };
                  break;
                }
                cumulative += seg.end - seg.start;
              }
              if (!matched) continue; // silenciada → drop
              next.push({ ...c, startTime: matched.newStart });
              continue;
            }
            // (c) Cualquier otro clip que arranca DESPUÉS del fin original
            //     de C → ripple-shift a la izquierda por totalRemoved.
            //     Tolerancia de 1e-3s para no colar al borde.
            if (c.startTime >= clipEndTL - 1e-3) {
              next.push({
                ...c,
                startTime: Math.max(0, c.startTime - totalRemoved),
              });
              continue;
            }
            next.push(c);
          }
          return next;
        });
        // Mostramos un mensaje breve; el usuario ve los cortes aplicados en
        // la timeline.
        const removedRounded = Math.round(totalRemoved * 10) / 10;
        setSilenceCutNotice(
          `Cortados ${cleanSilences.length} silencio(s) (${removedRounded}s).`,
        );
      } catch (err) {
        console.error("[VideoEditor] cutSilencesFromClip failed", err);
        setSilenceCutNotice(
          err instanceof Error ? err.message : "Falló el corte de silencios.",
        );
      } finally {
        setSilenceCutBusy(null);
      }
    },
    [clips],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // Mobile blocker

  return (
    <BrandColorsContext.Provider value={brandProfile?.colors || []}>
      <BrandFontsContext.Provider
        value={
          (brandProfile?.fonts || {}) as Record<string, string | undefined>
        }
      >
        <BrandCustomFontsContext.Provider value={customFontFamilies}>
          {/* ── Carga ───────────────────────────────────────────────────── */}
          {/* Mismo marco que el editor: si no, el spinner pinta un rectángulo a
              sangre mientras carga y el editor "salta" al aparecer. */}
          {projectLoading && (
            <div
              className={`flex items-center justify-center fixed right-0 bottom-0 z-20 bg-[#F9F9F9] overflow-hidden ${
                enElTelefono ? "" : `${MARCO_PANTALLA} ${BORDE_PANTALLA}`
              }`}
              style={
                enElTelefono
                  ? { left: 0, top: 0 }
                  : {
                      left: sidebarWidth,
                      right: derechaDelEditor,
                      top: 'calc(var(--shell-barra-creditos, 0px) + 8px)',
                    }
              }
            >
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          )}

          {/* ── Desktop: Full editor (fixed to bypass layout padding) ──────── */}
          {/* Flota como la sidebar y como los otros dos editores del shell
              (ADR-2026-08-05-el-estudio-de-video-entra-al-shell): 8px de aire a
              los cuatro lados y esquinas de 16px, con el aire de la izquierda
              dado por `sidebarWidth` (margen + ancho + margen). Sigue en `fixed`
              por lo mismo de siempre: saltearse el padding del layout.
              El gris de adentro es el del shell (#F9F9F9) y no `surface-bg`
              (#F0F2F7), que es el gris AZULADO del chrome viejo: pegado a la
              sidebar se leía como de otra app. Mismo cambio que en "Edición
              Canva" (ADR-2026-08-05-el-editor-flota-como-la-sidebar). */}
          <div
            className={`${projectLoading ? "hidden" : "flex"} flex-col overflow-hidden fixed right-0 bottom-0 z-20 transition-[right] duration-300 ease-out ${
              enElTelefono
                ? "bg-white"
                : `bg-[#F9F9F9] ${MARCO_PANTALLA} ${BORDE_PANTALLA}`
            }`}
            /* En el teléfono no hay marco flotante ni aire: el editor ES la
               pantalla, de borde a borde. El respiro de arriba y de abajo lo
               ponen adentro el topbar y la barra de herramientas con
               `env(safe-area-inset-*)`, para que el notch y la barra de gestos
               no se coman controles. */
            style={
              enElTelefono
                ? { left: 0, top: 0 }
                : {
                    left: sidebarWidth,
                    right: derechaDelEditor,
                    top: 'calc(var(--shell-barra-creditos, 0px) + 8px)',
                  }
            }
          >
            {/* Archivos de la computadora arrastrándose sobre el editor: el
                marco dice que se pueden soltar en cualquier lado. Sin eventos:
                el soltado lo atienden la timeline, el cuadro o el documento. */}
            {arrastrandoArchivos && !arrastrandoSobreElPreview && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-2 z-[60] flex items-start justify-center rounded-xl border-2 border-dashed border-brand-primary/70"
              >
                <span className="mt-16 rounded-full bg-white px-3 py-1.5 text-[12.5px] font-medium text-brand-black shadow-md">
                  Soltá para agregar al video
                </span>
              </div>
            )}
            {/* El guardado falló y se está reintentando: sin esto el proyecto
                dejaba de guardarse en silencio
                (ADR-2026-11-02-el-estudio-guarda-solo-lo-ultimo-y-avisa-si-falla). */}
            {/* Los avisos flotan apilados debajo de la barra de arriba: el del
                guardado y el del clip seleccionado (qué le pasó, en texto: antes
                sólo en el `title` del badge, invisible en el teléfono —
                ADR-2026-11-03-los-errores-del-estudio-se-ven). */}
            <div
              // `data-clip-ui`: tocar el aviso (Reintentar, cerrar) no suelta el clip.
              data-clip-ui
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 z-[55] flex flex-col items-center gap-2"
              style={{
                top: enElTelefono
                  ? "calc(52px + env(safe-area-inset-top, 0px) + 8px)"
                  : "60px",
              }}
            >
              <AvisoDeGuardado
                fallo={guardadoFallo}
                onReintentar={() => void guardarAhora()}
              />
              <AvisoDeConflicto
                hayConflicto={hayConflicto}
                onAbrirLoUltimo={() => window.location.reload()}
                onQuedarmeConEsto={() => {
                  forzarGuardadoRef.current = true;
                  hayConflictoRef.current = false;
                  setHayConflicto(false);
                  void guardarAhora();
                }}
              />
              <AvisoDelClip
                motivo={motivoDelSeleccionado}
                onCerrar={() => setAvisoDelClipCerrado(claveDelAvisoDelClip)}
                onReintentar={() => {
                  if (!selectedClip) return;
                  if (selectedClip.type === "audio")
                    void generateAiAudioClip(selectedClip.id);
                  else void generateAiClip(selectedClip.id);
                }}
              />
            </div>
            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "video")}
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => handleFileUpload(e, "audio")}
            />
            <input
              ref={agentUploadInputRef}
              type="file"
              multiple
              accept={agentUploadRequest?.accept || "image/*"}
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) handleAgentUploadFiles(files);
                e.target.value = "";
              }}
            />

            {/* ── Topbar del teléfono ──────────────────────────────────────
            La fila de arriba de cualquier editor de video de teléfono, y la
            misma que muestran CapCut, TikTok y Canva: salir a la izquierda, el
            nombre del proyecto en el medio y la acción principal —Exportar— en
            una pastilla a la derecha. Deshacer/rehacer entran acá y no en la
            fila del transporte porque son acciones "de documento", no de
            reproducción.

            `paddingTop: env(safe-area-inset-top)` para que en un teléfono con
            notch el topbar arranque debajo de la status bar y no atrás de ella. */}
            {enElTelefono && (
              <div
                className="relative flex items-center gap-1 h-[52px] px-2 border-b border-black/[0.06] bg-surface-card shrink-0"
                style={{
                  paddingTop: "env(safe-area-inset-top, 0px)",
                  height: "calc(52px + env(safe-area-inset-top, 0px))",
                }}
              >
                {/* Los dos costados miden LO MISMO (82px, el ancho de la
                pastilla de Exportar) y el nombre se queda con el `flex-1` del
                medio: así cae en el centro exacto de la pantalla. Con la X de
                36px a un lado y la pastilla de 92 al otro, el centro del hueco
                quedaba 28px corrido a la izquierda. Mismo truco que la fila del
                transporte de la timeline (tres columnas, el play en la del
                medio) y que el título del topbar de escritorio, que se centra
                en absoluto. */}
                <div className="w-[82px] shrink-0 flex justify-start">
                  <button
                    type="button"
                    onClick={() => navigate("/editor-video")}
                    className="w-9 h-9 flex items-center justify-center rounded-md text-brand-black/70 active:bg-neutral-100 transition-colors"
                    title="Ver todos los proyectos"
                    aria-label="Ver todos los proyectos"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {/* El nombre: TEXTO hasta que se lo toca, y ahí sí un input.
                No es un detalle de estilo — abajo de 640px hay una regla global
                que fuerza `font-size: 16px !important` en todo `input` (es el
                anti-zoom de iOS: Safari hace zoom al enfocar un campo de menos
                de 16px). O sea que como input, el título se dibujaba a 16px
                —más grande que "Exportar", que es 13— y ninguna clase de
                Tailwind lo bajaba. Como texto se dibuja a los 13px del diseño,
                y al tocarlo el input entra a 16px, que es justo el tamaño con
                el que iOS NO hace zoom. */}
                <div className="flex-1 min-w-0 flex justify-center">
                  {editandoNombreEnElTelefono ? (
                    <input
                      type="text"
                      autoFocus
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      onBlur={() => setEditandoNombreEnElTelefono(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          (e.target as HTMLInputElement).blur();
                      }}
                      className="w-full min-w-0 text-center font-medium text-brand-black/90 bg-white border-none outline-none rounded px-2 py-1 ring-1 ring-brand-primary/30"
                      spellCheck={false}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditandoNombreEnElTelefono(true)}
                      className="max-w-full truncate rounded px-2 py-1 text-[13px] font-medium text-brand-black/90 active:bg-neutral-100 transition-colors"
                      title="Renombrar el proyecto"
                    >
                      {projectName || "Proyecto sin título"}
                    </button>
                  )}
                </div>
                {/* Deshacer/Rehacer NO están acá: viven en la fila del
                transporte, debajo del preview y a la derecha, que es donde la
                mano llega sin cruzar la pantalla y donde las ponen las apps de
                edición del teléfono.
                Ver ADR-2026-09-17-el-transporte-del-telefono-se-reparte. */}
                <div className="w-[82px] shrink-0 flex justify-end">
                  <button
                    type="button"
                    data-tour="export"
                    onClick={() => setExportMenuOpen(true)}
                    className="h-8 px-3 flex items-center gap-1.5 rounded-md bg-brand-primary text-white text-[12px] font-semibold shadow-sm"
                  >
                    Exportar
                  </button>
                </div>
              </div>
            )}

            {!enElTelefono && (
              /* ── Topbar del editor — cruza TODA la ventana, por encima del
              rail y del área central, para que el logo quede pegado al borde
              izquierdo de la pantalla y no quede una franja vacía a su izquierda. */
              <div className="relative flex items-center justify-between h-[52px] pl-0 pr-4 border-b border-black/[0.06] bg-surface-card shrink-0">
              <div className="flex items-center gap-1">
                {/* El logo de Atlas abre el inicio de la app: es el "salir
                del editor" de toda la vida, en el lugar donde el usuario ya
                lo busca (arriba a la izquierda).

                Va dentro de una caja del ANCHO DEL RAIL (76px) y centrado en
                ella, para que caiga en el mismo eje vertical que las baldosas
                de Generar con IA / Archivos / Texto / Marca, que arrancan justo
                debajo. Si el rail cambia de ancho, este número lo sigue. */}
                <div className="w-[76px] shrink-0 flex justify-center">
                  <button
                    type="button"
                    onClick={() => navigate("/interfaz-v2")}
                    className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-neutral-100 transition-colors"
                    title="Ir al inicio de Atlas"
                    aria-label="Ir al inicio de Atlas"
                  >
                    <LogoDeMarca
                      variante="solo"
                      alt=""
                      className="w-[18px] h-[18px]"
                    />
                  </button>
                </div>
                {vueltaARedes && (
                  <button
                    type="button"
                    // `verCreaciones`: vuelve a las creaciones aunque haga un rato que no se
                    // genera (ADR-2026-10-08-redes-abre-colapsado-tras-un-rato).
                    onClick={() => navigate(vueltaARedes, { state: { verCreaciones: true } })}
                    className="h-8 px-3 flex items-center gap-1.5 rounded-md text-[13px] font-medium text-brand-black/60 hover:bg-neutral-100 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Volver a creaciones
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/editor-video")}
                  className="h-8 px-3 flex items-center gap-1.5 rounded-md text-[13px] font-medium text-brand-black/60 hover:bg-neutral-100 transition-colors"
                >
                  <FolderOpen className="w-4 h-4" />
                  Ver todos los proyectos
                </button>
                <div className="w-px h-5 bg-neutral-200 mx-1" />
                <button
                  type="button"
                  onClick={undo}
                  disabled={!canUndo}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/40 hover:bg-neutral-100 hover:text-brand-black/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand-black/40"
                  title="Deshacer (⌘Z)"
                >
                  <Undo2 className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={redo}
                  disabled={!canRedo}
                  className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/40 hover:bg-neutral-100 hover:text-brand-black/70 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-brand-black/40"
                  title="Rehacer (⌘⇧Z)"
                >
                  <Redo2 className="w-4 h-4" />
                </button>
              </div>
              {/* Nombre del proyecto — centrado en el header, no dentro del
              grupo de la izquierda: así queda en el medio de la pantalla
              sin importar cuánto ocupen deshacer/rehacer o los botones de
              la derecha. El ancho lo sigue dando el `size` del input (crece
              con el texto), topeado al 40% para no pisar los botones. */}
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    (e.target as HTMLInputElement).blur();
                }}
                size={Math.max(10, Math.min(projectName.length + 1, 60))}
                className="absolute left-1/2 -translate-x-1/2 text-center text-[13px] font-medium text-brand-black/90 bg-transparent border-none outline-none rounded px-2 min-w-[80px] max-w-[40%] hover:bg-neutral-100 focus:bg-white focus:ring-1 focus:ring-brand-primary/30 transition-colors"
                spellCheck={false}
              />
              <div className="flex items-center gap-2.5">
                {/* El saldo a la vista: el Estudio gasta créditos (escenas,
                voces, música, ediciones) y no se veía en ningún lado de esta
                pantalla (feedback de Nacho; ADR-2026-11-03-el-saldo-se-ve-en-el-estudio).
                Se refresca solo cuando se gastan o cargan créditos. */}
                {saldoDelEstudio && (
                  <CreditBadge
                    credits={saldoDelEstudio.disponibles}
                    tone="subtle"
                    title="Créditos disponibles"
                  />
                )}
                <button
                  type="button"
                  data-tour="export"
                  onClick={() => setExportMenuOpen(true)}
                  className="py-1.5 px-3.5 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[12.5px] font-medium shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Exportar
                </button>
                {/* "Preguntále a Atlas" — a la derecha de Exportar, dentro del
                header. Desde que el Director ES el agente del chat
                (ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente) el
                botón está SIEMPRE: el chat lo dibuja el cascarón y empuja el
                contenido desde afuera, así que no hay un estado "abierto" en el
                que este botón sobre. Ésta es la ÚNICA entrada al chat en esta
                pantalla: el flotante de la esquina no se dibuja acá
                (ADR-2026-09-22-el-estudio-y-el-lienzo-llaman-al-agente-por-su-nombre). */}
                <div data-tour="director" className="shrink-0">
                  <DirectorButton onClick={abrirDirector} />
                </div>
              </div>
              </div>
            )}

            {/* ── Main content: Left panel + center ──────────────────────────
            El chat de Atlas (que reemplazó al drawer del Director) vive en el
            shell y EMPUJA el contenido desde afuera, así que acá no se reserva
            ningún ancho. */}
            <div
              /* En el teléfono esta misma fila es una COLUMNA: arriba el
                 preview con la timeline (`flex-1`) y abajo la barra de
                 herramientas, que es el rail puesto de costado (`order-last`).
                 El panel de herramientas y el picker de voz dejan el flujo y
                 pasan a ser hojas `fixed` por encima, así que la columna sólo
                 tiene esos dos hijos. */
              className={`relative flex flex-1 min-h-0 overflow-hidden ${
                enElTelefono ? "flex-col" : ""
              }`}
            >
              {/* ── Left quick-access rail ─────────────────────────────────────
              Transparent vertical strip of icon tiles for the most common
              actions. Each tile is a small white rounded container with the
              icon inside and the label below, matching the look of the
              root-panel cards but compressed so the rail can stay narrow. */}
              {(() => {
                type RailItem = {
                  key: string;
                  label: string;
                  Icon: React.ComponentType<{
                    className?: string;
                    style?: React.CSSProperties;
                  }>;
                  active: boolean;
                  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
                };
                const railItems: RailItem[] = [
                  {
                    // Video y audio con IA son DOS baldosas, no un menú.
                    // Antes "Generar con IA" abría un dropdown de dos opciones
                    // anclado al costado del rail: dos clicks y una capa
                    // flotante para llegar a lo que el rail ya podía mostrar
                    // como dos entradas.
                    key: "ai-video",
                    label: "Video con IA",
                    Icon: Film,
                    active: activePanel === "ai-scene",
                    onClick: () => {
                      setEditingClipId(null);
                      setActivePanel((p) =>
                        p === "ai-scene" ? "none" : "ai-scene",
                      );
                    },
                  },
                  {
                    key: "ai-audio",
                    label: "Audio con IA",
                    Icon: Music,
                    active: activePanel === "ai-audio",
                    onClick: () => {
                      setEditingClipId(null);
                      setActivePanel((p) =>
                        p === "ai-audio" ? "none" : "ai-audio",
                      );
                    },
                  },
                  {
                    // Unified library entry. Replaces the old Videos + Audio
                    // tiles — the panel itself surfaces the type-tabs so the
                    // user keeps direct access while we save vertical room
                    // on the rail and don't visually pre-bias the content.
                    key: "files",
                    label: "Archivos",
                    Icon: activePanel === "files" ? FolderOpen : Folder,
                    active: activePanel === "files",
                    onClick: () =>
                      setActivePanel((p) => (p === "files" ? "none" : "files")),
                  },
                  {
                    key: "text",
                    label: "Texto",
                    Icon: Type,
                    active: activePanel === "text",
                    onClick: () =>
                      setActivePanel((p) => (p === "text" ? "none" : "text")),
                  },
                  {
                    // Animación gráfica: un prompt y el generador escribe la
                    // pieza (placa, título, intro). Misma capacidad que la
                    // tool `crear_animacion` del agente, sin pasar por el chat.
                    key: "animacion-grafica",
                    label: "Animación gráfica",
                    Icon: Sparkles,
                    active: activePanel === "animacion-grafica",
                    onClick: () => {
                      setEditingClipId(null);
                      setActivePanel((p) =>
                        p === "animacion-grafica" ? "none" : "animacion-grafica",
                      );
                    },
                  },
                  {
                    // Brand panel: edits the brand_profile attached to this
                    // project (memoria, fuentes favoritas y colores). Mirror
                    // surface for what the agent reads/writes via the
                    // update_brand_* tools.
                    key: "brand",
                    label: "Marca",
                    Icon: Palette,
                    active: activePanel === "brand",
                    onClick: () =>
                      setActivePanel((p) => (p === "brand" ? "none" : "brand")),
                  },
                ];
                // El rail es SIEMPRE una columna sólida: fondo blanco y una
                // línea gris a la derecha que lo separa del lienzo. Antes se
                // volvía transparente con todos los paneles cerrados —y con el
                // picker de "Cambiar voz" volvía a solidificarse para que su
                // blanco no se viera invadiendo el rail—, así que la barra de
                // herramientas aparecía y desaparecía según qué hubiera abierto.
                /* ── La misma barra, acostada: el pie del teléfono ─────────
                   En el teléfono el rail no es una columna al costado sino la
                   barra de abajo, que es donde vive la mano. Y tiene DOS
                   caras, como en CapCut, TikTok o Instagram Edits:

                   · sin clip tocado, las herramientas del proyecto (las mismas
                     baldosas del rail: Video con IA, Audio con IA, Archivos,
                     Texto, Marca) más el Director, que en escritorio vive en el
                     topbar y acá se quedaría sin puerta;
                   · con un clip tocado, las acciones DE ESE CLIP —las mismas
                     que en escritorio flotan al lado del cuadro— con "Ajustes"
                     adelante (es la que abre el panel con volumen, velocidad y
                     encuadre) y un "volver" que suelta la selección.

                   Scrollea en horizontal: no hay que elegir qué se cae de la
                   barra según el ancho del teléfono, y es el gesto que ya
                   traen todos los editores. Ver
                   ADR-2026-09-16-el-estudio-de-video-en-el-telefono. */
                if (enElTelefono) {
                  type ItemDeBarra = {
                    key: string;
                    label: string;
                    icono: React.ReactNode;
                    active?: boolean;
                    disabled?: boolean;
                    onClick: () => void;
                  };
                  const clipTocado = selectedClip;
                  // Las baldosas del rail, por clave: la barra del teléfono
                  // reusa SUS handlers (abrir/cerrar el panel, el estado
                  // activo) en vez de reescribirlos, que es como se
                  // desincronizan.
                  const delRail = (clave: string) =>
                    railItems.find((r) => r.key === clave)!;
                  const items: ItemDeBarra[] = clipTocado
                    ? [
                        {
                          key: "ajustes",
                          // "Avanzado": es el panel con volumen, velocidad,
                          // encuadre y opacidad del clip. "Ajustes" competía
                          // con el resto de la barra, que también son ajustes
                          // del clip (dividir, restaurar, duplicar).
                          label: "Avanzado",
                          // 16px, el MISMO tamaño que los íconos de las otras
                          // acciones del clip (vienen de
                          // `accionesDelClipSeleccionado` con `w-4 h-4`). A
                          // 21px —el tamaño de la barra de herramientas del
                          // proyecto— era el único grande de la fila.
                          icono: <SlidersHorizontal className="w-4 h-4" />,
                          active:
                            activePanel === "clip" ||
                            activePanel === "text" ||
                            activePanel === "animation",
                          onClick: () =>
                            setActivePanel(
                              clipTocado.type === "text"
                                ? "text"
                                : clipTocado.type === "animation"
                                  ? "animation"
                                  : "clip",
                            ),
                        },
                        ...accionesDelClipSeleccionado.map((a) => ({
                          key: a.label,
                          label: a.label,
                          icono: a.icon,
                          disabled: a.disabled,
                          onClick: a.onClick,
                        })),
                      ]
                    : /* Sin clip tocado: CUATRO lugares fijos, repartidos a
                         lo ancho. Antes eran seis baldosas de 66px que había
                         que scrollear en horizontal para llegar a Marca y al
                         Director; las tres que CREAN algo —Video con IA, Audio
                         con IA y Texto— se fueron al sheet de "Agregar", que es
                         el mismo gesto del botón Generar de la home. */
                      [
                        {
                          key: "agregar",
                          label: "Agregar",
                          icono: <Plus className="w-[21px] h-[21px]" />,
                          // Queda encendido mientras uno de los tres paneles
                          // que abre esté abierto: es la puerta por la que se
                          // entró, y sin eso la barra no dice dónde estás.
                          active:
                            activePanel === "ai-scene" ||
                            activePanel === "ai-audio" ||
                            activePanel === "text",
                          onClick: abrirSheetAgregar,
                        },
                        ...(["files", "brand"] as const).map((clave) => {
                          const r = delRail(clave);
                          return {
                            key: r.key,
                            label: r.label,
                            icono: <r.Icon className="w-[21px] h-[21px]" />,
                            active: r.active,
                            onClick: () =>
                              r.onClick(
                                {} as React.MouseEvent<HTMLButtonElement>,
                              ),
                          };
                        }),
                        // El chat, último: es el que NO abre un panel del
                        // editor sino el chat, y en escritorio vive aparte (en
                        // el topbar). Va en la punta, como el "perfil" de
                        // cualquier tab bar.
                        //
                        // Dice "Atlas" y no "Preguntále a Atlas" porque en una
                        // tab bar la etiqueta entra en una línea corta; es la
                        // misma acción y el mismo ícono que el botón de la
                        // barra en escritorio. Decía "Director", que era el
                        // nombre del agente que ya no existe, así que la misma
                        // puerta se llamaba distinto según el tamaño de la
                        // pantalla. Ver
                        // ADR-2026-09-22-el-estudio-y-el-lienzo-llaman-al-agente-por-su-nombre.
                        {
                          key: "director",
                          label: "Atlas",
                          icono: <Sparkles className="w-[21px] h-[21px]" />,
                          onClick: abrirDirector,
                        },
                      ];
                  // Con el video ampliado (el chevron del cuadro) la barra se
                  // va con la timeline: el pedido es que quede el video y nada
                  // más que el play y las flechas de deshacer. Vuelve sola al
                  // tocar el chevron de nuevo.
                  // Ver ADR-2026-09-17-el-chevron-agranda-el-video-en-el-telefono.
                  //
                  // Se va ANIMÁNDOSE, no con un `return null`: desmontarla la
                  // hacía desaparecer de un frame para el otro mientras la
                  // timeline bajaba con su transición de 260ms, y el video
                  // pegaba el salto de ese pedazo de alto justo al final. Se
                  // colapsa con la MISMA duración y curva que el alto de la
                  // timeline y que el giro del chevron
                  // (`DURACION_DEL_COLAPSO`), así las tres cosas se mueven como
                  // una. `max-height` y no `height` porque el alto real depende
                  // de cuántas acciones haya y del safe-area del teléfono; el
                  // techo sólo tiene que ser mayor que el más alto posible.
                  // El padding del safe-area también se va: con `max-h-0` y
                  // `overflow-hidden` seguiría aportando alto.
                  // Ver ADR-2026-09-17-la-barra-del-telefono-se-va-animada.
                  return (
                    <div
                      data-clip-ui="true"
                      aria-hidden={previewAmpliado}
                      className={`order-last shrink-0 w-full bg-surface-card border-t border-black/[0.06] overflow-hidden transition-[max-height,opacity] duration-[260ms] ease-out ${
                        previewAmpliado
                          ? "max-h-0 opacity-0 pointer-events-none"
                          : "max-h-[140px] opacity-100"
                      }`}
                      style={{
                        paddingBottom: previewAmpliado
                          ? 0
                          : "env(safe-area-inset-bottom, 0px)",
                      }}
                    >
                      <div
                        className={`flex items-stretch gap-0.5 px-1.5 py-1.5 ${
                          clipTocado
                            ? "overflow-x-auto scrollbar-hide"
                            : ""
                        }`}
                      >
                        {/* Volver: suelta el clip y devuelve la barra a las
                        herramientas del proyecto. Va pegado al borde, sin
                        línea divisoria: la tenía para decir "no es una
                        herramienta más" y en una barra de 5 ítems sólo sumaba
                        un trazo en el medio. Que no es una herramienta ya lo
                        dicen su forma (chevron sin etiqueta) y su lugar. */}
                        {clipTocado && (
                          <>
                            <button
                              type="button"
                              onClick={() => setSelectedClipId(null)}
                              className="shrink-0 w-10 flex items-center justify-center rounded-md text-brand-black/50 active:bg-neutral-100 transition-colors"
                              aria-label="Soltar el clip"
                            >
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {items.map((item) => (
                          <button
                            key={item.key}
                            type="button"
                            onClick={item.onClick}
                            disabled={item.disabled}
                            /* `h-[52px]`: las DOS caras de la barra —la del
                               proyecto y la del clip— miden lo mismo, así la
                               barra no salta de alto al tocar un clip. Con el
                               alto suelto lo decidían el ícono y la etiqueta, y
                               los íconos venían de dos lados distintos. */
                            className={`h-[52px] flex flex-col items-center justify-center gap-1 rounded-md active:bg-neutral-100 transition-colors disabled:opacity-35 ${
                              clipTocado
                                ? "shrink-0 w-[66px]"
                                : /* Cuatro lugares iguales, a lo ancho: es una
                                     tab bar, no una tira que se scrollea. */
                                  "flex-1 min-w-0"
                            }`}
                            style={{
                              color: item.active ? "var(--brand-primary)" : undefined,
                            }}
                          >
                            {/* El tamaño del ícono se fija ACÁ y no en cada
                            item: los de la cara del proyecto se escriben a
                            21px y los del clip vienen de
                            `accionesDelClipSeleccionado` con `w-4 h-4` (16px),
                            que es el tamaño que necesitan en la lista de
                            Herramientas IA. El selector de descendencia
                            (`[&_svg]`) le gana a esas clases, así que las dos
                            caras dibujan el mismo ícono. */}
                            <span
                              className={`[&_svg]:w-[21px] [&_svg]:h-[21px] ${
                                item.active ? "" : "text-brand-black/80"
                              }`}
                            >
                              {item.icono}
                            </span>
                            <span
                              className={`text-[10.5px] leading-tight text-center truncate max-w-full ${
                                item.active
                                  ? "font-semibold"
                                  : "text-brand-black/70"
                              }`}
                            >
                              {item.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="shrink-0 w-[76px] flex flex-col items-stretch bg-surface-card border-r border-black/[0.06]">
                    <div className="flex flex-col items-stretch gap-1.5 pt-2 pb-3 px-2">
                      {railItems.map((item) => {
                        const { Icon, active } = item;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            data-tour={
                              item.key === "ai-video"
                                ? "generate"
                                : item.key === "files"
                                  ? "files"
                                  : item.key === "brand"
                                    ? "brand"
                                    : undefined
                            }
                            onClick={item.onClick}
                            className="group w-full flex flex-col items-center gap-2 rounded-md py-1.5 transition-colors"
                            title={item.label}
                          >
                            {/* En reposo la baldosa es un relleno gris muy claro
                            y nada más: sin sombra y sin borde, el contorno lo da
                            el propio relleno contra el blanco del rail, y el
                            icono va en negro. Activa pasa a azul de marca
                            clarito con la etiqueta en azul pleno — el azul es el
                            único color del rail y por eso marca "acá estás".

                            Los azules van como rgba literal y no como
                            `bg-brand-primary/10`: los tokens `brand-*` son
                            `var(--brand-primary)` sin `<alpha-value>`, así que
                            el modificador de opacidad no emite ninguna regla. */}
                            <div
                              className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                                active
                                  ? ""
                                  : "bg-black/[0.04] group-hover:bg-black/[0.07]"
                              }`}
                              style={{
                                backgroundColor: active
                                  ? "rgb(var(--brand-primary-rgb) / 0.12)"
                                  : undefined,
                              }}
                            >
                              {/* En reposo el icono es negro; activo toma el
                              MISMO azul que la etiqueta, así la baldosa entera
                              (relleno, icono y texto) dice lo mismo. */}
                              <Icon
                                className={`w-[18px] h-[18px] ${active ? "" : "text-brand-black"}`}
                                style={{ color: active ? "var(--brand-primary)" : undefined }}
                              />
                            </div>
                            <span
                              className={`text-[11px] leading-tight font-semibold text-center transition-colors ${
                                active ? "" : "text-brand-black/70"
                              }`}
                              style={{ color: active ? "var(--brand-primary)" : undefined }}
                            >
                              {item.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {/* Video / Imagen dropdown for the Generar con IA rail tile */}
                  </div>
                );
              })()}

              {/* ── Left Tools Panel (visible only when a tool is active) ──────
              The whole panel is tagged `data-clip-ui` so the global
              "click outside any clip = deselect" pointerdown listener (see
              the effect that calls `setSelectedClipId(null)`) ignores every
              interaction inside it. Without this, opening a native color
              picker or clicking on hex inputs while editing an animation
              clip would deselect the clip and the editor would snap back to
              the catalog mid-edit. */}
              {/* El velo de la hoja, sólo en el teléfono: tocar afuera cierra
              el panel, que es como se cierra cualquier hoja del sistema. En
              escritorio no existe — ahí el panel EMPUJA al lienzo en vez de
              taparlo.

              Oscurece con `bg-black/40` —el MISMO de todas las demás capas
              que se abren encima del editor en el teléfono: "+ Agregar", el
              selector de imagen de un frame y el diálogo de exportar—, así una
              hoja se siente igual venga de donde venga. Es negro plano y no un
              token `brand-*` porque esos no llevan `<alpha-value>`: con el
              modificador de opacidad Tailwind no emite regla y el velo queda
              transparente (era el bug del `bg-brand-black/20` de antes, que
              además asomaba por las esquinas redondeadas y se leía como un
              borde gris sucio).
              Ver ADR-2026-09-17-la-hoja-se-separa-con-sombra-y-no-con-velo. */}
              {enElTelefono && activePanel !== "none" && (
                <div
                  /* z-[44] y no z-30: el marco de selección del clip
                     (`SelectionFrame`) se dibuja en z-40 dentro de ESTE mismo
                     contexto de apilado, así que con z-30 sus manijas quedaban
                     encima del velo. */
                  className="fixed inset-0 z-[44] bg-black/40 animate-in fade-in duration-300"
                  onClick={() => setActivePanel("none")}
                  aria-hidden="true"
                />
              )}
              <div
                ref={hojaDeHerramientasRef}
                data-clip-ui="true"
                /* La MISMA caja, en dos formatos. En escritorio es una columna
                   que se abre y se cierra estirando su ancho. En el teléfono es
                   una hoja que sube desde abajo: alto fijo, esquinas de arriba
                   redondeadas y sombra propia, por encima de la barra de
                   herramientas (que es justo desde donde se la llamó). Cerrada
                   queda abajo de todo y sin capturar toques, así la transición
                   es la misma bajada en los dos sentidos. */
                className={
                  enElTelefono
                    ? /* SIN `overflow-hidden` y con la sombra acá: el recorte
                         del padre se comía la sombra del hijo, así que la hoja
                         quedaba apoyada sobre el fondo sin nada que la
                         separara. El recorte del contenido lo hace el div de
                         adentro, que es el que tiene las esquinas. */
                      /* `flex flex-col`: con la hoja midiendo por su
                         contenido (Audio con IA), el hijo tiene que poder
                         ENCOGERSE cuando el contenido pasa del `maxHeight`. Con
                         el hijo en `h-full` —porcentaje contra un alto
                         `auto`— se resolvía a su contenido y se desbordaba por
                         arriba de la hoja. */
                      /* z-[45] y no z-40: el marco de selección del clip
                         también está en z-40 y, al EMPATAR, ganaba por orden
                         del DOM (el preview se renderiza después que el panel).
                         Con un clip seleccionado, las manijas del recuadro se
                         veían flotando encima de la hoja.
                         Ver ADR-2026-09-17-la-hoja-tapa-el-marco-de-seleccion. */
                      "fixed inset-x-0 bottom-0 z-[45] flex flex-col rounded-t-2xl shadow-[0_-4px_20px_rgb(var(--brand-black-rgb)_/_0.12)]"
                    : "relative z-20 shrink-0 overflow-hidden transition-[width] duration-250 ease-out"
                }
                style={
                  enElTelefono
                    ? {
                        /* Alto de la hoja: lo pone el CONTENIDO, con 92dvh
                           de techo (ver `hojaMidePorContenido`). Antes el
                           resto de los paneles estaba clavado en 72vh y las
                           opciones de abajo de "Editar video" quedaban
                           cortadas incluso con pantalla de sobra.

                           Los dos formularios largos de IA —Video con IA e
                           Imagen con IA— se plantan en el techo: son
                           formularios de varias caras (referencias, prompt,
                           duración, calidad, Generar) y no tienen que cambiar
                           de alto mientras se navega entre ellas.

                           `dvh` y no `vh`: en el teléfono la barra del
                           navegador aparece y desaparece, y `vh` mide siempre
                           el caso grande (la hoja se iba abajo de la
                           pantalla). */
                        height: hojaMidePorContenido
                          ? "auto"
                          : "min(92dvh, 900px)",
                        maxHeight: "min(92dvh, 900px)",
                        /* Cerrada se va 100% + 32px: con 101% justo, la sombra
                           —que sube 20px— seguía asomando por el borde de
                           abajo de la pantalla. */
                        transform:
                          activePanel !== "none"
                            ? "translateY(0)"
                            : "translateY(calc(100% + 32px))",
                        transition: TRANSICION_HOJA,
                        pointerEvents: activePanel !== "none" ? "auto" : "none",
                      }
                    : {
                        width:
                          activePanel !== "none" ? ANCHO_PANEL_HERRAMIENTAS : 0,
                      }
                }
              >
                <div
                  className={
                    enElTelefono
                      ? /* `flex-auto min-h-0` en vez de `h-full`: crece
                           hasta llenar la hoja cuando ésta tiene alto fijo, y
                           encoge (y deja scrollear adentro) cuando la hoja mide
                           por contenido y ese contenido pasa del tope. */
                        "flex flex-col flex-auto min-h-0 bg-surface-card rounded-t-2xl overflow-hidden"
                      : "flex flex-col h-full border-r border-black/[0.06] bg-surface-card"
                  }
                  style={
                    enElTelefono
                      ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
                      : {
                          minWidth: ANCHO_PANEL_HERRAMIENTAS,
                          width: ANCHO_PANEL_HERRAMIENTAS,
                        }
                  }
                >
                  {/* La manija de la hoja. Va ANTES de cualquier cabecera —
                  también de las de los tres paneles de IA, que traen la suya—
                  para que el gesto de bajarla esté siempre en el mismo lugar.
                  `touchAction: none` porque si no el navegador se queda el
                  arrastre vertical para scrollear el cuerpo del panel. */}
                  {enElTelefono && (
                    <div
                      onPointerDown={empezarAArrastrarLaHoja}
                      className="shrink-0 pt-2.5 pb-1.5 flex justify-center"
                      style={{ touchAction: "none", cursor: "grab" }}
                    >
                      <span className="w-9 h-1 rounded-full bg-black/[0.18]" />
                    </div>
                  )}
                  {activePanel !== "ai-scene" &&
                    activePanel !== "ai-image-gen" &&
                    activePanel !== "ai-audio" && (
                      <>
                        {/* Panel header — mismo alto que el header del chat (h-[52px]) */}
                        <div className="flex items-center justify-between h-[52px] px-3 shrink-0">
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            {/* Volver — sólo en las sub-pantallas. Va a la
                            izquierda del título porque es a lo que se refiere:
                            "atrás desde acá". La X de la derecha sigue
                            cerrando el panel entero, que es otra cosa. */}
                            {activePanel in PANEL_DEL_QUE_VIENE && (
                              <button
                                type="button"
                                onClick={() =>
                                  setActivePanel(
                                    PANEL_DEL_QUE_VIENE[
                                      activePanel as keyof typeof PANEL_DEL_QUE_VIENE
                                    ],
                                  )
                                }
                                className="w-7 h-7 -ml-1 inline-flex items-center justify-center rounded-full text-brand-black/50 hover:bg-black/[0.06] hover:text-brand-black/80 transition-colors shrink-0"
                                title="Volver"
                                aria-label="Volver"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                            )}
                            <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                              {activePanel === "brand"
                                ? "Marca"
                                : activePanel === "ai-generate"
                                  ? "Generar con IA"
                                  : activePanel === "upload"
                                    ? "Subir archivo"
                                    : activePanel === "audio"
                                      ? "Audio"
                                      : activePanel === "files"
                                        ? "Archivos"
                                        : activePanel === "text"
                                          ? "Texto"
                                          : activePanel === "animacion-grafica"
                                            ? "Animación gráfica"
                                          : activePanel === "effects"
                                            ? "Efectos"
                                            : activePanel === "transitions"
                                              ? "Transiciones"
                                              : activePanel === "animation"
                                                ? selectedClip?.animationCodigo
                                                  ? "Animación gráfica"
                                                  : "Elementos"
                                                : activePanel === "agent-upload"
                                                  ? "Subir archivos"
                                                  : activePanel ===
                                                      "edit-video-ia"
                                                    ? "Editar clip"
                                                    : activePanel ===
                                                        "upscale-video"
                                                      ? "Mejorar calidad"
                                                    : activePanel ===
                                                        "silencios"
                                                      ? "Eliminar silencios"
                                                    : activePanel === "voz"
                                                      ? "Cambiar voz"
                                                    : activePanel ===
                                                        "corregir-voz"
                                                      ? "Corregir voz"
                                                    : activePanel === "clip"
                                                      ? tituloDelPanelDeClip
                                                      : activePanel ===
                                                          "sin-seleccion"
                                                        ? "Selección"
                                                        : ""}
                            </h3>
                          </div>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors shrink-0"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />
                      </>
                    )}

                  <div
                    ref={cuerpoDelPanelRef}
                    /* `flex-auto` (basis auto) y no `flex-1` (basis 0)
                       cuando la hoja mide por su contenido: un ítem flex con
                       basis 0 y `overflow` distinto de `visible` tiene mínimo
                       automático CERO, así que bajo un padre de alto
                       automático aporta 0 y la hoja se colapsaría al alto de
                       la manija y la cabecera. Con basis auto aporta su
                       contenido, y si pasa del tope sigue encogiéndose y
                       scrolleando como siempre. */
                    className={`${
                      hojaMidePorContenido ? "flex-auto" : "flex-1"
                    } overflow-y-auto min-h-0 ${activePanel === "ai-scene" || activePanel === "ai-image-gen" || activePanel === "ai-audio" || activePanel === "text" || activePanel === "transitions" || activePanel === "edit-video-ia" ? "" : "p-3"}`}
                  >
                    {/* ── Estado vacío: el panel del clip sin clip ─────────
                    Mismo panel, sin nada tocado. No se cierra a propósito:
                    ver ADR-2026-09-15-el-panel-del-clip-no-se-cierra-al-deseleccionar. */}
                    {activePanel === "sin-seleccion" && (
                      <div className="h-full flex flex-col items-center justify-center text-center px-6 -mt-6">
                        <div className="w-14 h-14 rounded-2xl bg-black/[0.04] flex items-center justify-center">
                          <MousePointerClick className="w-6 h-6 text-brand-black/30" />
                        </div>
                        <p className="mt-4 text-[17px] font-semibold text-brand-black/90">
                          No hay nada seleccionado
                        </p>
                        <p className="mt-1.5 text-[13px] leading-snug text-[#3C3C43]/60">
                          Tocá un clip de la línea de tiempo para editarlo, o
                          agregá contenido nuevo desde la barra de la izquierda.
                        </p>
                      </div>
                    )}

                    {/* ── Panel del clip seleccionado ──────────────────────
                    Todo lo que se le puede hacer al clip que está tocado en
                    la timeline. Antes esto vivía en una barra flotante sobre
                    el preview que, al no entrar, escondía las etiquetas de
                    los botones y metía lo demás en dos dropdowns ("IA" y
                    "Más"). Las acciones son las MISMAS y llaman a los mismos
                    handlers: lo que cambia es dónde y cómo se muestran.
                    Ver ADR-2026-09-15-panel-del-clip-a-la-izquierda. */}
                    {activePanel === "clip" &&
                      selectedClip &&
                      (() => {
                        const clip = selectedClip;
                        const iaLista =
                          clip.type === "ai-generated" &&
                          clip.aiStatus === "ready";
                        const videoOrReady = clip.type === "video" || iaLista;
                        // Un clip de audio siempre suena. Una imagen nunca.
                        // Un video depende del archivo: se esconde el control
                        // sólo cuando la detección dijo que NO hay pista; si
                        // todavía no se sabe, se muestra.
                        const conAudio =
                          clip.type === "audio" ||
                          ((clip.type === "video" ||
                            clip.type === "ai-generated") &&
                            tienenAudio[clip.id] !== false);
                        const vol = clip.volume ?? 1;
                        const ponerVolumen = (v: number) =>
                          setClips((prev) =>
                            prev.map((c) =>
                              c.id === clip.id ? { ...c, volume: v } : c,
                            ),
                          );
                        // La opacidad se guarda 0..1 y se muestra 0..100, igual
                        // que el volumen. Es una capa visual, así que aplica a
                        // video e imagen, no a audio.
                        const ponerOpacidad = (v: number) =>
                          setClips((prev) =>
                            prev.map((c) =>
                              c.id === clip.id
                                ? { ...c, opacity: Math.max(0, Math.min(1, v)) }
                                : c,
                            ),
                          );
                        const esCapaVisual =
                          clip.type === "video" ||
                          clip.type === "ai-generated" ||
                          clip.type === "image";
                        const opacidad = clip.opacity ?? 1;

                        // ── Herramientas de IA ───────────────────────────
                        const ia: AccionDeClip[] = [];
                        // Quitar fondo: el producto (o logo, o persona) queda
                        // recortado con transparencia; el original sigue en
                        // Archivos (ADR-2026-09-27-quitar-fondo-en-el-estudio-birefnet).
                        if (clip.type === "image") {
                          const quitando = quitandoFondoDe === clip.id;
                          const sinSubir = !clip.storagePath;
                          ia.push({
                            label: "Quitar fondo",
                            desc: "Dejar sólo el producto, con fondo transparente",
                            icon: <Eraser className="w-4 h-4" />,
                            tono: "bg-sky-50 text-sky-600",
                            disabled: quitando || sinSubir,
                            note: quitando ? "Procesando…" : undefined,
                            title: sinSubir
                              ? "La imagen todavía no terminó de subir"
                              : "Recortar la imagen y dejar el fondo transparente (gratis)",
                            onClick: () => void quitarFondoDelClip(clip.id),
                          });
                        }
                        if (clip.type === "ai-generated")
                          ia.push({
                            label: "Editar",
                            desc: "Volver al generador de este clip",
                            icon: <Pencil className="w-4 h-4" />,
                            tono: "bg-violet-50 text-violet-600",
                            title: "Editar clip IA",
                            onClick: () => editAiClip(clip.id),
                          });
                        if (clip.type === "audio" && clip.aiPrompt)
                          ia.push({
                            label: "Editar",
                            desc: "Volver al generador de este audio",
                            icon: <Pencil className="w-4 h-4" />,
                            tono: "bg-violet-50 text-violet-600",
                            title: "Editar clip de audio IA",
                            onClick: () => editAiAudioClip(clip.id),
                          });
                        if (
                          (clip.type === "video" ||
                            clip.type === "ai-generated") &&
                          (clip.type !== "ai-generated" ||
                            clip.aiStatus === "ready")
                        ) {
                          // El tope va por el TRAMO que está en la timeline
                          // (en segundos de archivo): el worker corta ese
                          // pedazo y sólo eso va a Gemini. Ver
                          // ADR-2026-09-23-omni-edit-el-worker-recorta-la-ventana.
                          const tooLong = excedeElTopeDeOmni(clip);
                          const notReady = !clip.src;
                          ia.push({
                            label: "Editar clip",
                            desc: "Pedirle un cambio al clip con palabras",
                            icon: <Wand2 className="w-4 h-4" />,
                            tono: "bg-violet-50 text-violet-600",
                            disabled: tooLong || notReady,
                            note: tooLong ? "Máx. 10s" : undefined,
                            title: tooLong
                              ? "El clip es muy largo para editarlo (máx. 10s). Recortalo o dividilo."
                              : notReady
                                ? "El clip todavía no está listo"
                                : "Editar el clip con IA",
                            onClick: () => {
                              setOmniEditClipId(clip.id);
                              setActivePanel("edit-video-ia");
                            },
                          });
                        }

                        // Extender video — generar la continuación del clip.
                        // Estaba en la barra flotante del preview, entre
                        // dividir/duplicar/eliminar, como si fuera un gesto de
                        // edición; en realidad pide una escena nueva a la IA
                        // (y cuesta créditos), así que su lugar es esta lista.
                        if (videoOrReady) {
                          // Sin material no hay último frame que copiar: los
                          // clips IA lo tienen cacheado en `aiLastFrame`, los
                          // de video se capturan del `src`.
                          const sinFuente =
                            !clip.src &&
                            !(
                              clip.type === "ai-generated" && clip.aiLastFrame
                            );
                          ia.push({
                            label: "Extender video",
                            desc: "Seguir la escena desde el último frame",
                            // Fotogramas apilados con uno nuevo, más grande, al
                            // final: es literalmente lo que hace (el último
                            // frame del clip + la escena que se le agrega).
                            // `ChevronsRight` era un ">>" de avance rápido y
                            // `PanelLeftOpen` —el de antes— un panel de UI.
                            icon: <GalleryHorizontalEnd className="w-4 h-4" />,
                            tono: "bg-teal-50 text-teal-600",
                            disabled: sinFuente,
                            title: sinFuente
                              ? "El clip todavía no está listo"
                              : "Continuar este video con IA: toma su último frame, abre Video con IA con ese frame como inicio y genera la escena que sigue para sumarla a la timeline.",
                            onClick: () => extendVideoClip(clip.id),
                          });
                        }
                        if (
                          clip.type === "video" ||
                          clip.type === "ai-generated"
                        )
                          ia.push({
                            label: "Borrar objeto",
                            desc: "Pintar lo que sobra y sacarlo del video",
                            icon: <Eraser className="w-4 h-4" />,
                            tono: "bg-rose-50 text-rose-600",
                            title: "Borrado inteligente",
                            onClick: () => openEraseBrush(clip.id),
                          });
                        if (videoOrReady) {
                          const cost = creditsForVideoTranslate({
                            durationSec: clip.duration,
                          });
                          const translating = !!clip.aiTranslating;
                          const errMsg = clip.aiTranslateError;
                          ia.push({
                            label: translating
                              ? "Traduciendo…"
                              : "Traducir al inglés",
                            desc: "Doblar el clip con lip-sync",
                            icon: translating ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Languages className="w-4 h-4" />
                            ),
                            tono: "bg-blue-50 text-blue-600",
                            disabled: translating,
                            title: translating
                              ? "Traduciendo al inglés… (puede tardar varios minutos)"
                              : errMsg
                                ? `Error: ${errMsg} — clickeá para reintentar (cuesta ${cost} créditos)`
                                : `Doblar el clip al inglés con lip-sync. Cuesta ${cost} créditos.`,
                            onClick: () => setTranslateConfirmClipId(clip.id),
                          });
                        }
                        if (clipEsBorradorVigente(clip)) {
                          const cost = creditosDePasarA1080p(clip.aiBorrador!.segundos);
                          const pasando = clip.pasarA1080pStatus === "processing";
                          ia.push({
                            label: pasando ? "Pasando a 1080p…" : "Pasar a 1080p",
                            desc: "Misma toma en alta",
                            icon: pasando ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ArrowUpFromLine className="w-4 h-4" />
                            ),
                            tono: "bg-blue-50 text-blue-600",
                            disabled: pasando,
                            title: AYUDA_BORRADOR,
                            onClick: () => {
                              if (!assertCredits(cost)) return;
                              setPasarA1080pClipId(clip.id);
                            },
                          });
                        }
                        if (videoOrReady) {
                          const notReady = !clip.src;
                          ia.push({
                            label: "Mejorar calidad",
                            desc: "Más resolución y nitidez",
                            icon: <Sparkles className="w-4 h-4" />,
                            tono: "bg-amber-50 text-amber-600",
                            disabled: notReady,
                            title: notReady
                              ? "El clip todavía no está listo"
                              : "Aumentar la resolución y nitidez del clip con IA (Topaz).",
                            onClick: () => {
                              setUpscaleClipId(clip.id);
                              setActivePanel("upscale-video");
                            },
                          });
                        }
                        if (
                          clip.type === "video" ||
                          clip.type === "audio" ||
                          iaLista
                        ) {
                          const loading =
                            subtitlesLoading &&
                            subtitlesSourceClipId === clip.id;
                          ia.push({
                            label: "Subtítulos",
                            desc: "Transcribir y sumarlos a la timeline",
                            icon: loading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Captions className="w-4 h-4" />
                            ),
                            tono: "bg-emerald-50 text-emerald-600",
                            disabled: subtitlesLoading,
                            title:
                              "Transcribir este clip y agregar subtítulos al timeline",
                            onClick: () => openSubtitlesModalForClip(clip.id),
                          });
                        }
                        if (
                          clip.type === "audio" ||
                          clip.type === "video" ||
                          iaLista
                        ) {
                          const changing =
                            clip.voiceChangeStatus === "processing";
                          ia.push({
                            label: changing ? "Cambiando voz…" : "Cambiar voz",
                            desc: "Otra voz, mismo ritmo y entonación",
                            icon: changing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Mic className="w-4 h-4" />
                            ),
                            tono: "bg-indigo-50 text-indigo-600",
                            disabled: changing,
                            title:
                              "Cambiar la voz por una voz IA (mantiene el ritmo y la entonación)",
                            onClick: () => {
                              setChangeVoiceClipId(clip.id);
                              setActivePanel("voz");
                            },
                          });
                        }
                        // Corregir voz: cambiar una palabra mal dicha sin
                        // regenerar el clip. Lo mismo que el agente hace con
                        // corregir_voz_en_clip, a mano. Sin pista de audio no
                        // hay voz que corregir.
                        if (
                          conAudio &&
                          (clip.type === "audio" ||
                            clip.type === "video" ||
                            iaLista)
                        ) {
                          const corrigiendo =
                            clip.corregirVozStatus === "processing";
                          ia.push({
                            label: corrigiendo
                              ? "Corrigiendo voz…"
                              : "Corregir voz",
                            desc: "Arreglar una palabra mal dicha",
                            icon: corrigiendo ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <AudioLines className="w-4 h-4" />
                            ),
                            tono: "bg-fuchsia-50 text-fuchsia-600",
                            title:
                              "Tocar una palabra de la transcripción y volver a decirla con la misma voz. No consume créditos.",
                            onClick: () => {
                              setCorregirVozClipId(clip.id);
                              setActivePanel("corregir-voz");
                            },
                          });
                        }

                        // Las acciones sobre el clip (dividir, duplicar,
                        // eliminar…) se arman en `accionesDelClipSeleccionado`
                        // y se dibujan al costado del preview.

                        // Cortar silencios es buscar dónde NO se habla: sin
                        // pista de audio no hay nada que buscar.
                        const puedeCortarSilencios =
                          conAudio &&
                          (clip.type === "video" ||
                            clip.type === "audio" ||
                            iaLista);
                        const cortando = silenceCutBusy === clip.id;

                        // Eliminar silencios es una herramienta más de la
                        // lista de IA: antes tenía sección propia con dos
                        // campos siempre a la vista, que ocupaban lugar fijo
                        // para algo que se usa de a ratos. Los parámetros se
                        // fueron con ella a su sub-pantalla.
                        if (puedeCortarSilencios) {
                          ia.push({
                            label: "Eliminar silencios",
                            desc: "Cortar las pausas largas del audio",
                            icon: (
                              <ScissorsLineDashed className="w-4 h-4" />
                            ),
                            tono: "bg-sky-50 text-sky-600",
                            title:
                              "Detectar los silencios del clip y sacarlos, dejando un margen",
                            onClick: () => {
                              setSilenceClipId(clip.id);
                              setActivePanel("silencios");
                            },
                          });
                        }

                        return (
                          <div>
                            {/* El panel arranca directo en los controles. No
                            lleva cabecera con el archivo: el nombre es casi
                            siempre autogenerado ("Video generado.mp4") y la
                            duración ya se ve en la timeline, en el ancho del
                            clip y en el transporte. Cuál es el clip elegido lo
                            dicen la timeline —que lo tiene resaltado— y el
                            marco en el preview. */}

                            {conAudio && (
                              <SeccionDelPanel titulo="Reproducción">
                                <FilaDelPanel etiqueta="Volumen">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      ponerVolumen(vol === 0 ? 1 : 0)
                                    }
                                    className="w-9 h-9 shrink-0 rounded-md border border-black/[0.08] bg-surface-card flex items-center justify-center text-brand-black/70 hover:bg-black/[0.03] transition-colors"
                                    title={
                                      vol === 0 ? "Activar audio" : "Silenciar"
                                    }
                                  >
                                    {vol === 0 ? (
                                      <VolumeX className="w-4 h-4" />
                                    ) : (
                                      <Volume2 className="w-4 h-4" />
                                    )}
                                  </button>
                                  <div className="flex-1 min-w-0 relative flex items-center">
                                    <input
                                      type="range"
                                      min={0}
                                      max={2}
                                      step={0.01}
                                      value={vol}
                                      onChange={(e) =>
                                        ponerVolumen(
                                          parseFloat(e.target.value),
                                        )
                                      }
                                      className="w-full accent-brand-primary"
                                      aria-label="Volumen del clip"
                                    />
                                    {/* Marca del 100%: el slider llega hasta
                                    200% (amplificación), así que sin la marca
                                    no se sabe dónde está el volumen original. */}
                                    <span
                                      aria-hidden
                                      className="pointer-events-none absolute top-1/2 -translate-y-1/2 w-px h-2.5 bg-neutral-400/70"
                                      style={{ left: "50%" }}
                                    />
                                  </div>
                                  <div className="w-[68px] h-9 shrink-0 flex items-center rounded-md border border-black/[0.08] bg-surface-card">
                                    <NumberField
                                      value={Math.round(vol * 100)}
                                      min={0}
                                      max={200}
                                      step={1}
                                      decimals={0}
                                      onCommit={(n) => ponerVolumen(n / 100)}
                                      className="campo-numerico-del-editor flex-1 min-w-0 h-full pl-2 text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                      ariaLabel="Volumen en porcentaje"
                                    />
                                    <span className="pr-2 text-[12px] text-neutral-400">
                                      %
                                    </span>
                                  </div>
                                </FilaDelPanel>
                                {/* Velocidad — sólo para video: acelerar un
                                audio le sube el tono, y eso es otra decisión.
                                Mover el slider cambia lo que DURA el clip, así
                                que se ve al toque en la timeline. */}
                                {videoOrReady && (
                                  <FilaDelPanel etiqueta="Velocidad">
                                    <div className="flex-1 min-w-0 relative flex items-center">
                                      <input
                                        type="range"
                                        min={VELOCIDAD_MIN}
                                        max={VELOCIDAD_MAX}
                                        step={0.05}
                                        value={velocidadDeClip(clip)}
                                        onChange={(e) =>
                                          cambiarVelocidadDeClip(
                                            clip.id,
                                            parseFloat(e.target.value),
                                          )
                                        }
                                        className="w-full accent-brand-primary"
                                        aria-label="Velocidad del clip"
                                      />
                                      {/* Marca del 1x: el slider va de 0,5x a
                                      2x, y el original NO cae en el medio. */}
                                      <span
                                        aria-hidden
                                        className="pointer-events-none absolute top-1/2 -translate-y-1/2 w-px h-2.5 bg-neutral-400/70"
                                        style={{
                                          left: `${((1 - VELOCIDAD_MIN) / (VELOCIDAD_MAX - VELOCIDAD_MIN)) * 100}%`,
                                        }}
                                      />
                                    </div>
                                    <div className="w-[68px] h-9 shrink-0 flex items-center rounded-md border border-black/[0.08] bg-surface-card">
                                      <NumberField
                                        value={velocidadDeClip(clip)}
                                        min={VELOCIDAD_MIN}
                                        max={VELOCIDAD_MAX}
                                        step={0.05}
                                        decimals={2}
                                        onCommit={(n) =>
                                          cambiarVelocidadDeClip(clip.id, n)
                                        }
                                        className="campo-numerico-del-editor flex-1 min-w-0 h-full pl-2 text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                        ariaLabel="Velocidad en equis"
                                      />
                                      <span className="pr-2 text-[12px] text-neutral-400">
                                        x
                                      </span>
                                    </div>
                                  </FilaDelPanel>
                                )}
                              </SeccionDelPanel>
                            )}

                            {esCapaVisual && (
                              <SeccionDelPanel titulo="Capa">
                                <FilaDelPanel etiqueta="Opacidad">
                                  <div className="flex-1 min-w-0 flex items-center">
                                    <input
                                      type="range"
                                      min={0}
                                      max={1}
                                      step={0.01}
                                      value={opacidad}
                                      onChange={(e) =>
                                        ponerOpacidad(
                                          parseFloat(e.target.value),
                                        )
                                      }
                                      className="w-full accent-brand-primary"
                                      aria-label="Opacidad de la capa"
                                    />
                                  </div>
                                  <div className="w-[68px] h-9 shrink-0 flex items-center rounded-md border border-black/[0.08] bg-surface-card">
                                    <NumberField
                                      value={Math.round(opacidad * 100)}
                                      min={0}
                                      max={100}
                                      step={1}
                                      decimals={0}
                                      onCommit={(n) => ponerOpacidad(n / 100)}
                                      className="campo-numerico-del-editor flex-1 min-w-0 h-full pl-2 text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                      ariaLabel="Opacidad en porcentaje"
                                    />
                                    <span className="pr-2 text-[12px] text-brand-black/40">
                                      %
                                    </span>
                                  </div>
                                </FilaDelPanel>
                              </SeccionDelPanel>
                            )}

                            {/* Animaciones de entrada y salida de una capa de
                            imagen: los mismos selectores (con vista previa al
                            pasar el mouse) y los mismos presets comunes que
                            tienen los Elementos; sin los de texto, que no
                            tienen sentido en una imagen
                            (ADR-2026-09-27-las-capas-de-imagen-entran-y-salen). */}
                            {clip.type === "image" && (
                              <SeccionDelPanel titulo="Animaciones">
                                <div className="grid grid-cols-2 gap-2">
                                  {(
                                    [
                                      {
                                        kind: "enter" as const,
                                        label: "Entrada",
                                        valor: clip.enterAnimation ?? "none",
                                        dur: clip.enterDurationSec ?? 0.5,
                                        campo: "enterAnimation" as const,
                                        campoDur: "enterDurationSec" as const,
                                      },
                                      {
                                        kind: "exit" as const,
                                        label: "Salida",
                                        valor: clip.exitAnimation ?? "none",
                                        dur: clip.exitDurationSec ?? 0.5,
                                        campo: "exitAnimation" as const,
                                        campoDur: "exitDurationSec" as const,
                                      },
                                    ] as const
                                  ).map((lado) => (
                                    <div key={lado.kind} className="space-y-1.5">
                                      <MotionPicker
                                        label={lado.label}
                                        kind={lado.kind}
                                        scope="common"
                                        value={lado.valor}
                                        onChange={(id) =>
                                          setClips((prev) =>
                                            prev.map((c) =>
                                              c.id === clip.id
                                                ? { ...c, [lado.campo]: id }
                                                : c,
                                            ),
                                          )
                                        }
                                      />
                                      {lado.valor !== "none" && (
                                        <div className="h-9 flex items-center gap-1.5 px-2 rounded-md border border-black/[0.08] bg-surface-card">
                                          <NumberField
                                            value={lado.dur}
                                            min={0.1}
                                            max={5}
                                            step={0.1}
                                            decimals={1}
                                            onCommit={(n) =>
                                              setClips((prev) =>
                                                prev.map((c) =>
                                                  c.id === clip.id
                                                    ? { ...c, [lado.campoDur]: n }
                                                    : c,
                                                ),
                                              )
                                            }
                                            className="campo-numerico-del-editor flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                            ariaLabel={`Duración ${lado.label.toLowerCase()}`}
                                          />
                                          <span className="text-[12px] text-brand-black/40">
                                            seg
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </SeccionDelPanel>
                            )}

                            {/* Encuadre — sólo aparece cuando el clip está
                            encuadrado a mano. Es la única forma de volver
                            EXACTO al original: a ojo se puede dejar la escala
                            en 0.99 y el video queda con una línea de fondo
                            que no se ve hasta exportar. */}
                            {(clip.type === "video" ||
                              clip.type === "ai-generated" ||
                              clip.type === "image") &&
                              !esEncuadreNeutro(clip.encuadre) && (
                                <SeccionDelPanel titulo="Encuadre">
                                  <FilaDelPanel etiqueta="Tamaño y posición">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        restablecerEncuadre(clip.id)
                                      }
                                      className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-black/[0.08] bg-surface-card text-[13px] text-brand-black/70 hover:bg-black/[0.03] transition-colors"
                                      title="Volver al encuadre original: el clip vuelve a llenar el cuadro, sin recorte ni giro"
                                    >
                                      <RotateCcw className="w-3.5 h-3.5" />
                                      Restablecer
                                    </button>
                                  </FilaDelPanel>
                                </SeccionDelPanel>
                              )}

                            {ia.length > 0 && (
                              <SeccionDelPanel titulo="Herramientas IA">
                                <ListaDeAcciones items={ia} />
                              </SeccionDelPanel>
                            )}

                            {/* Las acciones del clip (dividir, duplicar,
                            eliminar…) ya no viven acá: flotan al costado del
                            preview. */}
                          </div>
                        );
                      })()}

                    {/* ── Eliminar silencios ───────────────────────────────
                    Sub-pantalla de la herramienta: los dos parámetros y el
                    botón. Se abre desde la lista de Herramientas IA del panel
                    del clip y vuelve ahí con la flecha de la cabecera. */}
                    {activePanel === "silencios" &&
                      silenceClipId &&
                      (() => {
                        const clip = clips.find((c) => c.id === silenceClipId);
                        if (!clip) return null;
                        const cortando = silenceCutBusy === clip.id;
                        return (
                          <div className="space-y-2">
                            <>
                                <FilaDelPanel
                                  etiqueta="Cortar los de más de"
                                  anchoEtiqueta={124}
                                >
                                  <div className="w-[92px] h-9 flex items-center rounded-md border border-black/[0.08] bg-surface-card">
                                    <NumberField
                                      value={silenceThresholdSec}
                                      min={0.1}
                                      max={10}
                                      step={0.1}
                                      decimals={1}
                                      onCommit={(n) =>
                                        setSilenceThresholdSec(n)
                                      }
                                      className="campo-numerico-del-editor flex-1 min-w-0 h-full pl-2 text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                      ariaLabel="Umbral de silencio en segundos"
                                    />
                                    <span className="pr-2 text-[12px] text-neutral-400">
                                      s
                                    </span>
                                  </div>
                                </FilaDelPanel>
                                <FilaDelPanel
                                  etiqueta="Dejar un margen de"
                                  anchoEtiqueta={124}
                                >
                                  <div className="w-[92px] h-9 flex items-center rounded-md border border-black/[0.08] bg-surface-card">
                                    <NumberField
                                      value={silencePaddingSec}
                                      min={0}
                                      max={1}
                                      step={0.02}
                                      decimals={2}
                                      onCommit={(n) => setSilencePaddingSec(n)}
                                      className="campo-numerico-del-editor flex-1 min-w-0 h-full pl-2 text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                      ariaLabel="Margen antes y después de cada tramo de habla en segundos"
                                    />
                                    <span className="pr-2 text-[12px] text-neutral-400">
                                      s
                                    </span>
                                  </div>
                                </FilaDelPanel>
                                <button
                                  type="button"
                                  onClick={() =>
                                    void cutSilencesFromClip(
                                      clip.id,
                                      silenceThresholdSec,
                                      silencePaddingSec,
                                    )
                                  }
                                  disabled={cortando}
                                  className="h-9 w-full inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors disabled:cursor-wait disabled:opacity-70"
                                  style={{
                                    // Inline y no `mt-*`: el contenedor usa
                                    // `space-y-2`, que también escribe
                                    // `margin-top` y le gana a la clase.
                                    marginTop: 16,
                                    backgroundColor: "rgb(var(--brand-primary-rgb) / 0.08)",
                                    color: "var(--brand-primary)",
                                  }}
                                >
                                  {cortando ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />{" "}
                                      Procesando…
                                    </>
                                  ) : (
                                    <>
                                      <ScissorsLineDashed className="w-3.5 h-3.5" />{" "}
                                      Eliminar silencios
                                    </>
                                  )}
                                </button>
                                {silenceCutNotice && !cortando && (
                                  <p className="text-[12px] text-neutral-500 leading-snug">
                                    {silenceCutNotice}
                                  </p>
                                )}
                              </>
                          </div>
                        );
                      })()}

                    {/* ── Corregir voz ─────────────────────────────────────
                    La transcripción del clip, tocable palabra por palabra. Al
                    aplicar llama a la MISMA corregirVozEnClip que el agente:
                    shimmer en el clip, reemplazo in-place, original en Archivos.
                    Ver ADR-2026-09-24-corregir-voz-es-un-boton-del-estudio. */}
                    {activePanel === "corregir-voz" &&
                      corregirVozClipId &&
                      (() => {
                        const clip = clips.find(
                          (c) => c.id === corregirVozClipId,
                        );
                        if (!clip) return null;
                        const desde = clip.mediaOffset ?? 0;
                        const bloqueo = !clip.storagePath
                          ? "El clip todavía se está subiendo. Esperá unos segundos."
                          : clip.eraseStatus === "processing" ||
                              clip.aiTranslating
                            ? "El clip tiene otra operación en curso (borrado o traducción). Esperá a que termine."
                            : undefined;
                        return (
                          <CorregirVozDelClip
                            key={clip.id}
                            // Sólo si el transcript es de ESTE archivo: tras
                            // "Editar clip" o traducir, el viejo mostraba las
                            // oraciones de otro tramo.
                            palabras={transcriptVigente(clip)?.words ?? null}
                            ventana={{
                              desde,
                              hasta:
                                desde +
                                clip.duration * velocidadDeClip(clip),
                            }}
                            corrigiendo={
                              clip.corregirVozStatus === "processing"
                            }
                            error={
                              clip.corregirVozStatus === "error"
                                ? clip.corregirVozError
                                : undefined
                            }
                            bloqueo={bloqueo}
                            onTranscribir={() =>
                              transcribirClipParaCorregirVoz(clip.id)
                            }
                            onCorregir={(ediciones) =>
                              void corregirVozEnClip(clip.id, ediciones)
                            }
                          />
                        );
                      })()}

                    {/* ── Cambiar voz ──────────────────────────────────────
                    La misma lista de voces que antes se abría en un segundo
                    panel de 380px al costado, ahora como sub-pantalla de este:
                    es un ajuste del clip, igual que Eliminar silencios, y dos
                    paneles gemelos uno al lado del otro obligaban a leer dos
                    cabeceras para entender cuál mandaba.
                    Ver ADR-2026-09-16-cambiar-voz-es-una-sub-pantalla. */}
                    {activePanel === "voz" &&
                      changeVoiceClipId &&
                      (() => {
                        const target = clips.find(
                          (c) => c.id === changeVoiceClipId,
                        );
                        if (!target) return null;
                        // Costo estimado = misma fórmula S2S que el panel de
                        // audio (creditsForVoice sobre ~12.5 chars/seg), con la
                        // duración del clip.
                        const durSec =
                          target.sourceDuration || target.duration || 0;
                        const estCredits = creditsForVoice(
                          Math.ceil(durSec * 12.5),
                        );
                        const volverAlClip = () => {
                          setChangeVoiceClipId(null);
                          setActivePanel("clip");
                        };
                        return (
                          <ClipVoicePicker
                            embebido
                            open
                            userId={currentUserId}
                            companyId={companyId}
                            estimatedCredits={estCredits}
                            onGenerate={(voiceId) => {
                              const t = changeVoiceClipId;
                              setChangeVoiceClipId(null);
                              // El cambio de voz se muestra ENCIMA del clip en
                              // la timeline (shimmer + "Cambiando voz…"), igual
                              // que los subtítulos. En el teléfono la hoja la
                              // tapa, así que al arrancar se cierra en vez de
                              // volver al panel del clip; en escritorio vuelve,
                              // que es de donde se vino.
                              // Ver ADR-2026-09-17-subtitulos-en-el-telefono-cierra-la-hoja.
                              setActivePanel(
                                enElTelefonoRef.current ? "none" : "clip",
                              );
                              if (t) void changeClipVoice(t, voiceId);
                            }}
                            onClose={volverAlClip}
                          />
                        );
                      })()}

                    {/* Panel de edición de video con IA (Gemini Omni). Feature
                    nueva e independiente del editor Sora. Ver ADR-500. */}
                    {activePanel === "edit-video-ia" &&
                      omniEditClipId &&
                      companyId &&
                      projectId &&
                      (() => {
                        const clip = clipsRef.current.find(
                          (c) => c.id === omniEditClipId,
                        );
                        if (!clip || !clip.src) return null;
                        const cId = companyId;
                        const pId = projectId;
                        const omniAspect: "16:9" | "9:16" =
                          aspectRatio === "9:16" ? "9:16" : "16:9";
                        return (
                          <EditarVideoIA
                            // La edición es del clip, no del panel: cerrarlo no
                            // la corta y al reabrirlo está como quedó.
                            sesionId={clip.id}
                            clipUrl={clip.src}
                            originalPath={clip.storagePath}
                            // Se procesa (y se cobra) el tramo que está en la
                            // timeline: el worker lo corta con estos dos datos.
                            durationSec={segundosDelRecorteOmni(clip)}
                            mediaOffsetSec={clip.mediaOffset ?? 0}
                            aspectRatio={omniAspect}
                            // `clip.src` se firmó a 1h al abrir el proyecto;
                            // el worker la usa mucho después.
                            refirmarOriginal={
                              clip.storagePath
                                ? async () => {
                                    const { data } = await supabase.storage
                                      .from("video-project-assets")
                                      .createSignedUrl(
                                        clip.storagePath!,
                                        60 * 60 * 3,
                                      );
                                    return data?.signedUrl ?? null;
                                  }
                                : undefined
                            }
                            projectId={pId}
                            companyId={cId}
                            onClose={() => {
                              setOmniEditClipId(null);
                              setActivePanel("none");
                            }}
                            onSave={async (result, mode) => {
                              try {
                                const {
                                  data: { user },
                                } = await supabase.auth.getUser();
                                const asset = await registerExistingAsset({
                                  projectId: pId,
                                  companyId: cId,
                                  userId: user?.id ?? "",
                                  storagePath: result.path,
                                  type: "video",
                                  name: `${clip.name || "Video"} (editado)`,
                                  mimeType: "video/mp4",
                                  durationSeconds:
                                    result.segundos ?? segundosDelRecorteOmni(clip),
                                });
                                if (mode === "replace") {
                                  // El archivo editado ES el tramo: empieza en
                                  // 0 y dura lo que duraba el recorte. Dejar
                                  // el `mediaOffset` viejo haría que el clip
                                  // busque un segundo que el archivo nuevo no
                                  // tiene.
                                  // Si mientras se editaba el clip se
                                  // recortó, el archivo sigue siendo el tramo
                                  // de antes: el clip vuelve a ese largo en vez
                                  // de mostrar otro recorte o quedarse sin
                                  // material.
                                  const recorteActual = segundosDelRecorteOmni(clip);
                                  const material = result.segundos ?? recorteActual;
                                  const cambioElTramo =
                                    Math.abs(material - recorteActual) > 0.05;
                                  setClips((prev) =>
                                    prev.map((c) =>
                                      c.id === clip.id
                                        ? {
                                            ...c,
                                            ...(cambioElTramo
                                              ? { duration: material / (c.speed || 1) }
                                              : {}),
                                            src: result.url,
                                            storagePath: result.path,
                                            assetId: asset?.id ?? c.assetId,
                                            mediaOffset: 0,
                                            sourceDuration: material,
                                            // El archivo nuevo es otro tramo (y
                                            // otra voz si se pidió): el
                                            // transcript viejo mostraba las
                                            // oraciones del principio del
                                            // original en "Corregir voz".
                                            transcript: undefined,
                                          }
                                        : c,
                                    ),
                                  );
                                }
                              } catch (e) {
                                console.error(
                                  "[omni-edit] error al guardar:",
                                  e,
                                );
                                // Re-lanzar: EditarVideoIA lo muestra en su
                                // banner y mantiene el panel abierto para
                                // reintentar (no cierra en silencio).
                                throw e;
                              }
                            }}
                          />
                        );
                      })()}

                    {/* Panel de Mejorar calidad (upscale con Topaz). Ver ADR-596. */}
                    {activePanel === "upscale-video" &&
                      upscaleClipId &&
                      companyId &&
                      projectId &&
                      (() => {
                        const clip = clipsRef.current.find(
                          (c) => c.id === upscaleClipId,
                        );
                        if (!clip || !clip.src) return null;
                        const cId = companyId;
                        const pId = projectId;
                        return (
                          <UpscaleVideoPanel
                            companyId={cId}
                            projectId={pId}
                            clipId={clip.id}
                            clipUrl={clip.src}
                            clipStoragePath={clip.storagePath}
                            durationSec={clip.duration}
                            onClose={() => {
                              setUpscaleClipId(null);
                              setActivePanel("none");
                            }}
                            onIniciado={(predictionId) =>
                              setClips((prev) =>
                                prev.map((c) =>
                                  c.id === clip.id
                                    ? {
                                        ...c,
                                        mejorarCalidadStatus: "processing",
                                        mejorarCalidadJobId: predictionId,
                                      }
                                    : c,
                                ),
                              )
                            }
                            onSave={(result) =>
                              aplicarMejoraDeCalidad(clip.id, result.path, result.url)
                            }
                          />
                        );
                      })()}

                    {activePanel === "files" && !modelPickerOpen && (
                      <div className="space-y-3">
                        {/* Hidden multi-type file input shared by the "Subir
                      archivo" CTA. We sniff the MIME and route through the
                      existing per-type uploaders so behavior stays
                      consistent with the rest of the app:
                        · video/audio → handleFileUpload (auto-creates a
                          timeline clip + uploads as asset in background).
                        · image → uploadProjectAsset only (images aren't
                          standalone clips today; they're used as
                          frames/refs inside AI panels). */}
                        <input
                          ref={filesUploadInputRef}
                          type="file"
                          accept="video/*,audio/*,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file || !companyId || !projectId) return;
                            if (file.type.startsWith("video/")) {
                              setFilesTab("media");
                              // Re-dispatch through the dedicated video input so
                              // we hit the timeline-aware path without
                              // duplicating its 50+ lines of clip wiring.
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              if (fileInputRef.current) {
                                fileInputRef.current.files = dt.files;
                                fileInputRef.current.dispatchEvent(
                                  new Event("change", { bubbles: true }),
                                );
                              }
                              return;
                            }
                            if (file.type.startsWith("audio/")) {
                              setFilesTab("audio");
                              const dt = new DataTransfer();
                              dt.items.add(file);
                              if (audioInputRef.current) {
                                audioInputRef.current.files = dt.files;
                                audioInputRef.current.dispatchEvent(
                                  new Event("change", { bubbles: true }),
                                );
                              }
                              return;
                            }
                            if (file.type.startsWith("image/")) {
                              setFilesTab("media");
                              // Sube y queda arriba de la galería; no entra a
                              // la timeline (eso lo hace el arrastre).
                              await subirImagenAArchivos(file);
                              return;
                            }
                            // No es imagen, video ni audio: el `accept` del
                            // input no alcanza (el usuario puede forzar "todos
                            // los archivos" en el diálogo del sistema), así que
                            // avisamos en vez de no hacer nada.
                            setUploadErrorMsg(
                              describeUploadError(
                                new Error("Unsupported file type"),
                                file.name,
                              ),
                            );
                          }}
                        />

                        {/* Subir archivo / Generar con IA / tabs de tipo:
                       ocultos en modo selección (cuando venís de un slot de
                       escena IA solo se elige una imagen ya existente). */}
                        {!filesPickTarget && (
                          <>
                        {/* Pills de tipo (estilo modal) */}
                        {/* Ámbito: de dónde sale lo que se lista. Va ARRIBA de las pastillas
                            porque es la división más gruesa — lo de este proyecto contra lo de
                            la cuenta —, y las pastillas filtran por tipo DENTRO de lo elegido.
                            El control es el mismo de la plataforma (`SegmentedControl`). */}
                        <SegmentedControl
                          opciones={OPCIONES_DE_AMBITO}
                          valor={ambitoDeArchivos}
                          onCambio={cambiarAmbitoDeArchivos}
                          ariaLabel="De dónde salen los archivos"
                          className="mb-2"
                        />
                        {/* "Escenas" no está acá: son los fondos de ATLAS,
                        material para ARMAR una escena, no archivos del
                        proyecto. Sigue viva en el modo selección de abajo, que
                        es donde sirve (elegir la imagen de un frame).

                        Con UNA sola carpeta no hay nada que elegir: una
                        pastilla sola que no se puede apagar es un botón que
                        miente. En ese caso el nombre se muestra como TÍTULO,
                        con el mismo estilo que "Videos" e "Imágenes" de la
                        grilla de abajo. Hoy le toca a Atlas, que quedó con
                        Creaciones sola cuando Productos se mudó a Marca. */}
                        {PESTANAS_POR_AMBITO[ambitoDeArchivos].length > 1 ? (
                          <div
                            className="flex items-center gap-1"
                            data-tour="files-pills"
                          >
                            {PESTANAS_POR_AMBITO[ambitoDeArchivos].map((t) => {
                              const PIcon =
                                t === "media"
                                  ? Images
                                  : t === "animaciones"
                                    ? Sparkles
                                  : t === "audio"
                                    ? Music
                                    : t === "productos"
                                      ? Package
                                      : Clock;
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setFilesTab(t)}
                                  className={clasesDePastilla(filesTab === t)}
                                >
                                  <PIcon className="w-3.5 h-3.5" />
                                  {ETIQUETA_DE_PESTANA[t]}
                                </button>
                              );
                            })}
                          </div>
                        ) : filesTab === "historial" ? (
                          /* Creaciones no lleva título: sus pastillas son
                             Imágenes y Videos, que es la división que importa
                             acá adentro. El nombre de la carpeta ya lo dice el
                             segmento de arriba ("Atlas"). */
                          <div
                            className="flex items-center gap-1"
                            data-tour="files-pills"
                          >
                            {PASTILLAS_DE_CREACIONES.map((op) => {
                              const PIcon =
                                op.id === "videos" ? Film : Images;
                              return (
                                <button
                                  key={op.id}
                                  type="button"
                                  onClick={() => setMuestraDeCreaciones(op.id)}
                                  className={clasesDePastilla(
                                    muestraDeCreaciones === op.id,
                                  )}
                                >
                                  <PIcon className="w-3.5 h-3.5" />
                                  {op.label}
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p
                            className="text-[14px] font-semibold text-brand-black/90"
                            data-tour="files-pills"
                          >
                            {/* El título dice la carpeta que se está VIENDO, no
                            la primera del ámbito: el agente puede dejar el
                            panel en Productos (`request_product_selection`)
                            aunque esa carpeta ya no tenga pastilla, y ahí el
                            título tiene que acompañar a lo que se muestra. */}
                            {ETIQUETA_DE_PESTANA[filesTab]}
                          </p>
                        )}
                          </>
                        )}

                        {/* Pills en modo selección (desde un frame de escena):
                        las MISMAS que las del panel y en el mismo orden, menos
                        Audio — un mp3 no sirve de frame. */}
                        {filesPickTarget && (
                          <div className="flex items-center gap-1">
                            {(
                              ["historial", "media", "productos"] as const
                            ).map((t) => {
                              const active = filesTab === t;
                              const PIcon =
                                t === "media"
                                  ? Images
                                  : t === "productos"
                                    ? Package
                                    : Clock;
                              return (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setFilesTab(t)}
                                  className={clasesDePastilla(active)}
                                >
                                  <PIcon className="w-3.5 h-3.5" />
                                  {ETIQUETA_DE_PESTANA[t]}
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Buscador — SÓLO en Productos. Es la única pestaña
                        con un catálogo largo, donde buscar por nombre es más
                        rápido que recorrer la grilla; en Multimedia, Audio y
                        Creaciones lo que hay entra de un vistazo y el campo era
                        una fila más entre las pastillas y el contenido.
                        Cambiar de pestaña ya limpia el texto (ver
                        `filesBusqueda`), así que no queda ningún filtro puesto
                        de una pestaña que no lo muestra. */}
                        {filesTab === "productos" && (
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-brand-black/30 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          <input
                            type="text"
                            value={filesBusqueda}
                            onChange={(e) => setFilesBusqueda(e.target.value)}
                            placeholder="Buscar en esta pestaña…"
                            className="w-full h-8 pl-8 pr-7 rounded-full bg-black/[0.04] text-[12px] text-brand-black/85 placeholder:text-brand-black/35 focus:outline-none focus:bg-black/[0.06] transition-colors"
                          />
                          {filesBusqueda && (
                            <button
                              type="button"
                              onClick={() => setFilesBusqueda("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                              title="Limpiar"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        )}

                        {/* 4. Tab content ──────────────────────────────────── */}
                        {filesTab === "animaciones" && (
                          <div className="pt-1">
                            {animacionesGraficas.length === 0 ? (
                              <p className="text-[13px] text-neutral-500 px-1 py-6 text-center">
                                Todavía no hay animaciones gráficas en este
                                proyecto. Creá una con «Animación gráfica» o
                                pedísela al asistente.
                              </p>
                            ) : (
                              <div className="grid grid-cols-3 gap-2">
                                {animacionesGraficas
                                  .filter((a) =>
                                    coincideConLaBusqueda(a.nombre, filesBusqueda),
                                  )
                                  .map((a) => (
                                    <div key={a.id} className="relative group">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          crearClipDeAnimacionGrafica({
                                            animacion: a.animacion,
                                            tipo: a.animacion.tipo,
                                            duracionSeg: versionActual(a.animacion)
                                              .duracionSeg,
                                            nombre: a.nombre,
                                          })
                                        }
                                        draggable
                                        onDragStart={(e) => {
                                          e.dataTransfer.setData(
                                            TIPO_ANIMACION,
                                            a.id,
                                          );
                                          e.dataTransfer.setData(
                                            TIPO_FAMILIA("elements"),
                                            "1",
                                          );
                                          e.dataTransfer.effectAllowed = "copy";
                                          const img = buildTimelineClipDragImage(
                                            "image",
                                            a.nombre,
                                            a.miniatura || null,
                                          );
                                          // x = 0: el borde izquierdo del
                                          // fantasma es donde va a arrancar el
                                          // clip (el soltado usa la X del
                                          // puntero).
                                          e.dataTransfer.setDragImage(
                                            img,
                                            0,
                                            20,
                                          );
                                        }}
                                        className="w-full aspect-square rounded-md overflow-hidden border border-black/[0.06] bg-neutral-100 hover:border-brand-primary/50 transition-colors cursor-grab active:cursor-grabbing"
                                        title="Tocala para sumarla en el cursor o arrastrala a la timeline"
                                      >
                                        {a.miniatura ? (
                                          <img
                                            src={a.miniatura}
                                            alt=""
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Sparkles className="w-5 h-5 text-neutral-400" />
                                          </div>
                                        )}
                                      </button>
                                      <p
                                        className="mt-1 text-[11px] text-brand-black/70 truncate"
                                        title={a.nombre}
                                      >
                                        {a.nombre}
                                      </p>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAnimacionesGraficas((prev) =>
                                            quitarAnimacion(prev, a.id),
                                          );
                                        }}
                                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 text-neutral-500 hover:text-red-500 opacity-0 group-hover:opacity-100 flex items-center justify-center shadow-sm transition-opacity"
                                        title="Sacar de la biblioteca (no toca la timeline)"
                                        aria-label="Sacar de la biblioteca"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}

                        {filesTab === "audio" && (
                          <div className="pt-1">
                            {libraryLoading && !filesLoadedOnceRef.current ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                              </div>
                            ) : (
                              <>
                              {/* Una sola grilla de 3 columnas, igual que
                              Multimedia: "Subir" y los audios son baldosas
                              cuadradas del mismo tamaño y el primero convive
                              con los otros en la misma fila. Antes los audios
                              eran filas anchas de 2 columnas con la onda al
                              medio; la onda ahora vive arriba del nombre. */}
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    filesUploadInputRef.current?.click()
                                  }
                                  className="aspect-square rounded-md border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1"
                                >
                                  <Upload className="w-4 h-4 text-neutral-400" />
                                  <span className="text-[11px] font-medium text-neutral-500">
                                    Subir
                                  </span>
                                </button>
                                {libraryAssets
                                  .filter((a) =>
                                    coincideConLaBusqueda(a.name, filesBusqueda),
                                  )
                                  .map((asset) => {
                                  const isPlaying =
                                    playingAudioAssetId === asset.id;
                                  const peaks = audioAssetPeaks[asset.id];
                                  const url = assetPreviewUrls[asset.id];
                                  return (
                                    <div
                                      key={asset.id}
                                      onClick={() => addAssetToTimeline(asset)}
                                      draggable
                                      onDragStart={(e) => {
                                        e.dataTransfer.setData(
                                          TIPO_ASSET,
                                          asset.id,
                                        );
                                        // La familia viaja como tipo: es lo
                                        // único que se puede leer en `dragover`.
                                        e.dataTransfer.setData(
                                          TIPO_FAMILIA("audio"),
                                          "1",
                                        );
                                        e.dataTransfer.effectAllowed = "copy";
                                        const img = buildTimelineClipDragImage(
                                          "audio",
                                          asset.name || "Audio",
                                        );
                                        e.dataTransfer.setDragImage(
                                          img,
                                          0, // el clip arranca donde está el puntero
                                          20,
                                        );
                                      }}
                                      className="group aspect-square p-2 flex flex-col rounded-md border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all text-left cursor-grab active:cursor-grabbing"
                                      title={`${asset.name || "Audio"} · tocalo para sumarlo o arrastralo a la timeline`}
                                    >
                                      {/* Mitad de arriba: el ícono, o la onda
                                      mientras suena. Ocupa el alto que sobra
                                      para que el nombre y el play queden
                                      siempre apoyados abajo, midan lo que midan
                                      las barras. */}
                                      <div className="flex-1 min-h-0 flex items-center justify-center">
                                        {isPlaying &&
                                        peaks &&
                                        peaks.length > 0 ? (
                                          <div className="flex items-center h-8 w-full gap-[2px] overflow-hidden">
                                            {peaks.map((p, i) => {
                                              const barRatio =
                                                (i + 0.5) / peaks.length;
                                              const isPlayed =
                                                barRatio <=
                                                audioPlaybackProgress;
                                              const dist = Math.abs(
                                                barRatio -
                                                  audioPlaybackProgress,
                                              );
                                              const pulse =
                                                dist < 0.04 ? 1.35 : 1;
                                              const h = Math.max(
                                                0.2,
                                                p * pulse,
                                              );
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    width: 2,
                                                    height: `${Math.min(1, h) * 100}%`,
                                                    backgroundColor: isPlayed
                                                      ? "#10b981"
                                                      : "#a7f3d0",
                                                    borderRadius: 1,
                                                    transition:
                                                      "height 80ms linear, background-color 120ms linear",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="w-10 h-10 rounded-md bg-emerald-50 flex items-center justify-center">
                                            <Music className="w-4 h-4 text-emerald-600" />
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        <p className="flex-1 min-w-0 text-[11px] font-medium text-brand-black/80 truncate">
                                          {asset.name || "Audio"}
                                        </p>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (!url) return;
                                            playAudioPreview(asset.id, url);
                                          }}
                                          disabled={!url}
                                          aria-label={
                                            isPlaying ? "Pausar" : "Reproducir"
                                          }
                                          className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-colors disabled:opacity-50 ${
                                            isPlaying
                                              ? "bg-brand-primary border-brand-primary text-white"
                                              : "bg-white border-neutral-200 text-brand-black/70 hover:bg-brand-primary hover:border-brand-primary hover:text-white"
                                          }`}
                                        >
                                          {isPlaying ? (
                                            <Pause className="w-3 h-3" />
                                          ) : (
                                            <Play className="w-3 h-3 translate-x-[1px]" />
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              </>
                            )}
                          </div>
                        )}

                        {filesTab === "media" &&
                          (() => {
                            // Videos del proyecto (solo fuera de modo selección
                            // — un frame es una imagen). Las imágenes (subidas +
                            // generadas, ya deduplicadas) salen del memo
                            // `imagenesSubidas`, que comparte con el selector de
                            // imagen de las escenas.
                            const videoItems = filesPickTarget
                              ? []
                              : libraryAssets.map((v) => ({
                                  key: `vid:${v.id}`,
                                  kind: "video" as const,
                                  id: v.id,
                                  url: assetPreviewUrls[v.id] as
                                    | string
                                    | undefined,
                                  name: v.name || "Video",
                                  asset: v as ProjectAsset | undefined,
                                  ts: v.created_at || "",
                                }));
                            // Videos + imágenes subidas + generadas, TODO junto y
                            // ordenado por fecha (más nuevo arriba). Las que no
                            // tienen fecha conocida caen al final.
                            const all = [...videoItems, ...imagenesSubidas]
                              .filter((i) =>
                                coincideConLaBusqueda(i.name, filesBusqueda),
                              )
                              .sort((a, b) =>
                                (b.ts || "").localeCompare(a.ts || ""),
                              );
                            return (
                              <div className="pt-1">
                                {filesPickTarget && (
                                  <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                    <span className="text-[12px] font-medium text-brand-black/80">
                                      {filesPickTarget.kind === "first"
                                        ? "Elegí imagen para el frame inicial"
                                        : filesPickTarget.kind === "last"
                                          ? "Elegí imagen para el frame final"
                                          : "Elegí imagen de referencia"}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setFilesPickTarget(null);
                                        setActivePanel("ai-scene");
                                      }}
                                      className="text-[12px] text-brand-primary hover:underline shrink-0"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                )}
                                {(imageLibraryLoading || libraryLoading) &&
                                all.length === 0 &&
                                libraryAssets.length === 0 ? (
                                  <div className="flex items-center justify-center py-8">
                                    <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-3 gap-2">
                                    {!filesPickTarget && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          filesUploadInputRef.current?.click()
                                        }
                                        className="aspect-square rounded-md border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1"
                                      >
                                        <Upload className="w-4 h-4 text-neutral-400" />
                                        <span className="text-[11px] font-medium text-neutral-500">
                                          Subir
                                        </span>
                                      </button>
                                    )}
                                    {all.map((item) => {
                                      // Tile de VIDEO (cuadrado, play + badge).
                                      if (item.kind === "video") {
                                        const asset = item.asset;
                                        if (!asset) return null;
                                        const url = item.url;
                                        const isPlaying =
                                          playingVideoAssetId === asset.id;
                                        return (
                                          <div
                                            key={item.key}
                                            onClick={() =>
                                              addAssetToTimeline(asset)
                                            }
                                            draggable
                                            onDragStart={(e) => {
                                              e.dataTransfer.setData(
                                                TIPO_ASSET,
                                                asset.id,
                                              );
                                              e.dataTransfer.setData(
                                                TIPO_FAMILIA("video"),
                                                "1",
                                              );
                                              e.dataTransfer.effectAllowed =
                                                "copy";
                                              const img =
                                                buildTimelineClipDragImage(
                                                  "video",
                                                  asset.name || "Video",
                                                  url || null,
                                                );
                                              e.dataTransfer.setDragImage(
                                                img,
                                                0, // el clip arranca donde está el puntero
                                                20,
                                              );
                                            }}
                                            className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all cursor-grab active:cursor-grabbing"
                                            title={`${asset.name || "Video"} · tocalo para sumarlo o arrastralo a la timeline`}
                                          >
                                            {url ? (
                                              <video
                                                ref={(el) => {
                                                  if (el)
                                                    videoPreviewElsRef.current.set(
                                                      asset.id,
                                                      el,
                                                    );
                                                  else
                                                    videoPreviewElsRef.current.delete(
                                                      asset.id,
                                                    );
                                                }}
                                                src={url}
                                                className="w-full h-full object-cover pointer-events-none"
                                                muted={!isPlaying}
                                                playsInline
                                                preload="metadata"
                                                onEnded={() =>
                                                  setPlayingVideoAssetId(
                                                    (prev) =>
                                                      prev === asset.id
                                                        ? null
                                                        : prev,
                                                  )
                                                }
                                              />
                                            ) : (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                              </div>
                                            )}
                                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/55 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5 pointer-events-none">
                                              <Film className="w-2.5 h-2.5" />
                                              Video
                                            </div>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (!url) return;
                                                toggleVideoPreview(asset.id);
                                              }}
                                              disabled={!url}
                                              aria-label={
                                                isPlaying
                                                  ? "Pausar"
                                                  : "Reproducir"
                                              }
                                              // Sólo la esquina: antes este botón
                                              // tapaba TODA la ficha y se quedaba con
                                              // el toque (con `stopPropagation`), así
                                              // que tocar un video le daba play y
                                              // nunca lo sumaba a la timeline. Ahora
                                              // tocar la ficha la suma y el play es
                                              // éste, como en las fichas de audio
                                              // (ADR-2026-11-03-tocar-la-ficha-de-video-la-suma).
                                              className="absolute bottom-1 right-1 w-8 h-8 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center shadow-md transition-[transform,background-color] duration-150 ease-out hover:bg-black/75 active:scale-[0.96] disabled:opacity-50"
                                            >
                                              {isPlaying ? (
                                                <Pause className="w-3.5 h-3.5" />
                                              ) : (
                                                <Play className="w-3.5 h-3.5 translate-x-[1px]" />
                                              )}
                                            </button>
                                          </div>
                                        );
                                      }
                                      // Tile de IMAGEN / generada.
                                      return (
                                      <button
                                        key={item.key}
                                        draggable={!!item.url}
                                        onDragStart={(e) => {
                                          // Una subida viaja con su id de asset;
                                          // una generada con IA, con el id de la
                                          // generación (se vuelve asset al soltar,
                                          // como al tocarla).
                                          e.dataTransfer.setData(
                                            item.kind === "generated"
                                              ? TIPO_GENERADA
                                              : TIPO_ASSET,
                                            item.id,
                                          );
                                          e.dataTransfer.setData(
                                            TIPO_FAMILIA("elements"),
                                            "1",
                                          );
                                          e.dataTransfer.effectAllowed = "copy";
                                          const img =
                                            buildTimelineClipDragImage(
                                              "image",
                                              item.name || "Imagen",
                                              item.url || null,
                                            );
                                          // x = 0: el borde izquierdo del
                                          // fantasma es donde va a arrancar el
                                          // clip (el soltado usa la X del
                                          // puntero).
                                          e.dataTransfer.setDragImage(
                                            img,
                                            0,
                                            20,
                                          );
                                        }}
                                        onClick={async () => {
                                          // Modo selección (viniste de un slot de
                                          // escena IA con "Ver todas en Archivos"):
                                          // asignar y volver, sin el menú genérico.
                                          if (filesPickTarget) {
                                            if (item.kind === "asset") {
                                              assignSceneImage(
                                                filesPickTarget.kind,
                                                item.id,
                                              );
                                              setFilesPickTarget(null);
                                              setActivePanel("ai-scene");
                                              return;
                                            }
                                            // Las generadas no tienen asset id
                                            // todavía: se materializan al vuelo
                                            // (mismo camino que productos /
                                            // escenas / historial) y recién ahí
                                            // se asignan al slot.
                                            if (item.kind === "generated") {
                                              const target = filesPickTarget.kind;
                                              if (
                                                imageGenImportingGenIds.has(
                                                  item.id,
                                                )
                                              )
                                                return;
                                              const token = ++filesPickTokenRef.current;
                                              setImageGenImportingGenIds(
                                                (prev) => {
                                                  const next = new Set(prev);
                                                  next.add(item.id);
                                                  return next;
                                                },
                                              );
                                              try {
                                                const assetId =
                                                  await importGeneratedImageAsAsset(
                                                    item.id,
                                                  );
                                                // La subida tarda: si en el
                                                // medio el usuario canceló o
                                                // tocó otra imagen, esta ya no
                                                // corresponde.
                                                if (
                                                  assetId &&
                                                  filesPickTokenRef.current ===
                                                    token
                                                ) {
                                                  assignSceneImage(
                                                    target,
                                                    assetId,
                                                  );
                                                  setFilesPickTarget(null);
                                                  setActivePanel("ai-scene");
                                                }
                                              } finally {
                                                setImageGenImportingGenIds(
                                                  (prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(item.id);
                                                    return next;
                                                  },
                                                );
                                              }
                                            }
                                            return;
                                          }
                                          // Fuera del modo selección, tocar una
                                          // imagen la SUMA a la timeline como
                                          // capa nueva. Antes abría un menú con
                                          // caminos de IA; hoy el click hace lo
                                          // que el usuario espera de una
                                          // biblioteca de archivos.
                                          // Ver ADR-2026-09-16-tocar-un-archivo-lo-suma-a-la-timeline.
                                          if (item.kind === "generated") {
                                            // Las generadas todavía no son un
                                            // asset del proyecto: se materializan
                                            // al vuelo y recién ahí entran.
                                            if (
                                              imageGenImportingGenIds.has(item.id)
                                            )
                                              return;
                                            setImageGenImportingGenIds((prev) => {
                                              const next = new Set(prev);
                                              next.add(item.id);
                                              return next;
                                            });
                                            try {
                                              const assetId =
                                                await importGeneratedImageAsAsset(
                                                  item.id,
                                                );
                                              if (assetId)
                                                await agregarImagenAlVideo(
                                                  assetId,
                                                  item.name || "Imagen",
                                                );
                                            } finally {
                                              setImageGenImportingGenIds(
                                                (prev) => {
                                                  const next = new Set(prev);
                                                  next.delete(item.id);
                                                  return next;
                                                },
                                              );
                                            }
                                            return;
                                          }
                                          await agregarImagenAlVideo(
                                            item.id,
                                            item.name || "Imagen",
                                          );
                                        }}
                                        className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all"
                                        title={item.name}
                                      >
                                        {item.url ? (
                                          <img
                                            src={item.url}
                                            alt={item.name}
                                            className="w-full h-full object-cover pointer-events-none"
                                          />
                                        ) : (
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                          </div>
                                        )}
                                        {item.kind === "generated" && (
                                          <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-brand-primary/85 text-white text-[9px] font-semibold uppercase tracking-wide flex items-center gap-0.5">
                                            <Sparkles className="w-2.5 h-2.5" />
                                            IA
                                          </div>
                                        )}
                                        {item.kind === "generated" &&
                                          imageGenImportingGenIds.has(
                                            item.id,
                                          ) && (
                                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                                            </div>
                                          )}
                                      </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                        {/* Productos — catálogo de la marca. Al tocar uno se
                       importa como imagen del proyecto y se abre el mismo menú
                       que las imágenes (Generar IA / Usar como referencia /
                       Animar). */}
                        {(filesTab === "productos" ||
                          filesTab === "historial") &&
                          (() => {
                            const esHistorial = filesTab === "historial";
                            // La grilla es de IMÁGENES (cada tile importa el
                            // archivo como asset de imagen y abre el menú de
                            // "usar como frame"), así que en la pastilla Videos
                            // no va. El bloque de videos vive adentro de este
                            // mismo contenedor, por eso se apaga la grilla y no
                            // el contenedor entero.
                            const muestraLaGrilla =
                              !esHistorial ||
                              muestraDeCreaciones !== "videos" ||
                              !!filesPickTarget;
                            const items = (
                              esHistorial ? filesHistorialItems : filesProductItems
                            ).filter((p) =>
                              coincideConLaBusqueda(p.name, filesBusqueda),
                            );
                            const loading = esHistorial
                              ? filesHistorialLoading
                              : filesProductsLoading;
                            const importingSet = esHistorial
                              ? filesImportingHistorialIds
                              : filesImportingProductIds;
                            const setImporting = esHistorial
                              ? setFilesImportingHistorialIds
                              : setFilesImportingProductIds;
                            const emptyMsg = filesBusqueda.trim()
                              ? `Nada que coincida con “${filesBusqueda.trim()}”.`
                              : esHistorial
                                ? "Todavía no generaste nada con esta marca."
                                : "No hay productos en esta marca.";
                            return (
                              <div className="pt-1">
                                {/* Videos generados — sólo en Creaciones, arriba
                                de las imágenes. Van en su propio bloque y no
                                mezclados en la grilla de abajo porque esa grilla
                                es de imágenes: cada tile importa el archivo como
                                asset de IMAGEN y abre el menú de "usar como
                                frame / referencia". Un video necesita otro tile
                                (preview que se reproduce) y otra acción (sumarlo
                                a la timeline). */}
                                {/* Vacío de la pastilla Videos. Hace falta
                                desde que es una pestaña propia: antes, sin
                                videos, el bloque no se dibujaba y abajo seguían
                                las imágenes; ahora el panel quedaría en blanco
                                sin decir por qué. */}
                                {filesTab === "historial" &&
                                  muestraDeCreaciones === "videos" &&
                                  !filesPickTarget &&
                                  !filesHistorialLoading &&
                                  !filesHistorialVideos.some((v) =>
                                    coincideConLaBusqueda(v.name, filesBusqueda),
                                  ) && (
                                    <p className="text-[13px] text-neutral-400 px-1 py-6 text-center">
                                      {filesBusqueda.trim()
                                        ? `Nada que coincida con “${filesBusqueda.trim()}”.`
                                        : "Todavía no generaste videos con esta marca."}
                                    </p>
                                  )}
                                {filesTab === "historial" &&
                                  muestraDeCreaciones !== "imagenes" &&
                                  !filesPickTarget &&
                                  filesHistorialVideos.some((v) =>
                                    coincideConLaBusqueda(v.name, filesBusqueda),
                                  ) && (
                                    /* Sin título adentro: la pastilla de arriba
                                       ya dice "Videos". */
                                    <div className="mb-3">
                                      <div className="grid grid-cols-3 gap-2">
                                        {filesHistorialVideos
                                          .filter((v) =>
                                            coincideConLaBusqueda(
                                              v.name,
                                              filesBusqueda,
                                            ),
                                          )
                                          .map((v) => {
                                          const importando =
                                            filesImportingVideoIds.has(v.id);
                                          return (
                                            <button
                                              key={v.id}
                                              type="button"
                                              title={v.name}
                                              disabled={importando}
                                              onClick={() =>
                                                void importarVideoDelHistorial(
                                                  v,
                                                )
                                              }
                                              className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 transition-all hover:ring-2 hover:ring-brand-primary/50 disabled:opacity-60"
                                              onMouseEnter={(e) => {
                                                const el =
                                                  e.currentTarget.querySelector(
                                                    "video",
                                                  ) as HTMLVideoElement | null;
                                                if (el)
                                                  void el.play().catch(() => {});
                                              }}
                                              onMouseLeave={(e) => {
                                                const el =
                                                  e.currentTarget.querySelector(
                                                    "video",
                                                  ) as HTMLVideoElement | null;
                                                if (el) {
                                                  el.pause();
                                                  try {
                                                    el.currentTime = 0;
                                                  } catch {}
                                                }
                                              }}
                                            >
                                              {/* `preload="none"` + `poster`: la
                                              grilla puede traer 40 videos y sin
                                              esto el navegador se baja los
                                              metadatos de todos al abrir la
                                              pestaña. El mp4 recién se toca al
                                              pasar el mouse. */}
                                              <video
                                                src={v.videoUrl}
                                                poster={v.thumbUrl || undefined}
                                                muted
                                                loop
                                                playsInline
                                                preload="none"
                                                className="w-full h-full object-cover"
                                              />
                                              <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                                <Film className="w-2.5 h-2.5" />
                                                Video
                                              </span>
                                              {importando && (
                                                <span className="absolute inset-0 flex items-center justify-center bg-white/70">
                                                  <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                                                </span>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                {filesTab === "productos" &&
                                  agentProductRequest && (
                                    <div className="mb-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <span className="text-[12px] font-medium text-brand-black/80">
                                          {agentProductRequest.message}
                                        </span>
                                        <button
                                          onClick={cancelAgentProductPick}
                                          className="text-[12px] text-brand-primary hover:underline shrink-0"
                                        >
                                          Cancelar
                                        </button>
                                      </div>
                                      {agentProductRequest.multiple && (
                                        <button
                                          onClick={() =>
                                            confirmAgentProductPick(
                                              agentProductSelection,
                                            )
                                          }
                                          disabled={
                                            agentProductSelection.length === 0
                                          }
                                          className="mt-2 w-full rounded-md bg-brand-primary text-white text-[12px] font-medium py-1.5 disabled:opacity-40"
                                        >
                                          {agentProductRequest.buttonLabel} (
                                          {agentProductSelection.length})
                                        </button>
                                      )}
                                    </div>
                                  )}
                                {filesPickTarget && (
                                  <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                                    <span className="text-[12px] font-medium text-brand-black/80">
                                      {filesPickTarget.kind === "first"
                                        ? "Elegí imagen para el frame inicial"
                                        : filesPickTarget.kind === "last"
                                          ? "Elegí imagen para el frame final"
                                          : "Elegí imagen de referencia"}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setFilesPickTarget(null);
                                        setActivePanel("ai-scene");
                                      }}
                                      className="text-[12px] text-brand-primary hover:underline shrink-0"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                )}
                                {!muestraLaGrilla ? null : loading ? (
                                  <ProductGridSkeleton
                                    count={9}
                                    gridClassName="grid grid-cols-3 gap-2"
                                    tileClassName="rounded-md"
                                  />
                                ) : items.length === 0 ? (
                                  <p className="text-[13px] text-neutral-400 px-1 py-6 text-center">
                                    {emptyMsg}
                                  </p>
                                ) : (
                                  <div className="grid grid-cols-3 gap-2">
                                    {items.map((p) => {
                                      const importing = importingSet.has(p.id);
                                      const selected =
                                        filesTab === "productos" &&
                                        !!agentProductRequest?.multiple &&
                                        agentProductSelection.some(
                                          (x) => x.id === p.id,
                                        );
                                      return (
                                        <button
                                          key={`fpick-${p.id}`}
                                          type="button"
                                          title={p.name}
                                          onClick={async () => {
                                            // Modo "selección para el Director"
                                            // (request_product_selection): tocar
                                            // resuelve (simple) o marca (múltiple).
                                            if (
                                              filesTab === "productos" &&
                                              agentProductRequest
                                            ) {
                                              if (agentProductRequest.multiple) {
                                                setAgentProductSelection(
                                                  (prev) =>
                                                    prev.some(
                                                      (x) => x.id === p.id,
                                                    )
                                                      ? prev.filter(
                                                          (x) => x.id !== p.id,
                                                        )
                                                      : [...prev, p],
                                                );
                                              } else {
                                                confirmAgentProductPick([p]);
                                              }
                                              return;
                                            }
                                            if (!filesPickTarget) {
                                              // Archivos: tocar la suma a la
                                              // timeline como capa nueva. El
                                              // producto o la creación se
                                              // materializan como asset recién
                                              // acá (la importación está
                                              // cacheada, no duplica).
                                              if (importing) return;
                                              setImporting((prev) => {
                                                const n = new Set(prev);
                                                n.add(p.id);
                                                return n;
                                              });
                                              try {
                                                const assetId =
                                                  await importProductToImageAsset(
                                                    p,
                                                  );
                                                if (assetId)
                                                  await agregarImagenAlVideo(
                                                    assetId,
                                                    p.name || "Imagen",
                                                  );
                                              } finally {
                                                setImporting((prev) => {
                                                  const n = new Set(prev);
                                                  n.delete(p.id);
                                                  return n;
                                                });
                                              }
                                              return;
                                            }
                                            // Modo selección: importar y asignar
                                            // al frame.
                                            if (importing) return;
                                            const target = filesPickTarget.kind;
                                            const token =
                                              ++filesPickTokenRef.current;
                                            setImporting((prev) => {
                                              const n = new Set(prev);
                                              n.add(p.id);
                                              return n;
                                            });
                                            try {
                                              const assetId =
                                                await importProductToImageAsset(
                                                  p,
                                                );
                                              // Mismo guard que las generadas:
                                              // la importación tarda y el
                                              // usuario puede haber cancelado o
                                              // elegido otra en el medio.
                                              if (
                                                assetId &&
                                                filesPickTokenRef.current ===
                                                  token
                                              ) {
                                                assignSceneImage(
                                                  target,
                                                  assetId,
                                                );
                                                setFilesPickTarget(null);
                                                setActivePanel("ai-scene");
                                              }
                                            } finally {
                                              setImporting((prev) => {
                                                const n = new Set(prev);
                                                n.delete(p.id);
                                                return n;
                                              });
                                            }
                                          }}
                                          className={`group relative aspect-square rounded-md overflow-hidden bg-neutral-100 transition-all ${
                                            selected
                                              ? "ring-2 ring-brand-primary"
                                              : "hover:ring-2 hover:ring-brand-primary/50"
                                          }`}
                                        >
                                          <CachedImg
                                            src={p.imageUrl}
                                            alt={p.name}
                                            className="w-full h-full object-cover pointer-events-none"
                                          />
                                          {/* El nombre del producto va DENTRO
                                          de la foto, sobre un degradado negro:
                                          las fotos de producto suelen tener el
                                          fondo claro y un texto suelto ahí se
                                          perdería. Sólo en Productos — en
                                          Creaciones el "nombre" es el archivo
                                          generado y no dice nada. */}
                                          {!esHistorial && (
                                            <div className="absolute inset-x-0 bottom-0 px-1.5 pb-1.5 pt-5 pointer-events-none bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                                              <span className="block text-left text-[11px] font-medium leading-tight text-white line-clamp-2">
                                                {p.name}
                                              </span>
                                              <SkuDelProducto sku={(p as { sku?: string | null }).sku} chico sobreFoto />
                                            </div>
                                          )}
                                          {importing && (
                                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                                            </div>
                                          )}
                                          {selected && (
                                            <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-brand-primary text-white flex items-center justify-center shadow">
                                              <Check className="w-3.5 h-3.5" />
                                            </div>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                      </div>
                    )}

                    {/* Picker de modelos (Modo Diálogo) — mismo estilo que la
                        biblioteca: vive en el panel Archivos, con banner + grilla.
                        El modelo elegido pasa a ser el "Frame inicial". */}
                    {activePanel === "files" && modelPickerOpen && (
                      // En alta de personaje la sección toma TODA la altura
                      // del scroller (flex-col) para que el wrapper de la
                      // vista scrollee él mismo y el footer sticky de
                      // CreateView/UploadFlow quede pegado al fondo del
                      // panel, como en el sheet del catálogo V2.
                      <div
                        className={
                          personajeAddView !== null
                            ? "h-full flex flex-col"
                            : "space-y-3"
                        }
                      >
                        <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-brand-primary/10 px-3 py-2">
                          <span className="text-[12px] font-medium text-brand-black/80">
                            {`Elegí el ${palabraDeModeloActual().singular} que va a hablar`}
                          </span>
                          <button
                            onClick={() => {
                              setModelPickerOpen(false);
                              setActivePanel("ai-scene");
                            }}
                            className="text-[12px] text-brand-primary hover:underline shrink-0"
                          >
                            Cancelar
                          </button>
                        </div>
                        {personajeAddView !== null ? (
                          // Alta de personaje — mismas vistas que el catálogo
                          // de la V2, con el wrapper del sheet: scroll propio
                          // (flex-1 min-h-0) y px-5 porque el footer sticky
                          // de las vistas usa -mx-5/px-5 para el full-bleed.
                          // Sin fila "Volver": CreateView y UploadFlow traen
                          // su propio Cancelar en el footer.
                          <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-1 pb-6 md:pb-0 animate-mode-swap">
                            {personajeAddView === "create" ? (
                              <CreateView
                                userId={currentUserId}
                                companyId={companyId ?? imageGenCompanyId}
                                onBack={() => setPersonajeAddView(null)}
                                onModelCreated={() => {}}
                                onConfirmModel={(m) => {
                                  if (m.thumbnailUrl) {
                                    setModelItems((prev) => [m, ...prev]);
                                  }
                                  setPersonajeAddView(null);
                                }}
                              />
                            ) : (
                              <UploadFlow
                                userId={currentUserId}
                                companyId={companyId ?? imageGenCompanyId}
                                onModelCreated={() => {}}
                                onBack={() => setPersonajeAddView(null)}
                                onUtilizar={(m) => {
                                  if (m.thumbnailUrl) {
                                    setModelItems((prev) => [m, ...prev]);
                                  }
                                  setPersonajeAddView(null);
                                }}
                              />
                            )}
                          </div>
                        ) : modelsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 text-neutral-400 animate-spin" />
                          </div>
                        ) : (
                          <>
                          {modelItems.length === 0 && (
                            <p className="text-[13px] text-neutral-400 px-1 pt-2 text-center">
                              Todavía no tenés {palabraDeModeloActual().plural}.
                              Crealo con IA o subí una foto.
                            </p>
                          )}
                          <div className="grid grid-cols-3 gap-2">
                            <AddOptionCard
                              label="Agregar"
                              compact
                              className="w-full aspect-[3/4]"
                              options={[
                                {
                                  icon: <Sparkles className="w-3.5 h-3.5" />,
                                  label: `Creá tu ${palabraDeModeloActual().singular}`,
                                  onClick: () => setPersonajeAddView("create"),
                                },
                                {
                                  icon: <UserPlus className="w-3.5 h-3.5" />,
                                  label: `Cargá tu propio ${palabraDeModeloActual().singular}`,
                                  onClick: () => setPersonajeAddView("upload"),
                                },
                              ]}
                            />
                            {modelItems.map((m) => {
                              const importing = modelsImportingIds.has(m.id);
                              return (
                                <button
                                  key={`model-${m.id}`}
                                  type="button"
                                  title={m.nombre}
                                  disabled={importing}
                                  onClick={async () => {
                                    if (importing) return;
                                    setModelsImportingIds((prev) => {
                                      const n = new Set(prev);
                                      n.add(m.id);
                                      return n;
                                    });
                                    try {
                                      const assetId =
                                        await importModelToImageAsset(m);
                                      if (assetId) {
                                        // Modo Diálogo: NO lo ponemos crudo como
                                        // Frame inicial. Lo llevamos a Generar
                                        // con IA con el modelo adjunto como
                                        // referencia, apuntando al Frame inicial
                                        // (al "Utilizar" la generada queda ahí).
                                        // Así el usuario lo customiza (ej:
                                        // "vestido de chef") antes de usarlo.
                                        setImageGenAttachedAssetIds([assetId]);
                                        setModelPickerOpen(false);
                                        openImageGenPanel({ kind: "first" });
                                      }
                                    } finally {
                                      setModelsImportingIds((prev) => {
                                        const n = new Set(prev);
                                        n.delete(m.id);
                                        return n;
                                      });
                                    }
                                  }}
                                  className="group relative aspect-[3/4] rounded-md overflow-hidden bg-neutral-100 hover:ring-2 hover:ring-brand-primary/50 transition-all"
                                >
                                  <img
                                    src={m.thumbnailUrl}
                                    alt={m.nombre}
                                    className="w-full h-full object-cover pointer-events-none"
                                  />
                                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1.5 py-1">
                                    <span className="block text-[10px] font-medium text-white truncate">
                                      {m.nombre}
                                    </span>
                                  </div>
                                  {importing && (
                                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          </>
                        )}
                      </div>
                    )}

                    {activePanel === "animacion-grafica" && (
                      <div className="space-y-3" data-clip-ui="true">
                        <p className="text-[13px] text-neutral-500 px-0.5">
                          Creá una animación gráfica de elementos en movimiento:
                          textos, botones, cuadros y formas, animados como
                          quieras. Contá qué se ve, los textos exactos y cómo se
                          mueve; se hace con los colores y las fuentes de tu
                          marca.
                        </p>
                        <textarea
                          value={animGrafPrompt}
                          onChange={(e) => setAnimGrafPrompt(e.target.value)}
                          disabled={animGrafGenerando}
                          placeholder='Ej: placa con "30% OFF" enorme en el rojo de la marca, abajo "solo por 24 hs"; entra deslizándose desde abajo y se desvanece al final.'
                          className="w-full h-32 px-3 py-2.5 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-none placeholder:text-neutral-400 disabled:opacity-60"
                        />
                        <div className="flex items-start justify-between gap-3 px-0.5">
                          <div>
                            <p className="text-[13px] font-medium text-brand-black/80">
                              Fondo transparente
                            </p>
                            <p className="text-[12px] text-neutral-500 mt-0.5">
                              {animGrafTransparente
                                ? "Se superpone al video: placas, títulos, sellos."
                                : "Escena completa con fondo propio: intro, cierre, showcase."}
                            </p>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={animGrafTransparente}
                            disabled={animGrafGenerando}
                            onClick={() => setAnimGrafTransparente((v) => !v)}
                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              animGrafTransparente
                                ? "bg-brand-primary"
                                : "bg-neutral-200"
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                animGrafTransparente
                                  ? "translate-x-5"
                                  : "translate-x-0"
                              }`}
                            />
                          </button>
                        </div>
                        {animGrafError && (
                          <p className="text-[12.5px] text-red-600 px-0.5">
                            {animGrafError}
                          </p>
                        )}
                        <button
                          type="button"
                          disabled={
                            animGrafGenerando || animGrafPrompt.trim().length < 3
                          }
                          onClick={async () => {
                            const prompt = animGrafPrompt.trim();
                            if (prompt.length < 3 || animGrafGenerando) return;
                            const tipo: TipoDeAnimacionParaClip =
                              animGrafTransparente ? "superpuesta" : "escena";
                            setAnimGrafGenerando(true);
                            setAnimGrafError(null);
                            // El clip aparece ya, como "Generando…", donde está
                            // el playhead.
                            const { id } = crearPlaceholderDeAnimacion({
                              nombre: nombreDeAnimacion("", prompt),
                              tipo,
                            });
                            try {
                              const r = await pedirAnimacionPorCodigo({
                                brief: prompt,
                                tipo,
                                formato: formatoDeAnimacion(aspectRatio),
                                marca: marcaDelProyectoParaAnimacion(),
                                assets: [],
                              });
                              if (!r.ok) {
                                descartarPlaceholderDeAnimacion(id);
                                setAnimGrafError(
                                  r.codigo === "no-compila"
                                    ? "No salió una animación válida con ese pedido. Probá describirla más simple. No se cobró."
                                    : r.mensaje,
                                );
                                return;
                              }
                              const { enTimeline } = completarAnimacionGrafica(id, {
                                codigo: r.codigo,
                                resumen: r.resumen,
                                tipo,
                                duracionSeg: r.duracionSeg,
                                nombre: nombreDeAnimacion(r.resumen, prompt),
                              });
                              setAnimGrafPrompt("");
                              if (!enTimeline) {
                                // El clip se borró mientras se generaba: no
                                // hay panel de clip al que ir. Quedó guardada.
                                setAnimGrafError(
                                  "El clip se borró mientras se generaba. La animación quedó guardada en Archivos → Animaciones.",
                                );
                                return;
                              }
                              // Al panel del clip nuevo: versiones, tamaño, Editar.
                              setActivePanel("animation");
                            } finally {
                              setAnimGrafGenerando(false);
                            }
                          }}
                          className="w-full py-2.5 px-5 inline-flex items-center justify-center gap-2 rounded-md text-[13px] font-medium bg-brand-primary text-white hover:bg-brand-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {animGrafGenerando ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Escribiendo la animación… (15 a 40 s)
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generar
                              <CreditBadge
                                credits={CREDITOS_ANIMACION.crear}
                                tone="on-primary"
                                size="sm"
                                className="ml-0.5"
                              />
                            </>
                          )}
                        </button>
                        <p className="text-[11.5px] text-neutral-400 px-0.5">
                          {CREDITOS_ANIMACION.crear} créditos por animación (si
                          falla, no se cobra). Queda en Archivos → Animaciones
                          para volver a usarla. Para retocarla, pedíselo al
                          agente desde el botón Editar del clip
                          ({CREDITOS_ANIMACION.modificar} créditos por cambio).
                        </p>
                      </div>
                    )}

                    {activePanel === "ai-generate" && (
                      <div className="space-y-3">
                        <textarea
                          value={aiPrompt}
                          onChange={(e) => setAiPrompt(e.target.value)}
                          placeholder="Describí el video que querés crear..."
                          className="w-full h-24 px-3 py-2.5 text-[15px] border border-neutral-200 rounded-md bg-surface-card focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-colors resize-none placeholder:text-neutral-400"
                        />
                        <div>
                          <p className="text-[13px] font-medium text-brand-black/80 mb-1.5">
                            Duración
                          </p>
                          <div className="flex gap-2">
                            {(["3", "5", "10"] as const).map((d) => (
                              <button
                                key={d}
                                onClick={() => setAiDuration(d)}
                                className={`flex-1 h-9 rounded-md text-[13px] font-medium transition-colors ${
                                  aiDuration === d
                                    ? "bg-brand-primary text-white shadow-sm"
                                    : "bg-surface-elevated border border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                                }`}
                              >
                                {d}s
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={handleAiGenerate}
                          disabled={!aiPrompt.trim() || aiGenerating}
                          className={`w-full h-8 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                            !aiPrompt.trim() || aiGenerating
                              ? "bg-neutral-100 text-neutral-400 cursor-not-allowed"
                              : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                          }`}
                        >
                          {aiGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4" />
                              Generar video
                            </>
                          )}
                        </button>
                      </div>
                    )}

                    {activePanel === "upload" && (
                      <div className="space-y-3">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <Film className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir video
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              MP4, MOV, WebM
                            </p>
                          </div>
                        </button>
                        <button
                          onClick={() => audioInputRef.current?.click()}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors"
                        >
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <Music className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir audio
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              MP3, WAV, AAC
                            </p>
                          </div>
                        </button>
                        <button className="w-full flex items-center gap-3 px-3 py-3 rounded-md border border-dashed border-neutral-300 hover:border-brand-primary hover:bg-brand-primary/5 transition-colors">
                          <div className="w-9 h-9 rounded-sm bg-neutral-100 flex items-center justify-center shrink-0">
                            <ImageIcon className="w-4 h-4 text-neutral-600" />
                          </div>
                          <div className="text-left">
                            <p className="text-[15px] font-medium text-brand-black/80">
                              Subir imagen
                            </p>
                            <p className="text-[11px] text-neutral-400">
                              JPG, PNG, WebP
                            </p>
                          </div>
                        </button>
                      </div>
                    )}

                    {activePanel === "audio" && (
                      <div className="space-y-3">
                        {[
                          "Música corporativa",
                          "Lofi tranquilo",
                          "Energía pop",
                          "Cinematic épico",
                          "Ambiente natural",
                        ].map((name, i) => (
                          <button
                            key={name}
                            onClick={() => {
                              const audioTrack = getTrackForClipType("audio");
                              const newClip: TimelineClip = {
                                id: generateId(),
                                type: "audio",
                                name,
                                startTime: 0,
                                trackIndex: capaNuevaPara(clips, "audio"),
                                duration: [8, 12, 6, 15, 10][i],
                                color: audioTrack.color,
                                volume: 1,
                              };
                              setClips((prev) => [...prev, newClip]);
                              setSelectedClipId(newClip.id);
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
                              <Music className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div className="text-left flex-1 min-w-0">
                              <p className="text-[15px] font-medium text-brand-black/80 truncate">
                                {name}
                              </p>
                              <p className="text-[11px] text-neutral-400">
                                {[8, 12, 6, 15, 10][i]}s
                              </p>
                            </div>
                            <Plus className="w-4 h-4 text-neutral-400 shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}

                    {activePanel === "text" &&
                      (() => {
                        // When a text clip is selected, the panel edits THAT clip
                        // live (changes flow back to the clip via setClips). When
                        // nothing is selected (or non-text clip), the panel composes
                        // a NEW clip from `textDraft` and appends it on submit.
                        const editingTextClip =
                          selectedClip && selectedClip.type === "text"
                            ? selectedClip
                            : null;
                        const cur = editingTextClip
                          ? {
                              text: editingTextClip.text ?? "",
                              fontFamily:
                                editingTextClip.fontFamily ??
                                DEFAULT_TEXT_CLIP.fontFamily,
                              fontSizePct:
                                editingTextClip.fontSizePct ??
                                DEFAULT_TEXT_CLIP.fontSizePct,
                              fontWeight: (editingTextClip.fontWeight ??
                                DEFAULT_TEXT_CLIP.fontWeight) as
                                | "normal"
                                | "bold",
                              fontStyle: (editingTextClip.fontStyle ??
                                DEFAULT_TEXT_CLIP.fontStyle) as
                                | "normal"
                                | "italic",
                              textColor:
                                editingTextClip.textColor ??
                                DEFAULT_TEXT_CLIP.textColor,
                              textAlign: (editingTextClip.textAlign ??
                                DEFAULT_TEXT_CLIP.textAlign) as
                                | "left"
                                | "center"
                                | "right",
                              enterAnimation:
                                editingTextClip.enterAnimation ?? "none",
                              enterDurationSec:
                                editingTextClip.enterDurationSec ?? 0.5,
                              exitAnimation:
                                editingTextClip.exitAnimation ?? "none",
                              exitDurationSec:
                                editingTextClip.exitDurationSec ?? 0.5,
                            }
                          : textDraft;
                        const patch = (p: Partial<typeof cur>) => {
                          if (editingTextClip) {
                            const namePatch =
                              p.text !== undefined
                                ? {
                                    name:
                                      p.text.length > 20
                                        ? p.text.slice(0, 17) + "…"
                                        : p.text || "Texto",
                                  }
                                : {};
                            setClips((prev) =>
                              patchClipsWithSubtitleSync(
                                prev,
                                editingTextClip.id,
                                { ...p, ...namePatch } as Partial<TimelineClip>,
                              ),
                            );
                          } else {
                            setTextDraft((d) => ({ ...d, ...p }));
                          }
                        };
                        // Sólo se usa al CREAR: editando, los cambios se
                        // aplican en vivo y el panel no tiene botón.
                        const onSubmit = () => {
                          const t = (cur.text || "").trim();
                          if (!t) return;
                          addTextClip({
                            text: t,
                            fontFamily: cur.fontFamily,
                            fontSizePct: cur.fontSizePct,
                            fontWeight: cur.fontWeight,
                            fontStyle: cur.fontStyle,
                            textColor: cur.textColor,
                            textAlign: cur.textAlign,
                            enterAnimation: cur.enterAnimation,
                            enterDurationSec: cur.enterDurationSec,
                            exitAnimation: cur.exitAnimation,
                            exitDurationSec: cur.exitDurationSec,
                          });
                          // Reset draft so the next time the user opens the panel
                          // they start fresh, not with the previous content.
                          setTextDraft((d) => ({ ...d, text: "" }));
                        };
                        const canSubmit = (cur.text || "").trim().length > 0;
                        return (
                          /* Columna, como los paneles de IA: el cuerpo
                             scrollea y el CTA vive en un pie fijo con su línea
                             divisoria. Antes el botón era el último elemento
                             del cuerpo, así que quedaba a mitad de la hoja en
                             el teléfono (y había que scrollear hasta el fondo
                             para encontrarlo). El `p-3` que le ponía el
                             scroller compartido se mudó al cuerpo, para que la
                             línea del pie llegue de borde a borde. */
                          <div className="flex flex-col h-full min-h-0">
                            <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
                            <p className="text-[14px] font-semibold text-brand-black/90 px-1">
                              {editingTextClip
                                ? "Editar texto"
                                : "Añadir texto"}
                            </p>

                            {/* Subtitle-group scope toggle. Only meaningful when the
                        clip was created as part of a "Generar subtítulos"
                        batch (isSubtitle + subtitleGroupId). Off (default)
                        means style edits propagate to every sibling in the
                        group; on means edits affect only this clip. */}
                            {editingTextClip &&
                              editingTextClip.isSubtitle &&
                              editingTextClip.subtitleGroupId && (
                                <label className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-md bg-black/[0.04] hover:bg-black/[0.06] transition-colors cursor-pointer">
                                  <span className="text-[13px] font-semibold text-neutral-700">
                                    Editar solo este subtítulo
                                  </span>
                                  <input
                                    type="checkbox"
                                    className="h-4 w-4 accent-brand-primary cursor-pointer"
                                    checked={
                                      !!editingTextClip.subtitleStyleOverride
                                    }
                                    onChange={(e) => {
                                      const next = e.target.checked;
                                      setClips((prev) =>
                                        prev.map((c) =>
                                          c.id === editingTextClip.id
                                            ? {
                                                ...c,
                                                subtitleStyleOverride: next,
                                              }
                                            : c,
                                        ),
                                      );
                                    }}
                                  />
                                </label>
                              )}

                            {/* Live preview */}
                            <div
                              className="rounded-md px-3 py-6 flex items-center justify-center min-h-[80px] overflow-hidden transition-colors"
                              style={{
                                textAlign: cur.textAlign,
                                // Gris intermedio de arranque (el texto es
                                // negro por defecto) y, si el usuario elige un
                                // color claro, un oscuro que no llega a negro
                                // pleno — así la vista previa nunca se come el
                                // texto y tampoco simula un fondo que el video
                                // no tiene.
                                backgroundColor: esColorClaro(cur.textColor)
                                  ? "#1B1F27"
                                  : "#D8DBE0",
                              }}
                            >
                              <span
                                className="leading-tight whitespace-pre-wrap break-words max-w-full"
                                style={{
                                  fontFamily: pilaDeFuenteComoEnElRender(
                                    cur.fontFamily,
                                    customFontFamilies,
                                  ),
                                  fontSize: `${Math.max(12, Math.min(48, cur.fontSizePct * 1.6))}px`,
                                  fontWeight:
                                    cur.fontWeight === "bold" ? 700 : 400,
                                  fontStyle: cur.fontStyle,
                                  color: cur.textColor,
                                }}
                              >
                                {cur.text ||
                                  (editingTextClip ? "" : "Vista previa")}
                              </span>
                            </div>

                            {/* Text content */}
                            <div>
                              <label className="block text-[14px] font-semibold text-brand-black/90 mb-1.5">
                                Texto
                              </label>
                              <textarea
                                value={cur.text}
                                onChange={(e) =>
                                  patch({ text: e.target.value })
                                }
                                placeholder="Escribí tu texto..."
                                rows={3}
                                maxLength={500}
                                className="w-full text-[14px] text-neutral-900 resize-none border border-neutral-200 rounded-md px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-brand-primary"
                              />
                            </div>

                            {/* Font */}
                            <div>
                              <label className="block text-[14px] font-semibold text-brand-black/90 mb-1.5">
                                Fuente
                              </label>
                              {/* ARRIBA del dropdown y no adentro: se ve sin
                              abrir la lista, y la fuente que sube queda elegida
                              y listada en "Fuentes de la marca" la próxima vez
                              que la abra. */}
                              <div className="mb-2">
                                <SubirTipografia
                                  onSubir={subirTipografiaPropia}
                                  onSubida={(fam) =>
                                    patch({ fontFamily: fam })
                                  }
                                />
                              </div>
                              <FontPicker
                                value={cur.fontFamily}
                                onChange={(fam) => patch({ fontFamily: fam })}
                                triggerClassName="w-full h-9 flex items-center justify-between gap-2 text-[14px] text-neutral-900 border border-neutral-200 rounded-md px-2.5 py-2 hover:bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-brand-primary bg-white transition-colors"
                                panelAlign="left"
                              />
                            </div>

                            {/* Dos filas: arriba el TAMAÑO y el COLOR —lo que se
                        toca primero al escribir— y abajo negrita, cursiva y
                        alineación. En una sola fila el color terminaba
                        siempre solo en el renglón de abajo, lejos del
                        control con el que se usa. */}
                            <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              {/* Tamaño — pill con − [n] + */}
                              <div className="h-9 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() =>
                                    patch({
                                      fontSizePct: Math.max(
                                        2,
                                        Math.round(
                                          (cur.fontSizePct - 0.5) * 2,
                                        ) / 2,
                                      ),
                                    })
                                  }
                                  className="h-9 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                  title="Reducir tamaño"
                                >
                                  −
                                </button>
                                <NumberField
                                  value={cur.fontSizePct}
                                  min={2}
                                  max={30}
                                  step={0.5}
                                  decimals={1}
                                  onCommit={(n) => patch({ fontSizePct: n })}
                                  className="w-10 h-9 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                  ariaLabel="Tamaño"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    patch({
                                      fontSizePct: Math.min(
                                        30,
                                        Math.round(
                                          (cur.fontSizePct + 0.5) * 2,
                                        ) / 2,
                                      ),
                                    })
                                  }
                                  className="h-9 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                  title="Aumentar tamaño"
                                >
                                  +
                                </button>
                              </div>

                              <div className="w-px h-6 bg-neutral-200 mx-0.5" />

                              {/* Color — el picker compartido (fila de marca +
                          presets + hex a mano), acá al lado del tamaño. */}
                              <ColorSwatchPicker
                                value={cur.textColor}
                                onChange={(c) => patch({ textColor: c })}
                                align="right"
                                ariaLabel="Color del texto"
                                triggerLabel="Color"
                              />
                            </div>

                            <div className="flex items-center gap-1.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() =>
                                  patch({
                                    fontWeight:
                                      cur.fontWeight === "bold"
                                        ? "normal"
                                        : "bold",
                                  })
                                }
                                className={`w-9 h-9 rounded-md border text-[14px] font-bold transition-colors ${
                                  cur.fontWeight === "bold"
                                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary border-transparent"
                                    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                }`}
                                title="Negrita"
                              >
                                B
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  patch({
                                    fontStyle:
                                      cur.fontStyle === "italic"
                                        ? "normal"
                                        : "italic",
                                  })
                                }
                                className={`w-9 h-9 rounded-md border text-[14px] italic transition-colors ${
                                  cur.fontStyle === "italic"
                                    ? "bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary border-transparent"
                                    : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                }`}
                                title="Cursiva"
                              >
                                I
                              </button>
                              <div className="w-px h-6 bg-neutral-200 mx-0.5" />
                              {(["left", "center", "right"] as const).map(
                                (a) => (
                                  <button
                                    key={a}
                                    type="button"
                                    onClick={() => patch({ textAlign: a })}
                                    className={`w-9 h-9 rounded-md border inline-flex items-center justify-center transition-colors ${
                                      cur.textAlign === a
                                        ? "bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary border-transparent"
                                        : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                                    }`}
                                    title={
                                      a === "left"
                                        ? "Izquierda"
                                        : a === "center"
                                          ? "Centrado"
                                          : "Derecha"
                                    }
                                  >
                                    {a === "left" && (
                                      <AlignLeft className="w-4 h-4" />
                                    )}
                                    {a === "center" && (
                                      <AlignCenter className="w-4 h-4" />
                                    )}
                                    {a === "right" && (
                                      <AlignRight className="w-4 h-4" />
                                    )}
                                  </button>
                                ),
                              )}

                            </div>
                            </div>

                            {/* Animaciones de entrada y salida del texto: los
                            mismos selectores que Elementos e imágenes, con los
                            presets de texto (typewriter, cascadas) además de
                            los comunes. Se sacó el 16/09 a pedido
                            (ADR-2026-09-16-el-texto-no-tiene-animaciones-de-entrada-y-salida)
                            y vuelve a pedido
                            (ADR-2026-09-28-vuelven-las-animaciones-del-texto). */}
                            <div className="pt-3 mt-1 border-t border-neutral-200/70 space-y-2">
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                                Animaciones
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {(
                                  [
                                    {
                                      kind: "enter" as const,
                                      label: "Entrada",
                                      valor: cur.enterAnimation,
                                      dur: cur.enterDurationSec,
                                      campo: "enterAnimation" as const,
                                      campoDur: "enterDurationSec" as const,
                                    },
                                    {
                                      kind: "exit" as const,
                                      label: "Salida",
                                      valor: cur.exitAnimation,
                                      dur: cur.exitDurationSec,
                                      campo: "exitAnimation" as const,
                                      campoDur: "exitDurationSec" as const,
                                    },
                                  ] as const
                                ).map((lado) => (
                                  <div key={lado.kind} className="space-y-1.5">
                                    <MotionPicker
                                      label={lado.label}
                                      kind={lado.kind}
                                      scope="text"
                                      value={lado.valor}
                                      onChange={(id) =>
                                        patch({ [lado.campo]: id })
                                      }
                                    />
                                    {lado.valor !== "none" && (
                                      <div className="h-9 flex items-center gap-1.5 px-2 rounded-md border border-black/[0.08] bg-surface-card">
                                        <NumberField
                                          value={lado.dur}
                                          min={0.1}
                                          max={5}
                                          step={0.1}
                                          decimals={1}
                                          onCommit={(n) =>
                                            patch({ [lado.campoDur]: n })
                                          }
                                          className="campo-numerico-del-editor flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-brand-black/90 bg-transparent focus:outline-none"
                                          ariaLabel={`Duración ${lado.label.toLowerCase()}`}
                                        />
                                        <span className="text-[12px] text-brand-black/40">
                                          seg
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Duplicar, eliminar y compañía flotan al costado
                            del preview; el largo se ajusta estirando el clip en
                            la timeline. */}

                            </div>

                            {/* CTA — sólo al CREAR. Editando un clip no hay
                            nada que confirmar: los cambios ya están aplicados
                            en vivo, y el botón "Listo" no hacía más que cerrar
                            el panel, que es lo que hace la X.

                            El pie es el MISMO que el de Audio con IA y Video
                            con IA: `shrink-0 px-3 py-3 border-t`, con el botón
                            a todo el ancho y a 13px. */}
                            {!editingTextClip && (
                              <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
                                <button
                                  type="button"
                                  onClick={onSubmit}
                                  disabled={!canSubmit}
                                  className={`w-full h-10 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                    canSubmit
                                      ? "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                      : "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                  }`}
                                >
                                  <Plus className="w-4 h-4" />
                                  Añadir texto
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {activePanel === "agent-upload" && agentUploadRequest && (
                      <div className="flex flex-col h-full">
                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto space-y-4">
                          {/* Agent message */}
                          <div className="px-3 py-3 rounded-md bg-brand-primary/5 border border-brand-primary/15">
                            <div className="flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-brand-primary/15 flex items-center justify-center shrink-0">
                                <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                              </div>
                              <p className="text-[14px] leading-relaxed text-brand-black/85 whitespace-pre-wrap">
                                {agentUploadRequest.message}
                              </p>
                            </div>
                          </div>

                          {/* Drop / browse area */}
                          <div
                            onClick={() => agentUploadInputRef.current?.click()}
                            onDragOver={(e) => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              const files = e.dataTransfer.files;
                              if (files && files.length > 0)
                                handleAgentUploadFiles(files);
                            }}
                            className="group cursor-pointer rounded-md border-2 border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03] transition-colors px-4 py-8 flex flex-col items-center justify-center text-center"
                          >
                            <div className="w-11 h-11 rounded-full bg-neutral-100 group-hover:bg-brand-primary/10 flex items-center justify-center mb-2.5 transition-colors">
                              <Upload className="w-5 h-5 text-neutral-500 group-hover:text-brand-primary transition-colors" />
                            </div>
                            <p className="text-[14px] font-medium text-brand-black/80">
                              Hacé click para subir
                            </p>
                            <p className="text-[12px] text-neutral-400 mt-0.5">
                              o arrastrá los archivos acá
                            </p>
                            <p className="text-[11px] text-neutral-400 mt-2">
                              {agentUploadRequest.accept === "video/*"
                                ? "Solo videos"
                                : agentUploadRequest.accept ===
                                    "image/*,video/*"
                                  ? "Imágenes o videos"
                                  : "Solo imágenes"}
                            </p>
                          </div>

                          {/* Uploaded files list */}
                          {agentUploadedAssets.length > 0 && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1 mb-2">
                                {agentUploadedAssets.length}{" "}
                                {agentUploadedAssets.length === 1
                                  ? "archivo subido"
                                  : "archivos subidos"}
                              </p>
                              <div className="space-y-2">
                                {agentUploadedAssets.map((asset) => (
                                  <div
                                    key={asset.id}
                                    className="group flex items-center gap-3 px-2.5 py-2 rounded-md bg-surface-card border border-black/[0.06] shadow-sm"
                                  >
                                    <div className="w-11 h-11 rounded-sm bg-neutral-100 overflow-hidden flex items-center justify-center shrink-0">
                                      {asset.url &&
                                      /\.(mp4|mov|webm)$/i.test(asset.name) ? (
                                        <video
                                          src={asset.url}
                                          className="w-full h-full object-cover"
                                          muted
                                          playsInline
                                          preload="metadata"
                                        />
                                      ) : asset.url ? (
                                        <img
                                          src={asset.url}
                                          alt={asset.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <ImageIcon className="w-4 h-4 text-neutral-400" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[13px] font-medium text-brand-black/85 truncate">
                                        {asset.name}
                                      </p>
                                      <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                                        <Check className="w-3 h-3" /> Subido
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeAgentUploadedAsset(asset.id)
                                      }
                                      className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-error hover:bg-error-soft transition-colors shrink-0"
                                      title="Quitar de la selección"
                                    >
                                      <Plus className="w-4 h-4 rotate-45" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {agentUploadInProgress && (
                            <div className="flex items-center justify-center gap-2 py-2 text-[12px] text-neutral-500">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {agentUploadRequest.procesarComoPersonaje
                                ? `Procesando ${palabraDeModeloActual().singular}… (~30-60s)`
                                : "Subiendo..."}
                            </div>
                          )}
                        </div>

                        {/* Sticky confirm button */}
                        <div className="pt-3 mt-3 border-t border-black/[0.06]">
                          <button
                            type="button"
                            onClick={confirmAgentUpload}
                            disabled={
                              agentUploadedAssets.length === 0 ||
                              agentUploadInProgress
                            }
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary/90 disabled:bg-neutral-200 disabled:text-neutral-400 disabled:cursor-not-allowed text-white rounded-md font-medium text-[14px] shadow-sm transition-colors"
                          >
                            {agentUploadRequest.buttonLabel}
                          </button>
                        </div>
                      </div>
                    )}

                    {activePanel === "brand" && (
                      <div className="space-y-6">
                        {!brandProfile ? (
                          <div className="rounded-md border border-dashed border-black/[0.12] px-4 py-8 text-center">
                            <Palette className="w-6 h-6 text-brand-black/30 mx-auto mb-2" />
                            <p className="text-[13px] text-brand-black/60">
                              Este proyecto todavía no tiene una marca asociada.
                            </p>
                          </div>
                        ) : (
                          <>
                            {/* El panel abre en LOGOS y sin selector de pestañas:
                          es lo único que se usa desde el editor (la identidad
                          se mira y se edita en Configuración → Mis Marcas). La
                          vista "Marca" sigue existiendo abajo porque el agente
                          la abre por su cuenta con `open_brand_panel`, que
                          setea `brandTab`. */}
                            {/* ── Marca: vista unificada read-only (Memoria +
                          Tipografía + Colores) con el mismo look que
                          Configuración → Mis Marcas. La identidad se edita
                          desde Configuración; acá solo se muestra y alimenta
                          los selectores de color del editor. */}
                            {brandTab === "memoria" && (
                              <section className="space-y-3">
                                {/* Memoria de la IA */}
                                <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                  <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-2">
                                    Memoria de la IA
                                  </div>
                                  {brandProfile.context ? (
                                    <div className="relative text-[13px] text-brand-black/80">
                                      <div
                                        className={
                                          brandMemoriaExpanded
                                            ? "pb-12"
                                            : "line-clamp-[8] pb-10"
                                        }
                                      >
                                        {renderMarkdown(brandProfile.context)}
                                      </div>
                                      {!brandMemoriaExpanded && (
                                        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-surface-elevated via-surface-elevated/80 to-transparent" />
                                      )}
                                      <div className="absolute bottom-0 right-0 z-10">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setBrandMemoriaExpanded((v) => !v)
                                          }
                                          className="h-8 px-3.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors"
                                        >
                                          {brandMemoriaExpanded
                                            ? "Ver menos"
                                            : "Ver más"}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-[13px] text-neutral-500">
                                      No hay información disponible todavía.
                                      Completá la descripción de marca para
                                      generar el análisis.
                                    </div>
                                  )}
                                </div>
                                {/* Tipografía */}
                                {(() => {
                                  const f = (brandProfile.fonts ||
                                    {}) as Record<string, unknown>;
                                  const suggested: string[] = Array.isArray(
                                    (f as any).suggested,
                                  )
                                    ? ((f as any).suggested as unknown[]).filter(
                                        (x): x is string => typeof x === "string",
                                      )
                                    : (Array.from(
                                        new Set(
                                          Object.values(f).filter(
                                            (v): v is string =>
                                              typeof v === "string",
                                          ),
                                        ),
                                      ) as string[]);
                                  const fontStyle =
                                    typeof (f as any).style === "string"
                                      ? ((f as any).style as string)
                                      : "";
                                  if (suggested.length === 0) return null;
                                  return (
                                    <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                      <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
                                        Tipografía
                                      </div>
                                      <div className="grid grid-cols-2 gap-3">
                                        {suggested
                                          .slice(0, 2)
                                          .map((font, idx) => (
                                            <div
                                              key={`${font}-${idx}`}
                                              className="flex flex-col items-center justify-center text-center"
                                            >
                                              <div
                                                className="text-[40px] sm:text-[48px] leading-none text-brand-black/90 mb-1"
                                                style={{
                                                  fontFamily: fontStackFor(font),
                                                }}
                                              >
                                                Aa
                                              </div>
                                              <div
                                                className="text-[13px] text-brand-black/80 truncate w-full px-1"
                                                style={{
                                                  fontFamily: fontStackFor(font),
                                                }}
                                              >
                                                {font}
                                              </div>
                                              <div className="text-[10px] text-neutral-500 uppercase tracking-wide mt-1">
                                                {idx === 0
                                                  ? "Principal"
                                                  : "Secundaria"}
                                              </div>
                                            </div>
                                          ))}
                                      </div>
                                      {fontStyle && (
                                        <p className="text-[11px] text-neutral-500 italic pt-3 mt-3 border-t border-neutral-200/70">
                                          {fontStyle}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })()}
                                {/* Paleta de colores */}
                                {brandProfile.colors.length > 0 && (
                                  <div className="bg-surface-elevated border border-neutral-200 rounded-lg p-4">
                                    <div className="text-[11px] font-medium text-neutral-500 uppercase tracking-wider mb-3">
                                      Paleta de colores
                                    </div>
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                      {brandProfile.colors
                                        .slice(0, 6)
                                        .map((color, idx) => (
                                          <div
                                            key={`${color}-${idx}`}
                                            className="flex flex-col items-center gap-1.5"
                                          >
                                            <div
                                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-neutral-200 shadow-sm"
                                              style={{ backgroundColor: color }}
                                              title={color}
                                            />
                                            <div className="text-[9px] sm:text-[10px] text-neutral-500 font-mono">
                                              {color}
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </section>
                            )}
                            {/* ── Carpetas de la marca ──────────────────────────────
                            Lo que la marca ya tiene cargado en Atlas, cada cosa
                            en su carpeta. Las pastillas son las MISMAS que las
                            del panel de Archivos (`clasesDePastilla`), para que
                            las dos pantallas se naveguen igual. Tocar una
                            imagen la mete en el video como capa, que es lo que
                            ya hacía el logo principal.
                            Ver ADR-2026-09-16-las-carpetas-de-la-marca. */}
                            {brandTab === "imagenes" && (
                              <section className="space-y-3">
                                <div className="flex flex-wrap items-center gap-1">
                                  {CARPETAS_DE_MARCA.map(({ id, label }) => {
                                    const CIcon =
                                      id === "logos"
                                        ? ImageIcon
                                        : id === "productos"
                                          ? Package
                                          : id === "personajes"
                                            ? Users
                                            : Palette;
                                    return (
                                      <button
                                        key={id}
                                        type="button"
                                        onClick={() => setCarpetaDeMarca(id)}
                                        className={clasesDePastilla(
                                          carpetaDeMarca === id,
                                        )}
                                      >
                                        <CIcon className="w-3.5 h-3.5" />
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {carpetaDeMarca === "logos" && (
                                  <>
                                {/* Subir un logo lo guarda como brand_asset tipo
                            'logo' — NO toca el logo principal de la marca
                            (logo_url), que solo se edita desde Configuración. */}
                                <input
                                  ref={brandLogoInputRef}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = "";
                                    if (file)
                                      await handleBrandAssetUpload(file, "logo");
                                  }}
                                />
                                {(() => {
                                  const logoItems = brandAssets.filter(
                                    (a) => a.tipo === "logo",
                                  );
                                  const isUploading =
                                    brandAssetsUploading === "logo";
                                  // Checker background so transparent logos read
                                  // as transparent (most logos are PNG/SVG).
                                  const checker: React.CSSProperties = {
                                    backgroundImage:
                                      "linear-gradient(45deg, rgba(0,0,0,0.04) 25%, transparent 25%), linear-gradient(-45deg, rgba(0,0,0,0.04) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.04) 75%), linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.04) 75%)",
                                    backgroundSize: "12px 12px",
                                    backgroundPosition:
                                      "0 0, 0 6px, 6px -6px, -6px 0",
                                  };
                                  const hasNoLogos =
                                    logoItems.length === 0 &&
                                    !brandProfile.logoUrl;
                                  return (
                                    <div className="grid grid-cols-3 gap-2">
                                      {/* Upload tile */}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          brandLogoInputRef.current?.click()
                                        }
                                        disabled={isUploading}
                                        className="aspect-square rounded-md border border-dashed border-black/[0.16] bg-neutral-50 hover:bg-neutral-100 hover:border-black/[0.28] flex flex-col items-center justify-center gap-1 text-neutral-500 transition-colors disabled:opacity-60"
                                      >
                                        {isUploading ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <>
                                            <Plus className="w-4 h-4" />
                                            <span className="text-[11px]">
                                              Subir
                                            </span>
                                          </>
                                        )}
                                      </button>
                                      {/* Logo principal (read-only): viene de
                                  brand_profiles.logo_url; sin botón de borrar
                                  porque solo se cambia desde Configuración. */}
                                      {brandProfile.logoUrl && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            insertLogoOverlay(
                                              brandProfile.logoUrl!,
                                              "Logo",
                                            )
                                          }
                                          className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06] hover:ring-brand-primary/40 transition-shadow"
                                          title="Agregar el logo al video"
                                        >
                                          <img
                                            src={brandProfile.logoUrl}
                                            alt="Logo principal"
                                            className="w-full h-full object-contain p-2 pointer-events-none"
                                            style={checker}
                                          />
                                          <span className="absolute bottom-1 left-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-black/55 text-white">
                                            Principal
                                          </span>
                                          <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-medium">
                                            Añadir al video
                                          </span>
                                        </button>
                                      )}
                                      {/* Logos extra subidos (borrables) */}
                                      {brandAssetsLoading && hasNoLogos ? (
                                        <div className="col-span-2 flex items-center justify-center text-neutral-400">
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        </div>
                                      ) : hasNoLogos ? (
                                        <p className="col-span-2 self-center text-[12px] text-neutral-400 px-1">
                                          Subí los logos de tu marca.
                                        </p>
                                      ) : (
                                        logoItems.map((item) => (
                                          <div
                                            key={item.id}
                                            className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06]"
                                            title={item.name || "Logo"}
                                          >
                                            {item.url ? (
                                              <img
                                                src={item.url}
                                                alt={item.name || "Logo"}
                                                className="w-full h-full object-contain p-2 pointer-events-none"
                                                style={checker}
                                              />
                                            ) : (
                                              <div className="absolute inset-0 flex items-center justify-center">
                                                <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                              </div>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleBrandAssetDelete(item)
                                              }
                                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                              title="Eliminar"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  );
                                })()}
                                                                </>
                                )}
                                {/* Productos, Personajes y Marcas comparten
                                grilla: cambia de dónde sale la lista y qué
                                importador trae la imagen al proyecto. Todas
                                entran como asset del proyecto (no como URL
                                firmada suelta), así el clip sobrevive a la
                                recarga. */}
                                {carpetaDeMarca !== "logos" &&
                                  (() => {
                                    type ItemDeMarca = {
                                      key: string;
                                      url?: string;
                                      name: string;
                                      sku?: string | null;
                                      agregar: () => Promise<void>;
                                    };
                                    let items: ItemDeMarca[] = [];
                                    let cargando = false;
                                    let vacio = "";
                                    if (carpetaDeMarca === "productos") {
                                      cargando = filesProductsLoading;
                                      vacio =
                                        "Esta marca todavía no tiene productos cargados en Atlas.";
                                      items = filesProductItems.map((pr) => ({
                                        key: `producto-${pr.id}`,
                                        url: pr.imageUrl,
                                        name: pr.name || "Producto",
                                        sku: pr.sku ?? null,
                                        agregar: async () => {
                                          const assetId =
                                            await importProductToImageAsset(pr);
                                          if (assetId)
                                            await agregarImagenAlVideo(
                                              assetId,
                                              pr.name || "Producto",
                                            );
                                        },
                                      }));
                                    } else if (
                                      carpetaDeMarca === "personajes"
                                    ) {
                                      cargando = modelsLoading;
                                      vacio =
                                        `Todavía no hay ${palabraDeModeloActual().plural} cargados en la cuenta.`;
                                      items = modelItems.map((m) => ({
                                        key: `personaje-${m.id}`,
                                        url: m.thumbUrl || m.thumbnailUrl,
                                        name: m.nombre || palabraDeModeloActual().titulo,
                                        agregar: async () => {
                                          const assetId =
                                            await importModelToImageAsset(m);
                                          if (assetId)
                                            await agregarImagenAlVideo(
                                              assetId,
                                              m.nombre || palabraDeModeloActual().titulo,
                                            );
                                        },
                                      }));
                                    } else {
                                      cargando = moodboardCargando;
                                      vacio =
                                        "Esta marca todavía no tiene estilos en Atlas.";
                                      // El rótulo de cada miniatura es el
                                      // nombre del TABLERO, no el del archivo
                                      // (que es un timestamp): es lo único que
                                      // dice de dónde salió la referencia.
                                      items = imagenesDeMoodboard.map((img) => ({
                                        key: `moodboard-${img.id}`,
                                        url: img.url,
                                        name: img.moodboard,
                                        agregar: async () => {
                                          const assetId =
                                            await importarImagenDeMarca({
                                              id: img.id,
                                              url: img.url,
                                              nombre: img.nombreArchivo,
                                            });
                                          if (assetId)
                                            await agregarImagenAlVideo(
                                              assetId,
                                              img.moodboard,
                                            );
                                        },
                                      }));
                                    }
                                    if (cargando && items.length === 0)
                                      return (
                                        <ProductGridSkeleton
                                          count={6}
                                          gridClassName="grid grid-cols-3 gap-2"
                                          tileClassName="rounded-md"
                                        />
                                      );
                                    if (items.length === 0)
                                      return (
                                        <p className="text-[13px] text-neutral-400 px-1 py-6 text-center">
                                          {vacio}
                                        </p>
                                      );
                                    return (
                                      <div className="grid grid-cols-3 gap-2">
                                        {items.map((it) => {
                                          const agregando =
                                            agregandoDeLaMarca === it.key;
                                          return (
                                            <button
                                              key={it.key}
                                              type="button"
                                              title={it.name}
                                              disabled={agregando}
                                              onClick={async () => {
                                                setAgregandoDeLaMarca(it.key);
                                                try {
                                                  await it.agregar();
                                                } finally {
                                                  setAgregandoDeLaMarca(null);
                                                }
                                              }}
                                              className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 ring-1 ring-black/[0.06] hover:ring-brand-primary/40 transition-shadow disabled:opacity-60"
                                            >
                                              {it.url ? (
                                                <CachedImg
                                                  src={it.url}
                                                  alt={it.name}
                                                  className="w-full h-full object-cover pointer-events-none"
                                                />
                                              ) : (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                  <Loader2 className="w-4 h-4 text-neutral-400 animate-spin" />
                                                </div>
                                              )}
                                              <div className="absolute inset-x-0 bottom-0 px-1.5 pb-1.5 pt-5 pointer-events-none bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                                                <span className="block text-left text-[11px] font-medium leading-tight text-white line-clamp-2">
                                                  {it.name}
                                                </span>
                                                <SkuDelProducto sku={it.sku} chico sobreFoto />
                                              </div>
                                              {agregando ? (
                                                <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                                                </span>
                                              ) : (
                                                <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-[11px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                                  Añadir al video
                                                </span>
                                              )}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                              </section>
                            )}
                          </>
                        )}
                      </div>
                    )}


                    {activePanel === "transitions" &&
                      (() => {
                        const targetClip = transitionTargetClipId
                          ? clips.find((c) => c.id === transitionTargetClipId)
                          : null;
                        const currentTransition = targetClip?.transitionIn;
                        // Catalogue. Adding a new transition = add an entry here +
                        // a case in computeIncoming/OutgoingTransitionState.
                        const transitions: Array<{
                          type: TransitionType;
                          label: string;
                        }> = [
                          { type: "crossfade", label: "Cross-fade" },
                          { type: "fade-to-black", label: "Fundido a negro" },
                          { type: "slide", label: "Deslizar derecha" },
                          { type: "slide-left", label: "Deslizar izquierda" },
                          { type: "slide-up", label: "Deslizar arriba" },
                          { type: "slide-down", label: "Deslizar abajo" },
                          { type: "zoom", label: "Zoom" },
                          {
                            type: "stretch-horizontal",
                            label: "Estirar horizontal",
                          },
                          {
                            type: "stretch-vertical",
                            label: "Estirar vertical",
                          },
                          { type: "reveal", label: "Revelación" },
                          { type: "diapositivas", label: "Diapositivas" },
                          { type: "light-leak", label: "Flash" },
                          { type: "plastic", label: "Plastico" },
                          { type: "distorsion", label: "Distorsion" },
                          { type: "vhs", label: "VHS" },
                          { type: "vintage", label: "Vintage" },
                          { type: "glitch", label: "Glitch" },
                        ];
                        // Per-type default durations. Most transitions look
                        // sharp at 0.3s (viral whip range) but some — like
                        // zoom-shake — need more time to play out their staged
                        // phases (blur build → tremor → zoom punch → settle).
                        // Switching types resets to that type's default so the
                        // user lands on a sensible starting point; tweaking the
                        // slider after preserves their value while they stay
                        // on the same type.
                        const defaultDurationFor = (
                          type: TransitionType,
                        ): number => {
                          // `diapositivas` has 3 staged phases — at 0.3s each
                          // phase is too short to register, so default longer.
                          if (type === "diapositivas") return 0.8;
                          // Overlay transitions need the full overlay video to
                          // play through, which is typically ~0.5s for light
                          // leaks. Shorter and the bright peak gets cut off.
                          if (type === "light-leak") return 0.5;
                          if (type === "plastic") return 0.6;
                          if (type === "distorsion") return 0.5;
                          // VHS / Vintage source clips are longer and need the
                          // full play-through to read as the intended effect.
                          if (type === "vhs") return 1.0;
                          if (type === "vintage") return 1.0;
                          return 0.3;
                        };
                        const apply = (type: TransitionType) => {
                          if (!targetClip) return;
                          const sameType = currentTransition?.type === type;
                          const dur = sameType
                            ? currentTransition!.durationSec
                            : defaultDurationFor(type);
                          setClipTransition(targetClip.id, type, dur);
                        };
                        const updateDuration = (newDur: number) => {
                          if (!targetClip || !currentTransition) return;
                          setClipTransition(
                            targetClip.id,
                            currentTransition.type,
                            newDur,
                          );
                        };
                        const remove = () => {
                          if (!targetClip) return;
                          removeClipTransition(targetClip.id);
                        };
                        return (
                          // The transitions panel manages its own padding (parent
                          // `p-3` is suppressed for activePanel==='transitions') so
                          // the footer can sit flush against the panel bottom edge
                          // without sticky/negative-margin tricks. Layout:
                          //   • flex-col filling the panel height
                          //   • scrollable grid in the middle (flex-1, min-h-0)
                          //   • footer pinned at the bottom (shrink-0) — only when
                          //     a transition is active
                          <div className="h-full flex flex-col">
                            <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-3">
                              <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                                Animación
                              </p>
                              <div className="grid grid-cols-2 gap-2">
                                {transitions.map((tr) => (
                                  <TransitionPreviewTile
                                    key={tr.type}
                                    type={tr.type}
                                    label={tr.label}
                                    aspectRatio={aspectRatio}
                                    isActive={
                                      currentTransition?.type === tr.type
                                    }
                                    onClick={() => apply(tr.type)}
                                    computeIncoming={(t, p) =>
                                      computeIncomingTransitionState(
                                        t as TransitionType,
                                        p,
                                      )
                                    }
                                    computeOutgoing={(t, p) =>
                                      computeOutgoingTransitionState(
                                        t as TransitionType,
                                        p,
                                      )
                                    }
                                    overlayUrl={OVERLAY_TRANSITIONS[tr.type]}
                                  />
                                ))}
                              </div>

                              {!targetClip && (
                                <p className="text-[12.5px] text-brand-black/50 px-1">
                                  Cerrá este panel y tocá el{" "}
                                  <span className="font-medium">+</span> entre
                                  dos clips de video adyacentes para elegir una
                                  transición.
                                </p>
                              )}
                            </div>

                            {currentTransition && (
                              <div className="shrink-0 border-t border-black/[0.06] bg-surface-card p-3 space-y-2">
                                <div className="bg-white ring-1 ring-black/[0.06] rounded-[10px] p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-[11px] uppercase tracking-wide font-medium text-brand-black/40">
                                      Duración
                                    </span>
                                    <span className="text-[12.5px] font-semibold text-brand-black/80 tabular-nums">
                                      {currentTransition.durationSec.toFixed(2)}
                                      s
                                    </span>
                                  </div>
                                  <input
                                    type="range"
                                    min={0.15}
                                    max={2}
                                    step={0.05}
                                    value={currentTransition.durationSec}
                                    onChange={(e) =>
                                      updateDuration(parseFloat(e.target.value))
                                    }
                                    className="w-full accent-brand-primary"
                                  />
                                  <div className="flex justify-between text-[10px] text-brand-black/35 mt-1 tabular-nums">
                                    <span>0.15s</span>
                                    <span>2s</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={remove}
                                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-md text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Quitar transición
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {activePanel === "effects" && (
                      <div className="space-y-3">
                        <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400 px-1">
                          Efectos y filtros
                        </p>
                        {[
                          { label: "Fade in/out", desc: "Transición suave" },
                          { label: "Zoom lento", desc: "Ken Burns effect" },
                          {
                            label: "Blanco y negro",
                            desc: "Filtro monocromático",
                          },
                          { label: "Sepia", desc: "Tono cálido vintage" },
                          {
                            label: "Blur de fondo",
                            desc: "Desenfoque gaussiano",
                          },
                          { label: "Cámara lenta", desc: "0.5x velocidad" },
                        ].map((fx) => (
                          <button
                            key={fx.label}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            <div className="w-9 h-9 rounded-sm bg-violet-50 flex items-center justify-center shrink-0">
                              <Wand2 className="w-4 h-4 text-violet-600" />
                            </div>
                            <div className="text-left">
                              <p className="text-[15px] font-medium text-brand-black/80">
                                {fx.label}
                              </p>
                              <p className="text-[11px] text-neutral-400">
                                {fx.desc}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {activePanel === "animation" &&
                      (() => {
                        // The panel has two modes:
                        //   1. Editor — when the selected clip is an animation, the
                        //      panel renders inputs derived from the template's
                        //      `propsSchema`. Edits flow live back into the clip.
                        //   2. Picker — otherwise, the panel lists every template
                        //      registered in `ANIMATION_TEMPLATES`. Clicking one
                        //      inserts a new clip on the elements track using the
                        //      template's defaults and selects it (which flips the
                        //      panel into edit mode for that clip).
                        const sel = clips.find(
                          (c) =>
                            c.id === selectedClipId && c.type === "animation",
                        );
                        if (
                          sel &&
                          sel.aiStatus === "generating" &&
                          !sel.animationTemplate &&
                          !sel.animationCodigo
                        ) {
                          return (
                            <div className="flex items-center gap-2 px-1 py-3 text-[13px] text-neutral-500">
                              <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                              Generando la animación gráfica… (15 a 40 s)
                            </div>
                          );
                        }
                        if (sel && sel.animationCodigo) {
                          // Animación por código: sin formulario de props. Se
                          // cambia hablando con el agente; acá, tamaño, centrar y
                          // el historial de versiones.
                          const anim = sel.animationCodigo;
                          const patch = (p: Partial<TimelineClip>) =>
                            setClips((prev) =>
                              prev.map((c) =>
                                c.id === sel.id ? { ...c, ...p } : c,
                              ),
                            );
                          return (
                            <PanelAnimacionPorCodigo
                              nombre={sel.name}
                              animacion={anim}
                              escala={sel.animScale ?? 1}
                              onEscala={(e) => patch({ animScale: e })}
                              onCentrar={() =>
                                patch({ animXPct: 50, animYPct: 50 })
                              }
                              onVolverAVersion={(i) => {
                                reiniciarSandbox(sel.id);
                                const vuelta = volverAVersion(anim, i);
                                patch({
                                  ...ventanaDeVersion(versionActual(vuelta).duracionSeg),
                                  animationCodigo: vuelta,
                                });
                              }}
                              // "Editar" abre el chat con la animación ya nombrada
                              // en el input: el usuario sólo escribe qué cambiar.
                              // El clip queda seleccionado, así el agente lo ve
                              // en el estado de la pantalla.
                              onEditar={() =>
                                abrirChat({
                                  contexto: {
                                    pantalla: "el Estudio de Video",
                                    ruta: `/editor-video/${projectId ?? ""}`,
                                    detalle: projectName
                                      ? `el proyecto «${projectName}»`
                                      : undefined,
                                  },
                                  vista: "panel",
                                  mensajeInicial: `Sobre la animación gráfica «${sel.name}» que tengo seleccionada, quiero que `,
                                })
                              }
                            />
                          );
                        }
                        if (sel) {
                          const tpl = getAnimationTemplate(
                            sel.animationTemplate,
                          );
                          if (!tpl) {
                            return (
                              <p className="text-[13px] text-neutral-500 px-1 py-3">
                                Plantilla desconocida (
                                <code>{sel.animationTemplate}</code>).
                                Probablemente fue removida del catálogo.
                              </p>
                            );
                          }
                          // Origen de los valores que muestra el formulario: el
                          // draft de edición. Si por algún motivo todavía no se
                          // inicializó (race entre selección y efecto), caemos a
                          // las props guardadas del clip.
                          const draftProps =
                            animationEditDraft &&
                            animationEditDraft.clipId === sel.id
                              ? animationEditDraft.props
                              : sel.animationProps || {};
                          const currentProps = {
                            ...(tpl.defaultProps as Record<string, unknown>),
                            ...draftProps,
                          };
                          const updateProp = (key: string, value: unknown) => {
                            setAnimationEditDraft((d) =>
                              d && d.clipId === sel.id
                                ? { ...d, props: { ...d.props, [key]: value } }
                                : d,
                            );
                          };
                          // ¿Hay cambios sin guardar? Comparamos las claves del draft
                          // con las del clip persistido. Si algún valor difiere, el
                          // botón de guardar se habilita.
                          const savedProps = sel.animationProps || {};
                          const allKeys = new Set<string>([
                            ...Object.keys(savedProps),
                            ...Object.keys(draftProps),
                          ]);
                          let isDirty = false;
                          for (const k of allKeys) {
                            if (savedProps[k] !== draftProps[k]) {
                              isDirty = true;
                              break;
                            }
                          }
                          const onSave = () => {
                            setClips((prev) =>
                              prev.map((c) =>
                                c.id === sel.id
                                  ? { ...c, animationProps: { ...draftProps } }
                                  : c,
                              ),
                            );
                          };
                          const onCancel = () => {
                            // Descartamos el draft volviendo a copiar las props
                            // guardadas del clip y salimos del modo edición.
                            setAnimationEditDraft(null);
                            setAnimationPreviewLoopClipId(null);
                            setIsPlaying(false);
                            setSelectedClipId(null);
                          };
                          const isLooping =
                            animationPreviewLoopClipId === sel.id && isPlaying;
                          const onTogglePreview = () => {
                            if (isLooping) {
                              setIsPlaying(false);
                              setAnimationPreviewLoopClipId(null);
                            } else {
                              setCurrentTime(sel.startTime);
                              setAnimationPreviewLoopClipId(sel.id);
                              setIsPlaying(true);
                            }
                          };
                          const schema = tpl.propsSchema || {};
                          // Collect every prop key referenced by a text spec's
                          // `styling` table. Those props live inside the per-text
                          // style popover and shouldn't render as separate top-level
                          // rows in the main form.
                          const linkedKeys = new Set<string>();
                          for (const s of Object.values(schema)) {
                            if (s?.type === "text" && s.styling) {
                              for (const k of Object.values(s.styling)) {
                                if (typeof k === "string") linkedKeys.add(k);
                              }
                            }
                          }
                          const allEntries = Object.entries(schema) as Array<
                            [string, AnimationPropSpec]
                          >;
                          // El "Fondo" siempre es lo último que el usuario configura
                          // (es ambiente, no contenido). Detectamos cualquier prop
                          // de tipo color cuya key empiece con `background` y la
                          // movemos al final, preservando el orden relativo del
                          // resto. Sort estable nativo en JS engines modernos.
                          const isBackgroundProp = (
                            key: string,
                            spec: AnimationPropSpec,
                          ) =>
                            spec.type === "color" && /^background/i.test(key);
                          const entries = allEntries
                            .filter(([k]) => !linkedKeys.has(k))
                            .sort(([ka, sa], [kb, sb]) => {
                              const a = isBackgroundProp(ka, sa) ? 1 : 0;
                              const b = isBackgroundProp(kb, sb) ? 1 : 0;
                              return a - b;
                            });
                          return (
                            // `data-clip-ui` keeps the global pointerdown handler
                            // from deselecting the clip when the user interacts with
                            // any input inside this panel. Without it, every click
                            // on a color picker / text field would clear
                            // `selectedClipId` and the panel would snap back to the
                            // catalog mid-edit.
                            <div className="space-y-4" data-clip-ui="true">
                              <div className="px-1">
                                <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
                                  Plantilla
                                </p>
                                <p className="text-[14px] font-medium text-brand-black/85 mt-0.5">
                                  {tpl.label}
                                </p>
                                {tpl.description && (
                                  <p className="text-[12px] text-neutral-500 mt-0.5">
                                    {tpl.description}
                                  </p>
                                )}
                              </div>

                              {/* Botón único de previsualización: arranca el playhead
                          al inicio del clip y lo deja en loop hasta que se
                          presione pausa. Reutiliza el player principal —
                          no agregamos un reproductor aparte. */}
                              <button
                                type="button"
                                onClick={onTogglePreview}
                                className={`w-full h-10 inline-flex items-center justify-center gap-2 rounded-md text-[13px] font-medium transition-colors ${
                                  isLooping
                                    ? "bg-brand-primary text-white hover:bg-brand-primary/90"
                                    : "border border-black/[0.08] bg-white text-brand-black/80 hover:bg-neutral-50"
                                }`}
                                title={
                                  isLooping
                                    ? "Detener la previsualización"
                                    : "Reproducir la animación en bucle"
                                }
                              >
                                {isLooping ? (
                                  <>
                                    <Pause className="w-4 h-4" /> Detener
                                    previsualización
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-4 h-4 ml-0.5" />{" "}
                                    Reproducir en bucle
                                  </>
                                )}
                              </button>

                              {/* ── Tamaño y posición ─────────────────────────
                          La posición se cambia arrastrando la animación
                          en el preview (igual que el texto). Acá ofrecemos
                          un slider de tamaño y un atajo para volver al
                          centro. Estos campos viven en el clip (no son
                          props del template) así que escribimos directo
                          sobre `clips` sin pasar por el draft. */}
                              {(() => {
                                const currentScale = sel.animScale ?? 1;
                                const setClipFields = (
                                  patch: Partial<TimelineClip>,
                                ) => {
                                  setClips((prev) =>
                                    prev.map((c) =>
                                      c.id === sel.id ? { ...c, ...patch } : c,
                                    ),
                                  );
                                };
                                const onScaleChange = (next: number) => {
                                  // No re-clampeamos al cambiar la escala — el
                                  // centro puede vivir libremente en [0,100],
                                  // que el usuario decida si quiere algo
                                  // parcialmente fuera de pantalla.
                                  setClipFields({ animScale: next });
                                };
                                return (
                                  <div className="space-y-2 px-1">
                                    <div className="flex items-center justify-between">
                                      <span className="text-[12px] font-medium text-brand-black/70">
                                        Tamaño
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setClipFields({
                                            animXPct: 50,
                                            animYPct: 50,
                                          })
                                        }
                                        className="text-[11.5px] text-brand-primary hover:underline"
                                        title="Volver al centro de la canvas"
                                      >
                                        Centrar
                                      </button>
                                    </div>
                                    {/* Pill − [N%] + análoga al control de font-size
                                del texto. Trabajamos en porcentaje (5..100)
                                para que el usuario lea un número redondo,
                                y traducimos a la escala interna 0.05..1
                                al commitear. Los handles del SelectionFrame
                                en el preview también escriben sobre este
                                campo, así que el input se mantiene en sync
                                en vivo mientras se arrastra. */}
                                    {(() => {
                                      const pct = Math.round(
                                        currentScale * 100,
                                      );
                                      const stepScale = (delta: number) => {
                                        const nextPct = Math.max(
                                          5,
                                          Math.min(300, pct + delta),
                                        );
                                        onScaleChange(nextPct / 100);
                                      };
                                      return (
                                        <div className="flex items-center gap-2">
                                          <div className="h-8 flex items-center rounded-md border border-neutral-200 bg-white overflow-hidden">
                                            <button
                                              type="button"
                                              onClick={() => stepScale(-5)}
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Reducir tamaño"
                                            >
                                              −
                                            </button>
                                            <NumberField
                                              value={pct}
                                              min={5}
                                              max={300}
                                              step={1}
                                              decimals={0}
                                              onCommit={(n) =>
                                                onScaleChange(
                                                  Math.max(
                                                    0.05,
                                                    Math.min(3, n / 100),
                                                  ),
                                                )
                                              }
                                              className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                              ariaLabel="Tamaño en porcentaje"
                                            />
                                            <button
                                              type="button"
                                              onClick={() => stepScale(5)}
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Aumentar tamaño"
                                            >
                                              +
                                            </button>
                                          </div>
                                          <span className="text-[11px] text-neutral-400">
                                            %
                                          </span>
                                        </div>
                                      );
                                    })()}
                                    {/* Animaciones — entrada / salida lado a lado,
                                con MotionPicker de preview-en-hover. Scope
                                'common' filtra los presets text-only. */}
                                    <div className="pt-3 mt-1 border-t border-neutral-200/70 space-y-2">
                                      <p className="text-[11px] uppercase tracking-wider font-medium text-neutral-400">
                                        Animaciones
                                      </p>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="space-y-1.5">
                                          <MotionPicker
                                            label="Entrada"
                                            kind="enter"
                                            scope="common"
                                            value={sel.enterAnimation ?? "none"}
                                            onChange={(id) =>
                                              setClipFields({
                                                enterAnimation: id,
                                              })
                                            }
                                          />
                                          {(sel.enterAnimation ?? "none") !==
                                            "none" && (
                                            <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                              <NumberField
                                                value={
                                                  sel.enterDurationSec ?? 0.5
                                                }
                                                min={0.1}
                                                max={5}
                                                step={0.1}
                                                decimals={1}
                                                onCommit={(n) =>
                                                  setClipFields({
                                                    enterDurationSec: n,
                                                  })
                                                }
                                                className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Duración entrada"
                                              />
                                              <span className="text-[11px] text-neutral-400">
                                                seg
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="space-y-1.5">
                                          <MotionPicker
                                            label="Salida"
                                            kind="exit"
                                            scope="common"
                                            value={sel.exitAnimation ?? "none"}
                                            onChange={(id) =>
                                              setClipFields({
                                                exitAnimation: id,
                                              })
                                            }
                                          />
                                          {(sel.exitAnimation ?? "none") !==
                                            "none" && (
                                            <div className="h-8 flex items-center gap-1.5 px-2 rounded-md border border-neutral-200 bg-white">
                                              <NumberField
                                                value={
                                                  sel.exitDurationSec ?? 0.5
                                                }
                                                min={0.1}
                                                max={5}
                                                step={0.1}
                                                decimals={1}
                                                onCommit={(n) =>
                                                  setClipFields({
                                                    exitDurationSec: n,
                                                  })
                                                }
                                                className="flex-1 min-w-0 h-7 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                                ariaLabel="Duración salida"
                                              />
                                              <span className="text-[11px] text-neutral-400">
                                                seg
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {entries.length === 0 ? (
                                <p className="text-[13px] text-neutral-500 px-1">
                                  Esta plantilla no tiene parámetros editables.
                                </p>
                              ) : (
                                <div className="space-y-3">
                                  {entries.map(([key, spec]) => {
                                    const value = (currentProps[key] ??
                                      "") as string;
                                    if (spec.type === "text") {
                                      const inputCls =
                                        "w-full px-3 py-2 text-[14px] rounded-md border border-black/[0.08] bg-white focus:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/15 transition-colors";
                                      const labelEl = (
                                        <span className="text-[12px] font-medium text-brand-black/70 block mb-1">
                                          {spec.label}
                                        </span>
                                      );
                                      const inputEl = spec.multiline ? (
                                        <textarea
                                          value={value}
                                          onChange={(e) =>
                                            updateProp(key, e.target.value)
                                          }
                                          placeholder={spec.placeholder}
                                          rows={3}
                                          className={`${inputCls} resize-none`}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          value={value}
                                          onChange={(e) =>
                                            updateProp(key, e.target.value)
                                          }
                                          placeholder={spec.placeholder}
                                          className={inputCls}
                                        />
                                      );
                                      // If this text has `styling`, sit the input
                                      // next to a small "Aa" trigger that opens the
                                      // dedicated style popover for this copy.
                                      if (spec.styling) {
                                        return (
                                          <div key={key}>
                                            {labelEl}
                                            <div className="flex items-start gap-2">
                                              <div className="flex-1 min-w-0">
                                                {inputEl}
                                              </div>
                                              <TextStyleButton
                                                styling={spec.styling}
                                                schema={
                                                  schema as Record<
                                                    string,
                                                    | AnimationPropSpec
                                                    | undefined
                                                  >
                                                }
                                                values={currentProps}
                                                onChange={(k, v) =>
                                                  updateProp(k, v)
                                                }
                                                title={`Estilo de ${spec.label.toLowerCase()}`}
                                              />
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <label key={key} className="block">
                                          {labelEl}
                                          {inputEl}
                                        </label>
                                      );
                                    }
                                    if (spec.type === "boolean") {
                                      const on = !!currentProps[key];
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => updateProp(key, !on)}
                                            aria-pressed={on}
                                            className={`h-7 w-12 inline-flex items-center rounded-full transition-colors ${
                                              on
                                                ? "bg-brand-primary justify-end pr-0.5"
                                                : "bg-neutral-200 justify-start pl-0.5"
                                            }`}
                                          >
                                            <span className="w-6 h-6 rounded-full bg-white shadow-sm" />
                                          </button>
                                        </div>
                                      );
                                    }
                                    if (spec.type === "select") {
                                      // Up to 4 options → segmented control for fast
                                      // scanning. More than that → native <select>
                                      // styled to match the rest of the editor.
                                      const useSegmented =
                                        spec.options.length <= 4;
                                      if (useSegmented) {
                                        return (
                                          <div
                                            key={key}
                                            className="flex items-center justify-between gap-3"
                                          >
                                            <span className="text-[12px] font-medium text-brand-black/70">
                                              {spec.label}
                                            </span>
                                            <div className="inline-flex items-center bg-neutral-100 rounded-md p-0.5">
                                              {spec.options.map((opt) => {
                                                const active =
                                                  opt.value === value;
                                                return (
                                                  <button
                                                    key={opt.value}
                                                    type="button"
                                                    onClick={() =>
                                                      updateProp(key, opt.value)
                                                    }
                                                    className={`h-7 px-2.5 text-[12px] font-medium rounded transition-colors ${
                                                      active
                                                        ? "bg-white text-brand-black shadow-sm"
                                                        : "text-neutral-500 hover:text-neutral-700"
                                                    }`}
                                                  >
                                                    {opt.label}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <select
                                            value={value}
                                            onChange={(e) =>
                                              updateProp(key, e.target.value)
                                            }
                                            className="h-8 pl-2.5 pr-7 text-[13px] rounded-md border border-black/[0.08] bg-white text-brand-black/85 hover:border-black/[0.16] focus:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/15 transition-colors appearance-none bg-no-repeat bg-right cursor-pointer"
                                            style={{
                                              backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
                                              backgroundPosition:
                                                "right 8px center",
                                            }}
                                          >
                                            {spec.options.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      );
                                    }
                                    if (spec.type === "image") {
                                      return (
                                        <div key={key}>
                                          <span className="text-[12px] font-medium text-brand-black/70 block mb-1">
                                            {spec.label}
                                          </span>
                                          <ImagePicker
                                            value={value}
                                            onChange={(url) =>
                                              updateProp(key, url)
                                            }
                                            projectId={projectId}
                                            companyId={companyId}
                                          />
                                        </div>
                                      );
                                    }
                                    if (spec.type === "font") {
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <FontPicker
                                            value={value || "Inter"}
                                            onChange={(fam) =>
                                              updateProp(key, fam)
                                            }
                                            triggerClassName="h-8 w-[160px] pl-2.5 pr-2 flex items-center justify-between gap-1 rounded-md border border-black/[0.08] bg-white text-[13px] text-brand-black/85 hover:border-black/[0.16] transition-colors"
                                            panelAlign="right"
                                            panelMinWidth={220}
                                          />
                                        </div>
                                      );
                                    }
                                    if (spec.type === "number") {
                                      const cur = Number(
                                        currentProps[key] ?? 0,
                                      );
                                      const step = spec.step ?? 1;
                                      const min = spec.min ?? 0;
                                      const max = spec.max ?? 9999;
                                      const dec = spec.decimals ?? 0;
                                      const clamp = (n: number) =>
                                        Math.min(max, Math.max(min, n));
                                      return (
                                        <div
                                          key={key}
                                          className="flex items-center justify-between gap-3"
                                        >
                                          <span className="text-[12px] font-medium text-brand-black/70">
                                            {spec.label}
                                          </span>
                                          <div className="h-8 flex items-center rounded-md border border-black/[0.08] bg-white overflow-hidden">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateProp(
                                                  key,
                                                  clamp(cur - step),
                                                )
                                              }
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Reducir"
                                            >
                                              −
                                            </button>
                                            <NumberField
                                              value={cur}
                                              min={min}
                                              max={max}
                                              step={step}
                                              decimals={dec}
                                              onCommit={(n) =>
                                                updateProp(key, n)
                                              }
                                              className="w-12 h-8 text-center text-[13px] tabular-nums text-neutral-800 bg-transparent focus:outline-none"
                                              ariaLabel={spec.label}
                                            />
                                            <button
                                              type="button"
                                              onClick={() =>
                                                updateProp(
                                                  key,
                                                  clamp(cur + step),
                                                )
                                              }
                                              className="h-8 w-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 transition-colors"
                                              title="Aumentar"
                                            >
                                              +
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    }
                                    // color
                                    return (
                                      <div
                                        key={key}
                                        className="flex items-center justify-between gap-3"
                                      >
                                        <span className="text-[12px] font-medium text-brand-black/70">
                                          {spec.label}
                                        </span>
                                        <ColorSwatchPicker
                                          value={value}
                                          onChange={(c) => updateProp(key, c)}
                                          ariaLabel={spec.label}
                                          allowTransparent={
                                            spec.allowTransparent
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Restaurar valores: sobreescribe el draft con los
                          valores por defecto de la plantilla. No persiste
                          nada hasta que se aprete "Guardar cambios". */}
                              <button
                                type="button"
                                onClick={() => {
                                  setAnimationEditDraft((d) =>
                                    d && d.clipId === sel.id
                                      ? {
                                          ...d,
                                          props: {
                                            ...(tpl.defaultProps as Record<
                                              string,
                                              unknown
                                            >),
                                          },
                                        }
                                      : d,
                                  );
                                }}
                                className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium text-brand-black/65 hover:bg-neutral-100 hover:text-brand-black/85 transition-colors"
                                title="Volver a los valores por defecto de esta plantilla"
                              >
                                <RotateCcw className="w-3.5 h-3.5 text-neutral-500" />
                                Restaurar valores por defecto
                              </button>

                              {/* Dividir, duplicar y eliminar flotan al costado
                          del preview, junto con las del resto de los clips. */}

                              {/* Acciones de cierre. Cancelar descarta el draft y
                          vuelve al catálogo. Guardar cambios persiste el
                          draft sobre el clip — solo está habilitado cuando
                          hay diferencias respecto a lo guardado. */}
                              <div className="pt-3 border-t border-black/[0.06] flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={onCancel}
                                  className="flex-1 h-9 inline-flex items-center justify-center rounded-md border border-black/[0.08] bg-white text-[13px] font-medium text-brand-black/75 hover:bg-neutral-50 hover:text-brand-black/90 transition-colors"
                                  title="Descartar los cambios y volver al catálogo"
                                >
                                  Cancelar
                                </button>
                                <button
                                  type="button"
                                  onClick={onSave}
                                  disabled={!isDirty}
                                  className={`flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors ${
                                    isDirty
                                      ? "bg-brand-primary text-white hover:bg-brand-primary/90"
                                      : "bg-neutral-200/70 text-neutral-400 cursor-not-allowed"
                                  }`}
                                  title={
                                    isDirty
                                      ? "Aplicar los cambios al clip"
                                      : "No hay cambios para guardar"
                                  }
                                >
                                  <Check className="w-3.5 h-3.5" />
                                  Guardar cambios
                                </button>
                              </div>
                            </div>
                          );
                        }

                        // Picker en dos niveles:
                        //   · Nivel 1 (`elementsCategory === null`): cards grandes
                        //     de categoría (Texto / Imagen / Animación).
                        //   · Nivel 2: contenido específico de la categoría.
                        //     Para 'text' e 'image' es la grilla de templates con
                        //     preview animada; para 'lottie' es el buscador.
                        const templates = Object.values(ANIMATION_TEMPLATES);
                        const insertTemplate = (
                          tpl: (typeof templates)[number],
                          propOverrides?: Record<string, unknown>,
                          name?: string,
                          durationOverrideSec?: number,
                        ) => {
                          const elementsTrack = TRACK_CONFIG.find(
                            (t) => t.type === "elements",
                          )!;
                          const clipId = generateId();
                          // Si el caller pasa una duración explícita (ej. la
                          // duración natural de un Lottie), la respetamos. Sin
                          // eso usábamos siempre los 4s default y el clip cortaba
                          // o estiraba la animación.
                          const duration =
                            durationOverrideSec && durationOverrideSec > 0
                              ? durationOverrideSec
                              : tpl.defaultDurationSec;
                          const newClip: TimelineClip = {
                            id: clipId,
                            type: "animation",
                            name: name ?? tpl.label,
                            ...ubicarClipNuevo("animation", {
                              duracion: duration,
                            }),
                            duration,
                            color: elementsTrack.color,
                            animationTemplate: tpl.id,
                            animationProps: {
                              ...(tpl.defaultProps as Record<string, unknown>),
                              ...(propOverrides || {}),
                            },
                            // Defaults de transform: centro de la canvas y escala
                            // 0.6 (≈ 60% del canvas) para que se vea claramente
                            // como un "elemento" movible. Los clips viejos sin
                            // estos campos siguen renderizándose full-screen.
                            animXPct: 50,
                            animYPct: 50,
                            animScale: 0.6,
                          };
                          setClips((prev) => [...prev, newClip]);
                          setSelectedClipId(clipId);
                        };

                        // ── Nivel 1: cards de categoría ──────────────────────
                        if (elementsCategory === null) {
                          const categories: Array<{
                            key: "text" | "image" | "lottie";
                            label: string;
                            description: string;
                            Icon: React.ComponentType<{ className?: string }>;
                            bg: string;
                            iconBg: string;
                            iconColor: string;
                          }> = [
                            {
                              key: "text",
                              label: "Texto",
                              description:
                                "Títulos, números, sellos y más con texto editable.",
                              Icon: Type,
                              bg: "bg-gradient-to-br from-violet-50 to-violet-100/40",
                              iconBg: "bg-violet-100",
                              iconColor: "text-violet-600",
                            },
                            {
                              key: "image",
                              label: "Imagen",
                              description:
                                "Animaciones que muestran una imagen del proyecto.",
                              Icon: ImageIcon,
                              bg: "bg-gradient-to-br from-emerald-50 to-emerald-100/40",
                              iconBg: "bg-emerald-100",
                              iconColor: "text-emerald-600",
                            },
                            {
                              key: "lottie",
                              label: "Animación",
                              description:
                                "Animaciones vectoriales de la biblioteca Lottie.",
                              Icon: Sparkles,
                              bg: "bg-gradient-to-br from-amber-50 to-amber-100/40",
                              iconBg: "bg-amber-100",
                              iconColor: "text-amber-600",
                            },
                          ];
                          return (
                            <div className="space-y-2.5">
                              {categories.map((cat) => (
                                <button
                                  key={cat.key}
                                  type="button"
                                  onClick={() => setElementsCategory(cat.key)}
                                  className={`group w-full text-left rounded-md border border-black/[0.06] shadow-sm hover:border-brand-primary/30 hover:shadow-md hover:-translate-y-px transition-all overflow-hidden ${cat.bg}`}
                                >
                                  <div className="flex items-center gap-3 px-3 py-4">
                                    <div
                                      className={`w-11 h-11 rounded-md ${cat.iconBg} flex items-center justify-center shrink-0`}
                                    >
                                      <cat.Icon
                                        className={`w-5 h-5 ${cat.iconColor}`}
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-[15px] font-semibold text-brand-black/90 leading-tight">
                                        {cat.label}
                                      </p>
                                      <p className="text-[12px] text-brand-black/55 mt-0.5 leading-snug">
                                        {cat.description}
                                      </p>
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                                  </div>
                                </button>
                              ))}
                            </div>
                          );
                        }

                        // Header común para los niveles 2 con un botón de volver.
                        const backButton = (
                          <button
                            type="button"
                            onClick={() => setElementsCategory(null)}
                            className="inline-flex items-center gap-1 px-2 py-1 -ml-1 rounded-md text-[13px] font-medium text-brand-black/65 hover:bg-neutral-100 hover:text-brand-black/85 transition-colors"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            Volver
                          </button>
                        );

                        // ── Nivel 2: Lottie picker ───────────────────────────
                        if (elementsCategory === "lottie") {
                          const lottieTpl = templates.find(
                            (t) => t.id === "lottie-asset-v1",
                          );
                          return (
                            <div className="space-y-3">
                              {backButton}
                              {lottieTpl ? (
                                <LottiePicker
                                  onPick={(item) =>
                                    insertTemplate(
                                      lottieTpl,
                                      { lottieUrl: item.url },
                                      item.title,
                                      item.durationSec,
                                    )
                                  }
                                />
                              ) : (
                                <p className="text-[13px] text-neutral-500 px-1">
                                  La plantilla de Lottie no está registrada.
                                </p>
                              )}
                            </div>
                          );
                        }

                        // ── Nivel 2: grilla de templates (Texto / Imagen) ─────
                        const items = templates.filter(
                          (t) =>
                            // Excluimos la plantilla de Lottie de las grillas
                            // genéricas — se inserta solo desde el buscador.
                            t.id !== "lottie-asset-v1" &&
                            (t.category || "text") === elementsCategory,
                        );
                        return (
                          <div className="space-y-3">
                            {backButton}
                            {/* Atajo a "texto simple" — la grilla de abajo solo
                        ofrece plantillas animadas de texto, pero a veces
                        el usuario solo quiere un texto plano (sin diseño)
                        sobre el video. Este botón lo manda directo al
                        panel `text` que ya implementa todo el editor de
                        un text-clip básico. */}
                            {elementsCategory === "text" && (
                              <button
                                type="button"
                                onClick={() => setActivePanel("text")}
                                className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-md border border-black/[0.08] bg-white text-[13px] font-medium text-brand-black/80 hover:bg-neutral-50 hover:border-black/[0.16] transition-colors shadow-sm"
                                title="Crear un texto plano sin plantilla animada"
                              >
                                <Plus className="w-4 h-4" />
                                Añadir texto simple
                              </button>
                            )}
                            {items.length === 0 ? (
                              <p className="text-[13px] text-neutral-500 px-1">
                                Todavía no hay plantillas en esta categoría.
                              </p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {items.map((tpl) => (
                                  <AnimationPreviewTile
                                    key={tpl.id}
                                    template={tpl}
                                    aspectRatio={aspectRatio}
                                    onClick={() => insertTemplate(tpl)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {activePanel === "ai-scene" && (
                      <div className="flex-1 h-full flex flex-col min-w-0">
                        {/* Hidden inputs for uploads */}
                        <input
                          ref={sceneRefImageRef}
                          type="file"
                          accept="image/*,video/*"
                          className="hidden"
                          onChange={handleRefImageUpload}
                        />
                        <input
                          ref={sceneFirstFrameRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFrameUpload(e, "first")}
                        />
                        <input
                          ref={sceneLastFrameRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFrameUpload(e, "last")}
                        />

                        {/* Header */}
                        <div className="flex items-center justify-between px-3 h-12">
                          {/* El título repite el nombre de la baldosa del
                          rail desde la que se abre: "Video con IA". Antes cada
                          panel se llamaba distinto ("Nueva escena con IA") y
                          había que deducir de dónde venía. Ya no tiene
                          sub-vistas: el panel es una sola cara, así que no hay
                          flecha de volver ni título alternativo
                          (ADR-2026-09-17-se-van-las-configuraciones-avanzadas-de-dialogo). */}
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            {/* Flecha de volver — sólo cuando se entró desde
                            otro panel ("Extender video", desde el clip). Es el
                            mismo botón que usan las sub-pantallas de la hoja
                            (Eliminar silencios, Cambiar voz): mismo lugar,
                            mismo tamaño y misma forma, así "volver" se ve igual
                            en todo el editor. */}
                            {panelDeVueltaDeEscena && (
                              <button
                                type="button"
                                onClick={() =>
                                  setActivePanel(panelDeVueltaDeEscena)
                                }
                                className="w-7 h-7 -ml-1 inline-flex items-center justify-center rounded-full text-brand-black/50 hover:bg-black/[0.06] hover:text-brand-black/80 transition-colors shrink-0"
                                title="Volver"
                                aria-label="Volver"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                            )}
                            <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                              Video con IA
                            </h3>
                          </div>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
                          {/* Sin selector de modo: el panel es "Modo General"
                          y punto. El estado `sceneMode` sigue existiendo —lo
                          pone el agente con `prefill_ai_scene` y lo pone el
                          editor al abrir un clip de diálogo ya creado—, así que
                          la UI de Modo Diálogo se sigue dibujando cuando el
                          modo llega por esos caminos; lo que se fue es la
                          puerta para cambiarlo a mano. */}
                          {/* Personas-only: single first-frame upload */}
                          {sceneMode === "personas" &&
                            (() => {
                              const firstPreview = sceneFirstFrameAssetId
                                ? assetPreviewUrls[sceneFirstFrameAssetId]
                                : null;
                              return (
                                <div data-tour="dialog-frame">
                                  <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                    Frame inicial
                                  </p>
                                  <div
                                    className={`rounded-[10px] border-2 ${sceneFirstFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-firstframe")}${panelNudgeClass("scene-firstframe")}`}
                                    style={{ height: 110, cursor: "pointer" }}
                                    onClick={() =>
                                      !sceneFirstFrameAssetId &&
                                      abrirSelectorDeImagen({ kind: "first" })
                                    }
                                  >
                                    {sceneFirstFrameAssetId ? (
                                      <>
                                        {firstPreview ? (
                                          <img
                                            src={firstPreview}
                                            alt="Frame inicial"
                                            className="object-contain rounded-[8px]"
                                            style={{
                                              maxWidth: "calc(100% - 16px)",
                                              maxHeight: "calc(100% - 16px)",
                                            }}
                                          />
                                        ) : (
                                          <span className="text-[11px] text-neutral-400">
                                            ...
                                          </span>
                                        )}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSceneFirstFrameAssetId(null);
                                          }}
                                          className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <Plus className="w-3 h-3 text-white rotate-45" />
                                        </button>
                                      </>
                                    ) : (
                                      <div className="flex flex-col items-center gap-1.5">
                                        <Plus className="w-5 h-5 text-neutral-300" />
                                        <span className="text-[13px] text-neutral-400 font-medium">
                                          Subí el frame inicial
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                          {/* Reference images toggle (General mode only) */}
                          {sceneMode === "general" && (
                            <div data-tour="scene-reference">
                              <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                Imágenes de referencia
                              </p>
                              {/* iOS Segmented Control */}
                              {/* Dos opciones, no tres: "Ninguna" se fue. No
                              era una tercera forma de dar referencias, era la
                              de NO darlas — y eso ya lo dice dejar los slots
                              vacíos, que es lo que traduce `refModeEfectivo`
                              en el generate. */}
                              <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                                <div
                                  className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                                  style={{
                                    width: "calc(50% - 3px)",
                                    left:
                                      sceneRefMode === "frames"
                                        ? "calc(50%)"
                                        : "3px",
                                  }}
                                />
                                {(["reference", "frames"] as const).map(
                                  (mode) => (
                                    <button
                                      key={mode}
                                      type="button"
                                      onClick={() => setSceneRefMode(mode)}
                                      className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                                        sceneRefMode === mode
                                          ? "text-brand-black/90"
                                          : "text-brand-black/50"
                                      }`}
                                    >
                                      {mode === "reference"
                                        ? "Referencia"
                                        : "Frames"}
                                    </button>
                                  ),
                                )}
                              </div>

                              {/* Description */}
                              {sceneRefMode === "reference" && (
                                <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                                  Subí imágenes como referencia visual, o un video
                                  (hasta 29 s) del que tomar el estilo, el ritmo y la
                                  cámara. Más de 14 s pide calidad Profesional.
                                </p>
                              )}
                              {sceneRefMode === "frames" && (
                                <p className="text-[13px] text-[#3C3C43]/60 mt-2">
                                  Definí el frame de inicio y fin para guiar la
                                  transición.
                                </p>
                              )}

                              {/* Reference images – horizontal list */}
                              {sceneRefMode === "reference" && (
                                <div
                                  className={`mt-3 flex gap-2 overflow-x-auto pb-1${panelBounceClass("scene-refs")}`}
                                  style={{ scrollbarWidth: "thin" }}
                                >
                                  {sceneRefAssetIds.map((assetId, idx) => {
                                    const previewUrl =
                                      assetPreviewUrls[assetId];
                                    const esVideoRef = libraryAssets.some(
                                      (a) =>
                                        a.id === assetId && a.type === "video",
                                    );
                                    return (
                                      <div
                                        key={assetId}
                                        className="relative shrink-0 rounded-[10px] overflow-hidden bg-neutral-100"
                                        style={{ height: 110 }}
                                      >
                                        {previewUrl && esVideoRef ? (
                                          <>
                                            <video
                                              src={previewUrl}
                                              muted
                                              playsInline
                                              preload="metadata"
                                              className="h-full w-auto object-contain rounded-[10px]"
                                            />
                                            <span className="absolute bottom-1 left-1 inline-flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white pointer-events-none">
                                              <Film className="w-2.5 h-2.5" />
                                              Video
                                            </span>
                                          </>
                                        ) : previewUrl ? (
                                          <img
                                            src={previewUrl}
                                            alt={`Ref ${idx + 1}`}
                                            className="h-full w-auto object-contain rounded-[10px]"
                                          />
                                        ) : (
                                          <div className="h-full w-[110px] flex items-center justify-center">
                                            <span className="text-[11px] text-neutral-400">
                                              ...
                                            </span>
                                          </div>
                                        )}
                                        <button
                                          onClick={() =>
                                            setSceneRefAssetIds((prev) =>
                                              prev.filter(
                                                (id) => id !== assetId,
                                              ),
                                            )
                                          }
                                          className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                          style={{ cursor: "pointer" }}
                                        >
                                          <Plus className="w-3 h-3 text-white rotate-45" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                  <div className="relative flex-1 min-w-[80px]">
                                    <button
                                      onClick={() =>
                                        abrirSelectorDeImagen({ kind: "ref" })
                                      }
                                      className={`w-full rounded-[10px] border-2 border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03] transition-colors flex flex-col items-center justify-center gap-1${panelNudgeClass("scene-ref")}`}
                                      style={{ height: 110, cursor: "pointer" }}
                                    >
                                      <Plus className="w-4 h-4 text-neutral-300" />
                                      <span className="text-[11px] text-neutral-400 font-medium">
                                        {sceneRefAssetIds.length === 0
                                          ? "Añadir imagen o video"
                                          : "Añadir otra"}
                                      </span>
                                    </button>
                                  </div>
                                </div>
                              )}

                              {/* Start/end frames */}
                              {sceneRefMode === "frames" &&
                                (() => {
                                  const firstPreview = sceneFirstFrameAssetId
                                    ? assetPreviewUrls[sceneFirstFrameAssetId]
                                    : null;
                                  const lastPreview = sceneLastFrameAssetId
                                    ? assetPreviewUrls[sceneLastFrameAssetId]
                                    : null;
                                  return (
                                    <div className="flex gap-3 mt-3">
                                      <div
                                        className={`flex-1 rounded-[10px] border-2 ${sceneFirstFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-firstframe")}${panelNudgeClass("scene-firstframe")}`}
                                        style={{
                                          height: 110,
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          !sceneFirstFrameAssetId &&
                                          abrirSelectorDeImagen({ kind: "first" })
                                        }
                                      >
                                        {sceneFirstFrameAssetId ? (
                                          <>
                                            {firstPreview ? (
                                              <img
                                                src={firstPreview}
                                                alt="Primer frame"
                                                className="object-contain rounded-[8px]"
                                                style={{
                                                  maxWidth: "calc(100% - 16px)",
                                                  maxHeight:
                                                    "calc(100% - 16px)",
                                                }}
                                              />
                                            ) : (
                                              <span className="text-[11px] text-neutral-400">
                                                ...
                                              </span>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSceneFirstFrameAssetId(null);
                                              }}
                                              className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Plus className="w-3 h-3 text-white rotate-45" />
                                            </button>
                                          </>
                                        ) : (
                                          <div className="flex flex-col items-center gap-1.5">
                                            <Plus className="w-5 h-5 text-neutral-300" />
                                            <span className="text-[13px] text-neutral-400 font-medium">
                                              Inicio
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                      <div
                                        className={`flex-1 rounded-[10px] border-2 ${sceneLastFrameAssetId ? "border-solid border-neutral-200" : "border-dashed border-neutral-200 hover:border-brand-primary/40 hover:bg-brand-primary/[0.03]"} transition-colors flex items-center justify-center relative${panelBounceClass("scene-lastframe")}${panelNudgeClass("scene-lastframe")}`}
                                        style={{
                                          height: 110,
                                          cursor: "pointer",
                                        }}
                                        onClick={() =>
                                          !sceneLastFrameAssetId &&
                                          abrirSelectorDeImagen({ kind: "last" })
                                        }
                                      >
                                        {sceneLastFrameAssetId ? (
                                          <>
                                            {lastPreview ? (
                                              <img
                                                src={lastPreview}
                                                alt="Último frame"
                                                className="object-contain rounded-[8px]"
                                                style={{
                                                  maxWidth: "calc(100% - 16px)",
                                                  maxHeight:
                                                    "calc(100% - 16px)",
                                                }}
                                              />
                                            ) : (
                                              <span className="text-[11px] text-neutral-400">
                                                ...
                                              </span>
                                            )}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSceneLastFrameAssetId(null);
                                              }}
                                              className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                                              style={{ cursor: "pointer" }}
                                            >
                                              <Plus className="w-3 h-3 text-white rotate-45" />
                                            </button>
                                          </>
                                        ) : (
                                          <div className="flex flex-col items-center gap-1.5">
                                            <Plus className="w-5 h-5 text-neutral-300" />
                                            <span className="text-[13px] text-neutral-400 font-medium">
                                              Final
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}
                            </div>
                          )}


                          {/* Static dialog: voice selector below Frame inicial */}
                          {sceneMode === "personas" &&
                            scenePersonType === "static" && (
                              <div data-tour="dialog-voice">
                                <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                  Voz
                                </p>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowAudioAiVoiceSelector(true)
                                  }
                                  className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                                >
                                  <span className="flex items-center gap-3 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                                      <Mic className="w-4 h-4 text-brand-primary" />
                                    </div>
                                    <div className="flex flex-col items-start min-w-0">
                                      <span className="text-[13px] text-brand-black/45 leading-tight">
                                        {audioAiVoiceId
                                          ? "Voz seleccionada"
                                          : "Seleccioná una voz"}
                                      </span>
                                      <span
                                        className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                      >
                                        {audioAiVoiceName || "Ninguna"}
                                      </span>
                                    </div>
                                  </span>
                                  <ChevronRight className="w-4 h-4 text-brand-black/40 shrink-0" />
                                </button>
                              </div>
                            )}

                          {/* Prompt de la escena. Se muestra SIEMPRE, también
                          en modo diálogo: desde que lo hablado se configura
                          aparte, este campo es sólo lo que se VE, y el panel no
                          puede quedarse sin él. Antes en `motion` no se
                          dibujaba porque el prompt lo traía el bloque de abajo,
                          que ahora está apagado. */}
                          {(
                              <div
                                data-tour={
                                  sceneMode === "personas"
                                    ? "dialog-text"
                                    : "scene-prompt"
                                }
                              >
                                <div className="flex items-center justify-between mb-2 gap-3">
                                  <p className="text-[14px] font-semibold text-brand-black/90">
                                    Prompt de la escena
                                  </p>
                                  {sceneMode === "personas" &&
                                    scenePersonType === "static" && (
                                      <div className="inline-flex p-0.5 bg-neutral-100 rounded-md text-[12px] font-medium">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSceneDialogueInputMode("text")
                                          }
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                            sceneDialogueInputMode === "text"
                                              ? "bg-white text-brand-black/90 shadow-sm"
                                              : "text-brand-black/55 hover:text-brand-black/80"
                                          }`}
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                          Escribir
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSceneDialogueInputMode("audio")
                                          }
                                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                            sceneDialogueInputMode === "audio"
                                              ? "bg-white text-brand-black/90 shadow-sm"
                                              : "text-brand-black/55 hover:text-brand-black/80"
                                          }`}
                                        >
                                          <Mic className="w-3.5 h-3.5" />
                                          Grabar
                                        </button>
                                      </div>
                                    )}
                                </div>
                                {sceneMode === "personas" &&
                                scenePersonType === "static" &&
                                sceneDialogueInputMode === "audio" ? (
                                  <DialogueAudioInput
                                    value={sceneDialogueAudio}
                                    onChange={setSceneDialogueAudio}
                                    projectId={projectId || null}
                                    companyId={companyId}
                                  />
                                ) : (
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-prompt")}`}
                                  >
                                    <textarea
                                      value={scenePrompt}
                                      onChange={(e) =>
                                        setScenePrompt(e.target.value)
                                      }
                                      placeholder={
                                        sceneMode === "personas"
                                          ? "Escribí el diálogo que dirán en la escena..."
                                          : "Describí qué debería pasar en esta escena..."
                                      }
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[88px] placeholder:text-neutral-400"
                                    />
                                    {/* Mejorar prompt: el escritor del Creador, suelto
                                        (el mismo botón que Redes). Necesita algo de qué
                                        partir: texto o una referencia. Sólo en escenas
                                        generales: en personas el texto es el diálogo
                                        (ADR-2026-10-05-el-estudio-usa-el-metaprompting-de-redes). */}
                                    {sceneMode === "general" && (
                                      <div className="mt-2 flex justify-end">
                                        <button
                                          type="button"
                                          onClick={() => void mejorarPromptDeEscena()}
                                          disabled={
                                            sceneMejorando ||
                                            (!scenePrompt.trim() &&
                                              sceneRefAssetIds.length === 0 &&
                                              !sceneFirstFrameAssetId)
                                          }
                                          aria-busy={sceneMejorando || undefined}
                                          className={CLASE_BOTON_MEJORAR}
                                        >
                                          {sceneMejorando ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Wand2 className="w-3.5 h-3.5" />
                                          )}
                                          {sceneMejorando ? "Mejorando…" : "Mejorar prompt"}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Motion dialog: Prompt + Diálogo + Voz + Duración.
                          APAGADO: su prompt, su diálogo, su voz y su duración
                          se repartieron entre la pantalla principal y las
                          configuraciones de diálogo. Queda en el código porque
                          un clip de diálogo YA CREADO se abre por este camino y
                          todavía hay que decidir cómo se edita. */}
                          {false &&
                            sceneMode === "personas" &&
                            scenePersonType === "motion" && (
                              <>
                                <div>
                                  <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                    Prompt de la escena
                                  </p>
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-prompt")}`}
                                  >
                                    <textarea
                                      value={scenePrompt}
                                      onChange={(e) =>
                                        setScenePrompt(e.target.value)
                                      }
                                      placeholder="Describí qué debería pasar en esta escena..."
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[88px] placeholder:text-neutral-400"
                                    />
                                  </div>
                                </div>

                                <div data-tour="dialog-text">
                                  <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                    Diálogo de la escena
                                  </p>
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("scene-dialogue")}`}
                                  >
                                    <textarea
                                      value={sceneDialogue}
                                      onChange={(e) =>
                                        setSceneDialogue(e.target.value)
                                      }
                                      placeholder="Escribí el diálogo que dirán en la escena..."
                                      className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[120px] placeholder:text-neutral-400"
                                    />
                                  </div>
                                </div>

                                {/* Voice selector */}
                                <div data-tour="dialog-voice">
                                  <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                    Voz
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowAudioAiVoiceSelector(true)
                                    }
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                                  >
                                    <span className="flex items-center gap-3 min-w-0">
                                      <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
                                        <Mic className="w-4 h-4 text-brand-primary" />
                                      </div>
                                      <div className="flex flex-col items-start min-w-0">
                                        <span className="text-[13px] text-brand-black/45 leading-tight">
                                          {audioAiVoiceId
                                            ? "Voz seleccionada"
                                            : "Seleccioná una voz"}
                                        </span>
                                        <span
                                          className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                        >
                                          {audioAiVoiceName || "Ninguna"}
                                        </span>
                                      </div>
                                    </span>
                                    <ChevronRight className="w-4 h-4 text-brand-black/40 shrink-0" />
                                  </button>
                                </div>

                                {/* Duration — thin vertical bars, snapping to UGC options */}
                                <div>
                                  <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                    Duración
                                  </p>
                                  <div className="flex items-center gap-2.5">
                                    {(() => {
                                      const DURATION_OPTIONS = [
                                        4, 6, 8, 12, 14, 16, 20, 22, 24, 28, 30,
                                        32,
                                      ];
                                      const TOTAL_BARS = 90;
                                      const snapFromRatio = (ratio: number) => {
                                        const clamped = Math.max(
                                          0,
                                          Math.min(1, ratio),
                                        );
                                        const idx = Math.round(
                                          clamped *
                                            (DURATION_OPTIONS.length - 1),
                                        );
                                        return DURATION_OPTIONS[idx];
                                      };
                                      const currentIdx = Math.max(
                                        0,
                                        DURATION_OPTIONS.indexOf(sceneDuration),
                                      );
                                      const lastSelectedIdx = Math.round(
                                        (currentIdx /
                                          (DURATION_OPTIONS.length - 1)) *
                                          (TOTAL_BARS - 1),
                                      );
                                      return (
                                        <div
                                          className="flex items-center flex-1 select-none"
                                          style={{
                                            height: 36,
                                            touchAction: "none",
                                            cursor: "pointer",
                                          }}
                                          onPointerDown={(e) => {
                                            const el = e.currentTarget;
                                            el.setPointerCapture(e.pointerId);
                                            const rect =
                                              el.getBoundingClientRect();
                                            const pick = (clientX: number) =>
                                              setSceneDuration(
                                                snapFromRatio(
                                                  (clientX - rect.left) /
                                                    rect.width,
                                                ),
                                              );
                                            pick(e.clientX);
                                            const onMove = (ev: PointerEvent) =>
                                              pick(ev.clientX);
                                            const onUp = () => {
                                              el.removeEventListener(
                                                "pointermove",
                                                onMove,
                                              );
                                              el.removeEventListener(
                                                "pointerup",
                                                onUp,
                                              );
                                            };
                                            el.addEventListener(
                                              "pointermove",
                                              onMove,
                                            );
                                            el.addEventListener(
                                              "pointerup",
                                              onUp,
                                            );
                                          }}
                                        >
                                          {Array.from(
                                            { length: TOTAL_BARS },
                                            (_, i) => {
                                              const isSelected =
                                                i <= lastSelectedIdx;
                                              const ratio =
                                                lastSelectedIdx > 0
                                                  ? i / lastSelectedIdx
                                                  : 1;
                                              const opacity = 0.3 + ratio * 0.7;
                                              const distFromEdge = Math.abs(
                                                i - lastSelectedIdx,
                                              );
                                              const delayMs = Math.min(
                                                distFromEdge * 4,
                                                120,
                                              );
                                              return (
                                                <div
                                                  key={i}
                                                  style={{
                                                    flex: "1 1 0%",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      width: 1.5,
                                                      height: 32,
                                                      borderRadius: 9999,
                                                      backgroundColor:
                                                        isSelected
                                                          ? `rgb(var(--brand-primary-rgb) / ${opacity})`
                                                          : "#d4d4d8",
                                                      transition: `background-color 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) ${delayMs}ms`,
                                                    }}
                                                  />
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      );
                                    })()}
                                    <span
                                      className="text-[15px] font-semibold text-brand-primary tabular-nums whitespace-nowrap"
                                      style={{
                                        minWidth: 32,
                                        textAlign: "right",
                                      }}
                                    >
                                      {sceneDuration}s
                                    </span>
                                  </div>
                                </div>
                              </>
                            )}

                          {/* Duración de la escena: el MISMO slider que la
                          pastilla de duración del modo Redes (`WakeSlider`,
                          ADR-2026-10-03-wake-slider), dentro de la tarjeta
                          del panel. Topes del contrato: 4 s, y 15 s en
                          Estándar o 30 s en Profesional
                          (ADR-2026-10-05-el-estudio-sin-multiescena-y-con-el-slider-de-redes). */}
                          {(() => {
                            const MIN_DUR = 4;
                            const MAX_DUR =
                              sceneQuality === "profesional" ? 30 : 15;
                            return (
                              <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                                <div className="px-4 py-[11px]">
                                  <div className="flex items-center justify-between mb-2.5">
                                    <span className="text-[15px] text-brand-black">
                                      Duración
                                    </span>
                                    <span className="text-[15px] font-semibold text-brand-primary tabular-nums">
                                      {sceneDuration} seg
                                    </span>
                                  </div>
                                  <WakeSlider
                                    value={sceneDuration}
                                    min={MIN_DUR}
                                    max={MAX_DUR}
                                    step={1}
                                    bars={28}
                                    height={40}
                                    restHeight={12}
                                    gap={3}
                                    onChange={(n) => setSceneDuration(n)}
                                    ariaLabel="Duración de la escena en segundos"
                                    formatValue={(n) => `${n} segundos`}
                                  />
                                  <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums text-[#3C3C43]/45">
                                    <span>{MIN_DUR} seg</span>
                                    <span>{MAX_DUR} seg</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Velocidad de habla. APAGADA acá: se mudó a las
                          configuraciones de diálogo, con el resto de lo
                          hablado. */}
                          {false && sceneMode === "personas" && (
                            <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                              <div className="px-4 py-[11px]">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[15px] text-brand-black">
                                    Velocidad de habla
                                  </span>
                                  <span className="text-[15px] font-medium text-brand-black tabular-nums">
                                    {sceneSpeechSpeed.toFixed(2)}x
                                  </span>
                                </div>
                                {/* Mismo control que Duración: tarjeta con el
                                nombre y el valor en una línea y un range nativo
                                debajo. Antes eran 90 barritas: el valor había
                                que leerlo contando cuáles estaban teñidas, y
                                dos ajustes del mismo panel se ajustaban de dos
                                maneras distintas. El rango sigue siendo el que
                                acepta ElevenLabs —0.7 a 1.2, de a 0.05— fuera
                                del cual devuelve 400 y no hay audio
                                (ADR-2026-09-07-velocidad-de-voz-fuera-del-rango-de-elevenlabs). */}
                                <input
                                  type="range"
                                  min={0.7}
                                  max={1.2}
                                  step={0.05}
                                  value={sceneSpeechSpeed}
                                  onChange={(e) =>
                                    setSceneSpeechSpeed(Number(e.target.value))
                                  }
                                  aria-label="Velocidad de habla de la escena"
                                  className="w-full accent-brand-primary cursor-pointer"
                                />
                              </div>
                            </div>
                          )}

                          {/* Calidad + Resolución — segmentados, no dropdowns.
                          Es el control de "Calidad" del modo automático
                          (`CalidadSegmentada` en AjustesRapidosProducto): celda
                          gris con la etiqueta arriba y las opciones a la vista
                          sobre un riel, la elegida en blanco con sombra. Son
                          pocos valores y cortos: verlos todos dice de una qué
                          se puede elegir y cuánto margen queda para arriba, sin
                          abrir nada. */}
                          <div className="flex flex-col gap-2">
                            {/* Calidad */}
                            {sceneMode !== "personas" && (
                              <div className="min-w-0 px-3 py-2 rounded-xl bg-black/[0.035]">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#3C3C43]/45">
                                  Calidad
                                </span>
                                <span className="mt-1 flex items-center gap-0.5 p-0.5 rounded-lg bg-black/[0.05]">
                                  {(["estandar", "profesional"] as const).map(
                                    (q) => (
                                      <button
                                        key={q}
                                        type="button"
                                        onClick={() => setSceneQuality(q)}
                                        className={`flex-1 min-w-0 h-7 rounded-md text-[12px] font-semibold transition-colors ${
                                          sceneQuality === q
                                            ? "bg-white text-brand-black/90 shadow-sm"
                                            : "text-[#3C3C43]/55 hover:text-brand-black/80"
                                        }`}
                                      >
                                        {q === "profesional"
                                          ? "Profesional"
                                          : "Estándar"}
                                      </button>
                                    ),
                                  )}
                                </span>
                                {/* La bajada de la opción elegida. En el
                                dropdown viejo cada opción traía su explicación;
                                acá no hay lista donde ponerlas, así que se
                                muestra la de la activa — es el dato que decide
                                la elección (cuántos segundos y cuántos
                                créditos). */}
                                <span className="mt-1.5 block text-[11px] leading-tight text-brand-black/45">
                                  {sceneQuality === "profesional"
                                    ? "El mejor modelo, videos de hasta 30 segundos. Consume más créditos"
                                    : "Buena calidad, videos de hasta 15 segundos"}
                                </span>
                              </div>
                            )}

                            {/* Resolución */}
                            <div className="min-w-0 px-3 py-2 rounded-xl bg-black/[0.035]">
                              <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#3C3C43]/45">
                                Resolución
                              </span>
                              <span className="mt-1 flex items-center gap-0.5 p-0.5 rounded-lg bg-black/[0.05]">
                                {(["480p", "720p", "1080p"] as const)
                                  .filter(
                                    (r) =>
                                      !(
                                        sceneMode === "personas" && r === "480p"
                                      ),
                                  )
                                  .map((r) => (
                                    <button
                                      key={r}
                                      type="button"
                                      onClick={() => setSceneResolution(r)}
                                      title={esBorrador(sceneQuality, r) ? AYUDA_BORRADOR : undefined}
                                      className={`flex-1 min-w-0 h-7 rounded-md text-[12px] font-semibold transition-colors ${
                                        sceneResolution === r
                                          ? "bg-white text-brand-black/90 shadow-sm"
                                          : "text-[#3C3C43]/55 hover:text-brand-black/80"
                                      }`}
                                    >
                                      {r}
                                      {/* 480p Profesional es borrador: después se pasa a 1080p
                                          (ADR-2026-09-27-borrador-480p-y-pasar-a-1080p). */}
                                      {esBorrador(sceneQuality, r) && (
                                        <span className="ml-1 text-[9px] font-semibold uppercase tracking-wide text-brand-black/45">
                                          Borrador
                                        </span>
                                      )}
                                    </button>
                                  ))}
                              </span>
                            </div>
                          </div>

                        </div>

                        {/* Pie del panel: Generar, que desde que el Director
                        se fue del pie va solo y toma todo el ancho. */}
                        <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
                          {(() => {
                            // Dialogue mode (HeyGen + Veo) always needs a
                            // selected voice — the workflow plugs it into the
                            // ElevenLabs URL, which 404s when the id is empty.
                            const missingVoice =
                              sceneMode === "personas" && !audioAiVoiceId;
                            // Audio input mode also needs the user's audio
                            // uploaded (no URL → nothing to feed s2s).
                            const missingAudio =
                              sceneMode === "personas" &&
                              scenePersonType === "static" &&
                              sceneDialogueInputMode === "audio" &&
                              !sceneDialogueAudio;
                            // Sin prompt el proveedor responde 400 "Prompt is
                            // required" y el intento se pierde entero: el
                            // usuario esperaba medio minuto y el clip quedaba en
                            // error (incidente 2026-09-07/08). El botón deja de
                            // ofrecer lo imposible; el backend lo rechaza igual.
                            const missingPrompt = faltaPromptDeEscena({
                              sceneMode,
                              prompt: scenePrompt,
                              multiescena: false,
                            });
                            const disabled =
                              missingVoice ||
                              missingAudio ||
                              missingPrompt;
                            const disabledReason = missingVoice
                                ? "Elegí una voz primero"
                                : missingAudio
                                  ? "Grabá o subí un audio primero"
                                  : missingPrompt
                                    ? "Escribí qué querés que pase en la escena"
                                    : undefined;
                            return (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    if (!disabled) addAiSceneClip("generate");
                                  }}
                                  disabled={disabled}
                                  title={disabledReason}
                                  className={`flex-1 h-10 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                    disabled
                                      ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                      : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                  }`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                  Generar
                                  {!disabled && (
                                    <CreditBadge
                                      credits={costoDeLaEscena}
                                      tone="on-primary"
                                      size="sm"
                                    />
                                  )}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {activePanel === "ai-audio" && (
                      <div
                        className={`flex flex-col min-w-0 ${
                          // En el teléfono la hoja mide por su contenido, así
                          // que el panel NO se estira al alto del contenedor
                          // (`h-full` lo forzaría a 100% de un alto que
                          // justamente se está calculando).
                          enElTelefono ? "flex-auto" : "flex-1 h-full"
                        }`}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between px-3 h-12">
                          {/* Sin flecha de volver: llevaba al panel Archivos
                          (tab Audio), que era de donde se llegaba acá cuando
                          generar audio colgaba de ahí. Hoy "Audio con IA" es su
                          propia baldosa del rail, así que ese "atrás" apuntaba a
                          un lugar del que el usuario no venía. */}
                          <div className="flex items-center gap-1.5 min-w-0">
                            <h3 className="text-[15px] font-semibold text-brand-black/90 truncate">
                              Audio con IA
                            </h3>
                          </div>
                          <button
                            onClick={() => setActivePanel("none")}
                            className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                          >
                            <Plus className="w-4 h-4 rotate-45" />
                          </button>
                        </div>
                        <div className="h-px bg-black/[0.08]" />

                        {/* Scrollable content. `flex-auto` en el teléfono:
                        con basis 0 este cuerpo aportaría 0 al alto automático
                        de la hoja (su mínimo automático es cero por tener
                        `overflow` propio) y la hoja se colapsaría. Con basis
                        auto aporta su contenido y, si pasa del tope de la hoja,
                        encoge y scrollea como siempre. */}
                        <div
                          className={`${
                            enElTelefono ? "flex-auto" : "flex-1"
                          } min-h-0 overflow-y-auto px-3 py-4 space-y-5`}
                        >
                          {/* Type selector (Voz en off / Música) — segmented control */}
                          <div data-tour="audio-type">
                            <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                              Tipo de audio
                            </p>
                            <div className="relative flex items-center p-[3px] bg-black/[0.06] rounded-[10px]">
                              <div
                                className="absolute top-[3px] bottom-[3px] rounded-[8px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
                                style={{
                                  width: "calc(33.333% - 2px)",
                                  left:
                                    audioAiType === "voice"
                                      ? "3px"
                                      : audioAiType === "music"
                                        ? "calc(33.333% + 1px)"
                                        : "calc(66.666% - 1px)",
                                }}
                              />
                              {(["voice", "music", "sfx"] as const).map((t) => (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setAudioAiType(t)}
                                  className={`relative z-10 flex-1 h-8 inline-flex items-center justify-center gap-1.5 text-[13px] font-medium rounded-[8px] text-center transition-colors duration-200 ${
                                    audioAiType === t
                                      ? "text-brand-black/90"
                                      : "text-brand-black/50"
                                  }`}
                                >
                                  {t === "voice" ? (
                                    <Mic className="w-3.5 h-3.5" />
                                  ) : t === "music" ? (
                                    <Music className="w-3.5 h-3.5" />
                                  ) : (
                                    <AudioLines className="w-3.5 h-3.5" />
                                  )}
                                  {t === "voice"
                                    ? "Voz en off"
                                    : t === "music"
                                      ? "Música"
                                      : "Efecto"}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Voz — visible only in voice mode */}
                          {audioAiType === "voice" && (
                            <div>
                              <p className="text-[14px] font-semibold text-brand-black/90 mb-2">
                                Voz
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAudioAiVoiceSelector(true)
                                }
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors"
                              >
                                <span className="flex items-center gap-3 min-w-0">
                                  <div className="w-9 h-9 rounded-sm bg-brand-primary/10 flex items-center justify-center shrink-0">
                                    <Mic className="w-4 h-4 text-brand-primary" />
                                  </div>
                                  <div className="flex flex-col items-start min-w-0">
                                    <span className="text-[13px] text-brand-black/45 leading-tight">
                                      {audioAiVoiceId
                                        ? "Voz seleccionada"
                                        : "Seleccioná una voz"}
                                    </span>
                                    <span
                                      className={`text-[15px] font-medium leading-tight truncate max-w-full ${audioAiVoiceId ? "text-brand-black/90" : "text-brand-black/50"}`}
                                    >
                                      {audioAiVoiceName || "Ninguna"}
                                    </span>
                                  </div>
                                </span>
                                <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                              </button>
                            </div>
                          )}

                          {/* Prompt — label & placeholder change with type */}
                          <div data-tour="audio-text">
                            <div className="flex items-center justify-between mb-2 gap-3">
                              <p className="text-[14px] font-semibold text-brand-black/90">
                                {audioAiType === "voice"
                                  ? "Diálogo"
                                  : audioAiType === "sfx"
                                    ? "Descripción del efecto"
                                    : "Descripción musical"}
                              </p>
                              {audioAiType === "voice" && (
                                <div className="inline-flex p-0.5 bg-neutral-100 rounded-md text-[12px] font-medium">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAudioAiDialogueInputMode("text")
                                    }
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                      audioAiDialogueInputMode === "text"
                                        ? "bg-white text-brand-black/90 shadow-sm"
                                        : "text-brand-black/55 hover:text-brand-black/80"
                                    }`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                    Escribir
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAudioAiDialogueInputMode("audio")
                                    }
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
                                      audioAiDialogueInputMode === "audio"
                                        ? "bg-white text-brand-black/90 shadow-sm"
                                        : "text-brand-black/55 hover:text-brand-black/80"
                                    }`}
                                  >
                                    <Mic className="w-3.5 h-3.5" />
                                    Grabar
                                  </button>
                                </div>
                              )}
                            </div>
                            {audioAiType === "voice" &&
                            audioAiDialogueInputMode === "audio" ? (
                              <DialogueAudioInput
                                value={audioAiDialogueAudio}
                                onChange={setAudioAiDialogueAudio}
                                projectId={projectId || null}
                                companyId={companyId}
                              />
                            ) : (
                              <div
                                className={`bg-white border border-neutral-200 rounded-lg p-4${panelBounceClass("audio-prompt")}`}
                              >
                                <textarea
                                  value={audioAiPrompt}
                                  onChange={(e) =>
                                    setAudioAiPrompt(e.target.value)
                                  }
                                  placeholder={
                                    audioAiType === "voice"
                                      ? "Escribí lo que querés que diga la voz..."
                                      : audioAiType === "sfx"
                                        ? "Describí el sonido (ej: puerta que se cierra fuerte, aplausos en un estadio, whoosh de transición)..."
                                        : "Describí la música que querés generar (género, instrumentos, mood)..."
                                  }
                                  className="w-full text-[15px] text-neutral-900 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[88px] placeholder:text-neutral-400"
                                />
                              </div>
                            )}
                            {audioAiType === "voice" &&
                              audioAiDialogueInputMode === "text" && (
                                <p className="mt-2 px-0.5 text-[13px] text-[#3C3C43]/60 leading-snug">
                                  Tip: usá puntuación (comas, puntos, signos)
                                  para que el diálogo tenga las pausas y el tono
                                  adecuados.
                                </p>
                              )}
                          </div>

                          {/* Speech speed — voice mode only (0.7x .. 1.2x, step 0.05, default 1.1) */}
                          {audioAiType === "voice" && (
                            <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                              <div className="px-4 py-[11px]">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[15px] text-brand-black">
                                    Velocidad de habla
                                  </span>
                                  <span className="text-[15px] font-medium text-brand-black tabular-nums">
                                    {audioAiSpeechSpeed.toFixed(2)}x
                                  </span>
                                </div>
                                {/* Mismo control que Duración: tarjeta con el
                                nombre y el valor en una línea y un range nativo
                                debajo. Antes eran 90 barritas: el valor había
                                que leerlo contando cuáles estaban teñidas, y
                                dos ajustes del mismo panel se ajustaban de dos
                                maneras distintas. El rango sigue siendo el que
                                acepta ElevenLabs —0.7 a 1.2, de a 0.05— fuera
                                del cual devuelve 400 y no hay audio
                                (ADR-2026-09-07-velocidad-de-voz-fuera-del-rango-de-elevenlabs). */}
                                <input
                                  type="range"
                                  min={0.7}
                                  max={1.2}
                                  step={0.05}
                                  value={audioAiSpeechSpeed}
                                  onChange={(e) =>
                                    setAudioAiSpeechSpeed(Number(e.target.value))
                                  }
                                  aria-label="Velocidad de habla de la voz"
                                  className="w-full accent-brand-primary cursor-pointer"
                                />
                              </div>
                            </div>
                          )}

                          {/* Duración — sólo efecto. ElevenLabs lo genera con
                          este largo, de 0,5 a 30 s; cuesta 1 crédito
                          (ADR-2026-09-27-efectos-de-sonido-con-elevenlabs). */}
                          {audioAiType === "sfx" && (
                            <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                              <div className="px-4 py-[11px]">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[15px] text-brand-black">
                                    Duración
                                  </span>
                                  <span className="text-[15px] font-medium text-brand-black tabular-nums">
                                    {acotarDuracionDeEfecto(audioAiDuration).toLocaleString("es-AR")} s
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={EFECTO_DURACION_MIN_SEG}
                                  max={EFECTO_DURACION_MAX_SEG}
                                  step={0.5}
                                  value={acotarDuracionDeEfecto(audioAiDuration)}
                                  onChange={(e) =>
                                    setAudioAiDuration(Number(e.target.value))
                                  }
                                  aria-label="Duración del efecto de sonido"
                                  className="w-full accent-brand-primary cursor-pointer"
                                />
                              </div>
                            </div>
                          )}

                          {/* Duración — sólo música. Mismo control que la
                          velocidad de habla. ElevenLabs Music compone el tema
                          con este largo (de 3 s a 5 min) y cobra 1 crédito
                          cada 30 s; con Suno salía un tema de 2 a 4 min que
                          no se podía acortar
                          (ADR-2026-09-26-musica-con-elevenlabs-y-duracion). */}
                          {audioAiType === "music" && (
                            <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
                              <div className="px-4 py-[11px]">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[15px] text-brand-black">
                                    Duración
                                  </span>
                                  <span className="text-[15px] font-medium text-brand-black tabular-nums">
                                    {formatearDuracionDeMusica(audioAiDuration)}
                                  </span>
                                </div>
                                <input
                                  type="range"
                                  min={MUSICA_DURACION_MIN_SEG}
                                  max={MUSICA_DURACION_MAX_SEG}
                                  step={1}
                                  value={acotarDuracionDeMusica(audioAiDuration)}
                                  onChange={(e) =>
                                    setAudioAiDuration(Number(e.target.value))
                                  }
                                  aria-label="Duración de la música"
                                  className="w-full accent-brand-primary cursor-pointer"
                                />
                              </div>
                            </div>
                          )}

                          {/* Instrumental-only toggle — music mode only */}
                          {audioAiType === "music" && (
                            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border border-neutral-200 rounded-lg">
                              <div className="flex flex-col min-w-0">
                                <span className="text-[14px] font-medium text-brand-black/90">
                                  Solo música instrumental
                                </span>
                                <span className="text-[12px] text-brand-black/50">
                                  Generá sin voces ni letras
                                </span>
                              </div>
                              <button
                                type="button"
                                role="switch"
                                aria-checked={audioAiInstrumentalOnly}
                                onClick={() =>
                                  setAudioAiInstrumentalOnly((v) => !v)
                                }
                                className={`relative shrink-0 w-10 h-6 rounded-full transition-colors ${
                                  audioAiInstrumentalOnly
                                    ? "bg-brand-primary"
                                    : "bg-black/[0.15]"
                                }`}
                              >
                                <span
                                  className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform"
                                  style={{
                                    transform: audioAiInstrumentalOnly
                                      ? "translateX(16px)"
                                      : "translateX(0)",
                                  }}
                                />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Pie del panel: Generar, que desde que el Director
                        se fue del pie va solo y toma todo el ancho. */}
                        <div className="shrink-0 px-3 py-3 border-t border-black/[0.06]">
                          {(() => {
                            // Block reasons, by priority. Each branch sets the
                            // tooltip so the user knows what's missing.
                            const isVoiceTextEmpty =
                              audioAiType === "voice" &&
                              audioAiDialogueInputMode === "text" &&
                              !audioAiPrompt.trim();
                            const isVoiceAudioMissing =
                              audioAiType === "voice" &&
                              audioAiDialogueInputMode === "audio" &&
                              !audioAiDialogueAudio;
                            const isVoiceWithoutVoiceId =
                              audioAiType === "voice" && !audioAiVoiceId;
                            const isMusicEmpty =
                              audioAiType === "music" && !audioAiPrompt.trim();
                            const isSfxEmpty =
                              audioAiType === "sfx" && !audioAiPrompt.trim();
                            const disabled =
                              isVoiceTextEmpty ||
                              isVoiceAudioMissing ||
                              isVoiceWithoutVoiceId ||
                              isMusicEmpty ||
                              isSfxEmpty;
                            const disabledReason = isVoiceAudioMissing
                              ? "Grabá o subí un audio primero"
                              : isVoiceWithoutVoiceId
                                ? "Elegí una voz primero"
                                : isVoiceTextEmpty
                                  ? "Escribí el diálogo primero"
                                  : isMusicEmpty
                                    ? "Describí la música primero"
                                    : isSfxEmpty
                                      ? "Describí el efecto primero"
                                      : undefined;
                            return (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    if (!disabled) addAiAudioClip("generate");
                                  }}
                                  disabled={disabled}
                                  title={disabledReason}
                                  className={`flex-1 h-10 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center justify-center gap-2 ${
                                    disabled
                                      ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                                      : "bg-brand-primary hover:bg-brand-primary-hover text-white"
                                  }`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                  Generar
                                  {!disabled && (
                                    <CreditBadge
                                      credits={audioPanelCost}
                                      tone="on-primary"
                                      size="sm"
                                    />
                                  )}
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {activePanel === "ai-image-gen" &&
                      (() => {
                        const detail = imageGenDetailId
                          ? imageGenResults.find(
                              (r) => r.id === imageGenDetailId,
                            )
                          : null;
                        const slotPreviewUrl = slotPreview
                          ? assetPreviewUrls[slotPreview.assetId]
                          : null;
                        const slotLabel = slotPreview
                          ? slotPreview.target.kind === "first"
                            ? "Primer frame"
                            : slotPreview.target.kind === "last"
                              ? "Último frame"
                              : "Imagen de referencia"
                          : "";
                        const examples = [
                          "Un atardecer cinematográfico sobre montañas nevadas",
                          "Una taza de café humeante sobre una mesa de madera rústica",
                          "Un paisaje futurista con luces de neón al anochecer",
                          "Una flor delicada con gotas de rocío y fondo desenfocado",
                        ];
                        // La grilla del panel de generar muestra SOLO lo que se
                        // generó en esta sesión (el historial completo está en
                        // Multimedia/Historial, no tiene sentido repetirlo acá).
                        const sessionGenResults = imageGenResults.filter(
                          (r) => r.session,
                        );
                        return (
                          <div className="flex-1 h-full flex flex-col min-w-0 relative">
                            {/* Header */}
                            <div className="flex items-center gap-2 px-3 h-12">
                              <button
                                onClick={backToSceneFromImageGen}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/60 hover:bg-black/[0.1] transition-colors"
                                title="Volver"
                              >
                                <ChevronLeft className="w-4 h-4" />
                              </button>
                              <h3 className="text-[15px] font-semibold text-brand-black/90">
                                {slotPreview
                                  ? slotLabel
                                  : detail
                                    ? "Imagen generada"
                                    : "Generar imagen con IA"}
                              </h3>
                            </div>
                            <div className="h-px bg-black/[0.08]" />

                            {/* Content */}
                            {slotPreview ? (
                              /* ── Slot-preview view ────────────────────────────── */
                              <div className="flex-1 min-h-0 flex flex-col bg-white">
                                {/* Image viewer */}
                                <div className="flex-1 min-h-0 relative flex items-center justify-center p-3">
                                  {slotPreviewUrl ? (
                                    <img
                                      src={slotPreviewUrl}
                                      alt="Imagen seleccionada"
                                      className="max-w-full max-h-full object-contain rounded-2xl"
                                    />
                                  ) : (
                                    <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                                  )}
                                </div>

                                {/* Action bar — Utilizar / Editar OR edit prompt card */}
                                {slotPreviewEditing ? (
                                  <div className="flex-shrink-0 px-3 pb-3 pt-2 bg-white">
                                    <div
                                      className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]${panelBounceClass("slot-edit-prompt")}`}
                                    >
                                      <textarea
                                        value={slotPreviewEditPrompt}
                                        onChange={(e) =>
                                          setSlotPreviewEditPrompt(
                                            e.target.value,
                                          )
                                        }
                                        placeholder="Describí el cambio que querés hacer a la imagen..."
                                        className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[90px]"
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-between gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setSlotPreviewEditing(false);
                                            setSlotPreviewEditPrompt("");
                                          }}
                                          className="py-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors"
                                        >
                                          <ChevronLeft className="w-3.5 h-3.5" />
                                          Cancelar
                                        </button>
                                        <button
                                          type="button"
                                          onClick={submitSlotPreviewEdit}
                                          disabled={
                                            !slotPreviewEditPrompt.trim()
                                          }
                                          className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          <Sparkles className="w-3.5 h-3.5" />
                                          Editar
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 px-3 pt-2 pb-3 bg-white">
                                    <div className="grid grid-cols-2 gap-2">
                                      <button
                                        onClick={useSlotPreviewImage}
                                        disabled={!slotPreviewUrl}
                                        className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${slotPreviewUrl ? "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                                      >
                                        <Check className="w-5 h-5" />
                                        <span>Utilizar</span>
                                      </button>
                                      <button
                                        onClick={() =>
                                          setSlotPreviewEditing(true)
                                        }
                                        disabled={!slotPreviewUrl}
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-brand-black/80 border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Pencil className="w-5 h-5" />
                                        <span>Editar</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : detail ? (
                              /* ── Detail view (mirrors mobile GeneratedImagesInline) ── */
                              <div className="flex-1 min-h-0 flex flex-col bg-white">
                                {/* Image viewer with horizontal snap-scroll between all results */}
                                <div className="flex-1 min-h-0 relative">
                                  {imageGenEditSubmitting && (
                                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-neutral-200 rounded-full shadow-sm">
                                        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
                                        <span className="text-[13px] font-medium text-neutral-700">
                                          Editando imagen
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                  <div
                                    ref={imageGenDetailScrollRef}
                                    className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
                                    style={{
                                      scrollbarWidth: "none",
                                      msOverflowStyle: "none",
                                      WebkitOverflowScrolling: "touch",
                                    }}
                                  >
                                    {imageGenResults.map((r) => (
                                      <div
                                        key={`detail-snap-${r.id}`}
                                        className="w-full h-full flex-shrink-0 snap-center flex items-center justify-center p-3"
                                      >
                                        {r.signedUrl ? (
                                          <img
                                            src={r.signedUrl}
                                            alt={`Generada ${r.id}`}
                                            className="max-w-full max-h-full object-contain rounded-2xl"
                                            onError={() =>
                                              refreshGenImageSignedUrl(r.id)
                                            }
                                          />
                                        ) : (
                                          <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Action bar — 3 buttons OR edit prompt card when editing */}
                                {imageGenEditing ? (
                                  <div className="flex-shrink-0 px-3 pb-3 pt-2 bg-white">
                                    <div
                                      className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] ${imageGenEditSubmitting ? "shine-effect" : ""}${panelBounceClass("gen-edit-prompt")}`}
                                    >
                                      <textarea
                                        value={imageGenEditPrompt}
                                        onChange={(e) =>
                                          setImageGenEditPrompt(e.target.value)
                                        }
                                        placeholder="Describí el cambio que querés hacer a la imagen..."
                                        disabled={imageGenEditSubmitting}
                                        className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[90px] disabled:cursor-not-allowed"
                                        autoFocus
                                      />
                                      <div className="flex items-center justify-between gap-2">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setImageGenEditing(false);
                                            setImageGenEditPrompt("");
                                            setImageGenEditError(null);
                                          }}
                                          disabled={imageGenEditSubmitting}
                                          className="py-2 inline-flex items-center gap-1.5 text-[13px] text-brand-black/60 hover:text-brand-black/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          <ChevronLeft className="w-3.5 h-3.5" />
                                          Cancelar
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleEditImage}
                                          disabled={
                                            !imageGenEditPrompt.trim() ||
                                            imageGenEditSubmitting
                                          }
                                          className="py-2 px-4 flex items-center gap-1.5 rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          {imageGenEditSubmitting ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Sparkles className="w-3.5 h-3.5" />
                                          )}
                                          Editar
                                          <CreditBadge
                                            credits={creditsForImage()}
                                            tone="on-primary"
                                            size="sm"
                                          />
                                        </button>
                                      </div>
                                    </div>
                                    {imageGenEditError && (
                                      <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-[8px] text-[12px] text-red-700">
                                        {imageGenEditError}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 px-3 pt-2 pb-3 bg-white">
                                    <div
                                      className={`grid gap-2 ${imageGenTarget ? "grid-cols-3" : "grid-cols-2"}`}
                                    >
                                      {/* Utilizar — SOLO cuando hay un slot de
                                          escena al que asignar (frame inicial/
                                          final/referencia). Desde Multimedia no
                                          tiene sentido (subía un duplicado y
                                          cerraba), así que no aparece. */}
                                      {imageGenTarget && (
                                        <button
                                          onClick={() =>
                                            useGeneratedImage(detail)
                                          }
                                          disabled={!detail.signedUrl}
                                          className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] ${detail.signedUrl ? "bg-brand-primary text-white hover:bg-brand-primary-hover shadow-sm" : "bg-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                                        >
                                          <Check className="w-5 h-5" />
                                          <span>Utilizar</span>
                                        </button>
                                      )}
                                      {/* Editar */}
                                      <button
                                        onClick={() => setImageGenEditing(true)}
                                        disabled={!detail.signedUrl}
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-brand-black/80 border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Pencil className="w-5 h-5" />
                                        <span>Editar</span>
                                      </button>
                                      {/* Eliminar */}
                                      <button
                                        onClick={() =>
                                          deleteGeneratedImage(detail.id)
                                        }
                                        className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md font-medium transition-colors text-[13px] bg-surface-card text-red-600 border border-neutral-200 hover:bg-red-50 hover:border-red-200"
                                      >
                                        <Trash2 className="w-5 h-5" />
                                        <span>Eliminar</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              /* ── Grid + Prompt view ──────────────────────────── */
                              <div className="flex-1 min-h-0 flex flex-col relative">
                                {/* Top: grid (if results) or examples/historial.
                            Stays in normal flow; the prompt card below is
                            absolutely positioned and overlays this area
                            when the user expands the gallery. */}
                                <div className="flex-1 overflow-y-auto px-3 py-4 space-y-4 min-h-0">
                                  {showImageGenHistorial ? (
                                    <div>
                                      <p className="text-[12px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                        Historial
                                      </p>
                                      {imageGenHistorial.length === 0 ? (
                                        <div className="px-3 py-6 text-[13px] text-brand-black/50 text-center bg-white border border-neutral-200 rounded-[10px]">
                                          Sin historial aún
                                        </div>
                                      ) : (
                                        <div className="space-y-1.5">
                                          {imageGenHistorial.map((p, i) => (
                                            <button
                                              key={i}
                                              type="button"
                                              onClick={() => {
                                                setImageGenPrompt(p);
                                                setShowImageGenHistorial(false);
                                              }}
                                              className="w-full text-left px-3 py-2 rounded-[8px] bg-white border border-neutral-200 text-[13px] text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                            >
                                              {p}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : sessionGenResults.length > 0 ||
                                    imageGenPendingCount > 0 ? (
                                    <div className="grid grid-cols-3 gap-2">
                                      {/* Pending placeholders (no ID yet) */}
                                      {Array.from({
                                        length: imageGenPendingCount,
                                      }).map((_, i) => (
                                        <div
                                          key={`pending-${i}`}
                                          className="relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100"
                                        >
                                          <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-24 h-24">
                                              <DotLottieReact
                                                src="https://lottie.host/848266a7-3f51-41c9-bbc0-b55500402910/XzeuQRXrLQ.lottie"
                                                autoplay
                                                loop
                                                style={{
                                                  width: "100%",
                                                  height: "100%",
                                                }}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                      {sessionGenResults.map((r) => (
                                        <button
                                          key={r.id}
                                          type="button"
                                          onClick={() =>
                                            r.signedUrl &&
                                            setImageGenDetailId(r.id)
                                          }
                                          disabled={!r.signedUrl}
                                          className="relative aspect-square rounded-[10px] overflow-hidden bg-neutral-100 transition-transform hover:scale-[0.98] disabled:cursor-default"
                                        >
                                          {r.signedUrl ? (
                                            <img
                                              src={r.signedUrl}
                                              alt="Generada"
                                              className="w-full h-full object-cover"
                                              onError={() =>
                                                refreshGenImageSignedUrl(r.id)
                                              }
                                            />
                                          ) : (
                                            <div className="absolute inset-0 flex items-center justify-center">
                                              <div className="w-24 h-24">
                                                <DotLottieReact
                                                  src="https://lottie.host/848266a7-3f51-41c9-bbc0-b55500402910/XzeuQRXrLQ.lottie"
                                                  autoplay
                                                  loop
                                                  style={{
                                                    width: "100%",
                                                    height: "100%",
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  ) : (
                                    <div>
                                      <p className="text-[12px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
                                        Ejemplos
                                      </p>
                                      <div className="space-y-1.5">
                                        {examples.map((ex, i) => (
                                          <button
                                            key={i}
                                            type="button"
                                            onClick={() =>
                                              setImageGenPrompt(ex)
                                            }
                                            className="w-full text-left px-3 py-2 rounded-[8px] bg-white border border-neutral-200 text-[13px] text-brand-black/80 hover:bg-neutral-50 transition-colors"
                                          >
                                            {ex}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {imageGenError && (
                                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-[8px] text-[12px] text-red-700">
                                      {imageGenError}
                                    </div>
                                  )}
                                </div>

                                {/* Bottom: prompt card (with shadow) — Añadir /
                            Historial left, Generar right. Stays in normal
                            flow at the bottom. */}
                                <div className="shrink-0 px-3 pb-3 pt-2">
                                  <input
                                    ref={imageGenFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageGenAttachUpload}
                                    className="hidden"
                                  />
                                  <div
                                    className={`bg-white border border-neutral-200 rounded-[14px] pl-5 pr-4 py-3 shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)] flex flex-col${panelBounceClass("image-prompt")}`}
                                  >
                                    {imageGenAttachedAssetIds.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mb-2">
                                        {imageGenAttachedAssetIds.map((id) => (
                                          <div
                                            key={`attached-${id}`}
                                            onClick={() =>
                                              assetPreviewUrls[id] &&
                                              setImageGenAttachedPreviewId(id)
                                            }
                                            className="relative w-14 h-14 rounded-[8px] overflow-hidden border border-neutral-200 bg-neutral-100 group cursor-zoom-in"
                                          >
                                            {assetPreviewUrls[id] ? (
                                              <img
                                                src={assetPreviewUrls[id]}
                                                alt="Adjunto"
                                                className="w-full h-full object-cover pointer-events-none"
                                              />
                                            ) : (
                                              <Loader2 className="w-4 h-4 text-neutral-400 animate-spin absolute inset-0 m-auto" />
                                            )}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setImageGenAttachedAssetIds(
                                                  (prev) =>
                                                    prev.filter(
                                                      (x) => x !== id,
                                                    ),
                                                );
                                              }}
                                              className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                                            >
                                              <Plus className="w-2.5 h-2.5 text-white rotate-45" />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    <textarea
                                      ref={imageGenPromptRef}
                                      value={imageGenPrompt}
                                      onChange={(e) =>
                                        setImageGenPrompt(e.target.value)
                                      }
                                      rows={1}
                                      placeholder="Describí la imagen que querés generar..."
                                      className="w-full text-[14px] text-brand-black/90 placeholder:text-brand-black/30 resize-none border-none outline-none focus:ring-0 p-0 bg-transparent min-h-[44px] max-h-[200px] overflow-y-auto mb-2"
                                    />
                                    {/* Acciones + Generar, en UNA línea. El panel
                                        mide 380px: con las acciones con etiqueta
                                        la fila se partía en tres renglones. Añadir
                                        e Historial van como íconos (con tooltip),
                                        Mejorar con su etiqueta —es la acción que
                                        hay que descubrir— y Generar a la derecha.
                                        "Usar modelo" se fue: los modelos están en
                                        la pestaña del selector de Añadir
                                        (ADR-2026-10-06-la-caja-de-imagen-del-estudio-en-una-linea). */}
                                    <div className="flex items-center gap-2">
                                      <div className="flex min-w-0 items-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            // El MISMO selector que "Añadir imagen o
                                            // video" del panel de video, con destino
                                            // "adjuntar"
                                            // (ADR-2026-10-05-anadir-en-imagen-abre-el-selector-del-video).
                                            setShowImageGenHistorial(false);
                                            abrirSelectorDeImagen({ kind: "attach" });
                                          }}
                                          disabled={imageGenUploading}
                                          aria-label="Añadir imagen de referencia"
                                          title="Añadir imagen de referencia"
                                          className={`w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full transition-colors disabled:opacity-50 ${selectorDeImagen?.kind === "attach" ? "text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/15" : "text-brand-black/60 hover:text-brand-black/90 hover:bg-black/[0.05]"}${panelNudgeClass("image-attach")}`}
                                        >
                                          {imageGenUploading ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <Plus className="w-4 h-4" />
                                          )}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setShowImageGenHistorial((v) => !v)
                                          }
                                          aria-label="Historial de prompts"
                                          aria-pressed={showImageGenHistorial}
                                          title="Historial de prompts"
                                          className={`w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-full transition-colors ${showImageGenHistorial ? "text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/15" : "text-brand-black/60 hover:text-brand-black/90 hover:bg-black/[0.05]"}`}
                                        >
                                          <Clock className="w-4 h-4" />
                                        </button>
                                        {/* Mejorar prompt: el escritor de imagen de
                                            Redes. Necesita texto o una referencia. */}
                                        <button
                                          type="button"
                                          onClick={() => void mejorarPromptDeImagen()}
                                          disabled={
                                            imageGenMejorando ||
                                            imageGenBusy ||
                                            (!imageGenPrompt.trim() &&
                                              imageGenAttachedAssetIds.length === 0)
                                          }
                                          aria-busy={imageGenMejorando || undefined}
                                          className="h-8 py-1.5 px-3 inline-flex items-center gap-1.5 rounded-full text-[13px] whitespace-nowrap text-brand-black/60 hover:text-brand-black/90 hover:bg-black/[0.05] transition-colors disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-brand-black/60"
                                        >
                                          {imageGenMejorando ? (
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <Wand2 className="w-3.5 h-3.5" />
                                          )}
                                          {imageGenMejorando ? "Mejorando…" : "Mejorar"}
                                        </button>
                                      </div>
                                      <button
                                        onClick={() => {
                                          void startImageGeneration();
                                        }}
                                        disabled={!imageGenPrompt.trim() || imageGenMejorando}
                                        className="ml-auto shrink-0 py-2 px-4 flex items-center gap-1.5 whitespace-nowrap rounded-md bg-brand-primary hover:bg-brand-primary-hover text-white text-[13px] font-medium shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <Sparkles className="w-3.5 h-3.5" />
                                        Generar
                                        <CreditBadge
                                          credits={creditsForImage()}
                                          tone="on-primary"
                                          size="sm"
                                        />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* ── Enlarged preview overlay for attached image ── */}
                            {imageGenAttachedPreviewId &&
                              assetPreviewUrls[imageGenAttachedPreviewId] && (
                                <div
                                  className="absolute inset-0 z-30 flex items-center justify-center p-6 bg-white/30 backdrop-blur-[4px] animate-in fade-in duration-150"
                                  onClick={() =>
                                    setImageGenAttachedPreviewId(null)
                                  }
                                >
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setImageGenAttachedPreviewId(null);
                                    }}
                                    className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/60 hover:bg-black/80 text-white transition-colors"
                                  >
                                    <Plus className="w-4 h-4 rotate-45" />
                                  </button>
                                  <img
                                    src={
                                      assetPreviewUrls[
                                        imageGenAttachedPreviewId
                                      ]
                                    }
                                    alt="Vista ampliada"
                                    onClick={(e) => e.stopPropagation()}
                                    className="max-w-full max-h-full object-contain rounded-[12px] shadow-2xl"
                                  />
                                </div>
                              )}
                          </div>
                        );
                      })()}
                  </div>
                </div>
              </div>

              {/* ── Voice selector — secondary sidebar to the right of tools panel ── */}
              {(() => {
                const pickerAbierto =
                  showAudioAiVoiceSelector &&
                  ((activePanel === "ai-audio" && audioAiType === "voice") ||
                    (activePanel === "ai-scene" && sceneMode === "personas"));
                return (
              <div
                /* El picker de voz es una SEGUNDA columna al lado del panel en
                   escritorio. En el teléfono no hay lugar para dos columnas: va
                   por encima de la hoja de herramientas, a pantalla completa,
                   como una sub-pantalla más. */
                className={
                  enElTelefono
                    ? `fixed inset-0 z-[55] bg-surface-card transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${
                        pickerAbierto
                          ? "translate-y-0"
                          : "translate-y-full pointer-events-none"
                      }`
                    : "relative z-10 shrink-0 overflow-hidden transition-[width] duration-250 ease-out"
                }
                style={
                  enElTelefono
                    ? {
                        paddingTop: "env(safe-area-inset-top, 0px)",
                        paddingBottom: "env(safe-area-inset-bottom, 0px)",
                      }
                    : { width: pickerAbierto ? 380 : 0 }
                }
              >
                <div
                  className={
                    enElTelefono
                      ? "flex flex-col w-full h-full bg-surface-card"
                      : "flex flex-col min-w-[380px] w-[380px] h-full border-r border-black/[0.06] bg-surface-card"
                  }
                >
                  {audioAiVoiceView === "create" ? (
                    <VoiceCloneCreator
                      userId={currentUserId ?? ""}
                      companyId={companyId ?? ""}
                      onCreated={(voice) => {
                        setClonedVoices((prev) => [voice, ...prev]);
                        setAudioAiVoiceView("list");
                        setAudioAiVoiceTab("mis-voces");
                        // Auto-seleccionar la voz recién creada
                        setAudioAiVoiceId(voice.elevenlabsVoiceId);
                        setAudioAiVoiceName(voice.nombre);
                      }}
                      onBack={() => setAudioAiVoiceView("list")}
                    />
                  ) : (
                    <>
                  <div className="flex items-center justify-between px-3 h-12">
                    <h3 className="text-[15px] font-semibold text-brand-black/90">
                      Seleccionar voz
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        if (audioAiVoicePreviewRef.current) {
                          audioAiVoicePreviewRef.current.pause();
                          audioAiVoicePreviewRef.current = null;
                        }
                        setAudioAiVoicePlayingId(null);
                        setShowAudioAiVoiceSelector(false);
                      }}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-full text-brand-black/40 hover:bg-black/[0.06] transition-colors"
                    >
                      <Plus className="w-4 h-4 rotate-45" />
                    </button>
                  </div>
                  <div className="h-px bg-black/[0.08]" />

                  {/* Tabs: Mis voces / Voces Atlas */}
                  <div className="px-3 pt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setAudioAiVoiceTab("mis-voces")}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                        audioAiVoiceTab === "mis-voces"
                          ? "bg-brand-primary text-white"
                          : "bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]"
                      }`}
                    >
                      Mis voces
                      {clonedVoices.length > 0
                        ? ` (${clonedVoices.length})`
                        : ""}
                    </button>
                    <button
                      type="button"
                      onClick={() => setAudioAiVoiceTab("voces-atlas")}
                      className={`px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                        audioAiVoiceTab === "voces-atlas"
                          ? "bg-brand-primary text-white"
                          : "bg-black/[0.05] text-brand-black/60 hover:bg-black/[0.08]"
                      }`}
                    >
                      Voces Atlas
                    </button>
                  </div>

                  {audioAiVoiceTab === "mis-voces" ? (
                    <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-2">
                      {clonedVoicesLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                        </div>
                      ) : clonedVoices.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
                          <Mic className="w-10 h-10 text-brand-black/30" />
                          <p className="text-[14px] text-brand-black/60">
                            Todavía no clonaste ninguna voz.
                          </p>
                        </div>
                      ) : (
                        clonedVoices.map((voice) => {
                          const isSelected =
                            audioAiVoiceId === voice.elevenlabsVoiceId;
                          return (
                            <button
                              key={voice.id}
                              type="button"
                              onClick={() => {
                                setAudioAiVoiceId(voice.elevenlabsVoiceId);
                                setAudioAiVoiceName(voice.nombre);
                                if (audioAiVoicePreviewRef.current) {
                                  audioAiVoicePreviewRef.current.pause();
                                  audioAiVoicePreviewRef.current = null;
                                }
                                setAudioAiVoicePlayingId(null);
                                setShowAudioAiVoiceSelector(false);
                              }}
                              className={`w-full text-left rounded-[10px] p-3 transition-colors ${
                                isSelected
                                  ? "bg-brand-primary/[0.06] ring-2 ring-brand-primary"
                                  : "bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Mic className="w-4 h-4 text-brand-primary shrink-0" />
                                <span className="text-[14px] font-medium text-brand-black/90 truncate">
                                  {voice.nombre}
                                </span>
                                {isSelected && (
                                  <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                                )}
                              </div>
                              <p className="text-[12px] text-brand-black/50 mt-0.5">
                                Voz clonada
                              </p>
                            </button>
                          );
                        })
                      )}
                      <button
                        type="button"
                        onClick={() => setAudioAiVoiceView("create")}
                        disabled={!currentUserId || !companyId}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 px-3 rounded-md bg-white border border-dashed border-brand-primary/40 hover:border-brand-primary text-brand-primary text-[14px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                        Crear nueva voz clonada
                      </button>
                    </div>
                  ) : (
                    <>
                  {/* Filtros (dropdowns custom en una fila): género + idioma.
                  Botón con label arriba + valor abajo, y un panel flotante
                  abajo con las opciones. Las opciones se derivan dinámicamente
                  de los datos del provider. (Calidad y Resolución del scene
                  panel usaban este mismo patrón hasta que pasaron a
                  segmentados; acá se queda porque la lista de idiomas es larga
                  y variable, no tres valores cortos.) */}
                  <div className="px-3 pt-3 pb-2 flex items-center gap-2">
                    {(() => {
                      const sexOptions = [
                        "Todas",
                        ...Array.from(
                          new Set(
                            audioAiVoices.map((v) => v.sexo).filter(Boolean),
                          ),
                        ).sort(),
                      ];
                      const langOptions = [
                        "Todas",
                        ...Array.from(
                          new Set(
                            audioAiVoices.map((v) => v.idioma).filter(Boolean),
                          ),
                        ).sort(),
                      ];
                      const sexLabel =
                        audioAiVoiceSexFilter === "Todas"
                          ? "Todas"
                          : audioAiVoiceSexFilter;
                      const langLabel =
                        audioAiVoiceLanguageFilter === "Todas"
                          ? "Todos"
                          : audioAiVoiceLanguageFilter;
                      return (
                        <>
                          {/* Género */}
                          <div className="relative flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                setVoiceSexMenuOpen((o) => !o);
                                setVoiceLangMenuOpen(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                            >
                              <div className="flex flex-col items-start leading-tight min-w-0">
                                <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">
                                  Género
                                </span>
                                <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">
                                  {sexLabel}
                                </span>
                              </div>
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${voiceSexMenuOpen ? "rotate-180" : ""}`}
                              />
                            </button>
                            {voiceSexMenuOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setVoiceSexMenuOpen(false)}
                                />
                                <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                                  {sexOptions.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => {
                                        setAudioAiVoiceSexFilter(opt);
                                        setVoiceSexMenuOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                                        audioAiVoiceSexFilter === opt
                                          ? "text-brand-primary font-medium"
                                          : "text-brand-black/80"
                                      }`}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                          {/* Idioma */}
                          <div className="relative flex-1">
                            <button
                              type="button"
                              onClick={() => {
                                setVoiceLangMenuOpen((o) => !o);
                                setVoiceSexMenuOpen(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] border border-neutral-200 hover:bg-neutral-50 transition-colors"
                            >
                              <div className="flex flex-col items-start leading-tight min-w-0">
                                <span className="text-[10px] text-brand-black/40 uppercase tracking-wide">
                                  Idioma
                                </span>
                                <span className="text-[13px] font-medium text-brand-black/80 truncate max-w-full">
                                  {langLabel}
                                </span>
                              </div>
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-neutral-400 shrink-0 transition-transform ${voiceLangMenuOpen ? "rotate-180" : ""}`}
                              />
                            </button>
                            {voiceLangMenuOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setVoiceLangMenuOpen(false)}
                                />
                                <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white rounded-[10px] border border-neutral-200 shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                                  {langOptions.map((opt) => (
                                    <button
                                      key={opt}
                                      type="button"
                                      onClick={() => {
                                        setAudioAiVoiceLanguageFilter(opt);
                                        setVoiceLangMenuOpen(false);
                                      }}
                                      className={`w-full text-left px-3 py-2 hover:bg-neutral-50 transition-colors text-[13px] ${
                                        audioAiVoiceLanguageFilter === opt
                                          ? "text-brand-primary font-medium"
                                          : "text-brand-black/80"
                                      }`}
                                    >
                                      {opt === "Todas" ? "Todos" : opt}
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Voices list */}
                  <div className="flex-1 overflow-y-auto px-3 pb-3">
                    {audioAiVoicesLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                      </div>
                    ) : (
                      (() => {
                        // AND ambos filtros — una voz solo aparece si pasa el de
                        // género Y el de idioma. 'Todas' actúa como "no filtrar
                        // por esa dimensión".
                        const filtered = audioAiVoices.filter((v) => {
                          if (
                            audioAiVoiceSexFilter !== "Todas" &&
                            v.sexo !== audioAiVoiceSexFilter
                          )
                            return false;
                          if (
                            audioAiVoiceLanguageFilter !== "Todas" &&
                            v.idioma !== audioAiVoiceLanguageFilter
                          )
                            return false;
                          return true;
                        });
                        if (filtered.length === 0) {
                          return (
                            <div className="flex items-center justify-center py-12">
                              <p className="text-[13px] text-brand-black/50">
                                No se encontraron voces.
                              </p>
                            </div>
                          );
                        }
                        return (
                          <div className="flex flex-col gap-2">
                            {filtered.map((voice) => {
                              const isSelected = audioAiVoiceId === voice.id;
                              const isPlaying =
                                audioAiVoicePlayingId === voice.id;
                              const shortName = voice.nombre?.includes(" - ")
                                ? voice.nombre.split(" - ")[0]
                                : voice.nombre || "Sin nombre";
                              const desc =
                                voice.descripcion &&
                                !voice.descripcion.includes("SILENT THOUGHT") &&
                                !voice.descripcion.includes("\\n")
                                  ? voice.descripcion.length > 100
                                    ? voice.descripcion.slice(0, 100) + "…"
                                    : voice.descripcion
                                  : "";
                              return (
                                <button
                                  key={voice.id}
                                  type="button"
                                  onClick={() => {
                                    setAudioAiVoiceId(voice.id);
                                    setAudioAiVoiceName(shortName);
                                    if (audioAiVoicePreviewRef.current) {
                                      audioAiVoicePreviewRef.current.pause();
                                      audioAiVoicePreviewRef.current = null;
                                    }
                                    setAudioAiVoicePlayingId(null);
                                    setShowAudioAiVoiceSelector(false);
                                  }}
                                  className={`w-full text-left rounded-[10px] p-3 transition-all ${
                                    isSelected
                                      ? "bg-brand-primary/[0.06] ring-2 ring-brand-primary"
                                      : "bg-white ring-1 ring-black/[0.06] hover:bg-black/[0.02]"
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[14px] font-medium text-brand-black/90 truncate">
                                          {shortName}
                                        </span>
                                        {isSelected && (
                                          <Check className="w-3.5 h-3.5 text-brand-primary shrink-0" />
                                        )}
                                      </div>
                                      {(voice.idioma || voice.acento) && (
                                        <p className="text-[12px] text-[#3C3C43]/40 mt-0.5 flex items-center gap-1.5">
                                          {voice.idioma && (
                                            <span>{voice.idioma}</span>
                                          )}
                                          {voice.idioma && voice.acento && (
                                            <span className="text-[#3C3C43]/25">
                                              ·
                                            </span>
                                          )}
                                          {voice.acento && (
                                            <span>{voice.acento}</span>
                                          )}
                                        </p>
                                      )}
                                      {(voice.sexo || desc) && (
                                        <p className="text-[12px] text-[#3C3C43]/60 mt-1 leading-relaxed line-clamp-2">
                                          {voice.sexo && (
                                            <span className="font-medium">
                                              {voice.sexo}
                                            </span>
                                          )}
                                          {voice.sexo && desc && (
                                            <span> · </span>
                                          )}
                                          {desc}
                                        </p>
                                      )}
                                    </div>
                                    {voice.preview_url && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            audioAiVoicePlayingId === voice.id
                                          ) {
                                            if (
                                              audioAiVoicePreviewRef.current
                                            ) {
                                              audioAiVoicePreviewRef.current.pause();
                                              audioAiVoicePreviewRef.current =
                                                null;
                                            }
                                            setAudioAiVoicePlayingId(null);
                                            return;
                                          }
                                          if (audioAiVoicePreviewRef.current) {
                                            audioAiVoicePreviewRef.current.pause();
                                            audioAiVoicePreviewRef.current =
                                              null;
                                          }
                                          const audio = new Audio(
                                            voice.preview_url,
                                          );
                                          audioAiVoicePreviewRef.current =
                                            audio;
                                          setAudioAiVoicePlayingId(voice.id);
                                          audio
                                            .play()
                                            .catch(() =>
                                              setAudioAiVoicePlayingId(null),
                                            );
                                          audio.addEventListener(
                                            "ended",
                                            () => {
                                              setAudioAiVoicePlayingId(null);
                                              audioAiVoicePreviewRef.current =
                                                null;
                                            },
                                          );
                                        }}
                                        className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                          isPlaying
                                            ? "bg-brand-primary text-white"
                                            : "bg-black/[0.05] text-brand-black/50 hover:bg-black/[0.08]"
                                        }`}
                                      >
                                        {isPlaying ? (
                                          <Pause className="w-4 h-4" />
                                        ) : (
                                          <Play className="w-4 h-4 ml-0.5" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                  </div>
                    </>
                  )}
                    </>
                  )}
                </div>
              </div>
                );
              })()}

              {/* ── Center: Toolbar + Preview + Timeline ─────────────────────── */}
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* ── Preview area ─────────────────────────────────────────────── */}
                {/* El aire vertical no es decorativo: el marco de encuadre tiene
                manijas que viven por FUERA del cuadro —el círculo de rotación a
                `SEPARACION_ROTACION` del borde, y el cartelito de grados en esa
                misma franja— y sin lugar quedan tapadas por el topbar, que
                además se come el click y deselecciona el clip en vez de
                girarlo. `py-14` reserva esos 56px. El cuadro pierde alto y a
                cambio el video queda flotando en el escenario, que es como se
                para en cualquier editor. */}
                <div
                  /* `py-14` reserva los 56px que necesitan las manijas del
                     encuadre: el círculo de rotación vive a
                     `SEPARACION_ROTACION` (36px) del borde y mide 34, así que
                     su mitad de arriba pide 53px de aire.

                     En el teléfono la franja se queda en 24px: reservar los 56
                     dejaba una banda vacía arriba y un video más chico. Cuando
                     el clip llega tan arriba que el círculo no entra afuera, el
                     marco lo dibuja ADENTRO del cuadro (`circuloDeRotacion`
                     pasa a "adentro"), que es lo que el usuario pidió.
                     Ver ADR-2026-09-18-el-circulo-de-rotacion-entra-al-cuadro. */
                  className={`flex-1 flex items-stretch justify-center bg-brand-black/[0.03] min-h-0 relative ${
                    enElTelefono ? "px-3 py-6" : "px-4 py-14"
                  }`}
                  /* Soltar sobre el cuadro es lo mismo que tocar la ficha, pero
                     en el PLAYHEAD en vez de al inicio
                     (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta). */
                  onDragOver={(e) => {
                    const dt = e.dataTransfer;
                    if (!familiaDelArrastre(dt) && !arrastraArchivos(dt)) return;
                    e.preventDefault();
                    dt.dropEffect = "copy";
                    if (!arrastrandoSobreElPreview)
                      setArrastrandoSobreElPreview(true);
                  }}
                  onDragLeave={(e) => {
                    if (
                      e.relatedTarget instanceof Node &&
                      e.currentTarget.contains(e.relatedTarget)
                    )
                      return;
                    setArrastrandoSobreElPreview(false);
                  }}
                  onDrop={(e) => {
                    const dt = e.dataTransfer;
                    setArrastrandoSobreElPreview(false);
                    if (!familiaDelArrastre(dt) && !arrastraArchivos(dt)) return;
                    e.preventDefault();
                    void soltarEnElEstudio(dt, {
                      startTime: Math.max(0, currentTimeRef.current),
                    });
                  }}
                >
                  {arrastrandoSobreElPreview && (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-3 z-40 flex items-center justify-center rounded-xl border-2 border-dashed border-brand-primary bg-brand-primary/[0.06]"
                    >
                      <span className="rounded-full bg-white px-3 py-1.5 text-[12.5px] font-medium text-brand-black shadow-sm">
                        Soltá para agregar al video
                      </span>
                    </div>
                  )}
                  {/* Contenedor del cuadro SIN recorte. Calza exacto con el
                  preview y existe sólo para que el marco de encuadre pueda
                  sobresalir: el cuadro de abajo necesita `overflow-hidden`
                  (si no, un clip agrandado se derrama sobre la pantalla), y
                  eso se comía la manija de rotación y la mitad de cada
                  manija de esquina, que caen JUSTO sobre el borde cuando el
                  clip todavía llena el cuadro. */}
                  <div
                    className="relative"
                    style={{
                      height: "100%",
                      maxWidth: "100%",
                      aspectRatio:
                        aspectRatio === "16:9"
                          ? "16/9"
                          : aspectRatio === "9:16"
                            ? "9/16"
                            : "3/4",
                    }}
                  >
                  {/* ── Chevron para agrandar el video ───────────────────
                  Va ADENTRO del cuadro, apoyado en su borde de abajo (y no al
                  pie del escenario): pegado al escenario quedaba a caballo
                  entre la imagen y el gris, y con un formato apaisado —donde
                  el video no llega al piso— se iba a flotar solo en el gris.

                  Tocarlo achica la timeline al piso —queda la regla y una sola
                  fila, para no perder de vista dónde está el cursor— y el
                  video se queda con todo el alto que sobra; tocarlo de nuevo
                  devuelve el alto que había.

                  La zona de toque es 64×44 (lo que se VE es una pastilla de
                  36×26): un chevron de 16px no se acierta con el pulgar.
                  `data-clip-ui` para que el pointerdown global no lo tome como
                  un toque "afuera" y deseleccione el clip.
                  Ver ADR-2026-09-17-el-chevron-agranda-el-video-en-el-telefono. */}
                  {enElTelefono && (
                    <button
                      type="button"
                      data-clip-ui="true"
                      onClick={alternarPreviewAmpliado}
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 z-30 w-16 h-11 flex items-end justify-center pb-2 active:opacity-60 transition-opacity"
                      aria-label={
                        previewAmpliado
                          ? "Volver a mostrar la timeline"
                          : "Agrandar el video"
                      }
                      aria-expanded={previewAmpliado}
                    >
                      {/* La pastilla se hunde un poco al tocarla y el chevron
                      GIRA en vez de cambiar de ícono: el giro cuenta que es el
                      mismo control que va y vuelve, y acompaña a la tarjeta de
                      la timeline, que baja con la misma duración. */}
                      <span className="w-9 h-[26px] rounded-full bg-black/25 backdrop-blur-[2px] flex items-center justify-center transition-transform duration-200 ease-out active:scale-90">
                        <ChevronDown
                          className={`w-4 h-4 text-white transition-transform duration-[260ms] ease-out ${
                            previewAmpliado ? "rotate-180" : "rotate-0"
                          }`}
                        />
                      </span>
                    </button>
                  )}

                  {/* ── Acciones del clip, flotando a la IZQUIERDA del cuadro
                  Columna de íconos con una palabra cada uno (el nombre
                  completo queda en el tooltip).
                  Vive afuera del cuadro para no taparle nada al video.
                  Aparece con un clip tocado y se va cuando no hay ninguno.
                  Ver ADR-2026-09-16-las-acciones-del-clip-flotan-al-lado-del-cuadro. */}
                  {/* En el teléfono estas mismas acciones son la barra de
                  abajo, y al costado del cuadro no habría ancho para ellas
                  (el preview ya toca los dos bordes). */}
                  {!enElTelefono && accionesDelClipSeleccionado.length > 0 && (
                    <div
                      data-clip-ui="true"
                      className="absolute right-full top-1/2 -translate-y-1/2 mr-3 z-20 flex flex-col p-1 rounded-xl bg-surface-card border border-black/[0.06] shadow-lg"
                    >
                      {accionesDelClipSeleccionado.map((it) => (
                        <button
                          key={it.label}
                          type="button"
                          onClick={it.onClick}
                          disabled={it.disabled}
                          title={it.title ?? it.label}
                          aria-label={it.label}
                          // El tamaño del ícono se fija acá, como en la lista
                          // de acciones del panel: los items lo traen en
                          // `w-4 h-4` y son varios.
                          className={`h-9 px-2.5 rounded-[8px] inline-flex items-center justify-start gap-1.5 whitespace-nowrap text-[11px] font-medium transition-colors [&_svg]:w-3.5 [&_svg]:h-3.5 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent ${
                            it.danger
                              ? "text-red-600 hover:bg-red-50"
                              : "text-brand-black/70 hover:bg-black/[0.05]"
                          }`}
                        >
                          {it.icon}
                          {it.corto ?? it.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div
                    ref={previewRef}
                    className="absolute inset-0 bg-black rounded-md overflow-hidden shadow-md"
                  >
                    <CargandoElVideo esperando={esperandoAlVideo} />
                    {/* Directional motion-blur filter — only mounted while a
                    slide transition is in progress. The stdDeviation is
                    pinned to one axis so the blur streaks in the direction
                    of motion (the trendy "viral whip" look) instead of
                    spreading omnidirectionally like CSS `blur()`. */}
                    {motionBlurState && (
                      <svg
                        aria-hidden
                        style={{
                          position: "absolute",
                          width: 0,
                          height: 0,
                          pointerEvents: "none",
                        }}
                      >
                        <filter
                          id={MOTION_BLUR_FILTER_ID}
                          x="-10%"
                          y="-10%"
                          width="120%"
                          height="120%"
                        >
                          <feGaussianBlur
                            stdDeviation={
                              motionBlurState.direction === "x"
                                ? `${motionBlurState.px} 0`
                                : `0 ${motionBlurState.px}`
                            }
                          />
                        </filter>
                      </svg>
                    )}
                    {/* White swipe line for the `reveal` transition. Rendered
                    above the videos with a soft glow so the edge reads
                    clearly against any content. */}
                    {revealLineState && (
                      <div
                        className="absolute top-0 bottom-0 pointer-events-none"
                        style={{
                          zIndex: zDeLaLineaDeRevelado,
                          left: `${revealLineState.leftPct}%`,
                          width: 4,
                          transform: "translateX(-2px)",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 0 16px 4px rgba(255,255,255,0.6)",
                        }}
                      />
                    )}
                    {/* Overlay video for the `light-leak`-style transitions.
                    Mounted only while one is in progress. Composited on
                    top of the video clips with `mix-blend-mode: screen`,
                    which treats the overlay's black background as
                    transparent and lets the bright effect through. The
                    sync effect below seeks this element's currentTime
                    to match the transition's normalised progress. */}
                    {overlayState && (
                      <video
                        key={overlayState.url}
                        ref={overlayVideoRef}
                        src={overlayState.url}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                        style={{
                          mixBlendMode: "screen",
                          zIndex: zDelOverlayDeTransicion,
                        }}
                        playsInline
                        muted
                        preload="auto"
                      />
                    )}
                    {/* Render a <video> for each video clip. During a normal cut
                    only one is visible at a time; during a transition window
                    two clips can overlap with complementary opacities so we
                    use opacity instead of display:none to avoid having to
                    re-mount videos when a transition starts/ends. */}
                    {videoClipsApilados.map((clip) => {
                        // Use the transition-aware active check so video clips
                        // keep rendering past their visual end during a successor's
                        // transition. Without this the outgoing clip would snap to
                        // black at exactly its endTime, breaking the fade.
                        const isActive = isClipActiveAtTime(
                          clip,
                          currentTime,
                          clips,
                        );
                        const state = isActive
                          ? computeClipRenderStateAtTime(
                              clip,
                              currentTime,
                              clips,
                            )
                          : { opacity: 0 };
                        // El encuadre manual (escala, recorte, giro, posición)
                        // va en un WRAPPER y no en el <video>: adentro, el
                        // transform/clip-path de las transiciones sigue siendo
                        // suyo. Si compartieran el mismo atributo, cada
                        // transición borraría el encuadre del usuario.
                        // El aspecto sólo aplica a lo que NO llena su caja
                        // (imágenes con `contain`): con él, el recorte se mide
                        // sobre la imagen y no sobre el cuadro.
                        const encuadre = estiloDelEncuadre(
                          clip.encuadre,
                          clip.type === "image"
                            ? aspectoDeImagen[clip.id]
                            : undefined,
                          previewSize.h > 0
                            ? previewSize.w / previewSize.h
                            : undefined,
                        );
                        return (
                          <Fragment key={clip.id}>
                          <div
                            data-encuadre-clip-id={clip.id}
                            className="absolute inset-0"
                            style={{
                              clipPath: encuadre.clipPath,
                              transform: encuadre.transform,
                              transformOrigin: encuadre.transformOrigin,
                              opacity: clip.opacity ?? 1,
                              // Qué tapa a qué: lo dice la timeline.
                              zIndex: zDelClip(clip),
                              // Fuera de su rango el clip no se dibuja.
                              visibility: isActive ? "visible" : "hidden",
                              // Los clics NO los recibe este wrapper, que
                              // ocupa el cuadro entero aunque la imagen sea un
                              // logo chico en una esquina: se los llevaba
                              // todos y un texto debajo de su fila era
                              // imposible de agarrar. Los recibe la zona de
                              // clic de abajo, del tamaño de lo que se VE
                              // (ADR-2026-09-28-la-zona-de-clic-de-una-capa-es-lo-que-se-ve).
                              pointerEvents: "none",
                            }}
                          >
                          {clip.type === "image" ? (
                          /* La entrada y la salida van en una capa PROPIA entre
                             el encuadre (afuera) y la transición (en el <img>):
                             en cualquiera de los dos pisaría su `transform`, y
                             un "deslizar" se multiplicaría por la escala del
                             clip. Es el mismo reparto que hace un Elemento
                             (ADR-2026-09-27-las-capas-de-imagen-entran-y-salen). */
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={
                              computeClipMotion({
                                localTime: currentTime - clip.startTime,
                                duration: clip.duration,
                                enterId: clip.enterAnimation,
                                exitId: clip.exitAnimation,
                                enterDur: clip.enterDurationSec,
                                exitDur: clip.exitDurationSec,
                              }).wrapperStyle
                            }
                          >
                            <img
                              // `imageUrl` cuando no hay `src`: al recargar el
                              // proyecto, una capa de logo sólo tiene la
                              // estable.
                              src={clip.src ?? clip.imageUrl}
                              alt=""
                              crossOrigin="anonymous"
                              draggable={false}
                              // `contain` y no `cover`: una imagen que se suma
                              // como capa (un logo, un sello) tiene que entrar
                              // entera. Recortarla es decisión del usuario, con
                              // las manijas de los lados.
                              className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                              style={{
                                opacity: state.opacity,
                                transform: buildClipTransform(state),
                                filter: buildClipFilter(state),
                                clipPath: buildClipClipPath(state),
                              }}
                              onLoad={(e) => {
                                const img = e.currentTarget;
                                // Se guarda en el clip además de en el estado:
                                // el render del MP4 lo necesita y allá no hay
                                // DOM para medirlo.
                                if (img.naturalWidth && img.naturalHeight) {
                                  const ar =
                                    img.naturalWidth / img.naturalHeight;
                                  setClips((prev) =>
                                    prev.map((c) =>
                                      c.id === clip.id && c.imageAspect !== ar
                                        ? { ...c, imageAspect: ar }
                                        : c,
                                    ),
                                  );
                                }
                                if (!img.naturalWidth || !img.naturalHeight)
                                  return;
                                const ar = img.naturalWidth / img.naturalHeight;
                                setAspectoDeImagen((prev) =>
                                  Math.abs((prev[clip.id] ?? 0) - ar) < 0.001
                                    ? prev
                                    : { ...prev, [clip.id]: ar },
                                );
                              }}
                            />
                          </div>
                          ) : (
                          <video
                            ref={(el) => {
                              if (el) mediaRefs.current.set(clip.id, el);
                              else mediaRefs.current.delete(clip.id);
                            }}
                            src={srcDelPreview(clip)}
                            // CORS anónimo: las signed URLs de Supabase devuelven
                            // Access-Control-Allow-Origin: *, así que pedir el video
                            // con CORS NO rompe la reproducción y permite dibujarlo
                            // en un canvas sin "ensuciarlo" (necesario para capturar
                            // el frame del pincel de borrado vía toDataURL).
                            crossOrigin="anonymous"
                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                            style={{
                              opacity: state.opacity,
                              transform: buildClipTransform(state),
                              filter: buildClipFilter(state),
                              clipPath: buildClipClipPath(state),
                            }}
                            playsInline
                            // Entero sólo lo que está cerca del cursor
                            // (ADR-2026-11-03-bajar-solo-lo-que-se-ve).
                            preload={precargaDelClip(clip, currentTime)}
                            onError={() =>
                              usaLaVersionLiviana(clip)
                                ? dejarLaVersionLiviana(clip.id)
                                : void refirmarSiVencio(clip.id)
                            }
                            onLoadedMetadata={(e) => {
                              const el = e.currentTarget as HTMLVideoElement;
                              // Con la metadata ya se sabe si el archivo trae
                              // audio: es lo que decide si el panel muestra el
                              // volumen y el corte de silencios.
                              const conAudio = detectarPistaDeAudio(el);
                              if (conAudio !== null) {
                                setTienenAudio((prev) =>
                                  prev[clip.id] === conAudio
                                    ? prev
                                    : { ...prev, [clip.id]: conAudio },
                                );
                              }
                              const dur = el.duration;
                              if (!isFinite(dur) || dur <= 0) return;
                              // Mismo arreglo si no cambia nada: si no, cada
                              // metadata re-renderiza el editor entero
                              // (ADR-2026-11-03-abrir-sin-esperar).
                              // La duración que manda es la del ORIGINAL: la
                              // versión liviana coincide por ±0,12 s y no la pisa.
                              if (usaLaVersionLiviana(clip) && clip.sourceDuration !== undefined)
                                return;
                              setClips((prev) =>
                                conDuracionDeOrigen(prev, clip.id, dur),
                              );
                            }}
                          />
                          )}
                          </div>
                          {/* La zona de clic: el rectángulo que el usuario VE
                              del clip (la misma caja que usa el marco de
                              selección: escala, posición, recorte, giro y el
                              `contain` de una imagen). Un video sin encuadre
                              sigue ocupando el cuadro entero; un logo al 60 %
                              ocupa su 60 %, y lo que queda alrededor es de la
                              capa de abajo. Fuera de su rango no existe: si
                              no, el de arriba se comería la selección del que
                              sí se está viendo. */}
                          {isActive &&
                            previewSize.w > 0 &&
                            (() => {
                              const caja = cajaVisibleDelClip(clip);
                              return (
                                <div
                                  // Seleccionar desde el preview es UI de
                                  // clip: sin esta marca, el listener global
                                  // de pointerdown deselecciona en el mismo
                                  // gesto que selecciona.
                                  data-clip-ui="true"
                                  className="absolute"
                                  onPointerDown={(e) =>
                                    handleEncuadrePointerDown(e, clip)
                                  }
                                  onPointerMove={handleEncuadrePointerMove}
                                  onPointerUp={handleEncuadrePointerUp}
                                  onPointerCancel={handleEncuadrePointerUp}
                                  style={{
                                    left: caja.centroXPx,
                                    top: caja.centroYPx,
                                    width: caja.anchoPx,
                                    height: caja.altoPx,
                                    transform: `translate(-50%, -50%) rotate(${caja.rotacionGrados}deg)`,
                                    zIndex: zDelClip(clip),
                                    cursor:
                                      selectedClipId === clip.id
                                        ? "grab"
                                        : "pointer",
                                    touchAction: "none",
                                  }}
                                />
                              );
                            })()}
                          </Fragment>
                        );
                      })}

                    {/* Render hidden <audio> elements for audio clips */}
                    {clips
                      .filter((c) => c.type === "audio" && c.src)
                      .map((clip) => (
                        <audio
                          key={clip.id}
                          ref={(el) => {
                            if (el) mediaRefs.current.set(clip.id, el);
                            else mediaRefs.current.delete(clip.id);
                          }}
                          src={srcDelPreview(clip)}
                          // CORS anónimo, igual que el <video> y el <img>. Sin
                          // esto, subir el volumen por encima de 100% dejaba el
                          // clip MUDO y sin retorno: amplificar necesita rutear
                          // el audio por la Web Audio API, y un media de otro
                          // origen pedido sin CORS sale de ahí en silencio. Las
                          // signed URLs de Supabase responden
                          // `Access-Control-Allow-Origin: *`, así que pedirlo
                          // con CORS no cambia nada de lo que ya andaba.
                          // Ver ADR-2026-09-22-subir-el-volumen-no-deja-mudo-el-clip.
                          crossOrigin="anonymous"
                          preload={precargaDelClip(clip, currentTime)}
                          onError={() =>
                            usaLaVersionLiviana(clip)
                              ? dejarLaVersionLiviana(clip.id)
                              : void refirmarSiVencio(clip.id)
                          }
                          onLoadedMetadata={(e) => {
                            const dur = (e.currentTarget as HTMLAudioElement)
                              .duration;
                            if (!isFinite(dur) || dur <= 0) return;
                            if (usaLaVersionLiviana(clip) && clip.sourceDuration !== undefined)
                              return;
                            setClips((prev) =>
                              conDuracionDeOrigen(prev, clip.id, dur),
                            );
                          }}
                        />
                      ))}

                    {/* Text clip overlays — one positioned, draggable layer per
                    active text clip. Coordinates are stored as percentages
                    so the same clip looks identical on any aspect ratio /
                    player size. Font size derives from the live canvas
                    height so what you see in the editor matches the export. */}
                    {/* ── Animation clips ──────────────────────────────────
                    Time-deterministic React components driven by Remotion's
                    <Player>. They cover the full preview canvas; if the
                    template uses a transparent background they composite on
                    top of any active video, otherwise they replace it.
                    Composition resolution is fixed per aspect ratio so the
                    in-editor preview matches what the export pipeline will
                    eventually render server-side. */}
                    {activeAnimationClips.map((clip) => {
                      const compW =
                        aspectRatio === "9:16"
                          ? 1080
                          : aspectRatio === "4:3"
                            ? 1440
                            : 1920;
                      const compH =
                        aspectRatio === "9:16"
                          ? 1920
                          : aspectRatio === "4:3"
                            ? 1080
                            : 1080;
                      // Posición + escala — defaults respetan el layout previo
                      // (centro, sin escalar) si el clip viene de un proyecto
                      // antiguo sin estos campos.
                      const xPct = clip.animXPct ?? 50;
                      const yPct = clip.animYPct ?? 50;
                      const scale = clip.animScale ?? 1;
                      const isSelected = selectedClipId === clip.id;

                      // ── Background extraído del template ────────────────
                      // Si el template define un prop `background` de tipo
                      // color, lo sacamos del flujo normal y lo pintamos como
                      // capa fija full-canvas detrás del elemento movible.
                      // Esto evita que el fondo se mueva/encoja con el
                      // wrapper: el elemento queda como un objeto sobre un
                      // "telón" estático, que era el comportamiento pedido.
                      const tpl = getAnimationTemplate(
                        clip.animationTemplate as string,
                      );
                      const sourceProps = (
                        animationEditDraft &&
                        animationEditDraft.clipId === clip.id
                          ? animationEditDraft.props
                          : clip.animationProps || {}
                      ) as Record<string, unknown>;
                      const bgSpec = tpl?.propsSchema?.background;
                      const hasBgProp = bgSpec?.type === "color";
                      const bgValue = hasBgProp
                        ? (sourceProps.background as string | undefined)
                        : undefined;
                      // Solo dibujamos la capa fija si hay un color definido
                      // y no es transparente — un fondo transparente equivale
                      // a "sin fondo", así que evitamos un div inerte de más.
                      const bgCovers =
                        hasBgProp &&
                        typeof bgValue === "string" &&
                        bgValue !== "transparent" &&
                        bgValue !== "";
                      // Cuando movemos el background a la capa fija, le
                      // pasamos `transparent` al template para que no lo
                      // pinte por dentro y termine duplicado.
                      const propsForTemplate = hasBgProp
                        ? { ...sourceProps, background: "transparent" }
                        : sourceProps;
                      // Una animación por código no tiene plantilla ni draft:
                      // sus props son fijas (assets, marca, duración).
                      const propsParaElItem = clip.animationCodigo
                        ? (propsDeAnimacionPorCodigo(clip) as unknown as Record<
                            string,
                            unknown
                          >)
                        : propsForTemplate;

                      return (
                        <Fragment key={clip.id}>
                          {bgCovers && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                background: bgValue,
                                // El telón va en la misma capa que su elemento.
                                zIndex: zDelClip(clip),
                              }}
                            />
                          )}
                          <AnimationCanvasItem
                            clip={clip}
                            z={zDelClip(clip)}
                            isSelected={isSelected}
                            xPct={xPct}
                            yPct={yPct}
                            scale={scale}
                            compW={compW}
                            compH={compH}
                            localTime={Math.max(
                              0,
                              currentTime - clip.startTime,
                            )}
                            propsForTemplate={propsParaElItem}
                            previewRef={previewRef}
                            onPointerDown={handleAnimPointerDown}
                            onPointerMove={handleAnimPointerMove}
                            onPointerUp={handleAnimPointerUp}
                            onResize={handleAnimResize(clip)}
                          />
                        </Fragment>
                      );
                    })}

                    {activeTextClips.map((clip) => {
                      const isSelected = selectedClipId === clip.id;
                      const isEditing = editingTextId === clip.id;
                      const sizePx =
                        (previewSize.h || 1) *
                        ((clip.fontSizePct ?? DEFAULT_TEXT_CLIP.fontSizePct) /
                          100);
                      // Calculamos motion del clip — pero la *desactivamos* en
                      // modo edición para que el usuario pueda escribir sin que
                      // las letras se desvanezcan o salten mientras tipea.
                      const localTime = currentTime - clip.startTime;
                      const motion = isEditing
                        ? {
                            wrapperStyle: {} as React.CSSProperties,
                            itemPreset: undefined,
                          }
                        : computeClipMotion({
                            localTime,
                            duration: clip.duration,
                            enterId: clip.enterAnimation,
                            exitId: clip.exitAnimation,
                            enterDur: clip.enterDurationSec,
                            exitDur: clip.exitDurationSec,
                          });
                      const textValue = clip.text || "";
                      // Split por items si el preset lo requiere (typewriter,
                      // cascade). Cada item se renderiza como `<span>` con su
                      // propio estilo. En modo edición usamos el render plano
                      // contenteditable (no se puede tipear sobre spans
                      // partidos sin un editor real).
                      const itemRender =
                        !isEditing && motion.itemPreset
                          ? (() => {
                              const { splitMode, styleAt } = motion.itemPreset;
                              const items = splitTextForMotion(
                                textValue,
                                splitMode,
                              );
                              return (
                                <>
                                  {items.map((item, i) => (
                                    <span
                                      key={i}
                                      style={styleAt(i, items.length)}
                                      // `whiteSpace: pre` preserva los espacios
                                      // que quedan adentro del item (modo words)
                                      // o el espacio en sí cuando es chars.
                                      // Sin esto el browser los colapsa.
                                      // eslint-disable-next-line react/forbid-dom-props
                                    >
                                      {item === " " ? "\u00A0" : item}
                                    </span>
                                  ))}
                                </>
                              );
                            })()
                          : null;
                      return (
                        <div
                          key={clip.id}
                          data-text-clip-id={clip.id}
                          onPointerDown={(e) => handleTextPointerDown(e, clip)}
                          onPointerMove={handleTextPointerMove}
                          onPointerUp={handleTextPointerUp}
                          onPointerCancel={handleTextPointerUp}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            setSelectedClipId(clip.id);
                            setEditingTextId(clip.id);
                          }}
                          className={`absolute touch-none ${
                            isEditing
                              ? "cursor-text"
                              : isSelected
                                ? "cursor-grabbing select-none"
                                : "cursor-grab select-none"
                          }`}
                          style={{
                            left: `${clip.textXPct ?? DEFAULT_TEXT_CLIP.textXPct}%`,
                            top: `${clip.textYPct ?? DEFAULT_TEXT_CLIP.textYPct}%`,
                            // Componemos el translate base con el motion
                            // wrapper style. El motion define `transform`
                            // adicional (slide / scale); lo concatenamos para
                            // que ambos coexistan.
                            // El giro va pegado al translate y ANTES del
                            // motion: así el enter/exit (slides, scales) se
                            // aplica sobre el texto ya girado, como si el
                            // usuario lo hubiera escrito en diagonal.
                            transform: [
                              "translate(-50%, -50%)",
                              clip.elementRotation
                                ? `rotate(${clip.elementRotation}deg)`
                                : "",
                              motion.wrapperStyle.transform ?? "",
                            ]
                              .filter(Boolean)
                              .join(" "),
                            opacity: motion.wrapperStyle.opacity,
                            filter: motion.wrapperStyle.filter,
                            // Qué tapa a qué: lo dice la timeline. Un texto en
                            // una capa por debajo de un video que ocupa toda la
                            // pantalla queda tapado, como corresponde.
                            zIndex: zDelClip(clip),
                            // Ancho explícito si el usuario reseteó la caja
                            // desde el preview; sino mantenemos el ancho
                            // natural acotado por `maxWidth`.
                            width:
                              typeof clip.textWidthPct === "number"
                                ? `${clip.textWidthPct}%`
                                : undefined,
                            // Cuando el usuario fijó un ancho explícito desde
                            // el preview no le imponemos un cap — puede
                            // extender la caja más allá de la canvas si así
                            // lo desea. El cap default sólo aplica al ancho
                            // natural (sin resize manual).
                            maxWidth:
                              typeof clip.textWidthPct === "number"
                                ? undefined
                                : "92%",
                            textAlign:
                              clip.textAlign ?? DEFAULT_TEXT_CLIP.textAlign,
                            // Outline solo en modo edición (focus inline);
                            // la selección normal usa el SelectionFrame
                            // amarillo de abajo, con manijas para resizear.
                            outline: isEditing
                              ? "1px solid rgb(var(--brand-primary-rgb) / 0.85)"
                              : "none",
                            outlineOffset: 4,
                            padding: "2px 4px",
                          }}
                        >
                          {/* Marco de selección con manijas de resize.
                          Solo en modo selección (no edición), porque
                          tipear adentro y a la vez ofrecer handles es
                          confuso y los handles "atrapan" clicks que
                          el contenteditable necesita para colocar el
                          caret. El componente es self-contained y
                          captura sus propios pointer events. */}
                          {isSelected && !isEditing && (
                            <SelectionFrame
                              // Handles N/S no aplican en texto: el alto
                              // del wrapper sigue al contenido (font-size
                              // + wrap), arrastrar arriba/abajo no tiene
                              // semántica clara.
                              handles={["nw", "ne", "sw", "se", "e", "w"]}
                              // Mismo marco que el encuadre de video: azul y
                              // con el círculo de girar. El marco vive DENTRO
                              // del wrapper del texto, así que gira con él sin
                              // ninguna cuenta extra.
                              color={AZUL_DEL_ENCUADRE}
                              onResize={handleTextResize(clip)}
                              onRotate={handleTextRotate(clip)}
                              circuloDeRotacion="arriba"
                            />
                          )}
                          {/* Margen invisible de agarre: 8px alrededor de la
                              caja del texto. Sin él había que acertarle a las
                              letras, y un dedo o un clic apenas afuera caía en
                              el video de abajo. No va en edición: ahí el
                              contenteditable necesita todos los clics para el
                              caret (ADR-2026-09-28-la-zona-de-clic-de-una-capa-es-lo-que-se-ve). */}
                          {!isEditing && (
                            <div aria-hidden="true" className="absolute -inset-2" />
                          )}
                          <span
                            // Re-key to force a fresh DOM node when entering /
                            // exiting edit mode (avoids stale contenteditable
                            // text after an external clip update).
                            key={
                              isEditing
                                ? `edit-${clip.text}`
                                : `view-${clip.text}`
                            }
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            spellCheck={isEditing}
                            onBlur={(e) => {
                              if (!isEditing) return;
                              const next = (
                                e.currentTarget.innerText || ""
                              ).replace(/\n{3,}/g, "\n\n");
                              setClips((prev) =>
                                prev.map((c) =>
                                  c.id === clip.id
                                    ? {
                                        ...c,
                                        text: next,
                                        name:
                                          next.length > 20
                                            ? next.slice(0, 17) + "…"
                                            : next || "Texto",
                                      }
                                    : c,
                                ),
                              );
                              setEditingTextId(null);
                            }}
                            onKeyDown={(e) => {
                              if (!isEditing) return;
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                (e.currentTarget as HTMLSpanElement).blur();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                // Restore original content and exit
                                (e.currentTarget as HTMLSpanElement).innerText =
                                  clip.text || "";
                                setEditingTextId(null);
                              }
                            }}
                            className="block whitespace-pre-wrap break-words leading-tight outline-none"
                            style={{
                              // Lo que el MP4 va a poder dibujar, no lo
                              // que la compu del usuario tenga instalado.
                              fontFamily: pilaDeFuenteComoEnElRender(
                                clip.fontFamily,
                                customFontFamilies,
                              ),
                              fontSize: `${sizePx}px`,
                              fontWeight:
                                clip.fontWeight === "bold" ? 700 : 400,
                              fontStyle: clip.fontStyle ?? "normal",
                              color:
                                clip.textColor ?? DEFAULT_TEXT_CLIP.textColor,
                              // Subtle shadow keeps light text legible over busy
                              // video backgrounds without forcing the user to
                              // pick a specific contrast color.
                              textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                              minWidth: isEditing ? "1ch" : undefined,
                            }}
                          >
                            {itemRender ?? textValue}
                          </span>
                        </div>
                      );
                    })}

                    {/* Sin nada en el cursor, el cuadro queda NEGRO y pelado:
                    no hay ícono ni cartel. Es lo que va a mostrar el video
                    exportado en ese instante, así que el preview no inventa
                    contenido que después no está.
                    Ver ADR-2026-09-16-el-preview-vacio-queda-negro. */}
                  </div>

                    {/* ── Marco de encuadre del clip de video seleccionado ──
                    Vive en coordenadas del CUADRO y no adentro del wrapper
                    transformado, igual que el marco de las animaciones: si se
                    escalara con el clip, a 3x las manijas se verían gigantes y
                    a 0.2x no habría de dónde agarrarlas. Acá el rectángulo se
                    calcula con la misma matemática que dibuja el clip y solo
                    se le copia el giro. */}
                    {(() => {
                      const clip = clips.find(
                        (c) =>
                          c.id === selectedClipId &&
                          (c.type === "video" ||
                            c.type === "ai-generated" ||
                            c.type === "image") &&
                          (c.src || c.imageUrl),
                      );
                      if (!clip) return null;
                      if (!previewSize.w || !previewSize.h) return null;
                      if (!isClipActiveAtTime(clip, currentTime, clips))
                        return null;

                      const caja = cajaVisibleDelClip(clip);
                      // ¿Entra el círculo de girar POR FUERA del borde de
                      // arriba? Necesita `SEPARACION_ROTACION` + su medio alto
                      // (36 + 17 = 53). El aire disponible es el padding del
                      // escenario más lo que el cuadro tenga de aire por
                      // encima. Si no entra —el caso típico del teléfono, con
                      // el clip ocupando todo el preview— el marco lo dibuja
                      // ADENTRO, pegado por debajo de ese mismo borde: mejor
                      // tapar un pedacito de imagen que tener un control que
                      // no se puede tocar.
                      // Ver ADR-2026-09-18-el-circulo-de-rotacion-entra-al-cuadro.
                      const aireDelEscenario = enElTelefono ? 24 : 56;
                      const aireArribaDelCuadro =
                        aireDelEscenario + (caja.centroYPx - caja.altoPx / 2);
                      const rotacionAdentro = aireArribaDelCuadro < 53;
                      return (
                        <div
                          className="absolute"
                          data-clip-ui="true"
                          style={{
                            left: caja.centroXPx,
                            top: caja.centroYPx,
                            width: caja.anchoPx,
                            height: caja.altoPx,
                            transform: `translate(-50%, -50%) rotate(${caja.rotacionGrados}deg)`,
                            pointerEvents: "none",
                            zIndex: 40,
                          }}
                        >
                          <SelectionFrame
                            // Azul de marca en vez del amarillo por defecto:
                            // el encuadre se usa sobre video, donde el amarillo
                            // se pierde contra luces cálidas y piel. El texto y
                            // las animaciones siguen en amarillo — ahí el marco
                            // convive con el color que el usuario le puso a su
                            // propio texto, y el azul chocaría más seguido.
                            color={AZUL_DEL_ENCUADRE}
                            onResize={handleEncuadreResize(clip)}
                            onRotate={handleEncuadreRotate(clip)}
                            circuloDeRotacion={
                              rotacionAdentro ? "adentro" : "arriba"
                            }
                          />
                        </div>
                      );
                    })()}

                    {/* ── Guías del imán ──────────────────────────────────────
                    La línea aparece sobre el borde (o el eje central) al que el
                    clip quedó pegado, y sólo mientras dura el arrastre. Sin
                    esto el imán se siente como que el clip "se traba" sin
                    motivo: la guía es lo que explica que se enganchó a algo.
                    Van por dentro del cuadro y no encima del borde exacto —
                    apoyadas justo afuera se comerían la mitad de su ancho. */}
                    {(guiasDelIman.x !== null || guiasDelIman.y !== null) && (
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ zIndex: 42 }}
                      >
                        {guiasDelIman.x !== null && (
                          <div
                            style={{
                              position: "absolute",
                              left: Math.min(
                                Math.max(guiasDelIman.x, 1),
                                previewSize.w - 1,
                              ),
                              top: 0,
                              bottom: 0,
                              width: 2,
                              transform: "translateX(-1px)",
                              background: AZUL_DEL_ENCUADRE,
                            }}
                          />
                        )}
                        {guiasDelIman.y !== null && (
                          <div
                            style={{
                              position: "absolute",
                              top: Math.min(
                                Math.max(guiasDelIman.y, 1),
                                previewSize.h - 1,
                              ),
                              left: 0,
                              right: 0,
                              height: 2,
                              transform: "translateY(-1px)",
                              background: AZUL_DEL_ENCUADRE,
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* ── Cartelito de grados ─────────────────────────────────
                    Sólo mientras se gira. Va FUERA del contenedor del marco
                    —que está rotado— porque si no el número giraría junto con
                    el clip y a 90° habría que leerlo de costado.

                    Se para en la MISMA franja que el círculo de rotación, no
                    encima: son 56px de aire y no entran los dos uno arriba del
                    otro. No se pisan porque apenas empezás a girar el círculo
                    se va por la diagonal y deja el lugar libre; en 0° el
                    cartelito lo tapa, y ahí lo que importa es el número.

                    El signo se muestra siempre (`+12°` / `-12°`): sin el `+`
                    no se distingue hacia qué lado se está girando, que es
                    justamente lo que el cartel tiene que contar. */}
                    {(() => {
                      if (!girandoClipId) return null;
                      const clip = clips.find((c) => c.id === girandoClipId);
                      if (!clip || !previewSize.w || !previewSize.h) return null;

                      const caja = cajaVisibleDelClip(clip);
                      const grados = Math.round(
                        normalizarEncuadre(clip.encuadre).rotation,
                      );
                      // El cartel acompaña al círculo: si el círculo se metió
                      // adentro del cuadro (no había aire arriba), el número
                      // también, o quedaría cortado contra el borde del
                      // escenario. Misma cuenta que la del marco.
                      const aireArribaDelCuadro =
                        (enElTelefono ? 24 : 56) +
                        (caja.centroYPx - caja.altoPx / 2);
                      const adentro = aireArribaDelCuadro < 53;
                      return (
                        <div
                          data-clip-ui="true"
                          className="absolute pointer-events-none select-none tabular-nums"
                          style={{
                            left: caja.centroXPx,
                            top:
                              caja.centroYPx -
                              caja.altoPx / 2 +
                              (adentro
                                ? SEPARACION_ROTACION
                                : -SEPARACION_ROTACION),
                            transform: "translate(-50%, -50%)",
                            zIndex: 41,
                            padding: "3px 9px",
                            borderRadius: 6,
                            background: AZUL_DEL_ENCUADRE,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            boxShadow: "0 1px 6px rgba(0,0,0,0.3)",
                          }}
                        >
                          {grados > 0 ? `+${grados}°` : `${grados}°`}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* ── Transport + Timeline ─────────────────────────────────── */}
                {/* El bloque de la timeline es una tarjeta BLANCA sobre el gris
                del shell, con el MISMO aire en los cuatro lados (16px = `px-4`
                + `pt-0`/`pb-4`; arriba lo reparten la mitad de abajo de la
                franja de agarre de la manija —10px— y el `mt-1.5` de la fila
                del transporte). Antes la izquierda medía el doble que la
                derecha —al `px-4` se le sumaba un `pl-4` en la zona de
                pistas— y abajo faltaba. */}
                <div
                  ref={bloqueTimelineRef}
                  className={`shrink-0 flex flex-col min-h-0 bg-white pt-0 ${
                    enElTelefono ? "px-3 pb-1.5" : "px-4 pb-4"
                  } ${
                    animandoElAlto
                      ? "transition-[height] duration-[260ms] ease-out"
                      : ""
                  }`}
                  style={{ height: altoDeLaTimeline ?? undefined }}
                >
                  {/* Manija para estirar la timeline: la LÍNEA de arriba, la
                  que separa el preview de la timeline, de lado a lado. En
                  reposo es un hilo gris que se lee como el borde del bloque;
                  al pasarle el mouse por encima el cursor cambia a ns-resize y
                  la línea se pone azul (y engorda un pelo) para avisar que se
                  puede agarrar; mientras se arrastra queda azul.

                  Lo que se VE es 1px, pero lo que se AGARRA son 20px (`h-5`)
                  centrados en el borde (`-mt-2.5`): 10 por arriba, sobre el
                  final del preview, y 10 por abajo, en el aire que queda antes
                  de la fila del transporte. Sin eso hay que clavarle el mouse
                  a un píxel.

                  `relative z-10` no es decorativo: el área de preview de
                  arriba es `relative`, así que pinta por encima de todo lo que
                  no está posicionado y se comía los clicks de la mitad de
                  arriba de la franja. Con z-index propio la manija gana. */}
                  {/* Minimizada, la línea sigue ahí —es el borde de arriba
                  de la tarjeta— pero no se agarra: no hay pistas que estirar,
                  y arrastrarla dejaría media tarjeta blanca vacía. */}
                  <div
                    onPointerDown={
                      timelineMinimizada ? undefined : empezarAEstirarLaTimeline
                    }
                    onDoubleClick={
                      timelineMinimizada
                        ? undefined
                        : () => setAltoDeLaTimeline(null)
                    }
                    className={`group/estirar relative z-10 shrink-0 h-5 -mx-4 -mt-2.5 flex items-center ${
                      timelineMinimizada ? "" : "cursor-ns-resize"
                    }`}
                    style={{ touchAction: "none" }}
                    title={
                      timelineMinimizada
                        ? undefined
                        : "Arrastrá para agrandar o achicar la timeline (doble click: automático)"
                    }
                  >
                    <span
                      className={`w-full transition-all ${
                        estirandoElAlto
                          ? "h-[2px] bg-brand-primary"
                          : timelineMinimizada
                            ? "h-px bg-black/[0.10]"
                            : "h-px bg-black/[0.10] group-hover/estirar:h-[2px] group-hover/estirar:bg-brand-primary"
                      }`}
                    />
                  </div>
                  {/* ── Fila del transporte: formato · tiempo + play · zoom ────
                  Una grilla de TRES columnas (`1fr · auto · 1fr`) con el play
                  solo en la del medio: así el play cae en el centro exacto de
                  la fila —y por lo tanto del preview, que también está
                  centrado— sin importar cuánto ocupe lo que tiene a los lados.
                  Como flex centrado, el play se corría un poco cada vez que un
                  grupo cambiaba de ancho (el zoom al sumarle "Ajustar
                  timeline", el tiempo al pasar de "0:09" a "0:10"). Los
                  dos costados son `minmax(0,1fr)` para que puedan achicarse
                  con el panel abierto en vez de empujar el centro. */}
                  {/* ── La misma fila, en el teléfono ─────────────────────
                  Tres cosas y nada más: dónde estoy (tiempo / total), play, y
                  el formato. El zoom se va —un slider de 104px no se maneja
                  con el dedo— y lo reemplaza el pellizco sobre la timeline, que
                  es el gesto con el que se acerca cualquier cosa en un
                  teléfono; "Ajustar timeline" se vuelve el ícono de encuadrar,
                  a un toque. */}
                  {enElTelefono && (
                    <div className="flex items-center gap-1.5 mt-1.5 mb-2">
                      {/* El play va en el MEDIO DE LOS DOS TIEMPOS: a la
                      izquierda por dónde va la timeline, a la derecha cuánto
                      dura en total. Antes los dos números vivían juntos como
                      "0:07 / 0:19" y el play estaba al borde izquierdo; que el
                      play los separe hace que se lean como lo que son, dos
                      cosas distintas, y pone el control donde el pulgar ya
                      está. Ver ADR-2026-09-17-el-play-del-telefono-parte-los-tiempos.

                      El grupo se centra con un espaciador `flex-1` de cada
                      lado: el de la derecha aloja deshacer/rehacer, así que el
                      centro queda en el medio de la fila y no se corre cuando
                      el tiempo pasa de "0:09" a "0:10". */}
                      <div className="flex-1 min-w-0" />
                      <div className="flex items-center gap-2.5 shrink-0">
                        {/* Los tiempos se muestran SIEMPRE, también con el
                        video ampliado: mirando es justo cuando querés saber por
                        dónde va y cuánto falta. Antes se escondían para no
                        competir con el video; lo que competía era la fila
                        entera de cinco controles, no dos números. */}
                        <span className="text-[12.5px] font-semibold text-brand-black/70 tabular-nums select-none">
                          {formatTime(currentTime)}
                        </span>
                        {/* El MISMO botón que en escritorio: disco azul
                        relleno con el ícono blanco. Supersede
                        ADR-2026-09-17-el-play-del-telefono-es-el-icono, que lo
                        había dejado como ícono pelado para que no compitiera
                        con el video — partido por los dos tiempos ya no compite
                        con nada, y el disco es lo que hace que se lea como EL
                        control de la fila y no como un ícono más al lado de
                        deshacer/rehacer.

                        `fill-current` sobre `text-white`: los iconos de lucide
                        son de trazo, así que sin el fill el play se ve como un
                        triángulo hueco sobre el azul. */}
                        <button
                          onClick={alternarReproduccion}
                          className="w-7 h-7 shrink-0 flex items-center justify-center rounded-full bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors"
                          aria-label={isPlaying ? "Pausar" : "Reproducir"}
                        >
                          {isPlaying ? (
                            <Pause className="w-3 h-3 text-white fill-current" />
                          ) : (
                            <Play className="w-3 h-3 text-white fill-current ml-0.5" />
                          )}
                        </button>
                        <span className="text-[12.5px] font-medium text-brand-black/35 tabular-nums select-none">
                          {formatTime(
                            contentDuration > 0 ? contentDuration : duration,
                          )}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 flex items-center justify-end gap-1">
                        {/* Acá NO van "Ajustar la timeline" ni el formato: los
                        dos se sacaron del teléfono a pedido del usuario. El
                        ícono de las dos flechas no decía qué hacía, y el
                        formato es una decisión que se toma una vez al armar el
                        proyecto, no algo que se toquetee editando desde el
                        celular — ocupaban la mitad de una barra que en un
                        teléfono angosto no sobra. Los dos siguen en escritorio,
                        en el bloque `!enElTelefono` de más abajo. */}
                        <button
                          type="button"
                          onClick={undo}
                          disabled={!canUndo}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/55 active:bg-black/[0.05] transition-colors disabled:opacity-30"
                          aria-label="Deshacer"
                        >
                          <Undo2 className="w-[17px] h-[17px]" />
                        </button>
                        <button
                          type="button"
                          onClick={redo}
                          disabled={!canRedo}
                          className="w-8 h-8 flex items-center justify-center rounded-md text-brand-black/55 active:bg-black/[0.05] transition-colors disabled:opacity-30"
                          aria-label="Rehacer"
                        >
                          <Redo2 className="w-[17px] h-[17px]" />
                        </button>
                      </div>
                    </div>
                  )}

                  {!enElTelefono && (
                  // `data-clip-ui`: Play, el zoom y "Ajustar" no sueltan el clip
                  // seleccionado. Antes apretar Play para buscar el punto de corte
                  // deseleccionaba y "Dividir" desaparecía
                  // (ADR-2026-11-02-tocar-un-clip-lleva-el-cursor).
                  <div
                    data-clip-ui
                    className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-5 mt-1.5 mb-3"
                  >
                    {/* Izquierda: formato · tiempo actual */}
                    <div className="flex min-w-0 items-center justify-end gap-4 overflow-hidden">
                      {/* ── Formato, como segmented control ──────────────────
                      Los tres viven en una sola pastilla gris y el elegido es la
                      pieza blanca que se levanta: se lee como UNA decisión con
                      tres opciones y no como tres botones sueltos que por
                      casualidad están juntos. */}
                      <div className="inline-flex items-center gap-0.5 rounded-lg bg-black/[0.05] p-0.5">
                        {/* 9:16 primero: el vertical es el formato con el que se arma la
                        mayoría de lo que sale de acá (redes), así que va donde se
                        lee primero. */}
                        {(["9:16", "16:9", "4:3"] as AspectRatio[]).map((ar) => (
                          <button
                            key={ar}
                            onClick={() => setAspectRatio(ar)}
                            aria-pressed={aspectRatio === ar}
                            className={`h-6 px-2.5 flex items-center justify-center rounded-md transition-colors ${
                              aspectRatio === ar
                                ? "bg-white shadow-sm"
                                : "hover:bg-black/[0.04]"
                            }`}
                            style={{
                              fontSize: 12,
                              fontWeight: aspectRatio === ar ? 600 : 500,
                              color:
                                aspectRatio === ar
                                  ? "var(--brand-primary)"
                                  : "rgb(var(--brand-black-rgb) / 0.55)",
                              cursor: "pointer",
                            }}
                          >
                            {ar}
                          </button>
                        ))}
                      </div>
                      <div className="w-px h-5 bg-neutral-200" />
                      <span className="text-[13px] font-semibold text-brand-black/70 tabular-nums select-none">
                        {formatTime(currentTime)}
                      </span>
                    </div>

                    {/* Centro: SOLO el play. Es la columna `auto` de la
                    grilla, así que queda centrada en la fila. */}
                    <button
                      onClick={alternarReproduccion}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors"
                    >
                      {/* `fill-current` sobre `text-white`: los iconos de lucide
                      son de trazo, así que sin el fill el play se ve como un
                      triángulo hueco sobre el azul. */}
                      {isPlaying ? (
                        <Pause className="w-4 h-4 text-white fill-current" />
                      ) : (
                        <Play className="w-4 h-4 text-white fill-current ml-0.5" />
                      )}
                    </button>

                    {/* Derecha: duración total · zoom · ajustar */}
                    <div className="flex min-w-0 items-center justify-start gap-4 overflow-hidden">
                      <span className="text-[13px] font-medium text-brand-black/40 tabular-nums select-none">
                        {formatTime(
                          contentDuration > 0 ? contentDuration : duration,
                        )}
                      </span>
                      <div className="w-px h-5 bg-neutral-200" />
                      {/* Zoom — slider entre los dos iconos, que acercan y
                      alejan por `PASO_DE_ZOOM`. El patrón (riel + relleno
                      dibujados aparte y un <input type="range"> transparente
                      encima, con el thumb estilado) es el mismo que usan los
                      sliders de los configuradores de video. El % dejó de
                      mostrarse como texto y vive en el `title`. */}
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            zoomDesdeLosControles(zoomRef.current / PASO_DE_ZOOM)
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-black/[0.04] transition-colors"
                          style={{ color: "rgb(var(--brand-black-rgb) / 0.5)" }}
                          title="Alejar"
                        >
                          <ZoomOut className="w-3.5 h-3.5" />
                        </button>
                        <div
                          className="relative h-[16px] w-full min-w-[40px] max-w-[104px] flex items-center"
                          title={`Zoom ${Math.round(zoom * 100)}%`}
                        >
                          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[4px] rounded-full bg-neutral-200" />
                          <div
                            className="absolute top-1/2 -translate-y-1/2 left-0 h-[3px] rounded-full"
                            style={{
                              width: `${posicionDelZoom(zoom) * 100}%`,
                              backgroundColor: "rgb(var(--brand-black-rgb) / 0.55)",
                            }}
                          />
                          {/* El riel es LOGARÍTMICO: el valor del input es la
                          posición 0..1 y el zoom sale de `zoomDesdePosicion`.
                          Con el rango de 0.25 a 60, un riel lineal deja todo
                          lo útil (0.5x–4x) apretado en el primer 6% y el resto
                          del recorrido son zooms de cuadro. En log, duplicar
                          el zoom es siempre el mismo tramo de riel. */}
                          <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.001}
                            value={posicionDelZoom(zoom)}
                            onChange={(e) =>
                              zoomDesdeLosControles(
                                zoomDesdePosicion(Number(e.target.value)),
                              )
                            }
                            aria-label="Zoom de la timeline"
                            className="absolute inset-0 w-full appearance-none bg-transparent cursor-pointer z-10 m-0 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[16px] [&::-webkit-slider-thumb]:h-[16px] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0.5px_4px_rgba(0,0,0,0.18),0_0_0_0.5px_rgba(0,0,0,0.12)] [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-[16px]"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            zoomDesdeLosControles(zoomRef.current * PASO_DE_ZOOM)
                          }
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-black/[0.04] transition-colors"
                          style={{ color: "rgb(var(--brand-black-rgb) / 0.5)" }}
                          title="Acercar"
                        >
                          <ZoomIn className="w-3.5 h-3.5" />
                        </button>
                        {/* "Ajustar": deja la timeline entera a la vista, en
                        los DOS ejes. A lo ancho, vuelve el zoom al que hace
                        entrar toda la duración en pantalla (el mismo cálculo
                        que corre solo al abrir el proyecto). A lo alto, le da
                        al bloque el alto justo de la regla más las capas que
                        haya: lo agranda si las de abajo no se veían y lo
                        achica si había quedado medio bloque en blanco. */}
                        <button
                          type="button"
                          onClick={ajustarLaTimelineEntera}
                          className="h-7 px-2.5 flex items-center justify-center whitespace-nowrap rounded-md text-[12px] font-semibold hover:bg-black/[0.04] transition-colors"
                          style={{ color: "rgb(var(--brand-black-rgb) / 0.8)" }}
                          title="Ajustar la timeline a la pantalla (ancho y alto)"
                        >
                          Ajustar timeline
                        </button>
                        {/* Minimizar / Expandir — esconde la regla y las
                        pistas y deja sólo esta fila: el preview se queda con
                        todo el alto y el transporte sigue a mano. Al expandir
                        el bloque vuelve al alto que tenía. Va acá, pegado a
                        "Ajustar timeline", porque son los dos botones que
                        cambian el tamaño del bloque. */}
                        <button
                          type="button"
                          onClick={alternarMinimizarTimeline}
                          className="h-7 px-2.5 flex items-center gap-1.5 whitespace-nowrap rounded-md text-[12px] font-semibold hover:bg-black/[0.04] transition-colors"
                          style={{ color: "rgb(var(--brand-black-rgb) / 0.8)" }}
                          title={
                            timelineMinimizada
                              ? "Mostrar la regla y las pistas de la timeline"
                              : "Esconder la regla y las pistas, y dejar sólo el transporte"
                          }
                        >
                          {timelineMinimizada ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                          {timelineMinimizada ? "Expandir" : "Minimizar"}
                        </button>
                        {/* Los atajos de teclado, a la vista
                        (ADR-2026-11-03-los-atajos-se-ven). */}
                        <BotonDeAtajos />
                      </div>
                    </div>
                  </div>
                  )}

                  {/* ── Cinta del cursor (sólo minimizada) ────────────────
                  Minimizada no hay regla ni clips: sin esto el usuario puede
                  reproducir pero no ir a un momento. El relleno azul dice
                  cuánto va del video sobre el total que muestra el transporte
                  de arriba, y se arrastra de punta a punta.

                  Va DENTRO del padding de la tarjeta (sin `-mx-4`, que es como
                  nació): a lo ancho de lado a lado, en el segundo 0 la cabeza
                  del cursor quedaba pegada al borde y cortada al medio, y
                  parecía que la cinta "entraba" al padding recién al tocarla.
                  Con los 16px de los dos lados el pin tiene aire desde el
                  arranque, igual que el 0s de la timeline —que también nace
                  corrido (`PAD_TIMELINE`) para no pegarse al borde.

                  Su alto es `ALTO_DE_LA_BARRA_DEL_CURSOR` y no una clase
                  cualquiera: es el número que el colapso le suma al marco para
                  calcular el alto de la tarjeta minimizada. */}
                  {timelineMinimizada && !enElTelefono && (
                    <div
                      onPointerDown={empezarAArrastrarLaBarra}
                      className="relative shrink-0 flex items-center cursor-pointer animate-in fade-in duration-200"
                      style={{
                        height: ALTO_DE_LA_BARRA_DEL_CURSOR,
                        // Los 7px de cada lado son la MITAD del pin: sin ellos,
                        // en el segundo 0 (y al final) la cabeza queda medio
                        // afuera del riel y se ve cortada, y sólo al arrastrar
                        // "entra" entera. Con el margen, el riel útil es el que
                        // el pin puede recorrer completo.
                        paddingLeft: 7,
                        paddingRight: 7,
                        touchAction: "none",
                      }}
                      role="slider"
                      aria-label="Posición del video"
                      aria-valuemin={0}
                      aria-valuemax={Math.round(
                        contentDuration > 0 ? contentDuration : duration,
                      )}
                      aria-valuenow={Math.round(currentTime)}
                      aria-valuetext={formatTime(currentTime)}
                    >
                      {(() => {
                        const fin =
                          contentDuration > 0 ? contentDuration : duration;
                        const porcentaje =
                          fin > 0
                            ? Math.min(100, Math.max(0, (currentTime / fin) * 100))
                            : 0;
                        return (
                          <div
                            ref={barraDelCursorRef}
                            className="relative w-full h-[4px] bg-black/[0.07]"
                          >
                            <div
                              className="absolute inset-y-0 left-0"
                              style={{
                                width: `${porcentaje}%`,
                                backgroundColor: AZUL_DE_LA_BARRA_DEL_CURSOR,
                              }}
                            />
                            {/* La MISMA cabeza que el cursor de la timeline:
                            es el mismo "dónde estoy", así que no puede ser un
                            círculo acá y un pin allá. Tampoco cambia de tamaño
                            al agarrarla: el acuse de recibo del gesto es que
                            se mueve y que el relleno azul la sigue.

                            Mide 18px de alto, igual que la cinta, y va
                            centrada sobre el riel de 4px: entra justa, sin
                            sobresalir de la tarjeta. */}
                            <CabezaDelCursor
                              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 pointer-events-none"
                              style={{ left: `${porcentaje}%` }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* ── Unified timeline area (ruler + tracks + playhead) ─────── */}
                  {(() => {
                    // El aire de los dos lados lo pone el `px-4` del bloque, y
                    // nada más: acá había además un `pl-4` que hacía que la
                    // izquierda midiera el doble que la derecha. Adentro, el
                    // respiro que le queda al 0s es `PAD_TIMELINE` (y las
                    // pistas se lo comen con `SANGRIA_PISTA`), así que la
                    // franja de clips arranca casi a ras del padding.
                    return (
                      <div
                        // Minimizada NO se desmonta ni se va a `display:none`:
                        // se queda en el flujo con `flex-1` dentro de una
                        // tarjeta que mide justo el marco, así que le toca 0 de
                        // alto y la recorta el `overflow` de la cinta, mientras
                        // se desvanece con la misma duración que baja la
                        // tarjeta. Desmontarla rompería el listener de rueda
                        // (zoom con trackpad) y el ResizeObserver del ancho:
                        // los dos se cuelgan una sola vez, con `[]` de deps,
                        // sobre `timelineAreaRef`.
                        className={`flex items-start transition-opacity duration-[260ms] ease-out ${
                          timelineMinimizada
                            ? "opacity-0 pointer-events-none overflow-hidden"
                            : "opacity-100"
                        } ${altoDeLaTimeline !== null ? "flex-1 min-h-0" : ""}`}
                        // La zona que scrollea se corre `PAD_TIMELINE` a la
                        // izquierda para que ese respiro interno (el que evita
                        // que el 0s arranque pegado y le da lugar al anillo de
                        // selección del primer clip) NO se sume al padding del
                        // bloque: así la pista arranca exactamente a los 16px
                        // del borde, los mismos que sobran a la derecha.
                        style={{
                          marginLeft: enElTelefono ? 0 : -PAD_TIMELINE,
                        }}
                      >
                        {/* Ruler + Tracks + Playhead container (horizontally scrollable, native scrollbar hidden) */}
                        <div
                          className={`flex-1 min-w-0 relative group ${
                            altoDeLaTimeline !== null ? "h-full" : ""
                          }`}
                        >
                          {/* ── Los "+" flotantes del teléfono ──────────────
                          En el teléfono el "+" no puede vivir DENTRO del
                          carril: el final de una pista casi siempre cae fuera
                          de la pantalla, y para alcanzarlo había que scrollear
                          a mano. Así que se dibuja acá, FUERA de lo que
                          scrollea, pegado al borde derecho y a la altura de su
                          propia pista — uno por pista con contenido.

                          Sigue el scroll vertical con un `translateY`: la
                          columna no scrollea sola, y sin eso los "+" quedarían
                          clavados mientras las pistas se mueven debajo.

                          `pointer-events-none` en la columna y `auto` en cada
                          botón: la franja de 44px no puede comerse el arrastre
                          de la cinta (que en el teléfono es el que mueve el
                          tiempo).
                          Ver ADR-2026-09-18-el-mas-del-telefono-flota-a-la-derecha. */}
                          {enElTelefono && (
                            <div className="pointer-events-none absolute inset-y-0 right-0 z-20 w-11 overflow-hidden">
                              <div
                                style={{
                                  transform: `translateY(${-timelineScrollTop}px)`,
                                }}
                              >
                                {displayRows.map((row, i) => {
                                  if (row.key === "elements-subtitles")
                                    return null;
                                  if (!clips.some(row.matchClip)) return null;
                                  // La misma cuenta que usa el hueco de las
                                  // filas: regla + separación + una fila por
                                  // cada una que vino antes.
                                  const topDeLaFila =
                                    TIMELINE_RULER_H +
                                    TIMELINE_ROW_GAP +
                                    i * (TIMELINE_ROW_H + TIMELINE_ROW_GAP);
                                  return (
                                    <button
                                      key={`mas-${row.key}`}
                                      type="button"
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                        const r =
                                          e.currentTarget.getBoundingClientRect();
                                        setMenuDelMas({
                                          izq: r.left,
                                          der: r.right,
                                          abajo: r.bottom,
                                          pista: pistaDelMas(row),
                                        });
                                      }}
                                      className={`pointer-events-auto absolute ${CLASES_DEL_MAS_DE_PISTA}`}
                                      style={{
                                        top:
                                          topDeLaFila +
                                          (TIMELINE_ROW_H - 30) / 2,
                                        right: 4,
                                      }}
                                      aria-label={
                                        row.trackType === "audio"
                                          ? "Agregar audio a esta pista"
                                          : row.trackType === "video"
                                            ? "Agregar video a esta pista"
                                            : "Agregar un elemento a esta pista"
                                      }
                                    >
                                      <Plus className="w-4 h-4" />
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          <div
                            ref={timelineAreaRef}
                            data-tour="timeline"
                            data-timeline-area="true"
                            className={`relative w-full select-none overflow-x-auto scrollbar-hide ${
                              altoDeLaTimeline !== null
                                ? "h-full overflow-y-auto"
                                : "overflow-y-hidden"
                            }`}
                            style={{
                              cursor: isDraggingPlayhead
                                ? "ew-resize"
                                : "pointer",
                              // `pan-x pan-y`: la cinta sigue scrolleando con
                              // el dedo, pero el navegador ya no se queda el
                              // pellizco para hacerle zoom a la página entera.
                              touchAction: enElTelefono
                                ? "pan-x pan-y"
                                : undefined,
                            }}
                            onPointerDown={handleTimelinePointerDown}
                            onTouchStart={
                              enElTelefono ? empezarElPellizco : undefined
                            }
                            onTouchMove={
                              enElTelefono ? seguirElPellizco : undefined
                            }
                            onTouchEnd={
                              enElTelefono
                                ? () => {
                                    pellizcoRef.current = null;
                                  }
                                : undefined
                            }
                            onPointerMove={handleTimelinePointerMove}
                            onPointerUp={(e) => {
                              if (enElTelefono) {
                                const inicio = toqueEnLaTimelineRef.current;
                                toqueEnLaTimelineRef.current = null;
                                const quieto =
                                  inicio !== null &&
                                  Math.hypot(
                                    e.clientX - inicio.x,
                                    e.clientY - inicio.y,
                                  ) < 8;
                                // Un toque quieto FUERA de cualquier clip es la
                                // forma de soltar la selección. Sobre un clip no:
                                // de eso se encarga el propio clip.
                                if (
                                  quieto &&
                                  !(e.target as HTMLElement | null)?.closest?.(
                                    "[data-clip-id]",
                                  )
                                )
                                  setSelectedClipId(null);
                              } else {
                                // Click (sin arrastrar) en el espacio vacío de
                                // una pista: el cursor va ahí.
                                const click = clickEnPistaRef.current;
                                clickEnPistaRef.current = null;
                                if (
                                  click &&
                                  !esArrastre(click, {
                                    x: e.clientX,
                                    y: e.clientY,
                                  })
                                )
                                  setCurrentTime(clientXToSeek(e.clientX));
                              }
                              handleTimelinePointerUp();
                            }}
                            // El navegador puede cancelar el gesto (una alerta,
                            // cambiar de pestaña, un gesto del sistema): sin esto
                            // el cursor quedaba pegado al mouse sin botón apretado.
                            onPointerCancel={() => {
                              toqueEnLaTimelineRef.current = null;
                              clickEnPistaRef.current = null;
                              handleTimelinePointerUp(true);
                            }}
                            onScroll={(e) => {
                              const el = e.currentTarget as HTMLDivElement;
                              setTimelineScrollLeft(el.scrollLeft);
                              setTimelineScrollTop(el.scrollTop);
                              // En el teléfono, mover la cinta ES mover el
                              // tiempo. Ver `sincronizarElScrollConElTiempo`.
                              if (enElTelefono) tiempoDesdeElScroll(el.scrollLeft);
                            }}
                            /* Soltar acá cae DONDE se soltó: el segundo sale
                               de la X y la capa de la Y, con la misma regla
                               que mover un clip entre capas
                               (ADR-2026-09-27-soltar-en-el-estudio-cae-donde-se-suelta).
                               Mientras pasa por encima se dibuja el destino. */
                            onDragOver={(e) => {
                              const dt = e.dataTransfer;
                              const familia = familiaDelArrastre(dt);
                              if (!familia && !arrastraArchivos(dt)) return;
                              e.preventDefault();
                              dt.dropEffect = "copy";
                              const el = timelineAreaRef.current;
                              // Cerca del borde la cinta corre sola, como al
                              // arrastrar un clip. `dragover` llega ~20 veces
                              // por segundo, no 60: de ahí el ×2.
                              if (el && !enElTelefono) {
                                const r = el.getBoundingClientRect();
                                const v = velocidadDeAutoscroll(
                                  e.clientX,
                                  r.left,
                                  r.right,
                                );
                                if (v !== 0) el.scrollLeft += v * 2;
                              }
                              if (!el || !familia) return;
                              const rect = el.getBoundingClientRect();
                              const destino = calcularDestinoDeSoltar({
                                y: e.clientY - rect.top + el.scrollTop,
                                filas: filasParaSoltar,
                                familia,
                                geometria: GEOMETRIA_DE_FILAS,
                              });
                              // Dónde ARRANCA de verdad: soltado `dentro` de
                              // una capa ocupada, se pega al final de lo que
                              // hay ahí (la misma `pegarAlFinal` del soltado).
                              // En `dragover` no se puede leer qué se arrastra,
                              // así que no se sabe su duración: si el hueco no
                              // le alcanza, al soltar puede correrse un poco más.
                              let segundo = clientXToSeconds(e.clientX);
                              if (destino.clase === "dentro") {
                                segundo = pegarAlFinal({
                                  clips: clips.filter(
                                    (c) =>
                                      esDeLaFamilia(c, familia) &&
                                      getClipTrackIndex(c) === destino.trackIndex,
                                  ),
                                  inicio: segundo,
                                });
                              }
                              setSegundoDeLoSoltado((prev) =>
                                prev !== null && Math.abs(prev - segundo) < 0.01
                                  ? prev
                                  : segundo,
                              );
                              setDestinoDeLoSoltado((prev) =>
                                prev &&
                                prev.clase === destino.clase &&
                                prev.yPx === destino.yPx
                                  ? prev
                                  : destino,
                              );
                            }}
                            onDragLeave={(e) => {
                              // Salir a un hijo también dispara `dragleave`:
                              // sólo cuenta salir del área entera.
                              const el = timelineAreaRef.current;
                              if (
                                el &&
                                e.relatedTarget instanceof Node &&
                                el.contains(e.relatedTarget)
                              )
                                return;
                              setDestinoDeLoSoltado(null);
                              setSegundoDeLoSoltado(null);
                            }}
                            onDrop={(e) => {
                              const dt = e.dataTransfer;
                              const familia = familiaDelArrastre(dt);
                              if (!familia && !arrastraArchivos(dt)) return;
                              e.preventDefault();
                              setDestinoDeLoSoltado(null);
                              setSegundoDeLoSoltado(null);
                              // El destino se recalcula del evento y no se
                              // lee del estado: el último `dragover` puede no
                              // haber llegado a dibujarse.
                              const el = timelineAreaRef.current;
                              const rect = el?.getBoundingClientRect();
                              const destino =
                                el && rect && familia
                                  ? calcularDestinoDeSoltar({
                                      y: e.clientY - rect.top + el.scrollTop,
                                      filas: filasParaSoltar,
                                      familia,
                                      geometria: GEOMETRIA_DE_FILAS,
                                    })
                                  : undefined;
                              void soltarEnElEstudio(dt, {
                                startTime: clientXToSeconds(e.clientX),
                                destino,
                              });
                            }}
                          >
                            <div
                              className="relative"
                              style={{
                                // En el teléfono el respiro va de los DOS lados
                                // (media pantalla cada uno): sin el de la
                                // derecha, el final del video nunca llega a
                                // pasar por debajo del cursor.
                                width:
                                  timelineWidth +
                                  padIzqTimeline +
                                  (enElTelefono ? padIzqTimeline : 0),
                                minWidth: "100%",
                                paddingLeft: padIzqTimeline,
                                paddingRight: enElTelefono
                                  ? padIzqTimeline
                                  : undefined,
                              }}
                            >
                              {/* ── Ruler ────────────────────────────────────────────── */}
                              {/* `data-timeline-scrub`: mover el cursor NO
                              deselecciona el clip. Sin esto no se podía
                              seleccionar un clip y después buscar el punto de
                              corte — el arrastre mataba la selección y con ella
                              el botón Dividir. Ver
                              ADR-2026-08-11-mover-el-cursor-no-deselecciona. */}
                              <div
                                ref={timelineRef}
                                data-timeline-scrub="true"
                                className="relative"
                                style={{ height: 28, width: timelineWidth }}
                              >
                                <div
                                  className="absolute bottom-0 left-0"
                                  style={{
                                    height: 1,
                                    width: timelineWidth,
                                    backgroundColor: "rgb(var(--brand-black-rgb) / 0.15)",
                                  }}
                                />
                                {(() => {
                                  // Tick density in seconds — based on how many pixels per second we have
                                  let labelInterval: number;
                                  let tickInterval: number;
                                  if (pps >= 1500) {
                                    // Zoom de cuadro: rótulo cada décima y
                                    // marca cada 25ms (~un cuadro).
                                    labelInterval = 0.1;
                                    tickInterval = 0.025;
                                  } else if (pps >= 900) {
                                    labelInterval = 0.25;
                                    tickInterval = 0.05;
                                  } else if (pps >= 600) {
                                    labelInterval = 0.5;
                                    tickInterval = 0.1;
                                  } else if (pps >= 250) {
                                    labelInterval = 1;
                                    tickInterval = 0.25;
                                  } else if (pps >= 100) {
                                    labelInterval = 1;
                                    tickInterval = 0.5;
                                  } else if (pps >= 60) {
                                    labelInterval = 2;
                                    tickInterval = 1;
                                  } else if (pps >= 30) {
                                    labelInterval = 5;
                                    tickInterval = 1;
                                  } else if (pps >= 15) {
                                    labelInterval = 10;
                                    tickInterval = 2;
                                  } else {
                                    labelInterval = 30;
                                    tickInterval = 5;
                                  }

                                  const els: React.ReactNode[] = [];
                                  const steps = Math.ceil(
                                    timelineDuration / tickInterval,
                                  );
                                  for (let i = 0; i <= steps; i++) {
                                    const sec = i * tickInterval;
                                    if (sec > timelineDuration) break;
                                    const leftPx = sec * pps;
                                    const isLabeled =
                                      Math.abs(sec % labelInterval) < 1e-6;
                                    const tickH = isLabeled ? 10 : 5;
                                    const tickColor = isLabeled
                                      ? "rgb(var(--brand-black-rgb) / 0.25)"
                                      : "rgb(var(--brand-black-rgb) / 0.12)";
                                    els.push(
                                      <div
                                        key={`tk-${sec}`}
                                        className="absolute"
                                        style={{
                                          left: leftPx,
                                          bottom: 0,
                                          height: tickH,
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: 1,
                                            height: tickH,
                                            backgroundColor: tickColor,
                                          }}
                                        />
                                        {isLabeled && (
                                          <span
                                            className="absolute whitespace-nowrap tabular-nums font-medium select-none"
                                            style={{
                                              bottom: tickH + 3,
                                              left: 2,
                                              fontSize: 11,
                                              color: "rgb(var(--brand-black-rgb) / 0.4)",
                                            }}
                                          >
                                            {/* `Number(toFixed(2))` en vez de
                                            un decimal fijo: sirve para 0.5,
                                            para 0.25 y para 1.1 sin escribir
                                            "1.00s", y de paso limpia la basura
                                            del acumulador (0.30000000000004). */}
                                            {sec === 0
                                              ? "0s"
                                              : labelInterval < 1
                                                ? `${Number(sec.toFixed(2))}s`
                                                : `${Math.round(sec)}s`}
                                          </span>
                                        )}
                                      </div>,
                                    );
                                  }
                                  return els;
                                })()}
                              </div>

                              {/* ── Dynamic track containers ──────────────────────── */}
                              <div
                                style={{
                                  maxHeight:
                                    altoDeLaTimeline !== null ? undefined : 800,
                                  overflow:
                                    altoDeLaTimeline !== null
                                      ? "visible"
                                      : "hidden",
                                  // Este contenedor también recortaba justo en
                                  // el 0s; se corre `SANGRIA_PISTA` a la
                                  // izquierda junto con las pistas.
                                  marginLeft: -SANGRIA_PISTA,
                                }}
                              >
                                {displayRows.map((row) => {
                                  const trackClips = clips.filter(
                                    row.matchClip,
                                  );
                                  return (
                                    <div
                                      key={row.key}
                                      className="relative overflow-hidden"
                                      style={{
                                        height: 48,
                                        width: timelineWidth + SANGRIA_PISTA,
                                        marginTop: 6,
                                      }}
                                    >
                                      {/* La pista visible arranca exacto en el
                                      0s: todas las cuentas de píxeles de acá
                                      adentro (clips, fantasma del imán,
                                      indicadores de transición) siguen siendo
                                      `segundos * pps` sin corrimientos. La caja
                                      de AFUERA es la que recorta, y llega
                                      `SANGRIA_PISTA` más a la izquierda para
                                      que lo que sobresale del primer clip —el
                                      anillo de selección y su manija— se vea
                                      entero. */}
                                      <div
                                        className="absolute flex items-center"
                                        style={{
                                          left: SANGRIA_PISTA,
                                          right: 0,
                                          top: 0,
                                          bottom: 0,
                                          borderRadius: 8,
                                          backgroundColor: "rgb(var(--brand-black-rgb) / 0.06)",
                                        }}
                                      >
                                      {/* ── El "+" al final de la pista ─────
                                      Va después del último clip de CADA pista
                                      con contenido, en la timeline (no en una
                                      fila aparte). Suma material del tipo de
                                      esa pista: video, audio o elementos.
                                      Antes vivía en una pista de video vacía
                                      que se forzaba siempre y quedaba colgada
                                      abajo sin pertenecer a nada.
                                      Se dibuja en escritorio y en el teléfono.
                                      Ver ADR-2026-09-18-el-mas-vive-al-final-de-cada-pista. */}
                                      {!enElTelefono &&
                                        trackClips.length > 0 &&
                                        row.key !== "elements-subtitles" &&
                                        (() => {
                                          const finDeLaPista = trackClips.reduce(
                                            (acc, c) =>
                                              Math.max(
                                                acc,
                                                c.startTime + c.duration,
                                              ),
                                            0,
                                          );
                                          return (
                                            <button
                                              type="button"
                                              onPointerDown={(e) => {
                                                e.stopPropagation();
                                                const r =
                                                  e.currentTarget.getBoundingClientRect();
                                                setMenuDelMas({
                                                  izq: r.left,
                                                  der: r.right,
                                                  abajo: r.bottom,
                                                  pista: pistaDelMas(row),
                                                });
                                              }}
                                              className={`absolute z-20 ${CLASES_DEL_MAS_DE_PISTA}`}
                                              style={{
                                                left: finDeLaPista * pps + 8,
                                              }}
                                              title={
                                                row.trackType === "audio"
                                                  ? "Agregar audio a esta pista"
                                                  : row.trackType === "video"
                                                    ? "Agregar video a esta pista"
                                                    : "Agregar un elemento a esta pista"
                                              }
                                              aria-label="Agregar al final de la pista"
                                            >
                                              <Plus className="w-4 h-4" />
                                            </button>
                                          );
                                        })()}
                                      {trackClips.length > 0 ? (
                                        trackClips.map((clip) => {
                                          const CLIP_VISUAL_GAP_PX = 3;
                                          const leftPx = clip.startTime * pps;
                                          const widthPx = Math.max(
                                            clip.duration * pps -
                                              CLIP_VISUAL_GAP_PX,
                                            2,
                                          );
                                          const isDragged =
                                            draggingClipRef.current?.id ===
                                            clip.id;
                                          const isAiClip =
                                            clip.type === "ai-generated";
                                          const isMedia =
                                            clip.type === "video" ||
                                            (isAiClip &&
                                              clip.aiStatus === "ready");
                                          // Una capa de imagen también se
                                          // muestra con su contenido y no como
                                          // una barra de color con el nombre
                                          // del archivo: en una timeline con
                                          // varias, el nombre no dice cuál es.
                                          // `imageUrl` es el respaldo para el
                                          // logo, que al recargar sólo tiene esa.
                                          const urlDeLaImagen =
                                            clip.type === "image"
                                              ? (clip.src ?? clip.imageUrl)
                                              : null;
                                          const showFrameStrip =
                                            (isMedia && !!clip.src) ||
                                            !!urlDeLaImagen;
                                          const isSelected =
                                            clip.id === selectedClipId;
                                          const thumbs =
                                            clipThumbnails[clip.id];
                                          // When there is a frame strip, skip the built-in color border; selection uses yellow handles instead
                                          return (
                                            <div
                                              key={clip.id}
                                              data-clip-id={clip.id}
                                              data-is-media={
                                                isMedia ? "1" : "0"
                                              }
                                              onPointerDown={(e) =>
                                                handleClipPointerDown(e, clip)
                                              }
                                              className={`absolute flex items-center overflow-visible${bounceClipIds.has(clip.id) ? " clip-bounce" : ""}`}
                                              style={{
                                                left: leftPx,
                                                width: widthPx,
                                                top: 4,
                                                bottom: 4,
                                                cursor: isDragged
                                                  ? "grabbing"
                                                  : "grab",
                                                // ESTO es lo que decidía que la
                                                // cinta no se pudiera arrastrar
                                                // desde un clip en el teléfono:
                                                // con `touch-action: none` el
                                                // navegador no arranca ningún
                                                // scroll ahí, sin importar
                                                // quién capture el puntero. En
                                                // escritorio sigue en `none`
                                                // (el arrastre del clip se
                                                // maneja a mano); en el
                                                // teléfono el clip pasa el
                                                // gesto a la cinta, con el
                                                // mismo `pan-x pan-y` que el
                                                // contenedor que scrollea.
                                                touchAction: enElTelefono
                                                  ? "pan-x pan-y"
                                                  : "none",
                                                opacity:
                                                  isDragged && snapTarget
                                                    ? 0.5
                                                    : 1,
                                                transition: "opacity 0.1s",
                                                zIndex: isSelected
                                                  ? 20
                                                  : isDragged
                                                    ? 15
                                                    : 1,
                                              }}
                                            >
                                              {/* Subtitle generation overlay. Rendered
                                        above the inner content (frame strip
                                        / waveform / AI preview) so it reads
                                        clearly while the webhook is running.
                                        The shimmer uses the .clip-shimmer
                                        class + ::before pseudo defined in
                                        index.css. */}
                                              {subtitlesLoading &&
                                                subtitlesSourceClipId ===
                                                  clip.id && (
                                                  <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none pl-2">
                                                    <div
                                                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                      style={{
                                                        maxWidth:
                                                          "calc(100% - 12px)",
                                                      }}
                                                    >
                                                      <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                                                      {widthPx >= 120 && (
                                                        <span className="truncate">
                                                          Generando subtítulos…
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>
                                                )}

                                              {/* Video translation overlay — mismo patrón que subtítulos.
                                              Si el clip lleva varios segundos traduciéndose
                                              también mostramos un contador alineado a la
                                              derecha (mismo patrón visual que el badge
                                              elapsed de generación de video AI). */}
                                              {clip.aiTranslating && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Languages className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Traduciendo al inglés…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.aiTranslateStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiTranslateStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Object-erase overlay — mismo
                                              patrón visual que traducción: shimmer
                                              + pill con icono y, si el clip es
                                              ancho, contador de tiempo a la derecha
                                              para que el cliente sepa que está
                                              andando. */}
                                              {/* "Editar clip" en curso, lista o con
                                              error: vive en la sesión del clip, así
                                              que se ve con el panel cerrado. La
                                              pastilla reabre el panel tal como quedó
                                              (ADR-2026-09-27-editar-clip-sigue-con-el-panel-cerrado). */}
                                              {(() => {
                                                const ed = sesionesDeEdicion.get(clip.id);
                                                if (!ed) return null;
                                                const abrir = (e: React.SyntheticEvent) => {
                                                  e.stopPropagation();
                                                  abrirEdicionDeClip(clip.id);
                                                };
                                                if (ed.enCurso)
                                                  return (
                                                    <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                      <button
                                                        type="button"
                                                        title="Ver la edición"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={abrir}
                                                        className="pointer-events-auto inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm hover:bg-white/35"
                                                        style={{ maxWidth: "calc(100% - 12px)" }}
                                                      >
                                                        <Wand2 className="w-3 h-3 shrink-0" />
                                                        {widthPx >= 120 && <span className="truncate">Editando clip…</span>}
                                                      </button>
                                                      {widthPx >= 160 && (
                                                        <span className="ml-auto">
                                                          <ElapsedBadge startedAt={ed.enCurso.desde} color="#ffffff" />
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                if (ed.abierta) return null;
                                                if (ed.error)
                                                  return (
                                                    <button
                                                      type="button"
                                                      title={ed.error}
                                                      onPointerDown={(e) => e.stopPropagation()}
                                                      onClick={abrir}
                                                      className="absolute top-1 left-1 z-30 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600/90 text-white text-[10px] font-semibold shadow"
                                                    >
                                                      <Wand2 className="w-3 h-3" />
                                                      Edición: error
                                                    </button>
                                                  );
                                                if (ed.lista)
                                                  return (
                                                    <button
                                                      type="button"
                                                      title="Ver la edición para guardarla"
                                                      onPointerDown={(e) => e.stopPropagation()}
                                                      onClick={abrir}
                                                      className="absolute top-1 left-1 z-30 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-primary text-white text-[10px] font-semibold shadow"
                                                    >
                                                      <Wand2 className="w-3 h-3" />
                                                      Edición lista
                                                    </button>
                                                  );
                                                return null;
                                              })()}
                                              {clip.corregirVozStatus === "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{ maxWidth: "calc(100% - 12px)" }}
                                                  >
                                                    <Mic className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && <span className="truncate">Corrigiendo la voz…</span>}
                                                  </div>
                                                </div>
                                              )}
                                              {clip.pasarA1080pStatus === "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{ maxWidth: "calc(100% - 12px)" }}
                                                  >
                                                    <ArrowUpFromLine className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && <span className="truncate">Pasando a 1080p…</span>}
                                                  </div>
                                                </div>
                                              )}
                                              {clip.eraseStatus === "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Eraser className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Borrando objeto…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.eraseStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.eraseStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Cambiar-voz overlay — mismo
                                              patrón visual que traducción/borrado:
                                              shimmer + pill. */}
                                              {clip.voiceChangeStatus ===
                                                "processing" && (
                                                <div className="clip-shimmer absolute inset-0 z-30 flex items-center justify-start rounded-md overflow-hidden bg-black/55 pointer-events-none px-2 gap-1.5">
                                                  <div
                                                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/25 backdrop-blur-sm text-white text-[11px] font-semibold shadow-sm"
                                                    style={{
                                                      maxWidth:
                                                        "calc(100% - 12px)",
                                                    }}
                                                  >
                                                    <Mic className="w-3 h-3 shrink-0" />
                                                    {widthPx >= 120 && (
                                                      <span className="truncate">
                                                        Cambiando voz…
                                                      </span>
                                                    )}
                                                  </div>
                                                  {clip.voiceChangeStartedAt &&
                                                    widthPx >= 160 && (
                                                      <span className="ml-auto">
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.voiceChangeStartedAt
                                                          }
                                                          color="#ffffff"
                                                        />
                                                      </span>
                                                    )}
                                                </div>
                                              )}

                                              {/* Muted badge — audio/video/AI clips with volume 0 */}
                                              {(clip.type === "audio" ||
                                                clip.type === "video" ||
                                                clip.type === "ai-generated") &&
                                                (clip.volume ?? 1) === 0 && (
                                                  <div
                                                    className="absolute z-30 flex items-center justify-center rounded-full bg-black/75 text-white shadow-sm pointer-events-none"
                                                    style={{
                                                      top: 3,
                                                      left: 3,
                                                      width: 16,
                                                      height: 16,
                                                    }}
                                                    title="Silenciado"
                                                  >
                                                    <VolumeX className="w-2.5 h-2.5" />
                                                  </div>
                                                )}

                                              {/* Load error badge: la signed URL del clip
                                              no se pudo generar al abrir el proyecto
                                              (típico: archivo eliminado del bucket o
                                              error de conexión). Click para reintentar
                                              SOLO la generación de la signed URL. */}
                                              {clip.loadError &&
                                                (() => {
                                                  const isRetrying =
                                                    retryingLoadForClipId ===
                                                    clip.id;
                                                  return (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.stopPropagation();
                                                        void retryLoadSignedUrl(
                                                          clip.id,
                                                        );
                                                      }}
                                                      disabled={isRetrying}
                                                      className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 disabled:opacity-70 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
                                                      style={{
                                                        bottom: 3,
                                                        right: 3,
                                                        width: 18,
                                                        height: 18,
                                                      }}
                                                      title={
                                                        isRetrying
                                                          ? "Reintentando cargar el archivo…"
                                                          : clip.storagePath
                                                            ? `${clip.loadError} (click para reintentar)`
                                                            : // Sin archivo (la subida no terminó): no hay nada que reintentar acá.
                                                              clip.loadError
                                                      }
                                                    >
                                                      {isRetrying ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                      ) : (
                                                        <AlertCircle className="w-3 h-3" />
                                                      )}
                                                    </button>
                                                  );
                                                })()}

                                              {/* Erase-object error badge: el borrado de
                                              objeto falló. Mismo patrón que loadError —
                                              badge rojo clickeable; al tocarlo se reabre
                                              el pincel para reintentar (re-marcar el
                                              objeto). El error va en el tooltip. */}
                                              {clip.pasarA1080pStatus === "error" && (
                                                <button
                                                  type="button"
                                                  title={clip.pasarA1080pError || "No se pudo pasar a 1080p"}
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? { ...c, pasarA1080pStatus: undefined, pasarA1080pError: undefined }
                                                          : c,
                                                      ),
                                                    );
                                                  }}
                                                  className="absolute top-1 left-1 z-30 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600/90 text-white text-[10px] font-semibold shadow"
                                                >
                                                  <ArrowUpFromLine className="w-3 h-3" />
                                                  1080p: error
                                                </button>
                                              )}
                                              {clip.corregirVozStatus === "error" && (
                                                <button
                                                  type="button"
                                                  title={clip.corregirVozError || "No se pudo corregir la voz"}
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? { ...c, corregirVozStatus: undefined, corregirVozError: undefined }
                                                          : c,
                                                      ),
                                                    );
                                                  }}
                                                  className="absolute top-1 left-1 z-30 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-600/90 text-white text-[10px] font-semibold shadow"
                                                >
                                                  <Mic className="w-3 h-3" />
                                                  Voz: error
                                                </button>
                                              )}
                                              {clip.eraseStatus === "error" && (
                                                <button
                                                  type="button"
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? {
                                                              ...c,
                                                              eraseStatus: undefined,
                                                              eraseError: undefined,
                                                            }
                                                          : c,
                                                      ),
                                                    );
                                                    openEraseBrush(clip.id);
                                                  }}
                                                  className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors"
                                                  style={{
                                                    bottom: 3,
                                                    right: 3,
                                                    width: 18,
                                                    height: 18,
                                                  }}
                                                  title={`No se pudo borrar el objeto: ${clip.eraseError || "error desconocido"} (click para reintentar)`}
                                                >
                                                  <Eraser className="w-3 h-3" />
                                                </button>
                                              )}

                                              {/* Cambiar-voz error badge: el S2S
                                              falló. Badge rojo clickeable; al
                                              tocarlo reabre el picker para
                                              reintentar. El error va en el tooltip. */}
                                              {clip.voiceChangeStatus === "error" && (
                                                <button
                                                  type="button"
                                                  onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                    setClips((prev) =>
                                                      prev.map((c) =>
                                                        c.id === clip.id
                                                          ? {
                                                              ...c,
                                                              voiceChangeStatus:
                                                                undefined,
                                                              voiceChangeError:
                                                                undefined,
                                                            }
                                                          : c,
                                                      ),
                                                    );
                                                    setChangeVoiceClipId(clip.id);
                                                    setActivePanel("voz");
                                                  }}
                                                  className="absolute z-30 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors"
                                                  style={{
                                                    bottom: 3,
                                                    right: 3,
                                                    width: 18,
                                                    height: 18,
                                                  }}
                                                  title={`No se pudo cambiar la voz: ${clip.voiceChangeError || "error desconocido"} (click para reintentar)`}
                                                >
                                                  <Mic className="w-3 h-3" />
                                                </button>
                                              )}

                                              {/* Asset-register warning badge: el archivo AI
                                              se generó OK pero no pudo guardarse en la
                                              biblioteca del proyecto. Click para reintentar
                                              SOLO el INSERT (no se re-genera el archivo
                                              ni se descuentan créditos). */}
                                              {clip.aiAssetWarning &&
                                                (() => {
                                                  const isRetrying =
                                                    retryingAssetForClipId ===
                                                    clip.id;
                                                  return (
                                                    <button
                                                      type="button"
                                                      onPointerDown={(e) => {
                                                        e.stopPropagation();
                                                        void retryAssetRegistration(
                                                          clip.id,
                                                        );
                                                      }}
                                                      disabled={isRetrying}
                                                      className="absolute z-30 flex items-center justify-center rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-70 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
                                                      style={{
                                                        top: 3,
                                                        right: 3,
                                                        width: 18,
                                                        height: 18,
                                                      }}
                                                      title={
                                                        isRetrying
                                                          ? "Reintentando guardar en biblioteca…"
                                                          : `${clip.aiAssetWarning} (click para reintentar)`
                                                      }
                                                    >
                                                      {isRetrying ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                      ) : (
                                                        <AlertCircle className="w-3 h-3" />
                                                      )}
                                                    </button>
                                                  );
                                                })()}

                                              {/* Inner content: frame strip OR colored body */}
                                              <div
                                                className="relative w-full h-full overflow-hidden flex items-center"
                                                style={{
                                                  borderRadius: isSelected
                                                    ? 0
                                                    : 6,
                                                  backgroundColor:
                                                    showFrameStrip
                                                      ? "#0a0a0a"
                                                      : clip.color + "20",
                                                  borderLeft: showFrameStrip
                                                    ? "none"
                                                    : `3px solid ${clip.color}`,
                                                  outline: isSelected
                                                    ? `2px solid ${COLOR_SELECCION}`
                                                    : "none",
                                                  outlineOffset: 0,
                                                }}
                                              >
                                                {showFrameStrip ? (
                                                  urlDeLaImagen ? (
                                                    /* Una imagen no tiene
                                                       frames que muestrear: se
                                                       repite a lo largo del
                                                       clip, que es lo que hace
                                                       la tira de video con sus
                                                       cuadros. `auto 100%` la
                                                       deja a la altura de la
                                                       fila sin deformarla. */
                                                    <div
                                                      className="absolute inset-0"
                                                      style={{
                                                        backgroundImage: `url("${urlDeLaImagen}")`,
                                                        backgroundSize: "auto 100%",
                                                        backgroundRepeat: "repeat-x",
                                                        backgroundPosition: "left center",
                                                      }}
                                                    />
                                                  ) : thumbs &&
                                                  thumbs.length > 0 ? (
                                                    (() => {
                                                      // Fixed-width film-strip frames. Each frame slot is anchored to a fixed pixel position
                                                      // within the clip — so its sampled source time is `mediaOffset + (pixelCenter / pps)`.
                                                      // This keeps already-visible frames stable when the user trims/extends the clip: frames
                                                      // only appear or disappear at the edges, instead of all the thumbnails shifting.
                                                      const FRAME_WIDTH = 56;
                                                      const frameCount =
                                                        Math.max(
                                                          1,
                                                          Math.ceil(
                                                            widthPx /
                                                              FRAME_WIDTH,
                                                          ) + 1,
                                                        );
                                                      const offset =
                                                        clip.mediaOffset || 0;
                                                      const totalSrcDuration =
                                                        clip.sourceDuration ??
                                                        clip.duration;
                                                      const frames: React.ReactNode[] =
                                                        [];
                                                      for (
                                                        let i = 0;
                                                        i < frameCount;
                                                        i++
                                                      ) {
                                                        const pixelCenter =
                                                          i * FRAME_WIDTH +
                                                          FRAME_WIDTH / 2;
                                                        const sourceTime =
                                                          offset +
                                                          (pps > 0
                                                            ? (pixelCenter /
                                                                pps) *
                                                              velocidadDeClip(
                                                                clip,
                                                              )
                                                            : 0);
                                                        const ratio =
                                                          totalSrcDuration > 0
                                                            ? sourceTime /
                                                              totalSrcDuration
                                                            : 0;
                                                        const thumbIdx =
                                                          Math.min(
                                                            thumbs.length - 1,
                                                            Math.max(
                                                              0,
                                                              Math.floor(
                                                                ratio *
                                                                  thumbs.length,
                                                              ),
                                                            ),
                                                          );
                                                        frames.push(
                                                          <div
                                                            key={i}
                                                            className="h-full shrink-0"
                                                            style={{
                                                              width:
                                                                FRAME_WIDTH,
                                                              backgroundImage: `url(${thumbs[thumbIdx]})`,
                                                              backgroundSize:
                                                                "cover",
                                                              backgroundPosition:
                                                                "center",
                                                            }}
                                                          />,
                                                        );
                                                      }
                                                      return (
                                                        <div className="flex items-stretch w-full h-full overflow-hidden">
                                                          {frames}
                                                        </div>
                                                      );
                                                    })()
                                                  ) : (
                                                    <div className="w-full h-full bg-gradient-to-r from-neutral-800 via-neutral-700 to-neutral-800 animate-pulse" />
                                                  )
                                                ) : isAiClip ? (
                                                  <div className="flex items-center w-full h-full px-1 gap-1 min-w-0">
                                                    {clip.aiFirstFrame && (
                                                      <img
                                                        src={clip.aiFirstFrame}
                                                        alt=""
                                                        className="h-full w-auto rounded-sm object-cover shrink-0"
                                                        style={{ maxWidth: 28 }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "generating" && (
                                                      <Loader2
                                                        className="w-3.5 h-3.5 animate-spin shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "draft" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors"
                                                      >
                                                        <Sparkles className="w-3 h-3" />
                                                        Generar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                            segundosDeVideoDeReferencia(clip.aiRefAssetIds),
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiStatus ===
                                                      "error" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        title={
                                                          clip.aiError ||
                                                          "Volver a generar"
                                                        }
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                      >
                                                        Reintentar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                            segundosDeVideoDeReferencia(clip.aiRefAssetIds),
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    <span
                                                      className="text-[11px] font-medium truncate select-none flex-1 min-w-0 px-0.5"
                                                      style={{
                                                        color: clip.color,
                                                      }}
                                                    >
                                                      {clip.aiStatus ===
                                                      "generating"
                                                        ? "Generando..."
                                                        : clip.aiPrompt ||
                                                          clip.name}
                                                    </span>
                                                    {clip.aiStatus ===
                                                      "generating" &&
                                                      clip.aiGenerationStartedAt && (
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiGenerationStartedAt
                                                          }
                                                          color={clip.color}
                                                        />
                                                      )}
                                                    {/* Cancelar la escena: no se cobra si el video
                                                    todavía no se generó
                                                    (ADR-2026-11-03-cancelar-una-escena-del-estudio).
                                                    Sólo las que van por el backend de Atlas. */}
                                                    {clip.aiStatus ===
                                                      "generating" &&
                                                      clip.aiExecutionId &&
                                                      USE_ATLAS_BACKEND &&
                                                      !N8N_ONLY_REF_MODES.has(
                                                        refModeDeLaEscena(clip),
                                                      ) && (
                                                        <button
                                                          type="button"
                                                          onPointerDown={(e) =>
                                                            e.stopPropagation()
                                                          }
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            void cancelarEscenaDelClip(
                                                              clip.id,
                                                            );
                                                          }}
                                                          title="Cancelar la escena (no se cobra si todavía no se generó)"
                                                          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold text-brand-black/70 bg-white/80 hover:bg-white transition-colors"
                                                        >
                                                          Cancelar
                                                        </button>
                                                      )}
                                                    {clip.aiLastFrame && (
                                                      <img
                                                        src={clip.aiLastFrame}
                                                        alt=""
                                                        className="h-full w-auto rounded-sm object-cover shrink-0"
                                                        style={{ maxWidth: 28 }}
                                                      />
                                                    )}
                                                  </div>
                                                ) : clip.type === "audio" &&
                                                  clip.src &&
                                                  audioPeaks[clip.id] ? (
                                                  (() => {
                                                    // Anchor each bar to a fixed pixel inside the clip, and index the peak
                                                    // by absolute source time. That way trimming only adds/removes bars at
                                                    // the edges — the rest of the waveform stays stable.
                                                    const peaks =
                                                      audioPeaks[clip.id];
                                                    const BAR_WIDTH = 2;
                                                    const GAP = 1;
                                                    const STEP =
                                                      BAR_WIDTH + GAP;
                                                    const innerWidth = Math.max(
                                                      widthPx,
                                                      2,
                                                    );
                                                    const barCount = Math.max(
                                                      1,
                                                      Math.floor(
                                                        innerWidth / STEP,
                                                      ),
                                                    );
                                                    const offset =
                                                      clip.mediaOffset || 0;
                                                    const totalSrcDuration =
                                                      clip.sourceDuration ??
                                                      clip.duration;
                                                    const bars: React.ReactNode[] =
                                                      [];
                                                    for (
                                                      let i = 0;
                                                      i < barCount;
                                                      i++
                                                    ) {
                                                      const pixelCenter =
                                                        i * STEP + STEP / 2;
                                                      const sourceTime =
                                                        offset +
                                                        (pps > 0
                                                          ? pixelCenter / pps
                                                          : 0);
                                                      const ratio =
                                                        totalSrcDuration > 0
                                                          ? sourceTime /
                                                            totalSrcDuration
                                                          : 0;
                                                      const idx = Math.min(
                                                        peaks.length - 1,
                                                        Math.max(
                                                          0,
                                                          Math.floor(
                                                            ratio *
                                                              peaks.length,
                                                          ),
                                                        ),
                                                      );
                                                      const h = Math.max(
                                                        0.08,
                                                        peaks[idx],
                                                      );
                                                      bars.push(
                                                        <div
                                                          key={i}
                                                          style={{
                                                            width: BAR_WIDTH,
                                                            height: `${h * 100}%`,
                                                            backgroundColor:
                                                              clip.color,
                                                            borderRadius: 1,
                                                            opacity: 0.9,
                                                            marginRight: GAP,
                                                            flexShrink: 0,
                                                          }}
                                                        />,
                                                      );
                                                    }
                                                    return (
                                                      <div className="flex items-center w-full h-full overflow-hidden">
                                                        {bars}
                                                      </div>
                                                    );
                                                  })()
                                                ) : clip.type === "audio" &&
                                                  clip.aiPrompt ? (
                                                  <div className="flex items-center w-full h-full px-2 gap-1.5 min-w-0">
                                                    {clip.aiStatus ===
                                                      "generating" && (
                                                      <Loader2
                                                        className="w-3.5 h-3.5 animate-spin shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    {clip.aiStatus ===
                                                      "draft" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiAudioClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors"
                                                      >
                                                        <Sparkles className="w-3 h-3" />
                                                        Generar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                            segundosDeVideoDeReferencia(clip.aiRefAssetIds),
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiStatus ===
                                                      "error" && (
                                                      <button
                                                        onPointerDown={(e) => {
                                                          e.stopPropagation();
                                                          generateAiAudioClip(
                                                            clip.id,
                                                          );
                                                        }}
                                                        title={
                                                          clip.aiError ||
                                                          "Volver a generar"
                                                        }
                                                        className="shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors"
                                                      >
                                                        Reintentar
                                                        <CreditBadge
                                                          credits={creditsForAiClip(
                                                            clip,
                                                            segundosDeVideoDeReferencia(clip.aiRefAssetIds),
                                                          )}
                                                          tone="on-primary"
                                                          size="sm"
                                                          className="ml-0.5"
                                                        />
                                                      </button>
                                                    )}
                                                    {clip.aiAudioType ===
                                                    "music" ? (
                                                      <Music
                                                        className="w-3 h-3 shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    ) : (
                                                      <Mic
                                                        className="w-3 h-3 shrink-0"
                                                        style={{
                                                          color: clip.color,
                                                        }}
                                                      />
                                                    )}
                                                    <span
                                                      className="text-[11px] font-medium truncate select-none flex-1 min-w-0"
                                                      style={{
                                                        color: clip.color,
                                                      }}
                                                    >
                                                      {clip.aiStatus ===
                                                      "generating"
                                                        ? "Generando..."
                                                        : clip.aiPrompt ||
                                                          clip.name}
                                                    </span>
                                                    {clip.aiStatus ===
                                                      "generating" &&
                                                      clip.aiGenerationStartedAt && (
                                                        <ElapsedBadge
                                                          startedAt={
                                                            clip.aiGenerationStartedAt
                                                          }
                                                          color={clip.color}
                                                        />
                                                      )}
                                                  </div>
                                                ) : clip.type === "animation" &&
                                                  clip.aiStatus ===
                                                    "generating" ? (
                                                  // El placeholder de una animación
                                                  // gráfica: como un video con IA
                                                  // generando (spinner + tiempo).
                                                  <div className="flex items-center w-full h-full px-1.5 gap-1 min-w-0">
                                                    <Loader2
                                                      className="w-3.5 h-3.5 animate-spin shrink-0"
                                                      style={{ color: clip.color }}
                                                    />
                                                    <span
                                                      className="text-[11px] font-medium truncate select-none flex-1 min-w-0 px-0.5"
                                                      style={{ color: clip.color }}
                                                    >
                                                      Generando...
                                                    </span>
                                                    {clip.aiGenerationStartedAt && (
                                                      <ElapsedBadge
                                                        startedAt={
                                                          clip.aiGenerationStartedAt
                                                        }
                                                        color={clip.color}
                                                      />
                                                    )}
                                                  </div>
                                                ) : (
                                                  <span
                                                    className="text-[11px] font-medium truncate select-none px-2.5"
                                                    style={{
                                                      color: clip.color,
                                                    }}
                                                  >
                                                    {clip.name}
                                                  </span>
                                                )}
                                              </div>

                                              {/* Borde interno blanco: la otra mitad
                                              del doble anillo. Va como overlay y no
                                              como `border` del contenido para no
                                              correr 2px la tira de frames al
                                              seleccionar. */}
                                              {isSelected && (
                                                <div
                                                  className="absolute pointer-events-none"
                                                  style={{
                                                    inset: 0,
                                                    border: "2px solid #FFFFFF",
                                                    zIndex: 2,
                                                  }}
                                                />
                                              )}

                                              {/* Selection handles — draggable to trim */}
                                              {isSelected && (
                                                <>
                                                  <div
                                                    onPointerDown={(e) =>
                                                      handleTrimPointerDown(
                                                        e,
                                                        clip,
                                                        "left",
                                                      )
                                                    }
                                                    className="absolute top-0 bottom-0 flex items-center justify-center"
                                                    style={{
                                                      left: -8,
                                                      width: 10,
                                                      backgroundColor:
                                                        COLOR_SELECCION,
                                                      borderTopLeftRadius: 4,
                                                      borderBottomLeftRadius: 4,
                                                      cursor: "ew-resize",
                                                      touchAction: "none",
                                                    }}
                                                  >
                                                    <ChevronLeft
                                                      className="w-3 h-3"
                                                      strokeWidth={3}
                                                      style={{
                                                        color: "#FFFFFF",
                                                      }}
                                                    />
                                                  </div>
                                                  <div
                                                    onPointerDown={(e) =>
                                                      handleTrimPointerDown(
                                                        e,
                                                        clip,
                                                        "right",
                                                      )
                                                    }
                                                    className="absolute top-0 bottom-0 flex items-center justify-center"
                                                    style={{
                                                      right: -8,
                                                      width: 10,
                                                      backgroundColor:
                                                        COLOR_SELECCION,
                                                      borderTopRightRadius: 4,
                                                      borderBottomRightRadius: 4,
                                                      cursor: "ew-resize",
                                                      touchAction: "none",
                                                    }}
                                                  >
                                                    <ChevronRight
                                                      className="w-3 h-3"
                                                      strokeWidth={3}
                                                      style={{
                                                        color: "#FFFFFF",
                                                      }}
                                                    />
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })
                                      ) : row.trackType === "video" ? (
                                        <button
                                          onPointerDown={(e) => {
                                            e.stopPropagation();
                                            setActivePanel("ai-scene");
                                          }}
                                          className="flex items-center justify-center transition-colors"
                                          style={{
                                            marginLeft: 8,
                                            width: 32,
                                            height: 32,
                                            borderRadius: 6,
                                            backgroundColor:
                                              "rgb(var(--brand-primary-rgb) / 0.08)",
                                            color: "rgb(var(--brand-primary-rgb) / 0.5)",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "rgb(var(--brand-primary-rgb) / 0.15)";
                                            e.currentTarget.style.color =
                                              "rgb(var(--brand-primary-rgb) / 0.8)";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "rgb(var(--brand-primary-rgb) / 0.08)";
                                            e.currentTarget.style.color =
                                              "rgb(var(--brand-primary-rgb) / 0.5)";
                                          }}
                                        >
                                          <Plus className="w-4 h-4" />
                                        </button>
                                      ) : (
                                        <span
                                          className="text-[11px] select-none"
                                          style={{
                                            marginLeft: 10,
                                            color: "rgb(var(--brand-black-rgb) / 0.15)",
                                          }}
                                        >
                                          Vacío
                                        </span>
                                      )}

                                      {/* ── Transition + indicators (only on video rows) ────
                                Appear at the boundary between every adjacent
                                video pair. Subtle by default, brighter when a
                                transition is already configured, and
                                animated on hover so the affordance is
                                discoverable without being noisy. */}
                                      {row.trackType === "video" &&
                                        trackClips.length > 1 &&
                                        (() => {
                                          const list = [...trackClips].sort(
                                            (a, b) => a.startTime - b.startTime,
                                          );
                                          const indicators: React.ReactNode[] =
                                            [];
                                          for (
                                            let i = 0;
                                            i < list.length - 1;
                                            i++
                                          ) {
                                            const left = list[i];
                                            const right = list[i + 1];
                                            const leftEnd =
                                              left.startTime + left.duration;
                                            const gap =
                                              right.startTime - leftEnd;
                                            if (
                                              gap > ADJACENCY_GAP_LIMIT ||
                                              gap < -ADJACENCY_OVERLAP_LIMIT
                                            )
                                              continue;
                                            const boundarySec =
                                              (leftEnd + right.startTime) / 2;
                                            const xPx = boundarySec * pps;
                                            const hasTransition =
                                              !!right.transitionIn;
                                            indicators.push(
                                              <button
                                                key={`tr-${left.id}-${right.id}`}
                                                type="button"
                                                data-tour={
                                                  i === 0
                                                    ? "clip-transition"
                                                    : undefined
                                                }
                                                onPointerDown={(e) =>
                                                  e.stopPropagation()
                                                }
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setTransitionTargetClipId(
                                                    right.id,
                                                  );
                                                  setActivePanel("transitions");
                                                }}
                                                className="group absolute z-30 flex items-center justify-center transition-all"
                                                style={{
                                                  // Con uno de los dos clips
                                                  // seleccionado, sus manijas de
                                                  // recorte están en esta unión:
                                                  // el "+" se achica y sube al
                                                  // borde de arriba para dejarles
                                                  // el centro. Antes, al querer
                                                  // estirar un clip se agarraba el
                                                  // "+" (Imanol probando, 3/11;
                                                  // ADR-2026-11-03-el-mas-de-transicion-deja-lugar-a-las-manijas).
                                                  ...(selectedClipId === left.id ||
                                                  selectedClipId === right.id
                                                    ? {
                                                        left: xPx - 7,
                                                        top: 2,
                                                        width: 14,
                                                        height: 14,
                                                        borderRadius: 7,
                                                      }
                                                    : {
                                                        left: xPx - 9,
                                                        top: "calc(50% - 9px)",
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: 9,
                                                      }),
                                                  backgroundColor: hasTransition
                                                    ? "rgb(var(--brand-primary-rgb) / 0.85)"
                                                    : "rgb(var(--brand-black-rgb) / 0.45)",
                                                  color: "#fff",
                                                  boxShadow:
                                                    "0 1px 3px rgba(0,0,0,0.25)",
                                                  cursor: "pointer",
                                                }}
                                                onMouseEnter={(e) => {
                                                  const el = e.currentTarget;
                                                  el.style.transform =
                                                    "scale(1.2)";
                                                  el.style.backgroundColor =
                                                    hasTransition
                                                      ? "var(--brand-primary)"
                                                      : "rgb(var(--brand-black-rgb) / 0.85)";
                                                }}
                                                onMouseLeave={(e) => {
                                                  const el = e.currentTarget;
                                                  el.style.transform =
                                                    "scale(1)";
                                                  el.style.backgroundColor =
                                                    hasTransition
                                                      ? "rgb(var(--brand-primary-rgb) / 0.85)"
                                                      : "rgb(var(--brand-black-rgb) / 0.45)";
                                                }}
                                                title={
                                                  hasTransition
                                                    ? `Transición: ${right.transitionIn!.type}`
                                                    : "Agregar transición"
                                                }
                                              >
                                                <Plus
                                                  className={
                                                    selectedClipId === left.id ||
                                                    selectedClipId === right.id
                                                      ? "w-2.5 h-2.5"
                                                      : "w-3 h-3"
                                                  }
                                                  strokeWidth={3}
                                                />
                                              </button>,
                                            );
                                          }
                                          return <>{indicators}</>;
                                        })()}

                                      {/* ── Snap ghost silhouette ─────────────────────────── */}
                                      {snapTarget &&
                                        (() => {
                                          const draggedClip = clips.find(
                                            (c) => c.id === snapTarget.clipId,
                                          );
                                          if (
                                            !draggedClip ||
                                            !row.matchClip(draggedClip)
                                          )
                                            return null;
                                          const ghostLeftPx =
                                            snapTarget.startTime * pps;
                                          const ghostWidthPx = Math.max(
                                            draggedClip.duration * pps,
                                            2,
                                          );
                                          return (
                                            <div
                                              className="absolute pointer-events-none"
                                              style={{
                                                left: ghostLeftPx,
                                                width: ghostWidthPx,
                                                top: 4,
                                                bottom: 4,
                                                borderRadius: 6,
                                                border: `2px dashed ${draggedClip.color}`,
                                                backgroundColor:
                                                  draggedClip.color + "10",
                                                transition:
                                                  "left 0.1s ease-out, width 0.1s ease-out",
                                              }}
                                            />
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* ── Guía del imán (al estirar y al mover un clip) ────── */}
                              {snapLine !== null && (
                                <div
                                  className="absolute"
                                  style={{
                                    // Mismo + PAD_TIMELINE que el playhead: el
                                    // absoluto se ancla al borde del padding.
                                    left: padIzqTimeline + snapLine * pps,
                                    top: 0,
                                    bottom: 0,
                                    width: 1.5,
                                    marginLeft: -0.75,
                                    // El mismo azul de las manijas de trim: la
                                    // guía es parte del mismo gesto.
                                    backgroundColor: COLOR_SELECCION,
                                    zIndex: 29,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}

                              {/* ── Línea de reorden de capas ─────────────────────────
                              Marca el hueco donde va a quedar la capa al
                              soltar. Cruza el ancho entero de la timeline para
                              que se lea como "acá va la fila", y no como una
                              marca sobre un clip suelto. El puntito de la
                              izquierda es lo que la distingue de un borde de
                              fila a simple vista. */}
                              {insercionDePista?.clase === "dentro" && (
                                /* Soltar acá mete el clip EN esta fila. Se
                                   marca con el recuadro de la fila entera y no
                                   con una línea: son dos resultados distintos y
                                   tienen que verse distintos, o el usuario no
                                   sabe cuál de los dos le va a tocar. */
                                <div
                                  className="absolute"
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: insercionDePista.yPx,
                                    height: TIMELINE_ROW_H,
                                    borderRadius: 6,
                                    border: `2px solid ${COLOR_SELECCION}`,
                                    backgroundColor: "rgb(var(--brand-primary-rgb) / 0.1)",
                                    zIndex: 31,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}

                              {/* Dónde va a quedar la capa que se arrastra
                              del tirador. Misma línea que la del reorden de
                              clips: es el mismo resultado (una fila cambia de
                              lugar), así que se dibuja igual. */}
                              {insercionDeFila && (
                                <div
                                  className="absolute"
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: insercionDeFila.yPx,
                                    height: 2,
                                    marginTop: -1,
                                    backgroundColor: COLOR_SELECCION,
                                    zIndex: 31,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}

                              {/* Dónde va a caer lo que se arrastra DESDE
                              AFUERA (Archivos, un archivo de la computadora).
                              Mismos dibujos que el reorden de clips: el
                              recuadro es "entra en esta capa" y la línea es
                              "abre una capa acá". */}
                              {/* El segundo donde arranca: línea vertical de
                              arriba abajo, del color de la selección. */}
                              {destinoDeLoSoltado && segundoDeLoSoltado !== null && (
                                <div
                                  aria-hidden="true"
                                  className="absolute top-0 bottom-0"
                                  style={{
                                    left:
                                      padIzqTimeline + segundoDeLoSoltado * pps,
                                    width: 2,
                                    marginLeft: -1,
                                    backgroundColor: COLOR_SELECCION,
                                    zIndex: 32,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                              {destinoDeLoSoltado?.clase === "dentro" && (
                                <div
                                  className="absolute"
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: destinoDeLoSoltado.yPx,
                                    height: TIMELINE_ROW_H,
                                    borderRadius: 6,
                                    border: `2px solid ${COLOR_SELECCION}`,
                                    backgroundColor: "rgb(var(--brand-primary-rgb) / 0.1)",
                                    zIndex: 31,
                                    pointerEvents: "none",
                                  }}
                                />
                              )}
                              {destinoDeLoSoltado?.clase === "hueco" && (
                                <div
                                  className="absolute"
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: destinoDeLoSoltado.yPx,
                                    height: 2,
                                    marginTop: -1,
                                    backgroundColor: COLOR_SELECCION,
                                    zIndex: 31,
                                    pointerEvents: "none",
                                  }}
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: 2,
                                      top: "50%",
                                      width: 8,
                                      height: 8,
                                      marginTop: -4,
                                      borderRadius: "50%",
                                      backgroundColor: COLOR_SELECCION,
                                    }}
                                  />
                                </div>
                              )}

                              {insercionDePista?.clase === "hueco" && (
                                <div
                                  className="absolute"
                                  style={{
                                    left: 0,
                                    right: 0,
                                    top: insercionDePista.yPx,
                                    height: 2,
                                    marginTop: -1,
                                    backgroundColor: COLOR_SELECCION,
                                    zIndex: 31,
                                    pointerEvents: "none",
                                  }}
                                >
                                  <span
                                    style={{
                                      position: "absolute",
                                      left: 2,
                                      top: "50%",
                                      width: 8,
                                      height: 8,
                                      marginTop: -4,
                                      borderRadius: "50%",
                                      backgroundColor: COLOR_SELECCION,
                                    }}
                                  />
                                </div>
                              )}

                              {/* ── Unified playhead (ruler through all tracks) ──────── */}
                              {/* Sólo en escritorio. En el teléfono el cursor no
                              viaja con la cinta: está clavado al centro de la
                              pantalla y se dibuja FUERA del contenedor que
                              scrollea (más abajo). Dibujarlo acá lo dejaba
                              temblando — el scroll es nativo y la posición que
                              le calcula React llega un frame tarde. */}
                              {!enElTelefono && (
                              <div
                                className="absolute"
                                style={{
                                  // + PAD_TIMELINE: el playhead es `absolute`
                                  // dentro del wrapper que tiene
                                  // `paddingLeft: PAD_TIMELINE`, y el absoluto
                                  // se ancla al borde del padding, no al del
                                  // contenido. Sin sumarlo, el cursor nacía 14px
                                  // ANTES del 0s de la regla. La etiqueta de
                                  // tiempo que lo acompaña ya lo sumaba.
                                  left: padIzqTimeline + currentTime * pps,
                                  top: 0,
                                  bottom: 0,
                                  zIndex: 30,
                                  pointerEvents: "none",
                                }}
                              >
                                {/* La cabeza: un pin BLANCO con contorno, no un
                                triángulo sólido. Relleno claro para que se
                                despegue de los clips oscuros por los que pasa,
                                y punta abajo para que se lea exactamente sobre
                                qué frame está parado. */}
                                <CabezaDelCursor
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{ top: 2 }}
                                />
                                {/* La línea nace donde termina la punta y va
                                opaca: media tinta se perdía sobre los clips. */}
                                <div
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{
                                    top: 18,
                                    bottom: 0,
                                    width: 1.5,
                                    backgroundColor: "var(--brand-black)",
                                  }}
                                />
                                {/* Zona de agarre invisible (cursor ew-resize).
                                Marcada como scrub: ya se veía arrastrable, y
                                agarrarla deseleccionaba el clip. Sólo a la
                                altura de la regla (donde está la cabeza): sobre
                                los clips la línea deja pasar el clic. Bajaba
                                hasta abajo y, como tocar un clip trae el cursor
                                a ese punto, el segundo clic del doble clic (o
                                arrastrar el clip desde ahí) agarraba el cursor
                                en vez del clip
                                (ADR-2026-11-03-el-cursor-se-agarra-desde-la-regla). */}
                                <div
                                  data-timeline-scrub="true"
                                  className="absolute left-1/2 -translate-x-1/2"
                                  style={{
                                    top: 0,
                                    height: TIMELINE_RULER_H,
                                    width: 12,
                                    cursor: "ew-resize",
                                    pointerEvents: "auto",
                                  }}
                                />
                              </div>
                              )}
                            </div>
                          </div>
                          {/* ── El cursor del teléfono ───────────────────────
                          Una línea quieta en el medio del viewport de la
                          timeline. No se arrastra ni se puede mover: lo que se
                          mueve es la cinta, y el tiempo es lo que quede abajo.
                          Por eso `pointerEvents: none` — todo toque pasa de
                          largo hacia los clips y hacia el scroll. */}
                          {enElTelefono && (
                            <div
                              className="absolute top-0 bottom-0 pointer-events-none"
                              style={{ left: "50%", zIndex: 30 }}
                            >
                              <div
                                className="absolute left-1/2 -translate-x-1/2 rounded-full"
                                style={{
                                  top: 0,
                                  bottom: 0,
                                  width: 2,
                                  backgroundColor: "var(--brand-black)",
                                }}
                              />
                              {/* La cabeza, del mismo lado que en escritorio:
                              punta abajo, para leer sobre qué frame se paró. */}
                              <CabezaDelCursor
                                className="absolute left-1/2 -translate-x-1/2"
                                style={{ top: -1 }}
                              />
                            </div>
                          )}

                          {/* Playhead time label — only visible while dragging the playhead, rendered OUTSIDE the scroll container to avoid clipping */}
                          {isDraggingPlayhead &&
                            (() => {
                              const playheadX =
                                currentTime * pps +
                                padIzqTimeline -
                                timelineScrollLeft;
                              if (
                                playheadX < -40 ||
                                (timelineViewportWidth > 0 &&
                                  playheadX > timelineViewportWidth + 40)
                              )
                                return null;
                              return (
                                <div
                                  className="absolute pointer-events-none z-20"
                                  style={{ left: playheadX, top: -22 }}
                                >
                                  <div className="px-2 py-0.5 rounded text-[11px] font-semibold tabular-nums whitespace-nowrap -translate-x-1/2 bg-brand-black text-white shadow-md">
                                    {formatTime(currentTime)}
                                  </div>
                                </div>
                              );
                            })()}
                        </div>
                      </div>
                    );
                  })()}

                </div>
              </div>
            </div>
          </div>

          {/* ── Frame-from-video picker: blur overlay + clickable clip hitboxes + frame choice dropdown ── */}
          {frameFromVideoTarget && (
            <>
              <div
                className="fixed inset-0 z-[50] bg-black/15 backdrop-blur-[2px]"
                onClick={() => {
                  setFrameFromVideoTarget(null);
                  setFrameFromVideoClipMenu(null);
                }}
              >
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white border border-neutral-200 rounded-full shadow-lg px-4 py-2 flex items-center gap-2">
                  <Film className="w-4 h-4 text-brand-primary" />
                  <span className="text-[13px] font-medium text-brand-black/80">
                    Elegí un video de la timeline
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFrameFromVideoTarget(null);
                      setFrameFromVideoClipMenu(null);
                    }}
                    className="ml-1 w-6 h-6 inline-flex items-center justify-center rounded-full hover:bg-black/[0.06] transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5 text-neutral-500 rotate-45" />
                  </button>
                </div>
              </div>
              {/* Clickable hitboxes over each media clip (above the blur overlay) — duplicate the clip visual so frames stay sharp */}
              {pickerClipRects.map((r) => {
                const clip = clips.find((c) => c.id === r.clipId);
                const thumbs = clipThumbnails[r.clipId];
                const isAi = clip?.type === "ai-generated";
                return (
                  <button
                    key={`picker-hit-${r.clipId}`}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFrameFromVideoClipMenu({
                        clipId: r.clipId,
                        top: r.top - 6,
                        left: r.left + r.width / 2,
                      });
                    }}
                    className="fixed z-[65] rounded-md ring-2 ring-brand-primary ring-offset-2 ring-offset-black/30 overflow-hidden cursor-pointer group"
                    style={{
                      top: r.top,
                      left: r.left,
                      width: r.width,
                      height: r.height,
                      backgroundColor: clip?.src
                        ? "#0a0a0a"
                        : clip?.color
                          ? clip.color + "20"
                          : "#0a0a0a",
                    }}
                  >
                    {thumbs && thumbs.length > 0 ? (
                      <div className="flex items-stretch w-full h-full">
                        {thumbs.map((t, i) => (
                          <div
                            key={i}
                            className="h-full shrink-0"
                            style={{
                              flex: "1 1 0",
                              backgroundImage: `url(${t})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        ))}
                      </div>
                    ) : isAi ? (
                      <div className="flex items-center w-full h-full px-1 gap-1 min-w-0">
                        {clip?.aiFirstFrame && (
                          <img
                            src={clip.aiFirstFrame}
                            alt=""
                            className="h-full w-auto rounded-sm object-cover shrink-0"
                            style={{ maxWidth: 28 }}
                          />
                        )}
                        <span
                          className="text-[11px] font-medium truncate select-none flex-1 min-w-0 px-0.5"
                          style={{ color: clip?.color }}
                        >
                          {clip?.aiPrompt || clip?.name}
                        </span>
                        {clip?.aiLastFrame && (
                          <img
                            src={clip.aiLastFrame}
                            alt=""
                            className="h-full w-auto rounded-sm object-cover shrink-0"
                            style={{ maxWidth: 28 }}
                          />
                        )}
                      </div>
                    ) : null}
                    <div className="absolute inset-0 bg-brand-primary/0 group-hover:bg-brand-primary/10 transition-colors" />
                  </button>
                );
              })}
              {frameFromVideoClipMenu && (
                <div
                  className="fixed z-[70] bg-white border border-neutral-200 rounded-[10px] shadow-lg overflow-hidden min-w-[200px] -translate-x-1/2 -translate-y-full"
                  style={{
                    top: frameFromVideoClipMenu.top,
                    left: frameFromVideoClipMenu.left,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() =>
                      pickFrameFromClip(frameFromVideoClipMenu.clipId, "first")
                    }
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 text-brand-black/50" />
                    Utilizar primer frame
                  </button>
                  <div className="h-px bg-black/[0.06]" />
                  <button
                    type="button"
                    onClick={() =>
                      pickFrameFromClip(frameFromVideoClipMenu.clipId, "last")
                    }
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-[13px] text-brand-black/80 hover:bg-neutral-50 text-left"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-brand-black/50" />
                    Utilizar último frame
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Export format selection modal (top-level so no transform
          ancestor clips it) ────────────────────────────────────────────── */}
          {exportMenuOpen &&
            (() => {
              // Three sources of "is this dialog busy" we have to combine:
              //   - `exporting`           → packaging the Premiere XML zip
              //   - exportState=rendering → an MP4 render is in flight
              // Both should block close + format-picker rendering.
              const xmlPackaging = !!exporting;
              const mp4Rendering = exportState.phase === "rendering";
              const isBusy = xmlPackaging || mp4Rendering;
              const closeDialog = () => {
                if (isBusy) return;
                setExportMenuOpen(false);
                setExportError(null);
              };
              const headerTitle = mp4Rendering
                ? "Exportando MP4"
                : exportState.phase === "done"
                  ? "Exportación lista"
                  : exportState.phase === "error"
                    ? "No se pudo exportar"
                    : xmlPackaging
                      ? "Empaquetando proyecto…"
                      : "Exportar proyecto";
              const headerSubtitle = mp4Rendering
                ? "Esto puede tardar unos minutos. No cierres esta ventana."
                : exportState.phase === "done"
                  ? "Tu video está listo para descargar."
                  : exportState.phase === "error"
                    ? "Revisá el detalle y volvé a intentar."
                    : xmlPackaging
                      ? `Descargando archivos (${exporting!.loaded}/${exporting!.total})`
                      : "Elegí el formato que mejor te sirva";
              return (
                <div
                  className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
                  onClick={closeDialog}
                >
                  <div
                    className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-md w-full p-5 animate-in zoom-in-95 fade-in duration-150"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-[16px] font-semibold text-neutral-900">
                          {headerTitle}
                        </h3>
                        <p className="text-[13px] text-neutral-500 mt-0.5">
                          {headerSubtitle}
                        </p>
                      </div>
                      <button
                        onClick={closeDialog}
                        disabled={isBusy}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Cerrar"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {/* XML packaging progress (Premiere XML zip download). Rendered
                inline because that flow re-uses the format-picker layout
                when finished — the bar here is just an upgrade. */}
                    {xmlPackaging && (
                      <div className="mb-4">
                        <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary transition-[width] duration-200"
                            style={{
                              width: `${exporting!.total === 0 ? 0 : Math.round((exporting!.loaded / exporting!.total) * 100)}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                    {exportError &&
                      !xmlPackaging &&
                      exportState.phase === "idle" && (
                        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{exportError}</span>
                        </div>
                      )}

                    {/* MP4 render — rendering phase: progress bar + cancel. */}
                    {mp4Rendering && exportState.phase === "rendering" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-4 h-4 text-brand-primary animate-spin shrink-0" />
                          <span className="text-[13.5px] text-neutral-700 truncate">
                            {renderMessageFor(
                              exportState.progress,
                              renderMessageTick,
                            )}
                          </span>
                          <span className="ml-auto text-[12.5px] font-semibold tabular-nums text-neutral-600">
                            {Math.round(exportState.progress * 100)}%
                          </span>
                        </div>
                        <div className="h-2 w-full bg-neutral-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-brand-primary transition-[width] duration-300 ease-out"
                            style={{
                              width: `${Math.round(exportState.progress * 100)}%`,
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            // Único lugar donde la persona PIDIÓ cancelar: acá
                            // sí se aborta el render en el servidor y se
                            // libera la cuota. Cerrar el panel (el efecto de
                            // `exportMenuOpen`) sólo deja de mirar.
                            exportState.handle.cancel({
                              abortarEnServidor: true,
                            });
                            setExportMenuOpen(false);
                          }}
                          className="self-end text-[13px] font-medium text-neutral-500 hover:text-neutral-700 px-3 py-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    )}

                    {/* MP4 render — done phase: download link. */}
                    {exportState.phase === "done" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-3 p-3.5 rounded-lg bg-brand-primary/5 border border-brand-primary/20">
                          <Check className="w-4 h-4 text-brand-primary shrink-0 mt-0.5" />
                          <div className="text-[13px] text-neutral-700 leading-relaxed">
                            Tu MP4 está listo. Podés descargarlo ahora o volver
                            a la edición.
                          </div>
                        </div>
                        {/* Lo que NO entró al video. El serializer siempre
                        devolvió esta lista; hasta ahora nadie la leía, así que
                        un clip descartado —un texto vacío, un elemento sin
                        plantilla, un archivo que no se pudo abrir— se
                        descubría mirando el MP4 terminado. */}
                        {exportState.avisos && exportState.avisos.length > 0 && (
                          <div className="flex items-start gap-3 p-3.5 rounded-lg bg-amber-50 border border-amber-200">
                            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-[13px] text-amber-900 leading-relaxed space-y-1">
                              {exportState.avisos.map((a, i) => (
                                <p key={i}>{a}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setExportState({ phase: "idle" })}
                            className="text-[13px] font-medium text-neutral-600 hover:text-neutral-800 px-3.5 py-2 rounded-md hover:bg-neutral-100 transition-colors"
                          >
                            Exportar otro formato
                          </button>
                          <a
                            href={exportState.outputUrl}
                            download
                            className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover px-4 py-2 rounded-md transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Descargar MP4
                          </a>
                        </div>
                      </div>
                    )}

                    {/* MP4 render — error phase: message + retry. */}
                    {exportState.phase === "error" && (
                      <div className="flex flex-col gap-4">
                        <div className="flex items-start gap-3 p-3.5 rounded-lg bg-red-50 border border-red-200">
                          <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                          <div className="text-[13px] text-red-800 leading-relaxed break-words">
                            {exportState.message}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExportState({ phase: "idle" })}
                          className="self-end text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover px-4 py-2 rounded-md transition-colors"
                        >
                          Volver
                        </button>
                      </div>
                    )}

                    {/* Idle phase: show both format buttons. Hidden during MP4 render
                or while we're packaging the Premiere XML zip — those flows
                replace the layout entirely above. */}
                    {!mp4Rendering && exportState.phase === "idle" && (
                      <div className="flex flex-col gap-2.5">
                        {/* Standard / MP4 — wired to Remotion render. */}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={handleExportMp4}
                          className="group text-left rounded-lg border border-neutral-200 p-4 bg-white hover:bg-neutral-50 hover:border-brand-primary/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Video className="w-4 h-4 text-brand-primary shrink-0" />
                            <span className="text-[14px] font-semibold text-neutral-900">
                              Formato estándar
                            </span>
                            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                              .mp4
                            </span>
                          </div>
                          <p className="text-[12.5px] leading-relaxed text-neutral-600">
                            Exporta el video en formato MP4, perfecto para
                            compartir o subir a las redes.
                          </p>
                        </button>
                        {/* Professional / Premiere XML — enabled */}
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={async () => {
                            setExportError(null);
                            // Map runtime clips down to the export-only shape so the
                            // util doesn't see anything it can't serialize.
                            const exportClips: PremiereXmlExportClip[] =
                              clips.map((c) => ({
                                id: c.id,
                                type: c.type,
                                name: c.name,
                                startTime: c.startTime,
                                duration: c.duration,
                                src: c.src,
                                mediaOffset: c.mediaOffset,
                                sourceDuration: c.sourceDuration,
                                trackIndex: c.trackIndex,
                                text: c.text,
                                fontFamily: c.fontFamily,
                                fontSizePct: c.fontSizePct,
                                fontWeight: c.fontWeight,
                                fontStyle: c.fontStyle,
                                textColor: c.textColor,
                                textAlign: c.textAlign,
                                aiStatus: c.aiStatus,
                              }));
                            setExporting({ loaded: 0, total: 0 });
                            try {
                              const { filename, blob } =
                                await exportProjectAsPremiereXmlZip({
                                  clips: exportClips,
                                  aspectRatio:
                                    aspectRatio as PremiereXmlAspectRatio,
                                  projectName: projectName || "Atlas Project",
                                  onProgress: (loaded, total) =>
                                    setExporting({ loaded, total }),
                                });
                              downloadBlob(filename, blob);
                              setExporting(null);
                              setExportMenuOpen(false);
                            } catch (err) {
                              setExporting(null);
                              setExportError(
                                err instanceof Error
                                  ? err.message
                                  : "No pudimos exportar el proyecto.",
                              );
                            }
                          }}
                          className="group text-left rounded-lg border border-neutral-200 p-4 bg-white hover:bg-neutral-50 hover:border-brand-primary/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            {xmlPackaging ? (
                              <Loader2 className="w-4 h-4 text-brand-primary shrink-0 animate-spin" />
                            ) : (
                              <Film className="w-4 h-4 text-brand-primary shrink-0" />
                            )}
                            <span className="text-[14px] font-semibold text-neutral-900">
                              Formato profesional
                            </span>
                            <span className="text-[10px] font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary">
                              .xml
                            </span>
                          </div>
                          <p className="text-[12.5px] leading-relaxed text-neutral-600">
                            Pensado para abrir el proyecto en Premiere Pro y
                            hacer retoques profesionales. En Premiere:{" "}
                            <span className="font-medium">File → Import</span> y
                            elegí el archivo{" "}
                            <span className="font-medium">.xml</span> de la
                            carpeta descargada.
                          </p>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          {/* ── Subtítulos (transcripción + confirmación) ─────────────────────
          Top-level so it isn't clipped by any transformed ancestor. The
          backdrop click closes only when not loading — avoids yanking
          the modal while the webhook is still in flight. */}
          {subtitlesModalOpen && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-150"
              onClick={() => {
                if (!subtitlesLoading) {
                  setSubtitlesModalOpen(false);
                  setSubtitlesResult(null);
                  setSubtitlesError(null);
                }
              }}
            >
              <div
                className="bg-surface-card rounded-lg shadow-xl border border-neutral-200 max-w-2xl w-full max-h-[85vh] flex flex-col animate-in zoom-in-95 fade-in duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between p-5 pb-3">
                  <div>
                    <h3 className="text-[16px] font-semibold text-neutral-900 flex items-center gap-2">
                      <Captions className="w-4 h-4 text-brand-primary" />
                      Generar subtítulos
                    </h3>
                    <p className="text-[13px] text-neutral-500 mt-0.5">
                      {subtitlesLoading
                        ? "Transcribiendo el audio del clip…"
                        : subtitlesError
                          ? "Ocurrió un problema."
                          : subtitlesResult
                            ? `Se detectaron ${subtitlesResult.segments.length} líneas de subtítulos.`
                            : "Listo para generar."}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (!subtitlesLoading) {
                        setSubtitlesModalOpen(false);
                        setSubtitlesResult(null);
                        setSubtitlesError(null);
                      }
                    }}
                    disabled={subtitlesLoading}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-3">
                  {subtitlesLoading && (
                    <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
                      <Loader2 className="w-6 h-6 animate-spin text-brand-primary mb-3" />
                      <p className="text-[13px]">
                        Esto puede tomar hasta un minuto según la duración del
                        clip.
                      </p>
                    </div>
                  )}

                  {!subtitlesLoading && subtitlesError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-[13px] text-red-800 flex items-start gap-2.5">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium mb-1">
                          No pudimos generar los subtítulos
                        </p>
                        <p className="text-red-700 leading-relaxed">
                          {subtitlesError}
                        </p>
                        {subtitlesSourceClipId && (
                          <button
                            type="button"
                            onClick={() =>
                              openSubtitlesModalForClip(subtitlesSourceClipId)
                            }
                            className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-red-800 hover:text-red-900 underline underline-offset-2"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> Reintentar
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!subtitlesLoading && !subtitlesError && subtitlesResult && (
                    <div className="space-y-4">
                      {/* Full transcript preview — collapsible-feeling box */}
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 mb-1.5">
                          Transcripción
                        </div>
                        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[13px] leading-relaxed text-neutral-800 max-h-28 overflow-y-auto">
                          {subtitlesResult.transcript.text || "—"}
                        </div>
                      </div>

                      {/* Segment list — each row shows timing + text, so the
                      user can eyeball chunk quality before committing. */}
                      <div>
                        <div className="flex items-baseline justify-between mb-1.5">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                            Subtítulos ({subtitlesResult.segments.length})
                          </div>
                          {typeof subtitlesResult.transcript
                            .audio_duration_secs === "number" && (
                            <div className="text-[11px] text-neutral-400 tabular-nums">
                              {subtitlesResult.transcript.audio_duration_secs.toFixed(
                                1,
                              )}
                              s totales
                            </div>
                          )}
                        </div>
                        <div className="rounded-md border border-neutral-200 divide-y divide-neutral-100 bg-white max-h-72 overflow-y-auto">
                          {subtitlesResult.segments.length === 0 ? (
                            <p className="text-[12.5px] text-neutral-500 p-3">
                              No se pudieron generar segmentos a partir de esta
                              transcripción.
                            </p>
                          ) : (
                            subtitlesResult.segments.map((seg, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-3 px-3 py-2"
                              >
                                <span className="shrink-0 text-[11px] font-medium tabular-nums text-neutral-400 w-20 pt-0.5">
                                  {seg.start.toFixed(2)}s → {seg.end.toFixed(2)}
                                  s
                                </span>
                                <span className="flex-1 text-[13px] text-neutral-800 leading-snug">
                                  {seg.text}
                                </span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 pt-3 border-t border-neutral-100">
                  <button
                    type="button"
                    onClick={() => {
                      if (!subtitlesLoading) {
                        setSubtitlesModalOpen(false);
                        setSubtitlesResult(null);
                        setSubtitlesError(null);
                      }
                    }}
                    disabled={subtitlesLoading}
                    className="h-9 px-3 rounded-md text-[13px] font-medium text-neutral-700 hover:bg-neutral-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={applySubtitlesToTimeline}
                    disabled={
                      subtitlesLoading ||
                      !subtitlesResult ||
                      subtitlesResult.segments.length === 0
                    }
                    className="h-9 px-3.5 rounded-md text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar al timeline
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Insufficient-credits modal ────────────────────────────────────
          Shared with the rest of the app (image gen, etc) so the UX of
          "you ran out of credits" stays consistent. Triggered by the
          assertCredits() guard before any AI generation kicks off. */}
          <SubscriptionLimitModal
            isOpen={showSubscriptionLimitModal}
            type={subscriptionLimitType}
            onClose={() => setShowSubscriptionLimitModal(false)}
          />

          {/* ── Modal de error de subida ───────────────────────────────────
          Avisa cuando el archivo elegido supera el límite de tamaño (hoy
          100MB del Global file size limit de Supabase Storage). Bloquea la
          subida antes de intentarla, así no falla con un 502 sin feedback.
          Desde ADR-2026-08-11-los-rechazos-de-subida-se-muestran también
          muestra los rechazos por FORMATO (RAW/TIFF/GIF/SVG) y las fallas de
          conversión, que antes morían en un `console.error`. */}
          {uploadErrorMsg && (
            <div
              // Este aviso también sale de trabajar sobre un clip (el pincel
              // avisa acá cuando el frame todavía no está listo): sin la marca,
              // tocarlo suelta la selección y cierra la hoja de atrás.
              data-clip-ui="true"
              className={claseVeloDeDialogo(enElTelefono)}
            >
              <div
                className={claseTarjetaDeDialogo(enElTelefono)}
                style={
                  enElTelefono
                    ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
                    : undefined
                }
              >
                {enElTelefono && <ManijaDeHoja />}
                <div className="p-5">
                  <h3 className="text-[17px] font-semibold text-brand-black/90 mb-2">
                    {tituloDelAviso ?? "No se pudo agregar el archivo"}
                  </h3>
                  {/* `whitespace-pre-line`: la subida del agente es múltiple y
                  junta los rechazos de la tanda separados por línea en blanco;
                  sin esto quedaban todos pegados en un párrafo. */}
                  <p className="text-[15px] text-brand-black/90 mb-5 whitespace-pre-line">
                    {uploadErrorMsg}
                  </p>
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setUploadErrorMsg(null);
                        setTituloDelAviso(null);
                      }}
                      className="inline-flex items-center justify-center h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
                    >
                      Entendido
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Modal grande del pincel (ver el frame grande, diseño Atlas) ──── */}
          {/* ── El menú del "+" de una pista ─────────────────────────────
          Las cuatro formas de sumar material. Se dibuja acá, al final del
          árbol y en coordenadas de PANTALLA (`fixed`), porque el botón que lo
          abre vive adentro de una pista con `overflow-hidden`: colgado de ahí
          se cortaría contra el borde del carril.

          Se para a la DERECHA del "+" y centrado en él. Si a la derecha no
          entra —el "+" del teléfono está pegado al borde de la pantalla— cae a
          la izquierda, que es el único lado con lugar. El `top` se centra en el
          botón y se recorta contra la ventana, para que el "+" de la última
          pista no abra un menú que se va abajo de la pantalla.
          Ver ADR-2026-09-18-el-mas-abre-un-menu-con-cuatro-formas-de-sumar. */}
          {menuDelMas &&
            (() => {
              const OPCIONES: Array<{
                label: string;
                icono: React.ReactNode;
                panel: ToolPanel;
              }> = [
                {
                  label: "Video con IA",
                  icono: <Film className="w-4 h-4" />,
                  panel: "ai-scene",
                },
                {
                  label: "Audio con IA",
                  icono: <Music className="w-4 h-4" />,
                  panel: "ai-audio",
                },
                {
                  label: "Texto",
                  icono: <Type className="w-4 h-4" />,
                  panel: "text",
                },
                {
                  label: "Animación gráfica",
                  icono: <Sparkles className="w-4 h-4" />,
                  panel: "animacion-grafica",
                },
                {
                  label: "Archivo",
                  icono: <Folder className="w-4 h-4" />,
                  panel: "files",
                },
              ];
              // 48 por opción (icono de 28 + 10 arriba y abajo) más los 8
              // del `py-1` del contenedor. Estaba en 40 y el cálculo del
              // recorte quedaba 32px corto: el "+" de la última pista abría un
              // menú que se cortaba contra el borde de abajo y la cuarta
              // opción no se podía tocar.
              const ALTO_DE_OPCION = 48;
              const ALTO = OPCIONES.length * ALTO_DE_OPCION + 8;
              const ANCHO = 184;
              // Crece de abajo hacia arriba desde el borde inferior del
              // "+": así nunca se mete sobre lo que hay DEBAJO —la tap bar del
              // teléfono, que antes quedaba tapada— y el menú se lee como que
              // sale del botón. Si para arriba no hay lugar (pantalla muy
              // baja), se ancla arriba y el `maxHeight` lo hace scrollear.
              const cabeHaciaArriba = menuDelMas.abajo - ALTO >= 8;
              const posicionVertical: React.CSSProperties = cabeHaciaArriba
                ? { bottom: window.innerHeight - menuDelMas.abajo }
                : { top: 8 };
              // El aire entre el "+" y el menú. 16 y no 8: pegado al botón
              // el menú se leía como parte de la pista de atrás.
              const SEPARACION = 16;
              const aLaDerecha = menuDelMas.der + SEPARACION;
              const entraALaDerecha =
                aLaDerecha + ANCHO + 8 <= window.innerWidth;
              const left = entraALaDerecha
                ? aLaDerecha
                : Math.max(8, menuDelMas.izq - ANCHO - SEPARACION);
              return (
                <>
                  <div
                    className="fixed inset-0 z-[80]"
                    onPointerDown={() => setMenuDelMas(null)}
                    aria-hidden="true"
                  />
                  <div
                    role="menu"
                    className="fixed z-[81] origin-bottom overflow-y-auto overscroll-contain rounded-[12px] border border-black/[0.06] bg-white py-1 shadow-[0_8px_28px_rgb(var(--brand-black-rgb)_/_0.18)] animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-150"
                    /* `maxHeight` + scroll como red de seguridad: si algún día
                       el menú suma opciones y no entra en la pantalla, se
                       scrollea en vez de cortarse. `dvh` porque en el teléfono
                       la barra del navegador aparece y desaparece. */
                    style={{
                      ...posicionVertical,
                      left,
                      width: ANCHO,
                      maxHeight: "calc(100dvh - 16px)",
                    }}
                  >
                    {OPCIONES.map((op) => (
                      <button
                        key={op.label}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          // Lo que entre por este panel sigue a la pista del
                          // "+" (ADR-2026-09-27-lo-que-entra-por-el-mas-sigue-a-su-pista).
                          origenDelMasRef.current = {
                            ...menuDelMas.pista,
                            startTime: menuDelMas.pista.fin,
                            panel: op.panel,
                          };
                          setMenuDelMas(null);
                          setActivePanel(op.panel);
                        }}
                        className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[14px] text-brand-black/85 transition-colors hover:bg-black/[0.03] active:bg-black/[0.06]"
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary">
                          {op.icono}
                        </span>
                        {op.label}
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}

          {eraseBrushFrame && eraseBrushClipId && companyId && (
            <EraseBrushPanel
              frameUrl={eraseBrushFrame.url}
              frameWidth={eraseBrushFrame.w}
              frameHeight={eraseBrushFrame.h}
              onRequestMask={requestBrushMask}
              /* Volver y cerrar hacen lo mismo —los dos devuelven el panel del
                 que se vino—, pero se dicen distinto: la flecha es "atrás" para
                 quien entró desde Herramientas IA, y la X es "salir de la
                 herramienta". */
              onBack={volverDelPincel}
              onClose={volverDelPincel}
              onConfirm={(marks) =>
                setEraseConfirm({ clipId: eraseBrushClipId, marks })
              }
            />
          )}

          {/* La edición de video con IA ahora vive como PANEL de la sidebar
          (activePanel === "edit-video-ia", arriba en el body), NO como modal. */}

          {/* ── Modal de confirmación del pincel (tiempo + créditos) ───────────
          Mismo patrón visual que la confirmación de traducción: card de
          Atlas, CreditBadge en el botón primario, sin emojis. */}
          {eraseConfirm &&
            (() => {
              const clip = clipsRef.current.find(
                (c) => c.id === eraseConfirm.clipId,
              );
              const cost = clip ? creditsForBrushErase(clip.duration) : 0;
              const cancel = () => setEraseConfirm(null);
              const confirm = () => {
                const ec = eraseConfirm;
                // Capturar el size del frame ANTES de cerrar el panel.
                const fr = eraseBrushFrame
                  ? { w: eraseBrushFrame.w, h: eraseBrushFrame.h }
                  : { w: 0, h: 0 };
                setEraseConfirm(null);
                setEraseBrushFrame(null);
                setEraseBrushClipId(null);
                if (clip && assertCredits(cost))
                  void runBrushErase(ec.clipId, ec.marks, fr);
              };
              return (
                <div
                  // Igual que el pincel: sin `data-clip-ui`, tocar esta
                  // confirmación suelta el clip seleccionado y al volver el
                  // panel del clip aparece vacío.
                  data-clip-ui="true"
                  className={claseVeloDeDialogo(enElTelefono)}
                  onClick={cancel}
                >
                  <div
                    className={claseTarjetaDeDialogo(enElTelefono)}
                    style={
                      enElTelefono
                        ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
                        : undefined
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    {enElTelefono && <ManijaDeHoja />}
                    <div className="p-5">
                      <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2">
                        Borrar objeto del clip
                      </h3>
                      <p className="text-[15px] text-brand-black/90 mb-3 leading-relaxed">
                        Vamos a borrar el objeto que marcaste y reconstruir el
                        fondo en todo el clip, manteniendo el resto intacto.
                      </p>
                      <div className="flex items-start gap-1.5 mb-6">
                        <Clock className="w-3.5 h-3.5 text-neutral-500 mt-0.5 shrink-0" />
                        <p className="text-[13px] text-neutral-500 leading-relaxed">
                          El borrado tarda unos minutos. Mientras tanto podés
                          seguir editando otros clips.
                        </p>
                      </div>
                      {/* `flex-col-reverse` en el teléfono: apilados, el
                      botón que confirma va ARRIBA —es lo que el usuario vino a
                      hacer y es donde cae el pulgar— y Cancelar abajo. El DOM
                      mantiene el orden Cancelar → confirmar, que es el que
                      corresponde en escritorio (secundario a la izquierda,
                      primario a la derecha) y el que leen el teclado y el
                      lector de pantalla. */}
                      <div className="flex flex-col-reverse sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                        <button
                          onClick={cancel}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirm}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors gap-2"
                        >
                          Borrar objeto
                          <CreditBadge credits={cost} tone="on-primary" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* ── Sheet "Agregar" (teléfono) ───────────────────────────────────
          Lo que se puede CREAR desde cero, en tres tarjetas repartidas a lo
          ancho: Video con IA, Audio con IA y Texto. Sube desde abajo al tocar
          el "+" de la barra, igual que el sheet de Generar de la home, y con su
          mismo cromo: scrim al 40%, hoja blanca con esquinas de 24px, manija
          arriba y alto por contenido.

          Tres columnas y no una lista: son tres, entran, y así la elección se
          ve entera de un vistazo sin scrollear. Cada tarjeta abre su panel y
          cierra el sheet — no hay paso intermedio como en la home, porque acá
          no hay familias que elegir.

          Va por `createPortal` al `body`: la barra y el editor viven adentro de
          contenedores con `overflow-hidden` y `transform`, que recortarían un
          `fixed`. */}
          {enElTelefono &&
            sheetAgregar &&
            createPortal(
              <>
                <div
                  aria-hidden="true"
                  onClick={cerrarSheetAgregar}
                  className={`fixed inset-0 z-[69] bg-black/40 transition-opacity duration-300 ${
                    sheetAgregarAnimado ? "opacity-100" : "opacity-0"
                  }`}
                />
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-label="Agregar al proyecto"
                  className={`fixed inset-x-0 bottom-0 z-[70] rounded-t-[24px] bg-white shadow-[0_-8px_40px_rgb(var(--brand-black-rgb)_/_0.18)] transition-transform duration-300 ease-out ${
                    sheetAgregarAnimado ? "translate-y-0" : "translate-y-full"
                  }`}
                  style={{
                    paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)",
                  }}
                >
                  <div
                    aria-hidden="true"
                    className="flex justify-center pb-1 pt-2.5"
                  >
                    <span className="h-1 w-9 rounded-full bg-black/15" />
                  </div>
                  <div className="px-5 pt-3">
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <h2 className="text-[20px] font-bold leading-[26px] text-[#18181B]">
                        Agregar
                      </h2>
                      <button
                        type="button"
                        onClick={cerrarSheetAgregar}
                        aria-label="Cerrar"
                        className="-mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-brand-black/70 active:opacity-60"
                      >
                        <Plus className="w-5 h-5 rotate-45" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        {
                          clave: "ai-video",
                          label: "Video con IA",
                          Icono: Film,
                          panel: "ai-scene" as ToolPanel,
                        },
                        {
                          clave: "ai-audio",
                          label: "Audio con IA",
                          Icono: Music,
                          panel: "ai-audio" as ToolPanel,
                        },
                        {
                          clave: "texto",
                          label: "Texto",
                          Icono: Type,
                          panel: "text" as ToolPanel,
                        },
                      ].map((op) => (
                        <button
                          key={op.clave}
                          type="button"
                          onClick={() => {
                            // Un clip abierto en el panel no tiene nada que ver
                            // con crear uno nuevo: si no, "Video con IA" abría
                            // el generador con la escena que se venía editando.
                            setEditingClipId(null);
                            setActivePanel(op.panel);
                            cerrarSheetAgregar();
                          }}
                          className="flex flex-col items-center justify-center gap-2.5 rounded-[20px] ring-1 ring-black/[0.06] bg-white py-5 px-2 active:bg-black/[0.03] transition-colors"
                        >
                          <span className="grid h-12 w-12 place-items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.1)] text-brand-primary">
                            <op.Icono className="w-[22px] h-[22px]" />
                          </span>
                          <span className="text-[12.5px] font-medium leading-tight text-center text-brand-black/80">
                            {op.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>,
              document.body,
            )}

          {/* ── Modal de confirmación de traducción ──────────────────────────
          Aparece cuando el usuario clickea "Traducir EN" del toolbar del
          clip. Le muestra cuánto le va a costar antes de gastar los
          créditos, para evitar sorpresas. No tiene "no me preguntes más" —
          decisión de producto: aparece SIEMPRE porque el costo varía con
          la duración del clip y la traducción tarda varios minutos. */}
          {pasarA1080pClipId &&
            (() => {
              const clip = clips.find((c) => c.id === pasarA1080pClipId);
              if (!clip?.aiBorrador) return null;
              const cerrar = () => {
                setPasarA1080pClipId(null);
                setSelectedClipId(clip.id);
                setActivePanel((p) => (p === "none" ? "clip" : p));
              };
              return (
                <PasarA1080pDialogo
                  abierto
                  creditos={creditosDePasarA1080p(clip.aiBorrador.segundos)}
                  segundos={clip.aiBorrador.segundos}
                  procesando={false}
                  onCerrar={cerrar}
                  onConfirmar={() => {
                    // El popup se cierra ya; el clip muestra el shimmer mientras corre.
                    cerrar();
                    void pasarClipA1080p(clip.id);
                  }}
                />
              );
            })()}
          {translateConfirmClipId &&
            (() => {
              const clip = clips.find((c) => c.id === translateConfirmClipId);
              if (!clip) return null;
              const cost = creditsForVideoTranslate({
                durationSec: clip.duration,
              });
              const durSec = Math.round(clip.duration);
              // Cerrar la confirmación —cancelando o confirmando— devuelve la
              // hoja de la que se vino: el panel del clip, que es donde está
              // "Traducir al inglés". Se reelige el clip y, si la hoja se había
              // cerrado, se vuelve a abrir. Sin esto el usuario cancelaba y
              // quedaba mirando la timeline, sin la lista de herramientas
              // desde la que había entrado.
              const volverAlPanelDelClip = () => {
                if (translateConfirmClipId)
                  setSelectedClipId(translateConfirmClipId);
                setActivePanel((p) => (p === "none" ? "clip" : p));
              };
              const cancel = () => {
                setTranslateConfirmClipId(null);
                volverAlPanelDelClip();
              };
              const confirm = () => {
                const id = translateConfirmClipId;
                volverAlPanelDelClip();
                setTranslateConfirmClipId(null);
                if (id) void translateClipToEnglish(id);
              };
              return (
                <div
                  // Igual que el pincel y su confirmación: sin `data-clip-ui`,
                  // el primer toque acá adentro suelta el clip seleccionado —y
                  // en el teléfono eso CIERRA la hoja del clip—, así que al
                  // cancelar no quedaba ninguna a la que volver.
                  data-clip-ui="true"
                  className={claseVeloDeDialogo(enElTelefono)}
                  onClick={cancel}
                >
                  <div
                    className={claseTarjetaDeDialogo(enElTelefono)}
                    style={
                      enElTelefono
                        ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
                        : undefined
                    }
                    onClick={(e) => e.stopPropagation()}
                  >
                    {enElTelefono && <ManijaDeHoja />}
                    <div className="p-5">
                      <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2">
                        Traducir clip al inglés
                      </h3>
                      <p className="text-[15px] text-brand-black/90 mb-3 leading-relaxed">
                        Vamos a doblar este clip de{" "}
                        <strong>{durSec}s</strong> al inglés, manteniendo la
                        voz del speaker original y sincronizando los labios.
                      </p>
                      <p className="text-[13px] text-neutral-500 mb-6 leading-relaxed">
                        La traducción tarda entre 5 y 15 minutos. Mientras
                        tanto podés seguir editando otros clips.
                      </p>
                      {/* `flex-col-reverse` en el teléfono: apilados, el
                      botón que confirma va ARRIBA —es lo que el usuario vino a
                      hacer y es donde cae el pulgar— y Cancelar abajo. El DOM
                      mantiene el orden Cancelar → confirmar, que es el que
                      corresponde en escritorio (secundario a la izquierda,
                      primario a la derecha) y el que leen el teclado y el
                      lector de pantalla. */}
                      <div className="flex flex-col-reverse sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
                        <button
                          onClick={cancel}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={confirm}
                          className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors gap-2"
                        >
                          Traducir
                          <CreditBadge credits={cost} tone="on-primary" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Selector de imagen de un slot de escena: pop-up CENTRADO con las
          fuentes en pestañas y sus miniaturas adentro. Vive acá, al final del
          árbol, y no dentro del slot: es un overlay de pantalla completa, y
          colgarlo del tile lo dejaba preso del `overflow` del panel.
          Ver ADR-2026-09-16-el-slot-de-imagen-abre-un-selector-centrado. */}
          {selectorDeImagen && (
            <SelectorDeImagenModal
              titulo={
                selectorDeImagen.kind === "first"
                  ? "Elegí el frame inicial"
                  : selectorDeImagen.kind === "last"
                    ? "Elegí el frame final"
                    : selectorDeImagen.kind === "attach"
                      ? "Elegí una imagen de referencia"
                      : "Elegí una imagen o un video de referencia"
              }
              pestanas={pestanasDelSelector}
              pestanaActiva={pestanaDelSelector}
              onCambiarPestana={(id) =>
                setPestanaDelSelector(id as typeof pestanaDelSelector)
              }
              acciones={accionesDelSelector}
              onCerrar={() => setSelectorDeImagen(null)}
            />
          )}

        </BrandCustomFontsContext.Provider>
      </BrandFontsContext.Provider>
    </BrandColorsContext.Provider>
  );
};

export default VideoEditor;
